import { getSplatRegionEffects } from '../effects/splat-region-effects';
import { guardStaleGsplatPlacements } from './gsplatPlacementGuard';
import { MAIN_SPLAT_OBJECT_ID, drawSplatObjectBounds, normalizeSplatObjectIds, pickSplatObjectAtScreen, registerSplatObject, splatObjectComponents, splatObjectSceneBounds } from '../effects/splat-objects';
import { WaterSystem } from '../effects/water/waterSystem';
import { WaterSplatSplit } from '../effects/water/waterSplatSplit';
import { CloudSystem } from '../effects/clouds/cloudSystem';
import { ScanSkyController } from '../effects/clouds/scanSky';
import { ProceduralSky } from '../effects/clouds/proceduralSky';
import { WindSystem } from '../effects/wind/windSystem';
import { windDirectionRuntime } from '../effects/wind/windParams';
import { defaultCloudLayerFor } from '../effects/clouds/cloudParams';
import { applyRegionToEntity, regionFromEntity, drawRegionWireframe, normalizeRegion, regionMatrix, writeRegionUniform, type RegionShape } from '../effects/region-shape';
import { initializeWebGPUDevice } from './initializeWebGPUDevice';
import { createRecoverableViewer, rememberGraphicsFailure } from './graphicsRecovery';
/**
 * Dynamic Viewer for StorySplat
 *
 * Creates an embedded PlayCanvas viewer from scene data.
 * Uses the same core logic as HTML export for 100% parity.
 */
import * as pc from 'playcanvas';
import { OverlayController, TriggerController, type TriggeredItem } from './overlays';
import { SwapAreaState } from './swapAreas';
import { CaptureSwapDriver, type CaptureSwapFrame, type CaptureSwapHost } from './captureSwaps';
import type { TransitionClock } from '../effects/transition-clock';
import { createLinkedActionRunner, holdAutoTrigger, isAutoTriggerHeld, linkedActionsOpenPopup, type LinkedHotspotTarget, type LinkedModelTarget } from './linkedActions';
import { buildCutawayEffect, CUTAWAY_KEY, cutawayLevelToHeight, sceneHeightRange, writeCutawayUniform, type CutawayDirection } from '../effects/cutaway';
import { planCutawayAction, sceneHasCutawayActions, stepCutawayTransition, type CutawayTransition } from '../effects/cutawayActions';
import { migrateRegionAnimations, regionAnimatableVolume, regionsFromLegacy, type RegionsConfig } from '../effects/regions';
import { REGIONS_EFFECT_KEY, regionRgb, resolveRegions } from '../effects/regions-effect';
import { RegionsRuntime } from './regionsRuntime';
import { regionOverlayStyle, thickLineOffsets } from './regionOverlayStyle';
import { bindRegionEvents, createRegionsApi, regionAnimationHandler } from './regionsApi';
import { mountLayersPanel, type LayersPanelHandle } from './layersPanel';
import { createMeasurementSetsApi, measurementShown, sceneMeasurementSets, type MeasurementSetsState } from '../utils/measurementSets';
import { buildMeasurementSetPicker, measurementSetsStartHidden, offersMeasurementSetPicker } from './measurementSetsPicker';
import { VisitorDirectoryState, type DirectoryView } from './visitorDirectory';
import { createDirectoryChrome, directoryLabels, mountDirectoryPanel, type DirectoryPanelHandle } from './directoryPanel';
import { DEFAULT_SCENE_GUIDE_ENDPOINT, mountSceneGuide, encodeSceneGuideScreenshot, resolveSceneGuide } from './sceneGuide';
import { cleanSurfacePoints, fitSurfacePolygon, formatArea, formatDistance as formatMeasureDistance } from '../utils/surfacePolygon';
import { transformSceneToExportProps, exportPropsToViewerConfig } from '../transformers/sceneToConfig';
import { buildWalkControllerConfig } from './walkControllerConfig';
import { playerCollisionRadius } from '../utils/playerSize';
import { computeRenderBounds } from '../utils/renderBounds';
import { collectMarkerRenders, hideForCapture } from './captureHiding';
import { hotspotLeaderLineShape, isPlaneFramedShape, leaderLineStartGap, portalLeaderLineShape, portalMarkerShape, portalMarkerUsesColor, type LeaderLineMarkerShape } from './leaderLineGeometry';
import { layoutMarkerStack, markerStackEdgeDistance, markerStackHasVideo, normalizeMarkerStackSpacing, rayHitsUnitPlane, sanitizeMarkerStack, MARKER_LAYER_PROVISIONAL_ASPECT, type MarkerStackLayout } from './markerStack';
import { createFrameCaptureSession, renderUntilSplatsReady, validateCaptureFps, validateCaptureSize } from './frameCapture';
import type { OverlayData, CapturePhotoOptions, CameraPose, CameraPoseFollower, FrameCaptureOptions, FrameCaptureSession, PathRenderInfo, CutawayAction, CutawayOptions, CutawayStatus, SceneData, ViewerInstance, EditorViewerInstance, ViewerOptions, ViewerEvent, ViewerLoadedData, HotspotData, HotspotOrbitView, ExtraWaypointData, MarkerLayer, LightConfig, ParticleConfig, CustomMeshConfig, CustomMeshUpdateData, HtmlMeshUpdateData, WaypointData, PortalData, CollisionMeshConfig, AudioEmitterConfig, ButtonLabels, SplatRelightingConfig, MirrorPlaneData, WaterPlaneData, CloudLayerData, LodSettingsConfig, WaypointEasingMode, CameraControlSettings, SplatSwapPoint, SplatSwapAction, AudioReactiveConfig, LinkedAction } from '../types';
import { buildPolygonMesh, sanitizeFootprint, HORIZONTAL_LAYOUT, VERTICAL_LAYOUT } from '../utils/polygonMesh';
import { createLayersChrome, syncToolbarVisibility, createOverlayLayer, destroyOverlayLayer, injectOverlayStyles, buildOverlayElement, updateOverlayElement, showOverlayElement, hideOverlayElement, showTopOverlayElement, hideTopOverlayElement, createUIElements, connectUIToViewer, injectStyles, hidePreloader, updatePreloaderProgress, showDetailIndicator, hideDetailIndicator, showHotspotPopup, hideHotspotPopup, setJoystickVisible, updateJoystickPosition, updateLookZoneState, setWasdHintVisible, setOrbitHintVisible, setDoubleTapHintVisible, setTourLookHintVisible, setTourOnboardingMode, type ControlsCardMode, setupWasdKeyInteraction, createLazyLoadUI, updateWaypointListActive, setupWaypointListClickHandlers, setupSceneMenuClickHandlers, markSceneVisited, showErrorPopup, showPhotoSharePanel, closePhotoSharePanel, closeShareLinkPopover, downloadPhotoBlob, showViewerToast, getButtonLabel, DEFAULT_BUTTON_LABELS, updateFpsCounter, type UIElements } from './viewerUI';
import { createTourOnboardingLifecycle, markSceneInteractionForOnboarding } from './tourOnboarding';
import { installMediaProtection } from './mediaProtection';
import { releaseScreenSize, resolveScreenSizeSettings, screenSizeFactor, stepScreenSize, type ScreenSizeState } from './hotspotScreenSize';
import { hotspotClickMayMoveCamera, hotspotIntoView, resolveHotspotCameraAction } from './hotspotCameraAction';
import { EXTRA_WAYPOINT_LOOK_DISTANCE, extraWaypointExploreMode, extraWaypointMenuEntries, extraWaypointView, findExtraWaypoint, normalizeExtraWaypoints, waypointRotationToPlayCanvas, waypointStepTarget } from './extraWaypoints';
import { createSkinUiTeardown } from './skinUiLifecycle';
import { CameraControls } from './CameraControls';
import { lookAxisSign } from './lookInversion';
import { clampTourPitch, composeTourLook } from './tourLook';
import { patchGpuSortClip, setSplatClipPlane } from './gsplatClipPlane';
import { CharacterController } from './CharacterController';
import { addContainerMeshes, attachTriangleCollider, colliderFileName, isLoadableColliderFile } from './customMeshCollider';
import { disableHelperShadows } from '../utils/helperShadows';
import { DroneController } from './DroneController';
import { readGamepad, GamepadEdgeTracker, GP_BUTTON } from './gamepadUtils';
import { SplatDepthPicker } from './SplatDepthPicker';
import { pickDiag } from './pickDiagnostics';
import { ExactSplatRaycaster } from './ExactSplatRaycaster';
import { crossSceneAudio } from './crossSceneAudio';
import { inheritPortalAudioReactiveConfig } from './portalAudioReactive';
import { ensureZstdDecoderModule } from './zstd-decoder';
import { runWideCapture } from './wideCapture';
import { getGsplatRevealRadialClass, getGsplatRevealBloomClass, getRevealPreset, type RevealPreset, type RevealStyle, getGsplatRelightingClass, getGsplatWipeTransitionClass, type GsplatWipeTransitionScript, getGsplatDissolveTransitionClass, type GsplatDissolveTransitionScript, mirrorClipShaderGLSL, mirrorClipShaderWGSL } from '../effects';
import { buildHazeEffect, createHazeRegionUniforms, drawableHazeRegions, resolveHazeLook, writeHazeUniform } from '../effects/haze-regions';
import { GsplatWeather, WEATHER_PRESETS } from '../effects/gsplat-weather';
import { GsplatAudioReactive, resolveAudioReactiveConfig } from '../effects/gsplat-audio-reactive';
import { audioAnalyserBus } from './audioAnalyserBus';
import { bindViewerMicrophoneControl } from './viewerMicrophoneControl';
import { CompareSlider } from '../ui/compare-slider';
import { createSplitViewCompare, type SplitViewCompareHandle } from './SplitViewCompare';
import { createStereoRenderer, type StereoRendererHandle } from './StereoRenderer';
import { createStereoUi, type StereoUiHandle } from './stereoUi';
import { clampStereoDepth, isSplitStereoFormat, isStereoFormat, resolveAutoScreenDistance } from './stereoMath';
import type { StereoFormat, StereoOptions } from '../types';
import type { CompareConfig } from '../types';
import { AnimatedGifTexture, isGifUrl } from './AnimatedGifHelper';
import { setupHtmlMeshes, type HtmlMeshConfig, type HtmlMeshManager } from './HtmlMeshHelper';
import { PerfHud, fmtBytes, type PerfSnapshot } from './PerfHud';
import { OnDemandRenderScheduler, CameraPoseTracker, DeferredOnDemandRendering, continuousRenderReasons, resolveOnDemandRendering, sceneGraphNeedsRender } from './OnDemandRenderScheduler';
import { BillboardPoseTracker } from './BillboardPoseTracker';
import { setupCustomScript, CustomScriptSystem } from './CustomScriptSystem';
import { syncMeshLightLayers as syncLightLayersForMeshes, resolveMeshAmbient, ambientEquals, setEntityUnlit } from './meshLighting';
import { FrameSequencePlayer } from './FrameSequencePlayer';
import { setupAnalyticsTracking, type SessionRecorderHandle } from './SessionRecorder';
import { createBandwidthReporter, type BandwidthReporterHandle } from './BandwidthReporter';
import {
    createAssetLeaseManager,
    PROD_ASSET_RENEWAL_ENDPOINT,
    type AssetLeaseHandle,
} from './AssetLeaseManager';
import { setupGoogleAnalytics, type GoogleAnalyticsHandle } from './GoogleAnalytics';
import { isAnalyticsAllowed } from './analyticsConsent';
import { EntityAnimationSystem } from './EntityAnimationSystem';
import { hexToAnimationRgb } from './animationProperties';
import { loadSegmentAttachment, type SegmentAttachment as SegmentAttachmentType } from '../segments/SegmentAttachment';
import { loadXR8 } from './eighthwall/XR8Loader';
import { createStorySplatPipeline } from './eighthwall/XR8Pipeline';
import { ARPlacement } from './eighthwall/ARPlacement';
import { arError, arInfo, arWarn } from './eighthwall/ARLogger';
import { generateWaypointPathSamples, getSegmentIndices, sampleWaypointPathSegment, type WaypointPathNode } from '../utils/waypointPath';
import { applyWaypointEasing, normalizeWaypointEasing } from '../utils/waypointEasing';
import { DampedPoseFollower } from '../utils/pathPoseFollower';
import { isSpzSplatUrl, usesNativeSplatFrame } from '../utils/splatFrame';
import { installGsplatProjectorCapFix } from '../utils/gsplatProjectorCapFix';
import { isIOSLikeDevice, planVideoPlaneAlpha } from '../utils/videoFormat';
import { createVideoAlphaProbe } from '../utils/videoAlphaProbe';
import { isMainSplatTarget, resolveClickSplatSwap, sanitizeSwapList, swapUrlCandidates } from '../utils/splatSwaps';
import { applyEntityOpacity as applyMaterialOpacity, hasVisibleEntityTranslucency, isEntityTranslucentMaterial, setTextureTranslucent, resolveAnimatedOpacity, resolveEntityOpacity, snapLegacySphereOpacity, FULL_OPACITY_THRESHOLD, INVISIBLE_OPACITY_THRESHOLD, type EntityOpacityConfigLike } from '../utils/materialOpacity';
import { installSplatDepthPass } from './splatDepthPass';
import { installParticleSplatDepth, hasVisibleParticles, particlesOnScreen } from '../effects/particleSplatDepth';
import { setSceneSplatBudget } from './splatBudget';
import { restoreNearClipAfterXrEnd } from './xrEnd';
import { ShadowSystem } from '../effects/shadows/shadowSystem';
import { resolveSceneShadows } from '../effects/shadows/shadowSettings';
import { lightDirectionOf, lightRotationFromDirection, lightRotationFromForward } from '../effects/shadows/lightDirection';
import { createInitialPlaybackGate, shouldRequestInitialPlayback } from './initialPlaybackGate';
import { createAutoplayDetailGate, shouldWaitForDetailBeforeAutoplay } from './autoplayDetailGate';
import { showAutoplayDetailOverlay, showAutoplayDetailStartButton, hideAutoplayDetailOverlay } from './viewerUI';
import type { ViewerButtonLabels } from '../types';
import { SpzParser } from 'playcanvas/scripts/esm/parsers/spz-parser.mjs';
import { SplatFormatParser } from './splatFormatParser';
import { XrControllers } from 'playcanvas/scripts/esm/xr/xr-controllers.mjs';
import { showControllerSelfLit } from './xrControllerMaterials';
import { VrFloatNavigation, vrNavigationTuning, vrRigPlacement, yawOfDirection } from './vrNavigation';
import { VrInterface, type VrInterfaceHost, type VrMeasurementGeometry } from './vrUi/vrInterface';
import { sceneMenuItems, type VrLabels, type VrPopupSource, type VrSceneMenuItem } from './vrUi/vrMenus';
import { showVrSavedLinks } from './vrUi/vrSavedLinks';
import { getWaypointBannerContent, loadPdfJs } from './viewerUI';
import { SPLAT_EDIT_ACTIVE_EVENT } from '../editor/splat-edit/events';
declare const __BUILD_TIMESTAMP__: string;
declare const __PACKAGE_VERSION__: string;

type ExploreModeSettingsUpdate = {
    enabledExploreModes?: Array<'orbit' | 'fly' | 'walk'> | null;
    initialSplatExploreMode?: 'orbit' | 'fly' | 'walk' | null;
    applyCurrent?: boolean;
};
type ViewerOptionsWithSessionRecorder = ViewerOptions & {
    __sessionRecorderHandle?: SessionRecorderHandle;
    /** Internal hand-off used by in-place portal navigation. */
    __googleAnalyticsHandle?: GoogleAnalyticsHandle;
    /** Internal hand-off used by in-place portal navigation. */
    __resumeFpvAfterPortal?: boolean;
};

// PlayCanvas 2.18+ gsplat renderer choice → engine constant. Kept as a single
// source of truth so the init path and live setLodSettings path can't drift.
// Numeric fallbacks guard against PlayCanvas builds where the constants
// aren't exposed; if they shift order in a future engine version, the explicit
// names take precedence.
const GSPLAT_RENDERER_MAP: Record<string, number> = {
    'auto': (pc as any).GSPLAT_RENDERER_AUTO ?? 0,
    'raster-cpu': (pc as any).GSPLAT_RENDERER_RASTER_CPU_SORT ?? 1,
    'raster-gpu': (pc as any).GSPLAT_RENDERER_RASTER_GPU_SORT ?? 2,
    'compute': (pc as any).GSPLAT_RENDERER_COMPUTE ?? 3,
};
// Reverse map for readable logs — converts whatever numeric/string the engine
// reports back for `currentRenderer` into the name we pass in the scene JSON.
const GSPLAT_RENDERER_NAME_BY_VALUE: Record<string, string> = Object.entries(GSPLAT_RENDERER_MAP)
    .reduce((acc, [name, val]) => { acc[String(val)] = name; return acc; }, {} as Record<string, string>);

// UI-level 'auto' (and unset) maps to raster-gpu under the hood. PlayCanvas
// silently falls back to raster-cpu on WebGL2, so this gives us WebGPU GPU-sort
// when available and a stable WebGL2 path otherwise — without exposing the
// raster-cpu/raster-gpu distinction to creators. Power users picking 'compute'
// in Advanced Settings still get the experimental WebGPU compute path.
function resolveEffectiveRenderer(choice: string | undefined): keyof typeof GSPLAT_RENDERER_MAP {
    if (!choice || choice === 'auto') return 'raster-gpu';
    return choice in GSPLAT_RENDERER_MAP ? (choice as keyof typeof GSPLAT_RENDERER_MAP) : 'raster-gpu';
}

/**
 * Emit a single, grep-friendly line describing which gsplat renderer is
 * actually active on the current device. Call this every time the renderer
 * is (re)set so we can confirm across devices whether the requested renderer
 * stuck or was silently downgraded (WebGPU-only modes fall back to
 * raster-cpu on WebGL / mobile Safari).
 *
 * Filter in DevTools:  [SPLAT][RENDERER]
 */
function logGsplatRenderer(app: pc.Application, phase: string, requested: string | undefined): void {
    const gsplat = (app.scene as any).gsplat;
    const activeRaw = gsplat?.currentRenderer;
    const activeName = GSPLAT_RENDERER_NAME_BY_VALUE[String(activeRaw)] ?? `unknown(${activeRaw})`;
    const device = app.graphicsDevice as any;
    const isWebGPU = device?.isWebGPU === true;
    const deviceType = isWebGPU ? 'webgpu' : (device?.isWebGL2 ? 'webgl2' : (device?.isWebGL1 ? 'webgl1' : 'unknown'));
    const fellBack = !!requested && requested !== 'auto' && requested !== activeName;
    console.log(
        `[SPLAT][RENDERER] ${phase}`,
        {
            requested: requested ?? '(none — engine default)',
            active: activeName,
            activeRaw,
            fellBack,
            device: deviceType,
            webgpu: isWebGPU,
            gpu: device?.deviceName ?? device?.gpuAdapter?.name ?? 'unknown',
            mobile: typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/.test(navigator.userAgent),
        }
    );
}
function toPlayCanvasCurveOut(curveOut?: WaypointData['curveOut']): WaypointPathNode['curveOut'] {
    if (!curveOut) return undefined;
    return {
        mode: curveOut.mode,
        handleOut: curveOut.handleOut
            ? { x: curveOut.handleOut.x, y: curveOut.handleOut.y, z: -curveOut.handleOut.z }
            : undefined,
        handleInNext: curveOut.handleInNext
            ? { x: curveOut.handleInNext.x, y: curveOut.handleInNext.y, z: -curveOut.handleInNext.z }
            : undefined,
    };
}
function buildWaypointPathNodes(waypointPositions: pc.Vec3[], waypoints?: WaypointData[]): WaypointPathNode[] {
    return waypointPositions.map((position, index) => ({
        position,
        curveOut: toPlayCanvasCurveOut(waypoints?.[index]?.curveOut),
    }));
}
interface WindowWithLegacyAudio extends Window {
    opera?: string;
    webkitAudioContext?: typeof AudioContext;
}
interface AudioSlotWithStoredVolume extends pc.SoundSlot {
    _storedVolume?: number;
}
interface ViewerRuntimeApp extends pc.Application {
    __htmlMeshManager?: HtmlMeshManager;
    /** The VR interface (tests and debugging read it). */
    __vrUi?: VrInterface;
    __customScriptSystem?: CustomScriptSystem;
}
interface ViewerRuntimeEntity extends pc.Entity {
    modelEntity?: pc.Entity;
    /** "Keep same size on screen" hotspots: the set size and the multiplier applied to it. */
    screenSizeState?: ScreenSizeState;
    _billboardActive?: boolean;
    _billboardOriginalRotation?: pc.Quat;
    visibilityRange?: {
        type: 'percentage' | 'waypoint';
        start: number;
        end: number;
    };
    opacityConfig?: {
        mode: string;
        value: number;
    };
    shouldBeVisible?: boolean;
    /** Hidden by a keyframe animation's Visible track; visibility writers keep it hidden. */
    animationHidden?: boolean;
    /** Hotspot whose folder a visitor (directory tick box) or a script hid; visibility writers keep it hidden. */
    folderHidden?: boolean;
    /** Opacity set by a keyframe animation; takes precedence over scroll-driven opacity. */
    animationOpacity?: number;
    /** Sphere marker color set by a keyframe animation (0-1 rgb). */
    animationMarkerColor?: [number, number, number];
    hiddenUntilTextureLoaded?: boolean;
    textureLoaded?: boolean;
    targetOpacity?: number;
    hotspotMaterial?: pc.StandardMaterial;
    /** Optional leader-line child entity drawn from icon to a world target point */
    leaderLineEntity?: pc.Entity;
    portalMaterial?: pc.StandardMaterial;
    videoElement?: HTMLVideoElement;
    alphaVideoElement?: HTMLVideoElement | null;
    mediaTriggerMode?: string;
    proximityDistance?: number;
    isVideoPlaying?: boolean;
    /** Set while a linked action overrides the automatic media trigger (see linkedActions.ts). */
    linkedTriggerHold?: boolean;
    videoSpatialAudio?: {
        audioCtx: AudioContext;
        source: MediaElementAudioSourceNode;
        panner: PannerNode;
    } | null;
    /** PlayCanvas sound slot ID for hotspot audio */
    hotspotAudioSlotId?: string;
    /** Whether hotspot audio is currently playing */
    hotspotAudioPlaying?: boolean;
    /** Whether hotspot audio asset has loaded */
    hotspotAudioReady?: boolean;
    hotspotData?: HotspotData;
    portalData?: PortalData;
    meshClickHandler?: (event: MouseEvent) => void;
    meshHoverHandler?: (event: MouseEvent) => void;
    meshLeaveHandler?: () => void;
    /** Delegated mesh-interaction registry entry (see registerInteractiveMesh). Typed as unknown —
     *  the entry interface is declared inside createViewer's closure; use sites cast. */
    meshInteractionEntry?: unknown;
    _collisionMeshId?: string;
    /** Collision logic reads these off an editor collider (CharacterController's CollisionEntity). */
    _collisionMeshType?: string;
    _collisionInsideMode?: boolean;
    wasInProximity?: boolean;
    proximityTriggered?: boolean;
    _audioHoverMode?: boolean;
    _audioHoverPlaying?: boolean;
    gifCanvas?: HTMLCanvasElement;
    gifTexture?: pc.Texture;
    gifAnimationFrame?: number;
    animatedGifTexture?: AnimatedGifTexture;
    videoOverlay?: pc.Entity;
    portalPopupEntity?: pc.Entity;
    portalPopupMaterial?: pc.StandardMaterial;
    portalPopupOpacity?: number;
    /** Stacked marker (`type: 'stack'`): its layers, materials and current layout. */
    markerStack?: MarkerStackRuntime;
    /** Layer plane entities of a stacked marker, top to bottom (children of this entity). */
    markerStackLayers?: pc.Entity[];
    /** Media and GPU resources a hotspot / portal marker owns (see ownMarkerResources). */
    markerResources?: MarkerOwnedResources;
}
/**
 * Hidden whatever the scene's own visibility rules say: by a keyframe animation (animationHidden)
 * or because its hotspot folder is hidden (folderHidden). Every writer of a marker's `enabled`
 * respects it, so showing it again gives back exactly the visibility it would otherwise have.
 */
function hiddenByOverride(entity: ViewerRuntimeEntity): boolean {
    return entity.animationHidden === true || entity.folderHidden === true;
}
/**
 * What a hotspot or portal marker owns outside its PlayCanvas components. entity.destroy()
 * removes the render / collision / sound components but never destroys materials, textures,
 * <video> elements, GIF players or registry assets, so these are released explicitly when the
 * marker entity is destroyed (editor removal or rebuild, viewer teardown).
 */
interface MarkerOwnedResources {
    released: boolean;
    /** Destroyed together with every texture they sample. */
    materials: pc.StandardMaterial[];
    textures: pc.Texture[];
    gifs: AnimatedGifTexture[];
    videos: HTMLVideoElement[];
    audioAssets: pc.Asset[];
}
/** One layer plane of a stacked marker. */
interface MarkerStackLayerRuntime {
    layer: MarkerLayer;
    entity: pc.Entity;
    material?: pc.StandardMaterial;
    /** "Tap to Start" prompt over a video layer. */
    overlay?: pc.Entity;
    /** Media width / height; provisional until the media reports its size. */
    aspect: number;
    /** Loaded, failed or timed out: the stack is shown once every layer has settled. */
    settled: boolean;
    gif?: AnimatedGifTexture;
    video?: HTMLVideoElement;
    alphaVideo?: HTMLVideoElement | null;
    videoTextures?: pc.Texture[];
}
interface MarkerStackRuntime {
    layers: MarkerStackLayerRuntime[];
    spacing: number;
    layout: MarkerStackLayout;
    /** Every layer material, for opacity and billboard culling. */
    materials: pc.StandardMaterial[];
    released: boolean;
}
/**
 * Whether a hotspot entity plays a video plane driven by mediaTriggerMode: a video hotspot,
 * or a stacked marker whose video layer is `entity.videoElement`.
 */
function hotspotHasVideoPlane(hotspot: { type?: string }, entity: { videoElement?: HTMLVideoElement }): boolean {
    return !!entity.videoElement && (hotspot.type === 'video' || hotspot.type === 'stack');
}
interface GraphicsDeviceRuntimeInfo {
    isWebGPU?: boolean;
    isWebGL2?: boolean;
    isWebGL1?: boolean;
}
type ProjectionCallback = (mat: pc.Mat4, view: number) => void;
type CameraComponentProjectionHook = {
    calculateProjection: ProjectionCallback | null;
};
interface MirrorPlaneRuntimeEntity extends pc.Entity {
    _mirrorCustomMesh?: pc.Mesh;
    _mirrorMaterial?: pc.Material;
    _mirrorReflCam?: pc.Entity;
    _mirrorRenderTarget?: pc.RenderTarget;
    _mirrorReflTexture?: pc.Texture;
    _mirrorData?: MirrorPlaneData;
}
interface UIElementsWithPortalPopup extends UIElements {
    portalPopup?: HTMLElement;
}
interface LegacyVec3 {
    x?: number;
    y?: number;
    z?: number;
    _x?: number;
    _y?: number;
    _z?: number;
}
type SceneVec3 = { x: number; y: number; z: number };

type VideoTextureUploadState = {
    sourceAttached: boolean;
    warnedBackResource: boolean;
    // Re-arms a texture upload when the displayed frame can change while PAUSED (initial load,
    // seek, pause-on-exact-frame). Cleared ONLY by a successful upload: uploads can transiently
    // fail (WebGPU backing-resource not ready until the frame decodes), and the per-frame retry
    // while this flag is set is what guarantees the first/paused frame eventually lands. Without
    // this gate, paused click-mode videos re-ran a full texImage2D-from-video EVERY frame forever.
    pendingUpload: boolean;
};

/** Wire the events after which a paused video's visible frame can differ from the uploaded one.
 *  Each sets pendingUpload; the per-frame handler uploads (with retry) until one succeeds. */
function armVideoUploadOnFrameChange(video: HTMLVideoElement, state: VideoTextureUploadState): void {
    const arm = () => { state.pendingUpload = true; };
    video.addEventListener('loadeddata', arm);
    video.addEventListener('seeked', arm);
    video.addEventListener('pause', arm);
}

function videoHasRenderableFrame(video: HTMLVideoElement): boolean {
    const haveCurrentData = typeof video.HAVE_CURRENT_DATA === 'number' ? video.HAVE_CURRENT_DATA : 2;
    return !video.error &&
        video.readyState >= haveCurrentData &&
        video.videoWidth > 0 &&
        video.videoHeight > 0;
}

function isVideoBackingResourceError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return message.includes('copyExternalImageToTexture') ||
        message.includes('Failed to import texture from video') ||
        message.includes('back resource');
}

function uploadVideoTextureFrame(
    texture: pc.Texture,
    video: HTMLVideoElement,
    state: VideoTextureUploadState,
    label: string
): boolean {
    if (!videoHasRenderableFrame(video)) {
        return false;
    }

    try {
        if (!state.sourceAttached) {
            texture.setSource(video);
            state.sourceAttached = true;
        } else {
            texture.upload();
        }
        state.warnedBackResource = false;
        state.pendingUpload = false;
        return true;
    } catch (error) {
        if (isVideoBackingResourceError(error)) {
            if (!state.warnedBackResource) {
                console.warn(`[VideoTexture] Waiting for decoded video backing resource before upload: ${label}`, error);
                state.warnedBackResource = true;
            }
        } else {
            console.warn(`[VideoTexture] Failed to upload video texture frame: ${label}`, error);
        }
        return false;
    }
}

function finiteSceneNumber(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return fallback;
}

function finitePositiveSceneNumber(value: unknown, fallback: number): number {
    const numeric = finiteSceneNumber(value, fallback);
    return numeric > 0 ? numeric : fallback;
}

function finiteNonNegativeSceneNumber(value: unknown, fallback: number): number {
    const numeric = finiteSceneNumber(value, fallback);
    return numeric >= 0 ? numeric : fallback;
}

function finiteSceneVec3(value: unknown, fallback: SceneVec3 = { x: 0, y: 0, z: 0 }): SceneVec3 {
    const source = value && typeof value === 'object' ? value as LegacyVec3 : {};
    return {
        x: finiteSceneNumber(source.x ?? source._x, fallback.x),
        y: finiteSceneNumber(source.y ?? source._y, fallback.y),
        z: finiteSceneNumber(source.z ?? source._z, fallback.z)
    };
}

function playCanvasPositionFromSceneVec3(value: unknown, fallback?: SceneVec3): SceneVec3 {
    const position = finiteSceneVec3(value, fallback);
    return { x: position.x, y: position.y, z: -position.z };
}

function isFiniteVec3(value: pc.Vec3): boolean {
    return Number.isFinite(value.x) && Number.isFinite(value.y) && Number.isFinite(value.z);
}
interface LegacyQuat extends LegacyVec3 {
    w?: number;
    _w?: number;
}
interface LegacyColor {
    r?: number;
    g?: number;
    b?: number;
    a?: number;
}
interface LegacyParticleConfig extends ParticleConfig {
    emitterPosition?: LegacyVec3;
    gravity?: LegacyVec3;
    color1?: LegacyColor;
    color2?: LegacyColor;
    colorDead?: LegacyColor;
    direction1?: LegacyVec3;
    direction2?: LegacyVec3;
    minLifeTime?: number;
    maxLifeTime?: number;
    emitterType?: string;
    emitBoxMin?: LegacyVec3;
    emitBoxMax?: LegacyVec3;
    blendMode?: string;
    minEmitPower?: number;
    maxEmitPower?: number;
    minSize?: number;
    maxSize?: number;
    minAngularSpeed?: number;
    maxAngularSpeed?: number;
    angularSpeed?: number;
    minInitialRotation?: number;
    maxInitialRotation?: number;
    minScaleX?: number;
    maxScaleX?: number;
    minScaleY?: number;
    maxScaleY?: number;
    emitRate?: number;
    depthWrite?: boolean;
    softParticles?: number;
    halfLambert?: boolean;
    alignToMotion?: boolean;
    stretch?: number;
    autoPlay?: boolean;
    orientation?: number;
    localSpace?: boolean;
    colorGradients?: Array<{
        gradient: number;
        color: LegacyColor;
    }>;
}
interface LegacyWaypointAudioConfig {
    loop?: boolean;
    volume?: number;
    spatialSound?: boolean;
    distanceModel?: string;
    maxDistance?: number;
    refDistance?: number;
    rolloffFactor?: number;
    autoplay?: boolean;
    triggerMode?: 'autoplay' | 'proximity';
    stopOnExit?: boolean;
    proximityRadius?: number;
    url?: string;
}
interface SceneWithToneMapping extends pc.Scene {
    toneMapping?: number;
}
/** Typed reveal effect script with specific properties from GsplatRevealRadial */
interface RevealEffectScript extends pc.ScriptType {
    center: pc.Vec3;
    speed: number;
    acceleration: number;
    delay: number;
    oscillationIntensity: number;
    dotTint: pc.Color;
    waveTint: pc.Color;
    endRadius: number;
}
/** Typed wipe transition script for splat swap transitions (scanline effect) */
interface WipeTransitionScript extends pc.ScriptType {
    mode: number;           // 1.0 = reveal, -1.0 = hide
    duration: number;
    bandWidth: number;
    wipeTop: number;
    wipeBottom: number;
    edgeTint: pc.Color;
    onComplete: (() => void) | null;
    effectTime: number;
    _effectInitialized: boolean;
    _shadersNeedApplication: boolean;
    material: pc.Material | null;
    clock: TransitionClock | null;
    advance: (dtSec: number) => void;
    finish: () => void;
}
/** Typed dissolve transition script for splat swap transitions (cross-dissolve effect) */
interface DissolveTransitionScript extends pc.ScriptType {
    mode: number;           // 1.0 = dissolve in, -1.0 = dissolve out
    duration: number;
    edgeTint: pc.Color;
    onComplete: (() => void) | null;
    effectTime: number;
    _effectInitialized: boolean;
    _shadersNeedApplication: boolean;
    material: pc.Material | null;
    clock: TransitionClock | null;
    advance: (dtSec: number) => void;
    finish: () => void;
}
/** Typed relighting effect script with specific properties from GsplatRelighting */
interface RelightingEffectScript extends pc.ScriptType {
    relightFade: number;
    ambientR: number;
    ambientG: number;
    ambientB: number;
    material: pc.Material | null;
    _shaderManagedExternally: boolean;
    setRelightFade: (fade: number) => void;
    setAmbientColor: (hex: string) => void;
    updateLights: (lights: Array<{ position: pc.Vec3; color: pc.Color; intensity: number; range: number }>) => void;
    _applyShaders: () => void;
}
type LightConfigLike = Omit<LightConfig, 'position' | 'rotation' | 'direction'> & {
    position?: LegacyVec3;
    rotation?: LegacyVec3;
    direction?: LegacyVec3;
    enabled?: boolean;
    angle?: number;
    exponent?: number;
    innerAngle?: number;
    outerAngle?: number;
    shadowBias?: number;
    normalOffsetBias?: number;
};
type WaypointConfigLike = Omit<WaypointData, 'position' | 'interactions'> & {
    position?: LegacyVec3;
    interactions?: Array<{
        id?: string;
        type?: string;
        data?: LegacyWaypointAudioConfig;
    }>;
};
type CustomMeshConfigLike = Omit<CustomMeshConfig, 'position' | 'rotation' | 'scale' | 'interaction'> & {
    position?: LegacyVec3;
    rotation?: LegacyVec3;
    scale?: LegacyVec3;
    enabled?: boolean;
    interaction?: (CustomMeshConfig['interaction'] & {
        popupContent?: string;
    }) | undefined;
};
type HotspotConfigLike = Omit<HotspotData, 'position' | 'rotation' | 'scale' | 'id' | 'type' | 'data' | 'activationMode'> & {
    position?: LegacyVec3;
    rotation?: LegacyVec3;
    scale?: number | LegacyVec3;
    id?: string;
    type?: string;
    data?: unknown;
    activationMode?: string;
    enabled?: boolean;
    videoSpatialAudio?: boolean;
    videoDistanceModel?: DistanceModelType;
    videoRefDistance?: number;
    videoMaxDistance?: number;
    videoRolloffFactor?: number;
};
type PortalConfigLike = Omit<PortalData, 'position' | 'rotation' | 'scale' | 'id' | 'type' | 'targetSceneId' | 'activationMode'> & {
    position?: LegacyVec3;
    rotation?: LegacyVec3;
    scale?: number | LegacyVec3;
    id?: string;
    type?: string;
    targetSceneId?: string;
    activationMode?: string;
    enabled?: boolean;
    useLighting?: boolean;
    proximityDistance?: number;
    confirmNavigation?: boolean;
};
type GSplatComponentWithRuntime = Omit<pc.GSplatComponent, '_instance'> & {
    unified?: boolean;
    instance?: pc.GSplatInstance;
    _instance?: pc.GSplatInstance;
    lodDistances?: number[];
    lodBaseDistance?: number;
    lodMultiplier?: number;
    lodRangeMin?: number;
    lodRangeMax?: number;
};
type MeshInstanceLike = pc.MeshInstance & {
    visible?: boolean;
};
type ScriptComponentWithCreate = Omit<pc.ScriptComponent, 'create'> & {
    create?: (scriptType: typeof pc.ScriptType | string) => pc.ScriptType;
};
interface GsplatHandlerWithOctreeParser {
    _octreeParser?: {
        load: (urlObj: {
            load: string;
            original?: string;
        }, callback: (err: unknown, resource: unknown) => void) => void;
    };
}
interface GsplatParserLike {
    canParse?: (context: { basename: string; ext: string; url: string }) => boolean;
    load: (urlObj: {
        load: string;
        original?: string;
    }, callback: (err: unknown, resource: unknown) => void, asset?: pc.Asset) => void;
}
interface GsplatHandlerWithParsers {
    // PlayCanvas <=2.20: name-keyed map ({ ply, sog, sogBundle, octree }).
    // PlayCanvas >=2.21: ResourceHandler#parsers returns an array (parser registry).
    parsers?: { octree?: GsplatParserLike } | GsplatParserLike[];
}

/**
 * Locate the gsplat octree (LOD) parser across engine generations. On the 2.21+ parser
 * registry the octree parser identifies itself via its own canParse; probe back-to-front
 * like the engine's _selectParser, because SogParser.canParse claims every .json URL and
 * only registration order (octree last) disambiguates lod-meta.json.
 */
function findOctreeParser(parsers: GsplatHandlerWithParsers['parsers'], url: string): GsplatParserLike | undefined {
    if (!parsers) return undefined;
    if (!Array.isArray(parsers)) return parsers.octree;
    for (let i = parsers.length - 1; i >= 0; i--) {
        const p = parsers[i];
        try {
            if (p?.canParse?.({ basename: 'lod-meta.json', ext: 'json', url }) === true) return p;
        } catch { /* a parser whose canParse throws on a synthetic context is not ours */ }
    }
    return undefined;
}

function collectRuntimeMeshInstances(entity: pc.Entity): MeshInstanceLike[] {
    const meshInstances: MeshInstanceLike[] = [];

    const add = (value: unknown) => {
        if (Array.isArray(value)) {
            value.forEach(add);
            return;
        }
        if (value) {
            meshInstances.push(value as MeshInstanceLike);
        }
    };

    const render = entity.render as { meshInstances?: readonly unknown[] } | undefined;
    const model = entity.model as { meshInstances?: readonly unknown[] } | undefined;
    const gsplat = entity.gsplat as unknown as {
        meshInstance?: unknown;
        meshInstances?: unknown[];
        instance?: { meshInstance?: unknown; meshInstances?: unknown[] };
        _instance?: { meshInstance?: unknown; meshInstances?: unknown[] };
    } | undefined;

    add(render?.meshInstances);
    add(model?.meshInstances);
    add(gsplat?.meshInstance);
    add(gsplat?.meshInstances);
    add(gsplat?.instance?.meshInstance);
    add(gsplat?.instance?.meshInstances);
    add(gsplat?._instance?.meshInstance);
    add(gsplat?._instance?.meshInstances);

    return meshInstances;
}

function hideMeshInstancesBeforeDestroy(entity: pc.Entity): void {
    const stack: pc.Entity[] = [entity];
    while (stack.length > 0) {
        const current = stack.pop();
        if (!current) continue;

        collectRuntimeMeshInstances(current).forEach((meshInstance) => {
            meshInstance.visible = false;
        });

        for (let i = 0; i < current.children.length; i++) {
            stack.push(current.children[i] as pc.Entity);
        }
    }
}

function destroyEntitySafely(entity: pc.Entity): void {
    hideMeshInstancesBeforeDestroy(entity);
    entity.enabled = false;
    // Unified gsplat streaming (PC 2.20) reconciles its placement snapshot only
    // inside app.render(), but its per-tick updateStreaming runs on 'framerender',
    // which fires BEFORE render. Calling entity.destroy() in the same tick nulls
    // the placement's resource while the streaming snapshot still references it —
    // the next tick then crashes constructing GSplatInfo ("Cannot read properties
    // of null (reading 'numSplats')"); the engine null-guards octree placements
    // but not layer placements. Disabling only detaches the entity from layers
    // and keeps the resource valid, so for gsplat-bearing entities: disable now
    // (already hidden above), force a render so the director reconciles, and do
    // the real destroy on the frame AFTER the snapshot no longer references it.
    // Hit live when toggling mobile preview / Replace Original mid-stream.
    const app = (entity as unknown as { _app?: pc.AppBase })._app;
    const hasGsplat = !!entity.findComponent?.('gsplat');
    if (hasGsplat && app) {
        app.renderNextFrame = true; // reconcile even if autoRender is paused
        app.once('frameend', () => {
            try {
                entity.destroy();
            } catch { /* app/entity already torn down — nothing left to do */ }
        });
        return;
    }
    entity.destroy();
}
type LodCoordinateCorrection = {
    sourceFormat?: string;
    eulerDegrees?: [number, number, number];
};

export type ComparePickSide = 'before' | 'after';

/**
 * Resolve which compare splat is visible under a CSS-pixel pointer coordinate.
 * SplitViewCompare uses the same top-down 0..1 convention for both axes:
 * vertical puts before on the left, horizontal puts before on top.
 */
export function resolveComparePickSide(
    screenX: number,
    screenY: number,
    canvasWidth: number,
    canvasHeight: number,
    dividerPosition: number,
    orientation: 'vertical' | 'horizontal',
): ComparePickSide {
    const extent = orientation === 'horizontal' ? canvasHeight : canvasWidth;
    const coordinate = orientation === 'horizontal' ? screenY : screenX;
    const position = Number.isFinite(dividerPosition)
        ? Math.max(0, Math.min(1, dividerPosition))
        : 0.5;
    if (!Number.isFinite(extent) || extent <= 0 || !Number.isFinite(coordinate)) {
        return 'before';
    }
    return coordinate / extent <= position ? 'before' : 'after';
}

/** Shared loader-cache resources are keyed by the Asset's resolved file URL. */
export function isSplatAssetResourceShared(
    fileUrl: string | undefined,
    referencedUrls: readonly (string | undefined)[],
): boolean {
    return !!fileUrl && referencedUrls.some(url => url === fileUrl);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventCallback = (...args: any[]) => void;
// Event emitter for viewer events
class EventEmitter {
    private listeners: Map<string, Set<EventCallback>> = new Map();
    // Internal observers (analytics, lazy-mode forwarders). They receive events
    // but don't count toward listenerCount, so they don't trick callers into
    // thinking an external consumer is handling the event.
    private internalListeners: Map<string, Set<EventCallback>> = new Map();
    on(event: string, callback: EventCallback): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);
    }
    onInternal(event: string, callback: EventCallback): void {
        if (!this.internalListeners.has(event)) {
            this.internalListeners.set(event, new Set());
        }
        this.internalListeners.get(event)!.add(callback);
    }
    off(event: string, callback: EventCallback): void {
        this.listeners.get(event)?.delete(callback);
        this.internalListeners.get(event)?.delete(callback);
    }
    emit(event: string, ...args: unknown[]): void {
        this.listeners.get(event)?.forEach(cb => cb(...args));
        this.internalListeners.get(event)?.forEach(cb => cb(...args));
    }
    listenerCount(event: string): number {
        return this.listeners.get(event)?.size || 0;
    }
}
// Mobile detection utility
function isMobileDevice(): boolean {
    // UA regex plus the desktop-UA iPad check below. Deliberately NOT a bare
    // maxTouchPoints test — many Windows laptops have touchscreens; the iPad
    // check stays scoped to the MacIntel platform, which real Macs share but
    // report with maxTouchPoints 0.
    const userAgent = navigator.userAgent || navigator.vendor || (window as WindowWithLegacyAudio).opera || '';
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
        || isDesktopUAiPad();
}
// iPadOS 13+ Safari reports a desktop 'Macintosh' UA, so isMobileDevice() misses
// those iPads. MacIntel platform + multi-touch is the remaining reliable signal
// (the same MacIntel + multi-touch pair other iPad checks use).
function isDesktopUAiPad(): boolean {
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}
/** Splat budget cap in a VR session: both eyes draw every frame, so hold to the mobile preset's. */
const VR_SPLAT_BUDGET = 2_000_000;
/** Standalone VR headsets (Quest, Pico, Wolvic): mobile-class GPUs behind a desktop-style UA. */
export function isStandaloneHeadset(): boolean {
    const userAgent = navigator.userAgent || '';
    return /OculusBrowser|\bQuest\b|Pico|Wolvic/i.test(userAgent);
}
/**
 * Render quality, asset variants and splat budgets for mobile-class GPUs: phones, tablets and
 * standalone headsets. Input and touch UI keep using isMobileDevice().
 */
function usesMobileRenderProfile(): boolean {
    return isMobileDevice() || isStandaloneHeadset();
}
/**
 * Where the AR button (8th Wall) is offered: phones and tablets that can open a camera. Headsets
 * report a getUserMedia too, but have no camera feed for it.
 */
function isArCapableDevice(): boolean {
    return isMobileDevice() && !isStandaloneHeadset() && !!navigator.mediaDevices?.getUserMedia;
}
/** The scene's XR mode; anything else ('none', unset) means both, as XR is on. */
export function normalizeXrMode(mode: unknown): 'ar' | 'vr' | 'both' {
    return mode === 'ar' || mode === 'vr' ? mode : 'both';
}
/**
 * Whether the scene offers AR (beta, opt-in per scene): XR on and a mode other than VR only.
 */
export function sceneOffersAR(config: { includeXR?: boolean; xrMode?: string }): boolean {
    return !!config.includeXR && normalizeXrMode(config.xrMode) !== 'vr';
}
/**
 * Safari on Apple Vision Pro: it sends the Mac's user agent, but has WebXR (Safari on a Mac
 * doesn't, and Chrome / Edge / Firefox on a Mac say so in theirs).
 */
function isAppleVisionPro(): boolean {
    const userAgent = navigator.userAgent || '';
    return /Macintosh/.test(userAgent) && !/Chrome|Chromium|CriOS|Firefox|FxiOS|Edg/.test(userAgent)
        && !!(navigator as Navigator & { xr?: object }).xr;
}
/** A headset's own browser (Quest, Pico, Wolvic, Apple Vision Pro): VR is on in every scene. */
export function isVrHeadsetBrowser(): boolean {
    return isStandaloneHeadset() || isAppleVisionPro();
}
/**
 * Whether the VR mode switch means anything on this device: desktops and laptops, where a PC VR
 * headset may be plugged in. Headsets offer VR anyway; phones and tablets don't do VR.
 */
export function vrModeAvailable(): boolean {
    return !isVrHeadsetBrowser() && !isMobileDevice();
}
const VR_MODE_STORAGE_KEY = 'storysplat.vrMode';
/** The visitor's VR mode: every scene they open in this browser is ready for a PC VR headset. */
export function getVrMode(): boolean {
    try {
        return window.localStorage?.getItem(VR_MODE_STORAGE_KEY) === 'true';
    } catch {
        return false; // storage unavailable (private browsing, sandboxed embeds)
    }
}
/** Turn the visitor's VR mode on or off. Takes effect on the next scene load (the renderer is picked at startup). */
export function setVrMode(on: boolean): void {
    try {
        if (on) window.localStorage?.setItem(VR_MODE_STORAGE_KEY, 'true');
        else window.localStorage?.removeItem(VR_MODE_STORAGE_KEY);
    } catch { /* storage unavailable */ }
}
/**
 * Whether this page asks for VR on any device: `?vr=1` (or `?vr`) on the page URL, else the
 * visitor's VR mode; `?vr=0` asks for none. Null: not asked (the device and the scene decide).
 */
export function readVrRequest(): boolean | null {
    try {
        const q = new URLSearchParams(window.location.search).get('vr');
        if (q !== null) return !(q === '0' || q === 'false');
    } catch { /* URL access unavailable */ }
    return getVrMode() ? true : null;
}
/**
 * Whether this visitor is offered VR. Headsets always are. Phones and tablets only when the page
 * asks for it (readVrRequest, or the host's `vr` option): Chrome on Android reports Cardboard VR,
 * and turning AR on saves XR on with VR ('both'), which would give every phone and tablet in an AR
 * scene a VR button. Desktops (PC VR) when asked, or when the scene turned XR on with VR, as it
 * could before VR was on for every scene (xrNeedsWebGL keeps an AR scene from holding their
 * startup for the headset check). `false` turns it off everywhere.
 */
export function vrOfferedTo(config: { includeXR?: boolean; xrMode?: string }, request: boolean | null): boolean {
    if (request !== null) return request;
    if (isVrHeadsetBrowser()) return true;
    if (isMobileDevice()) return false;
    return !!config.includeXR && normalizeXrMode(config.xrMode) !== 'ar';
}
/**
 * Whether this visitor needs the WebGL2 backend for XR. PlayCanvas only offers WebXR on WebGPU
 * where the browser has XRGPUBinding, and the 8th Wall AR engine renders with WebGL only. So a
 * scene starts on WebGL2 where XR could actually start (an AR-capable phone or tablet when the
 * scene offers AR, or a browser with a VR headset when VR is offered to it); every other visitor
 * keeps WebGPU and skips the VR check (phones and tablets aren't offered VR unless asked).
 *
 * A desktop that didn't ask is only checked in a VR-only scene. An AR scene saves XR on with
 * 'both', as a scene with XR on did before VR was on for every scene: there the desktop is still
 * offered VR where it costs nothing (a browser on WebGL2 anyway, or with XRGPUBinding), but
 * turning AR on doesn't hold every desktop visitor's startup for the headset check.
 */
export async function xrNeedsWebGL(config: { includeXR?: boolean; xrMode?: string }, vrRequest: boolean | null = null): Promise<boolean> {
    if (sceneOffersAR(config) && isArCapableDevice()) return true;
    if (!vrOfferedTo(config, vrRequest)) return false;
    if (vrRequest === null && !isVrHeadsetBrowser() && sceneOffersAR(config)) return false;
    if (typeof (globalThis as { XRGPUBinding?: unknown }).XRGPUBinding !== 'undefined') return false;
    const xr = (navigator as Navigator & { xr?: { isSessionSupported?: (sessionMode: string) => Promise<boolean> } }).xr;
    if (!xr?.isSessionSupported) return false;
    try {
        return await Promise.race([
            xr.isSessionSupported('immersive-vr'),
            new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 800)),
        ]);
    } catch {
        return false;
    }
}
/**
 * A device-agnostic bundle of candidate URLs for one splat (main or swap),
 * carrying both the desktop set and the optional mobile set.
 */
interface SplatUrlSet {
    sogUrl?: string;
    compressedPlyUrl?: string;
    splatUrl?: string;
    lodMetaUrl?: string;
    mobileSogUrl?: string;
    mobileCompressedPlyUrl?: string;
    mobileSplatUrl?: string;
    mobileLodMetaUrl?: string;
}
/**
 * Pick the device-appropriate URL set as a WHOLE (never mixing desktop + mobile).
 *
 * On a mobile UA (or editor preview override) the mobile set is used iff ANY mobile
 * URL exists; otherwise the desktop set is used. Mixing is wrong: with only
 * `mobileSogUrl` set, the desktop `lodMetaUrl` would still win and the mobile
 * asset would never load. Returns the chosen URLs without applying any
 * sog>compressedPly>splat priority — callers decide how to consume them.
 */
function selectAssetUrls(
    urlSet: SplatUrlSet,
    onMobile: boolean
): { sogUrl?: string; compressedPlyUrl?: string; splatUrl?: string; lodMetaUrl?: string } {
    const hasAnyMobile = Boolean(
        urlSet.mobileLodMetaUrl ||
        urlSet.mobileSogUrl ||
        urlSet.mobileSplatUrl ||
        urlSet.mobileCompressedPlyUrl
    );
    const useMobile = onMobile && hasAnyMobile;
    return useMobile
        ? {
            sogUrl: urlSet.mobileSogUrl,
            compressedPlyUrl: urlSet.mobileCompressedPlyUrl,
            splatUrl: urlSet.mobileSplatUrl,
            lodMetaUrl: urlSet.mobileLodMetaUrl,
        }
        : {
            sogUrl: urlSet.sogUrl,
            compressedPlyUrl: urlSet.compressedPlyUrl,
            splatUrl: urlSet.splatUrl,
            lodMetaUrl: urlSet.lodMetaUrl,
        };
}
/**
 * Runtime performance overrides for tuning/measurement (dev hook, not a public API).
 * Set `window.__storysplatPerfOverrides = { splatBudget, lodBehindPenalty, ... }` BEFORE
 * createViewer() runs to override the built-in defaults below. Every field is optional.
 */
type PerfOverrides = {
    splatBudget?: number;
    lodBaseDistance?: number;
    lodMultiplier?: number;
    lodUpdateAngle?: number;
    lodUpdateDistance?: number;
    lodBehindPenalty?: number;
    lodUnderfillLimit?: number;
    cooldownTicks?: number;
    colorUpdateAngle?: number;
    maxPixelRatio?: number;
    maxConcurrentLoads?: number;
    /** Splat budget for the water split on the CPU-sort renderer (effects/water/waterSplatSplit.ts). */
    waterSplitCpuSortSplatLimit?: number;
    /** Least share of the frame the water must cover for the split on the CPU-sort renderer. */
    waterSplitCpuSortMinScreenFraction?: number;
};
function getPerfOverrides(): PerfOverrides {
    try {
        const o = (window as unknown as { __storysplatPerfOverrides?: unknown }).__storysplatPerfOverrides;
        return o && typeof o === 'object' ? (o as PerfOverrides) : {};
    } catch { return {}; }
}
// Default LOD behaviour knobs (scene-level GSplatParams). See the setup block in createViewer.
// Behind-camera LOD penalty stays at the engine default (1, with update angle 0). SuperSplat's
// viewer uses 5 / 90 so nodes behind the camera drop levels and come back on rotation. Measured
// 2026-09-01 against a 6-level LOD set, same rendered count: desktop 4M budget re-streamed 182 MB
// during one orbit for a 1.6M resident saving; iPhone profile 2M budget re-streamed 75 MB and
// produced 30 frames over 50 ms (chunk decode churn) for a 0.5M saving. Demoted chunks expire
// from the cooldown and must be re-downloaded when the camera turns — a bad trade for metered
// bandwidth and for hitch-free rotation. Overridable via __storysplatPerfOverrides for testing.
const LOD_UPDATE_ANGLE_DEFAULT = 0;
const LOD_BEHIND_PENALTY_DEFAULT = 1;
const LOD_UNDERFILL_LIMIT_DEFAULT = 6;
// Pixel-ratio caps. Mobile 1.5 (was 2): an iPhone 15 renders 589x1278 instead of 786x1704,
// 44% fewer shaded fragments — splat rendering is fill/overdraw bound on phone GPUs. Still
// sharper than SuperSplat's mobile default (~1.37 effective). Performance mode halves it again.
const MOBILE_MAX_PIXEL_RATIO = 1.5;
const DESKTOP_MAX_PIXEL_RATIO = 2.5;
// Performance mode (end-user toggle): halve the pixel-ratio cap, never below 1 device pixel per
// CSS pixel (mobile 1.5 -> 1.0, desktop 2.5 -> 1.25), and halve the splat budget. An unlimited (0)
// budget becomes a finite one so the mode also caps memory on desktop presets.
const PERF_MODE_STORAGE_KEY = 'storysplat.performanceMode';
const PERF_MODE_PIXEL_RATIO_SCALE = 0.5;
const PERF_MODE_MIN_PIXEL_RATIO = 1;
const PERF_MODE_MIN_BUDGET = 500_000;
const PERF_MODE_UNLIMITED_BUDGET = 3_000_000;
// Stereo output survives a portal hop: the next scene created in the same container picks the
// format up (and keeps the fullscreen it was using), so a story chain keeps playing in 3D.
const stereoCarryOver = new WeakMap<HTMLElement, { format: StereoFormat; ownsFullscreen: boolean }>();
// Low-tier phones (<= 4 cores or <= 4 GB reported) start in performance mode unless the user
// has chosen otherwise; everything else starts with it off and can opt in from the help panel.
// Creators can force it on separately for mobile/tablet and desktop/laptop visitors.
function isLowEndMobileDevice(): boolean {
    if (!isMobileDevice()) return false;
    const nav = navigator as Navigator & { deviceMemory?: number };
    const cores = navigator.hardwareConcurrency || 4;
    return cores <= 4 || (nav.deviceMemory != null && nav.deviceMemory <= 4);
}
export function readInitialPerformanceMode(forceOnMobile = false, forceOnDesktop = false): boolean {
    try {
        const q = new URLSearchParams(window.location.search).get('perf');
        if (q !== null) return q === '' || q === '1' || q === 'true';
    } catch { /* URL access unavailable */ }
    // Creator settings beat the stored preference, but visitors can still turn the mode off
    // for this session. Apply before graphics-device creation and the first splat load.
    if (isMobileDevice() ? forceOnMobile : forceOnDesktop) return true;
    try {
        const stored = window.localStorage?.getItem(PERF_MODE_STORAGE_KEY);
        if (stored === 'true' || stored === 'false') return stored === 'true';
    } catch { /* storage unavailable (private browsing or embedded viewers) */ }
    return isLowEndMobileDevice();
}
/**
 * The creator's "force performance mode" settings are for visitors' devices. The editor's own
 * canvas ignores them, so creators keep a full-quality view of what they are editing.
 */
export function creatorForcedPerformanceMode(
    uiOptions: { forceMobilePerformanceMode?: boolean; forceDesktopPerformanceMode?: boolean } | undefined,
    isEditorMode: boolean,
): { mobile: boolean; desktop: boolean } {
    if (isEditorMode) return { mobile: false, desktop: false };
    return {
        mobile: uiOptions?.forceMobilePerformanceMode === true,
        desktop: uiOptions?.forceDesktopPerformanceMode === true,
    };
}
export function performanceModeBudget(baseBudget: number, enabled: boolean): number {
    if (!enabled) return baseBudget;
    return baseBudget > 0 ? Math.max(PERF_MODE_MIN_BUDGET, Math.floor(baseBudget / 2)) : PERF_MODE_UNLIMITED_BUDGET;
}
// LOD (Level of Detail) presets for different device capabilities
// Based on official PlayCanvas GSplat LOD streaming example
// Presets use PlayCanvas's default lodRange [0, 10] across the board. range was
// previously used as a quality dial (cap LOD index to limit detail), but in the
// LCC2 model each octree-node-depth maps to its own LOD index, so capping range
// silently drops nodes at the banned depths — leaving holes that read as blur.
// Preset differentiation moves to `splatBudget` instead, which PlayCanvas uses
// to rebalance LOD selection across nodes without dropping any depth band.
// Renderer choice is no longer a preset-level concern — every preset uses
// 'auto', which `resolveEffectiveRenderer` maps to raster-gpu (GPU-sort on
// WebGPU, automatic raster-cpu fallback on WebGL2). Power users opting into
// 'compute' do so via the Advanced Settings toggle, which writes the
// `renderer` field on lodSettings directly and overrides the preset default.
export const LOD_PRESETS = {
    // Desktop budgets were 0 (unlimited). On a 21.6M-splat scene that meant 8.8M resident splats,
    // 294 MB downloaded and ~1.4 GB of GPU allocations before the scene settled (2026-09-01). A
    // 4M budget measured 185 MB / 382 MB textures / settled in 18 s instead of 49 s with the same
    // frame times; far nodes simply sit one level coarser. desktop-max keeps headroom at 8M.
    'desktop-max': {
        range: [0, 10] as [number, number],
        lodBaseDistance: 15,
        lodMultiplier: 2,
        splatBudget: 8_000_000,
        renderer: 'auto' as const,
    },
    'desktop': {
        range: [0, 10] as [number, number],
        lodBaseDistance: 15,
        lodMultiplier: 2,
        splatBudget: 4_000_000,
        renderer: 'auto' as const,
    },
    // 3M (was 5M): a 5M work buffer + resident chunk textures + sort buffers is ~500 MB of GPU
    // memory on a phone; SuperSplat's viewer ships 2M/1M on mobile. Performance mode halves it.
    'mobile-max': {
        range: [0, 10] as [number, number],
        lodBaseDistance: 15,
        lodMultiplier: 2,
        splatBudget: 3_000_000,
        renderer: 'auto' as const,
    },
    'mobile': {
        range: [0, 10] as [number, number],
        lodBaseDistance: 15,
        lodMultiplier: 2,
        splatBudget: 2_000_000,
        renderer: 'auto' as const,
    },
} as const;
export type LodPresetName = keyof typeof LOD_PRESETS;

/**
 * Smart auto-detection of the best LOD preset based on device capabilities.
 * Uses navigator.deviceMemory, hardwareConcurrency, and devicePixelRatio.
 */
export function resolveAutoPreset(isMobile: boolean): LodPresetName {
    const nav = navigator as Navigator & { deviceMemory?: number };
    const memory = nav.deviceMemory; // Chrome/Edge only (GB)
    const cores = navigator.hardwareConcurrency || 4;
    const dpr = window.devicePixelRatio || 1;

    let preset: LodPresetName;
    if (isMobile) {
        // High-end mobile: >= 6 GB RAM and >= 6 cores, or high DPR with many cores.
        //
        // iPads need their own clause. iPadOS Safari exposes no `deviceMemory`
        // (undefined) and reports devicePixelRatio 2, so NEITHER of the first two
        // clauses can ever be true on one — an M-series iPad Pro would resolve to
        // the phone-tier 'mobile' preset and its 2M splat budget. Before iPads
        // counted as mobile they took the desktop branch and ran unbudgeted, so
        // that would be a visible detail regression on every existing LOD scene.
        // A multi-core iPad gets 'mobile-max' instead: it still uses mobile splat
        // variants and touch UI, just not a phone's memory ceiling. Older 4-core
        // iPads keep the conservative budget.
        const highEnd = (memory != null && memory >= 6 && cores >= 6) ||
                        (memory == null && cores >= 6 && dpr >= 3) ||
                        (isDesktopUAiPad() && cores >= 6);
        preset = highEnd ? 'mobile-max' : 'mobile';
    } else {
        // High-end desktop: >= 8 GB RAM and >= 8 cores
        const highEnd = (memory != null && memory >= 8 && cores >= 8) ||
                        (memory == null && cores >= 8);
        preset = highEnd ? 'desktop-max' : 'desktop';
    }

    console.log('[SPLAT] Auto LOD preset resolved:', preset, { memory, cores, dpr, isMobile, iPad: isDesktopUAiPad() });
    return preset;
}
type SplatRendererChoice = NonNullable<LodSettingsConfig['renderer']>;
export type ResolvedLodSettings = {
    presetName: LodPresetName;
    range: [number, number];
    lodBaseDistance: number;
    lodMultiplier: number;
    splatBudget: number;
    renderer: SplatRendererChoice;
};

/** The LOD settings a device class uses: the scene's mobile settings on phones when it has them. */
export function getEffectiveLodSettings(config: { lodSettings?: LodSettingsConfig; mobileLodSettings?: LodSettingsConfig }, isMobile: boolean): LodSettingsConfig | undefined {
    return isMobile && config.mobileLodSettings ? config.mobileLodSettings : config.lodSettings;
}

export interface ResolveLodSettingsOptions {
    /**
     * Picks the preset for `auto` settings. Defaults to this device's own capabilities
     * (resolveAutoPreset). The editor passes a fixed choice: it describes what visitors
     * get, not what the creator's computer would pick.
     */
    autoPreset?: (isMobile: boolean) => LodPresetName;
    /** Log which preset was used. Default true. */
    log?: boolean;
}

/**
 * Resolve scene LOD settings (a preset, `auto` or custom values) to the numbers the viewer
 * applies, including the splat budget. Exported so the editor shows the same budgets.
 */
export function resolveLodSettings(
    lodSettings: LodSettingsConfig | undefined,
    isMobile: boolean,
    { autoPreset = resolveAutoPreset, log = true }: ResolveLodSettingsOptions = {},
): ResolvedLodSettings {
    let presetName: LodPresetName;
    let range: [number, number];
    let lodBaseDistance: number;
    let lodMultiplier: number;
    // `splatBudget` is the canonical memory dial: PlayCanvas reshuffles which
    // LOD each octree node uses to fit within it, without dropping nodes.
    // Each preset declares its own budget; users in `custom` mode can override.
    let presetSplatBudget = 0;
    // Renderer choice — every preset is 'auto' now; resolveEffectiveRenderer
    // maps that to raster-gpu at apply time. Scene-level renderer (e.g.
    // 'compute' from the Advanced Settings toggle) still wins over the preset.
    let presetRenderer: SplatRendererChoice = 'auto';

    if (lodSettings && lodSettings.preset !== 'auto') {
        if (lodSettings.preset === 'custom') {
            let fallbackBase = 15;
            let fallbackMult = 2;
            if (lodSettings.lodDistances && !lodSettings.lodBaseDistance) {
                fallbackBase = lodSettings.lodDistances[0] || 15;
                if (lodSettings.lodDistances[1] && lodSettings.lodDistances[0]) {
                    fallbackMult = lodSettings.lodDistances[1] / lodSettings.lodDistances[0];
                }
            }
            presetName = 'desktop';
            range = [
                lodSettings.lodRangeMin ?? 0,
                lodSettings.lodRangeMax ?? 10,
            ];
            lodBaseDistance = lodSettings.lodBaseDistance ?? fallbackBase;
            lodMultiplier = lodSettings.lodMultiplier ?? fallbackMult;
            if (log) console.log('[SPLAT] Using custom LOD settings from scene');
        } else {
            presetName = lodSettings.preset as LodPresetName;
            const preset = LOD_PRESETS[presetName];
            range = [...preset.range] as [number, number];
            lodBaseDistance = preset.lodBaseDistance;
            lodMultiplier = preset.lodMultiplier;
            presetSplatBudget = preset.splatBudget;
            presetRenderer = preset.renderer;
            if (log) console.log('[SPLAT] Using scene-configured LOD preset:', presetName);
        }
    } else {
        if (isMobile && lodSettings?.mobilePreset) {
            presetName = lodSettings.mobilePreset as LodPresetName;
            if (log) console.log('[SPLAT] Using scene mobile override preset:', presetName);
        } else if (!isMobile && lodSettings?.desktopPreset) {
            presetName = lodSettings.desktopPreset as LodPresetName;
            if (log) console.log('[SPLAT] Using scene desktop override preset:', presetName);
        } else {
            presetName = autoPreset(isMobile);
        }
        const preset = LOD_PRESETS[presetName];
        range = [...preset.range] as [number, number];
        lodBaseDistance = preset.lodBaseDistance;
        lodMultiplier = preset.lodMultiplier;
        presetSplatBudget = preset.splatBudget;
        presetRenderer = preset.renderer;
    }

    return {
        presetName,
        range,
        lodBaseDistance,
        lodMultiplier,
        splatBudget: lodSettings?.splatBudget ?? presetSplatBudget,
        renderer: lodSettings?.renderer ?? presetRenderer,
    };
}

// Helper to detect if URL points to LOD streaming format
function isLodStreamingFormat(url: string): boolean {
    const isLod = url.includes('lod-meta.json');
    if (isLod) {
        console.log('[SPLAT] Detected LOD streaming format (lod-meta.json)');
    }
    return isLod;
}
type LodManifestRef = {
    url?: string;
    filename?: string;
};
type LodManifestEntry = {
    file?: number;
    offset?: number;
    count?: number;
};
type LodManifestTreeNode = {
    bound?: {
        min?: number[];
        max?: number[];
    };
    lods?: Record<string, LodManifestEntry>;
    children?: LodManifestTreeNode[];
};
type LodManifest = {
    filenames?: string[];
    chunks?: Array<LodManifestRef | string | number | boolean | null>;
    environment?: string;
    lodLevels?: number;
    tree?: LodManifestTreeNode;
    storySplatCoordinateCorrection?: LodCoordinateCorrection;
    storySplatLcc2LodMode?: string;
    storySplatLcc2BoundsSpace?: string;
};

const LCC2_BOUNDS_SPACE_SOG_LOCAL = 'sog-local-v1';
const LCC2_EXPECTED_LOD_MODE = 'frontier-lod-bounds-v2';

function isAbsoluteResourceUrl(value: string): boolean {
    return /^[a-z][a-z0-9+.-]*:/i.test(value);
}

function resolvePathWithinDirectory(basePath: string, relativePath: string): string {
    const baseParts = basePath.split('/').filter(Boolean);
    baseParts.pop();
    const relParts = relativePath.replace(/\\/g, '/').replace(/^\/+/, '').split('/').filter(Boolean);
    for (const part of relParts) {
        if (part === '.') continue;
        if (part === '..') {
            baseParts.pop();
        } else {
            baseParts.push(part);
        }
    }
    return baseParts.join('/');
}

function getFirebaseStorageObjectFromUrl(url: string): { bucket: string; objectPath: string } | null {
    try {
        const parsed = new URL(url);
        const directMatch = parsed.pathname.match(/\/v0\/b\/([^/]+)\/o\/(.+)$/);
        if (directMatch) {
            return {
                bucket: decodeURIComponent(directMatch[1]),
                objectPath: decodeURIComponent(directMatch[2]),
            };
        }

        // CDN proxy format:
        //   /{bucket}/{encoded-object-path}
        // Older manifests with relative chunk paths need the full object
        // directory restored before PlayCanvas asks the proxy for /3_0/meta.json.
        const proxyParts = parsed.pathname.replace(/^\/+/, '').split('/');
        if (proxyParts.length >= 2 && proxyParts[0].includes('firebasestorage.app')) {
            return {
                bucket: decodeURIComponent(proxyParts[0]),
                objectPath: decodeURIComponent(proxyParts.slice(1).join('/')),
            };
        }
    } catch {
        return null;
    }
    return null;
}

function resolveLodManifestReference(lodMetaUrl: string, reference: string): string {
    if (!reference || isAbsoluteResourceUrl(reference)) return reference;
    const storageObject = getFirebaseStorageObjectFromUrl(lodMetaUrl);
    if (!storageObject) {
        try {
            return new URL(reference, lodMetaUrl).toString();
        } catch {
            return reference;
        }
    }
    const objectPath = resolvePathWithinDirectory(storageObject.objectPath, reference);
    return `https://firebasestorage.googleapis.com/v0/b/${storageObject.bucket}/o/${encodeURIComponent(objectPath)}?alt=media`;
}

function fillMissingLodManifestEntries(manifest: LodManifest): { leafCount: number; filledCount: number } {
    const lodLevels = manifest.lodLevels;
    if (!manifest.tree || typeof lodLevels !== 'number' || lodLevels <= 0) {
        return { leafCount: 0, filledCount: 0 };
    }

    let leafCount = 0;
    let filledCount = 0;
    const visit = (node: LodManifestTreeNode) => {
        if (node.lods) {
            leafCount += 1;
            const availableLevels = Object.keys(node.lods)
                .map(level => Number(level))
                .filter(level => Number.isInteger(level) && level >= 0 && node.lods?.[String(level)])
                .sort((a, b) => a - b);
            if (availableLevels.length === 0) return;

            for (let level = 0; level < lodLevels; level += 1) {
                if (node.lods[String(level)]) continue;
                const coarserLevel = availableLevels.find(candidate => candidate >= level);
                const fallbackLevel = coarserLevel ?? availableLevels[availableLevels.length - 1];
                const fallback = node.lods[String(fallbackLevel)];
                if (fallback) {
                    node.lods[String(level)] = { ...fallback };
                    filledCount += 1;
                }
            }
            return;
        }

        node.children?.forEach(visit);
    };

    visit(manifest.tree);
    return { leafCount, filledCount };
}

function transformLcc2LodBoundsToSogLocalSpace(node: LodManifestTreeNode): number {
    let transformedCount = 0;
    const bound = node.bound;
    if (
        bound?.min &&
        bound.max &&
        bound.min.length >= 3 &&
        bound.max.length >= 3 &&
        bound.min.every(value => Number.isFinite(value)) &&
        bound.max.every(value => Number.isFinite(value))
    ) {
        const [minX, minY, minZ] = bound.min;
        const [maxX, maxY, maxZ] = bound.max;
        bound.min = [-maxX, -maxY, minZ];
        bound.max = [-minX, -minY, maxZ];
        transformedCount += 1;
    }

    node.children?.forEach(child => {
        transformedCount += transformLcc2LodBoundsToSogLocalSpace(child);
    });
    return transformedCount;
}

/** Validate the eulerDegrees shape of a manifest's coordinate-correction block. */
function sanitizeLodCoordinateCorrection(correction: LodCoordinateCorrection | undefined): LodCoordinateCorrection | null {
    if (
        correction &&
        Array.isArray(correction.eulerDegrees) &&
        correction.eulerDegrees.length === 3 &&
        correction.eulerDegrees.every(value => typeof value === 'number' && Number.isFinite(value))
    ) {
        return correction;
    }
    return null;
}

type PreparedLodManifest = {
    url: string;
    cleanup: () => void;
    /** Coordinate correction read from the single manifest fetch (null when absent/invalid). */
    correction: LodCoordinateCorrection | null;
    /** False when the manifest couldn't be fetched/parsed here — callers fall back to their own fetch. */
    manifestLoaded: boolean;
};

/** @internal Exported for focused cancellation regression tests. */
export async function prepareLodManifestUrl(
    url: string,
    signal?: AbortSignal,
): Promise<PreparedLodManifest> {
    if (!isLodStreamingFormat(url)) {
        return { url, cleanup: () => undefined, correction: null, manifestLoaded: false };
    }

    try {
        const response = signal
            ? await fetch(url, { signal })
            : await fetch(url);
        if (signal?.aborted) throw new Error('LOD manifest preparation cancelled');
        if (!response.ok) {
            // Say exactly why, with the status: the engine's own failure is a generic "Asset load
            // error" and the viewer then falls back to the full single SOG, which on a phone is
            // the worst outcome. 413 = the storage proxy refused an oversized manifest.
            const hint = response.status === 413
                ? ' (storage proxy: manifest too large — regenerate/re-upload the LOD set)'
                : '';
            console.error(`[SPLAT] LOD manifest request failed: HTTP ${response.status} ${response.statusText}${hint}`, url);
            return { url, cleanup: () => undefined, correction: null, manifestLoaded: false };
        }
        const manifest = await response.json() as LodManifest;
        if (signal?.aborted) throw new Error('LOD manifest preparation cancelled');
        const isLcc2LodManifest = manifest.storySplatCoordinateCorrection?.sourceFormat === 'lcc2';
        if (
            isLcc2LodManifest
            && manifest.storySplatLcc2LodMode !== LCC2_EXPECTED_LOD_MODE
        ) {
            console.warn('[SPLAT] Loaded an older LCC2 LOD manifest. The viewer will apply compatibility rewrites, but regenerate the LCC2 stream so the manifest reports the current LOD mode.', {
                lodLevels: manifest.lodLevels,
                storySplatLcc2LodMode: manifest.storySplatLcc2LodMode,
                expectedLodMode: LCC2_EXPECTED_LOD_MODE,
                url,
            });
        }
        let didRewrite = false;
        let didFillLodGaps = false;
        let didRewriteLcc2Bounds = false;
        // Absolutize the rewrite base. Self-hosted exports load the manifest via a
        // RELATIVE lodMetaUrl ('./scenes/<f>/lod/lod-meta.json'); with a relative base
        // both new URL() calls in resolveLodManifestReference throw, chunk refs stay
        // relative, and the engine would join them against the blob: URL below (every
        // chunk fetch fails). response.url is the fetch-resolved absolute URL; fall
        // back to resolving against document.baseURI for environments that omit it.
        let manifestBaseUrl = response.url;
        if (!manifestBaseUrl) {
            try {
                manifestBaseUrl = new URL(url, typeof document !== 'undefined' ? document.baseURI : undefined).toString();
            } catch {
                manifestBaseUrl = url;
            }
        }
        const rewriteRef = (value: string): string => {
            if (!value || isAbsoluteResourceUrl(value)) {
                return value;
            }
            didRewrite = true;
            return resolveLodManifestReference(manifestBaseUrl, value);
        };

        if (Array.isArray(manifest.filenames)) {
            manifest.filenames = manifest.filenames.map(rewriteRef);
        }
        if (Array.isArray(manifest.chunks)) {
            manifest.chunks = manifest.chunks.map((rawChunk) => {
                // splat-transform / our own LodManifest type accept both
                // `{ url, filename }` objects and bare relative strings. The
                // earlier version of this map skipped string entries entirely;
                // when the manifest used `chunks: ["0_0/meta.json"]`, those
                // relative paths slipped through unrewritten and PlayCanvas
                // had to rely on its per-chunk base-URL resolution.
                if (typeof rawChunk === 'string') {
                    return rewriteRef(rawChunk);
                }
                if (!rawChunk || typeof rawChunk !== 'object') return rawChunk;
                const chunk = { ...rawChunk };
                if (typeof chunk.url === 'string') {
                    chunk.url = rewriteRef(chunk.url);
                }
                if (typeof chunk.filename === 'string') {
                    chunk.filename = rewriteRef(chunk.filename);
                }
                return chunk;
            });
        }
        if (typeof manifest.environment === 'string') {
            manifest.environment = rewriteRef(manifest.environment);
        }
        if (
            isLcc2LodManifest &&
            manifest.tree &&
            manifest.storySplatLcc2BoundsSpace !== LCC2_BOUNDS_SPACE_SOG_LOCAL
        ) {
            const transformedBounds = transformLcc2LodBoundsToSogLocalSpace(manifest.tree);
            if (transformedBounds > 0) {
                didRewriteLcc2Bounds = true;
                manifest.storySplatLcc2BoundsSpace = LCC2_BOUNDS_SPACE_SOG_LOCAL;
                console.warn('[SPLAT] Rewrote older LCC2 LOD bounds to SOG-local space for compatibility:', {
                    transformedBounds,
                    url,
                });
            }
        }
        const fillStats = fillMissingLodManifestEntries(manifest);
        if (fillStats.filledCount > 0) {
            didFillLodGaps = true;
            console.warn('[SPLAT] Filled missing LOD manifest slots for compatibility:', fillStats);
        }

        const correction = sanitizeLodCoordinateCorrection(manifest.storySplatCoordinateCorrection);

        // ALWAYS hand the engine a blob of the manifest we just parsed — even with no compat
        // rewrites. The manifest used to be downloaded + JSON-parsed 3x serially before chunk
        // streaming could start (here, readLodCoordinateCorrection, then the engine's octree
        // parser); serving the blob (the '#lod-meta.json' fragment keeps format detection
        // working) makes the engine's fetch an in-memory read. didRewrite=false guarantees all
        // refs are already absolute, so the blob base URL never participates in chunk resolution.
        const blobUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/json' })) + '#lod-meta.json';
        if (didRewrite || didFillLodGaps || didRewriteLcc2Bounds) {
            console.log('[SPLAT] Prepared LOD manifest compatibility rewrite:', {
                url,
                didRewriteReferences: didRewrite,
                didFillLodGaps,
                didRewriteLcc2Bounds,
            });
        }
        return {
            url: blobUrl,
            cleanup: () => URL.revokeObjectURL(blobUrl.split('#')[0]),
            correction,
            manifestLoaded: true,
        };
    } catch (error) {
        // Cancellation is part of the compare-generation lifecycle, not a
        // compatibility-rewrite failure. Preserve the abort so the caller can
        // stop before issuing the fallback metadata fetch or creating an Asset.
        if (signal?.aborted) throw error;
        console.warn('[SPLAT] Could not prepare LOD manifest compatibility rewrite:', error);
        return { url, cleanup: () => undefined, correction: null, manifestLoaded: false };
    }
}
// Helper to detect SOG format (LOD is detected separately by isLodStreamingFormat)
function isSogFormat(url: string): boolean {
    return url.includes('.sog');
}
/**
 * Create an embedded viewer from scene data
 */
export async function createViewer(container: HTMLElement, scene: SceneData, options: ViewerOptions = {}): Promise<ViewerInstance> {
    // Lazy mode creates the monitored renderer when the user clicks Start.
    if (options.lazyLoad ?? scene.uiOptions?.lazyLoad) return createViewerRuntime(container, scene, options);
    return createRecoverableViewer(container, async webgl => {
        let initializingApp: pc.Application | undefined;
        try {
            return await createViewerRuntime(container, scene, {
                ...options, disableWebGPU: options.disableWebGPU || webgl,
                __onAppCreated: app => { initializingApp = app; },
            });
        } catch (error) {
            try { initializingApp?.destroy(); } catch { /* failed graphics initialization */ }
            throw error;
        }
    }, options.onGraphicsRecovery);
}

async function createViewerRuntime(container: HTMLElement, scene: SceneData, options: ViewerOptions & {
    __onAppCreated?: (app: pc.Application) => void;
} = {}): Promise<ViewerInstance> {
    const _buildVersion: string = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : 'dev';
    console.log(`[StorySplat Viewer] BUILD ${_buildVersion}`);
    // Install lease interception before lazy thumbnails, splats, LOD manifests,
    // chunks, or media can issue their first request.
    let assetLeaseHandle: AssetLeaseHandle | null = options.assetLease
        ? createAssetLeaseManager(options.assetLease)
        : null;
    // Handle lazy loading - show thumbnail with start button before loading viewer
    // Check both explicit option and scene uiOptions (self-hosted exports set it in scene.json)
    if (options.lazyLoad ?? scene.uiOptions?.lazyLoad) {
        let actualInstance: ViewerInstance | null = null;
        let pendingButtonLabels: Partial<ViewerButtonLabels> | undefined;
        const deferredRendering = new DeferredOnDemandRendering();
        // Queue listeners registered before the user clicks Start. We replay
        // them directly onto the real viewer's emitter (as external when
        // registered via .on) so its listenerCount reflects real consumers.
        // Forwarding through an intermediate emitter would hide external
        // listeners from the inner, causing internal fallback handlers to
        // double-fire alongside external overrides.
        type PendingListener = { event: ViewerEvent, callback: EventCallback, kind: 'on' | 'onInternal' };
        const pendingListeners: PendingListener[] = [];
        // Determine thumbnail URL and button text
        const thumbnailUrl = options.lazyLoadThumbnail || scene.uiOptions?.lazyLoadThumbnailUrl || scene.thumbnailUrl;
        // Priority: explicit option > scene lazyLoadButtonText > scene buttonLabels.startExperience > default
        const buttonText = options.lazyLoadButtonText
            || scene.uiOptions?.lazyLoadButtonText
            || getButtonLabel(scene.uiOptions?.buttonLabels, 'startExperience');
        const uiColor = scene.uiColor || '#CC5833';
        console.log('[StorySplat Viewer] Lazy loading enabled, showing start button');
        // Create lazy load UI (supports image, video, and GIF thumbnails)
        createLazyLoadUI(container, {
            thumbnailUrl,
            thumbnailType: options.lazyLoadThumbnailType || scene.uiOptions?.lazyLoadThumbnailType,
            buttonText,
            uiColor,
            onStart: async () => {
                console.log('[StorySplat Viewer] User clicked start, initializing viewer...');
                // Create actual viewer without lazy loading
                assetLeaseHandle?.dispose();
                assetLeaseHandle = null;
                actualInstance = await createViewer(container, scene, { ...options, lazyLoad: false });
                deferredRendering.bind(actualInstance);
                // Apply any pending button labels that were set before the instance was created
                if (pendingButtonLabels) {
                    actualInstance.setButtonLabels(pendingButtonLabels);
                    pendingButtonLabels = undefined;
                }
                for (const { event, callback, kind } of pendingListeners) {
                    if (kind === 'onInternal' && actualInstance.onInternal) {
                        actualInstance.onInternal(event, callback);
                    } else {
                        actualInstance.on(event, callback);
                    }
                }
                pendingListeners.length = 0;
            }
        });
        // Return deferred ViewerInstance - methods delegate to actual instance when available
        const deferredInstance: ViewerInstance = {
            app: null as unknown as pc.Application,
            canvas: null as unknown as HTMLCanvasElement,
            goToWaypoint: (index) => actualInstance?.goToWaypoint(index),
            nextWaypoint: () => actualInstance?.nextWaypoint(),
            prevWaypoint: () => actualInstance?.prevWaypoint(),
            getCurrentWaypointIndex: () => actualInstance?.getCurrentWaypointIndex() ?? 0,
            getWaypointCount: () => actualInstance?.getWaypointCount() ?? 0,
            goToExtraWaypoint: (id, options) => actualInstance?.goToExtraWaypoint(id, options) ?? false,
            getExtraWaypoints: () => actualInstance?.getExtraWaypoints() ?? (normalizeExtraWaypoints(scene.extraWaypoints).map((wp) => ({ id: wp.id, name: wp.name || '' }))),
            setPosition: (x, y, z) => actualInstance?.setPosition(x, y, z),
            setRotation: (x, y, z) => actualInstance?.setRotation(x, y, z),
            getPosition: () => actualInstance?.getPosition() ?? { x: 0, y: 0, z: 0 },
            getRotation: () => actualInstance?.getRotation() ?? { x: 0, y: 0, z: 0 },
            getCameraPose: () => actualInstance?.getCameraPose()
                ?? { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0, w: 1 }, fov: 60 },
            holdCameraPose: (pose) => actualInstance?.holdCameraPose(pose),
            releaseCameraPose: () => actualInstance?.releaseCameraPose(),
            getPathPoseAtProgress: (progress) => actualInstance?.getPathPoseAtProgress(progress) ?? null,
            getPathRenderInfo: () => actualInstance?.getPathRenderInfo() ?? { waypointCount: 0, loopMode: 'none', defaultDurationSec: 10 },
            createPathPoseFollower: () => {
                if (!actualInstance) throw new Error('[StorySplat Viewer] Viewer has not started yet.');
                return actualInstance.createPathPoseFollower();
            },
            beginFrameCapture: (options) => {
                if (!actualInstance) throw new Error('[StorySplat Viewer] Viewer has not started yet.');
                return actualInstance.beginFrameCapture(options);
            },
            capturePhoto: (options) => {
                if (!actualInstance) return Promise.reject(new Error('[StorySplat Viewer] Viewer has not started yet.'));
                return actualInstance.capturePhoto(options);
            },
            captureWide: (targetAspect: number) => {
                if (!actualInstance) return Promise.reject(new Error('[StorySplat Viewer] Viewer has not started yet.'));
                return actualInstance.captureWide(targetAspect);
            },
            shareCurrentView: () => {
                if (!actualInstance) return Promise.reject(new Error('[StorySplat Viewer] Viewer has not started yet.'));
                return actualInstance.shareCurrentView();
            },
            setCutaway: (options) => actualInstance?.setCutaway(options),
            getCutaway: () => actualInstance?.getCutaway() ?? { available: false, enabled: false, level: 1, direction: 'above', collisionsSuspended: false },
            setPhotoMode: (enabled) => actualInstance?.setPhotoMode(enabled),
            getPhotoMode: () => actualInstance?.getPhotoMode() ?? false,
            setStereoMode: (format) => actualInstance?.setStereoMode(format),
            getStereoMode: () => actualInstance?.getStereoMode() ?? null,
            isStereoAvailable: () => actualInstance?.isStereoAvailable() ?? false,
            setStereoOptions: (options) => actualInstance?.setStereoOptions(options),
            takePhoto: () => {
                if (!actualInstance) return Promise.reject(new Error('[StorySplat Viewer] Viewer has not started yet.'));
                return actualInstance.takePhoto();
            },
            play: () => actualInstance?.play(),
            pause: () => actualInstance?.pause(),
            stop: () => actualInstance?.stop(),
            isPlaying: () => actualInstance?.isPlaying() ?? false,
            playEntityAnimation: (id) => actualInstance?.playEntityAnimation(id),
            pauseEntityAnimation: (id) => actualInstance?.pauseEntityAnimation(id),
            resetEntityAnimation: (id) => actualInstance?.resetEntityAnimation(id),
            getEntityAnimations: () => actualInstance?.getEntityAnimations() ?? [],
            restartEntityAnimation: (id) => actualInstance?.restartEntityAnimation(id),
            seekEntityAnimation: (id, seconds) => actualInstance?.seekEntityAnimation(id, seconds),
            isEntityAnimationPlaying: (id) => actualInstance?.isEntityAnimationPlaying(id) ?? false,
            isFrameSequencePlaying: () => actualInstance?.isFrameSequencePlaying() ?? false,
            // Mode
            setCameraMode: (mode) => actualInstance?.setCameraMode(mode),
            getCameraMode: () => actualInstance?.getCameraMode() ?? 'tour',
            togglePerfHud: (visible?: boolean) => actualInstance?.togglePerfHud?.(visible),
            setPerformanceMode: (enabled: boolean) => actualInstance?.setPerformanceMode?.(enabled),
            requestRender: (reason?: string) => actualInstance?.requestRender?.(reason),
            setOnDemandRendering: (enabled: boolean) => deferredRendering.set(enabled),
            getOnDemandRendering: () => actualInstance?.getOnDemandRendering?.() ?? false,
            getOnDemandRenderStats: () => actualInstance?.getOnDemandRenderStats?.() ?? null,
            getShadowDebugInfo: () => actualInstance?.getShadowDebugInfo?.() ?? null,
            getCloudDebugInfo: () => actualInstance?.getCloudDebugInfo?.() ?? null,
            getWindDebugInfo: () => actualInstance?.getWindDebugInfo?.() ?? null,
            setWindClock: (seconds: number | null) => actualInstance?.setWindClock?.(seconds),
            getPerformanceMode: () => actualInstance?.getPerformanceMode?.() ?? false,
            resumeTourAtCurrentCamera: () => actualInstance?.resumeTourAtCurrentCamera(),
            setExploreMode: (mode) => actualInstance?.setExploreMode(mode),
            setExploreModeSettings: (settings) => {
                if (actualInstance) {
                    actualInstance.setExploreModeSettings(settings);
                    return;
                }
                if (settings.enabledExploreModes !== undefined) {
                    scene.enabledExploreModes = settings.enabledExploreModes ?? undefined;
                }
                if (settings.initialSplatExploreMode !== undefined) {
                    scene.initialSplatExploreMode = settings.initialSplatExploreMode ?? undefined;
                }
            },
            // Splat API
            goToOriginalSplat: () => actualInstance?.goToOriginalSplat(),
            goToSplat: async (url) => actualInstance?.goToSplat(url),
            getCurrentSplatUrl: () => actualInstance?.getCurrentSplatUrl() ?? '',
            isShowingOriginalSplat: () => actualInstance?.isShowingOriginalSplat() ?? true,
            getAdditionalSplats: () => actualInstance?.getAdditionalSplats() ?? [],
            // Compare mode
            isCompareMode: () => actualInstance?.isCompareMode() ?? false,
            getComparePosition: () => actualInstance?.getComparePosition() ?? 0.5,
            setComparePosition: (pos) => actualInstance?.setComparePosition(pos),
            // Progress
            setProgress: (progress, animate) => actualInstance?.setProgress(progress, animate),
            getProgress: () => actualInstance?.getProgress() ?? 0,
            setWaypointEasing: (mode) => {
                if (actualInstance) actualInstance.setWaypointEasing(mode);
                else scene.waypointEasing = mode;
            },
            setPathInterpolation: (mode) => {
                if (actualInstance) actualInstance.setPathInterpolation(mode);
                else scene.pathInterpolation = mode;
            },
            // Audio
            muteAll: () => actualInstance?.muteAll(),
            unmuteAll: () => actualInstance?.unmuteAll(),
            isMuted: () => actualInstance?.isMuted() ?? false,
            // Hotspot interaction
            getHotspots: () => actualInstance?.getHotspots() ?? [],
            triggerHotspot: (id) => actualInstance?.triggerHotspot(id),
            closeHotspot: () => actualInstance?.closeHotspot(),
            setHotspotFolderVisible: (id, visible) => actualInstance?.setHotspotFolderVisible(id, visible) ?? false,
            showOnlyHotspotFolder: (id) => actualInstance?.showOnlyHotspotFolder(id) ?? false,
            getVisibleHotspotFolders: () => actualInstance?.getVisibleHotspotFolders() ?? [],
            getHotspotFolders: () => actualInstance?.getHotspotFolders() ?? [],
            getDirectory: () => actualInstance?.getDirectory() ?? { enabled: false, available: false, open: false, title: '', hotspots: [], folders: [], views: [] },
            openDirectory: () => actualInstance?.openDirectory() ?? false,
            closeDirectory: () => actualInstance?.closeDirectory(),
            runLinkedAction: (action) => actualInstance?.runLinkedAction(action) ?? Promise.resolve(),
            setRegionVisible: (id, visible) => actualInstance?.setRegionVisible(id, visible) ?? false,
            showOnlyRegion: (id) => actualInstance?.showOnlyRegion(id) ?? false,
            getVisibleRegions: () => actualInstance?.getVisibleRegions() ?? [],
            getRegions: () => actualInstance?.getRegions() ?? [],
            setRegionActive: (kind, id, active) => actualInstance?.setRegionActive(kind, id, active) ?? false,
            getActiveRegions: () => actualInstance?.getActiveRegions() ?? { focus: [], haze: [] },
            setLayerGroupVisible: (id, visible) => actualInstance?.setLayerGroupVisible(id, visible) ?? false,
            showOnlyLayerGroup: (id) => actualInstance?.showOnlyLayerGroup(id) ?? false,
            setAllLayerGroupsVisible: (visible) => actualInstance?.setAllLayerGroupsVisible(visible),
            getVisibleLayerGroups: () => actualInstance?.getVisibleLayerGroups() ?? [],
            getLayerGroups: () => actualInstance?.getLayerGroups() ?? [],
            getMeasurementSets: () => actualInstance?.getMeasurementSets() ?? [],
            setMeasurementSetVisible: (id, visible) => actualInstance?.setMeasurementSetVisible(id, visible) ?? false,
            setAllMeasurementSetsVisible: (visible) => actualInstance?.setAllMeasurementSetsVisible(visible),
            showOverlay: (id) => actualInstance?.showOverlay(id) ?? false,
            hideOverlay: (id) => actualInstance?.hideOverlay(id) ?? false,
            getVisibleOverlays: () => actualInstance?.getVisibleOverlays() ?? [],
            destroy: () => {
                if (actualInstance) {
                    actualInstance.destroy();
                }
                else {
                    assetLeaseHandle?.dispose();
                    assetLeaseHandle = null;
                    // Clean up lazy load UI if viewer wasn't started
                    container.querySelectorAll('.storysplat-lazy-load-container, .storysplat-viewer-container').forEach(el => el.remove());
                    container.classList.remove('storysplat-viewer-container');
                }
            },
            resize: () => actualInstance?.resize(),
            navigateToScene: async (sceneId) => {
                if (actualInstance) {
                    return actualInstance.navigateToScene(sceneId);
                }
            },
            setButtonLabels: (labels) => {
                if (actualInstance) {
                    actualInstance.setButtonLabels(labels);
                }
                else {
                    pendingButtonLabels = { ...pendingButtonLabels, ...labels };
                }
            },
            // Panorama mode
            setPanoramaMode: (enabled) => actualInstance?.setPanoramaMode(enabled),
            isPanoramaMode: () => actualInstance?.isPanoramaMode() ?? false,
            on: (event, callback) => {
                if (actualInstance) actualInstance.on(event, callback);
                else pendingListeners.push({ event, callback, kind: 'on' });
            },
            off: (event, callback) => {
                if (actualInstance) {
                    actualInstance.off(event, callback);
                } else {
                    const idx = pendingListeners.findIndex(l => l.event === event && l.callback === callback);
                    if (idx !== -1) pendingListeners.splice(idx, 1);
                }
            },
            onInternal: (event, callback) => {
                if (actualInstance && actualInstance.onInternal) actualInstance.onInternal(event, callback);
                else pendingListeners.push({ event, callback, kind: 'onInternal' });
            }
        };
        return deferredInstance;
    }
    const events = new EventEmitter();
    const viewerOptions = options as ViewerOptionsWithSessionRecorder;
    let analyticsHandle: SessionRecorderHandle | null = viewerOptions.__sessionRecorderHandle ?? null;
    let gaHandle: GoogleAnalyticsHandle | null = viewerOptions.__googleAnalyticsHandle ?? null;
    // Streaming bandwidth meter. Created before any asset request goes out so
    // the primary splat download is counted too. Each viewer instance gets its
    // own reporter scoped to its own sceneId — portal-target scenes attribute
    // their bytes to the target scene, not the entry scene.
    // `isStorySplatHostedUrl` is a hoisted function declaration below.
    let bandwidthReporter: BandwidthReporterHandle | null = null;
    // Self-hosted, downloadable generated HTML sets `onlyReportBandwidthOnStorySplatHost`
    // so a copy opened on a customer domain / file:// doesn't phone home (matching the
    // injected view-ping script's guard). Live embeds (createViewerFromSceneId) leave it
    // unset and report from any host, since that egress is real and billable.
    const restrictAnalyticsToStorySplatHost = !!(
        options.analytics?.onlyReportAnalyticsOnStorySplatHost
        || options.analytics?.onlyReportBandwidthOnStorySplatHost
    );
    if (options.analytics && options.disableAnalytics !== true && typeof window !== 'undefined'
        && (!restrictAnalyticsToStorySplatHost || isStorySplatHostedPage())) {
        bandwidthReporter = createBandwidthReporter({
            baseUrl: options.analytics.baseUrl ?? 'https://discover.storysplat.com',
            sceneId: options.analytics.sceneId,
            ownerId: options.analytics.ownerId,
            isStorySplatHostedUrl: (url: string) => isStorySplatHostedUrl(url),
        });
    }
    // Emit 'loaded' with meter-backed bandwidth data. When the reporter is
    // active it (not the event consumer) owns posting bandwidth beacons —
    // `bandwidthSelfReported: true` tells consumers (createViewerFromSceneId,
    // the v2 HTML tracking script, StorySplatV2Viewer) to skip their legacy
    // one-shot report so bytes aren't double-counted.
    const emitSceneLoaded = (data: ViewerLoadedData & { url?: string }): void => {
        if (bandwidthReporter) {
            data.bandwidthUsed = bandwidthReporter.getTotalBytes();
            data.bandwidthSelfReported = true;
            bandwidthReporter.markLoaded();
        }
        events.emit('loaded', data);
    };
    // Style isolation: reset inherited styles from parent page unless opted out
    if (!options.allowParentStyles) {
        const originalWidth = container.style.width;
        const originalHeight = container.style.height;
        container.style.all = 'initial';
        container.style.position = 'relative';
        container.style.display = 'block';
        container.style.width = originalWidth || '100%';
        container.style.height = originalHeight || '100%';
        container.style.overflow = 'hidden';
        container.style.fontFamily = 'system-ui, -apple-system, sans-serif';
    }
    // Extract UI options from config (respecting JSON settings)
    const isEditorMode = options.editor === true;
    // Mark container so showErrorPopup can defensively skip editor containers
    if (isEditorMode) {
        container.setAttribute('data-storysplat-editor', 'true');
    }
    // Set up internal error handler to show user-friendly error popup
    // In editor mode, only log — the editor handles its own loading/error states
    events.on('error', (err: Error) => {
        console.error('[StorySplat Viewer] Error event:', err.message);
        if (!isEditorMode) {
            showErrorPopup(container, err.message);
        }
    });
    const config = exportPropsToViewerConfig(transformSceneToExportProps(scene));
    // Keyframes that targeted focus regions, the scene-wide focus look or annotation layers now
    // target the regions those convert to (regionsFromLegacy keeps their ids).
    config.entityAnimations = migrateRegionAnimations(config.entityAnimations, config.postProcessing?.focusRegions);
    /** From the editor's first setRegions on, the legacy focus / annotation fields are only derived data. */
    let regionsAuthoritative = false;
    /**
     * The regions this scene draws: `regions` when present (a viewer that knows Regions reads only
     * that), else the focus regions and annotation layers of a scene saved before Regions, converted.
     */
    const regionsInUse = (): RegionsConfig | undefined =>
        config.regions ?? (regionsAuthoritative ? undefined : regionsFromLegacy(config.postProcessing?.focusRegions, config.annotationLayers, config.entityAnimations));
    // Measurement sets (utils/measurementSets.ts): which sets' saved measurements show, every set at
    // first. Null without sets. The measurement tool draws by it; the API and, when the creator lets
    // visitors pick sets, the Measure panel's tick boxes switch it.
    const measurementSetsState: MeasurementSetsState | null = sceneMeasurementSets(config);
    const measurementSetPicker = offersMeasurementSetPicker(config, measurementSetsState);
    // The creator can start every set unticked: nothing saved shows until a visitor ticks one. Not in
    // the editor, where the Measure panel's eyes decide what the viewport shows.
    if (!isEditorMode && measurementSetsStartHidden(config, measurementSetsState)) measurementSetsState.setAllVisible(false);
    if (measurementSetsState) {
        const sets = measurementSetsState;
        sets.onChange(() => events.emit('measurementSetsChange', { visibleIds: sets.visibleIds(), sets: sets.list() }));
    }
    let effectiveAudioReactiveConfig = config.audioReactive;
    console.log('[StorySplat Viewer] Creating viewer with config:', config);
    console.log('[StorySplat Viewer] Scale config:', config.scale);
    console.log('[StorySplat Viewer] Raw scene data:', { splatScale: scene.splatScale, scale: scene.scale });
    let editorEditingHotspotId: string | null = null; // Set by editor to suppress popups for the hotspot being edited
    const showUI = isEditorMode ? (options.editorSkipUI ? false : options.showUI !== false) : (options.showUI !== false);
    const uiColor = config.uiColor || '#CC5833';
    const uiOpts = config.uiOptions || {};
    // Resolve template type: explicit option > scene uiOptions > default minimal
    const templateType = options.template || uiOpts.uiType || 'minimal';
    // Mutable button labels for runtime updates via setButtonLabels()
    let currentButtonLabels: ViewerButtonLabels | undefined = uiOpts.buttonLabels;
    // Map camera modes from export format to UI format
    const mapCameraMode = (mode: string) => {
        if (mode === 'first-person')
            return 'tour';
        if (mode === 'drone')
            return 'explore';
        return mode;
    };
    // Check if collision data is available for walk mode (primitives OR voxel octree)
    const hasCollisionMeshesForWalk = (config.collisionMeshesData && config.collisionMeshesData.length > 0) || !!config.voxelCollisionUrl || !!config.collisionMeshUrl;
    // Backwards compat: if allowedCameraModes was never set, include 'walk' when collision data exists.
    // When the user has explicitly set allowedCameraModes, respect their choice.
    const allowedModesExplicit = Array.isArray(config.allowedCameraModes);
    let allowedModes = (config.allowedCameraModes || ['orbit', 'first-person', 'drone'])
        .map(mapCameraMode)
        .filter((v, i, a) => a.indexOf(v) === i); // unique
    if (!allowedModesExplicit && hasCollisionMeshesForWalk && !allowedModes.includes('walk')) {
        allowedModes.push('walk');
    }
    // Remove 'walk' if no collision data (can't walk without collisions)
    if (!hasCollisionMeshesForWalk && allowedModes.includes('walk')) {
        allowedModes = allowedModes.filter(m => m !== 'walk');
    }
    let defaultMode = mapCameraMode(config.defaultCameraMode || 'orbit');
    // If default mode was removed (e.g. walk without collision data), fall back to first allowed mode
    if (!allowedModes.includes(defaultMode)) {
        defaultMode = allowedModes[0] || 'tour';
    }
    type ExploreSubMode = 'orbit' | 'fly' | 'walk';
    const normalizeExploreModes = (modes?: Array<ExploreSubMode> | null): Array<ExploreSubMode> => {
        const rawModes = (modes?.length ? modes : (['orbit', 'fly'] as Array<ExploreSubMode>))
            .filter((mode): mode is ExploreSubMode => mode === 'orbit' || mode === 'fly' || mode === 'walk');
        const unique = rawModes.filter((mode, index, list) => list.indexOf(mode) === index);
        return unique.length > 0 ? unique : (['orbit', 'fly'] as Array<ExploreSubMode>);
    };
    let enabledExploreModes: Array<ExploreSubMode> = normalizeExploreModes(config.enabledExploreModes);
    const isExploreSubModeEnabled = (mode: ExploreSubMode): boolean => enabledExploreModes.includes(mode);
    const getEnabledExploreSubMode = (mode?: ExploreSubMode): ExploreSubMode => {
        if (mode && isExploreSubModeEnabled(mode)) return mode;
        // Prefer fly, then orbit, then walk — walk last since it needs collision data.
        if (enabledExploreModes.includes('fly')) return 'fly';
        if (enabledExploreModes.includes('orbit')) return 'orbit';
        return enabledExploreModes[0];
    };
    // CameraControls only drives orbit/fly (walk is the CharacterController). Map a
    // resolved sub-mode to the nearest CameraControls mode for the orbit/fly paths.
    const toCameraControlsSubMode = (mode: ExploreSubMode): 'orbit' | 'fly' =>
        mode === 'walk' ? (enabledExploreModes.includes('fly') ? 'fly' : 'orbit') : mode;
    // Detect if scene has any audio sources (waypoint audio, hotspot audio, audio emitters, narration)
    const hasNarration = !!(config.narrationTrack?.enabled && config.narrationTrack?.audioUrl);
    const sceneHasAudio = !!(
        hasNarration ||
        config.audioEmitters?.length ||
        config.hotspots?.some(h => h.audioUrl) ||
        config.hotspots?.some(h => (h.type === 'video' || (h.type === 'stack' && markerStackHasVideo(h.markerStack))) && h.videoMuted !== true) ||
        config.waypoints?.some(w => w.interactions?.some(i => i.type === 'audio')) ||
        // Cross-scene ambient music keeps the mute button even when the scene's own JSON has no audio:
        // either this scene STARTS the persistent bed (entry scene), or a bed is ALREADY playing from
        // a scene we teleported in from (the crossSceneAudio singleton survives viewer recreation).
        (config.crossSceneAudio?.enabled && !!config.crossSceneAudio?.url) ||
        crossSceneAudio.isActive()
    );
    let uiElements: UIElements = {};
    // "Ask this scene" visitor guide (uiOptions.sceneGuide): published scenes with an id only,
    // never the editor. Mounted with the other panels below (mountSceneGuide).
    const sceneGuide = resolveSceneGuide(uiOpts.sceneGuide, {
        sceneId: scene.sceneId || options.analytics?.sceneId || options.assetLease?.sceneId,
        isEditorMode,
    });
    // Create UI elements FIRST (to show preloader while loading)
    if (showUI) {
        uiElements = createUIElements(container, config, {
            uiColor,
            showScrollControls: !uiOpts.hideNavigator && config.waypoints && config.waypoints.length > 0,
            showModeToggle: allowedModes.length > 1,
            showFullscreenButton: !uiOpts.hideFullscreenButton,
            showHelpButton: !uiOpts.hideHelpButton && !uiOpts.hideInfoButton,
            showMuteButton: !uiOpts.hideMuteButton && sceneHasAudio,
            // Mic-authored reactivity gets a deliberate viewer control; scene-audio reactivity
            // remains editor-authored and has no viewer toggle.
            showMicrophoneButton: !isEditorMode && !!(
                effectiveAudioReactiveConfig?.enabled && effectiveAudioReactiveConfig.source === 'mic'
            ),
            showPreloader: true,
            allowedCameraModes: allowedModes,
            enabledExploreModes,
            defaultCameraMode: defaultMode,
            buttonLabels: currentButtonLabels,
            // Whitelabeling options for preloader
            customPreloaderLogoUrl: uiOpts.customPreloaderLogoUrl,
            preloaderLogoSize: uiOpts.preloaderLogoSize,
            // Whitelabeling options for watermark
            hideWatermark: uiOpts.hideWatermark,
            watermarkText: uiOpts.watermarkText,
            watermarkLink: uiOpts.watermarkLink,
            watermarkImageUrl: uiOpts.watermarkImageUrl,
            watermarkImageHeight: uiOpts.watermarkImageHeight,
            // Bottom-left scene title overlay + "Current Space" menu header.
            // Prefer the creator-facing sceneTitle, falling back to the scene name.
            sceneTitle: scene.sceneTitle || scene.name,
            showSceneTitle: uiOpts.showSceneTitle,
            sceneTitleShowOnHover: uiOpts.sceneTitleShowOnHover,
            movementKeyDisplay: uiOpts.movementKeyDisplay,
            sceneId: scene.sceneId || options.analytics?.sceneId,
            // Waypoint list dropdown
            showWaypointList: uiOpts.showWaypointList,
            // Layout template
            template: templateType,
            // Debug mode (FPS counter)
            debugMode: uiOpts.debugMode,
            // Hide progress text
            hideProgressText: uiOpts.hideProgressText,
            // Custom viewer theme
            viewerTheme: uiOpts.viewerTheme,
            // Relighting toggle (shown only when relighting is enabled, allowViewerToggle is true, and lights exist)
            showRelightingToggle: !!(config.splatRelighting?.enabled !== false && config.splatRelighting?.allowViewerToggle && config.lights && config.lights.length > 0),
            // Fisheye/tiny-planet toggle — feature-flagged off while we refine the effect.
            // Existing scenes with uiOptions.showFisheyeButton=true are intentionally ignored.
            showFisheyeButton: false,
            // Scene navigation menu (auto-enabled when portals or custom links exist, unless explicitly hidden)
            showSceneMenu: !uiOpts.hideSceneMenu && ((config.portals && config.portals.length > 0) || (config.uiOptions?.sceneMenuLinks && config.uiOptions.sceneMenuLinks.length > 0)) ? true : false,
            // Custom external links for scene menu
            sceneMenuLinks: config.uiOptions?.sceneMenuLinks,
            // Scene menu layout style (classic dropdown vs full sidebar pullout)
            sceneMenuStyle: config.uiOptions?.sceneMenuStyle ?? 'classic',
            // Scene menu: start category folders collapsed + optional title/logo
            startCategoriesCollapsed: config.uiOptions?.startCategoriesCollapsed,
            // Per-connection preview thumbnails in the scene menu — OFF by default (opt-in).
            showSceneMenuPreviewImages: config.uiOptions?.showSceneMenuPreviewImages ?? false,
            // Persisted slot for the synthesized "Current Space" item (stable order/folder).
            currentSpaceMenuPath: config.uiOptions?.currentSpaceMenuPath,
            currentSpaceMenuOrder: config.uiOptions?.currentSpaceMenuOrder,
            // Explicit folder render order.
            sceneMenuFolderOrder: config.uiOptions?.sceneMenuFolderOrder,
            menuTitle: config.uiOptions?.menuTitle,
            menuTitleImageUrl: config.uiOptions?.menuTitleImageUrl,
            // Measurement mode
            measurementsEnabled: config.uiOptions?.measurementsEnabled,
            sceneScale: config.uiOptions?.sceneScale,
            sceneScaleUnit: config.uiOptions?.sceneScaleUnit,
            measurementColor: config.uiOptions?.measurementColor,
            // Layers menu: when a region is in it (and the menu isn't switched off).
            layersTitle: (() => {
                const resolved = resolveRegions(regionsInUse());
                return resolved?.menu.show && resolved.regions.some((r) => r.inLayersMenu) ? resolved.menu.title : undefined;
            })(),
            cutawayEnabled: uiOpts.cutawayEnabled === true,
            // Opt-in viewer button; never when the creator turned stereo off. The compare slider
            // clips by screen position, which has no meaning per eye.
            showStereoButton: uiOpts.showStereoButton === true && uiOpts.stereoEnabled !== false
                && !(config.splatSwapMode === 'compare' && (config.additionalSplats?.length ?? 0) > 0),
            showShareButton: !uiOpts.hideShareButton,
            showPhotoButton: uiOpts.hidePhotoButton !== true,
            sceneGuideName: sceneGuide?.name,
        });
        // Use the same UA-based device classification as the mobile Explorer
        // joystick, plus desktop-UA iPads: a touch-only iPad must get the
        // tap-and-drag onboarding, not the mouse/keyboard panel. Viewport width
        // alone must never select mobile onboarding.
        uiElements.tourLookHint?.classList.toggle('storysplat-tour-look-hint--mobile', isMobileDevice() || config.forceMobilePreview === true);
        // Setup WASD+QE key highlight and click interaction (desktop only). Chain the returned
        // cleanup into uiElements._cleanup (both destroy paths call it) — discarding it leaked the
        // document keydown/keyup listeners + wasdHint DOM per viewer instance across portal hops.
        const wasdCleanup = setupWasdKeyInteraction(uiElements);
        if (wasdCleanup) {
            const prevCleanup = uiElements._cleanup;
            uiElements._cleanup = () => {
                if (prevCleanup) prevCleanup();
                wasdCleanup();
            };
        }
    }
    // "Protect media" (uiOptions.protectMedia): no download / save affordances for uploaded photos,
    // videos and PDFs in popups, lightboxes, overlays and menu media (mediaProtection.ts). Not in
    // the editor, where it would only get in the creator's way.
    if (uiOpts.protectMedia === true && !isEditorMode) {
        const undoMediaProtection = installMediaProtection(container);
        const prevCleanup = uiElements._cleanup;
        uiElements._cleanup = () => {
            if (prevCleanup) prevCleanup();
            undoMediaProtection();
        };
    }
    // Create canvas
    let canvas = document.createElement('canvas');
    canvas.id = 'storysplat-viewer-canvas';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    // Default background so the canvas isn't transparent before the gsplat
    // pipeline clears it each frame. The camera's clearColor will override
    // this once rendering starts; this is just a safety net for the gap
    // between mount and first frame, and for any WebGPU canvas alphaMode
    // configuration that lets the underlying page show through.
    canvas.style.backgroundColor = '#7D7D7D';
    container.appendChild(canvas);
    // Initialize PlayCanvas graphics device. Request WebGPU first because it
    // unlocks the `compute` gsplat renderer. Mirror and water ShaderMaterials
    // ship native WGSL counterparts, so they should work on both WebGPU and
    // WebGL2. If WebGPU device creation itself fails (for example Windows
    // D3D12 device-removed/device-hung errors), retry WebGL2 explicitly instead
    // of letting the whole viewer fail.
    let app!: pc.Application;
    let graphicsDevice!: pc.GraphicsDevice;
    const forcedPerformanceMode = creatorForcedPerformanceMode(config.uiOptions, isEditorMode);
    let performanceMode = readInitialPerformanceMode(forcedPerformanceMode.mobile, forcedPerformanceMode.desktop);
    // On-demand rendering (see OnDemandRenderScheduler.ts). Armed after the initial reveal;
    // never in the editor. `?render=always` disables it, `?render=ondemand` forces it on even
    // for scenes with animated features (testing only).
    const renderUrlOverride = (() => { try { return new URLSearchParams(window.location.search).get('render'); } catch { return null; } })();
    let onDemandRenderingEnabled = resolveOnDemandRendering(isEditorMode, config.uiOptions?.onDemandRendering, renderUrlOverride);
    const onDemandRenderingForced = renderUrlOverride === 'ondemand';
    let onDemandRenderingActive = false;
    let onDemandRenderingArmed = false; // initial reveal done → allowed to start
    let onDemandContinuousReasons: string[] = [];
    const renderScheduler = new OnDemandRenderScheduler();
    const cameraPoseTracker = new CameraPoseTracker();
    const resolvePixelRatioCap = (): number => {
        const base = getPerfOverrides().maxPixelRatio ?? (usesMobileRenderProfile() ? MOBILE_MAX_PIXEL_RATIO : DESKTOP_MAX_PIXEL_RATIO);
        const cap = performanceMode ? Math.max(PERF_MODE_MIN_PIXEL_RATIO, base * PERF_MODE_PIXEL_RATIO_SCALE) : base;
        return Math.min(window.devicePixelRatio || 1, cap);
    };
    // Water renders whenever a scene has water planes; `enableWaterEffects: false` still turns it off.
    const waterEffectsEnabled = config.enableWaterEffects !== false;
    const webGpuDeviceTypes = [pc.DEVICETYPE_WEBGPU, pc.DEVICETYPE_WEBGL2];
    const webGlDeviceTypes = [pc.DEVICETYPE_WEBGL2];
    const disableWebGPU = options.disableWebGPU === true || config.uiOptions?.disableWebGPU === true;
    // XR needs WebGL on devices that can actually start it (see xrNeedsWebGL).
    // VR on desktops and phones is asked for (?vr=1, the visitor's VR mode, the host's vr option).
    const vrRequest = typeof options.vr === 'boolean' ? options.vr : readVrRequest();
    const xrWantsWebGL = !disableWebGPU && await xrNeedsWebGL(config, vrRequest);
    const createGraphicsDevice = async (deviceTypes: string[]): Promise<pc.GraphicsDevice> => {
        const deviceOptions = { antialias: false, powerPreference: 'high-performance' as const };
        let device: pc.GraphicsDevice;
        if (deviceTypes[0] === pc.DEVICETYPE_WEBGPU) {
            const gpu = new pc.WebgpuGraphicsDevice(canvas, deviceOptions);
            try {
                device = await initializeWebGPUDevice(gpu);
                // Before any splat shader compiles: the GPU-sort projector over-shrinks oversized
                // splats into streaks unless patched (utils/gsplatProjectorCapFix).
                installGsplatProjectorCapFix(device);
            } catch (error) {
                // A canvas already bound to WebGPU cannot acquire WebGL2.
                const replacement = canvas.cloneNode(false) as HTMLCanvasElement;
                canvas.replaceWith(replacement);
                canvas = replacement;
                throw error;
            }
        } else {
            device = await pc.createGraphicsDevice(canvas, { ...deviceOptions, deviceTypes });
        }
        // Cap device pixel ratio (#103 perf): DPR-3 phones would rasterize 9x the fragments of the
        // engine default for soft splat content that doesn't need it — capping cuts shaded pixels
        // ~2.25x on those devices (and backbuffer/post-effect RT memory with it). Applied inside the
        // helper so both the WebGPU and WebGL2-fallback devices (#107) get the cap.
        if (!device.isWebGPU && !device.isWebGL2) {
            device.destroy();
            throw new Error('No usable WebGPU or WebGL2 graphics device');
        }
        device.maxPixelRatio = resolvePixelRatioCap();
        if (performanceMode) console.log('[StorySplat Viewer] Performance mode ON at startup: pixel ratio cap', device.maxPixelRatio);
        return device;
    };
    try {
        if (disableWebGPU || xrWantsWebGL) {
            console.log(xrWantsWebGL
                ? '[StorySplat Viewer] This scene offers XR on this device, which needs WebGL2: using WebGL2'
                : '[StorySplat Viewer] WebGPU disabled by scene option, using WebGL2');
            graphicsDevice = await createGraphicsDevice(webGlDeviceTypes);
        } else {
            try {
                graphicsDevice = await createGraphicsDevice(webGpuDeviceTypes);
            } catch (webGpuError) {
                rememberGraphicsFailure();
                console.warn('[StorySplat Viewer] WebGPU graphics initialization failed, retrying with WebGL2:', webGpuError);
                graphicsDevice = await createGraphicsDevice(webGlDeviceTypes);
            }
        }
        const deviceInfo = graphicsDevice as pc.GraphicsDevice & GraphicsDeviceRuntimeInfo;
        const deviceKind = deviceInfo.isWebGPU ? 'WebGPU'
            : deviceInfo.isWebGL2 ? 'WebGL2'
            : deviceInfo.isWebGL1 ? 'WebGL1' : 'unknown';
        console.log(`[StorySplat Viewer] Graphics device initialized: ${deviceKind}, DPR: ${graphicsDevice.maxPixelRatio}`);
        app = new pc.Application(canvas, {
            graphicsDevice,
            mouse: new pc.Mouse(canvas),
            touch: new pc.TouchDevice(canvas),
            keyboard: new pc.Keyboard(window),
        });
    } catch (deviceError) {
        try { graphicsDevice?.destroy(); } catch { /* partially initialized */ }
        console.error('[StorySplat Viewer] Graphics initialization failed:', deviceError);
        const errorDiv = document.createElement('div');
        errorDiv.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#fff;background:rgba(0,0,0,0.8);padding:20px;border-radius:10px;text-align:center;font-family:sans-serif;';
        const heading = document.createElement('h3');
        heading.style.cssText = 'margin:0 0 10px 0;';
        heading.textContent = getButtonLabel(currentButtonLabels, 'errorWebGLTitle');
        const message = document.createElement('p');
        message.style.cssText = 'margin:0;';
        message.textContent = getButtonLabel(currentButtonLabels, 'errorWebGLMessage');
        errorDiv.appendChild(heading);
        errorDiv.appendChild(message);
        container.appendChild(errorDiv);
        // The bandwidth meter was created before device init; dispose it here or
        // its interval + global fetch/XHR registration leak for the page lifetime
        // (the caller never gets an instance to call destroy() on).
        if (bandwidthReporter) {
            try { bandwidthReporter.dispose(); } catch { /* cleanup must not mask the init error */ }
            bandwidthReporter = null;
        }
        assetLeaseHandle?.dispose();
        uiElements?._cleanup?.();
        canvas.remove();
        throw new Error('Graphics device initialization failed - browser may not support WebGPU or WebGL');
    }
    options.__onAppCreated?.(app);
    // Handle WebGL context loss
    canvas.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        console.error('[StorySplat Viewer] WebGL context lost');
        events.emit('error', new Error('WebGL context lost'));
    }, false);
    canvas.addEventListener('webglcontextrestored', () => {
        console.log('[StorySplat Viewer] WebGL context restored');
        // PlayCanvas should handle context restoration automatically
    }, false);
    app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    app.setCanvasResolution(pc.RESOLUTION_AUTO);
    // PlayCanvas 2.21's gsplat handler has no `.splat` parser (2.20 routed unknown
    // extensions to PLY), so scenes whose only splat is a .splat failed to load.
    try {
        const gsplatHandler = app.loader.getHandler('gsplat') as unknown as {
            addParser?: (parser: unknown) => void;
        } | null;
        gsplatHandler?.addParser?.(new SplatFormatParser(app));
    } catch (error) {
        console.warn('[StorySplat Viewer] .splat parser registration failed:', error);
    }
    // SPZ is an official PlayCanvas script rather than a core handler. Register it
    // before any assets load; the embedded ZSTD module keeps npm/CDN/self-hosted
    // viewers self-contained, while ViewerOptions.zstdWasm can supply hosted URLs.
    try {
        ensureZstdDecoderModule(options.zstdWasm);
        const gsplatHandler = app.loader.getHandler('gsplat') as unknown as {
            addParser?: (parser: unknown) => void;
        } | null;
        if (gsplatHandler?.addParser) {
            gsplatHandler.addParser(new SpzParser(app));
            console.log('[StorySplat Viewer] PlayCanvas SPZ parser registered');
        } else {
            console.warn('[StorySplat Viewer] SPZ parser registration unavailable: gsplat handler has no parser registry');
        }
    } catch (error) {
        console.warn('[StorySplat Viewer] SPZ support initialization failed:', error);
    }
    // Zero out default ambient light so scenes are dark until lights are added.
    // `baseAmbient` is the legacy ambient that every later writer (ambient/hemispheric
    // light configs, skybox IBL) targets through setBaseAmbient(); syncMeshAmbient()
    // decides whether the scene shows it or mirrors the splat relighting ambient so
    // 3D models get the same base level as the relit splats.
    const baseAmbient = new pc.Color(0, 0, 0);
    // Set once any of those writers runs; until then models get DEFAULT_MESH_AMBIENT
    // (meshLighting.ts) rather than black. Only scenes saved by an editor with per-light
    // shadows (splatRelighting.shadowsVersion) get the fill, so a scene published earlier keeps
    // its look until it is saved again.
    let baseAmbientSet = typeof config.splatRelighting?.shadowsVersion !== 'number';
    app.scene.ambientLight = new pc.Color(0, 0, 0);
    // Start the app first (before adding entities)
    app.start();
    console.log('[StorySplat Viewer] App started');
    // Eagerly prime the skybox image download so it runs in parallel with the splat
    // asset download. initSkybox() runs later (after splat load) and will reuse the
    // browser-cached image, so the sky shows up roughly when the splat does instead
    // of lagging seconds behind. We only fire-and-forget the HTTP request here; the
    // PlayCanvas skybox pipeline (equirect → cubemap → envAtlas) still runs later in
    // initSkybox() where the skybox state/entity plumbing lives.
    // Early-skybox plumbing: when the preloaded equirect finishes downloading BEFORE the splat,
    // run the full skybox pipeline right away (viewer mode only — the editor's React effects own
    // the skybox) instead of holding it for the post-load burst. `runEarlySkybox` is assigned just
    // before loadContent().then(...) so the onload can never call into uninitialized state; the
    // shared skyboxInitStarted flag keeps the early path and the burst path mutually exclusive.
    let eagerSkyboxPreloadImg: HTMLImageElement | null = null;
    let eagerSkyboxImgReady = false;
    let runEarlySkybox: (() => void) | null = null;
    let skyboxInitStarted = false;
    try {
        const eagerSkyboxUrl = config.skybox?.url || config.skyboxUrl;
        if (eagerSkyboxUrl) {
            const preloadImg = new Image();
            preloadImg.crossOrigin = 'anonymous';
            preloadImg.onload = () => {
                eagerSkyboxImgReady = true;
                if (runEarlySkybox) runEarlySkybox();
            };
            preloadImg.onerror = () => {
                // Preload failed — drop the reference so the burst path does its own (late) load.
                eagerSkyboxPreloadImg = null;
            };
            preloadImg.src = eagerSkyboxUrl;
            eagerSkyboxPreloadImg = preloadImg;
            console.log('[StorySplat Viewer] Preloading skybox image in parallel with splat:', eagerSkyboxUrl);
        }
    } catch (e) {
        console.warn('[StorySplat Viewer] Skybox preload failed (non-fatal):', e);
    }
    // Warm the scene-menu logo at scene load so the menu's first open is instant (no ~1s fetch +
    // top-to-bottom decode draw). decode() resolves once the bitmap is fully ready to paint, warming
    // both the HTTP and decode caches the hidden menu <img> later hits. No crossOrigin — keep it
    // consistent with the menu <img> (which has none) so they share the same cache entry.
    try {
        const menuLogoUrl = config.uiOptions?.menuTitleImageUrl;
        if (menuLogoUrl) {
            const pre = new Image();
            pre.decoding = 'async';
            pre.src = menuLogoUrl;
            pre.decode?.().catch(() => { /* non-fatal: failed load / src change */ });
        }
    } catch { /* non-fatal */ }
    // Configure GSplat LOD system for optimal performance
    // This improves rendering for all splat formats (PLY, SOG, LOD)
    const isMobile = isMobileDevice();
    // LOD presets and budgets follow the GPU class: standalone headsets get the mobile ones.
    const lodMobileProfile = usesMobileRenderProfile();
    let sceneLodSettings = getEffectiveLodSettings(config, lodMobileProfile);
    let resolvedLodSettings = resolveLodSettings(sceneLodSettings, lodMobileProfile);
    let resolvedLodRange: [number, number] = [...resolvedLodSettings.range] as [number, number];
    const perfOverrides = getPerfOverrides();
    let resolvedLodBaseDistance = perfOverrides.lodBaseDistance ?? resolvedLodSettings.lodBaseDistance;
    let resolvedLodMultiplier = perfOverrides.lodMultiplier ?? resolvedLodSettings.lodMultiplier;
    if (Object.keys(perfOverrides).length > 0) console.log('[SPLAT] Perf overrides active:', perfOverrides);

    console.log('[SPLAT] Initializing LOD system for device:', isMobile ? 'mobile' : 'desktop');
    // The budget before performance-mode scaling; setLodSettings keeps it current.
    let baseSplatBudget = 0;
    // The budget the scene actually runs under: the base with performance mode applied. Every
    // consumer (scene settings, the compare split, logs) reads this, never the raw base.
    const effectiveSplatBudget = (): number => performanceModeBudget(baseSplatBudget, performanceMode);
    const applySplatBudget = (base: number): void => {
        baseSplatBudget = base;
        const effective = effectiveSplatBudget();
        if (compareSplitView) {
            // The compare handle owns the split/global budget translation and
            // retains this desired non-split value for exact restoration.
            compareSplitView.setSplatBudget(effective);
        } else if (app.scene.gsplat) {
            // With the budget mode that keeps 0 meaning "LOD distances only" (splatBudget.ts).
            setSceneSplatBudget(app.scene.gsplat, effective);
        }
    };
    const setPerformanceMode = (enabled: boolean): void => {
        enabled = !!enabled;
        if (enabled === performanceMode) return;
        performanceMode = enabled;
        try { window.localStorage?.setItem(PERF_MODE_STORAGE_KEY, String(enabled)); } catch { /* private mode */ }
        graphicsDevice.maxPixelRatio = resolvePixelRatioCap();
        app.resizeCanvas();
        applySplatBudget(baseSplatBudget);
        renderScheduler.requestFrame('performance-mode');
        console.log('[StorySplat Viewer] Performance mode', enabled ? 'ON' : 'OFF', {
            pixelRatioCap: graphicsDevice.maxPixelRatio, splatBudget: effectiveSplatBudget(),
        });
        events.emit('performanceModeChange', enabled);
    };
    // Scene-level GSplat settings (applies to all gsplat components)
    if (app.scene.gsplat) {
        // LOD update settings: angle 0 / penalty 1 (engine defaults, see LOD_*_DEFAULT above) —
        // rotation alone never changes a node's optimal LOD, so there is nothing for a
        // rotation-triggered pass to do. Distance = 1 world unit.
        app.scene.gsplat.lodUpdateAngle = perfOverrides.lodUpdateAngle ?? LOD_UPDATE_ANGLE_DEFAULT;
        app.scene.gsplat.lodBehindPenalty = perfOverrides.lodBehindPenalty ?? LOD_BEHIND_PENALTY_DEFAULT;
        app.scene.gsplat.radialSorting = true; // Enable radial sorting for better quality
        app.scene.gsplat.lodUpdateDistance = perfOverrides.lodUpdateDistance ?? 1;
        // Display whatever LOD is currently resident, up to 6 levels coarser
        // than optimal. With a low underfill (≤1) the engine waits for the
        // optimal LOD before showing fine detail, producing a visible
        // "coarse → snap to fine" pop-in per chunk. With a high underfill,
        // each node progressively refines (coarsest → coarse → medium → fine)
        // as files stream in — visually continuous rather than binary.
        app.scene.gsplat.lodUnderfillLimit = perfOverrides.lodUnderfillLimit ?? LOD_UNDERFILL_LIMIT_DEFAULT;
        // LOD range settings - controls which LOD levels are used
        app.scene.gsplat.lodRangeMin = resolvedLodRange[0];
        app.scene.gsplat.lodRangeMax = resolvedLodRange[1];
        // SH (Spherical Harmonics) update settings. Default is 10° — SH
        // coefficients are re-evaluated when camera viewing angle to a node
        // changes by this much. Setting to 0 (every frame) caused black
        // rendering in WebGPU mode for some scenes, so we leave it at the
        // engine default.
        // The colorUpdateDistance / *DistanceLodScale / *AngleLodScale props
        // in earlier PlayCanvas versions were removed (replaced by automatic
        // per-node distance scaling); calling them now is a no-op + console
        // warning, so they're gone.
        app.scene.gsplat.colorUpdateAngle = perfOverrides.colorUpdateAngle ?? 10;
        // Chunk unload cooldown. Engine default is 100 ticks (~1.7s at 60fps): chunks for an area
        // the camera left 2 seconds ago are already unloaded from the asset registry, so waypoint
        // tours and back-and-forth navigation re-download + re-decode + re-upload the same chunks
        // over and over. Keep chunks resident longer on desktop; stay near the default on mobile
        // where memory is the tighter budget. Zero-ref resident files don't render and don't count
        // against the splat budget — the only cost is memory residency.
        app.scene.gsplat.cooldownTicks = perfOverrides.cooldownTicks ?? (isMobile ? 200 : 900);
        // Global splat budget (0 = disabled, uses distance-based LOD only). PlayCanvas 2.23
        // needs the LIMIT budget mode for that; setSceneSplatBudget picks the mode (splatBudget.ts).
        const splatBudget = perfOverrides.splatBudget ?? resolvedLodSettings.splatBudget;
        baseSplatBudget = splatBudget;
        setSceneSplatBudget(app.scene.gsplat, effectiveSplatBudget());
        if (splatBudget > 0) {
            console.log('[SPLAT] Global splat budget set:', splatBudget);
        }
        // Splat renderer selection (PlayCanvas 2.18+). UI exposes only 'auto'
        // and 'compute'; resolveEffectiveRenderer maps 'auto' → raster-gpu so
        // WebGPU devices get GPU-sort while WebGL2 falls back to raster-cpu
        // automatically inside PlayCanvas.
        const effectiveRenderer = resolveEffectiveRenderer(resolvedLodSettings.renderer);
        (app.scene.gsplat as any).renderer = GSPLAT_RENDERER_MAP[effectiveRenderer];
        // Always log — even for the default path — so we can confirm across
        // devices whether e.g. an iPad actually ran raster-cpu when a scene
        // asked for compute. One grep-friendly line per renderer set.
        logGsplatRenderer(app, 'init', effectiveRenderer);
        console.log('[SPLAT] LOD system configured:', {
            preset: sceneLodSettings?.preset ?? 'auto',
            lodRangeMin: resolvedLodRange[0],
            lodRangeMax: resolvedLodRange[1],
            lodBaseDistance: resolvedLodBaseDistance,
            lodMultiplier: resolvedLodMultiplier,
            splatBudget,
            isMobile
        });
    }
    else {
        console.warn('[SPLAT] GSplat scene settings not available - LOD may not work optimally');
    }
    // State
    let currentWaypointIndex = 0;
    // Where the tour camera is along the waypoints (fractional index) and whether the path
    // loops, for "between waypoints" triggers. Plain state because the tour helpers that
    // could compute them are declared further down.
    let currentWaypointPosition = -1;
    let currentTourLoops = false;
    /** The entity animations have had their first presented frame (initial waypoint trigger). */
    let entityAnimationsPresented = false;
    let isPlaying = false;
    let splatEntity: pc.Entity | null = null;
    let primarySplatCoordinateCorrection: LodCoordinateCorrection | null = null;
    // URL the primary splat entity was set up from (decides its coordinate frame)
    let primarySplatFrameUrl: string | null = null;
    let revealScript: RevealEffectScript | null = null; // Reference to reveal effect script
    let relightingScript: RelightingEffectScript | null = null; // Reference to relighting effect script (primary)
    const allRelightingScripts = new Set<RelightingEffectScript>(); // All relighting scripts (primary + swap entities)
    let relightingEnabled = false; // Whether relighting is currently active
    let _relightSyncLogged = false; // One-shot log for first sync frame
    let isDestroyed = false; // Flag to prevent async operations after destroy
    let initialRevealStarted = false;
    function startInitialReveal(): void {
        if (isDestroyed || initialRevealStarted || !revealScript || isCompareModeEnabled) return;
        initialRevealStarted = true;
        revealScript.enabled = true;
        console.log('[StorySplat Viewer] Reveal effect started with preloader dismissal');
    }
    // Scene option autoPlayWaitForFullDetail (see autoplayDetailGate.ts): the scene's initial
    // autoplay still waits behind the preloader exactly as before, then — for LOD scenes — holds
    // still until detail has settled, behind a centred "Adding in more detail" status.
    const autoplayDetailGate = createAutoplayDetailGate({
        readPending: () => readLodPending(),
        readCount: () => readLodCount(),
        startPlayback: () => {
            if (isDestroyed) return;
            exitVirtualStaging?.();
            startPlayback();
        },
        ui: {
            show: () => {
                showAutoplayDetailOverlay(container, {
                    label: getButtonLabel(currentButtonLabels, 'addingMoreDetail'),
                    startLabel: getButtonLabel(currentButtonLabels, 'startTourAnyway'),
                    uiColor,
                    theme: uiOpts.viewerTheme,
                    onStart: () => autoplayDetailGate.startNow(),
                });
                // One message at a time: the subtle corner indicator yields to this status.
                hideDetailIndicator(container);
            },
            showStartButton: () => showAutoplayDetailStartButton(container),
            hide: () => hideAutoplayDetailOverlay(container, isDestroyed),
        },
        onEnd: (outcome, waitedMs) => {
            console.log(`[StorySplat Viewer] Autoplay detail wait ended (${outcome}) after ${Math.round(waitedMs)}ms`);
        },
    });
    // Set by play() and cleared by pause()/stop(): an embedder that already called play() (e.g.
    // from a 'ready' listener) keeps its immediate start when the scene's own autoplay follows.
    let playRequestedBeforeInitialAutoplay = false;
    const initialPlaybackGate = createInitialPlaybackGate(() => {
        // An initial autoplay that waits for detail starts waiting once the scene is visible.
        if (autoplayDetailGate.isArmed()) autoplayDetailGate.begin();
        else startPlayback();
    }, !uiElements.preloader);
    // Set once the loading screen is completely gone (or at once when there is none). Things the
    // visitor should actually see, like the controls card, wait for it instead of appearing
    // behind the loader.
    let initialPresentationShown = false;
    let afterInitialPresentation: (() => void) | null = null;
    const markInitialPresentationShown = (): void => {
        if (initialPresentationShown || isDestroyed) return;
        initialPresentationShown = true;
        afterInitialPresentation?.();
    };
    let initialPreloaderDismissal: Promise<void> | null = null;
    const dismissInitialPreloader = (): Promise<void> => {
        if (initialPreloaderDismissal) return initialPreloaderDismissal;
        // The loader is opaque while active, so begin the configured scene reveal
        // at the same moment its fade starts instead of consuming the effect unseen.
        startInitialReveal();
        initialPreloaderDismissal = (uiElements.preloader
            ? hidePreloader(uiElements.preloader)
            : Promise.resolve()
        ).then(() => {
            if (isDestroyed) {
                initialPlaybackGate.cancelPendingPlayback();
                return;
            }
            initialPlaybackGate.markPresentationReady();
            markInitialPresentationShown();
            onDemandRenderingArmed = true;
            // Give the reveal effect / first tour frame a moment before switching modes.
            setTimeout(() => { if (!isDestroyed) startOnDemandRendering(); }, 1500);
        });
        return initialPreloaderDismissal;
    };
    const canvasToJpegBlob = (sourceCanvas: HTMLCanvasElement): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            try {
                sourceCanvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error('[StorySplat Viewer] Canvas capture returned no image data.'));
                }, 'image/jpeg', 0.92);
            } catch (err) {
                reject(err);
            }
        });
    };

    const rgbaToJpegBlob = (pixels: Uint8Array, width: number, height: number): Promise<Blob> => {
        const captureCanvas = document.createElement('canvas');
        captureCanvas.width = width;
        captureCanvas.height = height;
        const ctx = captureCanvas.getContext('2d');
        if (!ctx) {
            throw new Error('[StorySplat Viewer] Could not create capture canvas.');
        }
        const imageData = ctx.createImageData(width, height);
        const rowSize = width * 4;
        for (let y = 0; y < height; y++) {
            const srcStart = (height - 1 - y) * rowSize;
            const dstStart = y * rowSize;
            imageData.data.set(pixels.subarray(srcStart, srcStart + rowSize), dstStart);
        }
        ctx.putImageData(imageData, 0, 0);
        return canvasToJpegBlob(captureCanvas);
    };

    const waitForNextPostrender = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            const onPostrender = () => {
                window.clearTimeout(timer);
                resolve();
            };
            const timer = window.setTimeout(() => {
                app.off('postrender', onPostrender);
                reject(new Error('[StorySplat Viewer] Timed out waiting for a rendered frame.'));
            }, 5000);
            app.once('postrender', onPostrender);
            app.renderNextFrame = true;
        });
    };

    /**
     * Hide hotspot and portal markers, their leader lines and the walk-mode reticle for a
     * clean capture (photo, photo mode, video frames); returns the restore. Markers are
     * switched off per entity (see captureHiding.ts) because custom models share their layers.
     */
    const hideSceneMarkersForCapture = (): (() => void) => hideForCapture([
        ...collectMarkerRenders({ hotspots: hotspotEntities, portals: portalEntities }),
        reticleLayer,
    ]);
    /** Still captures and camera holds would fight a running frame capture for the canvas and camera. */
    const assertNoFrameCapture = (action: string): void => {
        if (frameCaptureActive) {
            throw new Error(`[StorySplat Viewer] Cannot ${action} while a frame capture is running. End it first.`);
        }
    };

    const capturePhoto = async (options?: CapturePhotoOptions): Promise<Blob> => {
        if (isDestroyed) {
            throw new Error('[StorySplat Viewer] Cannot capture after viewer is destroyed.');
        }
        assertNoFrameCapture('capture a photo');
        // Restored after the grab, so the on-screen view only drops them for the captured frame.
        const restoreEntities = options?.hideEntities ? hideSceneMarkersForCapture() : null;
        try {
            return await capturePhotoFrame();
        } finally {
            restoreEntities?.();
        }
    };

    const capturePhotoFrame = async (): Promise<Blob> => {
        const device = app.graphicsDevice as pc.GraphicsDevice & { isWebGPU?: boolean; readPixels?: (x: number, y: number, w: number, h: number, pixels: ArrayBufferView) => void };

        if (!device.isWebGPU && typeof device.readPixels === 'function') {
            try {
                return await new Promise<Blob>((resolve, reject) => {
                    const onPostrender = () => {
                        window.clearTimeout(timer);
                        try {
                            const width = Math.max(1, Math.floor((device as any).width || canvas.width));
                            const height = Math.max(1, Math.floor((device as any).height || canvas.height));
                            const pixels = new Uint8Array(width * height * 4);
                            device.readPixels!(0, 0, width, height, pixels);
                            rgbaToJpegBlob(pixels, width, height).then(resolve, reject);
                        } catch (err) {
                            reject(err);
                        }
                    };
                    const timer = window.setTimeout(() => {
                        app.off('postrender', onPostrender);
                        reject(new Error('[StorySplat Viewer] Timed out waiting for a rendered frame.'));
                    }, 5000);
                    app.once('postrender', onPostrender);
                    app.renderNextFrame = true;
                });
            } catch (err) {
                console.warn('[StorySplat Viewer] readPixels capture failed, falling back to canvas capture:', err);
            }
        }

        await waitForNextPostrender();
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        return canvasToJpegBlob(canvas);
    };

    /**
     * Capture the scene at a target aspect (e.g. 16/9) with the SAME vertical FOV
     * as the current view — a REAL wider render, not a padded/outpainted one. Any
     * narrower window is then a pixel-exact horizontal crop of this (perspective
     * is preserved under cropping). Used by Virtual Staging so the AI restyles
     * real geometry instead of inventing the sides.
     *
     * Implementation is WebGPU- AND post-processing-safe: it briefly fixes the
     * backbuffer to `targetAspect` and grabs the CANVAS (the CameraFrame post pass
     * still resolves to that backbuffer) rather than reading a custom render
     * target — the same reason capturePhoto() grabs the canvas on WebGPU. The
     * on-screen view is momentarily letterboxed/stretched during the grab; it is
     * restored in `finally`. Returns a PNG blob (lossless source for the model).
     */
    const captureWide = async (targetAspect: number): Promise<Blob> => {
        if (isDestroyed) throw new Error('[StorySplat Viewer] Cannot capture after viewer is destroyed.');
        // Reshaping the backbuffer mid-render would corrupt the capture's fixed output size.
        assertNoFrameCapture('capture a wide view');
        const cam = camera.camera;
        // Control flow (reshape → settle → grab → restore) lives in runWideCapture
        // (unit-tested); here we just inject the PlayCanvas primitives.
        return runWideCapture({
            targetAspect,
            canvasHeight: canvas.height,
            camera: cam ?? null,
            aspectAuto: pc.ASPECT_AUTO,
            resolutionFixed: pc.RESOLUTION_FIXED,
            resolutionAuto: pc.RESOLUTION_AUTO,
            canReshape: typeof app.setCanvasResolution === 'function',
            setResolution: (mode, width, height) => app.setCanvasResolution(mode, width, height),
            resizeCanvas: () => app.resizeCanvas(),
            requestRender: () => { app.renderNextFrame = true; },
            settleFrame: async () => {
                // Wait for the frame to be presented before reading pixels.
                await waitForNextPostrender();
                await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            },
            grab: () => new Promise<Blob>((resolve, reject) => {
                canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('[StorySplat Viewer] Wide capture produced no image.')), 'image/png');
            }),
        });
    };

    const getShareUrl = () => typeof window !== 'undefined' ? window.location.href : '';
    const getShareTitle = () => scene.sceneTitle || scene.name || 'StorySplat Scene';
    const getPhotoFileName = () => {
        const slug = getShareTitle().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'scene';
        const stamp = new Date().toISOString().slice(0, 10);
        return `storysplat-${slug}-${stamp}.jpg`;
    };
    const isShareAbort = (err: unknown) => {
        return typeof err === 'object' && err !== null && 'name' in err && (err as { name?: string }).name === 'AbortError';
    };

    const shareCurrentView = async (): Promise<void> => {
        const blob = await capturePhoto({ hideEntities: uiOpts.sharePhotoHideEntities === true });
        const fileName = getPhotoFileName();
        const shareUrl = getShareUrl();
        const title = getShareTitle();
        const panelTitle = 'Share StorySplat';
        const shareText = title
            ? `Check out "${title}" on StorySplat.`
            : 'Check out this StorySplat.';
        const nav = typeof navigator !== 'undefined' ? navigator as any : null;
        const file = typeof File !== 'undefined' ? new File([blob], fileName, { type: 'image/jpeg' }) : null;
        const files = file ? [file] : [];
        const canShareFiles = !!(nav?.share && file && typeof nav.canShare === 'function' && nav.canShare({ files }));

        showPhotoSharePanel(container, {
            blob,
            fileName,
            shareUrl,
            shareText,
            sceneTitle: title,
            title: panelTitle,
            buttonLabels: currentButtonLabels,
            aspectRatio: canvas.width > 0 && canvas.height > 0 ? canvas.width / canvas.height : undefined,
            sharePhoto: canShareFiles ? async () => {
                try {
                    await nav.share({
                        title,
                        text: shareText,
                        url: shareUrl,
                        files,
                    });
                } catch (err) {
                    if (isShareAbort(err)) return;
                    await nav.share({ title, text: shareText, files });
                }
            } : undefined,
        });
    };
    let frameSequencePlayer: FrameSequencePlayer | null = null; // 4DGS frame sequence player
    // SplatSwap system state
    const ORIGINAL_SPLAT_URL = "__ORIGINAL__";
    let additionalSplats = sanitizeSwapList(config.additionalSplats);
    const keepMeshesInMemory = config.keepMeshesInMemory ?? false;
    let initialSplatExploreMode = getEnabledExploreSubMode(config.initialSplatExploreMode); // Default to fly (POV) when available
    let refocusTapMode: 'single' | 'double' | 'none' = config.refocusTapMode || 'single'; // 'single', 'double', or 'none' (default: single)
    let currentSplatUrl: string | null = null;
    let primarySplatUrlOverride: string | null = null;
    let isLoadingSplat = false;
    let initialSplatLoadDone = false;
    const preloadedSplats = new Map<string, pc.Entity>(); // Map URL -> splat entity
    // ACTUAL loaded URLs (asset.file.url) of preloads currently in flight. The preloadedSplats
    // map is only populated on asset.ready, so without this a disposeSplat during a concurrent
    // preload of the same URL could unload the shared loader-cache resource out from under it.
    const inFlightPreloadUrls = new Set<string>();
    let failedSwapTargetUrl: string | null = null; // Prevent immediate retry loops after load errors
    let lastSplatCheckProgress = -1;
    let lastSplatCheckWaypointIndex = -1;
    let lastSwapProgress = 0; // Track progress at last swap to determine direction
    // Proximity swaps: a swap with a proximityArea is shown while the camera is inside it
    // (chain and menu modes, any camera mode). The area entered last wins; leaving every area
    // returns to the splat shown before the first one (chain mode: what the tour position calls
    // for). Not run in the editor, where swaps are previewed and transformed by hand.
    // An area with hideOnExit:false keeps its splat on screen after the camera leaves; the areas
    // still stop holding (SwapAreaState 'keep'), so the TriggerController gets the leave too.
    type SwapAreaItem = TriggeredItem & { swapUrl: string; keep: boolean };
    const swapAreas = new SwapAreaState();
    let areaSwapSkyboxChanged = false;
    // Chain mode, after a keep-on-exit area: its splat stays until the tour position calls for a
    // different chain splat than it did when the camera left the area.
    let keptAreaHold: { url: string; chainTarget: string } | null = null;
    // Bumped by an explicit menu choice (goToSplat / goToOriginalSplat): a swap load that was
    // already in flight then finishes hidden instead of showing a second splat.
    let swapShowGeneration = 0;
    let menuChoiceGeneration = 0;
    const swapAreaController = new TriggerController<SwapAreaItem>({
        show: (item) => { swapAreas.enter(item.swapUrl, { keep: item.keep }); if (!captureSwaps) reconcileAreaSwap(); },
        hide: (item) => { swapAreas.leave(item.swapUrl); if (!captureSwaps) reconcileAreaSwap(); },
    });
    const buildSwapAreaItems = (): SwapAreaItem[] => {
        if (config.splatSwapMode === 'compare') return [];
        return additionalSplats.filter((s) =>
            s && s.url && s.url !== ORIGINAL_SPLAT_URL && s.proximityArea && typeof s.proximityArea === 'object')
            .map((s) => {
                // Swap areas need both edges: hideOnExit is handled by SwapAreaState, not by
                // latching the trigger the way overlays do.
                const { hideOnExit, ...area } = s.proximityArea!;
                return {
                    id: `swap-area:${s.url}`,
                    swapUrl: s.url,
                    keep: hideOnExit === false,
                    triggers: [{ ...area, type: 'region' as const }],
                };
            });
    };
    const syncSwapAreas = () => {
        swapAreaController.setItems(isEditorMode ? [] : buildSwapAreaItems());
        // (Only reached during a capture, never at start-up before the compare state exists.)
        if (captureSwaps) captureSwaps.areaController.setItems(isCompareModeEnabled ? [] : buildSwapAreaItems());
    };
    // Video capture (beginFrameCapture, the editor's Render Video): while a capture runs, splat
    // swaps follow the captured frames instead of the live camera. The capture gets its own
    // area state, driven by each frame's pose (even in the editor), and its own tour progress;
    // swap transitions run on the video's clock. The live area state is left as it was and
    // resumes, with the pre-capture splat put back, when the capture ends.
    type CaptureSwapSession = {
        owner: 'frame-capture' | 'editor-render';
        driver: CaptureSwapDriver;
        areas: SwapAreaState;
        areaController: TriggerController<SwapAreaItem>;
        /** Path progress (0..1) of the frame being prepared, or null when it is off the tour. */
        progress: number | null;
        framesApplied: number;
        /** The splat on screen changed while preparing this frame (it must be ready before it renders). */
        splatChanged: boolean;
        /** Swap URLs already loaded before the capture (the rest are freed afterwards). */
        loadedBefore: Set<string>;
        saved: {
            splatUrl: string;
            skyboxUrl: string | null;
            skyboxRotation: number;
            areaSwapSkyboxChanged: boolean;
            keptAreaHold: { url: string; chainTarget: string } | null;
            lastSwapProgress: number;
        };
    };
    let captureSwaps: CaptureSwapSession | null = null;
    /** The area state swaps follow: the capture's while a capture runs, the live one otherwise. */
    const activeSwapAreas = (): SwapAreaState => captureSwaps?.areas ?? swapAreas;
    /** Tour progress swaps follow: the captured frame's while it has one, the live tour's otherwise. */
    const swapTourProgress = (): number => captureSwaps?.progress ?? currentProgress;
    // A capture keeps every swap splat it passes loaded (so the pre-capture splat is still there
    // to put back); what only the capture loaded is freed when it ends.
    const keepSwapMeshes = (): boolean => keepMeshesInMemory || captureSwaps !== null;
    // Swap transitions run on this clock; a capture switches it to the video's frame time.
    const swapTransitionClock: TransitionClock = { manual: false };
    // A capture's first frame cuts to its swap state instead of transitioning into it.
    let swapTransitionsCut = false;
    // Resolved once no swap load is in flight (a load's follow-up checks may start the next).
    let swapLoadIdleWaiters: Array<() => void> = [];
    const whenSwapLoadIdle = (): Promise<void> => isLoadingSplat
        ? new Promise<void>((resolve) => swapLoadIdleWaiters.push(resolve))
        : Promise.resolve();
    syncSwapAreas();
    // Compare mode state
    let isCompareModeEnabled = (config.splatSwapMode === 'compare') && additionalSplats.length > 0;
    let compareSlider: CompareSlider | null = null;
    let comparePosition = ((config.compareSettings?.initialPosition ?? 50) / 100); // 0-1
    let compareAfterEntity: pc.Entity | null = null;
    let compareAfterUrl: string | null = null;
    // Layer-based split compare: both sides render unified on their own layer, so
    // each keeps its own material, sorter, and streaming LOD stream. The clip
    // scripts above are no longer used by compare — they remain for the wipe and
    // dissolve transitions, which still run on non-unified swap splats.
    let compareSplitView: SplitViewCompareHandle | null = null;
    let compareAfterAsset: pc.Asset | null = null;
    // Assets owned by compare generations that have not committed yet. Keeping
    // them separate from the active globals prevents a cancelled generation's
    // cleanup from unloading a same-URL generation that is still loading.
    const compareLoadingAssets = new Set<pc.Asset>();
    // Registered before manifest preparation starts, so timeout/supersession/
    // viewer teardown can abort fetches even before a pc.Asset exists.
    const compareGenerationCancels = new Set<() => void>();
    const releasedCompareAssets = new WeakSet<pc.Asset>();
    // The after splat's own coordinate correction. LCC2 sources use a different
    // scale/position convention from legacy Babylon-authored splats, and the
    // editor's live transform sliders have to honour whichever one it loaded with.
    let compareAfterCorrection: LodCoordinateCorrection | null = null;
    // Whether the loaded after splat skipped the Babylon flip (LCC2 or SPZ); the
    // editor's transform sliders must match it (see updateSwapSplatTransform).
    let compareAfterNativeFrame = false;
    let compareAfterPickCacheKey: string | undefined;
    const compareSettings: CompareConfig = config.compareSettings || {};
    let compareInitId = 0; // Guard against concurrent initCompareMode calls
    // Panorama mode state (360° skybox-only view)
    let panoramaModeEnabled = false;
    let panoramaModeTransitioning = false;
    let prePanoramaCameraMode: string | null = null; // Camera mode to restore on exit
    let prePanoramaExploreSubMode: 'orbit' | 'fly' | 'walk' | null = null;
    let prePanoramaPitchRange: pc.Vec2 | null = null;
    let prePanoramaZoomRange: pc.Vec2 | null = null;
    // Create camera (matching HTML export CONFIG values)
    const camera = new pc.Entity('camera');
    // Parse background color from config (supports hex strings like "#1a1a1a")
    // Default to dark gray (0.1, 0.1, 0.1) if not specified
    let clearColor = new pc.Color(0.1, 0.1, 0.1);
    if (config.backgroundColor) {
        // Parse hex color string to RGB
        const hex = config.backgroundColor.replace('#', '');
        if (hex.length === 6) {
            const r = parseInt(hex.substring(0, 2), 16) / 255;
            const g = parseInt(hex.substring(2, 4), 16) / 255;
            const b = parseInt(hex.substring(4, 6), 16) / 255;
            clearColor = new pc.Color(r, g, b);
            console.log('[StorySplat Viewer] Background color set from config:', config.backgroundColor);
        }
    }
    /** The configured background (editor edits update it); animated backgrounds restore to it. */
    const baseBackgroundColor = clearColor.clone();
    camera.addComponent('camera', {
        clearColor,
        fov: config.fov || 60,
        nearClip: config.nearClip || 0.1,
        farClip: config.farClip || 1000
    });
    // Add audio listener for spatial audio (required for positional sound)
    camera.addComponent('audiolistener');

    // ── FXAA Post-Effect (NVIDIA, inlined from PlayCanvas posteffect-fxaa.js) ──
    // Screen-space antialiasing that smooths mesh edges without affecting gsplat
    // soft gradients. Uses the legacy PostEffectQueue system on the camera.
    const FXAA_FRAG = /* glsl */`
        uniform sampler2D uColorBuffer;
        uniform vec2 uResolution;

        #define FXAA_REDUCE_MIN   (1.0/128.0)
        #define FXAA_REDUCE_MUL   (1.0/8.0)
        #define FXAA_SPAN_MAX     8.0

        void main() {
            vec2 fragCoord = gl_FragCoord.xy * uResolution;
            vec4 rgbaM = texture2D(uColorBuffer, fragCoord);
            vec3 rgbM = rgbaM.rgb;
            float opacity = rgbaM.a;

            vec3 rgbNW = textureOffset(uColorBuffer, fragCoord, ivec2(-1, -1)).rgb;
            vec3 rgbNE = textureOffset(uColorBuffer, fragCoord, ivec2( 1, -1)).rgb;
            vec3 rgbSW = textureOffset(uColorBuffer, fragCoord, ivec2(-1,  1)).rgb;
            vec3 rgbSE = textureOffset(uColorBuffer, fragCoord, ivec2( 1,  1)).rgb;

            vec3 luma = vec3(0.299, 0.587, 0.114);
            float lumaNW = dot(rgbNW, luma);
            float lumaNE = dot(rgbNE, luma);
            float lumaSW = dot(rgbSW, luma);
            float lumaSE = dot(rgbSE, luma);
            float lumaM  = dot(rgbM, luma);
            float lumaMin = min(lumaM, min(min(lumaNW, lumaNE), min(lumaSW, lumaSE)));
            float lumaMax = max(lumaM, max(max(lumaNW, lumaNE), max(lumaSW, lumaSE)));

            vec2 dir;
            dir.x = -((lumaNW + lumaNE) - (lumaSW + lumaSE));
            dir.y =  ((lumaNW + lumaSW) - (lumaNE + lumaSE));
            float dirReduce = max((lumaNW + lumaNE + lumaSW + lumaSE) * (0.25 * FXAA_REDUCE_MUL), FXAA_REDUCE_MIN);
            float rcpDirMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + dirReduce);
            dir = min(vec2(FXAA_SPAN_MAX), max(vec2(-FXAA_SPAN_MAX), dir * rcpDirMin)) * uResolution;

            vec3 rgbA = 0.5 * (
                texture2D(uColorBuffer, fragCoord + dir * (1.0/3.0 - 0.5)).rgb +
                texture2D(uColorBuffer, fragCoord + dir * (2.0/3.0 - 0.5)).rgb);
            vec3 rgbB = rgbA * 0.5 + 0.25 * (
                texture2D(uColorBuffer, fragCoord + dir * -0.5).rgb +
                texture2D(uColorBuffer, fragCoord + dir *  0.5).rgb);
            float lumaB = dot(rgbB, luma);
            gl_FragColor = (lumaB < lumaMin || lumaB > lumaMax)
                ? vec4(rgbA, opacity)
                : vec4(rgbB, opacity);
        }
    `;

    class FxaaEffect extends pc.PostEffect {
        fxaaShader: pc.Shader;
        resolution: Float32Array;
        constructor(device: pc.GraphicsDevice) {
            super(device);
            this.fxaaShader = (pc as any).ShaderUtils.createShader(device, {
                uniqueName: 'StorySplatFxaa',
                attributes: { aPosition: pc.SEMANTIC_POSITION },
                vertexGLSL: (pc.PostEffect as any).quadVertexShader,
                fragmentGLSL: FXAA_FRAG,
            });
            this.resolution = new Float32Array(2);
        }
        render(inputTarget: pc.RenderTarget, outputTarget: pc.RenderTarget, rect: any) {
            this.resolution[0] = 1 / inputTarget.width;
            this.resolution[1] = 1 / inputTarget.height;
            this.device.scope.resolve('uResolution').setValue(this.resolution);
            this.device.scope.resolve('uColorBuffer').setValue(inputTarget.colorBuffer);
            (this as any).drawQuad(outputTarget, this.fxaaShader, rect);
        }
    }
    let fxaaEffect: FxaaEffect | null = null;

    // Helper: parse hex color to 0-1 RGB
    const hexToRgb = (hex: string) => ({
        r: parseInt(hex.slice(1, 3), 16) / 255,
        g: parseInt(hex.slice(3, 5), 16) / 255,
        b: parseInt(hex.slice(5, 7), 16) / 255,
    });

    // Helper: SSAO type string to PlayCanvas constant
    const ssaoTypeMap: Record<string, number> = {
        'lighting': (pc as any).SSAOTYPE_LIGHTING ?? 1,
        'combine': (pc as any).SSAOTYPE_COMBINE ?? 2,
    };

    // Helper: Tone mapping type string to PlayCanvas constant
    const toneMappingMap: Record<string, number> = {
        'linear': (pc as any).TONEMAP_LINEAR ?? 0,
        'filmic': (pc as any).TONEMAP_FILMIC ?? 1,
        'hejl': (pc as any).TONEMAP_HEJL ?? 2,
        'aces': (pc as any).TONEMAP_ACES ?? 3,
        'aces2': (pc as any).TONEMAP_ACES2 ?? 4,
        'neutral': (pc as any).TONEMAP_NEUTRAL ?? 5,
    };

    // Helper: Fog type name to PlayCanvas constant.
    // In 2.18+ FOG_* are strings ("linear"/"exp"/"exp2"/"none"); older builds used numbers.
    // Fall back to the string value since that matches the modern runtime.
    const fogTypeMap: Record<string, string> = {
        'linear': (pc as any).FOG_LINEAR ?? 'linear',
        'exp': (pc as any).FOG_EXP ?? 'exp',
        'exp2': (pc as any).FOG_EXP2 ?? 'exp2',
    };
    const fogNoneValue: string = (pc as any).FOG_NONE ?? 'none';

    // Warn once if PlayCanvas constants are missing (API may have changed)
    if (typeof (pc as any).SSAOTYPE_LIGHTING === 'undefined') {
        console.warn('[StorySplat Viewer] PlayCanvas SSAO type constants not found, using fallback values');
    }
    if (typeof (pc as any).TONEMAP_LINEAR === 'undefined') {
        console.warn('[StorySplat Viewer] PlayCanvas tone mapping constants not found, using fallback values');
    }
    if (typeof (pc as any).FOG_LINEAR === 'undefined') {
        console.warn('[StorySplat Viewer] PlayCanvas fog type constants not found, using fallback values');
    }

    // Apply all CameraFrame post-processing effects to the given frame
    const applyCameraFrameEffects = (frame: pc.CameraFrame, ppConfig: import('../types').PostProcessingConfig) => {
        const ce = ppConfig.colorEnhance;
        const vig = ppConfig.vignette;
        const bl = ppConfig.bloom;
        const ss = ppConfig.ssao;
        const df = ppConfig.dof;
        const fr = ppConfig.fringing;
        const gr = ppConfig.grading;
        const ta = ppConfig.taa;

        // ColorEnhance
        if (ce?.enabled) {
            frame.colorEnhance.enabled = true;
            frame.colorEnhance.shadows = ce.shadows ?? 0;
            frame.colorEnhance.highlights = ce.highlights ?? 0;
            frame.colorEnhance.vibrance = ce.vibrance ?? 0;
            frame.colorEnhance.midtones = ce.midtones ?? 0;
            frame.colorEnhance.dehaze = ce.dehaze ?? 0;
        } else {
            frame.colorEnhance.enabled = false;
        }

        // Vignette
        if (vig?.enabled) {
            frame.vignette.intensity = vig.intensity ?? 0.4;
            frame.vignette.inner = vig.inner ?? 0.5;
            frame.vignette.outer = vig.outer ?? 1.0;
            frame.vignette.curvature = vig.curvature ?? 1.5;
            if (vig.color) { const c = hexToRgb(vig.color); frame.vignette.color.set(c.r, c.g, c.b); }
        } else {
            frame.vignette.intensity = 0;
        }

        // Bloom
        if (bl?.enabled) {
            frame.bloom.intensity = bl.intensity ?? 0.02;
            frame.bloom.blurLevel = bl.blurLevel ?? 16;
        } else {
            frame.bloom.intensity = 0;
        }

        // SSAO
        if (ss?.enabled) {
            (frame.ssao as any).type = ssaoTypeMap[ss.type] ?? ssaoTypeMap['lighting'];
            frame.ssao.intensity = ss.intensity ?? 0.5;
            frame.ssao.radius = ss.radius ?? 30;
            frame.ssao.power = ss.power ?? 6;
            frame.ssao.samples = ss.samples ?? 12;
            frame.ssao.minAngle = ss.minAngle ?? 10;
            frame.ssao.blurEnabled = ss.blurEnabled !== false;
        } else {
            (frame.ssao as any).type = (pc as any).SSAOTYPE_NONE ?? 0;
        }

        // Depth of Field (it and TAA read the scene depth: see sceneDepthWrite below)
        if (df?.enabled) {
            frame.dof.enabled = true;
            frame.dof.focusDistance = df.focusDistance ?? 100;
            frame.dof.focusRange = df.focusRange ?? 10;
            frame.dof.blurRadius = df.blurRadius ?? 3;
            frame.dof.blurRings = df.blurRings ?? 4;
            frame.dof.nearBlur = df.nearBlur ?? false;
            frame.dof.highQuality = df.highQuality !== false;
        } else {
            frame.dof.enabled = false;
        }

        // Fringing (Chromatic Aberration)
        if (fr?.enabled) {
            frame.fringing.intensity = fr.intensity ?? 10;
        } else {
            frame.fringing.intensity = 0;
        }

        // Color Grading
        if (gr?.enabled) {
            frame.grading.enabled = true;
            frame.grading.brightness = gr.brightness ?? 1;
            frame.grading.contrast = gr.contrast ?? 1;
            frame.grading.saturation = gr.saturation ?? 1;
            if (gr.tint) { const c = hexToRgb(gr.tint); frame.grading.tint.set(c.r, c.g, c.b); }
        } else {
            frame.grading.enabled = false;
        }

        // TAA
        if (ta?.enabled) {
            frame.taa.enabled = true;
            frame.taa.jitter = ta.jitter ?? 1;
        } else {
            frame.taa.enabled = false;
        }

        // Tone Mapping
        const tm = ppConfig.toneMapping;
        if (tm?.enabled) {
            frame.rendering.toneMapping = toneMappingMap[tm.type] ?? toneMappingMap['linear'];
        } else {
            frame.rendering.toneMapping = toneMappingMap['linear'];
        }

        // Sharpness
        const sh = ppConfig.sharpness;
        if (sh?.enabled) {
            frame.rendering.sharpness = sh.value ?? 0.5;
        } else {
            frame.rendering.sharpness = 0;
        }

        // Render Scale
        const rs = ppConfig.renderScale;
        if (rs?.enabled) {
            frame.rendering.renderTargetScale = rs.value ?? 1;
        } else {
            frame.rendering.renderTargetScale = 1;
        }

        // DOF and TAA read the scene depth, which the splats only write with sceneDepthWrite (on
        // PlayCanvas 2.21 they were in no depth at all: its prepass draws depth-writing materials
        // only, and unified splats blend without depth). Without it DOF blurs through the splats and
        // TAA reprojects them as background. It writes an alpha-weighted splat depth during the main
        // pass, at the cost of one extra full-screen target, so it is on only while DOF or TAA is.
        // On devices without R32F targets the shared scene depth is stored at a lower precision
        // (GSplatParams#sceneDepthWrite); isSplatSceneDepthSupported rules out devices it cannot run on.
        if (app.scene.gsplat) {
            app.scene.gsplat.sceneDepthWrite = !!(df?.enabled || ta?.enabled) && pc.CameraFrame.isSplatSceneDepthSupported(app.graphicsDevice);
        }

        frame.update();
    };

    // Apply fog to scene (independent of CameraFrame).
    // PlayCanvas 2.18+: gsplat materials respect scene.fog natively; toggle via scene.gsplat.useFog.
    // True while a haze region effect is applied to the splats (it replaces the distance fog).
    let hazeEffectApplied = false;
    const applyFog = (fogConfig: import('../types').PostProcessingConfig['fog']) => {
        if (fogConfig?.enabled) {
            (app.scene.fog as any).type = fogTypeMap[fogConfig.type] ?? fogTypeMap['linear'];
            // FogParams defaults color to black, which is nearly invisible on dark scenes.
            // Match the editor's default light blue-gray when the scene JSON omits a color.
            const c = hexToRgb(fogConfig.color ?? '#b8c4d0');
            app.scene.fog.color.set(c.r, c.g, c.b);
            app.scene.fog.density = fogConfig.density ?? 0.05;
            app.scene.fog.start = fogConfig.start ?? 10;
            app.scene.fog.end = fogConfig.end ?? 100;
            // 2.18+: gsplat materials respect scene.fog natively when useFog = true.
            if (app.scene.gsplat) {
                (app.scene.gsplat as any).useFog = !hazeEffectApplied;
            }
        } else {
            (app.scene.fog as any).type = fogNoneValue;
            if (app.scene.gsplat) {
                (app.scene.gsplat as any).useFog = false;
            }
        }
    };

    // Apply exposure to scene (independent of CameraFrame)
    const applyExposure = (exposureConfig: import('../types').PostProcessingConfig['exposure']) => {
        if (exposureConfig?.enabled) {
            app.scene.exposure = exposureConfig.value ?? 1;
        } else {
            app.scene.exposure = 1;
        }
    };

    // Apply fisheye / tiny-planet projection to splats + skybox (PlayCanvas 2.18+).
    // Amount is clamped to [0, 1]. Setting to 0 reverts to standard perspective.
    //
    // radialSorting is forced ON while fisheye>0 and the prior value is restored
    // on deactivation. Capture happens fresh on each off→on transition, not
    // once-ever — otherwise a toggle sequence (on → [external code updates
    // radialSorting] → off) restores a stale pre-fisheye value.
    let fisheyePrevRadialSorting: boolean | undefined;
    let fisheyeCurrentAmount = 0;
    const applyFisheye = (rawAmount: number) => {
        const amount = Math.max(0, Math.min(1, rawAmount || 0));
        // No-op when nothing changed. The init path calls applyFisheye(0) and
        // engine state is already 0 — without this guard we'd poke property
        // setters that PlayCanvas treats as shader-define changes, costing a
        // potential recompile on cold load.
        if (amount === fisheyeCurrentAmount) return;
        const wasOn = fisheyeCurrentAmount > 0;
        const turningOn = !wasOn && amount > 0;
        const turningOff = wasOn && amount === 0;
        fisheyeCurrentAmount = amount;
        if (app.scene.gsplat) {
            (app.scene.gsplat as any).fisheye = amount;
            if (turningOn) {
                // Fresh capture at the transition. If fisheye stays on and the
                // external state changes, next deactivation will restore THIS
                // captured value, which is the best we can do without hooking
                // every writer of radialSorting.
                fisheyePrevRadialSorting = (app.scene.gsplat as any).radialSorting;
                (app.scene.gsplat as any).radialSorting = true;
            } else if (turningOff && fisheyePrevRadialSorting !== undefined) {
                (app.scene.gsplat as any).radialSorting = fisheyePrevRadialSorting;
                fisheyePrevRadialSorting = undefined;
            }
        }
        const sky = (app.scene as any).sky;
        if (sky) {
            sky.fisheye = amount;
        }
        console.log('[StorySplat Viewer] Fisheye set to', amount);
    };

    // True while the editor's Splat Edit is open (SPLAT_EDIT_ACTIVE_EVENT): it edits the raw scan,
    // so the weather, the clouds and the procedural sky backdrop are hidden until it closes.
    let splatEditActive = false;
    // Procedural splat weather (PlayCanvas 2.18+). One active instance at a time,
    // parented to app root, follows the camera so it feels infinite.
    let weatherInstance: import('../effects/gsplat-weather').GsplatWeather | null = null;
    // Tracks rebuild-relevant fields (extents + density). Scalar uniform edits
    // — speed/opacity/color/etc. — flow through applyOptions() and the next
    // update() pushes them to the GPU; only changes to this key require
    // tearing down the GSplatContainer and rebuilding the particle grid.
    let weatherStructuralKey: string | null = null;
    const weatherHexToRgb = (hex: string | undefined): [number, number, number] | undefined => {
        if (!hex) return undefined;
        let h = hex.replace('#', '');
        // Expand 3-digit shorthand (#fff → #ffffff). The editor's color picker
        // always emits 6 digits, but legacy scene JSON or hand-edits may use shorthand.
        if (h.length === 3) {
            h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        }
        if (h.length !== 6) return undefined;
        const n = parseInt(h, 16);
        if (Number.isNaN(n)) return undefined;
        return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
    };
    // Debounce structural rebuilds. The editor's density/extents sliders fire
    // ~30+ onChange events per drag; without this guard each tick destroys
    // the GSplatContainer and re-runs the up-to-1M-iteration grid fill loop.
    // Scalar uniform changes still apply immediately via applyOptions().
    const WEATHER_REBUILD_DEBOUNCE_MS = 150;
    let weatherPendingRebuild: { opts: any; key: string } | null = null;
    let weatherRebuildTimer: ReturnType<typeof setTimeout> | null = null;
    const performWeatherRebuild = () => {
        weatherRebuildTimer = null;
        const pending = weatherPendingRebuild;
        weatherPendingRebuild = null;
        if (!pending) return;
        // Guard against the timer firing after the viewer has been torn down
        // — touching `app` or constructing a new GSplatContainer against a
        // destroyed PlayCanvas app crashes. The portal-navigation cleanup
        // also clears the timer, but the public destroy path (and any future
        // teardown route) need only set `isDestroyed = true` to be safe.
        if (isDestroyed) return;
        if (weatherInstance) {
            weatherInstance.destroy();
            weatherInstance = null;
            weatherStructuralKey = null;
        }
        try {
            weatherInstance = new GsplatWeather(app, pending.opts);
            app.root.addChild(weatherInstance.entity);
            weatherStructuralKey = pending.key;
            // A rebuild that lands while the user is inside a 360° panorama
            // portal would otherwise render rain/snow over the skybox image.
            // Mirror the panorama-entry behavior so the exit restoration loop
            // (which keys off __panoramaHidden) picks this instance up.
            if (panoramaModeEnabled) {
                weatherInstance.entity.enabled = false;
                (weatherInstance.entity as any).__panoramaHidden = true;
            } else if (splatEditActive) {
                weatherInstance.entity.enabled = false;
                (weatherInstance.entity as any).__splatEditHidden = true;
            }
        } catch (e) {
            console.warn('[StorySplat Viewer] Weather setup failed:', e);
        }
    };
    const applyWeather = (weatherConfig: import('../types').WeatherConfig | undefined) => {
        if (!weatherConfig?.enabled) {
            if (weatherRebuildTimer) {
                clearTimeout(weatherRebuildTimer);
                weatherRebuildTimer = null;
                weatherPendingRebuild = null;
            }
            if (weatherInstance) {
                weatherInstance.destroy();
                weatherInstance = null;
                weatherStructuralKey = null;
            }
            return;
        }
        const presetKey = weatherConfig.preset === 'custom' ? 'snow' : weatherConfig.preset;
        const preset = WEATHER_PRESETS[presetKey] ?? WEATHER_PRESETS.snow;
        const color: [number, number, number] = weatherHexToRgb(weatherConfig.color) ?? preset.color;
        const opts = {
            ...preset,
            speed: weatherConfig.speed ?? preset.speed,
            drift: weatherConfig.drift ?? preset.drift,
            opacity: weatherConfig.opacity ?? preset.opacity,
            color,
            particleMinSize: weatherConfig.particleMinSize ?? preset.particleMinSize,
            particleMaxSize: weatherConfig.particleMaxSize ?? preset.particleMaxSize,
            elongate: weatherConfig.elongate ?? preset.elongate,
            density: weatherConfig.density ?? preset.density,
            extents: weatherConfig.extents,
            followEntity: camera
        };
        const ext = weatherConfig.extents ?? { x: 12, y: 12, z: 12 };
        const newKey = `${ext.x},${ext.y},${ext.z}|${opts.density}`;
        if (weatherInstance && weatherStructuralKey === newKey) {
            weatherInstance.applyOptions(opts);
            return;
        }
        // Structural change — coalesce into a single rebuild after the slider
        // settles. Scalar fields applied to the still-mounted instance in the
        // meantime so the user sees color/speed/opacity feedback live.
        if (weatherInstance) {
            weatherInstance.applyOptions(opts);
        }
        weatherPendingRebuild = { opts, key: newKey };
        if (weatherRebuildTimer) clearTimeout(weatherRebuildTimer);
        weatherRebuildTimer = setTimeout(performWeatherRebuild, WEATHER_REBUILD_DEBOUNCE_MS);
    };
    // Skip weather sim while in 360 panorama mode (entity is disabled so
    // nothing renders, but we also avoid recomputing particle positions).
    const weatherWindScratch = new pc.Vec3();
    const weatherUpdateHandler = (dt: number) => {
        if (!weatherInstance || panoramaModeEnabled) return;
        // Weather drifts with the scene wind unless it opts out (followSceneWind: false).
        if (config.weather?.followSceneWind !== false) {
            windWorldVelocity(weatherWindScratch);
            weatherInstance.setWind(weatherWindScratch.x, weatherWindScratch.z);
        } else {
            weatherInstance.setWind(0, 0);
        }
        weatherInstance.update(dt);
    };
    app.on('update', weatherUpdateHandler);

    // Check if any CameraFrame-based effect is enabled
    const anyCameraFrameEffect = (pp: import('../types').PostProcessingConfig | undefined) =>
        pp?.colorEnhance?.enabled || pp?.vignette?.enabled || pp?.bloom?.enabled
        || pp?.ssao?.enabled || pp?.dof?.enabled || pp?.fringing?.enabled
        || pp?.grading?.enabled || pp?.taa?.enabled || pp?.toneMapping?.enabled
        || pp?.sharpness?.enabled || pp?.renderScale?.enabled;

    // Setup CameraFrame for post-processing
    let cameraFrame: pc.CameraFrame | null = null;
    const postProcessing = config.postProcessing;
    if (anyCameraFrameEffect(postProcessing) && camera.camera) {
        try {
            cameraFrame = new pc.CameraFrame(app, camera.camera);
            cameraFrame.enabled = true;
            applyCameraFrameEffects(cameraFrame, postProcessing!);
            console.log('[StorySplat Viewer] Post-processing enabled:', Object.keys(postProcessing!).filter(k => (postProcessing as any)[k]?.enabled));
        } catch (e) {
            console.warn('[StorySplat Viewer] CameraFrame setup failed (WebGPU may be required):', e);
        }
    }
    // FXAA antialiasing — on by default, smooths mesh edges without affecting splats.
    // Skipped on WebGPU: our FXAA shader is GLSL-only and WebGPU's gsplat
    // pipeline doesn't ship the GLSL→WGSL transpilers by default → the shader
    // would fail to compile and the post-process step would render black.
    // WebGPU has access to higher-quality native MSAA via CameraFrame's render
    // target sample count, so the trade-off is fine.
    const isWebGPU = (app.graphicsDevice as { isWebGPU?: boolean }).isWebGPU === true;
    // Default ON on desktop. On mobile (WebGL2-fallback devices — the weakest GPUs) FXAA is a
    // full-resolution offscreen pass whose only benefit is mesh-edge smoothing, so it requires an
    // explicit opt-in (antialiasing.enabled === true); splat-only scenes are visually unaffected.
    const aaEnabled = usesMobileRenderProfile()
        ? postProcessing?.antialiasing?.enabled === true
        : postProcessing?.antialiasing?.enabled !== false;
    if (aaEnabled && camera.camera) {
        if (!cameraFrame && !isWebGPU) {
            // No CameraFrame — use PostEffectQueue FXAA (WebGL only).
            try {
                fxaaEffect = new FxaaEffect(app.graphicsDevice);
                camera.camera.postEffects.addEffect(fxaaEffect);
                console.log('[StorySplat Viewer] FXAA antialiasing enabled');
            } catch (e) {
                console.warn('[StorySplat Viewer] FXAA setup failed:', e);
            }
        } else if (isWebGPU && !cameraFrame) {
            console.log('[StorySplat Viewer] FXAA skipped on WebGPU; relying on engine-default sampling.');
        } else if (cameraFrame) {
            // CameraFrame active — use sharpness as edge-enhancement fallback
            cameraFrame.rendering.sharpness = Math.max(cameraFrame.rendering.sharpness, 0.5);
            console.log('[StorySplat Viewer] CameraFrame active, using sharpness for edge enhancement');
        }
    }
    // Apply scene-level effects (independent of CameraFrame)
    applyFog(postProcessing?.fog);
    applyExposure(postProcessing?.exposure);
    // Fisheye is opt-in via viewer button (uiOptions.showFisheyeButton) — starts disabled.
    applyFisheye(0);
    if (config.weather?.enabled) {
        applyWeather(config.weather);
    }
    console.log('[StorySplat Viewer] Camera settings:', {
        fov: config.fov,
        nearClip: config.nearClip,
        farClip: config.farClip,
        playerHeight: config.playerHeight
    });
    // Set initial camera position - matching HTML export behavior
    // If waypoints exist, set camera DIRECTLY to first waypoint to avoid 1-frame flash
    const playerHeight = config.playerHeight || 1.6;
    if (config.waypoints && config.waypoints.length > 0) {
        const wp = config.waypoints[0];
        console.log('[StorySplat Viewer] First waypoint raw:', wp);
        // Set position SYNCHRONOUSLY to avoid camera flash
        // Convert BabylonJS (left-handed) to PlayCanvas (right-handed) - negate Z
        if (wp.position) {
            const pos = wp.position;
            camera.setPosition(pos.x, pos.y, -pos.z);
            console.log('[StorySplat Viewer] Camera position (Z negated):', { x: pos.x, y: pos.y, z: -pos.z });
        }
        else {
            camera.setPosition(0, playerHeight, 5);
        }
        // Set rotation SYNCHRONOUSLY
        if (wp.rotation) {
            const finalRot = convertWaypointRotation(wp.rotation);
            camera.setRotation(finalRot);
            console.log('[StorySplat Viewer] Camera rotation set from waypoint');
        }
    }
    else if (scene.frameSequence && scene.frameSequence.frameUrls && scene.frameSequence.frameUrls.length > 0) {
        // Frame sequence scenes: position camera to orbit the content at origin
        camera.setPosition(0, playerHeight, 5);
        camera.lookAt(new pc.Vec3(0, 1, 0));
        console.log('[StorySplat Viewer] Camera set for frame sequence (orbit around origin)');
    }
    else {
        // No waypoints - use default position
        camera.setPosition(0, playerHeight, 5);
        camera.lookAt(new pc.Vec3(0, 0, 0));
        console.log('[StorySplat Viewer] Camera set to default position (0, playerHeight, 5)');
    }
    // Override camera position with orbit camera settings if available and in orbit mode
    let orbitCameraSettings = config.orbitCameraSettings;
    const getSavedOrbitView = (): { cameraPosition: pc.Vec3; pivot: pc.Vec3; radius: number } | null => {
        if (!orbitCameraSettings?.cameraPosition || !orbitCameraSettings.pivotPoint) {
            return null;
        }

        const cp = orbitCameraSettings.cameraPosition;
        const pp = orbitCameraSettings.pivotPoint;
        const pivot = new pc.Vec3(pp.x, pp.y, pp.z);
        const savedPosition = new pc.Vec3(cp.x, cp.y, cp.z);
        const derivedRadius = savedPosition.distance(pivot);
        return {
            cameraPosition: savedPosition,
            pivot,
            radius: Math.max(derivedRadius, 0),
        };
    };
    const applySavedOrbitViewToCamera = (): { cameraPosition: pc.Vec3; pivot: pc.Vec3; radius: number } | null => {
        const view = getSavedOrbitView();
        if (!view) return null;
        camera.setPosition(view.cameraPosition);
        camera.lookAt(view.pivot);
        return view;
    };
    const hasSavedOrbitView = (): boolean => {
        return !!(
            orbitCameraSettings?.cameraPosition &&
            orbitCameraSettings.pivotPoint
        );
    };
    const hasRestrictedSavedOrbitView = (): boolean => {
        return !!(
            orbitCameraSettings?.rotationLimits?.enabled &&
            orbitCameraSettings.cameraPosition &&
            orbitCameraSettings.pivotPoint
        );
    };
    if (orbitCameraSettings?.cameraPosition && orbitCameraSettings?.pivotPoint && initialSplatExploreMode === 'orbit') {
        const view = applySavedOrbitViewToCamera();
        console.log('[StorySplat Viewer] Camera set from orbitCameraSettings:', view?.cameraPosition, 'pivot:', view?.pivot, 'radius:', view?.radius);
    }
    // If walk is the default mode and the creator opted into spawn override, pre-position
    // the camera here so the initial frame matches the walk spawn (no visible flash
    // between waypoint[0] and the spawn teleport inside setCameraMode('walk')).
    if (defaultMode === 'walk' && config.walkSpawnOverride && config.walkCameraSettings?.cameraPosition) {
        const cp = config.walkCameraSettings.cameraPosition;
        camera.setPosition(cp.x, cp.y, cp.z);
        if (config.walkCameraSettings.rotation) {
            const r = config.walkCameraSettings.rotation;
            camera.setEulerAngles(r.x, r.y, r.z);
        }
        console.log('[StorySplat Viewer] Initial camera set from walkCameraSettings:', cp);
    }
    // Camera rig: parent entity for the camera. Stays at origin during normal use.
    // During VR, VrFloatNavigation (vrNavigation.ts) moves the rig for flying/teleportation
    // while WebXR controls the camera's local pose (head tracking).
    const cameraRig = new pc.Entity('camera-rig');
    app.root.addChild(cameraRig);
    cameraRig.addChild(camera);
    // Scene content root — holds all scene objects (splats, meshes, lights, etc.)
    // Kept separate from camera/UI so AR placement can move/scale content independently.
    const sceneContent = new pc.Entity('scene-content');
    app.root.addChild(sceneContent);
    const regionEffects = getSplatRegionEffects(app, sceneContent, isEditorMode);
    // PlayCanvas 2.21 / 2.22: a replaced unified splat can crash-loop the world update (gsplatPlacementGuard.ts).
    guardStaleGsplatPlacements(app, pc.version);
    // Scene wind (effects/wind): one wind for wind-reactive splat objects and areas, cloud layers
    // that follow it, and weather / particles that drift. A scene without wind or wind-reactive
    // parts does no work here (no listener, no shader code, no frames).
    const windSystem = new WindSystem({
        app,
        camera,
        sceneContent,
        regionEffects,
        requestFrame: (reason) => renderScheduler.requestFrame(reason),
    });
    windSystem.setSettings(config.wind);
    // The wind animates only while it moves a part on screen: on-demand rendering idles otherwise.
    renderScheduler.addSource({ name: 'wind', active: () => windSystem.isAnimatingOnScreen() });
    // Splat Edit hides the scan that wind areas move: they must not keep the editor redrawing.
    app.on(SPLAT_EDIT_ACTIVE_EVENT, (active: boolean) => windSystem.setAreasPaused(!!active));
    /** The wind's velocity in world space (units / s), zero while the scene has none. */
    const windWorldVelocity = (out: pc.Vec3): pc.Vec3 => {
        const w = windSystem.current();
        if (!w.enabled || !(w.speed > 0)) return out.set(0, 0, 0);
        const d = windDirectionRuntime(w.direction);
        out.set(d.x * w.speed, 0, d.z * w.speed);
        sceneContent.getWorldTransform().transformVector(out, out);
        out.y = 0;
        return out;
    };
    // Particle systems with followSceneWind (rain, snow and dust presets) drift with the wind: the
    // wind's velocity is added to their world velocity curve (gravity over lifetime). Rewritten
    // only when the wind changes noticeably, at most 4 times a second (rebuilding the curve
    // textures is cheap, but not free). An animated gravity on the same system wins while it plays.
    const particleWind = new Map<pc.Entity, { end: { x: number; y: number; z: number }; key: string; at: number }>();
    const particleWindScratch = new pc.Vec3();
    app.on('update', () => {
        if (!particleWind.size) return;
        windWorldVelocity(particleWindScratch);
        const wx = Math.round(particleWindScratch.x * 20) / 20;
        const wz = Math.round(particleWindScratch.z * 20) / 20;
        const key = `${wx},${wz}`;
        const now = performance.now();
        particleWind.forEach((entry, entity) => {
            const ps = entity.particlesystem;
            if (!ps) {
                particleWind.delete(entity); // destroyed (the editor rebuilds systems on edit)
                return;
            }
            if (entry.key === key || now - entry.at < 250) return;
            entry.key = key;
            entry.at = now;
            const { end } = entry;
            ps.velocityGraph = new pc.CurveSet([[0, wx, 1, end.x + wx], [0, 0, 1, end.y], [0, wz, 1, end.z + wz]]);
        });
    });
    // Triggered haze regions: a region with triggers applies only while its controller says so
    // (hotspot clicks toggle it). The editor honours the triggers too, so creators can check them
    // by moving the camera; the region being edited in the viewport (setRegionPreview) stays
    // applied so they see what they are shaping. (Focus regions became Regions, below.)
    const regionTriggerControllers = {
        haze: new TriggerController<TriggeredItem>({ show: () => applyRegionEffect('haze'), hide: () => applyRegionEffect('haze') }, { hotspotToggles: true }),
    };
    type RegionKind = 'haze';
    type HazeRegion = import('../effects/haze-regions').HazeRegion;
    let previewRegion: { kind: RegionKind; id: string } | null = null;
    const regionConfigFor = (_kind: RegionKind): import('../effects/haze-regions').HazeConfig | undefined => config.postProcessing?.hazeRegions;
    const regionIsActive = (kind: RegionKind, r: { id: string; enabled?: boolean; triggers?: unknown[] }) =>
        r.enabled !== false && ((previewRegion?.kind === kind && previewRegion.id === r.id) || !Array.isArray(r.triggers) || r.triggers.length === 0 || regionTriggerControllers[kind].isVisible(r.id));
    // The haze look (color / amount) rides in uniforms the viewer keeps, so keyframe animation
    // drives it without recompiling. Animated values are kept as overrides on top of the config
    // and survive the region set being rebuilt (triggers).
    const hazeUniform = new Float32Array(4);
    const hazeRegionUniforms = createHazeRegionUniforms();
    const regionLookOverrides: Record<RegionKind, Map<string, number>> = { haze: new Map() };
    // Animated region transforms, by region id (position / rotation / size / feather).
    const regionShapeOverrides: Record<RegionKind, Map<string, Map<string, number>>> = { haze: new Map() };
    // The regions the current effect was built from (uniform slot = index) and the effect itself.
    const activeRegionLists: Record<RegionKind, HazeRegion[]> = { haze: [] };
    const currentRegionEffects: Record<RegionKind, import('../effects/splat-region-effects').RegionColorEffect | undefined> = { haze: undefined };
    function animatedRegionShape(kind: RegionKind, region: HazeRegion) {
        const o = regionShapeOverrides[kind].get(region.id);
        const base = normalizeRegion({ ...region, shape: region.shape === 'box' ? 'box' : 'sphere' });
        if (!o || o.size === 0) return base;
        const v = (prefix: string, b: { x: number; y: number; z: number }) => ({
            x: o.get(`${prefix}.x`) ?? b.x,
            y: o.get(`${prefix}.y`) ?? b.y,
            z: o.get(`${prefix}.z`) ?? b.z,
        });
        return normalizeRegion({
            ...base,
            position: v('region.position', base.position),
            rotation: v('region.rotation', base.rotation),
            size: v('region.size', base.size),
            feather: o.get('region.feather') ?? base.feather,
        });
    }
    function writeRegionLook(kind: RegionKind): void {
        const cfg = regionConfigFor(kind);
        // Region transforms (animated or not) into their uniform slots; editor bounds follow.
        const slots = hazeRegionUniforms;
        const bounds = currentRegionEffects[kind]?.bounds;
        activeRegionLists[kind].forEach((region, i) => {
            if (!slots[i]) return;
            // Sphere / box only: outlines are baked, splat objects have no transform.
            if (region.shape !== 'outline' && region.shape !== 'splats') {
                const shape = animatedRegionShape(kind, region);
                writeRegionUniform(slots[i], shape);
                const bound = bounds?.[i];
                if (bound && bound.shape !== 'outline') Object.assign(bound, shape);
            }
        });
        const o = regionLookOverrides[kind];
        const look = resolveHazeLook(cfg);
        writeHazeUniform(hazeUniform, {
            color: {
                r: o.get('haze.color.r') ?? look.color.r,
                g: o.get('haze.color.g') ?? look.color.g,
                b: o.get('haze.color.b') ?? look.color.b,
            },
            amount: o.get('haze.amount') ?? look.amount,
        });
    }
    function applyRegionEffect(kind: RegionKind): void {
        const cfg = regionConfigFor(kind);
        const regions = cfg && Array.isArray(cfg.regions) ? cfg.regions.filter((r) => r && regionIsActive(kind, r)) : [];
        const effect = buildHazeEffect(cfg ? { ...cfg, regions } : undefined, hazeUniform, hazeRegionUniforms);
        // Same selection as resolveHazeConfig, so slot i is region i.
        activeRegionLists[kind] = !effect ? [] : drawableHazeRegions<HazeRegion>(regions, cfg?.mode).map((d) => d.item);
        currentRegionEffects[kind] = effect;
        if (effect) writeRegionLook(kind);
        regionEffects.set(kind, effect);
        // Haze replaces the distance fog on splats only while it is actually applied.
        hazeEffectApplied = !!effect;
        if (app.scene.gsplat && config.postProcessing?.fog?.enabled) (app.scene.gsplat as any).useFog = !effect;
    }
    /** Re-read the region config: feed triggered regions to the controller, then apply. */
    const syncRegionEffect = (kind: RegionKind) => {
        const cfg = regionConfigFor(kind);
        const triggered = cfg?.enabled && Array.isArray(cfg.regions)
            ? cfg.regions.filter((r) => r && Array.isArray(r.triggers) && r.triggers.length > 0).map((r) => ({ id: r.id, enabled: r.enabled !== false, triggers: r.triggers }))
            : [];
        regionTriggerControllers[kind].setItems(triggered);
        applyRegionEffect(kind);
    };
    // Regions (effects/regions.ts): colour, spotlight or label parts of the scan, switched from the
    // start, by triggers, by visitors in the Layers menu and by the API. One effect draws every
    // region: showing, hiding (a 250 ms fade), recolouring and animating them only write uniforms;
    // the shader recompiles when areas change. Replaces the focus regions and annotation layers
    // runtimes (scenes saved with those convert, see regionsInUse).
    const regionsRuntime = new RegionsRuntime(
        {
            // After destroy or a lost context the app has no device: there is nothing to draw.
            setEffect: (effect) => {
                if (isDestroyed || !app.graphicsDevice) return;
                regionEffects.set(REGIONS_EFFECT_KEY, effect);
            },
            requestRender: () => {
                if (isDestroyed || !app.graphicsDevice) return;
                app.renderNextFrame = true;
                renderScheduler.requestFrame('regions');
            },
        },
        { editor: isEditorMode },
    );
    // regionVisibilityChange per switched region; the legacy layerVisibilityChange for layers.
    bindRegionEvents(regionsRuntime, (event, data) => events.emit(event, data));
    const regionsApi = createRegionsApi(regionsRuntime);
    regionsRuntime.setConfig(regionsInUse());
    /** The viewer's Layers menu (viewers with UI; mounted with the rest of the UI, rebuilt on edits). */
    let layersPanel: LayersPanelHandle | null = null;
    // Hotspot folders and the visitor directory (visitorDirectory.ts): what the directory lists and
    // which folders' hotspots are shown. Every folder starts shown; scripts can hide folders even
    // when the directory is off. Applied to the markers by applyHotspotFolderVisibility.
    const directoryState = new VisitorDirectoryState({
        config: config.visitorDirectory,
        folders: config.hotspotFolders,
        hotspots: config.hotspots,
        extraWaypoints: config.extraWaypoints,
        waypoints: config.waypoints,
    });
    /** The visitor directory panel (viewers with UI, when the creator turned it on). */
    let directoryPanel: DirectoryPanelHandle | null = null;
    app.on('update', (dt: number) => {
        if (regionsRuntime.isFading()) regionsRuntime.tick(dt * 1000);
    });
    // Default light removed — comes from config.lights via initCustomLights() instead.
    // This ensures editor and viewer use the same lighting path.
    syncRegionEffect('haze');
    // Create CameraControls for explore mode (orbit + fly + focus)
    // Movement and rotation speeds tuned to match BabylonJS editor behavior
    // Damping values lowered for more responsive BabylonJS-like feel (0.75 vs previous 0.9)
    // Note: cameraRotationSensitivity is stored as BabylonJS angularSensibility (friction scale: 1000-100000, default 4000)
    // We convert it to PlayCanvas rotateSpeed by inverting: lower friction = higher sensitivity
    // BabylonJS uses much smaller speed values by default (e.g. 0.2).
    // Scale up for PlayCanvas so default scenes don't feel sluggish.
    let baseMoveSpeed = (config.cameraMovementSpeed ?? 1) * 5;
    const cameraControls = new CameraControls(camera, app, {
        moveSpeed: baseMoveSpeed,
        moveFastSpeed: baseMoveSpeed * 2.5,
        moveSlowSpeed: baseMoveSpeed * 0.5,
        rotateSpeed: 800 / (config.cameraRotationSensitivity || 4000),
        enableOrbit: isExploreSubModeEnabled('orbit'),
        enableFly: isExploreSubModeEnabled('fly'),
        enablePan: true,
        invertRotation: config.invertCameraRotation,
        // Damping controls camera smoothing (0.5-0.95). Lower = more responsive, higher = smoother.
        // Default 0.75 matches BabylonJS responsive feel
        moveDamping: config.cameraDamping ?? 0.75,
        rotateDamping: config.cameraDamping ?? 0.75,
        zoomDamping: 0.8
    });
    if (orbitCameraSettings?.rotationLimits) {
        const savedOrbitView = getSavedOrbitView();
        if (savedOrbitView) {
            // Snap so the controller's internal current/target both land on the
            // saved pose — without this, dampening pulls the camera away from
            // the saved position over the first few frames after init.
            cameraControls.syncFromCamera(savedOrbitView.pivot, true, true);
            cameraControls.setFocusDistance(savedOrbitView.pivot, savedOrbitView.radius, true);
        }
        cameraControls.setOrbitRotationLimits(orbitCameraSettings.rotationLimits, true);
    }
    /** "Limit orbit rotation" keeps the orbit pivot fixed, so the refocus hint row is dropped. */
    const orbitRefocusLocked = (): boolean => cameraControls.hasOrbitRotationLimits();
    // Cutaway (toolbar toggle): hide splats above/below a horizontal plane in scene space.
    // The plane height rides in a uniform, so dragging the slider never recompiles; only
    // toggling the mode installs/removes the shader hook. See effects/cutaway.ts.
    const cutawayUniform = new Float32Array(4);
    const cutawayEffect = buildCutawayEffect(cutawayUniform);
    let cutawayActive = false;
    let cutawayDirection: CutawayDirection = uiOpts.cutawayDirection === 'below' ? 'below' : 'above';
    // Start with nothing hidden: plane at the top when hiding above, at the bottom when hiding below.
    let cutawayLevel = cutawayDirection === 'above' ? 1 : 0;
    let cutawayRange = { min: 0, max: 0 };
    let syncCutawayUI: (() => void) | null = null;
    // The API drives the cutaway when visitors have the button, or when the scene's own elements
    // (hotspots, models, tour stops, extra waypoints, menu items) cut it (see CutawayAction).
    const cutawayActionsInScene = sceneHasCutawayActions(config);
    const cutawayAvailable = () => uiOpts.cutawayEnabled === true || cutawayActionsInScene;
    // Vertical extent of the current splat in scene-content space, padded so the slider's
    // end stops leave every splat outside the feather band.
    const computeCutawayRange = () => {
        const entity = splatEntity;
        const component = entity?.gsplat;
        const inverseScene = new pc.Mat4().copy(sceneContent.getWorldTransform()).invert();
        let range = { min: 0, max: 0 };
        const local = component?.resource?.aabb ?? component?.customAabb ?? null;
        const world = component?.instance?.meshInstance?.aabb;
        if (entity && local) {
            range = sceneHeightRange(local.getMin(), local.getMax(), new pc.Mat4().mul2(inverseScene, entity.getWorldTransform()));
        } else if (world) {
            range = sceneHeightRange(world.getMin(), world.getMax(), inverseScene);
        }
        const pad = Math.max(0.01, (range.max - range.min) * 0.02);
        cutawayRange = { min: range.min - pad, max: range.max + pad };
    };
    const applyCutaway = (toggled: boolean) => {
        const span = cutawayRange.max - cutawayRange.min;
        writeCutawayUniform(cutawayUniform, {
            height: cutawayLevelToHeight(cutawayLevel, cutawayRange),
            direction: cutawayDirection,
            feather: Math.max(0.01, span * 0.01),
        });
        // set() recompiles the splat shader, so only call it on a real on/off change.
        if (toggled) regionEffects.set(CUTAWAY_KEY, cutawayActive ? cutawayEffect : undefined);
        app.renderNextFrame = true;
        syncCutawayUI?.();
    };
    // While the cutaway is on, collision is switched off (unless the scene opts out) so the
    // camera can rise above a sliced building: the point of the view is a top-down look at
    // the rooms. The pre-cutaway setting is restored when the cutaway is turned off.
    let cutawayCollisionSaved: boolean | null = null;
    const suspendCollisionForCutaway = () => {
        if (cutawayCollisionSaved !== null || uiOpts.cutawayDisablesCollision === false) return;
        cutawayCollisionSaved = currentSplatCollisionEnabled;
        currentSplatCollisionEnabled = false;
        characterController?.setSplatCollisionEnabled(false);
        cameraControls.setSplatCollisionEnabled(false);
        cameraControls.setCollisionEntities([]);
    };
    const restoreCollisionAfterCutaway = () => {
        if (cutawayCollisionSaved === null) return;
        currentSplatCollisionEnabled = cutawayCollisionSaved;
        cutawayCollisionSaved = null;
        characterController?.setSplatCollisionEnabled(currentSplatCollisionEnabled);
        cameraControls.setSplatCollisionEnabled(currentSplatCollisionEnabled);
        cameraControls.setCollisionEntities(characterController?.collisionMeshEntities ?? []);
    };
    // The plane sliding to a level (a CutawayAction, or setCutaway with a duration); null at rest.
    let cutawayTransition: CutawayTransition | null = null;
    /** Turn the cutaway on or off, move its plane or flip its side, at once (no availability check). */
    const applyCutawayState = (options: CutawayOptions): void => {
        if (isDestroyed) return;
        if (options.direction === 'above' || options.direction === 'below') cutawayDirection = options.direction;
        if (typeof options.level === 'number' && Number.isFinite(options.level)) {
            cutawayLevel = Math.min(1, Math.max(0, options.level));
        }
        const toggled = typeof options.enabled === 'boolean' && options.enabled !== cutawayActive;
        if (toggled) cutawayActive = options.enabled as boolean;
        if (toggled && cutawayActive) {
            // Walk mode pins the camera to the floor; the cutaway is a free-camera view.
            if (currentCameraMode === 'walk') setCameraMode('explore');
            suspendCollisionForCutaway();
        } else if (toggled) {
            restoreCollisionAfterCutaway();
        }
        // Splat swaps and content transforms change the extent; re-measure while active.
        if (cutawayActive) computeCutawayRange();
        applyCutaway(toggled);
    };
    /**
     * Run a CutawayAction: slide the plane there (turning the cutaway on first, or off once it
     * arrives) over the action's duration. Runs alongside whatever else the element does; a newer
     * action takes over from wherever the plane is. Returns whether anything changed.
     */
    const runCutawayAction = (action: CutawayAction | null | undefined): boolean => {
        if (isDestroyed || !action) return false;
        const plan = planCutawayAction({ active: cutawayActive, level: cutawayLevel, direction: cutawayDirection }, action);
        if (!plan) return false;
        cutawayTransition = null;
        applyCutawayState({ enabled: cutawayActive || plan.enable, direction: plan.direction, level: plan.transition.from });
        if (plan.transition.duration > 0) {
            cutawayTransition = plan.transition;
        } else if (plan.transition.disableAtEnd) {
            applyCutawayState({ enabled: false, level: plan.transition.to });
        } else {
            applyCutawayState({ level: plan.transition.to });
        }
        renderScheduler.requestFrame('cutaway');
        return true;
    };
    const setCutaway = (options: CutawayOptions): void => {
        if (!cutawayAvailable() || isDestroyed) return;
        // A visitor's slider or a script takes over from a sliding plane.
        cutawayTransition = null;
        const duration = typeof options.duration === 'number' && Number.isFinite(options.duration) ? options.duration : 0;
        const turnsOff = options.enabled === false && cutawayActive;
        const movesVisibly = typeof options.level === 'number' && (options.enabled === true || (options.enabled === undefined && cutawayActive));
        if (duration > 0 && (turnsOff || movesVisibly)) {
            runCutawayAction(turnsOff
                ? { enabled: false, duration }
                : { enabled: true, level: options.level, direction: options.direction, duration });
            return;
        }
        applyCutawayState(options);
    };
    // Each frame of a slide: move the plane (re-measuring the splat, which a swap may change
    // mid-slide) and, at the end of an `enabled: false` action, turn the cutaway off.
    renderScheduler.addSource({ name: 'cutaway', active: () => cutawayTransition !== null });
    app.on('update', (dt: number) => {
        const transition = cutawayTransition;
        if (!transition || isDestroyed) return;
        const { level, done } = stepCutawayTransition(transition, dt);
        if (done) cutawayTransition = null;
        if (done && transition.disableAtEnd) {
            applyCutawayState({ enabled: false, level });
            return;
        }
        applyCutawayState({ level });
    });
    const getCutaway = (): CutawayStatus => ({
        available: cutawayAvailable(),
        enabled: cutawayActive,
        level: cutawayLevel,
        direction: cutawayDirection,
        collisionsSuspended: cutawayCollisionSaved !== null,
    });
    // Photo mode: a clean frame for taking pictures. Chrome is hidden through the container
    // class (see viewerUI .storysplat-photo-mode), scene markers through hideForCapture, and
    // the visitor keeps full camera control until "Take photo" downloads the image.
    let photoModeActive = false;
    let photoRestoreEntities: (() => void) | null = null;
    let syncPhotoUI: (() => void) | null = null;
    const setPhotoMode = (enabled: boolean): void => {
        if (isDestroyed || enabled === photoModeActive) return;
        photoModeActive = enabled;
        if (enabled) {
            hideHotspotPopup(container);
            photoRestoreEntities = hideSceneMarkersForCapture();
            container.classList.add('storysplat-photo-mode');
        } else {
            photoRestoreEntities?.();
            photoRestoreEntities = null;
            container.classList.remove('storysplat-photo-mode');
        }
        app.renderNextFrame = true;
        syncPhotoUI?.();
        events.emit('photoModeChange', { enabled });
    };
    const getPhotoMode = () => photoModeActive;
    const takePhoto = async (): Promise<Blob> => {
        // Markers are already off in photo mode; hide them for the frame otherwise.
        const blob = await capturePhoto({ hideEntities: true });
        const fileName = getPhotoFileName();
        downloadPhotoBlob(container, blob, fileName, currentButtonLabels);
        events.emit('photoCaptured', { fileName });
        return blob;
    };
    // Stereo 3D output for 3D TVs, projectors and red/cyan glasses (see StereoRenderer.ts). The
    // split formats fill the screen with one image per eye, so the 2D interface is drawn once
    // per eye too (see stereoUi.ts), and pointer rays are folded back onto the mono frame so
    // clicks, hover, refocus and walk-to hit what is under the cursor in either eye's half.
    // Stereo is switched on from outside the viewer by default (the Discover scene page's 3D View
    // control, or a host page through setStereoMode). Creators can also add the viewer's own 3D
    // button (uiOptions.showStereoButton), or turn stereo off for the scene
    // (uiOptions.stereoEnabled === false), in which case setStereoMode, which every path goes
    // through (portal hops included), refuses.
    const stereoEnabled = uiOpts.stereoEnabled !== false;
    let stereoRenderer: StereoRendererHandle | null = null;
    let stereoUi: StereoUiHandle | null = null;
    let stereoFormat: StereoFormat | null = null;
    let stereoDepth = clampStereoDepth(uiOpts.stereoDepth);
    const positiveDistance = (v: number | null | undefined): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);
    let stereoScreenDistance = positiveDistance(uiOpts.stereoScreenDistance);
    let stereoSwapEyes = false;
    let stereoPreCameraFrameEnabled: boolean | null = null;
    let stereoOwnsFullscreen = false;
    let syncStereoUI: (() => void) | null = null;
    const isStereoSplit = (): boolean => stereoFormat !== null && isSplitStereoFormat(stereoFormat);
    // Called every frame while stereo is on; the saved orbit distance only changes with the settings.
    let savedOrbitDistanceSource: typeof orbitCameraSettings | null = null;
    let savedOrbitDistance: number | null = null;
    const getStereoScreenDistance = (): number => {
        if (stereoScreenDistance) return stereoScreenDistance;
        if (savedOrbitDistanceSource !== orbitCameraSettings) {
            savedOrbitDistanceSource = orbitCameraSettings;
            savedOrbitDistance = getSavedOrbitView()?.radius ?? null;
        }
        const orbiting = (currentCameraMode === 'explore' || currentCameraMode === 'orbit') && cameraControls.mode === 'orbit';
        return resolveAutoScreenDistance({
            orbitFocusDistance: orbiting ? camera.getPosition().distance(cameraControls.focusPoint) : null,
            sceneScale: uiOpts.sceneScale,
            sceneScaleUnit: uiOpts.sceneScaleUnit,
            savedOrbitDistance,
        });
    };
    type FullscreenDocument = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
    const stereoFullscreenElement = (): Element | null => {
        const doc = document as FullscreenDocument;
        return doc.fullscreenElement || doc.webkitFullscreenElement || null;
    };
    // A 3D TV or projector shows the whole frame, so the split formats ask for fullscreen. The
    // request needs a user gesture; when it is refused (URL start, iframe without
    // allowfullscreen) stereo still runs in the page.
    const requestStereoFullscreen = (): void => {
        if (stereoFullscreenElement()) return;
        const el = container as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
        try {
            const pending = el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen?.();
            stereoOwnsFullscreen = true;
            if (pending && typeof pending.catch === 'function') pending.catch(() => { stereoOwnsFullscreen = false; });
        } catch {
            stereoOwnsFullscreen = false;
        }
    };
    const releaseStereoFullscreen = (): void => {
        if (!stereoOwnsFullscreen) return;
        stereoOwnsFullscreen = false;
        if (!stereoFullscreenElement()) return;
        const doc = document as FullscreenDocument;
        if (doc.exitFullscreen) doc.exitFullscreen().catch(() => { /* already leaving */ });
        else doc.webkitExitFullscreen?.();
    };
    // Each eye shows the whole frame in its half of the canvas, so a pointer position is mapped
    // to the mono frame before CameraComponent builds a ray from it.
    let stereoPointerPatched = false;
    const patchStereoPointer = (enabled: boolean): void => {
        const component = camera.camera as pc.CameraComponent & { screenToWorld: pc.CameraComponent['screenToWorld'] };
        if (!component) return;
        if (enabled && !stereoPointerPatched) {
            const base = pc.CameraComponent.prototype.screenToWorld;
            component.screenToWorld = function (screenx: number, screeny: number, cameraz: number, worldCoord?: pc.Vec3) {
                const rect = app.graphicsDevice.clientRect;
                const p = stereoRenderer ? stereoRenderer.mapPointer(screenx, screeny, rect.width, rect.height) : { x: screenx, y: screeny };
                return base.call(this, p.x, p.y, cameraz, worldCoord);
            };
            stereoPointerPatched = true;
        } else if (!enabled && stereoPointerPatched) {
            Reflect.deleteProperty(component, 'screenToWorld');
            stereoPointerPatched = false;
        }
    };
    const setStereoMode = (format: StereoFormat | null): void => {
        if (isDestroyed) return;
        const next = format !== null && isStereoFormat(format) ? format : null;
        if (next === stereoFormat) return;
        if (next) {
            if (!stereoEnabled) {
                console.warn('[StorySplat Viewer] Stereo 3D is turned off for this scene (editor: Settings > Stereo 3D > Allow 3D view).');
                return;
            }
            // A headset or AR session owns the camera's views.
            if (isInXR || isIn8thWallAR || isStarting8thWallAR) return;
            // The compare slider clips by screen position, which has no meaning per eye.
            if (compareSplitView || compareSlider) {
                console.warn('[StorySplat Viewer] Stereo 3D is unavailable while the compare slider is active.');
                return;
            }
        }
        const wasSplit = isStereoSplit();
        if (next) {
            if (photoModeActive) setPhotoMode(false);
            if (stereoRenderer) {
                stereoRenderer.setFormat(next);
            } else {
                // Post-processing renders through its own targets and can't wrap the eye pair
                // (VR turns it off for the same reason).
                if (cameraFrame) {
                    stereoPreCameraFrameEnabled = cameraFrame.enabled;
                    cameraFrame.enabled = false;
                }
                stereoRenderer = createStereoRenderer({
                    app,
                    camera,
                    format: next,
                    depth: stereoDepth,
                    swapEyes: stereoSwapEyes,
                    getScreenDistance: getStereoScreenDistance,
                });
            }
        } else if (stereoRenderer) {
            stereoRenderer.destroy();
            stereoRenderer = null;
            if (cameraFrame && stereoPreCameraFrameEnabled !== null) cameraFrame.enabled = stereoPreCameraFrameEnabled;
            stereoPreCameraFrameEnabled = null;
        }
        stereoFormat = next;
        const split = next !== null && isSplitStereoFormat(next);
        if (next !== null && isSplitStereoFormat(next)) {
            if (stereoUi) stereoUi.setFormat(next);
            else if (showUI) stereoUi = createStereoUi(container, canvas, next);
        } else if (stereoUi) {
            stereoUi.destroy();
            stereoUi = null;
        }
        patchStereoPointer(split);
        if (split && !wasSplit) {
            requestStereoFullscreen();
        } else if (!split && wasSplit) {
            releaseStereoFullscreen();
        }
        app.renderNextFrame = true;
        syncStereoUI?.();
        events.emit('stereoChange', { format: next });
    };
    const getStereoMode = (): StereoFormat | null => stereoFormat;
    /** Whether setStereoMode can switch stereo on right now (hosts show their control from this). */
    const isStereoAvailable = (): boolean =>
        stereoEnabled && !isDestroyed && !compareSplitView && !compareSlider && !isInXR && !isIn8thWallAR && !isStarting8thWallAR;
    const setStereoOptions = (options: StereoOptions): void => {
        if (options.depth !== undefined) {
            stereoDepth = clampStereoDepth(options.depth);
            stereoRenderer?.setDepth(stereoDepth);
        }
        if (options.screenDistance !== undefined) {
            stereoScreenDistance = positiveDistance(options.screenDistance);
            app.renderNextFrame = true;
        }
        if (options.swapEyes !== undefined) {
            stereoSwapEyes = options.swapEyes === true;
            stereoRenderer?.setSwapEyes(stereoSwapEyes);
        }
        syncStereoUI?.();
    };
    /** Teardown for both destroy paths (which run after isDestroyed blocks setStereoMode). */
    const teardownStereo = (keepFullscreen: boolean): void => {
        try { stereoRenderer?.destroy(); } catch { /* the app may already be gone */ }
        stereoRenderer = null;
        stereoUi?.destroy();
        stereoUi = null;
        stereoFormat = null;
        patchStereoPointer(false);
        if (!keepFullscreen) releaseStereoFullscreen();
    };
    // Overlays: trigger-driven information panels drawn over the scene (see ./overlays.ts).
    // Panels are built lazily from authored data and rebuilt whenever the set changes.
    // The editor skips the viewer UI (and its stylesheet) but still previews overlays.
    if (!showUI) injectOverlayStyles(uiColor, uiOpts.viewerTheme, container.id);
    const overlayLayer = createOverlayLayer(container);
    const overlayElements = new Map<string, HTMLElement>();
    const overlayController = new OverlayController({
        show: (overlay) => {
            let panel = overlayElements.get(overlay.id);
            if (!panel) {
                panel = buildOverlayElement(overlay, currentButtonLabels, () => overlayController.dismiss(overlay.id),
                    { protectMedia: uiOpts.protectMedia === true && !isEditorMode }) ?? undefined;
                if (!panel) return false; // nothing to show: keep it logically hidden too
                overlayElements.set(overlay.id, panel);
            }
            // 'top' overlays take the waypoint banner's place (under the toolbar), not the layer.
            if (overlay.layout === 'top') showTopOverlayElement(container, panel);
            else showOverlayElement(overlayLayer, panel);
            // In a headset the page can't show it: the VR interface does.
            if (vrUi && inVrSession()) vrUi.showOverlay(overlay);
            events.emit('overlayShow', { overlay });
            return true;
        },
        hide: (overlay) => {
            const panel = overlayElements.get(overlay.id);
            // Media is torn down on hide, so the next show rebuilds the panel from scratch
            // instead of re-hydrating stale DOM (duplicate listeners, PDF renders, model apps).
            overlayElements.delete(overlay.id);
            if (panel?.classList.contains('storysplat-overlay-top')) hideTopOverlayElement(panel);
            else if (panel) hideOverlayElement(panel);
            vrUi?.hideOverlay(overlay.id);
            events.emit('overlayHide', { overlay });
        },
        // An edit to a visible overlay (the editor sends the whole set per keystroke): patch
        // text / title / width / placement in place; anything structural is rebuilt by the
        // controller (hide, then show from the new data).
        update: (previous, next) => {
            const panel = overlayElements.get(next.id);
            return !!panel && updateOverlayElement(panel, previous, next, currentButtonLabels);
        },
    });
    // Unchanged visible overlays stay up untouched; removed ones are hidden by the controller.
    // The editor has no viewer UI, so no waypoint banner: there a waypoint's name and info
    // preview as 'top' overlays on that waypoint (the published banner's look), alongside the
    // authored overlays. They are never saved; the published scene keeps its banner.
    let authoredOverlays: OverlayData[] = [];
    const editorWaypointInfoOverlays = (): OverlayData[] => {
        if (!isEditorMode) return [];
        return (config.waypoints || []).flatMap((wp, index): OverlayData[] => {
            // Same rule as the published banner (getWaypointBannerContent): a hidden name shows only its info.
            const title = wp.hideNameInViewer === true ? '' : wp.name || '';
            const subtitle = wp.info || '';
            return title || subtitle
                ? [{ id: `waypoint-info-${index}`, title, subtitle, subtitleHtml: wp.infoHtml, layout: 'top', triggers: [{ type: 'waypoint', index }] }]
                : [];
        });
    };
    const applyOverlays = () => overlayController.setOverlays([...authoredOverlays, ...editorWaypointInfoOverlays()]);
    const setOverlays = (overlays: OverlayData[] | undefined) => {
        authoredOverlays = Array.isArray(overlays) ? overlays : [];
        applyOverlays();
    };
    setOverlays(config.overlays);
    const triggerControllers = () => [overlayController, regionTriggerControllers.haze, regionsRuntime.triggers, swapAreaController] as const;
    events.on('hotspotClick', (data?: { hotspot?: { id?: unknown } }) => {
        const id = data?.hotspot?.id;
        if (typeof id === 'string') for (const c of triggerControllers()) c.trigger('hotspot', id);
    });
    // Escape dismisses the most recently shown dismissible overlay (sheets and drawers alike),
    // unless a gallery lightbox is open (its own Escape closes just the lightbox) or the key
    // was typed into a form field on the host page. Removed in destroy().
    const onOverlayEscape = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || isDestroyed) return;
        if (container.querySelector('.storysplat-hotspot-lightbox')) return;
        const target = event.target as HTMLElement | null;
        if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
        // Panels sit in the layer in show order, so the last dismissible one is the most recent.
        const panels = Array.from(overlayLayer.querySelectorAll<HTMLElement>('.storysplat-overlay')).reverse();
        for (const panel of panels) {
            const id = panel.dataset.overlayId;
            const overlay = id ? overlayController.getOverlays().find((o) => o.id === id) : undefined;
            if (overlay && overlay.dismissible !== false && overlayController.isVisible(overlay.id)) {
                overlayController.dismiss(overlay.id);
                return;
            }
        }
    };
    document.addEventListener('keydown', onOverlayEscape);
    // Scene-start triggers count from 'ready', which follows the preloader's release; 'loaded'
    // fires while the loading screen is still up, so a short delay would elapse unseen.
    events.on('ready', () => { for (const c of triggerControllers()) c.start(performance.now()); });
    const overlayInverseScene = new pc.Mat4();
    const overlayCameraPos = new pc.Vec3();
    /**
     * A world position in editor scene coordinates, where regions are authored (Z flipped vs
     * PlayCanvas): into scene-content space, then through the same flip.
     */
    const toRegionScenePosition = (world: pc.Vec3): { x: number; y: number; z: number } => {
        overlayInverseScene.copy(sceneContent.getWorldTransform()).invert();
        overlayInverseScene.transformPoint(world, overlayCameraPos);
        return { x: overlayCameraPos.x, y: overlayCameraPos.y, z: -overlayCameraPos.z };
    };
    const updateOverlays = () => {
        // During a video capture the swap areas follow the captured frames, not the live camera.
        const liveSwapAreas = captureSwaps ? null : swapAreaController;
        if (!overlayController.getOverlays().length && !regionTriggerControllers.haze.getItems().length && !regionsRuntime.triggers.getItems().length && !liveSwapAreas?.getItems().length) return;
        const frame = {
            cameraScenePosition: toRegionScenePosition(camera.getPosition()),
            progress: currentProgress,
            // "At a waypoint" only means something while the tour drives the camera; in explore /
            // walk / fly the last tour index would otherwise keep a waypoint overlay up forever.
            waypointIndex: currentCameraMode === 'tour' ? currentWaypointIndex : -1,
            waypointPosition: currentCameraMode === 'tour' ? currentWaypointPosition : -1,
            tourLoops: currentTourLoops,
            timeMs: performance.now(),
        };
        for (const c of triggerControllers()) {
            if (c === swapAreaController && !liveSwapAreas) continue;
            c.update(frame);
        }
    };
    const applyOrbitZoomLimits = () => {
        const z = orbitCameraSettings?.zoomLimits;
        if (z?.enabled) {
            const min = Number.isFinite(z.min) ? Math.max(0.01, z.min as number) : 0.01;
            const max = Number.isFinite(z.max) && (z.max as number) > min ? (z.max as number) : Infinity;
            cameraControls.zoomRange = new pc.Vec2(min, max);
        } else {
            cameraControls.zoomRange = new pc.Vec2(0.01, Infinity);
        }
    };
    applyOrbitZoomLimits();
    const applyOrbitCameraSettingsToControls = () => {
        const limits = orbitCameraSettings?.rotationLimits ?? null;
        if (limits?.enabled && orbitCameraSettings?.cameraPosition && orbitCameraSettings?.pivotPoint) {
            const cp = orbitCameraSettings.cameraPosition;
            const pp = orbitCameraSettings.pivotPoint;
            cameraControls.setOrbitRotationLimitAnchor(
                new pc.Vec3(cp.x, cp.y, cp.z),
                new pc.Vec3(pp.x, pp.y, pp.z),
            );
        }
        cameraControls.setOrbitRotationLimits(
            limits,
            !(orbitCameraSettings?.cameraPosition && orbitCameraSettings?.pivotPoint),
        );
        applyOrbitZoomLimits();
    };
    const updateOrbitCameraSettings = (settings?: SceneData['orbitCameraSettings'] | null): void => {
        orbitCameraSettings = settings ?? undefined;
        config.orbitCameraSettings = orbitCameraSettings;
        applyOrbitCameraSettingsToControls();
    };
    const restoreSavedOrbitView = (): boolean => {
        if (!orbitCameraSettings?.cameraPosition ||
            !orbitCameraSettings.pivotPoint) {
            return false;
        }

        // Clear any existing rotation limits BEFORE updating pose so the stale
        // anchor from a prior orbit entry can't clamp the new pose. We re-set
        // limits at the end, which captures a fresh anchor from the now-correct
        // pose.
        cameraControls.setOrbitRotationLimits(null, false);

        const view = applySavedOrbitViewToCamera();
        if (!view) return false;
        // Snap (not soft-attach) so the orbit controller's internal "current"
        // state lands on the saved pose immediately instead of dampening from
        // the previous mode's pose — the cause of the visible "lag".
        if (cameraControls.mode === 'orbit') {
            cameraControls.syncFromCamera(view.pivot, true, true);
            cameraControls.setFocusDistance(view.pivot, view.radius, true);
        } else {
            cameraControls.setOrbitModeFromFocus(view.pivot, view.radius, true);
        }
        cameraControls.setOrbitRotationLimits(orbitCameraSettings.rotationLimits ?? null, true);
        applyOrbitZoomLimits();
        return true;
    };
    // Create CharacterController for walk mode (first-person with collisions)
    let characterController: CharacterController | null = null;
    // Hidden FPV drone mode (see enterFpvMode). Created lazily on first entry so
    // scenes that never trigger it pay nothing. It works in every scene and uses
    // authored collision when present to crash + respawn.
    let droneController: DroneController | null = null;
    let preFpvCameraMode: string = 'tour';
    const preFpvCameraPos = new pc.Vec3();
    const preFpvCameraRot = new pc.Quat();
    const fpvGamepadEdges = new GamepadEdgeTracker();
    // Rolling buffer for the typed "fpv" entry sequence.
    let fpvKeySequence = '';
    let fpvKeySequenceTime = 0;
    let fpvKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
    let currentVoxelCollisionUrl: string | null = config.voxelCollisionUrl ?? null;
    let currentCollisionMeshUrl: string | null = config.collisionMeshUrl ?? null;
    let currentCollisionSystem: 'voxel' | 'mesh' = config.collisionSystem ?? 'voxel';
    let currentSplatCollisionEnabled = config.splatCollisionEnabled !== false;
    // True while a swap with its own (or no) splat collision is on screen — the controller then
    // holds that swap's set (or the primary's, switched off), not the primary's live binding. The
    // primary-collision helpers below must not rebind or replace it: they only record the
    // primary's URL, and applyCollisionForSplat reinstalls + rebinds the primary on return.
    let swapCollisionActive = false;
    const applySplatCollisionStateToController = (): void => {
        if (!characterController) return;
        characterController.setCollisionSystem(currentCollisionSystem);
        characterController.setSplatCollisionEnabled(currentSplatCollisionEnabled);
    };
    const createLazyCharacterController = (reason: string): CharacterController => {
        if (!characterController) {
            characterController = new CharacterController(camera, app, buildWalkControllerConfig(config, baseMoveSpeed));
            applySplatCollisionStateToController();
            // Add 'walk' to allowed modes so the UI mode toggle shows it
            if (!allowedModes.includes('walk')) {
                allowedModes.push('walk');
            }
            console.log(`[StorySplat Viewer] CharacterController created lazily for ${reason}`);
        }
        return characterController;
    };
    const syncSplatCollisionBindings = (): void => {
        if (!characterController || !splatEntity) return;
        // A swap's own set is bound to the swap entity — binding it to the primary transform would
        // misplace it. applyCollisionForSplat binds the (current) primary entity on return.
        if (swapCollisionActive) return;
        characterController.setSplatEntity(splatEntity);
        const voxel = characterController.voxelCollisionInstance;
        if (voxel) {
            cameraControls.setVoxelCollision(voxel, splatEntity);
        }
        const mesh = characterController.meshCollisionInstance;
        if (mesh) {
            cameraControls.setMeshCollision(mesh, splatEntity);
        }
    };
    const setRuntimeVoxelCollisionUrl = async (jsonUrl: string | null): Promise<void> => {
        currentVoxelCollisionUrl = jsonUrl;
        // Swap collision on screen: leave its set alone. The URL is recorded above and
        // applyCollisionForSplat installs it on return; a clear still drops stale cached copies.
        if (swapCollisionActive) {
            if (!jsonUrl) characterController?.clearVoxelCollisionCache();
            return;
        }
        if (!jsonUrl) {
            characterController?.clearVoxelCollision();
            cameraControls.setVoxelCollision(null, null);
            return;
        }

        characterController = createLazyCharacterController('voxel collision');
        if (splatEntity) {
            characterController.setSplatEntity(splatEntity);
        }
        await characterController.initVoxelCollision(jsonUrl);
        if (currentVoxelCollisionUrl !== jsonUrl || swapCollisionActive) return;

        const voxel = characterController.voxelCollisionInstance;
        cameraControls.setVoxelCollision(voxel, voxel ? splatEntity : null);
    };
    // Per-swap collision: while a swap splat shows, splat collision is the primary's (default),
    // the swap's own set bound to the swap entity, or none. Custom collision primitives stay.
    // Applied on every splatChange; a newer change supersedes a load still in flight.
    let collisionSwitchToken = 0;
    const applyCollisionForSplat = async (url: string): Promise<void> => {
        const token = ++collisionSwitchToken;
        const swap = url !== getPrimarySplatUrl() ? additionalSplats.find((s) => s.url === url) : undefined;
        const mode = swap?.collision === 'own' || swap?.collision === 'none' ? swap.collision : 'primary';
        if (mode === 'primary' && !swapCollisionActive) return;
        if (mode === 'none') {
            swapCollisionActive = true;
            characterController?.setSplatCollisionEnabled(false);
            cameraControls.setSplatCollisionEnabled(false);
            console.log(`[SplatSwap] Collision → ${url} (none)`);
            return;
        }
        const own = mode === 'own' && swap ? swap : null;
        const entity = own ? (preloadedSplats.get(own.url) ?? null) : splatEntity;
        const voxelUrl = own ? (own.voxelCollisionUrl || null) : currentVoxelCollisionUrl;
        const meshUrl = own ? (own.collisionMeshUrl || null) : currentCollisionMeshUrl;
        if (!characterController && !voxelUrl && !meshUrl) {
            // No splat collision anywhere: just undo a 'none' swap's switch-off.
            swapCollisionActive = false;
            cameraControls.setSplatCollisionEnabled(currentSplatCollisionEnabled);
            console.log(`[SplatSwap] Collision → ${url} (primary: none set)`);
            return;
        }
        const cc = createLazyCharacterController('splat swap collision');
        swapCollisionActive = !!own;
        cc.setSplatEntity(entity);
        // Both loads are cached by URL, so returning to a splat re-activates its set at once.
        await Promise.all([
            voxelUrl ? cc.initVoxelCollision(voxelUrl) : Promise.resolve(cc.detachVoxelCollision()),
            meshUrl ? cc.initMeshCollision(meshUrl) : Promise.resolve(cc.detachMeshCollision()),
        ]);
        if (token !== collisionSwitchToken || isDestroyed) return;
        const system = own ? (own.collisionSystem ?? (meshUrl ? 'mesh' : 'voxel')) : currentCollisionSystem;
        cc.setCollisionSystem(system);
        cameraControls.setCollisionSystem(system);
        cc.setSplatCollisionEnabled(currentSplatCollisionEnabled);
        cameraControls.setSplatCollisionEnabled(currentSplatCollisionEnabled);
        const voxel = cc.voxelCollisionInstance;
        const mesh = cc.meshCollisionInstance;
        cameraControls.setVoxelCollision(voxel, voxel ? entity : null);
        cameraControls.setMeshCollision(mesh, mesh ? entity : null);
        console.log(`[SplatSwap] Collision → ${url} (${own ? 'own' : 'primary'} ${system}${mesh ? `, ${mesh.triangleCount} tris` : ''})`);
    };
    events.on('splatChange', (data?: { url?: string }) => {
        if (typeof data?.url === 'string') void applyCollisionForSplat(data.url);
    });
    const hasCollisionMeshes = config.collisionMeshesData && config.collisionMeshesData.length > 0;
    const hasAnyCollision = hasCollisionMeshes || !!config.voxelCollisionUrl || !!config.collisionMeshUrl;
    if (hasAnyCollision) {
        // CharacterController for walk mode - tuned for BabylonJS parity
        // Uses same conversions as CameraControls for consistent feel
        characterController = new CharacterController(camera, app, buildWalkControllerConfig(config, baseMoveSpeed));
        // Load primitive collision meshes asynchronously (if any)
        if (hasCollisionMeshes) {
            characterController.createCollisionMeshes(config.collisionMeshesData!).then(() => {
                console.log('[StorySplat Viewer] Collision meshes loaded for walk mode');
                // Share collision entities with CameraControls for explore mode (fly) collision
                cameraControls.setCollisionEntities(characterController!.collisionMeshEntities);
            }).catch((err) => {
                console.error('[StorySplat Viewer] Failed to load collision meshes:', err);
            });
        }
        // Load voxel octree collision if available (non-blocking)
        if (config.voxelCollisionUrl) {
            setRuntimeVoxelCollisionUrl(config.voxelCollisionUrl).catch((err) => {
                console.warn('[StorySplat Viewer] Voxel collision init failed:', err);
            });
        }
        // Load triangle-mesh collision (.collision.glb) if available — non-blocking.
        // CharacterController and CameraControls each keep both representations
        // loaded; setCollisionSystem chooses which one queries route to.
        if (config.collisionMeshUrl) {
            characterController.initMeshCollision(config.collisionMeshUrl).then(() => {
                // A swap switched collision while this loaded: applyCollisionForSplat owns the binding.
                if (swapCollisionActive) return;
                // Share with CameraControls so explore/fly mode also uses mesh when selected.
                const mesh = characterController!.meshCollisionInstance;
                if (mesh) {
                    cameraControls.setMeshCollision(mesh, splatEntity);
                    console.log(`[Collision] Mesh bound to walk + fly/orbit (${mesh.triangleCount} tris, system: ${currentCollisionSystem}, splatEnabled: ${currentSplatCollisionEnabled})`);
                }
            }).catch((err) => {
                console.warn('[StorySplat Viewer] Mesh collision init failed:', err);
            });
        }
        // Apply persisted splat-derived collision settings to both systems.
        applySplatCollisionStateToController();
        cameraControls.setCollisionSystem(currentCollisionSystem);
        cameraControls.setSplatCollisionEnabled(currentSplatCollisionEnabled);
    }
    // Keep CameraControls in sync even when collision data is lazy-created
    // later by the editor.
    cameraControls.setCollisionSystem(currentCollisionSystem);
    cameraControls.setSplatCollisionEnabled(currentSplatCollisionEnabled);
    // --- Segment hover system ---
    let segmentAttachment: SegmentAttachmentType | null = null;
    let segmentTooltipEl: HTMLDivElement | null = null;
    // Last segment OBJECT rendered into the tooltip (reference identity, not id — loadSegments can
    // hot-swap the attachment and a new attachment could reuse ids with different name/color).
    let _lastTooltipSeg: unknown = null;
    // Scratch ray for the per-mousemove segment centroid scan.
    const _segRayDir = new pc.Vec3();
    if (config.segmentDataUrl && config.enableSegmentHover) {
        // Wait for splat entity to have gsplat component ready
        let segRetries = 0;
        const tryLoadSegments = () => {
            if (!splatEntity?.gsplat) {
                if (++segRetries > 50) { console.warn('[StorySplat Viewer] Gave up waiting for splat entity for segments'); return; }
                setTimeout(tryLoadSegments, 200);
                return;
            }
            loadSegmentAttachment(splatEntity, config.segmentDataUrl!, config.segmentMetaUrl)
                .then((attachment) => {
                    segmentAttachment = attachment;
                })
                .catch((err) => {
                    console.warn('[StorySplat Viewer] Segment loading failed:', err);
                });
        };
        tryLoadSegments();
    }

    // Apply head bob toggle from config
    if (characterController && config.headBobEnabled != null) {
        characterController.headBobEnabled = config.headBobEnabled;
    }
    // Apply double-tap auto-move speed from config
    if (config.doubleTapMoveSpeed != null) {
        cameraControls.autoMoveSpeedFactor = config.doubleTapMoveSpeed;
        if (characterController) {
            characterController.autoMoveSpeedFactor = config.doubleTapMoveSpeed;
        }
    }

    // Track current camera mode
    let currentCameraMode = defaultMode;
    let autoMoveForward = false;

    function setAutoMoveForward(enabled: boolean): void {
        autoMoveForward = enabled;
        if (currentCameraMode === 'walk' && characterController) {
            characterController.autoMoveForward = enabled;
        } else if (currentCameraMode === 'explore') {
            cameraControls.autoMoveForward = enabled;
        }
    }

    function stopAutoMoveForward(): void {
        if (autoMoveForward) setAutoMoveForward(false);
    }

    let waypointControlEnabled = true; // When false, waypoint navigation doesn't control camera
    // Dismisses a live virtual-staging overlay (assigned when the skins UI is
    // wired up). Waypoint navigation must exit staging exactly like canvas
    // input does: showing a staging photo freezes the active camera driver
    // (tour sets waypointControlEnabled=false), so next/back would otherwise
    // no-op — or in percentage mode move the camera — behind the still-visible image.
    let exitVirtualStaging: (() => void) | null = null;
    // Per-waypoint orbit mode state (for tour mode with orbit waypoints)
    let currentWaypointOrbitEnabled = false;
    let currentWaypointOrbitTarget: pc.Vec3 | null = null;
    // Disable controls initially if in tour mode
    if (defaultMode === 'tour') {
        cameraControls.disable();
    }
    // GSplat point-picking. Camera focus / walk-to / mode-entry sync and the every-frame
    // reticle stay on the synchronous CPU surface raycast (reticleRaycast →
    // CharacterController real collision, no occupancy grid) — those are sync,
    // frame-rate call sites. Measurement-mode point queries take splat surfaces from the
    // CPU exact pick (haze-aware, see pickWorldPoint) and ask the GPU depth pick of the
    // UNIFIED splat (SplatDepthPicker) for meshes in front, since engine 2.21 fixed the pick-path device
    // hang: on 2.20, a picker pass over the unified splat disturbed the shared work
    // buffer (shimmer) and on NVIDIA Lovelace / WebGPU / Windows escalated to a TDR
    // (DXGI_ERROR_DEVICE_HUNG); 2.21's hybrid renderer submission-splits the pick
    // sort's indirect dispatches (playcanvas/engine#9036). WebGL2's raster-cpu
    // renderer has no unified pick path, so the CPU raycast remains both the
    // universal fallback and the only per-frame pick.
    // --- Reticle: white torus that follows the mouse/screen-center on the splat surface ---
    // Create dedicated layers so particles and reticle render AFTER the World layer (and gsplats).
    // drawOrder alone doesn't help because gsplats render in their own pass within World.
    // Particles layer: no depth clear. Its sub-layers move right after TranslucentEntityLayer's
    // (below, where that layer is created), i.e. right after the splats, in the same pass and depth
    // buffer as the opaque models, so the depth test hides particles behind models. Splats write no
    // depth; behind a splat surface particles fade out through the splat depth map
    // (effects/particleSplatDepth.ts patches the engine's particlePS, before any emitter compiles).
    installParticleSplatDepth(app.graphicsDevice);
    const particleLayer = new pc.Layer({ name: 'Particles' });
    app.scene.layers.push(particleLayer);
    // VoxelDebug layer: clearDepthBuffer so the overlay draws over the models too (splats write no depth).
    const voxelDebugLayer = new pc.Layer({ name: 'VoxelDebug', clearDepthBuffer: true });
    app.scene.layers.push(voxelDebugLayer);
    const reticleLayer = new pc.Layer({ name: 'Reticle' });
    app.scene.layers.push(reticleLayer);
    // Universal CPU exact-splat picking (editor AND viewer) — evaluates the
    // actual gaussians along the pick ray. THE shipped pick path; collision
    // raycast remains the fallback while sources build / for unsupported
    // formats (SOG/octree land in Phase 2).
    const exactSplatRaycaster = new ExactSplatRaycaster();
    // SOG cache key for the PRIMARY splat's pick index (its immutable load URL) — kept
    // so lazily-registered sources (mobile defers priming) still hit the IndexedDB cache.
    let splatPickCacheKey: string | undefined;
    // GPU device-loss post-mortem: when the device dies, freeze the pick
    // breadcrumb trail (window.__splatPickDiag.deviceLost) so we can see the
    // last actions before the loss even after Chrome truncates the console.
    {
        const gd = app.graphicsDevice as unknown as { wgpu?: { lost?: Promise<{ reason?: string; message?: string }> }; on?: (ev: string, fn: () => void) => void };
        gd.wgpu?.lost?.then((info) => {
            pickDiag.recordDeviceLost(String(info?.reason ?? 'unknown'), String(info?.message ?? ''));
        }).catch(() => { /* lost promise itself rejected — nothing to record */ });
        gd.on?.('devicelost', () => {
            if (!pickDiag.deviceLost) pickDiag.recordDeviceLost('engine-event', 'graphicsDevice devicelost event');
        });
        pickDiag.push('viewer:init', {
            renderer: app.graphicsDevice.isWebGPU ? 'webgpu' : 'webgl2',
            editor: isEditorMode,
            mobile: isMobile,
        });
    }
    // Unified GPU picking for point queries (measurement clicks + throttled hover: meshes in front,
    // and splats until the CPU exact index is ready).
    // The splat STAYS UNIFIED: engine 2.21 fixed the Windows/NVIDIA pick-path device
    // hang (playcanvas/engine#9036), so pc.Picker renders the World layer directly — no
    // non-unified switch, no dedicated pick layer, octree/LOD splats included. begin()
    // live-gates on WebGPU + a GPU-sort unified renderer; everywhere else (WebGL2
    // raster-cpu, forceCpuPick escape hatch, or any miss) the CPU raycast is the
    // fallback. Escape hatch: localStorage 'storysplat:forceCpuPick' = '1'.
    const splatDepthPicker = new SplatDepthPicker(app);
    // Add all layers to the camera: World → Particles → VoxelDebug → Reticle (reticle always on top)
    const camLayers = camera.camera!.layers;
    camera.camera!.layers = [...camLayers, particleLayer.id, voxelDebugLayer.id, reticleLayer.id];
    const reticleEntity = new pc.Entity('reticle');
    const reticleMat = new pc.StandardMaterial();
    reticleMat.emissive = new pc.Color(1, 1, 1);
    reticleMat.diffuse = new pc.Color(0, 0, 0);
    reticleMat.useLighting = false;
    reticleMat.blendType = pc.BLEND_NORMAL;
    reticleMat.opacity = 1.0;
    reticleMat.depthTest = false;
    reticleMat.depthWrite = false;
    reticleMat.cull = pc.CULLFACE_NONE; // Render both sides so torus is visible from any angle
    reticleMat.update();
    const reticleGeom = new pc.TorusGeometry({ tubeRadius: 0.048, ringRadius: 0.193, segments: 24, sides: 10 });
    let reticleTime = 0; // elapsed time for glow wave animation
    const reticleMesh = pc.Mesh.fromGeometry(app.graphicsDevice, reticleGeom);
    const reticleMI = new pc.MeshInstance(reticleMesh, reticleMat);
    reticleMI.drawOrder = 9999;
    reticleEntity.addComponent('render', { meshInstances: [reticleMI], castShadows: false, receiveShadows: false, layers: [reticleLayer.id] });
    reticleEntity.enabled = false;
    app.root.addChild(reticleEntity);
    // Reticle state
    let reticleScreenX = 0;
    let reticleScreenY = 0;
    let reticleTargetPos = new pc.Vec3();
    let reticleCurrentPos = new pc.Vec3();
    let reticleVisible = false;
    let reticleRevealUntil = 0; // Reveal-on-click mode: ring shown until this timestamp after a click
    let reticleDbgAt = 0; // TEMP: throttle for the [reticle] diagnostic (remove once cursor confirmed)
    let reticleFrameCounter = 0; // Cursor-follow mode: throttles the per-frame surface raycast
    let reticleLastPickX = NaN, reticleLastPickY = NaN, reticleLastPickAt = -Infinity;
    const reticleLastPickCam = [NaN, NaN, NaN, NaN, NaN, NaN, NaN];
    let reticleTargetNormal = new pc.Vec3(0, 1, 0);
    let reticleCurrentNormal = new pc.Vec3(0, 1, 0);
    const _reticleQuat = new pc.Quat();
    const _reticleLastPos = new pc.Vec3(NaN, NaN, NaN);
    const _reticleLastQuat = new pc.Quat(NaN, NaN, NaN, NaN);
    let _reticleLastScale = NaN;
    // Pre-allocated reticle temporaries (avoid GC in hot path)
    const _retEdgeA = new pc.Vec3();
    const _retEdgeB = new pc.Vec3();
    const _retNormal = new pc.Vec3();
    const _retToCamera = new pc.Vec3();
    const _retOffsetPos = new pc.Vec3();

    let reticleUserEnabled = true; // Can be toggled by editor (e.g. hide during gizmo interaction)
    // Reticle behaviour (creator setting): default = continuous cursor-follow (the ring is always
    // visible in explore/walk and snaps to the surface under the cursor). When reticleClickToReveal
    // is set, the ring instead only flashes at the click point then fades (older click-to-reveal).
    // Forced ON: the reticle is click-to-reveal ONLY. The old continuous cursor-follow mode did a
    // throttled per-frame CPU surface raycast (5 rays every ~4 frames) that tanked perf on heavy splat
    // scenes, so hover mode is removed. The ring now flashes at the click point (flashReticleAt) then
    // fades, in explore / walk / orbit. (Was: !!(uiOpts).reticleClickToReveal.)
    const reticleClickToReveal = true;
    let hoveringInteractable = false; // True when mouse is over a hotspot, portal, or clickable mesh
    let reticleOpacity = 0; // Current opacity for smooth fade in/out
    let reticleColorOverride: pc.Color | null = null; // Override color (e.g. orange for measurement mode)
    // Recolors the reticle (without the measurement-mode size change) while the
    // player is near an invisible/collision portal, signalling it's walkable.
    let reticlePortalColor: pc.Color | null = null;
    // True once a portal has triggered navigation; freezes the proximity loop so
    // the gradient popup / reticle cue don't re-show while the player is still
    // standing in the portal during the async scene transfer.
    let portalNavigationPending = false;
    // Portal teleport navigation (embeds / v2-html iframe). When configured, a
    // teleport swaps scenes IN PLACE (JS context survives → cross-scene audio bed
    // keeps playing) and pushState-updates the URL for deep-links + back button,
    // with a full reload only for version-pinned targets. See options.portalNavigation.
    const portalNav = (options.portalNavigation && options.portalNavigation.baseUrl)
        ? options.portalNavigation
        : null;
    const portalUrlFor = (sceneId: string): string =>
        `${portalNav!.baseUrl}${encodeURIComponent(sceneId)}${portalNav!.query ?? ''}`;
    const currentPortalSceneId = (): string | undefined => scene.sceneId || options.analytics?.sceneId;
    // Browser back/forward: swap in place to the scene recorded on the history
    // entry (no re-push). Guarded by isDestroyed so a leaked listener after full
    // teardown is a no-op.
    const onPortalPopstate = (e: PopStateEvent): void => {
        if (isDestroyed || !portalNav) return;
        const st = e.state as { storysplatSceneId?: string } | null;
        const id = st?.storysplatSceneId;
        if (id && id !== currentPortalSceneId() && !portalNavigationPending) {
            portalNavigationPending = true;
            void handlePortalNavigation({ targetSceneId: id } as PortalConfigLike, { fromPopstate: true });
        }
    };
    if (portalNav && typeof window !== 'undefined') {
        window.addEventListener('popstate', onPortalPopstate);
        // Stamp the initial history entry so a back-nav to the first scene has an
        // id to restore (otherwise it would leave a stale URL/scene mismatch).
        const initialId = currentPortalSceneId();
        if (initialId && typeof history !== 'undefined') {
            try { history.replaceState({ ...(history.state || {}), storysplatSceneId: initialId }, ''); } catch { /* noop */ }
        }
    }
    // Lazily-created DOM label shown near screen-center when near such a portal.
    let portalEnterLabelEl: HTMLDivElement | null = null;
    const _portalLabelScreen = new pc.Vec3();
    // Last-written label state — the setter runs every frame from checkPortalSpatialActivation, so
    // skip redundant DOM writes (textContent replaces its Text node even for identical strings).
    let _portalLabelShown = false;
    let _portalLabelText: string | null = null;
    let _portalLabelX = NaN, _portalLabelY = NaN;
    // Portal "enter" label: a subtle pill PINNED to the portal's 3D position (reprojected to screen
    // each frame) instead of a fixed 2D center overlay — so you can tell which doorway it refers to.
    // Hidden when the portal is behind the camera or off-screen (rather than clamping to an edge).
    const setPortalEnterLabel = (text: string | null, worldPos?: pc.Vec3 | null) => {
        const hideLabel = () => {
            if (!portalEnterLabelEl || !_portalLabelShown) return;
            _portalLabelShown = false;
            _portalLabelText = null;
            _portalLabelX = NaN; _portalLabelY = NaN;
            portalEnterLabelEl.style.opacity = '0';
            portalEnterLabelEl.style.display = 'none';
        };
        if (text && worldPos) {
            if (!portalEnterLabelEl) {
                portalEnterLabelEl = document.createElement('div');
                portalEnterLabelEl.className = 'storysplat-portal-enter-label';
                portalEnterLabelEl.style.cssText = [
                    'position:absolute', 'transform:translate(-50%,-100%)', 'margin-top:-12px',
                    'padding:5px 11px', 'border-radius:999px',
                    'background:rgba(0,0,0,0.62)', 'color:#fff', 'font:500 12px system-ui,sans-serif',
                    'pointer-events:none', 'z-index:2000', 'white-space:nowrap',
                    'transition:opacity 0.18s ease-out', 'box-shadow:0 2px 8px rgba(0,0,0,0.3)',
                    'backdrop-filter:blur(4px)', '-webkit-backdrop-filter:blur(4px)',
                ].join(';');
                container.appendChild(portalEnterLabelEl);
            }
            const sp = camera.camera!.worldToScreen(worldPos, _portalLabelScreen);
            const cEl = app.graphicsDevice.canvas as HTMLCanvasElement;
            if (sp.z < 0 || sp.x < 0 || sp.x > cEl.clientWidth || sp.y < 0 || sp.y > cEl.clientHeight) {
                // Behind camera or off-screen — hide instead of clamping to an edge.
                hideLabel();
                return;
            }
            if (text !== _portalLabelText) {
                _portalLabelText = text;
                portalEnterLabelEl.textContent = text;
            }
            // Exact float compare (no rounding — pixel-snapping would visibly step the motion).
            if (sp.x !== _portalLabelX) { _portalLabelX = sp.x; portalEnterLabelEl.style.left = `${sp.x}px`; }
            if (sp.y !== _portalLabelY) { _portalLabelY = sp.y; portalEnterLabelEl.style.top = `${sp.y}px`; }
            if (!_portalLabelShown) {
                _portalLabelShown = true;
                portalEnterLabelEl.style.display = 'block';
                portalEnterLabelEl.style.opacity = '1';
            }
        } else if (portalEnterLabelEl) {
            hideLabel();
        }
    };
    const RETICLE_REVEAL_MS = 700; // Reveal-on-click: hold the ring shown this long after a click, then fade
    const RETICLE_PICK_INTERVAL = 4; // Cursor-follow: raycast every Nth frame (4 ≈ 15Hz; lerp smooths it)
    const RETICLE_SNAP_THRESHOLD = 0.001; // Snap to target when within this distance
    // Normal smoothing is handled by the lerp (dt * 10) — no angle rejection needed
    const RETICLE_Z_OFFSET = 0.01; // Constant offset along normal (no distance scaling)
    // XR (VR/AR) Support: VR through WebXR on headsets in every scene, AR (beta) through 8th Wall on phones/tablets.
    let isInXR = false;
    let xrSessionType: 'vr' | 'ar' | null = null;
    // Removes setupXR's listeners and ends a live session; run before the app is destroyed.
    let xrTeardown: (() => void) | null = null;
    // The host page's engine location wins; the scene's own is already limited to a same-site path.
    const arEngineBaseUrl = options.eighthWallBaseUrl || config.eighthWallBaseUrl;
    // <style>/<link> nodes the 8th Wall engine adds to the page (body overflow:hidden, height:100%,
    // fonts). Switched off when AR ends so the host page scrolls again, back on for the next run.
    const xr8PageStyles = new Map<HTMLStyleElement | HTMLLinkElement, string | null>();
    let xr8StyleObserver: MutationObserver | null = null;
    let preArCanvasStyle: { style: string | null; width: number; height: number } | null = null;
    let preArPostEffects: { cameraFrame: boolean; fxaa: boolean } | null = null;
    let arAttributionEl: HTMLElement | null = null;
    let arStartWatchdog: ReturnType<typeof setTimeout> | null = null;
    // 8th Wall AR state
    let isIn8thWallAR = false;
    let isStarting8thWallAR = false;
    let arStartGeneration = 0;
    let xr8PlayCanvasSessionActive = false;
    let eighthWallPlacement: ARPlacement | null = null;
    let deferredSkyboxApply: (() => void) | null = null;
    let last8thWallTrackingStatus = '';
    let previousXR8GlobalPC: unknown;
    let hadPreviousXR8GlobalPC = false;
    let didSetXR8GlobalPC = false;
    // Saved so we can restore PlayCanvas's on-demand rendering after AR exits.
    // PlayCanvas ships with autoRender=false; XR8 drives its own RAF but does NOT
    // trigger PlayCanvas renders, so we flip autoRender on for the duration of AR.
    let preArAutoRender: boolean | null = null;
    let preArState: {
        cameraPosition: pc.Vec3;
        cameraRotation: pc.Quat;
        cameraRigPosition: pc.Vec3;
        cameraRigRotation: pc.Quat;
        scenePosition: pc.Vec3;
        sceneRotation: pc.Quat;
        sceneScale: pc.Vec3;
        clearColor: pc.Color;
        clearColorBuffer: boolean;
        fov: number;
        nearClip: number;
        farClip: number;
        aspectRatio: number;
        horizontalFov: boolean;
        calculateProjection: pc.CameraComponent['calculateProjection'];
        skybox: pc.Texture | null;
        envAtlas: pc.Texture | null;
        skyboxMip: number;
        skyboxIntensity: number;
        skyboxRotation: pc.Quat;
        fallbackSkyboxEntity: pc.Entity | null;
        fallbackSkyboxEnabled: boolean;
    } | null = null;

    function capturePreARState(): void {
        if (preArState) return;
        const cam = camera.camera!;
        const fallbackSkyboxEntity = app.root.findByName('skybox-fallback') as pc.Entity | null;
        preArState = {
            cameraPosition: camera.getPosition().clone(),
            cameraRotation: camera.getRotation().clone(),
            cameraRigPosition: cameraRig.getPosition().clone(),
            cameraRigRotation: cameraRig.getRotation().clone(),
            scenePosition: sceneContent.getLocalPosition().clone(),
            sceneRotation: sceneContent.getLocalRotation().clone(),
            sceneScale: sceneContent.getLocalScale().clone(),
            clearColor: cam.clearColor.clone(),
            clearColorBuffer: cam.clearColorBuffer,
            fov: cam.fov,
            nearClip: cam.nearClip,
            farClip: cam.farClip,
            aspectRatio: cam.aspectRatio,
            horizontalFov: cam.horizontalFov,
            calculateProjection: cam.calculateProjection,
            skybox: app.scene.skybox,
            envAtlas: app.scene.envAtlas,
            skyboxMip: app.scene.skyboxMip,
            skyboxIntensity: app.scene.skyboxIntensity,
            skyboxRotation: app.scene.skyboxRotation.clone(),
            fallbackSkyboxEntity,
            fallbackSkyboxEnabled: fallbackSkyboxEntity?.enabled ?? false,
        };
        arInfo('Captured pre-AR viewer state', {
            cameraPosition: preArState.cameraPosition,
            scenePosition: preArState.scenePosition,
            sceneScale: preArState.sceneScale,
            autoRender: app.autoRender,
        });
    }

    function suspendSkyboxForAR(): void {
        if (!preArState) return;

        app.scene.setSkybox(undefined);
        app.scene.envAtlas = null;

        const fallbackSkyboxEntity = preArState.fallbackSkyboxEntity ?? app.root.findByName('skybox-fallback') as pc.Entity | null;
        if (fallbackSkyboxEntity) {
            if (!preArState.fallbackSkyboxEntity) {
                preArState.fallbackSkyboxEntity = fallbackSkyboxEntity;
                preArState.fallbackSkyboxEnabled = fallbackSkyboxEntity.enabled;
            }
            fallbackSkyboxEntity.enabled = false;
        }
    }

    function restorePreARState(reason: string): void {
        if (!preArState) return;

        const state = preArState;
        preArState = null;

        cameraRig.setPosition(state.cameraRigPosition);
        cameraRig.setRotation(state.cameraRigRotation);
        camera.setPosition(state.cameraPosition);
        camera.setRotation(state.cameraRotation);
        sceneContent.setLocalPosition(state.scenePosition);
        sceneContent.setLocalRotation(state.sceneRotation);
        sceneContent.setLocalScale(state.sceneScale);

        const cam = camera.camera!;
        cam.clearColor = state.clearColor;
        cam.clearColorBuffer = state.clearColorBuffer;
        cam.fov = state.fov;
        cam.nearClip = state.nearClip;
        cam.farClip = state.farClip;
        cam.aspectRatio = state.aspectRatio;
        cam.horizontalFov = state.horizontalFov;
        cam.calculateProjection = state.calculateProjection;

        app.scene.skybox = state.skybox;
        app.scene.envAtlas = state.envAtlas;
        app.scene.skyboxMip = state.skyboxMip;
        app.scene.skyboxIntensity = state.skyboxIntensity;
        app.scene.skyboxRotation = state.skyboxRotation;
        if (state.fallbackSkyboxEntity) {
            state.fallbackSkyboxEntity.enabled = state.fallbackSkyboxEnabled;
        }

        cameraControls.syncFromCamera();
        app.renderNextFrame = true;

        arInfo('Restored pre-AR viewer state', {
            reason,
            cameraPosition: state.cameraPosition,
            scenePosition: state.scenePosition,
            sceneScale: state.sceneScale,
        });
    }

    function isXR8RuntimeRunning(XR8?: { isRunning?: unknown }): boolean {
        if (typeof XR8?.isRunning === 'function') {
            try {
                return !!XR8.isRunning();
            } catch (err) {
                arWarn('XR8.isRunning() threw; falling back to internal AR session state', err);
            }
        }
        return xr8PlayCanvasSessionActive;
    }

    function is8thWallARSuspendingSkybox(): boolean {
        return isStarting8thWallAR
            || isIn8thWallAR
            || xrSessionType === 'ar'
            || container.classList.contains('storysplat-ar-mode');
    }

    function replayDeferredSkyboxApply(): void {
        const apply = deferredSkyboxApply;
        if (!apply) return;

        requestAnimationFrame(() => {
            if (!isDestroyed && !is8thWallARSuspendingSkybox() && deferredSkyboxApply === apply) {
                deferredSkyboxApply = null;
                apply();
            }
        });
    }

    function ensurePlayCanvasGlobalForXR8(): void {
        const runtimeWindow = window as Window & { pc?: unknown };
        if (!didSetXR8GlobalPC) {
            hadPreviousXR8GlobalPC = Object.prototype.hasOwnProperty.call(runtimeWindow, 'pc');
            previousXR8GlobalPC = runtimeWindow.pc;
        }

        try {
            runtimeWindow.pc = pc;
            didSetXR8GlobalPC = true;
        } catch (err) {
            throw new Error(`8th Wall requires window.pc to point at this viewer's PlayCanvas runtime: ${err instanceof Error ? err.message : String(err)}`);
        }

        if (runtimeWindow.pc !== pc) {
            throw new Error("8th Wall requires window.pc to point at this viewer's PlayCanvas runtime");
        }
    }

    function restorePlayCanvasGlobalForXR8(): void {
        if (!didSetXR8GlobalPC) return;

        const runtimeWindow = window as Window & { pc?: unknown };
        try {
            if (hadPreviousXR8GlobalPC) {
                runtimeWindow.pc = previousXR8GlobalPC;
            } else {
                delete runtimeWindow.pc;
            }
        } catch (err) {
            arWarn('Failed to restore previous window.pc after 8th Wall AR exit', err);
        } finally {
            previousXR8GlobalPC = undefined;
            hadPreviousXR8GlobalPC = false;
            didSetXR8GlobalPC = false;
        }
    }

    function stopXR8Runtime(XR8: NonNullable<typeof window.XR8>): void {
        if (XR8.PlayCanvas?.stop) {
            try {
                XR8.PlayCanvas.stop();
                return;
            } catch (err) {
                arWarn('XR8.PlayCanvas.stop() failed; falling back to XR8.stop()', err);
            }
        }
        XR8.stop();
    }

    /** Turn post-processing (CameraFrame, the FXAA post-effect) off for an XR session. */
    function suspendPostEffectsForXR(): void {
        if (preArPostEffects) return;
        preArPostEffects = { cameraFrame: !!cameraFrame?.enabled, fxaa: false };
        if (cameraFrame) cameraFrame.enabled = false;
        if (fxaaEffect && camera.camera) {
            try {
                camera.camera.postEffects.removeEffect(fxaaEffect);
                preArPostEffects.fxaa = true;
            } catch { /* not in the queue */ }
        }
    }

    function restorePostEffectsAfterXR(): void {
        const state = preArPostEffects;
        preArPostEffects = null;
        if (!state || isDestroyed) return;
        if (cameraFrame) cameraFrame.enabled = state.cameraFrame;
        if (state.fxaa && fxaaEffect && camera.camera) {
            try { camera.camera.postEffects.addEffect(fxaaEffect); } catch { /* already present */ }
        }
    }

    /**
     * Credit the AR engine while AR runs, as its licence asks: Niantic Spatial as creator, the
     * copyright notice, and a link to the licence (which carries the warranty disclaimer).
     */
    function showARAttribution(): void {
        if (!arAttributionEl) {
            const el = document.createElement('div');
            el.className = 'storysplat-ar-attribution';
            // Above the Exit AR button (bottom-left too): over it, it took the taps meant to leave AR.
            el.style.cssText = 'position:absolute;left:8px;bottom:48px;z-index:10000;padding:3px 7px;border-radius:6px;background:rgba(0,0,0,0.45);color:rgba(255,255,255,0.85);font:11px/1.3 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;pointer-events:auto;';
            const link = document.createElement('a');
            link.href = `${(arEngineBaseUrl || '/cdn/8thwall').replace(/\/$/, '')}/LICENSE`;
            link.target = '_blank';
            link.rel = 'noopener';
            link.style.cssText = 'color:inherit;text-decoration:underline;';
            link.textContent = 'XR Engine License';
            el.append('AR by 8th Wall \u00a9 Niantic Spatial, Inc. \u00b7 ', link);
            arAttributionEl = el;
        }
        container.appendChild(arAttributionEl);
    }

    function hideARAttribution(): void {
        arAttributionEl?.remove();
    }

    /**
     * Start recording the styles 8th Wall adds to the page, and switch back on the ones a previous
     * session added (the engine creates them once per page). Also snapshot the canvas size and
     * inline style, which its PlayCanvas wrapper rewrites.
     */
    function watchXR8PageStyles(canvasEl: HTMLCanvasElement): void {
        for (const [el, media] of xr8PageStyles) {
            if (media === null) el.removeAttribute('media'); else el.setAttribute('media', media);
        }
        preArCanvasStyle ??= { style: canvasEl.getAttribute('style'), width: canvasEl.width, height: canvasEl.height };
        if (xr8StyleObserver || typeof MutationObserver === 'undefined') return;
        xr8StyleObserver = new MutationObserver((records) => {
            for (const record of records) {
                record.addedNodes.forEach((node) => {
                    if (node instanceof HTMLStyleElement || (node instanceof HTMLLinkElement && node.rel === 'stylesheet')) {
                        if (!xr8PageStyles.has(node)) xr8PageStyles.set(node, node.getAttribute('media'));
                    }
                });
            }
        });
        xr8StyleObserver.observe(document.head, { childList: true });
        xr8StyleObserver.observe(document.body, { childList: true });
    }

    /** Switch off the engine's page styles and put the canvas back as it was before AR. */
    function releaseXR8PageStyles(): void {
        xr8StyleObserver?.disconnect();
        xr8StyleObserver = null;
        for (const el of xr8PageStyles.keys()) {
            if (el.isConnected) el.setAttribute('media', 'not all');
        }
        const canvasState = preArCanvasStyle;
        preArCanvasStyle = null;
        if (canvasState && !isDestroyed) {
            const canvasEl = app.graphicsDevice.canvas as HTMLCanvasElement;
            if (canvasState.style === null) canvasEl.removeAttribute('style');
            else canvasEl.setAttribute('style', canvasState.style);
            try { app.resizeCanvas(); } catch { /* app torn down */ }
        }
    }

    /** Enter AR state (8th Wall) */
    function enterARState(): void {
        if (xrSessionType === 'ar' && isInXR) {
            arInfo('Ignoring duplicate AR enter signal');
            return;
        }
        clearARStartWatchdog();
        isInXR = true;
        isIn8thWallAR = true;
        xrSessionType = 'ar';
        uiElements.arButton?.classList.add('active');
        uiElements.arButton!.textContent = getButtonLabel(currentButtonLabels, 'exitAr');
        // Add AR mode CSS class for overlay styling
        container.classList.add('storysplat-ar-mode');
        // Close (not just detach) the photo-share panel so its Escape listener and object URL
        // are released. Inline rather than closePhotoSharePanel() — this function is also
        // exercised standalone by createViewer.onDemandAr.test.ts.
        const openPhotoSharePanel = container.querySelector('.storysplat-photo-share-backdrop') as (HTMLElement & { __storysplatClose?: () => void }) | null;
        if (openPhotoSharePanel?.__storysplatClose) openPhotoSharePanel.__storysplatClose();
        else openPhotoSharePanel?.remove();
        uiElements.skinsDropdown?.classList.remove('open');
        suspendSkyboxForAR();
        cameraControls.disable();
        if (characterController) {
            characterController.disable();
        }
        // Force continuous rendering so the splat redraws each frame with the
        // updated camera pose from the XR8 pipeline. Without this PlayCanvas
        // sits idle and the scene is invisible in AR.
        preArAutoRender = app.autoRender;
        app.autoRender = true;
        // Post-processing would compose a full-screen pass over the camera feed XR8 draws into
        // the backbuffer; VR and stereo switch it off too.
        suspendPostEffectsForXR();
        showARAttribution();
        arInfo('Entered 8th Wall AR state', {
            autoRenderBeforeAR: preArAutoRender,
            canvas: {
                width: canvas.width,
                height: canvas.height,
                clientWidth: canvas.clientWidth,
                clientHeight: canvas.clientHeight,
            },
        });
        events.emit('xrStart', { type: 'ar' });
    }

    /** Exit AR state (8th Wall) */
    function exitARState(): void {
        const shouldEmitXrEnd = xrSessionType === 'ar' || isIn8thWallAR || container.classList.contains('storysplat-ar-mode');
        const wasAr = shouldEmitXrEnd || preArState !== null;
        if (!wasAr) {
            arInfo('Ignoring duplicate AR exit signal');
            return;
        }
        isInXR = false;
        isIn8thWallAR = false;
        hideARContent();
        uiElements.arButton?.classList.remove('active');
        if (uiElements.arButton)
            uiElements.arButton.textContent = getButtonLabel(currentButtonLabels, 'ar');
        // Remove AR mode CSS class
        container.classList.remove('storysplat-ar-mode');
        xrSessionType = null;
        // Re-enable camera controls based on current mode
        if (currentCameraMode === 'explore') {
            cameraControls.enable();
        }
        else if (currentCameraMode === 'walk' && characterController) {
            characterController.enable();
        }
        // Restore render mode and force one frame so the stale AR camera feed
        // that XR8 left on the canvas is overdrawn by a normal PlayCanvas frame.
        restorePreARState('exitARState');
        if (preArAutoRender !== null) {
            app.autoRender = preArAutoRender;
            preArAutoRender = null;
        }
        restorePostEffectsAfterXR();
        hideARAttribution();
        releaseXR8PageStyles();
        app.renderNextFrame = true;
        arInfo('Exited 8th Wall AR state', { autoRender: app.autoRender });
        replayDeferredSkyboxApply();
        restorePlayCanvasGlobalForXR8();
        if (shouldEmitXrEnd) {
            events.emit('xrEnd', {});
        }
    }

    /**
     * Show an XR error. When the control that failed (the AR button by default, or the VR button)
     * is on screen the message is anchored next to it and stays until dismissed; otherwise falls
     * back to the centered toast.
     */
    function showARError(message: string, anchorButton: HTMLElement | null | undefined = uiElements.arButton): void {
        // Remove any existing AR error toast
        container.querySelector('.storysplat-ar-error')?.remove();
        const toast = document.createElement('div');
        toast.className = 'storysplat-ar-error';
        const arBtn = anchorButton;
        const containerRect = container.getBoundingClientRect();
        const btnRect = arBtn && arBtn.isConnected ? arBtn.getBoundingClientRect() : null;
        const anchored = !!btnRect && btnRect.width > 0 && btnRect.height > 0
            && btnRect.bottom > containerRect.top && btnRect.top < containerRect.bottom;
        if (anchored && btnRect) {
            // Above the button when it sits in the lower half, below it otherwise.
            const left = Math.max(8, Math.min(btnRect.left - containerRect.left, containerRect.width - 328));
            const inLowerHalf = (btnRect.top - containerRect.top) > containerRect.height / 2;
            const vertical = inLowerHalf
                ? `bottom:${Math.max(8, containerRect.bottom - btnRect.top + 8)}px;`
                : `top:${Math.max(8, btnRect.bottom - containerRect.top + 8)}px;`;
            toast.setAttribute('role', 'alert');
            toast.style.cssText = `position:absolute;${vertical}left:${left}px;z-index:10001;display:flex;align-items:flex-start;gap:8px;max-width:min(320px, calc(100% - 16px));box-sizing:border-box;background:rgba(20,20,20,0.9);color:white;padding:10px 8px 10px 12px;border-left:3px solid rgba(220,70,70,0.95);border-radius:8px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:13px;line-height:1.4;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);box-shadow:0 4px 12px rgba(0,0,0,0.3);`;
            const text = document.createElement('span');
            text.textContent = message;
            const close = document.createElement('button');
            close.type = 'button';
            close.textContent = '\u00d7';
            close.setAttribute('aria-label', getButtonLabel(currentButtonLabels, 'close'));
            close.style.cssText = 'flex:0 0 auto;background:transparent;border:none;color:inherit;cursor:pointer;font-size:18px;line-height:1;padding:0 4px;';
            close.addEventListener('click', () => toast.remove());
            toast.append(text, close);
            // Persists until dismissed — no auto-hide for an error next to its control.
            container.appendChild(toast);
            return;
        }
        toast.style.cssText = 'position:absolute;bottom:60px;left:50%;transform:translateX(-50%);z-index:10001;background:rgba(200,40,40,0.92);color:white;padding:12px 20px;border-radius:10px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:14px;max-width:90%;text-align:center;backdrop-filter:blur(8px);box-shadow:0 4px 12px rgba(0,0,0,0.3);cursor:pointer;';
        toast.textContent = message;
        toast.addEventListener('click', () => toast.remove());
        container.appendChild(toast);
        setTimeout(() => { if (toast.parentNode) toast.remove(); }, 8000);
    }

    function clearARStartWatchdog(): void {
        if (arStartWatchdog) clearTimeout(arStartWatchdog);
        arStartWatchdog = null;
    }

    /** Give up when the engine neither attaches nor asks for the camera within 12 s. */
    function armARStartWatchdog(startGeneration: number): void {
        clearARStartWatchdog();
        arStartWatchdog = setTimeout(() => {
            arStartWatchdog = null;
            if (isDestroyed || startGeneration !== arStartGeneration || isIn8thWallAR) return;
            arWarn('8th Wall did not start within 12 s; stopping');
            showARError("AR couldn't start in this browser. Try Safari on iPhone/iPad or Chrome on Android.");
            stop8thWallAR();
        }, 12000);
    }

    /** A short instruction over the AR view (finding the floor, placing the scene); null hides it. */
    function showARHint(text: string | null): void {
        let el = container.querySelector('.storysplat-ar-hint') as HTMLElement | null;
        if (!text) {
            el?.remove();
            return;
        }
        if (!el) {
            el = document.createElement('div');
            el.className = 'storysplat-ar-hint';
            el.setAttribute('role', 'status');
            el.style.cssText = 'position:absolute;top:16px;left:50%;transform:translateX(-50%);z-index:10000;max-width:calc(100% - 32px);padding:8px 14px;border-radius:10px;background:rgba(0,0,0,0.55);color:#fff;font:14px/1.35 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;text-align:center;pointer-events:none;';
            container.appendChild(el);
        }
        el.textContent = text;
    }

    /** Start an 8th Wall AR session */
    async function start8thWallAR(): Promise<void> {
        if (isStarting8thWallAR || isIn8thWallAR || xrSessionType === 'ar') {
            arWarn('Ignoring AR start request because AR is already starting or active', {
                isStarting8thWallAR,
                isIn8thWallAR,
                xrSessionType,
            });
            return;
        }

        // The 8th Wall engine renders with WebGL only. XR scenes start on WebGL2 on AR-capable
        // devices (xrNeedsWebGL); this covers a host that forced WebGPU some other way.
        if ((app.graphicsDevice as { isWebGPU?: boolean }).isWebGPU) {
            showARError('AR needs WebGL on this device. Reload the page and try again.');
            return;
        }
        setStereoMode(null);
        isStarting8thWallAR = true;
        const startGeneration = ++arStartGeneration;
        last8thWallTrackingStatus = '';

        try {
            arInfo('Starting 8th Wall AR', {
                mobile: isMobileDevice(),
                devicePixelRatio: window.devicePixelRatio,
                canvas: {
                    width: canvas.width,
                    height: canvas.height,
                    clientWidth: canvas.clientWidth,
                    clientHeight: canvas.clientHeight,
                },
            });
            const XR8 = await loadXR8(container, arEngineBaseUrl);
            if (isDestroyed || startGeneration !== arStartGeneration) {
                arInfo('Ignoring stale 8th Wall AR start after async load', {
                    isDestroyed,
                    startGeneration,
                    currentGeneration: arStartGeneration,
                });
                return;
            }

            if (!XR8.PlayCanvas?.run) {
                throw new Error('8th Wall PlayCanvas wrapper is not available on XR8.PlayCanvas.run');
            }

            // Create StorySplat pipeline module
            const pipeline = createStorySplatPipeline({
                app,
                camera,
                sceneRoot: sceneContent,
                onDisableControls: () => {
                    cameraControls.disable();
                    if (characterController) characterController.disable();
                },
                onRestoreControls: () => {
                    if (currentCameraMode === 'explore') cameraControls.enable();
                    else if (currentCameraMode === 'walk' && characterController) characterController.enable();
                },
                onARStart: () => {
                    enterARState();
                },
                onAREnd: () => {
                    exitARState();
                    // Clean up placement controls
                    if (eighthWallPlacement) {
                        eighthWallPlacement.stop();
                        eighthWallPlacement = null;
                    }
                },
                onTrackingStatus: (status, reason) => {
                    last8thWallTrackingStatus = status;
                    arInfo('Tracking status callback', { status, reason });
                    eighthWallPlacement?.setTrackingStatus(status);
                },
                onException: (error) => {
                    showARError(`AR error: ${error.message}`);
                    stop8thWallAR();
                },
                onCameraStatus: (status) => {
                    // The user is answering the camera prompt: no time limit while they decide.
                    if (status === 'requesting' || status === 'hasStream' || status === 'hasVideo') clearARStartWatchdog();
                    if (status === 'failed') {
                        showARError(window.self !== window.top
                            ? 'AR needs the camera, which this page blocks. Open the scene in its own tab, or ask the site to allow camera in its embed.'
                            : 'AR needs the camera. Allow camera access for this site in your browser settings, then try again.');
                        stop8thWallAR();
                    }
                },
            });

            if (isXR8RuntimeRunning(XR8)) {
                arWarn('XR8 was already running before AR start; stopping stale session first');
                stopXR8Runtime(XR8);
                xr8PlayCanvasSessionActive = false;
            }

            capturePreARState();
            watchXR8PageStyles(app.graphicsDevice.canvas as HTMLCanvasElement);

            // Re-configure controller before each run (singleton load only configures once)
            XR8.XrController.configure({ scale: 'absolute' });

            const arCanvas = app.graphicsDevice.canvas as HTMLCanvasElement;
            const graphicsDeviceInfo = app.graphicsDevice as unknown as { webgl2?: unknown; isWebGL2?: unknown };
            const runConfig = {
                canvas: arCanvas,
                ownRunLoop: false,
                webgl2: !!(graphicsDeviceInfo.webgl2 || graphicsDeviceInfo.isWebGL2),
                cameraConfig: XR8.XrConfig?.camera ? { direction: XR8.XrConfig.camera().BACK } : undefined,
            };

            arInfo('Running XR8.PlayCanvas integration', {
                runConfig: {
                    ownRunLoop: runConfig.ownRunLoop,
                    webgl2: runConfig.webgl2,
                    hasCameraConfig: !!runConfig.cameraConfig,
                },
                modules: ['reality', 'storysplat-ar'],
            });

            // XR8's PlayCanvas wrapper owns the correct frame order:
            // XR8 pre-render -> camera feed render -> PlayCanvas scene render -> XR8 post-render.
            if (isDestroyed || startGeneration !== arStartGeneration) {
                arInfo('Ignoring stale 8th Wall AR start before runtime run', {
                    isDestroyed,
                    startGeneration,
                    currentGeneration: arStartGeneration,
                });
                return;
            }
            ensurePlayCanvasGlobalForXR8();
            // Marked active before run(): the engine can report an exception synchronously inside
            // run(), and stop8thWallAR() must then stop the runtime it just started.
            xr8PlayCanvasSessionActive = true;
            XR8.PlayCanvas.run(
                { pcCamera: camera, pcApp: app },
                [
                    XR8.XrController.pipelineModule(),
                    pipeline,
                ],
                runConfig,
            );
            if (isDestroyed || startGeneration !== arStartGeneration) {
                arInfo('Ignoring stale 8th Wall AR start after runtime run', {
                    isDestroyed,
                    startGeneration,
                    currentGeneration: arStartGeneration,
                });
                return;
            }
            // The engine neither attaches nor reports an exception when the browser can't run it
            // (it cancels its own start); don't leave the button saying "Exit AR" over nothing.
            armARStartWatchdog(startGeneration);

            // Start placement controls (tap to place, pinch to scale/rotate)
            eighthWallPlacement?.stop();
            eighthWallPlacement = new ARPlacement({
                app,
                camera: camera.camera!,
                sceneRoot: sceneContent,
                canvas: arCanvas,
                tapMode: refocusTapMode,
                onHint: showARHint,
                shouldIgnoreTap: (clientX, clientY) => {
                    const rect = arCanvas.getBoundingClientRect();
                    const x = clientX - rect.left;
                    const y = clientY - rect.top;
                    return !!raycastPortals(x, y) || !!raycastHotspots(x, y) || interactiveMeshAtClientPoint(clientX, clientY);
                },
                onPlaced: (position) => {
                    arInfo('Scene placement callback', { position });
                },
            });
            if (last8thWallTrackingStatus) {
                eighthWallPlacement.setTrackingStatus(last8thWallTrackingStatus);
            }
            eighthWallPlacement.start();

        } catch (err) {
            if (isDestroyed || startGeneration !== arStartGeneration) {
                arWarn('Ignoring stale 8th Wall AR start error', err);
                return;
            }
            arError('Failed to start 8th Wall AR', err);
            const msg = err instanceof Error ? err.message : String(err);
            showARError(`AR failed to start: ${msg}`);
            // Stops a runtime run() already started, the placement controls and the AR state.
            stop8thWallAR();
            restorePlayCanvasGlobalForXR8();
        } finally {
            if (startGeneration === arStartGeneration) {
                isStarting8thWallAR = false;
            }
        }
    }

    /** Stop an 8th Wall AR session */
    function stop8thWallAR(): void {
        arStartGeneration += 1;
        isStarting8thWallAR = false;
        clearARStartWatchdog();
        showARHint(null);
        // Don't gate on isIn8thWallAR — it can desync from isInXR if onARStart
        // never fired (e.g. the pipeline errored mid-start). We still need to
        // tear down whatever state did get applied.
        const xr8Running = isXR8RuntimeRunning(window.XR8);
        if (!xr8Running && !isIn8thWallAR && xrSessionType !== 'ar' && !isStarting8thWallAR && !preArState) {
            replayDeferredSkyboxApply();
            restorePlayCanvasGlobalForXR8();
            return;
        }
        arInfo('Stopping 8th Wall AR', {
            isIn8thWallAR,
            xrSessionType,
            xr8Running,
        });
        try {
            if (xr8Running && window.XR8) {
                stopXR8Runtime(window.XR8);
            }
        } catch (err) {
            arError('Error stopping 8th Wall AR', err);
        } finally {
            xr8PlayCanvasSessionActive = false;
        }
        if (eighthWallPlacement) {
            eighthWallPlacement.stop();
            eighthWallPlacement = null;
        }
        exitARState();
    }

    // Setup XR: VR on headsets in every scene, elsewhere when asked for (vrOfferedTo); AR is the
    // scene's opt-in (beta).
    function setupXR(): void {
        const hasNativeXR = !!app.xr;
        const offersVR = hasNativeXR && vrOfferedTo(config, vrRequest);
        // A page that asked for VR (?vr=1, VR mode, the vr option) shows the VR button as a large
        // "Enter VR" prompt until VR starts or the visitor starts using the 2D view.
        let vrPrompt = vrRequest === true;
        const setVrPrompt = (on: boolean) => {
            vrPrompt = on;
            const button = uiElements.vrButton;
            if (!button || button.classList.contains('active')) return;
            button.classList.toggle('prompt', on);
            button.textContent = getButtonLabel(currentButtonLabels, on ? 'enterVr' : 'vr');
            // Both sit in the middle of the view: the visitor asked for VR, so the prompt wins.
            if (on) hideControlsCard();
        };
        // Not on a press of the prompt itself: shrunk mid-press, it would no longer be under the pointer to click.
        const dismissVrPrompt = (event?: Event) => {
            if (!vrPrompt || (event && uiElements.vrButton?.contains(event.target as Node))) return;
            setVrPrompt(false);
        };

        // --- AR Setup (8th Wall — requires camera + mobile device) ---
        if (sceneOffersAR(config)) {
            const arCapable = isArCapableDevice();
            if (arCapable) {
                uiElements.arButton?.classList.add('available');
                arInfo('AR is available via 8th Wall', { arCapable });

                // Pre-load the 8th Wall engine (about 6.5 MB) so it's ready when the user taps AR:
                // mobile browsers want getUserMedia() inside the tap's gesture, and loading the
                // scripts on tap can let the gesture expire. Wait until the scene has loaded and
                // the browser is idle, so it doesn't compete with splat streaming. A failed
                // preload (e.g. a host that doesn't serve the engine) hides the button quietly
                // instead of showing an error nobody asked for.
                const preloadArEngine = () => {
                    if (isDestroyed) return;
                    loadXR8(container, arEngineBaseUrl).catch((err) => {
                        const msg = err instanceof Error ? err.message : String(err);
                        arWarn('8th Wall pre-load failed; hiding the AR button', { message: msg });
                        uiElements.arButton?.classList.remove('available');
                    });
                };
                const whenIdle = (fn: () => void) => {
                    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
                    if (idle) idle(fn, { timeout: 4000 }); else setTimeout(fn, 1500);
                };
                const onSceneLoaded = () => { events.off('loaded', onSceneLoaded); whenIdle(preloadArEngine); };
                events.on('loaded', onSceneLoaded);
            } else {
                arInfo('AR not available', { arCapable });
            }
        }

        // Pre-XR state for restoration on XR end
        let preXrColorUpdateAngle = 10;
        let vrBudgetCapped = false;
        // Where the canvas sat in the container: Chrome Android moves it during XR.
        let preXrCanvasNextSibling: Node | null = null;

        // Cached camera rig transforms for restoration on XR end
        const preXrRigPosition = new pc.Vec3();
        const preXrRigRotation = new pc.Quat();
        const preXrCameraPosition = new pc.Vec3();
        const preXrCameraRotation = new pc.Quat();

        // A VR request is in flight between the click and the session starting (or failing).
        let vrStartPending = false;
        // Some headsets have no 'local-floor' space; after a failed start the next try uses 'local'.
        let vrSpaceType: string = pc.XRSPACE_LOCALFLOOR;
        let xrNavigation: VrFloatNavigation | null = null;
        const xrInputHandlers = new Map<pc.XrInputSource, { start: () => void; end: () => void }>();
        // Selects aimed at a hotspot / portal / the info panel: activated on release.
        const pendingXrActivations = new Map<pc.XrInputSource, () => void>();

        const setVrPending = (pending: boolean) => {
            vrStartPending = pending;
            uiElements.vrButton?.classList.toggle('pending', pending);
            if (pending) uiElements.vrButton?.setAttribute('aria-busy', 'true');
            else uiElements.vrButton?.removeAttribute('aria-busy');
        };

        const onVrStart = () => {
            if (isIn8thWallAR) return;
            setVrPending(false);
            dismissVrPrompt();
            // Sessions started outside the VR button (API, browser UI) too.
            setStereoMode(null);
            isInXR = true;
            xrSessionType = 'vr';
            console.log('[StorySplat Viewer] VR session started');
            uiElements.vrButton?.classList.add('active');
            if (uiElements.vrButton) uiElements.vrButton.textContent = getButtonLabel(currentButtonLabels, 'exitVr');
            cameraControls.disable();
            if (characterController) characterController.disable();
            if (droneController) droneController.disable();

            // Cache camera rig transforms for restoration on session end
            preXrRigPosition.copy(cameraRig.getPosition());
            preXrRigRotation.copy(cameraRig.getRotation());
            preXrCameraPosition.copy(camera.getPosition());
            preXrCameraRotation.copy(camera.getRotation());
            const canvasEl = app.graphicsDevice.canvas as HTMLCanvasElement;
            preXrCanvasNextSibling = canvasEl.parentElement === container ? canvasEl.nextSibling : null;

            // Start where the camera was: eyes at its height, facing its heading. With
            // 'local-floor' the rig is the floor, so it sits a player height below the eyes;
            // 'local' puts the origin at eye level. There is no gravity: the visitor floats
            // there and flies from it (vrNavigation.ts). The heading comes from the forward
            // vector: getEulerAngles().y only covers ±90°.
            const forward = camera.forward;
            const yaw = Math.atan2(-forward.x, -forward.z) * pc.math.RAD_TO_DEG;
            vrFloorBelowEye = vrSpaceType === pc.XRSPACE_LOCALFLOOR ? playerHeight : 0;
            vrStopIndex = currentWaypointIndex;
            const floorY = preXrCameraPosition.y - vrFloorBelowEye;
            cameraRig.setPosition(preXrCameraPosition.x, floorY, preXrCameraPosition.z);
            cameraRig.setEulerAngles(0, yaw, 0);
            // Fly speed and teleport reach follow the scene's size (a room vs an aerial scan).
            xrNavigation?.configure(vrNavigationTuning(computeRenderBounds(sceneContent)?.radius ?? 0));

            // Enable continuous rendering for XR (normally on-demand)
            app.autoRender = true;

            // --- VR Performance Optimizations ---
            // Widen the gsplat SH color update threshold to reduce per-frame color work in stereo.
            // (colorUpdateDistance is a no-op in PlayCanvas 2.x: distance scaling is automatic.)
            if (app.scene.gsplat) {
                preXrColorUpdateAngle = app.scene.gsplat.colorUpdateAngle ?? 10;
                app.scene.gsplat.colorUpdateAngle = 20;
                // Both eyes are drawn every frame: hold a large LOD budget to the mobile preset's.
                if (!compareSplitView && effectiveSplatBudget() > VR_SPLAT_BUDGET) {
                    setSceneSplatBudget(app.scene.gsplat, VR_SPLAT_BUDGET);
                    vrBudgetCapped = true;
                }
            }
            // Post-processing off in VR (CameraFrame and the FXAA post-effect: stereo doubles the cost)
            suspendPostEffectsForXR();
            // Disable reticle (no mouse cursor in VR)
            reticleEntity.enabled = false;
            reticleVisible = false;
            reticleOpacity = 0;

            // The VR interface: made on the first session, with this scene's menus.
            vrSceneMenu = uiOpts.hideSceneMenu ? [] : sceneMenuItems(config.portals ?? [], config.uiOptions?.sceneMenuLinks ?? []);
            vrUi ??= new VrInterface(app, cameraRig, camera, vrHost);
            (app as ViewerRuntimeApp).__vrUi = vrUi;
            vrUi.start();
            vrUi.setHtmlMeshCards(htmlMeshCardsForVr());
            // Overlays already up show in the headset too.
            for (const overlay of authoredOverlays) if (overlayElements.has(overlay.id)) vrUi.showOverlay(overlay);

            events.emit('xrStart', { type: 'vr' });
        };

        const onVrEnd = () => {
            if (isIn8thWallAR) return;
            setVrPending(false);
            isInXR = false;
            xrSessionType = null;
            pendingXrActivations.clear();
            if (xrNavigation) xrNavigation.enableTeleport = true;
            console.log('[StorySplat Viewer] VR session ended');
            // Links saved in VR ("Open after VR"): listed over the page once one can open them.
            const savedLinks = vrUi?.takeSavedLinks() ?? [];
            vrUi?.end();
            arContentVisible = false;
            arContentSource = null;
            if (savedLinks.length && showUI && !isDestroyed) {
                showVrSavedLinks(container, savedLinks, { title: getButtonLabel(currentButtonLabels, 'vrSavedLinksTitle'), close: getButtonLabel(currentButtonLabels, 'close') }, uiColor);
            }
            // Torn down mid-session (a portal hop, destroy): the camera and app are gone.
            if (isDestroyed || !camera.camera) {
                events.emit('xrEnd', {});
                return;
            }
            uiElements.vrButton?.classList.remove('active');
            if (uiElements.vrButton)
                uiElements.vrButton.textContent = getButtonLabel(currentButtonLabels, 'vr');
            // The 3D info panel is VR-only: don't leave it floating in the 2D view.
            hideARContent();

            // Restore camera rig transforms to pre-VR state
            cameraRig.setPosition(preXrRigPosition);
            cameraRig.setRotation(preXrRigRotation);
            camera.setPosition(preXrCameraPosition);
            camera.setRotation(preXrCameraRotation);

            // Restore continuous rendering. NOTE: PlayCanvas ships with autoRender=TRUE (the
            // old comment here believed the default was on-demand) — leaving this false froze
            // the 2D viewer after exiting VR, since nothing re-arms renderNextFrame on camera
            // movement.
            app.autoRender = true;
            app.renderNextFrame = true;

            // Restore near clip once the engine has finished ending the session (xrEnd.ts)
            const vrCamera = camera.camera;
            restoreNearClipAfterXrEnd(vrCamera, config.nearClip || 0.1, () => !isDestroyed && camera.camera === vrCamera);

            if (currentCameraMode === 'explore') cameraControls.enable();
            else if (currentCameraMode === 'walk' && characterController) characterController.enable();
            else if (currentCameraMode === 'fpv' && droneController) droneController.enable();

            // --- Restore VR Performance Optimizations ---
            if (app.scene.gsplat) {
                app.scene.gsplat.colorUpdateAngle = preXrColorUpdateAngle;
            }
            if (vrBudgetCapped) {
                vrBudgetCapped = false;
                applySplatBudget(baseSplatBudget);
            }
            restorePostEffectsAfterXR();

            // Chrome Android moves the canvas during XR — put it back where it was in the container
            requestAnimationFrame(() => {
                if (isDestroyed) return;
                const canvasEl = app.graphicsDevice.canvas as HTMLCanvasElement;
                if (canvasEl.parentElement !== container) {
                    const before = preXrCanvasNextSibling?.parentNode === container ? preXrCanvasNextSibling : null;
                    container.insertBefore(canvasEl, before);
                }
                app.renderNextFrame = true;
            });

            events.emit('xrEnd', {});
        };

        // Pointing at hotspots and portals with a controller or hand: a select (trigger / pinch)
        // aimed at one activates it on release, like a click, instead of teleporting.
        const onXrInputAdd = (inputSource: pc.XrInputSource) => {
            const start = () => {
                if (!isInXR || isIn8thWallAR) return;
                // A select while the system has the view (the Quest menu pinch) belongs to the system.
                if (app.xr?.visibilityState !== 'visible') return;
                // The VR interface first: a ray on a panel presses it, and never teleports.
                if (vrUi?.beginSelect(inputSource)) {
                    pendingXrActivations.set(inputSource, () => vrUi?.endSelect(inputSource));
                    if (xrNavigation) xrNavigation.enableTeleport = false;
                    return;
                }
                const activate = pickXrTarget(inputSource.getOrigin().clone(), inputSource.getDirection().clone());
                if (!activate) return;
                pendingXrActivations.set(inputSource, activate);
                if (xrNavigation) xrNavigation.enableTeleport = false;
            };
            const end = () => {
                const activate = pendingXrActivations.get(inputSource);
                if (!activate) return;
                pendingXrActivations.delete(inputSource);
                // After this select's other handlers: the engine's teleport handler can run after this one
                // (its scripts join once VR is available) and must still see teleport off.
                queueMicrotask(() => {
                    if (xrNavigation && pendingXrActivations.size === 0) xrNavigation.enableTeleport = true;
                });
                activate();
            };
            inputSource.on('selectstart', start);
            inputSource.on('selectend', end);
            xrInputHandlers.set(inputSource, { start, end });
        };
        const onXrInputRemove = (inputSource: pc.XrInputSource) => {
            const handlers = xrInputHandlers.get(inputSource);
            if (!handlers) return;
            inputSource.off('selectstart', handlers.start);
            inputSource.off('selectend', handlers.end);
            xrInputHandlers.delete(inputSource);
            pendingXrActivations.delete(inputSource);
            if (xrNavigation && pendingXrActivations.size === 0) xrNavigation.enableTeleport = true;
        };

        // --- VR Setup (native WebXR, every scene — 8th Wall doesn't do VR) ---
        // The XR scripts join the camera rig once VR is available, so visitors without a headset
        // don't build their meshes and materials (not needed for 8th Wall AR).
        // XrControllers: auto-loads controller/hand models from WebXR Input Profiles CDN
        // VrFloatNavigation: flying without gravity, snap turn, and teleports onto the scene
        let vrScriptsAttached = false;
        const attachVrScripts = () => {
            if (vrScriptsAttached || isDestroyed) return;
            vrScriptsAttached = true;
            cameraRig.addComponent('script');
            // Controller and hand models draw self-lit: their lit materials were black in scenes without lights.
            app.on('xr:controller:add', (_inputSource: pc.XrInputSource, entity: pc.Entity) => showControllerSelfLit(entity));
            cameraRig.script!.create(XrControllers);
            xrNavigation = cameraRig.script!.create(VrFloatNavigation) as unknown as VrFloatNavigation | null;
            if (xrNavigation) {
                xrNavigation.surfacePick = (start, end) => vrSurfacePick(start, end);
                xrNavigation.surfaceReady = () => vrSurfaceReady();
                // A / X: next tour stop, B / Y: previous.
                xrNavigation.onStopButton = (step) => stepVrStop(step);
                // A hand pointing at a scrollable panel scrolls it with its stick instead of flying.
                xrNavigation.stickCaptured = (hand) => vrUi?.stickCaptured(hand) ?? false;
            }
        };
        const onVrAvailable = (available: boolean) => {
            uiElements.vrButton?.classList.toggle('available', available);
            if (!available) return;
            attachVrScripts();
            if (vrPrompt) setVrPrompt(true);
        };
        if (offersVR) {
            const xr = app.xr!;
            if (xr.isAvailable(pc.XRTYPE_VR)) {
                onVrAvailable(true);
                console.log('[StorySplat Viewer] VR is available');
            }
            xr.on('available:' + pc.XRTYPE_VR, onVrAvailable);
            xr.on('start', onVrStart);
            xr.on('end', onVrEnd);
            xr.input.on('add', onXrInputAdd);
            xr.input.on('remove', onXrInputRemove);
        }

        // --- VR button click handler (native WebXR) ---
        const onVrButtonClick = () => {
            const xr = app.xr!;
            if (isInXR && xrSessionType === 'vr') {
                xr.end();
                return;
            }
            // One request at a time. A second click while the first is pending starts a second
            // requestSession, which the browser rejects, and that rejection broke the first
            // session's setup (a live session nobody rendered, "already started" until reload).
            if (vrStartPending || xrSessionType !== null || xr.active || isInXR || isStarting8thWallAR || isIn8thWallAR) return;
            if (!xr.isAvailable(pc.XRTYPE_VR)) return;
            const stereoBefore = stereoFormat;
            const nearBefore = camera.camera!.nearClip;
            setStereoMode(null);
            setVrPending(true);
            xrSessionType = 'vr';
            // The session takes its near plane when it starts, so set it first.
            camera.camera!.nearClip = 0.01;
            const spaceType = vrSpaceType;
            camera.camera!.startXr(pc.XRTYPE_VR, spaceType, {
                optionalFeatures: ['hand-tracking'],
                callback: (err: Error | null) => {
                    if (!err) return;
                    console.error('[StorySplat Viewer] Failed to start VR:', err);
                    setVrPending(false);
                    if (isDestroyed) return;
                    if (!isInXR) xrSessionType = null;
                    if (camera.camera && !isInXR) camera.camera.nearClip = nearBefore;
                    if (stereoBefore && !isInXR) setStereoMode(stereoBefore);
                    const tryLocalNext = spaceType === pc.XRSPACE_LOCALFLOOR;
                    if (tryLocalNext) vrSpaceType = pc.XRSPACE_LOCAL;
                    showARError(`Couldn't start VR${err.message ? `: ${err.message}` : ''}.${tryLocalNext ? ' Tap VR to try again.' : ''}`, uiElements.vrButton);
                },
            });
        };
        if (uiElements.vrButton && offersVR) {
            uiElements.vrButton.addEventListener('click', onVrButtonClick);
            if (vrPrompt) container.addEventListener('pointerdown', dismissVrPrompt, true);
        }

        // --- AR button click handler (always 8th Wall) ---
        const onArButtonClick = () => {
            // Treat *any* AR indicator as "we're in AR, exit it". The two
            // flags (xrSessionType and isIn8thWallAR) are set by different
            // code paths and can briefly disagree — the button must still
            // exit in that state or it soft-locks.
            if (xrSessionType === 'ar' || isIn8thWallAR || isStarting8thWallAR) {
                stop8thWallAR();
                return;
            }
            if (isInXR || vrStartPending) return; // VR is active or starting
            start8thWallAR();
        };
        if (uiElements.arButton) {
            uiElements.arButton.addEventListener('click', onArButtonClick);
        }

        xrTeardown = () => {
            uiElements.vrButton?.removeEventListener('click', onVrButtonClick);
            container.removeEventListener('pointerdown', dismissVrPrompt, true);
            uiElements.arButton?.removeEventListener('click', onArButtonClick);
            const xr = app.xr;
            if (!xr) return;
            // Our handlers keep this whole viewer alive through navigator.xr (the engine's
            // devicechange listener holds the XrManager) unless they are removed.
            xr.off('available:' + pc.XRTYPE_VR, onVrAvailable);
            xr.off('start', onVrStart);
            xr.off('end', onVrEnd);
            xr.input?.off('add', onXrInputAdd);
            xr.input?.off('remove', onXrInputRemove);
            for (const [inputSource, handlers] of xrInputHandlers) {
                inputSource.off('selectstart', handlers.start);
                inputSource.off('selectend', handlers.end);
            }
            xrInputHandlers.clear();
            pendingXrActivations.clear();
            // No xr.end() here: both callers destroy the app next, and app.destroy() ends a live
            // session itself. Ending it here too made that second end() reject (the session had
            // already ended, its end event not yet in).
        };
    }
    // ── VR travel: views the 2D viewer flies to are reached by teleport in VR ──
    // (hotspot camera actions, goToExtraWaypoint, goToWaypoint / Next / Prev, A / B on a controller)
    /** How far the rig sits below the eyes in this session's space (the player height on 'local-floor'). */
    let vrFloorBelowEye = 0;
    /** The tour stop VR is at: A / B and nextWaypoint() / prevWaypoint() step from it. */
    let vrStopIndex = 0;
    const _vrForward = new pc.Vec3();
    const _vrLookAt = new pc.Vec3();
    /** A WebXR VR session is running (not 8th Wall AR). */
    function inVrSession(): boolean {
        return isInXR && xrSessionType === 'vr' && !isIn8thWallAR;
    }
    /** Teleport the rig so the visitor's head lands at `eye`, facing along `forward`. False outside VR. */
    function placeVrRigAtView(eye: pc.Vec3, forward: pc.Vec3): boolean {
        if (!inVrSession()) return false;
        if (![eye.x, eye.y, eye.z].every(Number.isFinite)) return false;
        const headLocal = camera.getLocalPosition();
        const headYaw = yawOfDirection(camera.getLocalRotation().transformVector(pc.Vec3.FORWARD, _vrForward));
        // Looking straight up or down: keep the heading the visitor has now.
        const viewYaw = Number.isFinite(yawOfDirection(forward)) ? yawOfDirection(forward) : yawOfDirection(camera.forward);
        const place = vrRigPlacement(eye, Number.isFinite(viewYaw) ? viewYaw : 0, headLocal, Number.isFinite(headYaw) ? headYaw : 0, vrFloorBelowEye);
        cameraRig.setPosition(place.x, place.y, place.z);
        cameraRig.setEulerAngles(0, place.yaw, 0);
        renderScheduler.requestFrame('vr-teleport');
        return true;
    }
    function teleportVrToExtraWaypoint(waypoint: ExtraWaypointData): boolean {
        const view = extraWaypointView(waypoint, EXTRA_WAYPOINT_LOOK_DISTANCE);
        return !!view && placeVrRigAtView(view.position, _vrLookAt.sub2(view.lookAt, view.position));
    }
    function teleportVrToOrbitView(view: HotspotOrbitView | undefined): boolean {
        if (!view?.cameraPosition || !view.pivotPoint) return false;
        const p = playCanvasPositionFromSceneVec3(view.cameraPosition);
        const f = playCanvasPositionFromSceneVec3(view.pivotPoint);
        const eye = new pc.Vec3(p.x, p.y, p.z);
        return placeVrRigAtView(eye, _vrLookAt.set(f.x - p.x, f.y - p.y, f.z - p.z));
    }
    /**
     * In a tour-mode scene, VR's moves along the tour take the tour with them (its waypoint events,
     * audio, narration and swaps), as the tour's own camera does in 2D; explore scenes don't run it.
     */
    function followTourInVr(progress: number): void {
        if (currentCameraMode !== 'tour') return;
        currentProgress = progress;
        targetProgress = progress;
        applyTourProgress(progress);
    }
    /** Go to tour stop `index` in VR: teleport onto its view. */
    function goToVrStop(index: number): boolean {
        const position = waypointPositions[index];
        const rotation = waypointRotations[index];
        if (!position || !rotation || !placeVrRigAtView(position, rotation.transformVector(pc.Vec3.FORWARD, _vrForward))) return false;
        vrStopIndex = index;
        followTourInVr(getProgressForWaypointIndex(index));
        // The stop's name (and info) on the VR banner, as the page's banner shows it on the tour.
        const banner = getWaypointBannerContent('tour', config.waypoints?.[index]);
        vrUi?.showBanner(banner?.title ?? '', banner?.info ?? '');
        return true;
    }
    /** A hotspot's "go to n % of the tour" in VR: the tour's pose there. */
    function teleportVrToProgress(progress: number): boolean {
        // getPathPoseAtProgress reads timeline progress: a ping-pong tour's first half is the way out.
        const pose = getPathPoseAtProgress(loopMode === 'pingpong' ? progress / 2 : progress);
        if (!pose) return false;
        const rotation = new pc.Quat(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w);
        if (!placeVrRigAtView(new pc.Vec3(pose.position.x, pose.position.y, pose.position.z), rotation.transformVector(pc.Vec3.FORWARD, _vrForward))) return false;
        followTourInVr(progress);
        return true;
    }
    /** Next (1) or previous (-1) tour stop in VR, stepping over waypoints with "stop here" off. */
    function stepVrStop(step: 1 | -1): void {
        if (!config.waypoints?.length) return;
        const next = waypointStepTarget(config.waypoints, vrStopIndex, step, loopMode === 'loop');
        if (next !== null) goToVrStop(next);
    }
    // ═══ VR interface ═══
    // What a headset shows in place of the page UI (vrUi/vrInterface.ts): the wrist menu, the
    // floating menus (waypoints, directory, Layers, measurements, scenes), popups with their media,
    // overlays and the tour banner, and the saved measurements. Made on the first VR session; it
    // reads and drives the same state as the page's menus.
    let vrUi: VrInterface | null = null;
    let vrLabelsCache: VrLabels | null = null;
    /** "Show measurements" in VR; null: the scene's own default (vrMeasurementSource). */
    let vrMeasurementsShown: boolean | null = null;
    /** The scene menu as VR lists it (set when a session starts). */
    let vrSceneMenu: VrSceneMenuItem[] = [];
    /** Set by the measurement tool: the saved measurements VR draws itself. */
    let vrMeasurementSource: { shownByDefault: () => boolean; hasAny: () => boolean; geometry: () => VrMeasurementGeometry } | null = null;
    const vrMeasurementInfo: { sets: MeasurementSetsState | null; shown: boolean; geometry: () => VrMeasurementGeometry | null } = { sets: null, shown: false, geometry: () => null };
    /** Whether the popup is up (the VR interface's popup panel). */
    let arContentVisible = false;
    /** The popup it shows: selecting the same hotspot again closes it. */
    let arContentSource: VrPopupSource | null = null;
    const buildVrLabels = (): VrLabels => {
        const gl = (key: keyof ViewerButtonLabels) => getButtonLabel(currentButtonLabels, key);
        const dir = directoryLabels(currentButtonLabels);
        return {
            menu: gl('vrMenu'), waypoints: gl('waypoints'), next: gl('next'), previous: gl('previous'), scenes: gl('scenes'),
            mute: gl('mute'), unmute: gl('unmute'), exitVr: gl('exitVr'), close: gl('close'),
            measurements: gl('measureTitle'), measurementSets: gl('measureSets'), otherMeasurements: gl('measureOtherSet'),
            allMeasurements: gl('measureAllSets'), showMeasurements: gl('vrShowMeasurements'),
            views: dir.views, search: dir.search, noMatch: dir.noMatch, count: dir.count,
            savedLinks: gl('vrSavedLinks'), savedLinksTitle: gl('vrSavedLinksTitle'), saveLink: gl('vrSaveLink'), linkSaved: gl('vrLinkSaved'),
            play: gl('vrPlay'), pause: gl('vrPause'), photos: gl('vrPhotos'), video: gl('vrVideo'), pdf: gl('vrPdf'),
            modelOutsideVr: gl('vrModelOutsideVr'), webContentOutsideVr: gl('vrWebContent'),
        };
    };
    /** A scene-menu entry picked in VR: a portal hops (VR resumes from the prompt), media shows on the popup, a link waits for after VR. */
    function openVrSceneItem(item: VrSceneMenuItem): void {
        if (item.kind === 'portal') {
            const portal = config.portals?.find((p) => p.id === item.id);
            if (portal) void handlePortalNavigation(portal);
            return;
        }
        const link = (config.uiOptions?.sceneMenuLinks ?? []).find((l) => l.id === item.id);
        if (!link) return;
        runClickCutaway(link.cutaway);
        if (link.swapAction === 'splat-swap' && link.targetSplatUrl) {
            void instance.goToSplat(link.targetSplatUrl);
            return;
        }
        const media = link.mediaUrl || link.url;
        if (link.contentType === 'pdf') vrUi?.showPopup({ title: link.label, pdfUrl: media });
        else if (link.contentType === 'image') vrUi?.showPopup({ title: link.label, imageUrl: media });
        else if (link.contentType === 'video') vrUi?.showPopup({ title: link.label, videoUrl: media });
        else if (vrUi) {
            vrUi.saveLink({ url: link.contentType === 'iframe' ? media : link.url, label: link.label });
            vrUi.showBanner(getButtonLabel(currentButtonLabels, 'vrLinkSaved'), link.label, 2.5);
        }
    }
    /** HTML meshes are page content (CSS3D) a headset can't show: VR puts a card in their place. */
    const htmlMeshCardsForVr = () => {
        const manager = (app as ViewerRuntimeApp).__htmlMeshManager;
        const cards: Array<{ position: pc.Vec3; rotation: pc.Quat; url: string | null; title: string }> = [];
        if (!manager) return cards;
        for (const mesh of manager.getAllMeshes().values()) {
            if (!mesh.entity.enabled) continue;
            const cfg = mesh.config as typeof mesh.config & { iframeUrl?: unknown; title?: unknown; name?: unknown };
            const url = typeof cfg.iframeUrl === 'string' && /^https?:\/\//i.test(cfg.iframeUrl) ? cfg.iframeUrl : null;
            const title = typeof cfg.title === 'string' ? cfg.title : typeof cfg.name === 'string' ? cfg.name : '';
            cards.push({ position: mesh.entity.getPosition().clone(), rotation: mesh.entity.getRotation().clone(), url, title });
        }
        return cards;
    };
    const vrHost: VrInterfaceHost = {
        labels: () => (vrLabelsCache ??= buildVrLabels()),
        accent: () => uiColor,
        waypoints: () => {
            const menuOn = uiOpts.showWaypointList !== false;
            const stops = (config.waypoints ?? []).map((w) => ({ name: w.name, hideInMenu: !menuOn || w.hideInMenu === true }));
            const extras = menuOn ? extraWaypointMenuEntries(config.extraWaypoints) : [];
            if (!stops.length && !extras.length) return null;
            return { stops, extras, current: vrStopIndex };
        },
        goToStop: (index) => { goToVrStop(index); },
        stepStop: (step) => stepVrStop(step),
        goToExtraWaypoint: (id) => { goToExtraWaypointById(id); },
        directory: () => (directoryState.shouldShow() ? directoryState : null),
        activateHotspot: (id) => { activateHotspotById(id, { fromDirectory: true }); },
        goToDirectoryView: (view) => {
            if (view.kind === 'waypoint') goToVrStop(view.index);
            else goToExtraWaypointById(view.id);
        },
        isHotspotAvailable: (id) => {
            const entity = hotspotEntities.find((e) => e.hotspotData?.id === id);
            return !entity || (entity.shouldBeVisible !== false && entity.animationHidden !== true);
        },
        layers: () => (regionsRuntime.hasMenu() ? regionsRuntime.layersMenu : null),
        measurements: () => {
            if (!vrMeasurementSource?.hasAny()) return null;
            vrMeasurementInfo.sets = measurementSetsState;
            vrMeasurementInfo.shown = vrMeasurementsShown ?? vrMeasurementSource.shownByDefault();
            vrMeasurementInfo.geometry = vrMeasurementSource.geometry;
            return vrMeasurementInfo;
        },
        setMeasurementsShown: (shown) => { vrMeasurementsShown = shown; },
        sceneMenu: () => vrSceneMenu,
        openSceneItem: (item) => openVrSceneItem(item),
        muted: () => (sceneHasAudio ? globalMuted : null),
        toggleMute: () => { applyMuteToggle(); },
        exitVr: () => { app.xr?.end(); },
        dismissOverlay: (id) => overlayController.dismiss(id),
        loadPdfJs,
        requestRender: (reason) => renderScheduler.requestFrame(reason),
        onPopupClosed: () => {
            arContentVisible = false;
            arContentSource = null;
        },
    };
    /**
     * Show a hotspot's (or model's) popup in VR, on the VR interface's popup panel: its text, photo
     * gallery, video, PDF pages, and "Open after VR" for its link. Page UI can't show inside an
     * immersive session; 8th Wall AR runs in the page and shows the usual popup instead.
     */
    function showARContent(hotspot: VrPopupSource): void {
        if (!isInXR || !vrUi || !inVrSession()) return;
        vrUi.showPopup(hotspot);
        arContentSource = hotspot;
        arContentVisible = true;
        console.log('[StorySplat Viewer] VR popup shows:', hotspot.title ?? '');
    }
    function hideARContent(): void {
        arContentSource = null;
        arContentVisible = false;
        vrUi?.hidePopup();
    }
    // FPS counter update throttle
    let fpsUpdateTimer = 0;
    // Add update loop for CameraControls, CharacterController, and proximity checks
    app.on('update', (dt: number) => {
        // Update FPS counter (throttled to every 500ms)
        if (uiElements.fpsCounter) {
            fpsUpdateTimer += dt;
            if (fpsUpdateTimer >= 0.5) {
                fpsUpdateTimer = 0;
                updateFpsCounter(uiElements, 1 / dt);
            }
        }
        // Update either CameraControls or CharacterController based on mode.
        // During a video-render capture the render pins the camera pose each
        // manual frame; skip these autonomous drivers so they can't fight it.
        // Hidden FPV drone entry via controller: hold both bumpers + press
        // Start/Menu. Lets a controller-only user reach the Easter egg without a
        // keyboard. Skipped in the editor so it can't fire while authoring.
        if (!isEditorMode) {
            checkFpvGamepadChord();
        }
        // In VR/AR the headset or phone owns the camera pose.
        if (!renderCaptureActive && !isInXR) {
            if (currentCameraMode === 'fpv' && droneController) {
                droneController.update(dt);
            }
            else if (currentCameraMode === 'walk' && characterController) {
                // Mobile drag-look: drain the whole-screen raw touch deltas (both halves) and apply them
                // as a direct look BEFORE update(dt), so this frame's movement uses the new heading. On
                // desktop the mobile input source is detached, so skip the per-frame read. No eye-zone
                // highlight — the entire screen is the look zone now.
                if (isMobileDevice()) {
                    const look = cameraControls.readWalkLookDelta();
                    characterController.applyLookDelta(look.x, look.y);
                }
                characterController.update(dt);
            }
            else {
                cameraControls.update(dt);
            }
        }
        // Check proximity triggers for video hotspots (needed for explore/walk mode, and in VR / AR,
        // where the visitor walks up to things whatever the scene's camera mode)
        if (!waypointControlEnabled || isInXR) {
            updateProximityTriggers();
        }
        // Update waypoint audio with proximity trigger mode
        updateWaypointAudioProximity();
        // Update audio emitter proximity
        updateAudioEmitterProximity();
        // Update custom mesh audio with proximity trigger mode
        updateCustomMeshAudioProximity();
        // Check waypoint trigger distances for interaction execution
        checkWaypointTriggerDistance();
        // Overlay triggers (spatial regions, tour progress, waypoint, timers)
        updateOverlays();
        // The VR interface: wrist menu, panels, pointers, banner, measurements
        if (vrUi && isInXR && xrSessionType === 'vr') vrUi.update(dt);
        // Update playground light balls
        updatePlaygroundBalls(dt);
        // Update entity animations only after the opaque preloader is fully
        // removed. This keeps autoplay and early public play requests at their
        // initial pose until the scene is actually visible, matching the tour
        // and 4DGS presentation gate. The editor timeline still drives
        // evaluateAt() directly.
        if (entityAnimationSystem && !isEditorMode && initialPlaybackGate.isPresentationReady()) {
            // A tour that opens on a trigger waypoint counts as arriving there.
            if (!entityAnimationsPresented) {
                entityAnimationsPresented = true;
                if (currentCameraMode === 'tour') entityAnimationSystem.handleWaypointChange(currentWaypointIndex);
            }
            entityAnimationSystem.update(dt, currentProgress);
        }
        // --- Reticle. Two modes (creator setting reticleClickToReveal):
        //   • default — continuous cursor-follow: the throttled raycast in the postrender handler
        //     snaps the ring to the surface under the cursor and sets reticleVisible on a hit.
        //   • reveal-on-click — the ring only flashes at the click point (flashReticleAt) then fades.
        // Either way it's hidden (fades out) while dragging-to-look, holding a click, or over an
        // interactable, and reappears on release. ---
        if (isInXR) {
            // No mouse cursor in VR/AR.
            reticleVisible = false;
            reticleOpacity = 0;
            reticleEntity.enabled = false;
        } else {
            // Allow the reticle on MOBILE in reveal-on-click mode (the ring flashes at the click-to-move
            // point). The !isMobile gate only guards the continuous cursor-follow mode, which has no
            // hover cursor on touch — but that mode is off whenever reticleClickToReveal is set.
            const reticleActive = reticleUserEnabled && (!isMobile || reticleClickToReveal) && (currentCameraMode === 'explore' || currentCameraMode === 'walk' || currentCameraMode === 'orbit');
            if (reticleClickToReveal) {
                // Visible only within the post-click reveal window.
                reticleVisible = reticleActive && !hoveringInteractable && !pointerDown && (performance.now() < reticleRevealUntil);
            } else {
                // Cursor-follow: visibility is set true by the postrender raycast (reticleApplyPoints)
                // on a hit and persists between throttled picks; here we only force-hide.
                if (!reticleActive) reticleVisible = false;
                if (hoveringInteractable) reticleVisible = false;
                if (pointerDown) reticleVisible = false;
            }
        }
        // Smooth fade: quick to appear on click, slow to fade away.
        const reticleTargetOpacity = reticleVisible ? 1 : 0;
        const reticleFadeRate = reticleTargetOpacity > reticleOpacity ? 12 : 3;
        reticleOpacity += (reticleTargetOpacity - reticleOpacity) * Math.min(1, dt * reticleFadeRate);
        if (Math.abs(reticleOpacity - reticleTargetOpacity) < 0.01) reticleOpacity = reticleTargetOpacity;
        // Lerp and show/hide reticle
        if (reticleOpacity > 0) {
            // Snap to target when close enough to prevent perpetual micro-drift
            const posDist = reticleCurrentPos.distance(reticleTargetPos);
            if (posDist < RETICLE_SNAP_THRESHOLD) {
                reticleCurrentPos.copy(reticleTargetPos);
            } else {
                reticleCurrentPos.lerp(reticleCurrentPos, reticleTargetPos, Math.min(1, dt * 15));
            }
            // Snap the normal too: the asymptotic lerp otherwise keeps changing the rotation by a
            // hair every frame, which keeps on-demand rendering awake.
            if (reticleCurrentNormal.dot(reticleTargetNormal) > 0.99999) {
                reticleCurrentNormal.copy(reticleTargetNormal);
            } else {
                reticleCurrentNormal.lerp(reticleCurrentNormal, reticleTargetNormal, Math.min(1, dt * 10));
            }
            reticleCurrentNormal.normalize();
            // Scale by distance to camera for consistent apparent size
            const camPos = camera.getPosition();
            const dist = camPos.distance(reticleCurrentPos);
            const baseScale = reticleColorOverride ? 0.035 : 0.12; // Smaller reticle in measurement mode
            const minScale = reticleColorOverride ? 0.08 : 0.24;
            const s = Math.max(minScale, dist * baseScale);
            // Constant offset along normal to prevent z-fighting (does not grow with distance)
            _retOffsetPos.copy(reticleCurrentPos).addScaled(reticleCurrentNormal, RETICLE_Z_OFFSET);
            // Write transforms only when they changed: a settled reticle (lerp snapped, camera
            // still) otherwise dirties its node every frame, which keeps on-demand rendering awake.
            if (_reticleLastPos.distance(_retOffsetPos) > 1e-6 || Number.isNaN(_reticleLastPos.x)) {
                _reticleLastPos.copy(_retOffsetPos);
                reticleEntity.setPosition(_retOffsetPos);
            }
            // Flatten Y axis to make it look more like a disc/ring cursor
            if (Math.abs(s - _reticleLastScale) > 1e-6 || Number.isNaN(_reticleLastScale)) {
                _reticleLastScale = s;
                reticleEntity.setLocalScale(s, s * 0.45, s);
            }
            // Resolve the target emissive, then push material changes only when a VALUE actually
            // changed (compare resolved RGB + opacity, not object identity — reticlePortalColor
            // aliases the portal's live pc.Color). Steady state (opacity snapped to 1, color
            // unchanged) then skips the full StandardMaterial parameter rebuild every frame.
            const _remis = reticleColorOverride ?? reticlePortalColor;
            const _rr = _remis ? _remis.r : 1, _rg = _remis ? _remis.g : 1, _rb = _remis ? _remis.b : 1;
            if (reticleMat.opacity !== reticleOpacity ||
                reticleMat.emissive.r !== _rr || reticleMat.emissive.g !== _rg || reticleMat.emissive.b !== _rb) {
                reticleMat.opacity = reticleOpacity;
                reticleMat.emissive.set(_rr, _rg, _rb);
                reticleMat.update();
            }
            // Orient torus to lie flush with surface (align local Y to surface normal)
            _reticleQuat.setFromDirections(pc.Vec3.UP, reticleCurrentNormal);
            if (Number.isNaN(_reticleLastQuat.w) || Math.abs(_reticleLastQuat.x - _reticleQuat.x) > 1e-6 || Math.abs(_reticleLastQuat.y - _reticleQuat.y) > 1e-6 || Math.abs(_reticleLastQuat.z - _reticleQuat.z) > 1e-6 || Math.abs(_reticleLastQuat.w - _reticleQuat.w) > 1e-6) {
                _reticleLastQuat.copy(_reticleQuat);
                reticleEntity.setRotation(_reticleQuat);
            }
            reticleEntity.enabled = true;
        } else {
            reticleEntity.enabled = false;
        }
        // Sync relighting light positions/colors from PlayCanvas entities each frame
        if (relightingEnabled && allRelightingScripts.size > 0) {
            syncRelightingLights();
            syncMeshAmbient();
            if (!(_relightSyncLogged) && relightingScript) {
                _relightSyncLogged = true;
                console.log('[StorySplat Viewer] Relighting sync active, lights:', _relightLightData.length,
                    'fade:', relightingScript.relightFade,
                    'ambient:', [relightingScript.ambientR, relightingScript.ambientG, relightingScript.ambientB],
                    'material:', !!relightingScript.material,
                    'totalScripts:', allRelightingScripts.size);
            }
        }
    });
    // --- Reticle surface finding (no GPU gsplat pick) ---
    // PlayCanvas 2.20's unified gsplat uses ONE shared work buffer + an async sorter; a second
    // per-frame render of it (the old reticlePicker.prepare over the World layer) disturbs that
    // shared state and made the splats shimmer in explore/walk. We instead find the surface point
    // with a CPU raycast against the same collision the camera already uses — cheaper (no GPU pass,
    // no readback) and flicker-free. Walk mode always has collision; explore inherits it when the
    // scene provides it. Scenes with no collision at all hide the reticle (a depth-map fallback is
    // a planned follow-up — its CPU depth decode needs device verification before it can ship).
    const _retRayNear = new pc.Vec3();
    const _retRayFar = new pc.Vec3();
    const _retRayDir = new pc.Vec3();
    const _retRayDirNorm = new pc.Vec3();
    const RETICLE_SPREAD_PX = 12; // neighbour ray offset (CSS px) for surface-normal estimation

    // The auto splat-center occupancy grid is now DENSITY-AWARE (lands on the real surface, not
    // airborne floaters), so it serves as the universal pick fallback for scenes with no mesh/voxel
    // collision. Coarse (~scene/256) — great for cursor/click-to-move, not measurement-grade.
    // Splat occupancy-grid pick REMOVED. The reticle raycasts REAL collision only — primitive
    // colliders / baked mesh / voxel via CharacterController.raycastSurface. The coarse splat-center
    // grid missed thin surfaces and cost per-frame perf, so it's gone (see also allowSplatGridPick=false).
    const ALLOW_SPLAT_GRID_PICK = false;
    type SurfacePickTarget = {
        side: ComparePickSide | null;
        entity: pc.Entity | null;
        cacheKey?: string;
        /** Collision represents the primary/before world, never the compare-after splat. */
        allowCollisionFallback: boolean;
    };

    const resolveSurfacePickTarget = (sx: number, sy: number): SurfacePickTarget => {
        if (compareSplitView && compareAfterEntity) {
            const pickCanvas = app.graphicsDevice.canvas as HTMLCanvasElement;
            const side = resolveComparePickSide(
                sx,
                sy,
                pickCanvas.clientWidth,
                pickCanvas.clientHeight,
                comparePosition,
                compareSettings.orientation === 'horizontal' ? 'horizontal' : 'vertical',
            );
            return side === 'after'
                ? {
                    side,
                    entity: compareAfterEntity,
                    cacheKey: compareAfterPickCacheKey,
                    // CharacterController collision was built from the primary
                    // splat. Falling back to it on the after side would put the
                    // reticle/refocus/measurement on invisible before geometry.
                    allowCollisionFallback: false,
                }
                : {
                    side,
                    entity: splatEntity,
                    cacheKey: splatPickCacheKey,
                    allowCollisionFallback: true,
                };
        }

        const entity = getCurrentVisibleEntity() ?? splatEntity;
        return {
            side: null,
            entity,
            cacheKey: entity === splatEntity ? splatPickCacheKey : (currentSplatUrl ?? undefined),
            allowCollisionFallback: true,
        };
    };

    // Whether the selected side has a pickable surface. Compare-after deliberately
    // has no collision fallback because the authored/baked collision belongs to the
    // primary splat; it waits for the after entity's exact CPU source instead.
    const reticleSurfaceAvailable = (
        sx: number,
        sy: number,
        target: SurfacePickTarget = resolveSurfacePickTarget(sx, sy),
    ): boolean =>
        !!(target.entity && exactSplatRaycaster.ensureSource(target.entity, target.cacheKey))
        || (target.allowCollisionFallback && !!characterController?.hasSplatSurfaceCollision);

    // Cast one screen point to the collision surface; world point or null on a miss. Always attempts
    // the CharacterController raycast (primitive colliders / baked mesh / voxel — it returns null on a
    // miss), so authored primitive nav-meshes are picked even when hasSplatSurfaceCollision is
    // conservative. No GPU gsplat pick (that corrupts the 2.20 unified sort) and no splat grid.
    // Measurement picks look through haze (ExactSplatRaycaster PickOptions.seeThroughHaze); so does
    // the cursor ring while the visitor measures, so it shows where a click lands.
    let measurePickActive = false;
    const reticleRaycast = (
        sx: number,
        sy: number,
        target: SurfacePickTarget = resolveSurfacePickTarget(sx, sy),
        seeThroughHaze = false,
    ): pc.Vec3 | null => {
        const cam = camera.camera!;
        const near = cam.nearClip, far = cam.farClip;
        cam.screenToWorld(sx, sy, near, _retRayNear);
        cam.screenToWorld(sx, sy, far, _retRayFar);
        _retRayDir.sub2(_retRayFar, _retRayNear);
        // maxDist = the actual near->far world distance for THIS pixel ((far-near)/cos θ off-centre).
        const len = _retRayDir.length();
        _retRayDirNorm.copy(_retRayDir).mulScalar(1 / (len || 1));
        return raycastSurfaceAlong(_retRayNear, _retRayDirNorm, len, target, seeThroughHaze);
    };

    // Cast a world ray (unit direction, length len) to the scene surface, or null on a miss.
    // ONE pick for the move cursor, measurements and VR teleports.
    const raycastSurfaceAlong = (origin: pc.Vec3, dirNorm: pc.Vec3, len: number, target: SurfacePickTarget, seeThroughHaze = false): pc.Vec3 | null => {
        // PRIMARY: exact-splat CPU pick — evaluates the actual gaussians along
        // the ray (renderer's math on CPU), splat-accurate on any supported
        // scene, no collision data needed, no GPU touched. ensureSource is a
        // cheap no-op once registered; while the index builds (time-sliced,
        // post-load) or for not-yet-supported formats we fall back to the
        // collision raycast (authored primitives + baked mesh/voxel surface).
        let exact: pc.Vec3 | null = null;
        // Swap scenes keep multiple splat entities alive. Compare mode additionally
        // keeps both enabled, so the side resolved from the divider must be passed as
        // an explicit source filter — nearest-across-all-sources is visually wrong.
        const pickEntity = target.entity;
        if (pickEntity) {
            try {
                if (exactSplatRaycaster.ensureSource(pickEntity, target.cacheKey)) {
                    exact = exactSplatRaycaster.pick(origin, dirNorm, len, pickEntity, seeThroughHaze ? { seeThroughHaze } : undefined);
                }
            } catch (err) {
                // A throwing source (e.g. a poisoned cache record) must degrade to the
                // collision pick — never take down the postrender loop / navigation.
                pickDiag.push('exact:pick-THREW', { error: String(err) });
                exact = null;
            }
        }
        const collision = target.allowCollisionFallback
            ? (characterController?.raycastSurface(origin, dirNorm, len) ?? null)
            : null;
        if (exact && collision) {
            // An authored collider (placed mesh, primitive) DISTINCTLY in front of the
            // splat surface must catch the pick — otherwise clicking a placed object
            // would land on the splat behind it. But when both hits describe the SAME
            // surface, prefer the exact one: the baked splat collision (voxel/mesh) is
            // an approximation that often sits slightly proud of the true surface, so
            // a plain nearest-wins would silently re-degrade accuracy everywhere. The
            // tolerance treats "nearer by more than ~15cm (or 2% of distance)" as a
            // distinct occluding object rather than baked-surface error.
            const exactDist = origin.distance(exact);
            const collisionDist = origin.distance(collision);
            const occluderEps = Math.max(0.15, exactDist * 0.02);
            return collisionDist < exactDist - occluderEps ? collision : exact;
        }
        return exact ?? collision;
    };

    // VR teleport landings (vrNavigation.ts): the scene surface on one segment of the teleport
    // arc. Each query starts a little behind the segment: the exact pick ignores its first 5 cm,
    // which would otherwise leave a blind gap at every joint of the arc. The collision raycast
    // looks at least 1 m ahead, so a hit past the segment's end is left for the next segment.
    const VR_PICK_BACKSTEP = 0.06;
    const _vrPickOrigin = new pc.Vec3();
    const _vrPickDir = new pc.Vec3();
    const vrPickTarget = (): SurfacePickTarget => {
        const entity = getCurrentVisibleEntity() ?? splatEntity;
        return {
            side: null,
            entity,
            cacheKey: entity === splatEntity ? splatPickCacheKey : (currentSplatUrl ?? undefined),
            allowCollisionFallback: true,
        };
    };
    // An arc aimed past the scene's edge tests all 32 of its segments every frame. Segments wholly
    // outside the splat's full bounds (plus a margin for the gaussians' extent) can't hit it, so
    // they skip the pick. Collision can reach past the splat: scenes with collision always pick.
    const VR_PICK_BOUNDS_MARGIN = 0.5;
    const _vrSplatBounds = new pc.BoundingBox();
    const vrSegmentMayHit = (entity: pc.Entity | null, start: pc.Vec3, end: pc.Vec3): boolean => {
        if (characterController || !entity) return true;
        const gsplat = entity.gsplat as (pc.GSplatComponent & { resource?: { aabb?: pc.BoundingBox | null } | null; customAabb?: pc.BoundingBox | null }) | undefined;
        const local = gsplat?.resource?.aabb ?? gsplat?.customAabb;
        if (!local) return true;
        _vrSplatBounds.setFromTransformedAabb(local, entity.getWorldTransform());
        const min = _vrSplatBounds.getMin(), max = _vrSplatBounds.getMax(), m = VR_PICK_BOUNDS_MARGIN;
        return Math.max(start.x, end.x) >= min.x - m && Math.min(start.x, end.x) <= max.x + m
            && Math.max(start.y, end.y) >= min.y - m && Math.min(start.y, end.y) <= max.y + m
            && Math.max(start.z, end.z) >= min.z - m && Math.min(start.z, end.z) <= max.z + m;
    };
    const vrSurfacePick = (start: pc.Vec3, end: pc.Vec3): pc.Vec3 | null => {
        const target = vrPickTarget();
        if (!vrSegmentMayHit(target.entity, start, end)) return null;
        _vrPickDir.sub2(end, start);
        const segment = _vrPickDir.length();
        if (!(segment > 1e-6)) return null;
        _vrPickDir.mulScalar(1 / segment);
        _vrPickOrigin.copy(_vrPickDir).mulScalar(-VR_PICK_BACKSTEP).add(start);
        const length = segment + VR_PICK_BACKSTEP;
        const hit = raycastSurfaceAlong(_vrPickOrigin, _vrPickDir, length, target);
        return hit && _vrPickOrigin.distance(hit) <= length + 1e-4 ? hit : null;
    };
    // Teleports can land on the scene once the exact pick index is built (this starts it on
    // devices that defer it) or the scene has collision.
    const vrSurfaceReady = (): boolean => {
        const target = vrPickTarget();
        return !!(target.entity && exactSplatRaycaster.ensureSource(target.entity, target.cacheKey)) || !!characterController;
    };

    // Fold the exact-splat pick index build into scene loading so the first
    // double-click is instant instead of paying the (async, time-sliced) SOG
    // decompress + BVH build on first pick. Retries across frames until the gsplat
    // resource is resident, then the async build proceeds on its own; bounded so an
    // unsupported / never-resident resource can't spin forever.
    const primeExactSplatPickEntity = (entity: pc.Entity, cacheKey?: string): void => {
        let attempts = 0;
        const tick = (): void => {
            if (isDestroyed) return;
            exactSplatRaycaster.ensureSource(entity, cacheKey);
            if (exactSplatRaycaster.hasSource(entity)) return; // registered → build runs itself
            if (attempts++ > 600) return;                           // ~10s @60fps safety bound
            requestAnimationFrame(tick);
        };
        tick();
    };

    const primeExactSplatPick = (cacheKey?: string): void => {
        if (splatEntity) primeExactSplatPickEntity(splatEntity, cacheKey);
    };

    // Shared validity + normal-from-neighbours logic (mirrors the previous picker behaviour).
    const reticleApplyPoints = (pts: (pc.Vec3 | null)[]): boolean => {
        const camPos = camera.getPosition();
        const isValid = (p: pc.Vec3 | null): p is pc.Vec3 => {
            if (!p || !isFinite(p.x) || !isFinite(p.y) || !isFinite(p.z)) return false;
            const d = camPos.distance(p);
            return d > 0.1 && d < 500;
        };
        let pos: pc.Vec3 | null = null;
        if (isValid(pts[0])) pos = pts[0];
        else for (let i = 1; i < pts.length; i++) { if (isValid(pts[i])) { pos = pts[i]; break; } }
        if (!pos) return false;
        reticleTargetPos.copy(pos);
        let normalComputed = false;
        const neighbors = pts.filter((p) => p !== pos && isValid(p)) as pc.Vec3[];
        if (neighbors.length >= 2) {
            _retEdgeA.sub2(neighbors[0], pos);
            _retEdgeB.sub2(neighbors[1], pos);
            if (_retEdgeA.lengthSq() > 1e-8 && _retEdgeB.lengthSq() > 1e-8) {
                _retNormal.cross(_retEdgeA, _retEdgeB).normalize();
                _retToCamera.sub2(camPos, pos);
                if (_retNormal.dot(_retToCamera) < 0) _retNormal.mulScalar(-1);
                if (_retNormal.lengthSq() > 0.5) {
                    reticleTargetNormal.copy(_retNormal);
                    normalComputed = true;
                }
            }
        }
        if (!normalComputed && reticleTargetNormal.lengthSq() < 0.5) {
            reticleTargetNormal.copy(pc.Vec3.UP);
        }
        reticleVisible = true;
        return true;
    };

    // Flash the reticle at a move destination, then fade it (see the update loop). Called from the
    // click-to-MOVE handlers (walk/explore) so the reticle appears exactly when — and exactly where —
    // the camera moves to, honoring the scene's single/double-click-to-move setting. The position is
    // the GPU depth-pick world point (the same point the camera flies to), so reticle and move stay in
    // sync. The surface normal (orientation) comes from a one-time CPU raycast at the click — only on
    // a move, NOT per frame, so it doesn't reintroduce the per-frame perf cost. (sx, sy in CSS px.)
    const flashReticleAt = (worldPoint: pc.Vec3, sx: number, sy: number): void => {
        // Show the click-to-move cursor on MOBILE too: the reticle only flashes on a move now (not a
        // per-frame hover raycast), so it's cheap, and users tapping to move want to see where they're
        // going in explore/fly/orbit/walk. (Was gated out on mobile.)
        if (isInXR || !reticleUserEnabled) return;
        if (!(currentCameraMode === 'explore' || currentCameraMode === 'walk' || currentCameraMode === 'orbit')) return;
        const sp = RETICLE_SPREAD_PX;
        const target = resolveSurfacePickTarget(sx, sy);
        const gotSurface = reticleSurfaceAvailable(sx, sy, target) && reticleApplyPoints([
            reticleRaycast(sx, sy, target),
            reticleRaycast(sx + sp, sy, target),
            reticleRaycast(sx, sy + sp, target),
            reticleRaycast(sx - sp, sy, target),
            reticleRaycast(sx, sy - sp, target),
        ]);
        // Position EXACTLY at the move destination (overrides the CPU raycast point, which can differ
        // slightly from the GPU pick the camera actually flies to).
        reticleTargetPos.copy(worldPoint);
        if (!gotSurface) {
            // No CPU surface for a normal — face the camera as a fallback.
            reticleTargetNormal.sub2(camera.getPosition(), worldPoint);
            if (reticleTargetNormal.lengthSq() < 1e-6) reticleTargetNormal.copy(pc.Vec3.UP);
            reticleTargetNormal.normalize();
        }
        // Appear in place (don't slide from a stale position), then fade.
        reticleCurrentPos.copy(reticleTargetPos);
        reticleCurrentNormal.copy(reticleTargetNormal);
        reticleRevealUntil = performance.now() + RETICLE_REVEAL_MS;
    };
    // Cursor-follow reticle (default mode): a throttled CPU surface raycast (5 rays, every Nth frame)
    // snaps the ring to the surface under the cursor in explore/walk. Skipped in reveal-on-click mode
    // and while dragging/hovering. On a miss the ring stays put; reticleApplyPoints sets reticleVisible
    // on a hit. CPU-only (no GPU pick) so it can't disturb the 2.20 gsplat work buffer.
    app.on('postrender', () => {
        if (isInXR || reticleClickToReveal) return;
        const reticleActive = reticleUserEnabled && !isMobile && (currentCameraMode === 'explore' || currentCameraMode === 'walk' || currentCameraMode === 'orbit');
        const reticleCanvas = app.graphicsDevice.canvas as HTMLCanvasElement;
        // TEMP diagnostic (remove once cursor confirmed): report the reticle gates for no-collision
        // scenes — whichever field is false tells us exactly why the cursor doesn't engage. ~1s throttle.
        if (performance.now() - reticleDbgAt > 1000) {
            reticleDbgAt = performance.now();
            const _cx = (reticleCanvas?.clientWidth ?? 0) * 0.5, _cy = (reticleCanvas?.clientHeight ?? 0) * 0.5;
            // eslint-disable-next-line no-console
            console.debug('[reticle]', {
                mode: currentCameraMode, userEnabled: reticleUserEnabled, active: reticleActive,
                ccCollision: !!characterController?.hasSplatSurfaceCollision,
                surfaceAvailable: reticleSurfaceAvailable(_cx, _cy),
                centerHit: reticleActive ? !!reticleRaycast(_cx, _cy) : 'n/a',
            });
        }
        if (!reticleActive || !reticleCanvas || hoveringInteractable || pointerDown) return;
        if (++reticleFrameCounter < RETICLE_PICK_INTERVAL) return;
        reticleFrameCounter = 0;
        const isPointerLocked = !!document.pointerLockElement;
        const sx = isPointerLocked ? reticleCanvas.clientWidth * 0.5 : reticleScreenX;
        const sy = isPointerLocked ? reticleCanvas.clientHeight * 0.5 : reticleScreenY;
        // Re-pick only when the pointer or the camera moved (or once a second so newly streamed
        // detail under the cursor is picked up). A pick on a still frame lands a hair differently
        // each time, which would move the reticle forever and keep on-demand rendering awake.
        const _rpCam = camera.getPosition(); const _rpRot = camera.getRotation();
        const _rpNow = performance.now();
        const _rpUnchanged = sx === reticleLastPickX && sy === reticleLastPickY
            && _rpCam.x === reticleLastPickCam[0] && _rpCam.y === reticleLastPickCam[1] && _rpCam.z === reticleLastPickCam[2]
            && _rpRot.x === reticleLastPickCam[3] && _rpRot.y === reticleLastPickCam[4] && _rpRot.z === reticleLastPickCam[5] && _rpRot.w === reticleLastPickCam[6];
        if (_rpUnchanged && _rpNow - reticleLastPickAt < 1000) return;
        reticleLastPickX = sx; reticleLastPickY = sy; reticleLastPickAt = _rpNow;
        reticleLastPickCam[0] = _rpCam.x; reticleLastPickCam[1] = _rpCam.y; reticleLastPickCam[2] = _rpCam.z;
        reticleLastPickCam[3] = _rpRot.x; reticleLastPickCam[4] = _rpRot.y; reticleLastPickCam[5] = _rpRot.z; reticleLastPickCam[6] = _rpRot.w;
        const target = resolveSurfacePickTarget(sx, sy);
        if (!reticleSurfaceAvailable(sx, sy, target)) { reticleVisible = false; return; }
        const sp = RETICLE_SPREAD_PX;
        const haze = measurePickActive;
        reticleApplyPoints([
            reticleRaycast(sx, sy, target, haze),
            reticleRaycast(sx + sp, sy, target, haze),
            reticleRaycast(sx, sy + sp, target, haze),
            reticleRaycast(sx - sp, sy, target, haze),
            reticleRaycast(sx, sy - sp, target, haze),
        ]);
    });
    // Listen to CameraControls joystick events for UI updates + walk mode input
    const JOYSTICK_MAX_RADIUS = 60;
    app.on('joystick:left', (bx: number, by: number, sx: number, sy: number) => {
        if (bx < 0 || by < 0) {
            // Joystick released
            updateJoystickPosition(uiElements, false, 0, 0, JOYSTICK_MAX_RADIUS);
            // Clear CharacterController joystick input on release
            if (currentCameraMode === 'walk' && characterController) {
                characterController.setJoystickMove(0, 0);
            }
        }
        else {
            // Joystick active - calculate offset from base to stick
            const dx = sx - bx;
            const dy = sy - by;
            updateJoystickPosition(uiElements, true, dx, dy, JOYSTICK_MAX_RADIUS);
            // Forward joystick to CharacterController in walk mode
            if (currentCameraMode === 'walk' && characterController) {
                // Normalize to -1..1 and invert Y (screen down = forward)
                characterController.setJoystickMove(dx / JOYSTICK_MAX_RADIUS, -dy / JOYSTICK_MAX_RADIUS);
            }
        }
    });
    // The on-screen joystick is a directly-grabbed WIDGET: touching it drives movement, while the rest
    // of the screen is drag-to-look (the whole-screen 'touch-touch' layout). It captures its own
    // pointer, so its moves never reach the canvas/DualGestureSource and never leak into the look delta.
    // Mobile only (desktop walk uses WASD). Replaces the old left-half floating joystick.
    if (isMobile && uiElements.joystick) {
        const js = uiElements.joystick;
        js.style.pointerEvents = 'auto';
        let jsPointerId: number | null = null;
        let jsCx = 0, jsCy = 0;
        // The widget drives movement in BOTH walk (CharacterController) and explore-fly (CameraControls);
        // both run the same whole-screen-look 'touch-touch' layout. Orbit hides the joystick, so a grab
        // there never happens.
        js.addEventListener('pointerdown', (e: PointerEvent) => {
            const canGrab = (currentCameraMode === 'walk' && characterController) || currentCameraMode === 'explore';
            if (!canGrab) return;
            jsPointerId = e.pointerId;
            const r = js.getBoundingClientRect();
            jsCx = r.left + r.width / 2;
            jsCy = r.top + r.height / 2;
            try { js.setPointerCapture(e.pointerId); } catch { /* ignore */ }
            e.preventDefault();
            e.stopPropagation();
        });
        js.addEventListener('pointermove', (e: PointerEvent) => {
            if (jsPointerId !== e.pointerId) return;
            const dx = e.clientX - jsCx;
            const dy = e.clientY - jsCy;
            updateJoystickPosition(uiElements, true, dx, dy, JOYSTICK_MAX_RADIUS);
            // Normalize to -1..1 (clamped downstream) and invert Y (screen-down = forward).
            const nx = dx / JOYSTICK_MAX_RADIUS, ny = -dy / JOYSTICK_MAX_RADIUS;
            if (currentCameraMode === 'walk') characterController?.setJoystickMove(nx, ny);
            else cameraControls.setFlyJoystickMove(nx, ny);
        });
        const jsRelease = (e: PointerEvent) => {
            if (jsPointerId !== e.pointerId) return;
            jsPointerId = null;
            updateJoystickPosition(uiElements, false, 0, 0, JOYSTICK_MAX_RADIUS);
            characterController?.setJoystickMove(0, 0);
            cameraControls.setFlyJoystickMove(0, 0);
        };
        js.addEventListener('pointerup', jsRelease);
        js.addEventListener('pointercancel', jsRelease);
        // The whole screen is the look zone now — retire the eye-icon overlay entirely on mobile.
        if (uiElements.lookZone) uiElements.lookZone.style.display = 'none';
    }
    // Listen to right joystick (look zone) events for UI updates + walk mode look
    app.on('joystick:right', (bx: number, by: number, sx: number, sy: number) => {
        if (bx < 0 || by < 0) {
            // Look control released
            updateLookZoneState(uiElements, false);
            if (currentCameraMode === 'walk' && characterController) {
                characterController.setJoystickLook(0, 0);
            }
        }
        else {
            // Look control active
            updateLookZoneState(uiElements, true);
            // Forward look input to CharacterController in walk mode (normalized to -1..1)
            if (currentCameraMode === 'walk' && characterController) {
                characterController.setJoystickLook(
                    (sx - bx) / JOYSTICK_MAX_RADIUS,
                    (sy - by) / JOYSTICK_MAX_RADIUS
                );
            }
        }
    });
    // Orbit/Fly/Walk text buttons in scroll controls (both desktop and mobile)
    const exploreBtns = uiElements.scrollControls?.querySelectorAll('.storysplat-explore-btn');
    let currentExploreSubMode: 'orbit' | 'fly' | 'walk' = (initialSplatExploreMode as 'orbit' | 'fly') || 'fly';
    let pendingExploreDefaultSubMode: 'orbit' | 'fly' | 'walk' | null = null;
    let firstExploreEntry = true;
    let doubleTapHintDismissed = false;
    // Controls card ("start card"). A new lifecycle is created for each scene viewer, including
    // portal destinations. It shows once, after the loading screen is completely gone, in
    // whichever mode the scene opened in (tour or explore), stays CONTROLS_CARD_VISIBLE_MS and
    // then fades, unless the visitor closes it sooner with its X. The card lets pointer input
    // through, so it never needs dismissing to use the scene.
    const tourOnboarding = createTourOnboardingLifecycle();
    const CONTROLS_CARD_VISIBLE_MS = 7000;
    let tourLookHintTimer: ReturnType<typeof setTimeout> | null = null;
    const clearTourLookHintTimer = () => {
        if (tourLookHintTimer !== null) {
            clearTimeout(tourLookHintTimer);
            tourLookHintTimer = null;
        }
    };
    const controlsCardMode = (): ControlsCardMode | null => {
        if (currentCameraMode === 'tour') return 'tour';
        if (currentCameraMode === 'walk') return 'walk';
        if (currentCameraMode === 'explore') return currentExploreSubMode;
        return null; // hidden modes (FPV) have their own HUD
    };
    const hideControlsCard = () => {
        tourOnboarding.dismiss();
        clearTourLookHintTimer();
        setTourLookHintVisible(uiElements, false);
    };
    const showControlsCard = () => {
        const mode = controlsCardMode();
        // Not over the "Enter VR" prompt of a page that asked for VR (setupXR).
        if (uiElements.vrButton?.classList.contains('prompt')) return;
        if (!uiElements.tourLookHint || !mode || !tourOnboarding.tryShow(isEditorMode)) return;
        setTourOnboardingMode(uiElements.tourLookHint, mode, currentButtonLabels);
        setTourLookHintVisible(uiElements, true);
        clearTourLookHintTimer();
        tourLookHintTimer = setTimeout(() => {
            tourLookHintTimer = null;
            hideControlsCard();
        }, CONTROLS_CARD_VISIBLE_MS);
    };
    // While the card is up, keep it explaining the mode the visitor is actually in.
    const syncControlsCardMode = () => {
        if (tourLookHintTimer === null || !uiElements.tourLookHint) return;
        const mode = controlsCardMode();
        if (mode) setTourOnboardingMode(uiElements.tourLookHint, mode, currentButtonLabels);
        else hideControlsCard();
    };
    const controlsCardClose = uiElements.tourLookHint?.querySelector<HTMLElement>('.storysplat-tour-look-hint-close');
    controlsCardClose?.addEventListener('pointerdown', (e) => e.stopPropagation());
    controlsCardClose?.addEventListener('click', (e) => {
        e.stopPropagation();
        hideControlsCard();
    });
    afterInitialPresentation = showControlsCard;
    if (initialPresentationShown) showControlsCard();
    const updateExploreBtnState = (activeMode: 'orbit' | 'fly' | 'walk') => {
        currentExploreSubMode = activeMode;
        exploreBtns?.forEach(btn => {
            const mode = btn.getAttribute('data-explore-mode');
            btn.classList.toggle('selected', mode === activeMode);
        });
        syncControlsCardMode();
    };
    const applyExploreSubMode = (mode: ExploreSubMode): void => {
        if (!isExploreSubModeEnabled(mode)) {
            console.warn(`[StorySplat Viewer] Explore ${mode} mode is disabled for this scene.`);
            return;
        }
        // Walk is driven by the CharacterController, not CameraControls — route it
        // through the dedicated walk camera mode. Falls back to a non-walk sub-mode
        // if the scene has no collision data (walk would be a dead mode otherwise).
        if (mode === 'walk') {
            if (!hasCollisionMeshesForWalk) {
                console.warn('[StorySplat Viewer] Explore walk mode needs collision data; falling back.');
                const fallback = enabledExploreModes.find(m => m !== 'walk');
                if (fallback) applyExploreSubMode(fallback);
                return;
            }
            if (currentCameraMode !== 'walk') setCameraMode('walk');
            updateExploreBtnState('walk');
            return;
        }
        if (currentCameraMode === 'walk') setCameraMode('explore');
        if (mode === 'orbit') {
            if (!restoreSavedOrbitView()) {
                cameraControls.setMode(mode);
            }
        } else {
            cameraControls.setMode(mode);
        }
        updateExploreBtnState(mode);
        if (isMobile) {
            setJoystickVisible(uiElements, mode === 'fly');
            cameraControls.mobileInputLayout = mode === 'fly' ? 'touch-touch' : 'joystick-touch';
        } else {
            setWasdHintVisible(uiElements, mode === 'fly');
            setOrbitHintVisible(uiElements, mode === 'orbit', orbitRefocusLocked());
        }
        setDoubleTapHintVisible(uiElements, false);
    };
    /**
     * Travel to a view in explore mode (hotspot "Go to Orbit View" / "Fly to extra waypoint",
     * goToExtraWaypoint): go to explore + `subMode` if needed, then tween the camera to `position`
     * facing `lookAt`; in orbit it then orbits `lookAt`. The camera stays where it is through the mode
     * switch (which would otherwise jump to the scene's saved orbit view first), so the move starts
     * from what the visitor sees. Returns false when the scene can't use `subMode` or the view is
     * unusable.
     */
    const moveCameraToExploreView = (subMode: 'orbit' | 'fly', position: pc.Vec3, lookAt: pc.Vec3, onDone?: () => void, instant = false): boolean => {
        const finite = [position.x, position.y, position.z, lookAt.x, lookAt.y, lookAt.z].every(Number.isFinite);
        if (!finite || position.distance(lookAt) < 1e-4) return false;
        if (!allowedModes.includes('explore') || !isExploreSubModeEnabled(subMode)) return false;
        const startPos = camera.getPosition().clone();
        const startRot = camera.getRotation().clone();
        if (currentCameraMode !== 'explore') setCameraMode('explore');
        if (currentExploreSubMode !== subMode || cameraControls.mode !== subMode) applyExploreSubMode(subMode);
        if (currentCameraMode !== 'explore' || cameraControls.mode !== subMode) return false;
        // Undo the switch's jump (nothing has been drawn since): the move starts from this pose.
        cameraControls.syncFromPose(startPos, startRot);
        const started = subMode === 'orbit'
            ? cameraControls.animateToOrbitView(position, lookAt, onDone, { instant })
            : cameraControls.animateToFlyView(position, lookAt, onDone, { instant });
        if (started) {
            renderScheduler.requestFrame('camera-view-move');
            // The editor keeps its own camera-mode state (its Orbit / Fly / Tour switch): tell it.
            events.emit('cameraViewMove', { exploreMode: subMode });
        }
        return started;
    };
    /** Hotspot "Go to Orbit View": travel to the view and orbit its pivot (see moveCameraToExploreView). */
    const moveCameraToOrbitView = (view: HotspotOrbitView | undefined, onDone?: () => void): boolean => {
        if (!view?.cameraPosition || !view.pivotPoint) return false;
        const p = playCanvasPositionFromSceneVec3(view.cameraPosition);
        const f = playCanvasPositionFromSceneVec3(view.pivotPoint);
        return moveCameraToExploreView('orbit', new pc.Vec3(p.x, p.y, p.z), new pc.Vec3(f.x, f.y, f.z), onDone);
    };
    // An extra waypoint with its own FOV eases the explore camera to it alongside the move.
    const EXPLORE_FOV_EASE_SECONDS = 0.9;
    let exploreFovEase: { from: number; to: number; t: number } | null = null;
    app.on('update', (dt: number) => {
        if (!exploreFovEase) return;
        const cameraComponent = camera.camera;
        // Leaving explore (the tour damps its own FOV) or a vanished camera ends the ease.
        if (!cameraComponent || currentCameraMode !== 'explore') {
            exploreFovEase = null;
            return;
        }
        exploreFovEase.t = Math.min(1, exploreFovEase.t + dt / EXPLORE_FOV_EASE_SECONDS);
        const eased = 1 - Math.pow(1 - exploreFovEase.t, 3);
        cameraComponent.fov = exploreFovEase.from + (exploreFovEase.to - exploreFovEase.from) * eased;
        renderScheduler.requestFrame('camera-view-move');
        if (exploreFovEase.t >= 1) exploreFovEase = null;
    });
    /**
     * "Fly to extra waypoint" (hotspot click, goToExtraWaypoint): travel to a named view off the tour
     * path in explore mode, orbiting its orbit target, or in fly mode for a first-person one (each
     * falls back to the other sub-mode when the scene turned it off). Returns whether the camera moves.
     */
    const moveCameraToExtraWaypoint = (waypoint: ExtraWaypointData, onDone?: () => void, instant = false): boolean => {
        const zoom = cameraControls.zoomRange;
        const view = extraWaypointView(waypoint, pc.math.clamp(EXTRA_WAYPOINT_LOOK_DISTANCE, zoom.x, zoom.y));
        if (!view) return false;
        const subMode = extraWaypointExploreMode(view, isExploreSubModeEnabled);
        if (!subMode || !moveCameraToExploreView(subMode, view.position, view.lookAt, onDone, instant)) return false;
        const cameraComponent = camera.camera;
        if (view.fov !== undefined && cameraComponent && Math.abs(cameraComponent.fov - view.fov) > 1e-3) {
            if (instant) {
                exploreFovEase = null;
                cameraComponent.fov = view.fov;
            } else {
                exploreFovEase = { from: cameraComponent.fov, to: view.fov, t: 0 };
            }
        }
        return true;
    };
    const updateExploreModeSettings = (settings: ExploreModeSettingsUpdate): void => {
        if (settings.enabledExploreModes !== undefined) {
            enabledExploreModes = normalizeExploreModes(settings.enabledExploreModes);
            config.enabledExploreModes = enabledExploreModes;
            cameraControls.enableOrbit = isExploreSubModeEnabled('orbit');
            cameraControls.enableFly = isExploreSubModeEnabled('fly');
        }

        if (settings.initialSplatExploreMode !== undefined || !isExploreSubModeEnabled(initialSplatExploreMode)) {
            initialSplatExploreMode = getEnabledExploreSubMode(settings.initialSplatExploreMode ?? initialSplatExploreMode);
            config.initialSplatExploreMode = initialSplatExploreMode;
            pendingExploreDefaultSubMode = initialSplatExploreMode;
        }

        if (currentExploreSubMode !== 'walk' && !isExploreSubModeEnabled(currentExploreSubMode)) {
            pendingExploreDefaultSubMode = getEnabledExploreSubMode(initialSplatExploreMode);
        }

        const nextMode = pendingExploreDefaultSubMode ?? getEnabledExploreSubMode(initialSplatExploreMode);
        if (settings.applyCurrent && (currentCameraMode === 'explore' || currentCameraMode === 'walk')) {
            applyExploreSubMode(nextMode);
            pendingExploreDefaultSubMode = null;
        } else if (currentCameraMode !== 'explore' && currentCameraMode !== 'walk') {
            updateExploreBtnState(nextMode);
            pendingExploreDefaultSubMode = null;
        }
    };
    exploreBtns?.forEach(btn => {
        btn.addEventListener('click', () => {
            if (currentCameraMode !== 'explore' && currentCameraMode !== 'walk')
                return;
            const mode = btn.getAttribute('data-explore-mode') as 'orbit' | 'fly' | 'walk';
            if (mode === 'walk') {
                // Switch to walk mode (CharacterController)
                if (currentCameraMode !== 'walk') {
                    setCameraMode('walk');
                }
                updateExploreBtnState('walk');
            } else {
                // Switch to orbit or fly — ensure we're in explore mode
                if (currentCameraMode === 'walk') {
                    setCameraMode('explore');
                }
                applyExploreSubMode(mode);
            }
        });
    });
    // Listen for CameraControls internal mode changes (e.g., after focus completes)
    app.on('cameracontrols:modechange', (mode: string) => {
        if (mode === 'orbit' || mode === 'fly') {
            updateExploreBtnState(mode as 'orbit' | 'fly');
            // Update joystick/WASD/orbit hint visibility based on device
            if (currentCameraMode === 'explore' || currentCameraMode === 'walk') {
                if (isMobile) {
                    setJoystickVisible(uiElements, mode === 'fly');
                    // Explore-fly uses the walk-style whole-screen layout; walk manages its own layout.
                    if (currentCameraMode === 'explore') cameraControls.mobileInputLayout = mode === 'fly' ? 'touch-touch' : 'joystick-touch';
                } else {
                    setWasdHintVisible(uiElements, mode === 'fly');
                    setOrbitHintVisible(uiElements, mode === 'orbit', orbitRefocusLocked());
                }
                // Sync double-tap hint with sub-mode
                setDoubleTapHintVisible(uiElements, false);
            }
        }
        // When switching to orbit, set the orbit pivot to the splat surface.
        // Use the reticle's last known surface position (synchronous, no jump).
        // Only fall back to an async depth pick if the reticle isn't available.
        if (mode === 'orbit' && !hasSavedOrbitView()) {
            let reticleSynced = false;
            // Synchronous: use the reticle's surface depth to set a good orbit distance.
            // We project along the camera's forward (not toward the mouse position) so the
            // camera orientation is preserved — only the orbit radius changes.
            if (reticleVisible && reticleTargetPos) {
                const camPos = camera.getPosition();
                const reticleDist = camPos.distance(reticleTargetPos);
                if (reticleDist > 0.3 && reticleDist < 500) {
                    const fwd = camera.forward;
                    const orbitTarget = new pc.Vec3(
                        camPos.x + fwd.x * reticleDist,
                        camPos.y + fwd.y * reticleDist,
                        camPos.z + fwd.z * reticleDist
                    );
                    cameraControls.syncFromCamera(orbitTarget);
                    reticleSynced = true;
                }
            }
            // Fallback: only if the reticle surface wasn't available. CPU surface raycast at
            // screen center — no GPU gsplat pick (that disturbs the 2.20 unified work buffer and
            // can hang the device; see reticleRaycast).
            // Skip for frame-sequence scenes — orbit target is set explicitly.
            if (!reticleSynced && !frameSequencePlayer) {
                try {
                    const wp = reticleRaycast(canvasEl.clientWidth * 0.5, canvasEl.clientHeight * 0.5);
                    if (wp && isFinite(wp.x) && isFinite(wp.y) && isFinite(wp.z)) {
                        const dist = camera.getPosition().distance(wp);
                        if (dist > 0.3 && dist < 500) {
                            cameraControls.syncFromCamera(wp);
                        }
                    }
                } catch (_e) {}
            }
        }
    });
    // Proximity check function (called every frame in explore mode)
    // Settle detection for the LOD loading effect: hold the "developing" look until the live
    // loaded-splat count plateaus (detail has streamed in), then trigger the resolve. Adaptive to
    // network speed; degrades gracefully to a time cap if the count can't be read.
    // Live active-splat count: the engine's own per-frame total, set by the gsplat director on the
    // renderer (PlayCanvas 2.20). Grows as LOD nodes stream in. NOTE: app.scene.gsplat is GSplatParams
    // (no .splats) — the count lives on app.renderer._gsplatCount. Returns -1 if unreadable.
    function readLodCount(): number {
        try {
            const c = (app.renderer as unknown as { _gsplatCount?: number })?._gsplatCount;
            if (typeof c === 'number') return c;
        } catch { /* engine shape changed */ }
        return -1;
    }

    // Pending LOD-node downloads, summed across the director's managers. The truthful "detail is
    // still streaming" signal — it stays > 0 across network stalls (unlike the count, which goes flat
    // during a stall). Reachable only via engine internals, so heavily guarded; -1 if unreadable.
    function readLodPending(): number {
        try {
            const director = (app.renderer as unknown as {
                gsplatDirector?: { camerasMap?: Map<unknown, { layersMap?: Map<unknown, { gsplatManager?: { world?: { pendingLoadCount?: number } } }> }> };
            })?.gsplatDirector;
            const cams = director?.camerasMap;
            if (!cams) return -1;
            let pending = 0, found = false;
            cams.forEach((cd) => {
                cd.layersMap?.forEach((ld) => {
                    const p = ld?.gsplatManager?.world?.pendingLoadCount;
                    if (typeof p === 'number') { pending += p; found = true; }
                });
            });
            return found ? pending : -1;
        } catch { /* engine shape changed */ }
        return -1;
    }

    // LOD "reveal hold": resolves once the freshly loaded LOD scene has streamed to "decent" detail —
    // the moment the published viewer lifts its preloader. Shared with the editor API
    // (whenRevealReady) so creators get the same reveal instead of a black canvas while the first
    // chunks download. Hold through the coarse phase AND the chunky coarse→decent snap, then reveal
    // once the active-splat count has grown to a "decent" multiple of the coarse level (adaptive to
    // connection speed — holds longer on slow links, which is the intent). Finer LODs then refine in
    // the open. A time cap guarantees very slow links still reveal; the post-reveal "adding detail"
    // indicator then signals ongoing streaming.
    // NEVER hangs: resolves at REVEAL_CAP_MS via both the rAF loop and a wall-clock timer (rAF
    // pauses in background tabs), and resolves immediately-ish once the viewer is destroyed.
    // `onProgress` receives 0..1 through the hold (count growth toward REVEAL_RATIO, floored by
    // elapsed time toward the cap) so the preloader can keep moving 85→100%.
    function waitForLodReveal(onProgress?: (fraction: number) => void): Promise<void> {
        const MIN_HOLD_MS = 400;       // never reveal at the bare-coarse first frame
        const REVEAL_RATIO = 4;        // "decent" = count grown to this multiple of coarse (tune)
        const REVEAL_CAP_MS = 8000;    // reveal anyway after this (very slow link); indicator covers the rest
        const PENDING_ZERO_MS = 1200;  // nothing more streaming for this long → reveal (fast/small/done); longer = less twitchy
        return new Promise<void>((resolve) => {
            const start = performance.now();
            let coarseCount = -1, done = false, pendingZeroSince = -1;
            let capTimer: ReturnType<typeof setTimeout> | null = null;
            const finish = () => {
                if (done) return;
                done = true;
                if (capTimer !== null) { clearTimeout(capTimer); capTimer = null; }
                resolve();
            };
            // Wall-clock backstop: rAF is throttled/paused in hidden tabs.
            capTimer = setTimeout(finish, REVEAL_CAP_MS + 250);
            const tick = () => {
                if (done) return;
                if (isDestroyed) { finish(); return; }
                const elapsed = performance.now() - start;
                const count = readLodCount();
                const pending = readLodPending();
                if (count > 0 && coarseCount < 0) coarseCount = count;
                // "Decent" detail reached: count grew to a multiple of coarse (streaming scenes).
                const decent = coarseCount > 0 && count >= coarseCount * REVEAL_RATIO;
                // Nothing more is streaming (pending drained to 0 for a moment): fast/small/done
                // scenes — reveal instead of waiting out the cap. pending<0 (unreadable) never matches.
                if (pending === 0) { if (pendingZeroSince < 0) pendingZeroSince = performance.now(); }
                else { pendingZeroSince = -1; }
                const drained = count > 0 && pendingZeroSince >= 0 && (performance.now() - pendingZeroSince) >= PENDING_ZERO_MS;
                if (onProgress) {
                    const growth = coarseCount > 0 ? (count / coarseCount - 1) / (REVEAL_RATIO - 1) : 0;
                    const fraction = Math.max(0, Math.min(1, Math.max(growth, elapsed / REVEAL_CAP_MS)));
                    try { onProgress(fraction); } catch { /* progress is cosmetic */ }
                }
                if ((decent || drained) && elapsed >= MIN_HOLD_MS) { finish(); return; }
                if (elapsed >= REVEAL_CAP_MS) { finish(); return; }
                requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        });
    }

    // Non-blocking "adding detail" indicator for LOD scenes. Started right after the scene is
    // revealed. Subtle static text while detail is still streaming in; hides once it stops.
    // PRIMARY signal: pendingLoadCount (pending LOD-node downloads) — truthful and survives network
    // stalls, so on a slow trickle it stays up the whole time (the user's main case). FALLBACK (when
    // pending is unreadable): "active-splat count grew recently", with a generous flat window so a
    // stall doesn't hide it early. Skips fast loads (only shows after a delay if still loading), and
    // is a one-shot for the initial load so navigation LOD swaps don't re-trigger it.
    function startLodDetailMonitor(): void {
        // The visual can be toggled off here while keeping the monitor/hook running.
        const VISUAL_ENABLED = true;
        const SHOW_DELAY_MS = 600;       // don't flash on fast loads
        const HIDE_DEBOUNCE_MS = 800;    // hide after "not loading" holds this long (anti-flicker)
        const FALLBACK_FLAT_MS = 5000;   // fallback only: count flat this long = "not loading"
        const MAX_MS = 60000;            // safety stop (non-blocking, so generous)
        const start = performance.now();
        let shown = false, stopped = false;
        let lastCount = -1, lastGrowthAt = start, notLoadingSince = -1;
        const stop = () => {
            if (stopped) return;
            stopped = true;
            if (shown && VISUAL_ENABLED) hideDetailIndicator(container);
        };
        const tick = () => {
            if (stopped) return;
            if (isDestroyed) { stop(); return; }
            const now = performance.now();
            const elapsed = now - start;
            const pending = readLodPending();
            let isLoading: boolean;
            if (pending >= 0) {
                isLoading = pending > 0;
            } else {
                const count = readLodCount();
                if (count > lastCount) { lastGrowthAt = now; lastCount = count; }
                isLoading = count >= 0 && (now - lastGrowthAt) < FALLBACK_FLAT_MS;
            }
            // While the initial autoplay waits for detail, its centred status is the one message.
            const autoplayWaitPending = autoplayDetailGate.isEngaged();
            if (shown && autoplayWaitPending) {
                shown = false;
                if (VISUAL_ENABLED) hideDetailIndicator(container);
            }
            if (isLoading) {
                notLoadingSince = -1;
                if (!shown && !autoplayWaitPending && elapsed >= SHOW_DELAY_MS) {
                    shown = true;
                    if (VISUAL_ENABLED) showDetailIndicator(container);
                }
            } else {
                if (notLoadingSince < 0) notLoadingSince = now;
                if (now - notLoadingSince >= HIDE_DEBOUNCE_MS) { stop(); return; }
            }
            if (elapsed >= MAX_MS) { stop(); return; }
            requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }

    function updateProximityTriggers(): void {
        const cameraPos = camera.getPosition();
        hotspotEntities.forEach((entity) => {
            const hotspot = entity.hotspotData;
            if (!hotspot)
                return;
            const mediaTriggerMode = entity.mediaTriggerMode || 'click';
            // Legacy fallback: if no audioTriggerMode is set, inherit from mediaTriggerMode
            // so that old hotspots with mediaTriggerMode:'proximity' still trigger audio on approach
            const audioTriggerMode = hotspot.audioTriggerMode || (mediaTriggerMode === 'proximity' ? 'proximity' : 'click');
            const hasMediaProximity = mediaTriggerMode === 'proximity';
            const hasAudioProximity = audioTriggerMode === 'proximity' && entity.hotspotAudioSlotId;
            // Skip if neither media nor audio uses proximity
            if (!hasMediaProximity && !hasAudioProximity)
                return;
            const hotspotPos = entity.getPosition();
            const distance = cameraPos.distance(hotspotPos);
            const proximityDistance = entity.proximityDistance || 5;
            const isInProximity = distance <= proximityDistance;
            const wasInProximity = entity.wasInProximity || false;
            if (isInProximity && !wasInProximity) {
                // Entering proximity — media triggers
                if (hasMediaProximity) {
                    if (hotspotHasVideoPlane(hotspot, entity)) {
                        // A video a linked action paused in range stays paused (linkedActions.ts).
                        if (!isAutoTriggerHeld(entity, true)) playVideoHotspot(entity, hotspot);
                    }
                    else if (hotspot.type === 'gif') {
                        entity.enabled = true;
                        renderScheduler.requestFrame('proximity-enter');
                    }
                }
                // Entering proximity — audio trigger via PlayCanvas slot
                if (hasAudioProximity && entity.hotspotAudioReady) {
                    const slot = entity.sound?.slot(entity.hotspotAudioSlotId!);
                    if (slot && !slot.isPlaying) {
                        slot.play();
                        entity.hotspotAudioPlaying = true;
                        console.log('[Audio] Hotspot audio started (proximity):', hotspot.title);
                    }
                }
                entity.wasInProximity = true;
            }
            else if (!isInProximity && wasInProximity) {
                // Leaving proximity — media triggers
                if (hasMediaProximity) {
                    if (hotspotHasVideoPlane(hotspot, entity)) {
                        // Evaluated first so leaving always releases a linked hold taken in range.
                        const heldByLinkedAction = isAutoTriggerHeld(entity, false);
                        if (hotspot.pauseOnLeaveProximity !== false && !heldByLinkedAction) {
                            pauseVideoHotspot(entity);
                        }
                    }
                    else if (hotspot.type === 'gif') {
                        entity.enabled = false;
                        renderScheduler.requestFrame('proximity-leave');
                    }
                }
                // Leaving proximity — audio trigger via PlayCanvas slot
                if (hasAudioProximity) {
                    const stopOnExit = hotspot.audioStopOnExit ?? true;
                    if (stopOnExit) {
                        const slot = entity.sound?.slot(entity.hotspotAudioSlotId!);
                        if (slot && slot.isPlaying) {
                            slot.stop();
                            entity.hotspotAudioPlaying = false;
                            console.log('[Audio] Hotspot audio stopped (proximity):', hotspot.title);
                        }
                    }
                }
                entity.wasInProximity = false;
            }
        });
    }
    // Mode switching function (Tour, Explore, or Walk)
    // --- Hidden FPV drone mode ------------------------------------------------
    // A quadcopter flight sim you can drop into on any scene. Entered via the
    // typed "fpv" sequence, a gamepad chord (both bumpers + Start), or the
    // public viewer API — never through the normal UI, so it stays an Easter egg.
    function getDroneController(): DroneController {
        if (!droneController) {
            droneController = new DroneController(camera, app, {
                // Use the same normalized speed as explore/fly. Scene files use
                // 0.2 as their default cameraMovementSpeed, which maps to a
                // 1.0 world-speed multiplier through baseMoveSpeed; passing the
                // raw value here made every drone translation roughly 5x slower.
                worldScale: baseMoveSpeed,
                // CharacterController owns the aggregate collision truth:
                // authored primitives/custom meshes plus the selected/enabled
                // generated mesh or voxel data. The closure also sees collision
                // that finishes loading after the drone was created.
                collisionSweep: (from, to, radius) =>
                    characterController?.sweepCollisionSphere(from, to, radius) ?? false,
                onExitRequest: () => exitFpvMode(),
            });
            console.log('[StorySplat Viewer] DroneController created lazily for FPV mode');
        }
        return droneController;
    }

    function enterFpvMode(): void {
        if (currentCameraMode === 'fpv') return;
        // Remember where we came from so exiting restores the exact prior view.
        preFpvCameraMode = currentCameraMode;
        preFpvCameraPos.copy(camera.getPosition());
        preFpvCameraRot.copy(camera.getRotation());
        stopAutoMoveForward();
        if (panoramaModeEnabled && !panoramaModeTransitioning) {
            exitPanoramaPortal();
        }
        if (currentCameraMode === 'tour' && isAutoplayEngaged()) {
            pause();
        }
        // Tear down whichever driver was active.
        if (characterController) characterController.disable();
        cameraControls.disable();
        waypointControlEnabled = false;
        // Hide the reticle while flying.
        reticleEntity.enabled = false;
        reticleVisible = false;
        reticleOpacity = 0;
        currentCameraMode = 'fpv';
        getDroneController().enable();
        // Emit the same modeChange setCameraMode() emits. Entry can't literally
        // route through setCameraMode(): it has no 'fpv' branch, so 'fpv' falls
        // into the tour `else`, which re-enables waypoint control and snaps the
        // camera back to the tour pose — i.e. it would break the drone. Exit
        // already emits (via setCameraMode(restoreMode)), so without this the
        // mode funnel showed FPV exits with no matching entries.
        events.emit('modeChange', { mode: 'fpv' });
        console.log('[StorySplat Viewer] Entered hidden FPV drone mode');
    }

    function exitFpvMode(): void {
        if (currentCameraMode !== 'fpv') return;
        droneController?.disable();
        // Restore the pre-FPV camera pose before handing back to the prior mode
        // so it re-syncs from where the user actually was, not where they flew to.
        camera.setPosition(preFpvCameraPos);
        camera.setRotation(preFpvCameraRot);
        // setCameraMode expects currentCameraMode to still reflect the mode being
        // left; it only special-cases 'walk'/'explore' for teardown, and the
        // drone is already down, so mark us as leaving tour to avoid a re-pause.
        currentCameraMode = preFpvCameraMode === 'fpv' ? 'tour' : preFpvCameraMode;
        const restoreMode = currentCameraMode;
        currentCameraMode = 'tour';
        setCameraMode(restoreMode);
        console.log('[StorySplat Viewer] Exited FPV drone mode, restored:', restoreMode);
    }

    function toggleFpvMode(): void {
        if (currentCameraMode === 'fpv') exitFpvMode();
        else enterFpvMode();
    }

    // Controller chord: both bumpers held + Start/Menu pressed toggles FPV mode.
    // The drone's own flight controls never use the bumpers or Start, so this
    // can't collide with piloting once you're in.
    function checkFpvGamepadChord(): void {
        const gp = readGamepad();
        if (!gp.connected) { fpvGamepadEdges.commit([]); return; }
        const bothBumpers = (gp.buttons[GP_BUTTON.LB] ?? false) && (gp.buttons[GP_BUTTON.RB] ?? false);
        if (bothBumpers && fpvGamepadEdges.justPressed(gp.buttons, GP_BUTTON.START)) {
            toggleFpvMode();
        }
        fpvGamepadEdges.commit(gp.buttons);
    }

    // Typed "fpv" cheat-code to enter/exit the hidden drone mode from a keyboard.
    function setupFpvKeySequence(): void {
        fpvKeydownHandler = (e: KeyboardEvent) => {
            if (isEditorMode) return;
            const tag = (document.activeElement as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable) return;
            // While already flying, the DroneController owns the keyboard (ESC exits);
            // don't also run the sequence matcher.
            if (currentCameraMode === 'fpv') return;
            const key = e.key?.toLowerCase();
            if (!key || key.length !== 1) return;
            const now = (typeof performance !== 'undefined' ? performance.now() : 0);
            if (now - fpvKeySequenceTime > 1200) fpvKeySequence = '';
            fpvKeySequenceTime = now;
            fpvKeySequence = (fpvKeySequence + key).slice(-3);
            if (fpvKeySequence === 'fpv') {
                fpvKeySequence = '';
                enterFpvMode();
            }
        };
        window.addEventListener('keydown', fpvKeydownHandler);
    }
    // Scene editors may disable only the typed Easter-egg sequence. Gamepad
    // activation and the public FPV API remain available regardless.
    if (config.uiOptions?.fpvKeyboardShortcutEnabled !== false) {
        setupFpvKeySequence();
    }

    function setCameraMode(mode: string): void {
        stopAutoMoveForward();
        // Exit panorama mode if active (user clicked a mode button to switch)
        if (panoramaModeEnabled && !panoramaModeTransitioning) {
            exitPanoramaPortal();
        }
        // Stop tour playback when leaving tour mode so waypoints don't keep advancing
        if (currentCameraMode === 'tour' && mode !== 'tour' && isAutoplayEngaged()) {
            pause();
        }
        // Hide reticle on mode switch (update loop will re-show if appropriate)
        reticleEntity.enabled = false;
        reticleVisible = false;
        reticleOpacity = 0;
        // Disable previous mode's controllers
        if (currentCameraMode === 'fpv' && droneController) {
            // Leaving the hidden FPV mode via a normal mode switch — tear down
            // the drone (removes its input listeners + HUD) so it can't linger.
            droneController.disable();
        }
        else if (currentCameraMode === 'walk' && characterController) {
            characterController.disable();
            // Restore the non-walk mobile input layout so fly mode's rightInput
            // deltas work again (fly reads rightInput, which only accumulates
            // when the layout's right half is 'touch', not 'joystick').
            // Guarded on mobile to match the entry path — desktop never wrote
            // the joystick-joystick layout, so there's nothing to restore.
            if (isMobileDevice()) {
                cameraControls.mobileInputLayout = 'joystick-touch';
            }
        }
        else if (currentCameraMode === 'explore') {
            cameraControls.disable();
        }
        currentCameraMode = mode;
        console.log('[StorySplat Viewer] Switching to mode:', mode);
        const isMobile = isMobileDevice();
        if (mode === 'walk' && characterController) {
            // Walk mode: Enable CharacterController (first-person with collisions)
            waypointControlEnabled = false;
            // Disable CameraControls but keep mobile input sources for joystick
            cameraControls.disable();
            if (isMobile) {
                // Keep DualGestureSource attached so touch deltas flow for the drag-look.
                cameraControls.detachInputSources();
                cameraControls.reattachMobileInput();
                // Walk mode: the WHOLE screen is drag-to-look; movement comes from the on-screen
                // joystick widget alone. 'touch-touch' makes BOTH halves raw touches (unclamped per-move
                // deltas on deltas.left/rightInput), which readWalkLookDelta sums and applyLookDelta
                // turns into camera look. The joystick DOM widget captures its own pointer (see the
                // joystick handler below), so touching it drives movement and never leaks into look.
                cameraControls.mobileInputLayout = 'touch-touch';
            } else {
                cameraControls.detachInputSources();
            }
            // Apply saved walk start position/rotation if the creator opted in.
            // Default behavior (walkSpawnOverride=false) leaves the camera where it
            // already was — typically the explore/tour position the user was last at.
            // Must happen before enable() so CharacterController syncs its yaw/pitch
            // and ground offset from the correct camera pose.
            const walkCameraSettings = config.walkCameraSettings;
            if (config.walkSpawnOverride && walkCameraSettings?.cameraPosition) {
                const cp = walkCameraSettings.cameraPosition;
                camera.setPosition(cp.x, cp.y, cp.z);
                if (walkCameraSettings.rotation) {
                    const r = walkCameraSettings.rotation;
                    camera.setEulerAngles(r.x, r.y, r.z);
                }
                console.log('[StorySplat Viewer] Walk mode: positioned from walkCameraSettings', cp);
            }
            // Enable CharacterController
            characterController.enable();
            // Update explore sub-mode buttons and show joystick on mobile
            updateExploreBtnState('walk');
            if (isMobile) {
                setJoystickVisible(uiElements, true);
            }
            // Desktop: show the WASD + Space-to-jump hint in walk mode (mobile uses the joystick).
            setWasdHintVisible(uiElements, !isMobile, 'walk');
            setOrbitHintVisible(uiElements, false, orbitRefocusLocked());
            setDoubleTapHintVisible(uiElements, false);
        }
        else if (mode === 'explore') {
            // Explore mode: Enable CameraControls (orbit + fly + pan)
            // Disable CharacterController if active
            if (characterController) {
                characterController.disable();
            }
            // CRITICAL: Reset user rotation offsets and disable waypoint control
            userYawOffset = 0;
            userPitchOffset = 0;
            isUserDragging = false;
            waypointControlEnabled = false;
            // IMPORTANT: Call disable() first to clear any accumulated input from tour mode dragging
            // The CameraControls input sources accumulate mouse/touch input even when disabled,
            // and this would cause an offset when we enable and the update loop reads that input
            cameraControls.disable(); // Clears input buffers
            cameraControls.enable();
            cameraControls.enableOrbit = isExploreSubModeEnabled('orbit');
            cameraControls.enableFly = isExploreSubModeEnabled('fly');
            cameraControls.enablePan = true;
            // Determine the explore sub-mode to apply
            const exploreDefault = initialSplatExploreMode || 'fly';
            const firstExploreEntryWas = firstExploreEntry;
            // On first entry in orbit mode: position camera at the right place FIRST,
            // then sync. This mirrors what tour→orbit does (which works correctly).
            if (firstExploreEntry && exploreDefault === 'orbit') {
                if (orbitCameraSettings?.cameraPosition && orbitCameraSettings?.pivotPoint) {
                    // Saved orbit settings: position camera there, preserving saved orbit distance.
                    applySavedOrbitViewToCamera();
                    console.log('[StorySplat Viewer] Orbit first entry: positioned from saved settings');
                } else if (waypointPositions.length > 0) {
                    // No saved orbit settings: use waypoint[0] like tour mode would
                    camera.setPosition(waypointPositions[0]);
                    camera.setRotation(waypointRotations[0]);
                    console.log('[StorySplat Viewer] Orbit first entry: positioned at waypoint[0]');
                }
            }
            // Sync CameraControls from the camera's current position/rotation.
            // syncFromPose derives angles from the forward vector (more reliable than getEulerAngles).
            const currentPos = camera.getPosition().clone();
            const currentRot = camera.getRotation().clone();
            cameraControls.syncFromPose(currentPos, currentRot);
            // Find a better focus distance via a CPU surface raycast at screen center — no GPU
            // gsplat pick (that disturbs the 2.20 unified work buffer; see reticleRaycast).
            // Only update the orbit distance — do NOT re-orient the camera.
            const findBetterFocusPoint = () => {
                try {
                    const worldPoint = reticleRaycast(canvasEl.clientWidth * 0.5, canvasEl.clientHeight * 0.5);
                    if (worldPoint) {
                        const cameraPos = camera.getPosition();
                        const distance = cameraPos.distance(worldPoint);
                        if (distance > 0.5 && distance < 500) {
                            cameraControls.setFocusDistance(worldPoint, distance);
                            console.log('[StorySplat Viewer] Updated focus distance:', distance.toFixed(2));
                        }
                    }
                }
                catch (err) {
                    // Raycast failed, keep default
                }
            };
            // Skip async focus picking on first entry — the splat may not be loaded yet.
            // On re-entry (e.g. tour→explore), the splat IS loaded and this works correctly.
            if (!frameSequencePlayer && !firstExploreEntry && !orbitCameraSettings?.pivotPoint) {
                findBetterFocusPoint();
            }
            // Apply the explore sub-mode: use initial default on first entry,
            // preserve user's last choice on re-entry
            const resolvedSubMode: ExploreSubMode = firstExploreEntry
                ? (exploreDefault as ExploreSubMode)
                : getEnabledExploreSubMode(pendingExploreDefaultSubMode ?? (currentExploreSubMode === 'walk' ? undefined : currentExploreSubMode));
            pendingExploreDefaultSubMode = null;
            firstExploreEntry = false;
            // If the resolved default explore sub-mode is walk, hand off to the
            // CharacterController-driven walk mode instead of CameraControls.
            if (resolvedSubMode === 'walk' && hasCollisionMeshesForWalk) {
                setCameraMode('walk');
                updateExploreBtnState('walk');
                return;
            }
            // CameraControls only handles orbit/fly; if walk resolved without
            // collision data, fall back to a usable sub-mode.
            const exploreSubMode: 'orbit' | 'fly' = resolvedSubMode === 'walk'
                ? (enabledExploreModes.includes('fly') ? 'fly' : 'orbit')
                : resolvedSubMode;
            // For saved orbit views, switch directly into the exact saved pose and
            // distance. The generic Fly→Orbit path derives a temporary forward pivot,
            // which is only appropriate when no saved orbit view exists.
            if (exploreSubMode === 'orbit' && restoreSavedOrbitView()) {
                console.log('[StorySplat Viewer] Restored saved orbit view');
            }
            else {
                cameraControls.setMode(exploreSubMode);
            }
            if (!orbitCameraSettings?.cameraPosition && firstExploreEntryWas && exploreSubMode === 'orbit' && orbitCameraSettings?.pivotPoint) {
                const pp = orbitCameraSettings.pivotPoint;
                cameraControls.syncFromCamera(new pc.Vec3(pp.x, pp.y, pp.z), true, true);
                cameraControls.setOrbitRotationLimits(orbitCameraSettings.rotationLimits ?? null, true);
                applyOrbitZoomLimits();
                console.log('[StorySplat Viewer] Re-applied saved orbit pivot after setMode:', pp);
            }
            updateExploreBtnState(exploreSubMode);
            if (isMobile) {
                setJoystickVisible(uiElements, exploreSubMode === 'fly');
                // Fly uses the SAME walk-style controls: whole-screen drag-to-look + a grabbable joystick
                // widget ('touch-touch'). Orbit keeps its drag-to-rotate touch layout.
                cameraControls.mobileInputLayout = exploreSubMode === 'fly' ? 'touch-touch' : 'joystick-touch';
            } else {
                // Show WASD hint on desktop when in fly mode, orbit hint when in orbit mode
                setWasdHintVisible(uiElements, exploreSubMode === 'fly');
                setOrbitHintVisible(uiElements, exploreSubMode === 'orbit', orbitRefocusLocked());
            }
            // Double-click hint is now integrated into WASD hint; no standalone pill needed
        }
        else {
            // Tour mode - disable camera controls, enable waypoint control
            // Re-attach input sources first in case they were detached for walk mode
            cameraControls.enable();
            cameraControls.disable();
            if (characterController) {
                characterController.disable();
            }
            waypointControlEnabled = true;
            // Safety: if waypoint arrays are empty but config has waypoints, rebuild them
            if (waypointPositions.length === 0 && config.waypoints && config.waypoints.length > 0) {
                config.waypoints.forEach(wp => {
                    const pos = wp.position
                        ? new pc.Vec3(wp.position.x, wp.position.y, -wp.position.z)
                        : new pc.Vec3(0, config.playerHeight || 1.6, 0);
                    waypointPositions.push(pos);
                    const rot = wp.rotation
                        ? convertWaypointRotation(wp.rotation)
                        : new pc.Quat();
                    waypointRotations.push(rot);
                    let fov = wp.fov || defaultFOV;
                    if (fov < 3.5) fov = fov * (180 / Math.PI);
                    waypointFOVs.push(fov);
                });
                console.log('[StorySplat Viewer] Rebuilt waypoint arrays from config:', waypointPositions.length);
            }
            // Initialize camera targets from current progress so damping drives to the right position
            updateCameraFromProgress(currentProgress);
            // Snap camera to targets immediately so there's no delay entering tour mode
            if (targetCameraPosition && targetCameraRotation) {
                camera.setPosition(targetCameraPosition);
                camera.setRotation(targetCameraRotation);
                if (camera.camera && waypointFOVs.length > 0) {
                    camera.camera.fov = targetFOV;
                }
            }
            // Hide joystick and hints in tour mode
            setJoystickVisible(uiElements, false);
            setWasdHintVisible(uiElements, false);
            setOrbitHintVisible(uiElements, false, orbitRefocusLocked());
            setDoubleTapHintVisible(uiElements, false);
        }
        // The controls card is shown once per viewer after loading (showControlsCard); a mode
        // switch while it is up only swaps its text, it never brings it back.
        syncControlsCardMode();
        events.emit('modeChange', { mode });
        // Pause narration when leaving tour mode, resume when returning
        if (narrationAudio && narrationEnabled) {
            if (mode !== 'tour') {
                narrationAudio.pause();
                if (narrationMusicAudio) narrationMusicAudio.pause();
            }
            // Tour mode narration is resumed by syncNarrationToProgress via progressUpdate
        }
    }
    // =====================================================
    // PANORAMA MODE (360° skybox-only view)
    // =====================================================
    /**
     * Toggle panorama mode: instantly hides all 3D scene content and locks the
     * camera to rotation-only so the skybox appears as a standard 360° viewer.
     * The camera stays exactly where it is — no repositioning.
     */
    function setPanoramaMode(enabled: boolean): void {
        if (enabled === panoramaModeEnabled) return;
        if (panoramaModeTransitioning) return;

        panoramaModeTransitioning = true;
        console.log('[StorySplat Viewer] Panorama mode:', enabled ? 'entering' : 'exiting');

        if (enabled) {
            // Save current state for restoration
            prePanoramaCameraMode = currentCameraMode;
            prePanoramaExploreSubMode = currentExploreSubMode;
            prePanoramaPitchRange = cameraControls.pitchRange.clone();
            prePanoramaZoomRange = cameraControls.zoomRange.clone();

            // --- Camera: stay in place, just lock to rotation-only ---
            // Disable any active controllers WITHOUT repositioning
            if (currentCameraMode === 'walk' && characterController) {
                characterController.disable();
            }
            // Kill tour mode rotation constraints and waypoint control
            waypointControlEnabled = false;
            userYawOffset = 0;
            userPitchOffset = 0;
            isUserDragging = false;

            // Capture exact camera pose before any mode switch
            const camPos = camera.getPosition().clone();
            const camRot = camera.getRotation().clone();

            // Enable orbit controls from the current position (no reposition)
            cameraControls.disable();
            cameraControls.enable();
            cameraControls.enableOrbit = true;
            cameraControls.setMode('orbit');
            // Sync orbit pivot to camera position (distance 0 = pure rotation)
            cameraControls.syncFromPose(camPos, camRot);
            cameraControls.setFocusDistance(camPos, 0.001, true);

            // Lock: no pan, no fly, no zoom — rotation only
            cameraControls.enablePan = false;
            cameraControls.enableFly = false;
            cameraControls.zoomRange = new pc.Vec2(0.001, 0.001);
            cameraControls.pitchRange = new pc.Vec2(-85, 85);

            currentCameraMode = 'explore';

            // Hide UI hints
            setJoystickVisible(uiElements, false);
            setWasdHintVisible(uiElements, false);
            setOrbitHintVisible(uiElements, false, orbitRefocusLocked());
            setDoubleTapHintVisible(uiElements, false);

            // --- Hide all non-splat scene content immediately ---
            // Disable each child of sceneContent EXCEPT the splat entity and the
            // panorama skybox (which setPanoramaSkybox just added to sceneContent
            // moments before this call — we'd otherwise hide the thing we're
            // trying to show).
            for (let i = 0; i < sceneContent.children.length; i++) {
                const child = sceneContent.children[i];
                if (child !== splatEntity && child !== currentSkyboxEntity && child.enabled) {
                    child.enabled = false;
                    (child as any).__panoramaHidden = true;
                }
            }
            // Also hide swap splat entities
            for (const [, e] of preloadedSplats) {
                if (e.enabled) { e.enabled = false; (e as any).__panoramaHidden = true; }
            }

            // Procedural splat weather lives under app.root (not sceneContent),
            // so it isn't caught by the loop above. Hide it explicitly so rain/snow
            // doesn't bleed over the 360 image. update() is cheap but we also gate
            // it via the panoramaModeEnabled flag in the weather tick handler.
            if (weatherInstance && weatherInstance.entity.enabled) {
                weatherInstance.entity.enabled = false;
                (weatherInstance.entity as any).__panoramaHidden = true;
            }

            // Dissolve the splat out, then hide it
            if (splatEntity) {
                applyDissolveToEntity(splatEntity, -1.0, () => {
                    if (splatEntity) splatEntity.enabled = false;
                });
            }

            panoramaModeEnabled = true;
            panoramaModeTransitioning = false;
            events.emit('panoramaModeChange', { enabled: true });
            console.log('[StorySplat Viewer] Panorama mode active');
        } else {
            // --- Restore all hidden scene content ---
            if (splatEntity) splatEntity.enabled = true;
            for (let i = 0; i < sceneContent.children.length; i++) {
                const child = sceneContent.children[i] as any;
                if (child.__panoramaHidden) {
                    child.enabled = true;
                    delete child.__panoramaHidden;
                }
            }
            for (const [, e] of preloadedSplats) {
                if ((e as any).__panoramaHidden) {
                    e.enabled = true;
                    delete (e as any).__panoramaHidden;
                }
            }

            // Restore weather if we hid it on entry
            if (weatherInstance && (weatherInstance.entity as any).__panoramaHidden) {
                weatherInstance.entity.enabled = true;
                delete (weatherInstance.entity as any).__panoramaHidden;
            }

            // Dissolve splat back in
            if (splatEntity) {
                applyDissolveToEntity(splatEntity, 1.0);
            }

            // Restore camera controls
            cameraControls.enablePan = true;
            cameraControls.enableOrbit = isExploreSubModeEnabled('orbit');
            cameraControls.enableFly = isExploreSubModeEnabled('fly');
            cameraControls.pitchRange = prePanoramaPitchRange || new pc.Vec2(-89, 89);
            cameraControls.zoomRange = prePanoramaZoomRange || new pc.Vec2(0.01, Infinity);
            prePanoramaPitchRange = null;
            prePanoramaZoomRange = null;

            // Restore previous camera mode (this handles controller re-enabling)
            if (prePanoramaCameraMode && prePanoramaCameraMode !== 'explore') {
                setCameraMode(prePanoramaCameraMode);
            } else {
                const subMode = prePanoramaExploreSubMode || 'fly';
                if (subMode === 'walk') {
                    setCameraMode('walk');
                } else {
                    const restoredSubMode = toCameraControlsSubMode(getEnabledExploreSubMode(subMode));
                    cameraControls.setMode(restoredSubMode);
                    updateExploreBtnState(restoredSubMode);
                    const mobile = isMobileDevice();
                    if (mobile) {
                        setJoystickVisible(uiElements, restoredSubMode === 'fly');
                        cameraControls.mobileInputLayout = restoredSubMode === 'fly' ? 'touch-touch' : 'joystick-touch';
                    } else {
                        setWasdHintVisible(uiElements, restoredSubMode === 'fly');
                        setOrbitHintVisible(uiElements, restoredSubMode === 'orbit', orbitRefocusLocked());
                    }
                }
            }

            panoramaModeEnabled = false;
            panoramaModeTransitioning = false;
            prePanoramaCameraMode = null;
            prePanoramaExploreSubMode = null;
            events.emit('panoramaModeChange', { enabled: false });
        }
    }

    /** Active panorama portal state (for exit restoration) */
    let activePanoramaPortal: PortalConfigLike | null = null;
    let panoramaExitButton: HTMLElement | null = null;

    /** Loading spinner shown while panorama image loads */
    let panoramaLoadingEl: HTMLElement | null = null;

    /** True from activation-click to onload/onerror; blocks re-entry while the
     *  image is in flight (panoramaModeEnabled isn't set until after load). */
    let panoramaLoadPending = false;

    /** Handle for the deferred skybox-restore in exitPanoramaPortal; cleared on
     *  re-entry so a stale timer doesn't overwrite the next panorama's skybox. */
    let panoramaRestoreTimer: ReturnType<typeof setTimeout> | null = null;

    /** Cache of decoded panorama images so re-entry and first-activation (after
     *  the portal preview loaded it) skip redownload + redecode on the main
     *  thread. Portal-preview creation and explicit preloads populate this. */
    const panoramaImageCache = new Map<string, HTMLImageElement | Promise<HTMLImageElement>>();

    function preloadPanoramaImage(url: string): Promise<HTMLImageElement> {
        const cached = panoramaImageCache.get(url);
        if (cached) return Promise.resolve(cached as HTMLImageElement | Promise<HTMLImageElement>);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = url;
        const decoded = (img.decode ? img.decode() : new Promise<void>((resolve, reject) => {
            if (img.complete && img.naturalWidth > 0) resolve();
            else { img.onload = () => resolve(); img.onerror = reject; }
        })).then(() => {
            panoramaImageCache.set(url, img);
            return img;
        }).catch((err) => {
            panoramaImageCache.delete(url);
            throw err;
        });
        panoramaImageCache.set(url, decoded);
        return decoded;
    }

    /**
     * Handle activation of a panorama portal — show loading spinner,
     * load the 360° image, build the skybox, then transition.
     */
    function handlePanoramaPortalActivation(portal: PortalConfigLike): void {
        if (panoramaModeEnabled || panoramaModeTransitioning || panoramaLoadPending) {
            console.warn('[Portal] Panorama activation skipped — already active or in-flight', {
                panoramaModeEnabled, panoramaModeTransitioning, panoramaLoadPending
            });
            return;
        }
        const panoUrl = portal.panoramaUrl;
        if (!panoUrl) {
            console.warn('[Portal] Panorama portal has no panoramaUrl', portal);
            return;
        }
        console.log('[StorySplat Viewer] Panorama portal activated:', portal.title || portal.id, panoUrl);
        activePanoramaPortal = portal;
        panoramaLoadPending = true;

        // If the user re-enters panorama mode before the previous exit's
        // deferred skybox-restore fires, cancel it so it doesn't overwrite
        // the new panorama's skybox 1.2s later.
        if (panoramaRestoreTimer) {
            clearTimeout(panoramaRestoreTimer);
            panoramaRestoreTimer = null;
        }

        // Show loading spinner only if the image isn't already decoded in the
        // cache — a cache hit takes <1 frame so a spinner would just flash.
        const cachedEntry = panoramaImageCache.get(panoUrl);
        const cacheHit = cachedEntry instanceof HTMLImageElement && cachedEntry.complete && cachedEntry.naturalWidth > 0;
        if (!cacheHit) {
            const spinner = document.createElement('div');
            spinner.className = 'storysplat-panorama-loading';
            const spinnerDot = document.createElement('div');
            spinnerDot.className = 'storysplat-panorama-loading-spinner';
            spinner.appendChild(spinnerDot);
            container.appendChild(spinner);
            panoramaLoadingEl = spinner;
        }

        // Reuse the decoded image from the cache (populated when the portal
        // preview loaded) or kick off a fresh preload. Either way we get a
        // fully-decoded HTMLImageElement with zero main-thread decode cost
        // when we hand it to the skybox pipeline.
        const removeSpinner = () => {
            if (!panoramaLoadingEl) return;
            const el = panoramaLoadingEl;
            panoramaLoadingEl = null;
            el.classList.add('storysplat-panorama-loading--hiding');
            setTimeout(() => el.remove(), 200);
        };

        preloadPanoramaImage(panoUrl).then((img) => {
            panoramaLoadPending = false;
            if (isDestroyed || activePanoramaPortal !== portal) return;
            // Use the lightweight sphere-based skybox — no cubemap reproject,
            // no env atlas allocation (those crash iOS Safari on larger panoramas).
            setPanoramaSkybox(panoUrl, img);
            setPanoramaMode(true);

            removeSpinner();

            // Create exit button overlay
            const exitBtn = document.createElement('button');
            exitBtn.className = 'storysplat-panorama-exit-btn';
            exitBtn.textContent = getButtonLabel(currentButtonLabels, 'exitPanorama');
            exitBtn.setAttribute('aria-label', getButtonLabel(currentButtonLabels, 'exitPanorama'));
            exitBtn.addEventListener('click', () => exitPanoramaPortal());
            container.appendChild(exitBtn);
            panoramaExitButton = exitBtn;

            // Escape key to exit
            const escHandler = (e: KeyboardEvent) => {
                if (e.key === 'Escape') exitPanoramaPortal();
            };
            document.addEventListener('keydown', escHandler);
            (exitBtn as any)._escHandler = escHandler;
        }).catch((err) => {
            panoramaLoadPending = false;
            console.warn('[Portal] Failed to load panorama image', panoUrl, err);
            removeSpinner();
            activePanoramaPortal = null;
        });
    }

    /**
     * Exit panorama portal mode — dissolve splat back in, THEN restore skybox.
     * Keeps the panorama skybox visible during the dissolve so there's no grey flash.
     */
    function exitPanoramaPortal(): void {
        if (!panoramaModeEnabled && !panoramaModeTransitioning) return;

        // Panorama portals keep the viewer alive — re-enable portal proximity
        // checks that were frozen when the portal triggered.
        portalNavigationPending = false;

        // Exit panorama mode — this re-enables the splat and starts dissolve-in.
        // Do NOT swap skybox yet; the panorama stays as background during dissolve.
        setPanoramaMode(false);

        // Restore the original skybox AFTER the dissolve-in finishes (or immediately
        // if there's no splat to dissolve). The dissolve duration is ~1s.
        const restoreSkybox = () => {
            panoramaRestoreTimer = null;
            if (originalSkyboxUrl) {
                setSkyboxRuntime(originalSkyboxUrl, originalSkyboxRotation, undefined, originalSkyboxVerticalOffset);
            } else {
                cleanupSkybox();
            }
        };
        if (panoramaRestoreTimer) {
            clearTimeout(panoramaRestoreTimer);
            panoramaRestoreTimer = null;
        }
        if (splatEntity) {
            panoramaRestoreTimer = setTimeout(restoreSkybox, 1200);
        } else {
            restoreSkybox();
        }

        // Remove exit button
        if (panoramaExitButton) {
            const escHandler = (panoramaExitButton as any)._escHandler;
            if (escHandler) document.removeEventListener('keydown', escHandler);
            panoramaExitButton.remove();
            panoramaExitButton = null;
        }

        activePanoramaPortal = null;
        console.log('[StorySplat Viewer] Exited panorama portal mode');
    }

    // Update preloader progress helper (defined early so loadSplat can use it).
    // EVERY initial-load progress update goes through here. The value is monotonic (a URL
    // fallback or a later phase never rewinds it), and the preloader text always shows the
    // same number as the bar with the plain "Loading" label until the preloader hides.
    // Phases: init 20% → download 30–85% → LOD reveal hold 85–100%.
    let displayedLoadProgress = 0;
    let lastEmittedPercent = -1;
    const updateProgress = (progress: number, text?: string) => {
        const requested = Number.isFinite(progress) ? Math.max(0, Math.min(1, progress)) : 0;
        const previous = displayedLoadProgress;
        displayedLoadProgress = Math.max(displayedLoadProgress, requested);
        // The reveal hold calls this every frame — skip no-op updates (same whole percent), but
        // always deliver the final 1.0.
        const percent = Math.round(displayedLoadProgress * 100);
        if (percent === lastEmittedPercent && !(displayedLoadProgress === 1 && previous < 1)) return;
        lastEmittedPercent = percent;
        if (uiElements.preloader) {
            updatePreloaderProgress(uiElements.preloader, displayedLoadProgress, undefined, getButtonLabel(currentButtonLabels, 'loading'));
        }
        events.emit('progress', { progress: displayedLoadProgress, text });
    };
    // Helper to check if URL is hosted on StorySplat servers (Firebase Storage)
    // Only count bandwidth for files we host, not self-hosted files
    function isStorySplatHostedUrl(url: string): boolean {
        const storySplatHosts = [
            'firebasestorage.googleapis.com/v0/b/story-splat',
            'storage.googleapis.com/story-splat',
            'storysplat.com',
            'discover.storysplat.com',
            // Cloudflare Worker CDN proxy. src/utils/cdnProxy.ts rewrites Firebase
            // Storage URLs to this host at save time when VITE_CDN_ENABLED=true, so
            // in production this is the host the viewer actually loads from. Cache
            // hits served by Cloudflare cost no Firebase egress but are still
            // StorySplat-hosted traffic and must be attributed to the scene —
            // without this entry every CDN-served scene (i.e. essentially every
            // current production scene) reports bandwidthUsed: 0 as if self-hosted.
            'storysplat-storage-proxy.the-b89.workers.dev/story-splat'
        ];
        // Honor a deployment-configured proxy host so a changed worker host (editor/discover set it
        // from an env var) doesn't drift from this hardcoded default and misreport bandwidth as 0.
        if (options.cdnProxyHost) storySplatHosts.push(options.cdnProxyHost);
        return storySplatHosts.some(host => url.includes(host));
    }
    // Is the page itself served from a StorySplat domain? Self-hosted exports
    // (downloaded v2 HTML opened on a customer domain or file://) bake in the
    // `analytics` option, but must NOT phone home — mirror the injected view-
    // ping script's host guard so the streaming meter respects the same
    // privacy/offline contract instead of beaconing to discover.storysplat.com.
    function isStorySplatHostedPage(): boolean {
        try {
            if (typeof location === 'undefined') return false;
            const host = location.hostname || '';
            return host === 'discover.storysplat.com'
                || host === 'storysplat.com'
                || host === 'storysplat.app'
                || host.endsWith('.storysplat.com')
                || host.endsWith('.storysplat.app');
        } catch {
            return false;
        }
    }
    // Load splat
    async function loadSplat(): Promise<void> {
        // Track bandwidth used during loading (only for StorySplat-hosted files)
        const loadStartedAt = performance.now();
        let bandwidthUsed = 0;
        let loadedUrl = ''; // Track which URL was successfully loaded
        // Build URL list with priority: LOD streaming > SOG > PLY/Other formats
        // This ensures backward compatibility with all formats while preferring optimal ones
        const urls: string[] = [];
        // Mobile UA (or editor preview override) uses the mobile URL *set* as a whole — never mixed
        // with desktop URLs in the priority cascade. Mixing is wrong: with only mobileSogUrl set,
        // the desktop lodMetaUrl would still win and the mobile asset would never load.
        // NOTE: the desktop branch deliberately leaves compressedPlyUrl undefined — the main
        // splat only ships a separately-hosted compressed PLY for the mobile upload path.
        const onMobile = usesMobileRenderProfile() || config.forceMobilePreview === true;
        const selected = selectAssetUrls({
            sogUrl: config.sogUrl,
            compressedPlyUrl: undefined,
            splatUrl: config.splatUrl,
            lodMetaUrl: config.lodMetaUrl,
            mobileSogUrl: config.mobileSogUrl,
            mobileCompressedPlyUrl: config.mobileCompressedPlyUrl,
            mobileSplatUrl: config.mobileSplatUrl,
            mobileLodMetaUrl: config.mobileLodMetaUrl,
        }, onMobile);
        const lodMetaUrl = selected.lodMetaUrl;
        const sogUrl = selected.sogUrl;
        const compressedPlyUrl = selected.compressedPlyUrl;
        const splatUrl = selected.splatUrl;
        const usingMobileAsset = onMobile && Boolean(
            config.mobileLodMetaUrl || config.mobileSogUrl || config.mobileSplatUrl || config.mobileCompressedPlyUrl
        );
        console.log('[SPLAT] Building URL priority list...');
        console.log('[SPLAT] Mobile asset selection:', {
            onMobile,
            forceMobilePreview: config.forceMobilePreview === true,
            usingMobileAsset,
        });
        console.log('[SPLAT] Available URLs:', {
            lodMetaUrl: lodMetaUrl || '(none)',
            sogUrl: sogUrl || '(none)',
            compressedPlyUrl: compressedPlyUrl || '(none)',
            splatUrl: splatUrl || '(none)',
            fallbackUrls: config.fallbackUrls?.length || 0
        });
        // Highest priority: LOD streaming format (lod-meta.json) if available
        // NOTE: lod-meta.json should have RELATIVE paths for chunk meta.json files (e.g., "0_0/meta.json")
        // PlayCanvas resolves these relative to the lod-meta.json base URL
        // Only the chunk meta.json files should have full URLs for their texture references
        if (lodMetaUrl) {
            urls.push(lodMetaUrl);
            console.log('[SPLAT] ✓ LOD streaming URL added (highest priority):', lodMetaUrl);
        }
        // Second priority: SOG compressed format
        if (sogUrl) {
            urls.push(sogUrl);
            console.log('[SPLAT] ✓ SOG URL added (second priority):', sogUrl);
        }
        // Third priority: compressed PLY, when available separately from the original mobile upload.
        if (compressedPlyUrl && compressedPlyUrl !== splatUrl) {
            urls.push(compressedPlyUrl);
            console.log('[SPLAT] ✓ Compressed PLY URL added (third priority):', compressedPlyUrl);
        }
        // Fourth priority: Original format (PLY, SPLAT, etc.)
        if (splatUrl) {
            urls.push(splatUrl);
            console.log('[SPLAT] ✓ Original splat URL added (fourth priority):', splatUrl);
        }
        // Fallback URLs for additional format support
        if (config.fallbackUrls) {
            urls.push(...config.fallbackUrls);
            console.log('[SPLAT] ✓ Fallback URLs added:', config.fallbackUrls);
        }
        console.log('[SPLAT] Final URL priority order:', urls);
        console.log('[SPLAT] Will try URLs in order: LOD streaming > SOG > compressed PLY > PLY/Other');
        updateProgress(0.3, getButtonLabel(currentButtonLabels, 'loading'));
        for (const url of urls) {
            if (!url)
                continue;
            try {
                // Detect 3D mesh models (GLB/GLTF) — these need container loading, not gsplat
                const urlClean = decodeURIComponent(url.toLowerCase().split(/[?#]/)[0]);
                const urlFilename = urlClean.split('/').pop() || '';
                if (urlFilename.endsWith('.glb') || urlFilename.endsWith('.gltf')) {
                    console.log('[SPLAT] Detected GLB/GLTF container (meshes and/or KHR_gaussian_splatting):', url);
                    loadCustomMesh({ id: '__primary-mesh__', name: 'Primary Model', modelUrl: url, position: { x: 0, y: 0, z: 0 } }, 0);
                    // Mesh-primary scenes previously returned without emitting
                    // 'loaded', so bandwidth beacons and load-complete consumers
                    // never fired for them. Emit when the container asset is
                    // actually ready (loadCustomMesh registered it by meshId).
                    const isHostedMesh = isStorySplatHostedUrl(url);
                    const primaryMeshAsset = customMeshEntities.get('__primary-mesh__')?.modelAsset;
                    if (primaryMeshAsset) {
                        primaryMeshAsset.on('progress', (received: number, total: number) => {
                            if (total > 0 && isHostedMesh) bandwidthUsed = received;
                        });
                        primaryMeshAsset.ready(() => {
                            emitSceneLoaded({
                                bandwidthUsed: isHostedMesh ? bandwidthUsed : 0,
                                isStorySplatHosted: isHostedMesh,
                                loadTimeMs: Math.round(performance.now() - loadStartedAt),
                                modelUrl: url
                            });
                        });
                    } else {
                        emitSceneLoaded({
                            bandwidthUsed: 0,
                            isStorySplatHosted: isHostedMesh,
                            loadTimeMs: Math.round(performance.now() - loadStartedAt),
                            modelUrl: url
                        });
                    }
                    updateProgress(1.0, '');
                    return; // Mesh loaded successfully, no need to try other URLs
                }
                // Determine asset type based on extension
                const ext = url.split('.').pop()?.toLowerCase() || 'splat';
                const assetType = 'gsplat'; // PlayCanvas uses 'gsplat' for all gaussian splat formats
                // Check format type for logging
                const isLodFormat = url.includes('lod-meta.json');
                const urlIsSogFormat = url.includes('.sog') || isLodFormat;
                console.log('[SPLAT] Attempting to load URL:', url);
                console.log('[SPLAT] Format detection:', {
                    extension: ext,
                    isLodStreaming: isLodFormat,
                    isSogFormat: urlIsSogFormat,
                    assetType: assetType
                });
                const preparedLod = isLodFormat
                    ? await prepareLodManifestUrl(url)
                    : { url, cleanup: () => undefined, correction: null, manifestLoaded: false };
                const loadUrl = preparedLod.url;
                // Correction comes from prepareLodManifestUrl's single fetch; only fall back to a
                // separate fetch when that fetch failed (matches the old failure behavior).
                const coordinateCorrection = isLodFormat
                    ? (preparedLod.manifestLoaded ? preparedLod.correction : await readLodCoordinateCorrection(loadUrl))
                    : null;
                const asset = new pc.Asset('splat-' + Date.now(), assetType, { url: loadUrl });
                // Note: lod-meta.json filenames are rewritten to absolute CDN URLs during upload,
                // so PlayCanvas's GSplatOctree uses them as-is (path.isRelativePath returns false).
                // No mapUrl workaround is needed.
                // Track asset loading progress
                asset.on('progress', (received: number, total: number) => {
                    if (total > 0) {
                        // Track bandwidth: use received bytes (actual data transferred)
                        // Only track if this is a StorySplat-hosted URL
                        if (isStorySplatHostedUrl(url)) {
                            bandwidthUsed = received;
                        }
                        // Map progress from 0.3 to 0.85 (0.85-1.0 is the post-load / LOD reveal hold)
                        const loadProgress = 0.3 + Math.min(1, received / total) * 0.55;
                        const percent = Math.round((received / total) * 100);
                        if (percent % 25 === 0 || percent === 100) { // Log at 25%, 50%, 75%, 100%
                            console.log(`[SPLAT] Loading progress: ${percent}% (${(received / 1024 / 1024).toFixed(2)}MB / ${(total / 1024 / 1024).toFixed(2)}MB)`);
                        }
                        updateProgress(loadProgress);
                    }
                });
                // Track which URL we're attempting to load
                loadedUrl = url;
                await new Promise<void>((resolve, reject) => {
                    let hasRejected = false;
                    // Catch unhandled promise rejections during loading (e.g., SOG v1 parse errors)
                    // Listen for SOG formats which may have deprecated format issues
                    const unhandledRejectionHandler = urlIsSogFormat ? (event: PromiseRejectionEvent) => {
                        if (hasRejected)
                            return;
                        const errorMsg = event.reason?.message || String(event.reason);
                        // Check if this is a SOG parsing error (deprecated v1 format)
                        if (errorMsg.includes('shape') || errorMsg.includes('upgradeMeta')) {
                            console.warn('[SPLAT] ⚠️ Parse error detected (likely deprecated v1 format):', errorMsg);
                            hasRejected = true;
                            event.preventDefault(); // Prevent console error
                            // Emit a deprecation warning event for UI to display
                            events.emit('warning', {
                                type: 'deprecated_format',
                                message: 'This scene uses an outdated SOG format. Please re-upload your scene with the latest tools for better performance.',
                                details: 'SOG v1 format is deprecated. Use splat-transform v2+ to convert your PLY files.',
                                url: url
                            });
                            console.log('[SPLAT] Will try fallback URL if available...');
                            reject(new Error(`SOG parse error: ${errorMsg}`));
                        }
                    } : null;
                    if (urlIsSogFormat && unhandledRejectionHandler) {
                        console.log('[SPLAT] Registered error handler for SOG format parsing');
                        window.addEventListener('unhandledrejection', unhandledRejectionHandler);
                    }
                    // Cleanup handler when done
                    const cleanup = () => {
                        if (urlIsSogFormat && unhandledRejectionHandler) {
                            window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
                        }
                        preparedLod.cleanup();
                    };
                    // Use asset.ready() like HTML export - this waits for asset AND resources to be ready
                    asset.ready(() => {
                        // Check if viewer was destroyed while loading
                        if (isDestroyed) {
                            console.log('[SPLAT] Ignoring load - viewer was destroyed during loading');
                            cleanup();
                            reject(new Error('Viewer destroyed'));
                            return;
                        }
                        console.log('[SPLAT] ✓ Asset loaded and resources ready');
                        try {
                            primarySplatCoordinateCorrection = coordinateCorrection;
                            primarySplatFrameUrl = loadUrl;
                            splatEntity = setupSplatEntity(asset, loadUrl, coordinateCorrection);
                            syncSplatCollisionBindings();
                            splatPickCacheKey = loadUrl;
                            // Mobile has no cursor-follow reticle — defer the index build
                            // (readback + RAM + CPU) to the first tap-pick instead of the
                            // already-saturated load/reveal window; the collision fallback
                            // covers the interim.
                            if (!isMobile) primeExactSplatPick(loadUrl); // prebuild the CPU pick index during load (loadUrl = SOG cache key)
                            // Format-specific logging
                            if (isLodStreamingFormat(url)) {
                                console.log('[SPLAT] ✓ LOD streaming enabled for this splat');
                                console.log('[SPLAT] LOD configured:', {
                                    lodBaseDistance: resolvedLodBaseDistance,
                                    lodMultiplier: resolvedLodMultiplier,
                                    preset: resolvedLodSettings.presetName
                                });
                            }
                            else if (urlIsSogFormat) {
                                console.log('[SPLAT] ✓ Single SOG file loaded (no LOD streaming)');
                            }
                            else {
                                console.log('[SPLAT] Standard format loaded (PLY/SPLAT)');
                            }
                            // Apply reveal effect if configured (start disabled - enabled after preloader hides)
                            const revealPresetName = (options.revealEffect || scene.revealEffect || config.revealEffect || 'none') as RevealPreset;
                            const revealStyleName = (options.revealStyle || scene.revealStyle || config.revealStyle || 'bloom') as RevealStyle;
                            const revealConfig = getRevealPreset(revealPresetName, revealStyleName);
                            if (revealConfig) {
                                // Add script component for reveal effect
                                splatEntity.addComponent('script');
                                // Create the reveal effect - bloom (default) or radial
                                const RevealClass = revealStyleName === 'radial'
                                    ? getGsplatRevealRadialClass()
                                    : getGsplatRevealBloomClass();
                                revealScript = ((splatEntity.script as ScriptComponentWithCreate | undefined)?.create?.(RevealClass) as RevealEffectScript | undefined) ?? null;
                                if (revealScript) {
                                    // Start disabled - will be enabled after preloader hides
                                    revealScript.enabled = false;
                                    // Apply preset configuration
                                    revealScript.center.set(0, 0, 0);
                                    revealScript.speed = revealConfig.speed;
                                    revealScript.acceleration = revealConfig.acceleration;
                                    revealScript.delay = revealConfig.delay;
                                    revealScript.oscillationIntensity = revealConfig.oscillationIntensity;
                                    revealScript.dotTint.set(revealConfig.dotTint.r, revealConfig.dotTint.g, revealConfig.dotTint.b);
                                    revealScript.waveTint.set(revealConfig.waveTint.r, revealConfig.waveTint.g, revealConfig.waveTint.b);
                                    revealScript.endRadius = revealConfig.endRadius;
                                    console.log('[StorySplat Viewer] Reveal effect configured (waiting to start):', revealStyleName, revealPresetName);
                                }
                            }
                            else {
                                console.log('[StorySplat Viewer] Reveal effect disabled');
                            }
                            // For SOG v1, wait briefly to let async texture parse errors surface —
                            // the unhandled-rejection handler (registered only when urlIsSogFormat)
                            // needs a beat to reject first and trigger URL fallback. LOD streaming
                            // and plain PLY/SPLAT never register that handler (hasRejected can never
                            // trip), so the 100ms was pure dead time on their critical path.
                            if (isLodFormat || !urlIsSogFormat) {
                                cleanup();
                                resolve();
                            } else {
                                setTimeout(() => {
                                    if (hasRejected)
                                        return; // Already rejected by unhandled rejection handler
                                    cleanup();
                                    resolve();
                                }, 100);
                            }
                        }
                        catch (syncError) {
                            console.error('[StorySplat Viewer] Error during gsplat setup:', syncError);
                            cleanup();
                            reject(syncError);
                        }
                    });
                    asset.on('error', (err: unknown) => {
                        // Enhanced error logging to help diagnose SOG/PLY loading issues
                        const errObj = err as Record<string, unknown> | null;
                        console.error('[SPLAT] ✗ Asset load error:', {
                            url,
                            assetType,
                            isSogFormat: urlIsSogFormat,
                            isLodFormat: isLodFormat,
                            error: err,
                            message: errObj?.message || 'Unknown error',
                            status: errObj?.status || errObj?.statusCode || 'N/A'
                        });
                        cleanup();
                        reject(err);
                    });
                    loadGsplatAsset(asset, loadUrl);
                });
                primarySplatUrlOverride = loadedUrl;
                const isHostedOnStorySplat = isStorySplatHostedUrl(loadedUrl);
                const loadTimeMs = Math.round(performance.now() - loadStartedAt);
                // Only report bandwidth for StorySplat-hosted files. When the
                // streaming meter is active, emitSceneLoaded overrides
                // bandwidthUsed with the meter total (covers manifest + initial
                // LOD chunks, not just this asset's progress bytes).
                emitSceneLoaded({
                    bandwidthUsed: isHostedOnStorySplat ? bandwidthUsed : 0,
                    isStorySplatHosted: isHostedOnStorySplat,
                    loadTimeMs,
                    modelUrl: loadedUrl
                });
                console.log('[SPLAT] ✓✓✓ SPLAT LOADED SUCCESSFULLY ✓✓✓');
                console.log('[SPLAT] Load summary:', {
                    url: loadedUrl,
                    format: isLodFormat ? 'LOD streaming (lod-meta.json)' : urlIsSogFormat ? 'SOG (compressed)' : isSpzSplatUrl(loadedUrl) ? 'SPZ (native v4, Y-up frame)' : 'Standard (PLY/SPLAT)',
                    lodEnabled: isLodFormat,
                    lodPreset: isLodFormat ? resolvedLodSettings.presetName : 'N/A',
                    bandwidthUsed: `${(bandwidthUsed / 1024 / 1024).toFixed(2)}MB`,
                    loadTimeMs,
                    isStorySplatHosted: isHostedOnStorySplat,
                    bandwidthCounted: isHostedOnStorySplat ? 'Yes' : 'No (self-hosted)'
                });
                return;
            }
            catch (err) {
                console.warn('[SPLAT] ✗ Failed to load URL, trying next fallback:', url);
                console.warn('[SPLAT] Error:', err);
            }
        }
        console.error('[SPLAT] ✗✗✗ FAILED TO LOAD SPLAT FROM ANY URL ✗✗✗');
        console.error('[SPLAT] Tried URLs:', urls);
        events.emit('error', new Error('Failed to load splat from any URL'));
    }
    /**
     * Shared helper: create a splat entity from a loaded asset and apply config transforms.
     * Used by both loadSplat() and loadSplatByUrl() to avoid duplicated setup logic.
     */
    function setupSplatEntity(asset: pc.Asset, url: string, coordinateCorrection?: LodCoordinateCorrection | null): pc.Entity {
        const entity = new pc.Entity('splat');
        // "Splat object" selections name the scan by this id (effects/splat-objects.ts).
        registerSplatObject(entity, MAIN_SPLAT_OBJECT_ID);
        entity.addComponent('gsplat', {
            asset: asset,
            unified: true
        });
        // Set LOD parameters for LOD streaming format (using new geometric API)
        const gs = entity.gsplat as unknown as GSplatComponentWithRuntime | undefined;
        if (gs && isLodStreamingFormat(url)) {
            gs.lodBaseDistance = resolvedLodBaseDistance;
            gs.lodMultiplier = resolvedLodMultiplier;
            // PlayCanvas 2.20 made the scene-level lodRange setters no-ops; the
            // functional path is per-GSplatComponent. Set it on each loaded splat
            // so the LOD range (incl. the custom preset) actually applies.
            (gs as any).lodRangeMin = resolvedLodRange[0];
            (gs as any).lodRangeMax = resolvedLodRange[1];
        }
        // LCC2 from splat-transform is already in PlayCanvas-native coords (modulo
        // the source's Z-up convention, handled by the rotation correction below),
        // and so is SPZ, whose spec stores splats in the Y-up RUB frame (see
        // utils/splatFrame). For those, we apply identity scale + identity position
        // so the gsplat shader gets a clean rotation matrix to decompose. The legacy
        // Babylon-style (1,-1,-1) flip + Z-negation that PLY/SOG/.splat data needs
        // would render them upside down and Z-mirrored, and produces a covariance
        // basis that PlayCanvas's WebGPU raster-gpu shader can't extract cleanly,
        // rendering splats as long streaks.
        const isNativeFrameSource = usesNativeSplatFrame(url, coordinateCorrection?.sourceFormat);
        const scale = config.scale || { x: 1, y: 1, z: 1 };
        const invertX = config.invertXScale || false;
        const invertY = config.invertYScale || false;
        const finalScale = isNativeFrameSource ? {
            x: invertX ? -scale.x : scale.x,
            y: invertY ? -scale.y : scale.y,
            z: (invertX !== invertY) ? -scale.z : scale.z,
        } : {
            // Legacy Babylon-authored splat/PLY: replicate BabylonJS GaussianSplattingMesh
            // Y=-1 initial scaling + Z handedness flip.
            x: invertX ? -scale.x : scale.x,
            y: invertY ? scale.y : -scale.y,
            z: (invertX !== invertY) ? scale.z : -scale.z,
        };
        entity.setLocalScale(finalScale.x, finalScale.y, finalScale.z);
        const pos = config.position || [0, 0, 0];
        entity.setPosition(pos[0], pos[1], isNativeFrameSource ? pos[2] : -pos[2]);
        const rawRot = config.rotation || [0, 0, 0];
        const rot: [number, number, number] = [rawRot[0] || 0, rawRot[1] || 0, rawRot[2] || 0];
        console.log('[SPLAT][TRANSFORM]', {
            buildMarker: 'native-frame-scale-v3',
            isNativeFrameSource,
            isSpzSource: isSpzSplatUrl(url),
            correctionSourceFormat: coordinateCorrection?.sourceFormat ?? null,
            correctionEulerDegrees: coordinateCorrection?.eulerDegrees ?? null,
            finalScale,
            position: { x: pos[0], y: pos[1], z: isNativeFrameSource ? pos[2] : -pos[2] },
            rotationRadians: rot,
        });
        applySplatEntityRotation(entity, rot, coordinateCorrection);
        sceneContent.addChild(entity);
        // Set alphaClip for GSplat picking support
        if (entity.gsplat?.material) {
            entity.gsplat.material.setParameter('alphaClip', 0.01);
        }
        return entity;
    }

    /**
     * Shared helper: load a gsplat asset by URL, using octree parser for LOD formats.
     */
    function loadGsplatAsset(asset: pc.Asset, url: string): void {
        app.assets.add(asset);
        if (url.includes('lod-meta.json')) {
            // Hand-drive the octree parser instead of app.assets.load: the engine picks
            // parsers by URL basename, which misses URL-encoded paths (Firebase Storage's
            // .../o/scenes%2Flod-meta.json yields basename "scenes%2flod-meta.json", and
            // SogParser then claims the .json), and the load callback below is where the
            // chunk-download concurrency gets raised.
            const handler = app.loader.getHandler('gsplat') as GsplatHandlerWithParsers | null;
            const octreeParser = findOctreeParser(handler?.parsers, url);
            if (octreeParser) {
                (octreeParser as { load: (urlObj: { load: string; original?: string }, callback: (err: unknown, resource: unknown) => void, asset?: pc.Asset) => void }).load(
                    { load: url, original: url },
                    (err: unknown, resource: unknown) => {
                        if (err) {
                            asset.fire('error', err);
                            return;
                        }
                        asset.resource = resource as object;
                        // Raise the engine's chunk-download concurrency (GSplatAssetLoader
                        // hardcodes maxConcurrentLoads=2 with a FIFO queue — a private surface,
                        // hence the guarded poke, same pattern as readLodPending). 2 concurrent
                        // fetches leave high-RTT links mostly idle during the initial fill; keep
                        // mobile lower since more concurrent SOG decodes raise peak memory.
                        const octreeLoader = (resource as { octree?: { assetLoader?: { maxConcurrentLoads?: unknown } } })?.octree?.assetLoader;
                        if (octreeLoader && typeof octreeLoader.maxConcurrentLoads === 'number') {
                            octreeLoader.maxConcurrentLoads = getPerfOverrides().maxConcurrentLoads ?? (usesMobileRenderProfile() ? 4 : 6);
                        }
                        asset.loaded = true;
                        asset.fire('load', asset);
                    },
                    asset
                );
            } else {
                app.assets.load(asset);
            }
        } else {
            app.assets.load(asset);
        }
    }

    // Editor API: settles when the most recent loadSplatByUrl() has reached the same "decent
    // detail" reveal condition the published viewer holds its preloader for (LOD only; non-LOD
    // and mesh loads are ready as soon as loadSplatByUrl resolves). Always settles — capped.
    let lastLoadRevealReady: Promise<void> = Promise.resolve();
    /**
     * Load a new primary splat by URL (editor use).
     * Destroys the existing primary splat entity and loads a new one.
     * Priority: sogUrl > splatUrl (matching standard URL priority).
     */
    async function loadSplatByUrl(splatUrl: string, sogUrl?: string, ...fallbackUrls: Array<string | undefined>): Promise<void> {
        lastLoadRevealReady = Promise.resolve();
        // Build URL list with fallback. For normal splats keep the historical
        // SOG > splat priority. For LOD reloads, try lod-meta first, then fall
        // back to SOG/CPLY/original so a bad LOD manifest doesn't blank editor.
        const urls: string[] = [];
        const pushUnique = (candidate?: string) => {
            if (candidate && !urls.includes(candidate)) urls.push(candidate);
        };
        if (splatUrl.includes('lod-meta.json')) {
            pushUnique(splatUrl);
            pushUnique(sogUrl);
            fallbackUrls.forEach(pushUnique);
        } else {
            pushUnique(sogUrl);
            pushUnique(splatUrl);
            fallbackUrls.forEach(pushUnique);
        }
        if (urls.length === 0) {
            console.warn('[SPLAT] loadSplatByUrl called with no URL');
            return;
        }
        console.log('[SPLAT] loadSplatByUrl: URLs to try:', urls);
        // Destroy existing primary splat entity. Its asset is unloaded AFTER the replacement
        // loads (same-URL reloads reuse the loader cache; unloading first would double-fetch).
        let previousPrimaryAsset: pc.Asset | null = null;
        if (splatEntity) {
            console.log('[SPLAT] loadSplatByUrl: Destroying existing splat entity');
            previousPrimaryAsset = resolveGsplatAsset(splatEntity);
            destroyEntitySafely(splatEntity);
            splatEntity = null;
        }
        for (const url of urls) {
            try {
                // Detect 3D mesh models (GLB/GLTF) — load as container, not gsplat
                const urlCleanReload = decodeURIComponent(url.toLowerCase().split(/[?#]/)[0]);
                const urlFilenameReload = urlCleanReload.split('/').pop() || '';
                if (urlFilenameReload.endsWith('.glb') || urlFilenameReload.endsWith('.gltf')) {
                    console.log('[SPLAT] loadSplatByUrl: Detected GLB/GLTF container (meshes and/or KHR_gaussian_splatting):', url);
                    // Remove previous primary mesh if it exists
                    const prev = customMeshEntities.get('__primary-mesh__');
                    if (prev) {
                        prev.removed = true;
                        prev.entity.destroy();
                        if (prev.modelAsset) app.assets.remove(prev.modelAsset);
                        customMeshEntities.delete('__primary-mesh__');
                    }
                    loadCustomMesh({ id: '__primary-mesh__', name: 'Primary Model', modelUrl: url, position: { x: 0, y: 0, z: 0 } }, 0);
                    // Match loadSplat's mesh branch: emit 'loaded' when the
                    // container asset is ready so consumers see the swap finish.
                    const reloadMeshAsset = customMeshEntities.get('__primary-mesh__')?.modelAsset;
                    if (reloadMeshAsset) {
                        reloadMeshAsset.ready(() => {
                            emitSceneLoaded({ bandwidthUsed: 0, isStorySplatHosted: isStorySplatHostedUrl(url), modelUrl: url });
                        });
                    }
                    return;
                }
                console.log('[SPLAT] loadSplatByUrl: Trying URL:', url);
                const isLodFormat = url.includes('lod-meta.json');
                const preparedLod = isLodFormat
                    ? await prepareLodManifestUrl(url)
                    : { url, cleanup: () => undefined, correction: null, manifestLoaded: false };
                const loadUrl = preparedLod.url;
                const coordinateCorrection = isLodFormat
                    ? (preparedLod.manifestLoaded ? preparedLod.correction : await readLodCoordinateCorrection(loadUrl))
                    : null;
                const asset = new pc.Asset('splat-' + Date.now(), 'gsplat', { url: loadUrl });
                await new Promise<void>((resolve, reject) => {
                    const cleanup = () => {
                        preparedLod.cleanup();
                    };
                    asset.ready(() => {
                        if (isDestroyed) {
                            cleanup();
                            resolve();
                            return;
                        }
                        try {
                            primarySplatCoordinateCorrection = coordinateCorrection;
                            primarySplatFrameUrl = loadUrl;
                            splatEntity = setupSplatEntity(asset, loadUrl, coordinateCorrection);
                            syncSplatCollisionBindings();
                            splatPickCacheKey = loadUrl;
                            if (!isMobile) primeExactSplatPick(loadUrl); // prebuild the CPU pick index during (re)load (loadUrl = SOG cache key); mobile defers to first pick
                            console.log('[SPLAT] loadSplatByUrl: New splat entity added to scene');
                            cleanup();
                            resolve();
                        } catch (err) {
                            console.error('[SPLAT] loadSplatByUrl: Error during gsplat setup:', err);
                            cleanup();
                            reject(err);
                        }
                    });
                    asset.on('error', (err: unknown) => {
                        console.error('[SPLAT] loadSplatByUrl: Asset load error:', { url, error: err });
                        cleanup();
                        reject(err);
                    });
                    loadGsplatAsset(asset, loadUrl);
                });
                primarySplatUrlOverride = url;
                // Replacement is live — free the old primary's asset unless the reload resolved
                // to the same loaded URL (shared loader-cache resource) or another consumer holds it.
                const prevUrl = (previousPrimaryAsset?.file as { url?: string } | undefined)?.url;
                if (previousPrimaryAsset && prevUrl !== loadUrl) {
                    unloadSplatAssetIfUnreferenced(previousPrimaryAsset);
                }
                emitSceneLoaded({ bandwidthUsed: 0, isStorySplatHosted: isStorySplatHostedUrl(url), modelUrl: url });
                if (isLodFormat && !isDestroyed) lastLoadRevealReady = waitForLodReveal();
                return; // Success — stop trying
            } catch (err) {
                console.warn('[SPLAT] loadSplatByUrl: Failed, trying next fallback:', url, err);
            }
        }
        throw new Error('[SPLAT] loadSplatByUrl: Failed to load from any URL');
    }

    // Load 4DGS frame sequence
    async function loadFrameSequence(): Promise<void> {
        if (!scene.frameSequence || !scene.frameSequence.frameUrls || scene.frameSequence.frameUrls.length === 0) {
            throw new Error('No frame sequence URLs provided');
        }
        console.log('[StorySplat Viewer] Loading 4DGS frame sequence:', scene.frameSequence.frameUrls.length, 'frames');
        updateProgress(0.3, 'Loading 4DGS frames...');
        frameSequencePlayer = new FrameSequencePlayer(app, {
            frameUrls: scene.frameSequence.frameUrls,
            fps: scene.frameSequence.fps || 24,
            loop: scene.frameSequence.loop !== false,
            preloadCount: scene.frameSequence.preloadCount || 10,
            // Initial playback is coordinated with the same presentation gate as tours.
            autoplay: false,
            rotation: scene.frameSequence.rotation,
        }, {
            onFrameChange: (frame, total) => {
                events.emit('frameChange', frame, total);
            },
            onLoadProgress: (loaded, total) => {
                const progress = 0.3 + (total > 0 ? Math.min(1, loaded / total) : 0) * 0.55;
                updateProgress(progress, `Loading frames... ${loaded}/${total}`);
            },
            onError: (error) => {
                console.error('[StorySplat Viewer] Frame sequence error:', error);
                events.emit('error', new Error(error));
            }
        });
        // Forward 'complete' event as 'frameComplete' for the public API
        frameSequencePlayer.on('complete', () => {
            events.emit('frameComplete');
        });
        // Hook into app update loop
        app.on('update', (dt: number) => {
            if (frameSequencePlayer && !isDestroyed) {
                frameSequencePlayer.update(dt);
            }
        });
        console.log('[StorySplat Viewer] 4DGS frame sequence player initialized');
        // Per-asset progress isn't tracked for frame sequences; the streaming
        // meter (emitSceneLoaded + periodic deltas) counts frame downloads
        // when analytics is enabled, including frames streamed after load.
        const firstFrameUrl = scene.frameSequence.frameUrls[0] || '';
        emitSceneLoaded({ bandwidthUsed: 0, isStorySplatHosted: isStorySplatHostedUrl(firstFrameUrl), modelUrl: firstFrameUrl });
    }
    // Helper to convert waypoint rotation to PlayCanvas quaternion
    // Matching POC waypointNavigation.ts exactly - NO yFlip
    // Quaternion (BabylonJS → PlayCanvas: X and Y negated for handedness) or Euler degrees.
    // Shared with extra waypoints (extraWaypoints.ts).
    function convertWaypointRotation(rot: LegacyQuat): pc.Quat {
        return waypointRotationToPlayCanvas(rot);
    }
    // =========================================================
    // WIPE TRANSITION HELPER
    // Creates and starts a wipe effect on a gsplat entity.
    // Used for splat swaps: hide on old splat + reveal on new splat.
    // =========================================================
    const revealPresetNameForSwap = (options.revealEffect || scene.revealEffect || config.revealEffect || 'none') as RevealPreset;
    const revealStyleForSwap = (options.revealStyle || scene.revealStyle || config.revealStyle || 'bloom') as RevealStyle;
    const revealConfigForSwap = getRevealPreset(revealPresetNameForSwap, revealStyleForSwap);

    // Wipe transition config
    // Scanline wipe config
    const WIPE_DURATION = 0.75;  // seconds (fast "whoop" sweep)
    const WIPE_BAND_WIDTH = 1.5; // world units (tight band for clean sync between old/new)
    const WIPE_TOP = 50.0;       // Y coordinate (generous to cover full scene including sky)
    const WIPE_BOTTOM = -50.0;   // Y coordinate (generous to cover full scene including floor)

    // Dissolve config
    const DISSOLVE_DURATION = 1.0; // seconds

    /**
     * Apply a wipe transition to a gsplat entity.
     * @param entity - The entity to apply the wipe to
     * @param mode - 1.0 = reveal, -1.0 = hide
     * @param forward - true = top-to-bottom, false = bottom-to-top
     * @param onComplete - Optional callback when wipe finishes
     */
    function applyWipeToEntity(entity: pc.Entity, mode: number, forward: boolean, onComplete?: () => void): void {
        if (!entity.script) {
            entity.addComponent('script');
        }

        // Re-use existing wipe script instance (critical for repeated swaps -
        // pc.createScript instances can only be .create()'d once per entity)
        let script = (entity.script as any)?.gsplatWipeTransition as WipeTransitionScript | undefined;
        if (!script) {
            const WipeClass = getGsplatWipeTransitionClass();
            script = ((entity.script as ScriptComponentWithCreate | undefined)?.create?.(WipeClass) as WipeTransitionScript | undefined) ?? undefined;
        }

        if (script) {
            // Disable to reset (triggers _removeShaders)
            script.enabled = false;

            // Configure wipe parameters
            script.mode = mode;
            script.duration = WIPE_DURATION;
            script.bandWidth = WIPE_BAND_WIDTH;
            // Forward = sweep top to bottom; Backward = sweep bottom to top
            script.wipeTop = forward ? WIPE_TOP : WIPE_BOTTOM;
            script.wipeBottom = forward ? WIPE_BOTTOM : WIPE_TOP;
            script.onComplete = onComplete || null;
            script.clock = swapTransitionClock;
            // Reset timer for re-use (the enable handler also resets, but be explicit)
            script.effectTime = 0;

            // Use different edge tints for reveal vs hide
            if (mode > 0) {
                // Reveal: light blue edge
                script.edgeTint.set(0.2, 0.6, 1.0);
            } else {
                // Hide: warm orange edge
                script.edgeTint.set(1.0, 0.4, 0.1);
            }

            // Enable to start (triggers _applyShaders)
            script.enabled = true;
        } else if (onComplete) {
            onComplete();
        }
    }

    /**
     * Apply paired wipe swap transition: hide old splat + reveal new splat simultaneously.
     * The old entity is disabled after the hide wipe completes.
     * @param forward - true = top-to-bottom sweep (forward navigation), false = bottom-to-top (backward)
     */
    function applyWipeSwapTransition(oldEntity: pc.Entity, newEntity: pc.Entity, forward: boolean = true): void {
        // Hide wipe on old splat
        applyWipeToEntity(oldEntity, -1.0, forward, () => {
            // When hide completes, disable the old entity
            oldEntity.enabled = false;
            console.log('[WipeTransition] Old splat hidden after wipe');
        });

        // Reveal wipe on new splat - starts simultaneously
        applyWipeToEntity(newEntity, 1.0, forward);

        console.log(`[WipeTransition] Dual wipe swap started (${forward ? 'forward' : 'backward'})`);
    }

    // =========================================================
    // DISSOLVE TRANSITION (cross-dissolve effect)
    // Both splats visible simultaneously, dissolving through each other
    // =========================================================

    /**
     * Apply a dissolve transition to a gsplat entity.
     * @param mode - 1.0 = dissolve in (reveal), -1.0 = dissolve out (hide)
     */
    function applyDissolveToEntity(entity: pc.Entity, mode: number, onComplete?: () => void): void {
        if (!entity.script) {
            entity.addComponent('script');
        }

        // Re-use existing dissolve script instance
        let script = (entity.script as any)?.gsplatDissolveTransition as DissolveTransitionScript | undefined;
        if (!script) {
            const DissolveClass = getGsplatDissolveTransitionClass();
            script = ((entity.script as ScriptComponentWithCreate | undefined)?.create?.(DissolveClass) as DissolveTransitionScript | undefined) ?? undefined;
        }

        if (script) {
            script.enabled = false;
            script.mode = mode;
            script.duration = DISSOLVE_DURATION;
            script.onComplete = onComplete || null;
            script.clock = swapTransitionClock;
            script.effectTime = 0;

            if (mode > 0) {
                script.edgeTint.set(0.3, 0.5, 0.8);  // soft blue
            } else {
                script.edgeTint.set(0.8, 0.4, 0.2);  // warm amber
            }

            script.enabled = true;
        } else if (onComplete) {
            onComplete();
        }
    }

    /**
     * Apply paired dissolve swap: dissolve out old + dissolve in new simultaneously.
     */
    function applyDissolveSwapTransition(oldEntity: pc.Entity, newEntity: pc.Entity): void {
        applyDissolveToEntity(oldEntity, -1.0, () => {
            oldEntity.enabled = false;
        });
        applyDissolveToEntity(newEntity, 1.0);

        console.log('[DissolveTransition] Cross-dissolve swap started');
    }

    // =========================================================
    // SWAP TRANSITION ROUTER
    // Selects between scanline (wipe) and dissolve effects
    // =========================================================
    type SwapTransitionType = 'scanline' | 'dissolve';
    const swapTransitionType: SwapTransitionType = config.swapTransitionType || 'dissolve';
    let swapTransitionEnabled = config.enableSwapTransition !== false; // Default: true

    const SWAP_TRANSITION_SCRIPTS = ['gsplatDissolveTransition', 'gsplatWipeTransition'] as const;
    type SwapTransitionScript = WipeTransitionScript | DissolveTransitionScript;
    function swapTransitionScriptsOf(entity: pc.Entity | null): SwapTransitionScript[] {
        const scripts = entity?.script as unknown as Record<string, SwapTransitionScript | undefined> | undefined;
        if (!scripts) return [];
        return SWAP_TRANSITION_SCRIPTS.map((name) => scripts[name]).filter((s): s is SwapTransitionScript => !!s);
    }
    /**
     * Stop any transition an entity was in the middle of (without its completion callback), so
     * a cut leaves it fully shown: a half-run dissolve would otherwise restart when it is shown.
     */
    function cancelSwapTransitions(entity: pc.Entity): void {
        for (const script of swapTransitionScriptsOf(entity)) {
            script.onComplete = null;
            script.enabled = false;
        }
    }
    /** Every splat entity a swap transition can run on: the primary and the swap splats. */
    function forEachSwapTransition(callback: (script: SwapTransitionScript) => void): void {
        const scripts: SwapTransitionScript[] = [...swapTransitionScriptsOf(splatEntity)];
        preloadedSplats.forEach((entity) => scripts.push(...swapTransitionScriptsOf(entity)));
        for (const script of scripts) callback(script);
    }

    /** Apply the configured swap transition between old and new entities */
    function applySwapTransition(oldEntity: pc.Entity, newEntity: pc.Entity, forward: boolean): void {
        // Two unified splats (the primary and a LOD / LCC2 swap) share scene.gsplat.material, so
        // they cannot dissolve against each other: cut instead.
        if (!swapTransitionEnabled || swapTransitionsCut || (isUnifiedSplat(oldEntity) && isUnifiedSplat(newEntity))) {
            cancelSwapTransitions(oldEntity);
            oldEntity.enabled = false;
            cancelSwapTransitions(newEntity);
            return;
        }
        if (swapTransitionType === 'scanline') {
            applyWipeSwapTransition(oldEntity, newEntity, forward);
        } else {
            applyDissolveSwapTransition(oldEntity, newEntity);
        }
    }

    /** Apply the configured reveal-only transition to an entity */
    function applyRevealTransition(entity: pc.Entity, forward: boolean): void {
        if (!swapTransitionEnabled || swapTransitionsCut) {
            cancelSwapTransitions(entity);
            return;
        }
        if (swapTransitionType === 'scanline') {
            applyWipeToEntity(entity, 1.0, forward);
        } else {
            applyDissolveToEntity(entity, 1.0);
        }
    }

    // =========================================================
    // SPLATSWAP SYSTEM
    // Handles swapping between multiple splat files during navigation
    // =========================================================
    /**
     * Hide a preloaded splat (Option 1: keep in memory)
     */
    function hideSplat(entity: pc.Entity): void {
        entity.enabled = false;
        console.log('[SplatSwap] Splat hidden');
    }
    /** Resolve the pc.Asset backing an entity's gsplat component (component stores id or Asset). */
    function resolveGsplatAsset(entity: pc.Entity | null): pc.Asset | null {
        const raw = entity?.gsplat?.asset;
        if (raw == null) return null;
        return typeof raw === 'number' ? (app.assets.get(raw) ?? null) : raw as pc.Asset;
    }

    /**
     * Unload a disposed splat's gsplat asset when nothing else references it. entity.destroy()
     * releases only the placement/instance — the pc.Asset keeps GPU stream textures + CPU splat
     * data resident until asset.unload(), which previously only ever happened at full app
     * teardown (~30-150 MB retained per distinct disposed splat URL). The loader cache shares
     * ONE resource object across assets with the same URL, so guard by ACTUAL loaded URL
     * (asset.file.url — the map key can differ via resolveSwapUrlByTriggerUrl): skip when the
     * primary splat, any remaining preloaded/compare entity, or an in-flight preload uses it.
     */
    function unloadSplatAssetIfUnreferenced(asset: pc.Asset | null): void {
        if (!asset) return;
        const fileUrl = (asset.file as { url?: string } | undefined)?.url;
        const assetOf = (entity: pc.Entity | null): pc.Asset | null => resolveGsplatAsset(entity);
        const loadedUrlOf = (entity: pc.Entity | null): string | undefined =>
            (assetOf(entity)?.file as { url?: string } | undefined)?.url;

        // Never detach the exact Asset object while a live entity still points at
        // it. Same-URL *different* assets are safe to remove from the registry,
        // but their shared loader-cache resource must stay loaded.
        if (assetOf(splatEntity) === asset || assetOf(compareAfterEntity) === asset) return;
        for (const [, entity] of preloadedSplats) {
            if (assetOf(entity) === asset) return;
        }

        const referencedUrls: (string | undefined)[] = [
            loadedUrlOf(splatEntity),
            loadedUrlOf(compareAfterEntity),
        ];
        preloadedSplats.forEach(entity => referencedUrls.push(loadedUrlOf(entity)));
        if (fileUrl && inFlightPreloadUrls.has(fileUrl)) referencedUrls.push(fileUrl);
        compareLoadingAssets.forEach(loadingAsset => {
            if (loadingAsset !== asset) {
                referencedUrls.push((loadingAsset.file as { url?: string } | undefined)?.url);
            }
        });
        const sharedResourceStillInUse = isSplatAssetResourceShared(fileUrl, referencedUrls);

        // Asset-registry ownership belongs to each generation even when the
        // underlying loader resource is shared. Always remove this generation's
        // Asset; only destroy the resource when no other generation/entity uses it.
        app.assets.remove(asset);
        if (sharedResourceStillInUse) {
            console.log('[SplatSwap] Detached redundant splat asset; shared resource retained:', fileUrl);
            return;
        }
        asset.unload();
        console.log('[SplatSwap] Splat asset unloaded:', fileUrl ?? asset.name);
    }

    /**
     * Dispose of a preloaded splat (Option 2: free memory)
     */
    function disposeSplat(url: string): void {
        const entity = preloadedSplats.get(url);
        if (entity) {
            // Remove relighting script from tracking before destroy
            const rlScript = (entity.script as any)?.gsplatRelighting as RelightingEffectScript | undefined;
            if (rlScript) allRelightingScripts.delete(rlScript);
            // Capture the asset BEFORE destroy (entity.gsplat is gone afterward)
            const asset = resolveGsplatAsset(entity);
            destroyEntitySafely(entity);
            preloadedSplats.delete(url);
            unloadSplatAssetIfUnreferenced(asset);
            console.log('[SplatSwap] Splat disposed:', url);
        }
    }
    /**
     * Show a preloaded splat (no transition - caller handles that)
     */
    function showSplat(url: string): boolean {
        const entity = preloadedSplats.get(url);
        if (!entity)
            return false;
        entity.enabled = true;
        currentSplatUrl = url;
        console.log('[SplatSwap] Splat shown:', url);
        return true;
    }
    /**
     * Get the currently visible splat entity (primary or swap).
     */
    function getCurrentVisibleEntity(): pc.Entity | null {
        if (currentSplatUrl) {
            const swapEntity = preloadedSplats.get(currentSplatUrl);
            if (swapEntity && swapEntity.enabled) return swapEntity;
        }
        if (splatEntity && splatEntity.enabled) return splatEntity;
        return null;
    }

    // Fallback only: the normal path reads the correction from prepareLodManifestUrl's single
    // manifest fetch; this re-fetch runs only when that fetch failed.
    async function readLodCoordinateCorrection(
        url: string,
        signal?: AbortSignal,
    ): Promise<LodCoordinateCorrection | null> {
        if (!isLodStreamingFormat(url)) return null;
        try {
            const correctionUrl = url.split('#')[0];
            const response = signal
                ? await fetch(correctionUrl, { signal })
                : await fetch(correctionUrl);
            if (signal?.aborted) throw new Error('LOD coordinate correction read cancelled');
            if (!response.ok) return null;
            const meta = await response.json() as { storySplatCoordinateCorrection?: LodCoordinateCorrection };
            if (signal?.aborted) throw new Error('LOD coordinate correction read cancelled');
            const correction = sanitizeLodCoordinateCorrection(meta.storySplatCoordinateCorrection);
            if (correction) {
                return correction;
            }
        } catch (error) {
            if (signal?.aborted) throw error;
            console.warn('[SPLAT] Could not read LOD coordinate correction metadata:', error);
        }
        return null;
    }

    function applySplatEntityRotation(entity: pc.Entity, rotationRadians: [number, number, number], correction?: LodCoordinateCorrection | null): void {
        const finalRotDeg: [number, number, number] = [
            rotationRadians[0] * (180 / Math.PI),
            rotationRadians[1] * (180 / Math.PI),
            -rotationRadians[2] * (180 / Math.PI)
        ];

        if (correction?.eulerDegrees) {
            const baseRotation = new pc.Quat().setFromEulerAngles(finalRotDeg[0], finalRotDeg[1], finalRotDeg[2]);
            const correctionRotation = new pc.Quat().setFromEulerAngles(
                correction.eulerDegrees[0],
                correction.eulerDegrees[1],
                correction.eulerDegrees[2]
            );
            entity.setRotation(new pc.Quat().mul2(baseRotation, correctionRotation));
            console.log('[SPLAT] Applied coordinate correction:', correction);
            return;
        }

        entity.setEulerAngles(finalRotDeg[0], finalRotDeg[1], finalRotDeg[2]);
    }

    /**
     * Resolve the device-appropriate asset URL for a swap splat.
     *
     * Builds a URL set from the swap's optional optimized/mobile fields and runs it
     * through selectAssetUrls (mobile set as a whole on mobile, else desktop), then
     * applies the primary splat's priority lodMeta > sog > compressedPly > url. Falls back
     * to swap.url whenever no optimized/mobile fields exist.
     */
    function resolveSwapSplatUrl(swap: SplatSwapPoint): string {
        return resolveSwapSplatUrlCandidates(swap)[0];
    }
    /**
     * Every file the swap can load on this device, best first (swapUrlCandidates): the first is
     * what resolveSwapSplatUrl returns, the rest are what loadSwapEntity falls back to when it
     * fails to load.
     */
    function resolveSwapSplatUrlCandidates(swap: SplatSwapPoint): string[] {
        const onMobile = usesMobileRenderProfile() || config.forceMobilePreview === true;
        const selected = selectAssetUrls({
            sogUrl: swap.sogModelUrl,
            compressedPlyUrl: swap.compressedPlyUrl,
            splatUrl: swap.url,
            lodMetaUrl: swap.lodMetaUrl,
            mobileSogUrl: swap.mobileSogModelUrl,
            mobileCompressedPlyUrl: swap.mobileCompressedPlyUrl,
            mobileSplatUrl: swap.mobileUrl,
            mobileLodMetaUrl: swap.mobileLodMetaUrl,
        }, onMobile);
        // Priority: lodMeta > sog > compressedPly > url. A LOD manifest loads unified with its
        // own stream (loadSwapEntity).
        return swapUrlCandidates(selected, swap.url);
    }
    /**
     * Look up the full SplatSwapPoint for a trigger url and resolve its device URL.
     * Falls back to the raw trigger url when the swap isn't found (e.g. ad-hoc
     * goToSplat targets) so behavior is unchanged for unknown urls.
     */
    function resolveSwapUrlByTriggerUrl(triggerUrl: string): string {
        const swap = additionalSplats?.find(s => s.url === triggerUrl);
        return swap ? resolveSwapSplatUrl(swap) : triggerUrl;
    }
    /** The candidates of resolveSwapSplatUrlCandidates for a trigger url ([triggerUrl] when no swap has it). */
    function resolveSwapUrlCandidatesByTriggerUrl(triggerUrl: string): string[] {
        const swap = additionalSplats?.find(s => s.url === triggerUrl);
        return swap ? resolveSwapSplatUrlCandidates(swap) : [triggerUrl];
    }
    type SwapOverrides = { position?: [number, number, number]; rotation?: [number, number, number]; scale?: { x: number; y: number; z: number } };
    // How each swap entity was framed at load (native frame, LCC2 correction), so the editor's
    // transform sliders re-apply the same convention.
    const swapEntityFrames = new WeakMap<pc.Entity, { nativeFrame: boolean; correction: LodCoordinateCorrection | null }>();
    const isUnifiedSplat = (entity: pc.Entity): boolean => entity.gsplat?.unified === true;
    /**
     * Load a swap splat into a new, hidden entity under sceneContent.
     *
     * A LOD streaming manifest (an LCC2 import or a LOD folder) loads like the primary splat:
     * unified, so it gets its own octree LOD stream, with the manifest's coordinate correction.
     * Any other file loads non-unified, so each swap splat has its OWN material for the
     * independent cross-dissolve (PlayCanvas defaults `unified` to true, so this MUST be
     * explicit; otherwise both splats share scene.gsplat.material and the dissolves clobber
     * each other). Trigger / cache identity stays `triggerUrl`; the asset loads the
     * device-resolved URL (lodMeta / sog / compressedPly / mobile).
     */
    async function loadSwapEntity(triggerUrl: string, overrides?: SwapOverrides): Promise<pc.Entity> {
        // A file that fails to load falls back to the swap's next one (e.g. an old scene's swap
        // whose lodMetaUrl no longer loads still shows from its SOG / PLY), so it is not lost.
        const candidates = resolveSwapUrlCandidatesByTriggerUrl(triggerUrl);
        for (let i = 0; ; i++) {
            try {
                return await loadSwapEntityFrom(candidates[i], overrides);
            } catch (error) {
                if (isDestroyed || i >= candidates.length - 1) throw error;
                console.warn(`[SplatSwap] Could not load ${candidates[i]}; trying ${candidates[i + 1]}`, error);
            }
        }
    }
    /** loadSwapEntity for one resolved file. */
    async function loadSwapEntityFrom(resolvedUrl: string, overrides?: SwapOverrides): Promise<pc.Entity> {
        const isLod = isLodStreamingFormat(resolvedUrl);
        let prepared: PreparedLodManifest | null = null;
        let asset: pc.Asset | null = null;
        let loadUrl = resolvedUrl;
        inFlightPreloadUrls.add(resolvedUrl);
        try {
            let correction: LodCoordinateCorrection | null = null;
            if (isLod) {
                prepared = await prepareLodManifestUrl(resolvedUrl);
                loadUrl = prepared.url;
                correction = prepared.manifestLoaded ? prepared.correction : await readLodCoordinateCorrection(loadUrl);
                inFlightPreloadUrls.add(loadUrl);
            }
            if (isDestroyed) throw new Error('Viewer destroyed');
            const loadingAsset = new pc.Asset('splat-swap-' + Date.now(), 'gsplat', { url: loadUrl });
            asset = loadingAsset;
            await new Promise<void>((resolve, reject) => {
                loadingAsset.ready(() => {
                    if (isDestroyed) reject(new Error('Viewer destroyed'));
                    else resolve();
                });
                loadingAsset.once('error', (err: Error | string) => reject(err instanceof Error ? err : new Error(String(err))));
                if (isLod) {
                    loadGsplatAsset(loadingAsset, loadUrl);
                } else {
                    app.assets.add(loadingAsset);
                    app.assets.load(loadingAsset);
                }
            });
            const entity = new pc.Entity('splat-swap');
            // A swap scan stands in for the main scan: selections of the scan follow it.
            registerSplatObject(entity, MAIN_SPLAT_OBJECT_ID);
            entity.addComponent('gsplat', { asset: loadingAsset, unified: isLod });
            const gs = entity.gsplat;
            if (gs && isLod) {
                gs.lodBaseDistance = resolvedLodBaseDistance;
                gs.lodMultiplier = resolvedLodMultiplier;
                gs.lodRangeMin = resolvedLodRange[0];
                gs.lodRangeMax = resolvedLodRange[1];
            }
            // Same native-frame split as setupSplatEntity: SPZ and LCC2 sources are already Y-up,
            // so they skip the Babylon Y/Z flip that PLY/SOG splats need.
            const nativeFrame = usesNativeSplatFrame(resolvedUrl, correction?.sourceFormat);
            const scale = overrides?.scale || config.scale || { x: 1, y: 1, z: 1 };
            const invertX = config.invertXScale || false;
            const invertY = config.invertYScale || false;
            const finalScale = nativeFrame ? {
                x: invertX ? -scale.x : scale.x,
                y: invertY ? -scale.y : scale.y,
                z: (invertX !== invertY) ? -scale.z : scale.z,
            } : {
                x: invertX ? -scale.x : scale.x,
                y: invertY ? scale.y : -scale.y, // Base Y=-1; invertY cancels it
                z: (invertX !== invertY) ? scale.z : -scale.z
            };
            entity.setLocalScale(finalScale.x, finalScale.y, finalScale.z);
            const pos = overrides?.position || config.position || [0, 0, 0];
            entity.setPosition(pos[0], pos[1], nativeFrame ? pos[2] : -pos[2]);
            const rot = overrides?.rotation || config.rotation || [0, 0, 0];
            applySplatEntityRotation(entity, [rot[0] || 0, rot[1] || 0, rot[2] || 0], correction);
            swapEntityFrames.set(entity, { nativeFrame, correction });
            entity.enabled = false;
            sceneContent.addChild(entity);
            // Relighting lets lights affect this splat and lets the wipe transition combine shaders.
            addRelightingToEntity(entity);
            return entity;
        } catch (error) {
            if (asset) unloadSplatAssetIfUnreferenced(asset);
            throw error;
        } finally {
            inFlightPreloadUrls.delete(resolvedUrl);
            inFlightPreloadUrls.delete(loadUrl);
            prepared?.cleanup();
        }
    }
    /**
     * Preload a splat in the background without displaying it
     */
    async function preloadSplat(url: string, splatOverrides?: SwapOverrides): Promise<void> {
        if (preloadedSplats.has(url))
            return;
        if (url === currentSplatUrl)
            return;
        if (isDestroyed)
            return;
        console.log('[SplatSwap] Preloading splat:', url);
        try {
            const entity = await loadSwapEntity(url, splatOverrides);
            if (preloadedSplats.has(url)) {
                // Another load of the same splat finished first; keep that one.
                const duplicateAsset = resolveGsplatAsset(entity);
                destroyEntitySafely(entity);
                unloadSplatAssetIfUnreferenced(duplicateAsset);
                return;
            }
            preloadedSplats.set(url, entity);
            console.log('[SplatSwap] Preload complete:', url);
        }
        catch (error) {
            console.error('[SplatSwap] Error preloading:', url, error);
        }
    }
    /**
     * Apply the appropriate explore sub-mode for a splat
     * Only applies if currently in explore mode
     */
    function applyExploreModeForSplat(splatExploreMode?: 'orbit' | 'fly', isOriginal: boolean = false): void {
        if (currentCameraMode !== 'explore')
            return;
        // A video capture owns the camera; the visitor's controls stay as they were.
        if (captureSwaps)
            return;
        // Determine the target mode: use splat-specific mode, or initial mode for original.
        // Coerce to a CameraControls sub-mode — splat swaps don't switch into walk.
        const targetMode = toCameraControlsSubMode(getEnabledExploreSubMode(isOriginal
            ? initialSplatExploreMode
            : (splatExploreMode || initialSplatExploreMode)));
        if (targetMode && cameraControls) {
            const currentMode = cameraControls.mode;
            if (currentMode !== targetMode) {
                cameraControls.setMode(targetMode);
                console.log(`[SplatSwap] Switching explore sub-mode to: ${targetMode}`);
                // Update UI
                updateExploreBtnState(targetMode as 'orbit' | 'fly');
                if (isMobile) {
                    setJoystickVisible(uiElements, targetMode === 'fly');
                    cameraControls.mobileInputLayout = targetMode === 'fly' ? 'touch-touch' : 'joystick-touch';
                } else {
                    setWasdHintVisible(uiElements, targetMode === 'fly');
                    setOrbitHintVisible(uiElements, targetMode === 'orbit', orbitRefocusLocked());
                }
                setDoubleTapHintVisible(uiElements, false);
            }
        }
    }
    /**
     * Preload the next splat in the sequence
     */
    async function preloadNextSplat(currentIndex: number): Promise<void> {
        if (!additionalSplats || additionalSplats.length === 0)
            return;
        const nextIndex = (currentIndex + 1) % additionalSplats.length;
        const nextSplat = additionalSplats[nextIndex];
        if (nextSplat && nextSplat.url) {
            await preloadSplat(nextSplat.url, nextSplat);
        }
    }
    /**
     * Load a splat (switch to it, handling show/hide or dispose)
     */
    async function loadSwapSplat(url: string, forward: boolean = true, splatOverrides?: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: { x: number; y: number; z: number } }): Promise<void> {
        if (url === currentSplatUrl)
            return;
        if (isLoadingSplat)
            return;
        if (isDestroyed)
            return;
        let loadSucceeded = false;
        isLoadingSplat = true;
        const generation = swapShowGeneration;
        console.log(`[SplatSwap] Switching to splat (${forward ? 'fwd' : 'bwd'}):`, url);
        try {
            // Capture old entity for wipe transition BEFORE changing state
            const oldEntity = getCurrentVisibleEntity();

            // Show new splat (preloaded or load fresh)
            if (preloadedSplats.has(url)) {
                showSplat(url);
                events.emit('splatChange', { url, isOriginal: false });

                // Apply swap transition
                const newEntity = preloadedSplats.get(url)!;
                if (oldEntity && oldEntity !== newEntity) {
                    applySwapTransition(oldEntity, newEntity, forward);
                } else {
                    // No old entity visible, just reveal the new one
                    applyRevealTransition(newEntity, forward);
                }
            }
            else {
                // Load the new splat (loadSwapEntity resolves the device / LOD URL).
                let entity = await loadSwapEntity(url, splatOverrides);
                if (isDestroyed) throw new Error('Viewer destroyed');
                const existing = preloadedSplats.get(url);
                if (existing && existing !== entity) {
                    // A preload of the same splat (e.g. a menu choice) finished first; keep that one.
                    const duplicateAsset = resolveGsplatAsset(entity);
                    destroyEntitySafely(entity);
                    unloadSplatAssetIfUnreferenced(duplicateAsset);
                    entity = existing;
                }
                preloadedSplats.set(url, entity);
                const superseded = generation !== swapShowGeneration;
                if (!superseded) currentSplatUrl = url;

                // Check if current progress still warrants showing this swap splat.
                // If the user navigated backward while loading, skip the forward
                // transition - the finally block's updateSplats() will revert.
                // Area swaps hold for as long as the camera is inside, whatever the tour position.
                const percentageAtLoad = swapTourProgress() * 100;
                const waypointIndexAtLoad = getWaypointIndexForProgress(swapTourProgress());
                const swapPoint = additionalSplats.find(s => s.url === url);
                const stillAtSwapPoint = swapPoint && !swapPoint.proximityArea
                    ? (swapPoint.waypointIndex !== -1
                        ? waypointIndexAtLoad >= swapPoint.waypointIndex
                        : swapPoint.percentage !== -1
                            ? percentageAtLoad >= swapPoint.percentage
                            : true)
                    : true;

                if (superseded) {
                    // A splat was picked from the menu while this one loaded: it stays hidden.
                    console.log('[SplatSwap] Load finished after a menu choice - keeping it hidden:', url);
                } else if (stillAtSwapPoint) {
                    entity.enabled = true;
                    // Apply swap transition: hide old + reveal new
                    if (oldEntity) {
                        applySwapTransition(oldEntity, entity, forward);
                    } else {
                        applyRevealTransition(entity, forward);
                    }
                    events.emit('splatChange', { url, isOriginal: false });
                    console.log('[SplatSwap] New splat loaded and shown:', url);
                } else {
                    // User moved behind the swap point before load completed.
                    // Keep entity hidden; updateSplats() in finally will revert.
                    console.log('[SplatSwap] Load completed but user already past swap point - skipping forward transition:', url);
                }
            }
            // Background preload next splat
            const swapIndex = additionalSplats.findIndex(s => s.url === url);
            if (swapIndex !== -1) {
                preloadNextSplat(swapIndex);
            }
            failedSwapTargetUrl = null;
            loadSucceeded = true;
        }
        catch (error) {
            failedSwapTargetUrl = url;
            // A proximity area does not retry it until the camera enters the area again.
            activeSwapAreas().fail(url);
            console.error('[SplatSwap] Error switching splat:', error);
        }
        finally {
            isLoadingSplat = false;
            initialSplatLoadDone = true;
            // Reset the progress-change guard so updateSplats re-evaluates after async load.
            // Bug: if the user navigated backward while the swap was loading, the guard
            // could be frozen at the pre-swap position (e.g. progress=0), preventing the
            // swap-back from ever being detected. We also call updateSplats() directly
            // because the inertia system skips calling it when the user is at rest.
            lastSplatCheckProgress = -1;
            lastSplatCheckWaypointIndex = -1;
            if (loadSucceeded) {
                updateSplats();
            }
            // An area entered or left while this splat was loading is applied now.
            reconcileAreaSwap();
            if (!isLoadingSplat && swapLoadIdleWaiters.length) {
                const waiters = swapLoadIdleWaiters;
                swapLoadIdleWaiters = [];
                waiters.forEach((resolve) => resolve());
            }
        }
    }
    /**
     * Get the primary splat URL (first URL tried in loadSplat)
     */
    function getPrimarySplatUrl(): string {
        if (primarySplatUrlOverride)
            return primarySplatUrlOverride;
        const onMobile = usesMobileRenderProfile() || config.forceMobilePreview === true;
        // Same device-select as loadSplat — desktop branch leaves compressedPlyUrl
        // undefined (only the mobile upload path hosts a separate compressed PLY).
        const { lodMetaUrl, sogUrl, compressedPlyUrl, splatUrl } = selectAssetUrls({
            sogUrl: config.sogUrl,
            compressedPlyUrl: undefined,
            splatUrl: config.splatUrl,
            lodMetaUrl: config.lodMetaUrl,
            mobileSogUrl: config.mobileSogUrl,
            mobileCompressedPlyUrl: config.mobileCompressedPlyUrl,
            mobileSplatUrl: config.mobileSplatUrl,
            mobileLodMetaUrl: config.mobileLodMetaUrl,
        }, onMobile);
        if (lodMetaUrl)
            return lodMetaUrl;
        if (sogUrl)
            return sogUrl;
        if (compressedPlyUrl)
            return compressedPlyUrl;
        if (splatUrl)
            return splatUrl;
        if (config.fallbackUrls && config.fallbackUrls.length > 0)
            return config.fallbackUrls[0];
        return '';
    }
    /**
     * The chain splat the tour position calls for: the swap whose waypoint / percentage trigger
     * was passed last (percentage triggers win over waypoint ones), the primary before the
     * first one or at a "return to original" marker. Swaps with a proximity area are left out:
     * the camera's position drives them, not the tour.
     */
    function chainSwapTarget(percentage: number, waypointIndex: number): { bestSplat: SplatSwapPoint | null; isReturnToOriginal: boolean; targetUrl: string } {
        // Use separate tracking for waypoint and percentage triggers to avoid
        // comparing different value ranges (waypoint 0-N vs percentage 0-100)
        let bestWaypointSplat: SplatSwapPoint | null = null;
        let bestPercentageSplat: SplatSwapPoint | null = null;
        let bestWaypointTrigger = -Infinity;
        let bestPercentageTrigger = -Infinity;
        for (const splat of additionalSplats) {
            if (!splat || splat.proximityArea) continue;
            if (splat.waypointIndex !== -1) {
                // Trigger by waypoint index
                if (waypointIndex >= splat.waypointIndex && splat.waypointIndex > bestWaypointTrigger) {
                    bestWaypointTrigger = splat.waypointIndex;
                    bestWaypointSplat = splat;
                }
            }
            else if (splat.percentage !== -1) {
                // Trigger by percentage
                if (percentage >= splat.percentage && splat.percentage > bestPercentageTrigger) {
                    bestPercentageTrigger = splat.percentage;
                    bestPercentageSplat = splat;
                }
            }
        }
        // Prefer percentage-based triggers over waypoint-based (percentage is more precise)
        const bestSplat = bestPercentageSplat || bestWaypointSplat;
        // Check if best splat is a "return to original" marker
        const isReturnToOriginal = !!bestSplat && bestSplat.url === ORIGINAL_SPLAT_URL;
        const primaryUrl = getPrimarySplatUrl();
        const targetUrl = bestSplat
            ? (isReturnToOriginal ? primaryUrl : bestSplat.url)
            : primaryUrl;
        return { bestSplat, isReturnToOriginal, targetUrl };
    }
    /**
     * Update splats based on current progress (called from navigation loop)
     */
    function updateSplats(): void {
        // Compare mode handles both splats simultaneously — skip chain swap logic
        if (isCompareModeEnabled) return;
        // Menu mode: splats only swap from scene-menu items (goToSplat). There are no
        // waypoint/percentage triggers, so skip all progress-based auto-swapping.
        if (config.splatSwapMode === 'menu') return;
        // A proximity area holds its splat until the camera leaves it (reconcileAreaSwap).
        if (activeSwapAreas().isActive) return;
        if (!additionalSplats || additionalSplats.length === 0)
            return;
        const percentage = swapTourProgress() * 100;
        const waypointIndex = getWaypointIndexForProgress(swapTourProgress());
        // Skip if no meaningful change
        if (Math.abs(percentage - lastSplatCheckProgress) < 0.1 &&
            waypointIndex === lastSplatCheckWaypointIndex) {
            return;
        }
        lastSplatCheckProgress = percentage;
        lastSplatCheckWaypointIndex = waypointIndex;
        const chain = chainSwapTarget(percentage, waypointIndex);
        let bestSplat = chain.bestSplat;
        let isReturnToOriginal = chain.isReturnToOriginal;
        let targetUrl = chain.targetUrl;
        const primaryUrl = getPrimarySplatUrl();
        if (keptAreaHold) {
            // A keep-on-exit area's splat stays until the tour calls for a different chain splat.
            const held = keptAreaHold.chainTarget === targetUrl
                ? additionalSplats.find((s) => s.url === keptAreaHold!.url)
                : undefined;
            if (held) {
                bestSplat = held;
                isReturnToOriginal = false;
                targetUrl = held.url;
            } else {
                keptAreaHold = null;
            }
        }
        if (failedSwapTargetUrl && targetUrl !== failedSwapTargetUrl) {
            failedSwapTargetUrl = null;
        }
        if (targetUrl && targetUrl === failedSwapTargetUrl && targetUrl !== currentSplatUrl) {
            // Avoid hot-looping failed network requests while progress still points at the same swap target.
            return;
        }
        // Switch splat if needed
        if (targetUrl && targetUrl !== currentSplatUrl) {
            // Determine direction: forward if progress increased since last swap
            const forward = percentage >= lastSwapProgress;
            lastSwapProgress = percentage;

            if (targetUrl === primaryUrl && splatEntity && !currentSplatUrl) {
                // First load - just mark current URL
                currentSplatUrl = primaryUrl;
            }
            else if (targetUrl === primaryUrl && splatEntity) {
                // Return to primary splat with wipe transition
                const oldEntity = getCurrentVisibleEntity();
                // Show primary splat
                splatEntity.enabled = true;
                currentSplatUrl = primaryUrl;

                if (oldEntity && oldEntity !== splatEntity) {
                    // Transition: hide swap splat + reveal primary
                    applySwapTransition(oldEntity, splatEntity, forward);
                    // Hide/dispose other swap splats that aren't the transitioning one
                    preloadedSplats.forEach((entity, url) => {
                        if (url !== primaryUrl && entity !== oldEntity) {
                            if (keepSwapMeshes()) {
                                hideSplat(entity);
                            } else {
                                disposeSplat(url);
                            }
                        }
                    });
                } else {
                    // No visible swap entity, just reveal primary
                    applyRevealTransition(splatEntity, forward);
                    preloadedSplats.forEach((entity, url) => {
                        if (url !== primaryUrl) {
                            if (keepSwapMeshes()) hideSplat(entity);
                            else disposeSplat(url);
                        }
                    });
                }

                events.emit('splatChange', { url: primaryUrl, isOriginal: true });
                console.log('[SplatSwap] Returned to primary splat');
                applyExploreModeForSplat(undefined, true);
            }
            else {
                // Switch to a different splat — pass per-splat overrides if present
                const overrides = bestSplat && (bestSplat.position || bestSplat.rotation || bestSplat.scale)
                    ? { position: bestSplat.position, rotation: bestSplat.rotation, scale: bestSplat.scale }
                    : undefined;
                loadSwapSplat(targetUrl, forward, overrides);
                // Apply the splat's explore mode if specified
                if (bestSplat) {
                    applyExploreModeForSplat(bestSplat.defaultExploreMode, false);
                }
            }
            // Apply per-splat skybox if specified, or restore original on return
            if (bestSplat && bestSplat.skyboxUrl && !isReturnToOriginal) {
                applySkybox(bestSplat.skyboxUrl, bestSplat.skyboxRotation || 0);
            } else if (isReturnToOriginal && originalSkyboxUrl) {
                // Restore the original scene skybox
                applySkybox(originalSkyboxUrl, originalSkyboxRotation);
            } else if (!bestSplat && originalSkyboxUrl && !panoramaModeEnabled && currentSkyboxUrl() !== originalSkyboxUrl) {
                // Back before the first swap point: the primary shows with the scene's own skybox.
                // (Not while a panorama portal shows its panorama as the skybox: leaving the
                // panorama puts the scene's skybox back itself.)
                applySkybox(originalSkyboxUrl, originalSkyboxRotation);
            }
        }
    }

    // =========================================================
    // COMPARE MODE
    // Interactive before/after slider for two simultaneous splats
    // =========================================================

    // The per-entity `gsplatCompareClip` script is no longer used here. It clipped
    // by writing `gsplatModifyVS` onto the entity's material, which for a unified
    // splat is the SCENE-WIDE template — so it clipped every unified splat in the
    // scene, not just the compared one, and only worked at all because the after
    // splat was forced non-unified (costing it its LODs). SplitViewCompare clips
    // the per-layer materials instead. The script class stays exported from
    // `effects/` for external consumers.

    /** Update the divider position across the split view and the slider. */
    function setComparePositionInternal(pos: number): void {
        comparePosition = Math.max(0, Math.min(1, pos));
        compareSplitView?.setPosition(comparePosition);
        events.emit('comparePositionChange', { position: Math.round(comparePosition * 100) });
    }

    /**
     * Resolve the after splat's URL for COMPARE mode, LOD manifest first: both sides render
     * unified on their own layer, so the after splat streams its LODs like the primary. Chain
     * and menu swaps resolve the same way.
     */
    function resolveCompareAfterUrl(swap: SplatSwapPoint): string {
        return resolveSwapSplatUrl(swap);
    }

    /**
     * Load the compare "after" splat as a UNIFIED entity with its own LOD stream.
     *
     * Mirrors the primary splat's load path (manifest prep, coordinate correction,
     * octree parser hand-drive, per-component LOD params) but applies the swap's
     * own transform overrides. The resulting entity keeps `unified: true`, which
     * is what lets `SplitViewCompare` give it its own GSplatManager — and with it
     * its own material, sorter, and LOD budget.
     */
    type LoadedCompareAfterSplat = {
        entity: pc.Entity;
        asset: pc.Asset;
        correction: LodCoordinateCorrection | null;
        resolvedUrl: string;
    };

    const releaseCompareEntity = (entity: pc.Entity): void => {
        const script = entity.script as (pc.ScriptComponent & {
            gsplatRelighting?: RelightingEffectScript;
        }) | undefined;
        const relighting = script?.gsplatRelighting;
        if (relighting) allRelightingScripts.delete(relighting);
        exactSplatRaycaster.removeSource(entity);
        destroyEntitySafely(entity);
    };

    const releaseCompareAsset = (asset: pc.Asset): void => {
        if (releasedCompareAssets.has(asset)) return;
        releasedCompareAssets.add(asset);
        compareLoadingAssets.delete(asset);
        unloadSplatAssetIfUnreferenced(asset);
    };

    const releaseLoadedCompareSplat = (loaded: LoadedCompareAfterSplat): void => {
        releaseCompareEntity(loaded.entity);
        releaseCompareAsset(loaded.asset);
    };

    const cancelInFlightCompareLoads = (): void => {
        // Abort every generation first. This rejects both fetch() and any pending
        // Asset-ready promise; then drain the assets that already exist. A loader
        // callback racing cancellation repeats guarded cleanup after late resource
        // assignment, while same-URL generations still protect shared resources.
        for (const cancel of [...compareGenerationCancels]) cancel();
        for (const asset of [...compareLoadingAssets]) releaseCompareAsset(asset);
    };

    async function loadCompareAfterSplat(
        swap: SplatSwapPoint,
        isCancelled: () => boolean,
    ): Promise<LoadedCompareAfterSplat | null> {
        const url = resolveCompareAfterUrl(swap);
        const isLodFormat = isLodStreamingFormat(url);
        console.log('[CompareMode] Loading after splat:', url, isLodFormat ? '(LOD streaming)' : '(single-detail)');

        const abortController = new AbortController();
        const cancelledError = (): Error => new Error('Compare splat load cancelled');
        let rejectAssetLoad: ((reason: Error) => void) | null = null;
        const cancelLoad = (): void => {
            abortController.abort();
            rejectAssetLoad?.(cancelledError());
        };
        // Register before the first async operation: manifest preparation and the
        // correction fallback are part of this generation's cancellable lifetime.
        compareGenerationCancels.add(cancelLoad);
        const generationCancelled = (): boolean => abortController.signal.aborted || isCancelled();
        let preparedLod: PreparedLodManifest = {
            url,
            cleanup: () => undefined,
            correction: null,
            manifestLoaded: false,
        };
        let asset: pc.Asset | null = null;
        let entity: pc.Entity | null = null;
        try {
            if (generationCancelled()) {
                cancelLoad();
                return null;
            }
            if (isLodFormat) {
                preparedLod = await prepareLodManifestUrl(url, abortController.signal);
            }
            if (generationCancelled()) {
                cancelLoad();
                return null;
            }
            const loadUrl = preparedLod.url;
            const coordinateCorrection: LodCoordinateCorrection | null = isLodFormat
                ? (preparedLod.manifestLoaded
                    ? preparedLod.correction
                    : await readLodCoordinateCorrection(loadUrl, abortController.signal))
                : null;
            if (generationCancelled()) {
                cancelLoad();
                return null;
            }

            const loadingAsset = new pc.Asset('compare-after-' + Date.now(), 'gsplat', { url: loadUrl });
            asset = loadingAsset;
            compareLoadingAssets.add(loadingAsset);
            try {
                await new Promise<void>((resolve, reject) => {
                    let settled = false;
                    const settleResolve = (): void => {
                        if (settled) return;
                        settled = true;
                        resolve();
                    };
                    const settleReject = (reason: Error): void => {
                        if (settled) return;
                        settled = true;
                        reject(reason);
                    };
                    rejectAssetLoad = settleReject;
                    loadingAsset.ready(() => {
                        if (generationCancelled()) {
                            // Cancellation may have unloaded the Asset before the
                            // loader assigned its resource. Re-run the guarded
                            // same-URL cleanup: a newer active generation may now
                            // share this loader-cache resource.
                            unloadSplatAssetIfUnreferenced(loadingAsset);
                            settleReject(cancelledError());
                            return;
                        }
                        settleResolve();
                    });
                    loadingAsset.once('error', (err: Error | string) => {
                        settleReject(err instanceof Error ? err : new Error(String(err)));
                    });
                    loadGsplatAsset(loadingAsset, loadUrl);
                });
            } finally {
                rejectAssetLoad = null;
            }
            if (generationCancelled()) throw cancelledError();

            entity = new pc.Entity('splat-compare-after');
            registerSplatObject(entity, MAIN_SPLAT_OBJECT_ID);
            entity.addComponent('gsplat', { asset, unified: true });
            const gs = entity.gsplat as unknown as GSplatComponentWithRuntime | undefined;
            if (gs && isLodFormat) {
                gs.lodBaseDistance = resolvedLodBaseDistance;
                gs.lodMultiplier = resolvedLodMultiplier;
                gs.lodRangeMin = resolvedLodRange[0];
                gs.lodRangeMax = resolvedLodRange[1];
            }

            // Transform: same native-frame-vs-legacy split as the primary
            // (setupSplatEntity), but sourced from this swap's overrides so the after
            // splat can be nudged into alignment with the before splat.
            const isNativeFrameSource = usesNativeSplatFrame(url, coordinateCorrection?.sourceFormat);
            const scale = swap.scale || config.scale || { x: 1, y: 1, z: 1 };
            const invertX = config.invertXScale || false;
            const invertY = config.invertYScale || false;
            const finalScale = isNativeFrameSource ? {
                x: invertX ? -scale.x : scale.x,
                y: invertY ? -scale.y : scale.y,
                z: (invertX !== invertY) ? -scale.z : scale.z,
            } : {
                x: invertX ? -scale.x : scale.x,
                y: invertY ? scale.y : -scale.y,
                z: (invertX !== invertY) ? scale.z : -scale.z,
            };
            entity.setLocalScale(finalScale.x, finalScale.y, finalScale.z);
            const pos = swap.position || config.position || [0, 0, 0];
            entity.setPosition(pos[0], pos[1], isNativeFrameSource ? pos[2] : -pos[2]);
            const rawRot = swap.rotation || config.rotation || [0, 0, 0];
            applySplatEntityRotation(
                entity,
                [rawRot[0] || 0, rawRot[1] || 0, rawRot[2] || 0],
                coordinateCorrection,
            );
            sceneContent.addChild(entity);
            addRelightingToEntity(entity);
            return { entity, asset, correction: coordinateCorrection, resolvedUrl: url };
        } catch (error) {
            if (!generationCancelled()) console.error('[CompareMode] After splat failed to load:', url, error);
            if (entity) releaseCompareEntity(entity);
            if (asset) releaseCompareAsset(asset);
            return null;
        } finally {
            compareGenerationCancels.delete(cancelLoad);
            preparedLod.cleanup();
        }
    }

    /**
     * Initialize compare mode: load the after splat and set up the slider overlay
     */
    async function initCompareMode(afterUrl: string, afterOverrides?: Partial<SplatSwapPoint>): Promise<void> {
        // An unpaired re-init still supersedes the previous generation. Abort its
        // manifest/correction fetch or asset wait before creating the new token.
        cancelInFlightCompareLoads();
        const myInitId = ++compareInitId;
        console.log('[CompareMode] Initializing with after splat:', afterUrl);

        // The swap record carries the LOD manifest and the per-splat transform.
        // Prefer the caller's own record: the editor uploads a new after splat
        // mid-session, so `additionalSplats` (captured at construction) can be
        // stale, and a stale record would miss the new lodMetaUrl.
        const swap: SplatSwapPoint = {
            ...(additionalSplats?.find(s => s.url === afterUrl) ?? {}),
            ...(afterOverrides ?? {}),
            url: afterUrl,
        } as SplatSwapPoint;

        const loaded = await loadCompareAfterSplat(
            swap,
            () => myInitId !== compareInitId || isDestroyed,
        );

        // A load generation owns its entity, asset, and coordinate correction
        // until it wins this generation check. A superseded generation must not
        // publish any of them into the active compare globals.
        if (myInitId !== compareInitId || isDestroyed) {
            console.log('[CompareMode] Init cancelled (superseded or destroyed)');
            if (loaded) releaseLoadedCompareSplat(loaded);
            return;
        }

        if (!loaded) {
            console.error('[CompareMode] Failed to load after splat:', afterUrl);
            isCompareModeEnabled = false;
            return;
        }

        const previousEntity = compareAfterEntity;
        const previousAsset = compareAfterAsset;
        const previousSplitView = compareSplitView;
        const afterEntity = loaded.entity;
        afterEntity.enabled = true;

        // Both splats render unified, each on its own layer, so each keeps its own
        // GSplatManager — own material, own sorter, own LOD stream. The clip is
        // applied per layer material by the split view, not per entity, which is
        // why there are no clip scripts on this path.
        // Callers are expected to destroy compare mode first, but an unpaired
        // re-init would otherwise strand a pair of layers on the scene.
        let nextSplitView: SplitViewCompareHandle | null = null;
        try {
            previousSplitView?.destroy();
            compareSplitView = null;
            nextSplitView = createSplitViewCompare({
                app,
                camera,
                position: comparePosition,
                orientation: compareSettings.orientation === 'horizontal' ? 'horizontal' : 'vertical',
            });
            nextSplitView.setSplats(splatEntity, afterEntity);
            // Hand the split the budget the scene is running under (performance mode and dev
            // overrides applied), not the raw preset value. The split halves whatever it gets per
            // layer and restores exactly that value on destroy, so a raw value here doubled the
            // budget for the whole compare session and left it doubled after exit.
            nextSplitView.setSplatBudget(effectiveSplatBudget());
        } catch (error) {
            console.error('[CompareMode] Failed to create split view:', error);
            nextSplitView?.destroy();
            compareSplitView = null;
            // The previous split has already been torn down. Release the new
            // candidate while the previous globals still protect any shared
            // same-URL resource, then clear and release the previous generation.
            releaseLoadedCompareSplat(loaded);
            compareAfterEntity = null;
            compareAfterAsset = null;
            compareAfterCorrection = null;
            compareAfterNativeFrame = false;
            compareAfterPickCacheKey = undefined;
            compareAfterUrl = null;
            if (previousEntity) releaseCompareEntity(previousEntity);
            if (previousAsset) releaseCompareAsset(previousAsset);
            if (compareSlider) {
                compareSlider.destroy();
                compareSlider = null;
            }
            isCompareModeEnabled = false;
            return;
        }

        // Commit the complete winning generation atomically. Commit before
        // releasing the previous asset so same-URL replacement generations keep
        // their shared loader resource alive.
        compareLoadingAssets.delete(loaded.asset);
        compareSplitView = nextSplitView;
        compareAfterEntity = loaded.entity;
        compareAfterAsset = loaded.asset;
        compareAfterCorrection = loaded.correction;
        compareAfterNativeFrame = usesNativeSplatFrame(loaded.resolvedUrl, loaded.correction?.sourceFormat);
        compareAfterPickCacheKey = loaded.resolvedUrl;
        compareAfterUrl = afterUrl;

        if (previousEntity && previousEntity !== loaded.entity) releaseCompareEntity(previousEntity);
        if (previousAsset && previousAsset !== loaded.asset) releaseCompareAsset(previousAsset);

        // SplatDepthPicker prepares only the World layer. SplitViewCompare moves
        // both splats to custom layers, so keeping that picker active would return
        // an unrelated World-layer hit (or a miss) instead of the visible side.
        if (splatDepthPicker.active) {
            pickDiag.push('compare:depth-picker-ended', { reason: 'split-custom-layers' });
            splatDepthPicker.end();
        }
        primeExactSplatPickEntity(afterEntity, loaded.resolvedUrl);

        if (!splatEntity) {
            console.warn('[CompareMode] Primary splat not ready yet — will attach before-side when available');
            // The primary loads async; hand it to the split view once it exists.
            const pollInterval = setInterval(() => {
                if (isDestroyed || myInitId !== compareInitId || compareSplitView !== nextSplitView) {
                    clearInterval(pollInterval);
                    clearTimeout(pollTimeout);
                    return;
                }
                // `nextSplitView` is a captured `let`; older TypeScript drops its narrowing inside
                // this callback, so guard explicitly (the identity check above already implies it).
                if (splatEntity && nextSplitView) {
                    nextSplitView.setSplats(splatEntity, afterEntity);
                    console.log('[CompareMode] Attached before-side to primary splat (deferred)');
                    clearInterval(pollInterval);
                    clearTimeout(pollTimeout);
                }
            }, 100);
            const pollTimeout = setTimeout(() => clearInterval(pollInterval), 10000);
        }

        // Safety: destroy any existing slider before creating a new one
        // (guards against double-init from React strict mode or rapid re-calls)
        if (compareSlider) {
            compareSlider.destroy();
            compareSlider = null;
        }
        // Belt-and-suspenders: remove any orphaned overlay DOM elements
        // (can happen if a previous viewer was destroyed without proper cleanup)
        container.querySelectorAll('.ss-compare-overlay').forEach(el => el.remove());

        // Create the slider overlay UI
        compareSlider = new CompareSlider({
            container,
            config: compareSettings,
            onPositionChange: (pos) => {
                setComparePositionInternal(pos);
            },
        });

        console.log('[CompareMode] Initialized successfully');
    }

    /**
     * Tear down compare mode: remove clip shaders, hide after splat, destroy slider
     */
    function destroyCompareMode(): void {
        compareInitId++; // Cancel any in-flight initCompareMode
        cancelInFlightCompareLoads();
        // Restore the primary splat's layer, the splat budget, and the camera's
        // layer list BEFORE the after entity goes away, so the split view's
        // teardown sees the state it set up.
        if (compareSplitView) {
            compareSplitView.destroy();
            compareSplitView = null;
        }
        const entity = compareAfterEntity;
        const asset = compareAfterAsset;
        compareAfterEntity = null;
        compareAfterAsset = null;
        compareAfterCorrection = null;
        compareAfterNativeFrame = false;
        compareAfterPickCacheKey = undefined;
        compareAfterUrl = null;

        // Entity and asset ownership are independent: a failed/partial generation
        // can have one without the other, and both must be released. Do not touch
        // preloadedSplats here — a same-URL preload is a separate entity/asset.
        if (entity) releaseCompareEntity(entity);
        if (asset) releaseCompareAsset(asset);
        if (compareSlider) {
            compareSlider.destroy();
            compareSlider = null;
        }
    }

    /** Every file of the main splat (desktop and mobile sets, fallbacks): see isMainSplatTarget. */
    function mainSplatFiles(): Array<string | null | undefined> {
        return [
            config.splatUrl, config.sogUrl, config.lodMetaUrl,
            config.mobileSplatUrl, config.mobileSogUrl, config.mobileCompressedPlyUrl, config.mobileLodMetaUrl,
            ...(config.fallbackUrls ?? []),
        ];
    }
    /**
     * Show a splat picked by hand (scene menu, swap on click, goToSplat): one of the swaps, any
     * other splat file (placed like the main splat), or the main splat (its marker or any of its files).
     */
    async function goToSplat(url: string): Promise<void> {
        const primaryUrl = getPrimarySplatUrl();
        // The main splat: a menu item's "Original / primary splat" names the uploaded file, which
        // is not the file on screen when the scene has a SOG or LOD manifest.
        if (isMainSplatTarget(url, primaryUrl, mainSplatFiles(), additionalSplats)) {
            goToOriginalSplat();
            return;
        }
        // An explicit choice: the proximity areas let go, and an area swap load in flight
        // will not show a second splat (again after the preload, for one that started meanwhile).
        const choice = ++menuChoiceGeneration;
        chooseSplatByHand(url);
        // Check if it's in additionalSplats
        const splatConfig = additionalSplats.find(s => s.url === url);
        if (!splatConfig && !preloadedSplats.has(url)) {
            // Need to preload it first
            await preloadSplat(url);
        } else if (splatConfig && !preloadedSplats.has(url)) {
            await preloadSplat(url, splatConfig);
        }
        // A later menu choice made while this one was loading wins.
        if (choice !== menuChoiceGeneration || isDestroyed) return;
        chooseSplatByHand(url);
        // Hide current splat
        const currentUrl = currentSplatUrl || primaryUrl;
        if (currentUrl === primaryUrl && splatEntity) {
            hideSplat(splatEntity);
        }
        else if (currentUrl) {
            const currentEntity = preloadedSplats.get(currentUrl);
            if (currentEntity)
                currentEntity.enabled = false;
        }
        // Show the requested splat
        if (!showSplat(url)) {
            throw new Error(`[StorySplat Viewer] Failed to switch to splat: ${url}`);
        }
        // Apply explore mode if applicable
        if (splatConfig) {
            applyExploreModeForSplat(splatConfig.defaultExploreMode, false);
        }
        events.emit('splatChange', { url, isOriginal: false });
    }
    /**
     * Manually switch back to the original/initial splat
     */
    function goToOriginalSplat(): void {
        menuChoiceGeneration++;
        chooseSplatByHand(getPrimarySplatUrl());
        returnToPrimarySplat(true);
    }
    /**
     * A splat picked by hand (scene menu, goToSplat) wins over the proximity areas: it shows
     * now and stays when the camera leaves, and the area the camera is in only swaps again on
     * a fresh entry. A swap load already in flight finishes hidden instead of showing too.
     */
    function chooseSplatByHand(url: string): void {
        swapShowGeneration++;
        keptAreaHold = null;
        activeSwapAreas().choose();
        if (areaSwapSkyboxChanged) restoreSkyboxAfterArea(url);
    }
    /** Show the primary splat again (backward transition); optionally apply its explore mode. */
    function returnToPrimarySplat(applyExploreMode: boolean): void {
        const primaryUrl = getPrimarySplatUrl();
        if (currentSplatUrl === primaryUrl)
            return;
        const oldEntity = getCurrentVisibleEntity();
        // Show original splat
        if (splatEntity) {
            splatEntity.enabled = true;

            if (oldEntity && oldEntity !== splatEntity) {
                // Going back to original = backward direction
                applySwapTransition(oldEntity, splatEntity, false);
                // Hide/dispose other swap splats that aren't transitioning
                preloadedSplats.forEach((entity, url) => {
                    if (url !== primaryUrl && entity !== oldEntity) {
                        if (keepSwapMeshes()) hideSplat(entity);
                        else disposeSplat(url);
                    }
                });
            } else {
                applyRevealTransition(splatEntity, false);
                preloadedSplats.forEach((entity, url) => {
                    if (url !== primaryUrl) {
                        if (keepSwapMeshes()) hideSplat(entity);
                        else disposeSplat(url);
                    }
                });
            }
        }
        currentSplatUrl = primaryUrl;
        lastSwapProgress = 0; // Reset since we're back at original
        events.emit('splatChange', { url: primaryUrl, isOriginal: true });
        console.log('[SplatSwap] Returned to original splat');
        if (applyExploreMode) applyExploreModeForSplat(undefined, true);
    }
    /**
     * Apply what the proximity areas ask for: the most recently entered area's splat, or,
     * once every area is left, the splat from before. Runs again after a swap load finishes,
     * so a change made while a splat was loading is not lost.
     */
    function reconcileAreaSwap(): void {
        if (isDestroyed) return;
        const action = activeSwapAreas().next(currentSplatUrl || getPrimarySplatUrl(), isLoadingSplat);
        if (!action) return;
        if (action.kind === 'show') {
            showAreaSwapSplat(action.url);
            return;
        }
        if (action.kind === 'keep') {
            // The area's splat (and skybox) stay; they are now what a later area returns to.
            areaSwapSkyboxChanged = false;
            if (config.splatSwapMode !== 'menu') {
                // Chain swaps resume once the tour position calls for a different splat than now.
                const percentage = swapTourProgress() * 100;
                keptAreaHold = {
                    url: action.url,
                    chainTarget: chainSwapTarget(percentage, getWaypointIndexForProgress(swapTourProgress())).targetUrl,
                };
                lastSplatCheckProgress = -1;
                lastSplatCheckWaypointIndex = -1;
            }
            return;
        }
        const base = action.baseUrl;
        if (areaSwapSkyboxChanged) restoreSkyboxAfterArea(base);
        if (config.splatSwapMode !== 'menu') {
            // Chain mode: the tour position decides what shows (and applies its own skybox).
            lastSplatCheckProgress = -1;
            lastSplatCheckWaypointIndex = -1;
            updateSplats();
        } else if (base !== currentSplatUrl) {
            if (base === getPrimarySplatUrl()) returnToPrimarySplat(false);
            else showAreaSwapSplat(base, false);
        }
    }
    /** Swap to an additional splat for a proximity area, with the usual transition. */
    function showAreaSwapSplat(url: string, forward: boolean = true): void {
        const swap = additionalSplats.find((s) => s.url === url);
        const overrides = swap && (swap.position || swap.rotation || swap.scale)
            ? { position: swap.position, rotation: swap.rotation, scale: swap.scale }
            : undefined;
        void loadSwapSplat(url, forward, overrides);
        // Only a splat that sets its own camera mode changes it: walking into a room should not
        // flip a visitor between fly and orbit otherwise.
        if (swap?.defaultExploreMode) applyExploreModeForSplat(swap.defaultExploreMode, false);
        if (swap?.skyboxUrl) {
            applySkybox(swap.skyboxUrl, swap.skyboxRotation || 0);
            areaSwapSkyboxChanged = true;
        } else if (areaSwapSkyboxChanged) {
            // Moving on from an area that set its own skybox into one that doesn't.
            restoreSkyboxAfterArea(activeSwapAreas().baseUrl);
        }
    }
    /** Put back the skybox of the splat an area swap started from (or the scene's own). */
    function restoreSkyboxAfterArea(baseUrl: string | null): void {
        areaSwapSkyboxChanged = false;
        const baseSwap = baseUrl ? additionalSplats.find((s) => s.url === baseUrl) : undefined;
        if (baseSwap?.skyboxUrl) applySkybox(baseSwap.skyboxUrl, baseSwap.skyboxRotation || 0);
        else if (originalSkyboxUrl) applySkybox(originalSkyboxUrl, originalSkyboxRotation);
    }
    /**
     * Get the URL of the currently displayed splat
     */
    function getCurrentSplatUrl(): string {
        return currentSplatUrl || getPrimarySplatUrl();
    }
    /**
     * Check if currently showing the original splat
     */
    function isShowingOriginalSplat(): boolean {
        return currentSplatUrl === getPrimarySplatUrl() || currentSplatUrl === null;
    }
    /**
     * Apply a skybox (for per-splat skyboxes)
     * Note: For proper cubemap skyboxes, use 6-face URLs or HDR/EXR environment maps
     * This simplified version loads a single texture and creates a basic skybox
     */
    function applySkybox(url: string, rotation: number = 0): void {
        console.log('[SplatSwap] Applying skybox:', url, 'rotation:', rotation);
        // Use the same native pipeline as initSkybox (equirect → cubemap → envAtlas)
        setSkyboxRuntime(url, rotation);
    }
    /** URL of the skybox the scene shows (or is loading) now. */
    function currentSkyboxUrl(): string | null {
        return config.skybox?.url || config.skyboxUrl || null;
    }
    // =========================================================
    // END SPLATSWAP SYSTEM
    // =========================================================
    // Progress-based navigation (matching HTML export)
    let currentProgress = 0; // 0 to 1
    let targetProgress = 0; // Scroll target for inertia (currentProgress lerps toward this)
    let isAnimatingProgress = false; // Flag to skip inertia during button animations
    let progressAnimationFrameId: number | null = null; // Track animation frame for cancellation
    let pendingWaypointTarget: number | null = null; // Track intended waypoint when spamming next/prev
    const totalDuration = config.waypoints?.reduce((sum, wp) => sum + (wp.duration || 2000), 0) || 1;
    // Convert autoPlaySpeed from HTML format (per-frame path units) to progress per second
    // HTML: scrollTarget += autoPlaySpeed * animationRatio (per frame, path units 0 to pathLength-1)
    // NPM: progress += playbackSpeed * deltaTime (per frame, progress 0 to 1)
    // Normalize loopMode: handle boolean values (true='loop', false='none') and string values
    // Scene data may have boolean loopMode from older versions
    const rawLoopMode = config.loopMode as unknown;
    let loopMode: 'loop' | 'pingpong' | 'none';
    if (rawLoopMode === true) {
        loopMode = 'loop';
    }
    else if (rawLoopMode === false) {
        loopMode = 'none';
    }
    else if (rawLoopMode === 'loop' || rawLoopMode === 'pingpong' || rawLoopMode === 'none') {
        loopMode = rawLoopMode;
    }
    else {
        loopMode = 'loop'; // Default to 'loop' for backward compatibility
    }
    const getTourSegmentCount = (mode: 'loop' | 'pingpong' | 'none' = loopMode, waypointCount = config.waypoints?.length || 1): number => {
        if (waypointCount < 2) return 1;
        return mode === 'loop' ? waypointCount : waypointCount - 1;
    };
    const timelineProgressToPathProgress = (progress: number, mode: 'loop' | 'pingpong' | 'none' = loopMode): number => {
        if (mode === 'loop') return ((progress % 1) + 1) % 1;
        if (mode === 'pingpong') {
            const cycle = ((progress % 1) + 1) % 1;
            return cycle <= 0.5 ? cycle * 2 : (1 - cycle) * 2;
        }
        return Math.max(0, Math.min(1, progress));
    };
    const normalizeProgressForMode = (progress: number, mode: 'loop' | 'pingpong' | 'none' = loopMode): number => {
        return mode === 'loop'
            ? ((progress % 1) + 1) % 1
            : Math.max(0, Math.min(1, progress));
    };
    const getProgressForWaypointIndex = (index: number): number => {
        const waypointCount = config.waypoints?.length || 1;
        if (waypointCount < 2) return 0;
        const clampedIndex = Math.max(0, Math.min(index, waypointCount - 1));
        return clampedIndex / getTourSegmentCount(loopMode, waypointCount);
    };
    const getWaypointIndexForProgress = (progress: number): number => {
        const waypointCount = config.waypoints?.length || 1;
        if (waypointCount < 2) return 0;
        const segmentCount = getTourSegmentCount(loopMode, waypointCount);
        const segmentProgress = normalizeProgressForMode(progress) * segmentCount;
        const roundedIndex = Math.round(segmentProgress);
        return loopMode === 'loop'
            ? ((roundedIndex % waypointCount) + waypointCount) % waypointCount
            : Math.max(0, Math.min(waypointCount - 1, roundedIndex));
    };
    const remapProgressForLoopMode = (
        progress: number,
        fromMode: 'loop' | 'pingpong' | 'none',
        toMode: 'loop' | 'pingpong' | 'none'
    ): number => {
        if (fromMode === toMode) return normalizeProgressForMode(progress, toMode);
        const waypointCount = config.waypoints?.length || 1;
        const fromSegments = getTourSegmentCount(fromMode, waypointCount);
        const toSegments = getTourSegmentCount(toMode, waypointCount);
        const waypointSpaceProgress = normalizeProgressForMode(progress, fromMode) * fromSegments;
        return normalizeProgressForMode(waypointSpaceProgress / toSegments, toMode);
    };
    // pathLength = segment count * 20 subdivisions
    // At 60fps: autoPlaySpeed * 60 path units per second
    // Progress per second = (autoPlaySpeed * 60) / pathLength
    const numWaypoints = config.waypoints?.length || 1;
    const pathLength = Math.max(1, getTourSegmentCount(loopMode, numWaypoints) * 20);
    let playbackSpeed = config.autoplaySpeed !== undefined
        ? (config.autoplaySpeed * 60) / pathLength
        : 1000 / totalDuration;
    let playbackDirection = 1; // 1 = forward, -1 = backward (for pingpong mode)
    console.log('[StorySplat Viewer] Playback config:', {
        loopMode,
        playbackSpeed,
        totalDuration,
        autoPlay: config.autoPlay,
        autoplaySpeed: config.autoplaySpeed,
        rawLoopMode: config.loopMode
    });
    // Damped camera interpolation (matching Babylon's pullStrength behavior)
    let targetCameraPosition: pc.Vec3 | null = null;
    let targetCameraRotation: pc.Quat | null = null;
    // PULL_STRENGTH controls camera smoothness during playback
    // HTML export formula: scrollInterpolationSpeed = 0.01 + transitionSpeed * 0.1
    // With transitionSpeed = 0.5: 0.06 (smoother), transitionSpeed = 1: 0.11, transitionSpeed = 2: 0.21 (snappier)
    let PULL_STRENGTH = 0.01 + (config.transitionSpeed || 1) * 0.1;
    // Elastic user rotation (allows user to look around in tour mode, pulls back to target)
    // Using Euler angles (yaw/pitch) instead of quaternion accumulation to prevent roll drift
    let userYawOffset = 0; // Accumulated yaw offset in degrees
    let userPitchOffset = 0; // Accumulated pitch offset in degrees
    const MAX_PITCH_OFFSET = 60; // Limit pitch to prevent flipping
    let isUserDragging = false;
    let gizmoInputSuppressed = false; // When true, suppress ALL camera input (tour rotation + CameraControls)
    let renderCaptureActive = false; // When true, video-render capture owns the camera pose; freeze autonomous drivers
    // renderCaptureActive is the union of every owner of the camera pose: the editor's
    // video render (setRenderCaptureMode), a public frame capture, and a public pose hold.
    let editorRenderCaptureRequested = false;
    let frameCaptureActive = false;
    let cameraHoldActive = false;
    const syncRenderCaptureActive = () => {
        renderCaptureActive = editorRenderCaptureRequested || frameCaptureActive || cameraHoldActive;
    };
    let lastPointerX = 0;
    let lastPointerY = 0;
    const USER_ROTATION_SENSITIVITY = 0.3; // How responsive rotation is to drag
    const USER_ROTATION_DECAY = 0.95; // How fast rotation returns to target (0.95 = slow, 0.8 = fast)
    // Pre-calculate waypoint positions, rotations, and FOVs for interpolation
    const waypointPositions: pc.Vec3[] = [];
    const waypointRotations: pc.Quat[] = [];
    const waypointFOVs: number[] = [];
    let defaultFOV = config.fov || 60;
    let targetFOV = defaultFOV;
    if (config.waypoints && config.waypoints.length > 0) {
        config.waypoints.forEach(wp => {
            const pos = wp.position
                ? new pc.Vec3(wp.position.x, wp.position.y, -wp.position.z)
                : new pc.Vec3(0, config.playerHeight || 1.6, 0);
            waypointPositions.push(pos);
            const rot = wp.rotation
                ? convertWaypointRotation(wp.rotation)
                : new pc.Quat();
            waypointRotations.push(rot);
            // Extract FOV from waypoint (convert from radians if needed)
            let fov = wp.fov || defaultFOV;
            if (fov < 3.5) {
                // FOV is in radians, convert to degrees
                fov = fov * (180 / Math.PI);
            }
            waypointFOVs.push(fov);
        });
        // Initialize target position/rotation/FOV immediately for damping to work on load
        if (waypointPositions.length > 0) {
            targetCameraPosition = waypointPositions[0].clone();
            targetCameraRotation = waypointRotations[0].clone();
            targetFOV = waypointFOVs[0];
        }
    }
    // Tour path shape: 'smooth' spline (default) or 'linear' straight segments.
    // Editor-mutable via setPathInterpolation below.
    let pathInterpolationMode: 'smooth' | 'linear' = config.pathInterpolation === 'linear' ? 'linear' : 'smooth';
    // Update camera based on progress (sets targets for damped interpolation)
    // Project a world-space point onto the tour waypoint path and return the
    // nearest global progress (0..1) plus distance. Used to resume tour mode
    // from whatever position the camera is in — after a skin overlay, editor
    // camera restore, etc. — rather than snapping back to the previously
    // paused progress.
    function findNearestPathProgress(p: pc.Vec3): { progress: number; distance: number } | null {
        if (waypointPositions.length < 2) return null;
        const pathNodes = buildWaypointPathNodes(waypointPositions, config.waypoints);
        const numSegments = getTourSegmentCount(loopMode, waypointPositions.length);
        const samples = generateWaypointPathSamples(pathNodes, 20, loopMode, pathInterpolationMode);
        if (samples.length < 2) return null;
        let bestDist = Infinity;
        let bestProgress = 0;
        for (let i = 0; i < samples.length - 1; i++) {
            const a = samples[i];
            const b = samples[i + 1];
            const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
            const len2 = abx * abx + aby * aby + abz * abz;
            if (len2 === 0) continue;
            const apx = p.x - a.x, apy = p.y - a.y, apz = p.z - a.z;
            let t = (apx * abx + apy * aby + apz * abz) / len2;
            if (t < 0) t = 0; else if (t > 1) t = 1;
            const dx = p.x - (a.x + abx * t);
            const dy = p.y - (a.y + aby * t);
            const dz = p.z - (a.z + abz * t);
            const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (d < bestDist) {
                bestDist = d;
                const aProgress = (a.segmentIndex + a.t) / numSegments;
                const bProgress = (b.segmentIndex + b.t) / numSegments;
                bestProgress = aProgress + (bProgress - aProgress) * t;
            }
        }
        return bestDist === Infinity ? null : { progress: bestProgress, distance: bestDist };
    }

    // Switch to tour mode, seeding progress from the nearest point on the path
    // to the camera's current pose. Produces a single clean jump to tour[nearest]
    // with no animation from the previously paused progress.
    function resumeTourAtCurrentCamera(): void {
        const curPos = camera.getPosition().clone();
        const nearest = findNearestPathProgress(curPos);
        if (nearest) {
            currentProgress = nearest.progress;
            targetProgress = nearest.progress;
        }
        setCameraMode('tour');
    }

    function updateCameraFromProgress(progress: number): void {
        if (!waypointControlEnabled || waypointPositions.length < 2 || isInXR)
            return;
        const numWaypoints = waypointPositions.length;
        const isLooping = loopMode === 'loop';

        // In loop mode, the path is a closed ring with N segments (last→first is the wrap segment).
        // In non-loop mode, the path has N-1 segments (first→last, no wrap).
        const numSegments = isLooping ? numWaypoints : numWaypoints - 1;

        // Allow progress slightly beyond [0,1] for smooth wrap-around, then modulo
        if (isLooping) {
            progress = ((progress % 1) + 1) % 1; // wrap to [0, 1)
        } else {
            progress = Math.max(0, Math.min(1, progress));
        }

        const segmentProgress = progress * numSegments;
        const segmentIndex = Math.min(Math.floor(segmentProgress), numSegments - 1);
        const t = segmentProgress - segmentIndex;
        currentWaypointPosition = segmentProgress;
        currentTourLoops = isLooping;

        const pathNodes = buildWaypointPathNodes(waypointPositions, config.waypoints);
        const segmentIndices = getSegmentIndices(pathNodes, segmentIndex, loopMode);
        const sampledPosition = sampleWaypointPathSegment(pathNodes, segmentIndex, t, loopMode, pathInterpolationMode);
        if (!segmentIndices || !sampledPosition) return;

        const startRot = waypointRotations[segmentIndices.startIndex];
        const endRot = waypointRotations[segmentIndices.endIndex];
        const startFOV = waypointFOVs[segmentIndices.startIndex];
        const endFOV = waypointFOVs[segmentIndices.endIndex];

        targetCameraPosition = new pc.Vec3(sampledPosition.x, sampledPosition.y, sampledPosition.z);
        // Calculate target rotation (for damped interpolation)
        targetCameraRotation = new pc.Quat();
        targetCameraRotation.slerp(startRot, endRot, t);
        // Calculate target FOV (for damped interpolation)
        targetFOV = lerp(startFOV, endFOV, t);
        applyTourProgress(progress);
    }
    /**
     * What tour progress drives besides the camera: the current waypoint (its event, audio and
     * orbit settings, closing popups), progress events, narration and splat swaps. The tour calls it
     * as it moves the camera; VR calls it when the visitor goes to a tour stop (goToVrStop).
     */
    function applyTourProgress(progress: number): void {
        // Update waypoint index and emit event if changed
        const newIndex = getWaypointIndexForProgress(progress);
        if (newIndex !== currentWaypointIndex) {
            const prevIndex = currentWaypointIndex;
            currentWaypointIndex = newIndex;
            // Get current waypoint's camera mode
            const currentWaypoint = config.waypoints![newIndex];
            const waypointCameraMode = currentWaypoint.cameraMode || 'first-person';
            // Update orbit mode state based on per-waypoint camera mode
            if (waypointCameraMode === 'orbit') {
                // Enable orbit-style controls while on tour path
                currentWaypointOrbitEnabled = true;
                // Set orbit target (waypoint position or custom target)
                currentWaypointOrbitTarget = currentWaypoint.orbitTarget
                    ? new pc.Vec3(currentWaypoint.orbitTarget.x, currentWaypoint.orbitTarget.y, -(currentWaypoint.orbitTarget.z || 0) // Negate Z for coordinate conversion
                    )
                    : new pc.Vec3(waypointPositions[newIndex].x, waypointPositions[newIndex].y, waypointPositions[newIndex].z);
            }
            else {
                currentWaypointOrbitEnabled = false;
                currentWaypointOrbitTarget = null;
            }
            events.emit('waypointChange', {
                index: newIndex,
                waypoint: currentWaypoint,
                prevIndex,
                cameraMode: waypointCameraMode
            });
            // Start/stop waypoint audio — handles legacy tracks (no triggerMode) on waypoint entry
            updateWaypointAudio(newIndex, prevIndex);
            // Close any open hotspot popup and stop all hotspot audio on waypoint change
            stopAllHotspotAudio();
            stopHotspotPopupMedia(container);
            const popupEl = container.querySelector('.storysplat-hotspot-popup') as HTMLElement;
            if (popupEl)
                popupEl.classList.remove('visible');
        }
        // Progress also drives visibility and opacity, even between identical camera poses.
        // The settled-camera fast path cannot signal those changes through transform dirtiness.
        renderScheduler.requestFrame('progress-update');
        // Emit progress event for UI (ensure clamped value)
        events.emit('progressUpdate', { progress: Math.max(0, Math.min(1, progress)), index: currentWaypointIndex });
        // Sync narration audio to current progress
        syncNarrationToProgress(Math.max(0, Math.min(1, progress)));
        // Update splat swap system based on progress
        updateSplats();
    }
    // Apply damped camera interpolation (matching Babylon's pullStrength behavior)
    // Only active in tour mode - explore/walk modes control the camera directly
    function updateCameraDamping(): void {
        // Don't interfere with explore/walk mode cameras, an in-progress video-render capture, or
        // a VR/AR session (the headset or phone owns the camera; pulling it toward the tour also
        // skewed splat sorting and LOD, which use the camera entity's position).
        if (!waypointControlEnabled || renderCaptureActive || isInXR)
            return;
        // Interpolate progress toward target (creates scroll inertia - matching BabylonJS HTML export)
        // Skip during button animations to prevent fighting with animateToProgress easing
        if (!isAnimatingProgress) {
            // BabylonJS formula: scrollPosition += (scrollTarget - scrollPosition) * scrollInterpolationSpeed
            let progressDiff = targetProgress - currentProgress;
            // In loop mode, take the shortest path around the wrap boundary
            if (loopMode === 'loop') {
                if (progressDiff > 0.5) progressDiff -= 1;
                else if (progressDiff < -0.5) progressDiff += 1;
            }
            if (Math.abs(progressDiff) > 0.0001) {
                currentProgress += progressDiff * PULL_STRENGTH;
                // Wrap currentProgress for loop mode
                if (loopMode === 'loop') {
                    currentProgress = ((currentProgress % 1) + 1) % 1;
                }
                updateCameraFromProgress(currentProgress);
            }
        }
        if (!targetCameraPosition || !targetCameraRotation)
            return;
        // Decay user rotation offset when not dragging (elastic pull back to target).
        // (Runs before the convergence check so a released drag still eases back.)
        if (!isUserDragging) {
            userYawOffset *= USER_ROTATION_DECAY;
            userPitchOffset *= USER_ROTATION_DECAY;
            // Snap to zero if very small to avoid floating point drift
            if (Math.abs(userYawOffset) < 0.01)
                userYawOffset = 0;
            if (Math.abs(userPitchOffset) < 0.01)
                userPitchOffset = 0;
        }
        const currentPos = camera.getPosition();
        const currentRot = camera.getRotation();
        const cameraComponent = camera.camera;
        // Settled? Skip the per-frame setPosition/setRotation (each dirties the camera transform
        // and runs a parent-matrix invert) once the damping has converged and there is no user
        // offset. Any targetProgress change, drag, or editor target rebuild breaks convergence.
        // With zero user offsets the rotation target IS targetCameraRotation, so compare directly.
        if (!isUserDragging && userYawOffset === 0 && userPitchOffset === 0 &&
            currentPos.distance(targetCameraPosition) < 1e-5 &&
            currentRot.equalsApprox(targetCameraRotation, 1e-5) &&
            (!(cameraComponent && waypointFOVs.length > 0) || Math.abs(cameraComponent.fov - targetFOV) < 1e-4)) {
            return;
        }
        // Lerp position toward target (matching Babylon's 0.1 pullStrength)
        _dampPos.lerp(currentPos, targetCameraPosition, PULL_STRENGTH);
        camera.setPosition(_dampPos.x, _dampPos.y, _dampPos.z);
        // Lerp FOV toward target for smooth transitions between waypoints
        if (cameraComponent && waypointFOVs.length > 0) {
            const currentFOV = cameraComponent.fov;
            const newFOV = lerp(currentFOV, targetFOV, PULL_STRENGTH);
            cameraComponent.fov = newFOV;
        }
        // Waypoint rotation plus the user's look offset: yaw about world up, pitch about the camera's
        // right axis (as in fly/walk), so turning at a waypoint that looks down doesn't bank.
        composeTourLook(targetCameraRotation, userYawOffset, clampTourPitch(targetCameraRotation, userPitchOffset, MAX_PITCH_OFFSET), _dampTargetRot);
        // Slerp rotation toward combined target using the same transition pull as
        // position/FOV so all three stay in lockstep.
        _dampNewRot.slerp(currentRot, _dampTargetRot, PULL_STRENGTH);
        camera.setRotation(_dampNewRot);
    }
    // Scratch for updateCameraDamping (per-frame; hoisted to avoid 4 allocations/frame at rest).
    const _dampPos = new pc.Vec3();
    const _dampTargetRot = new pc.Quat();
    const _dampNewRot = new pc.Quat();
    // Register camera damping in the render loop
    app.on('update', updateCameraDamping);
    // Set progress directly (for button navigation and API calls)
    function setProgress(progress: number, animate = false): void {
        const clampedProgress = Math.max(0, Math.min(1, progress));
        targetProgress = clampedProgress; // Always update target to keep inertia system in sync
        // If narration is playing, pause audio during external scrub and schedule resume
        if (narrationEnabled && isPlaying && narrationAudio) {
            startNarrationScrub();
            scheduleNarrationScrubResume();
        }
        if (animate) {
            animateToProgress(clampedProgress);
        }
        else {
            currentProgress = clampedProgress; // Snap immediately for non-animated calls
            updateCameraFromProgress(currentProgress);
        }
    }
    // Base transition duration (ms) - can be scaled by transitionSpeed setting
    // transitionSpeed multiplies the base duration (0.5 = faster/250ms, 2 = slower/1000ms)
    const baseTransitionDuration = 500;
    let transitionDuration = baseTransitionDuration * (config.transitionSpeed || 1);
    let waypointEasing = normalizeWaypointEasing(config.waypointEasing);
    // Animate to a target progress
    function animateToProgress(animationTarget: number, duration = transitionDuration, onComplete?: () => void): void {
        // Cancel any in-progress animation to prevent multiple loops fighting
        if (progressAnimationFrameId !== null) {
            cancelAnimationFrame(progressAnimationFrameId);
            progressAnimationFrameId = null;
        }
        const startProgress = currentProgress;
        // In loop mode, take the shortest path around the wrap boundary
        // e.g., from 0.05 to 0.9 should go 0.05 → 0.0 → 0.9 (delta -0.15)
        // not 0.05 → 0.9 (delta +0.85 traversing the entire loop)
        let delta = animationTarget - startProgress;
        if (loopMode === 'loop') {
            if (delta > 0.5) delta -= 1;
            else if (delta < -0.5) delta += 1;
        }
        const startTime = performance.now();
        isAnimatingProgress = true; // Prevent inertia from interfering
        const animate = () => {
            const elapsed = performance.now() - startTime;
            const t = Math.min(elapsed / duration, 1);
            const eased = applyWaypointEasing(t, waypointEasing);
            let p = startProgress + delta * eased;
            // Wrap progress for loop mode (handles negative values from shortest-path)
            if (loopMode === 'loop') {
                p = ((p % 1) + 1) % 1;
            }
            currentProgress = p;
            updateCameraFromProgress(currentProgress);
            if (t < 1) {
                progressAnimationFrameId = requestAnimationFrame(animate);
            }
            else {
                progressAnimationFrameId = null;
                isAnimatingProgress = false; // Animation complete, re-enable inertia
                pendingWaypointTarget = null; // Clear pending target
                onComplete?.();
            }
        };
        progressAnimationFrameId = requestAnimationFrame(animate);
    }
    // Navigation functions
    function goToWaypoint(index: number): void {
        if (!config.waypoints || index < 0 || index >= config.waypoints.length)
            return;
        // VR: teleport to the stop (the tour can't drive a headset's camera).
        if (inVrSession()) {
            goToVrStop(index);
            return;
        }
        // Exit staging first — it re-enables the frozen tour driver, so the
        // waypointControlEnabled check below passes and the jump actually runs.
        exitVirtualStaging?.();
        if (!waypointControlEnabled)
            return;
        // Extra views and manual cutaway changes leave the tour index unchanged. Returning to
        // that stop (or selecting it again) won't emit waypointChange, so reapply its action here.
        if (index === currentWaypointIndex) {
            runArrivalCutaway(config.waypoints[index].cutaway);
        }
        // In loop mode: N segments (closed ring), so each waypoint is at index/N
        // In non-loop mode: N-1 segments (open path), so each waypoint is at index/(N-1)
        const waypointProgress = getProgressForWaypointIndex(index);
        targetProgress = waypointProgress; // Update module-level target for inertia system
        animateToProgress(waypointProgress);
    }
    function nextWaypoint(): void {
        if (!config.waypoints || config.waypoints.length === 0)
            return;
        if (inVrSession()) {
            stepVrStop(1);
            return;
        }
        // Covers percentage mode too, which bypasses goToWaypoint and would
        // otherwise animate the camera behind the still-visible staging photo.
        exitVirtualStaging?.();
        // Pause autoplay when user manually navigates
        if (isAutoplayEngaged()) pause();
        // scrollButtonMode: 'waypoint' jumps to next waypoint, 'percentage' scrolls by scrollAmount
        const buttonMode = config.scrollButtonMode || 'waypoint';
        if (buttonMode === 'percentage' || buttonMode === 'continuous' || buttonMode === 'incremental') {
            // Percentage mode: scroll by scrollAmount percentage
            // Use targetProgress (not currentProgress) so rapid clicks accumulate correctly
            const scrollAmountSetting = config.scrollAmount || 10;
            const increment = scrollAmountSetting / 100; // Convert percentage to 0-1
            let newProgress = targetProgress + increment;
            // Handle loop mode for percentage-based navigation
            if (newProgress > 1) {
                if (loopMode === 'loop') {
                    newProgress = 0; // Wrap to beginning
                }
                else {
                    newProgress = 1; // Clamp to end
                }
            }
            targetProgress = newProgress; // Update module-level target for inertia system
            animateToProgress(newProgress);
        }
        else {
            // Waypoint mode: jump to the next waypoint that Prev / Next stop at (skipOnNextPrev
            // waypoints are stepped over; the camera still passes through them), wrapping in a
            // loop tour and staying at the last stop otherwise.
            // Use pendingWaypointTarget if mid-animation so rapid clicks accumulate
            const baseIndex = pendingWaypointTarget !== null ? pendingWaypointTarget : currentWaypointIndex;
            const next = waypointStepTarget(config.waypoints, baseIndex, 1, loopMode === 'loop');
            if (next === null) return;
            pendingWaypointTarget = next;
            goToWaypoint(next);
        }
    }
    function prevWaypoint(): void {
        if (!config.waypoints || config.waypoints.length === 0)
            return;
        if (inVrSession()) {
            stepVrStop(-1);
            return;
        }
        // Covers percentage mode too, which bypasses goToWaypoint and would
        // otherwise animate the camera behind the still-visible staging photo.
        exitVirtualStaging?.();
        // Pause autoplay when user manually navigates
        if (isAutoplayEngaged()) pause();
        // scrollButtonMode: 'waypoint' jumps to prev waypoint, 'percentage' scrolls by scrollAmount
        const buttonMode = config.scrollButtonMode || 'waypoint';
        if (buttonMode === 'percentage' || buttonMode === 'continuous' || buttonMode === 'incremental') {
            // Percentage mode: scroll by scrollAmount percentage
            // Use targetProgress (not currentProgress) so rapid clicks accumulate correctly
            const scrollAmountSetting = config.scrollAmount || 10;
            const increment = scrollAmountSetting / 100; // Convert percentage to 0-1
            let newProgress = targetProgress - increment;
            // Handle loop mode for percentage-based navigation
            if (newProgress < 0) {
                if (loopMode === 'loop') {
                    newProgress = 1; // Wrap to end
                }
                else {
                    newProgress = 0; // Clamp to start
                }
            }
            targetProgress = newProgress; // Update module-level target for inertia system
            animateToProgress(newProgress);
        }
        else {
            // Waypoint mode: jump to the previous waypoint that Prev / Next stop at (see nextWaypoint)
            // Use pendingWaypointTarget if mid-animation so rapid clicks accumulate
            const baseIndex = pendingWaypointTarget !== null ? pendingWaypointTarget : currentWaypointIndex;
            const prev = waypointStepTarget(config.waypoints, baseIndex, -1, loopMode === 'loop');
            if (prev === null) return;
            pendingWaypointTarget = prev;
            goToWaypoint(prev);
        }
    }
    // Narration scrub state — when the user scrolls during narration playback,
    // we pause audio and let them scrub freely. After a short idle, we resume.
    let narrationScrubbing = false;
    let narrationScrubResumeTimer: ReturnType<typeof setTimeout> | null = null;
    const NARRATION_SCRUB_RESUME_DELAY = 1000; // ms of scroll idle before resuming narration

    function startNarrationScrub(): void {
        if (!narrationEnabled || !narrationAudio || !isPlaying) return;
        narrationScrubbing = true;
        if (!narrationAudio.paused) narrationAudio.pause();
        if (narrationMusicAudio && !narrationMusicAudio.paused) narrationMusicAudio.pause();
        // Clear any pending resume
        if (narrationScrubResumeTimer) {
            clearTimeout(narrationScrubResumeTimer);
            narrationScrubResumeTimer = null;
        }
    }

    function scheduleNarrationScrubResume(): void {
        if (!narrationScrubbing) return;
        if (narrationScrubResumeTimer) clearTimeout(narrationScrubResumeTimer);
        narrationScrubResumeTimer = setTimeout(() => {
            narrationScrubResumeTimer = null;
            narrationScrubbing = false;
            // Resume narration from the current scrubbed position
            if (narrationAudio && narrationEnabled && isPlaying && currentCameraMode === 'tour') {
                const { time, shouldPlay } = getNarrationStateForProgress(currentProgress);
                if (shouldPlay) {
                    narrationAudio.currentTime = time;
                    if (!globalMuted) narrationAudio.play().catch(() => {});
                    if (narrationMusicAudio && !globalMuted) narrationMusicAudio.play().catch(() => {});
                }
            }
        }, NARRATION_SCRUB_RESUME_DELAY);
    }

    // Continuous playback using requestAnimationFrame
    let lastPlaybackTime = 0;
    let playbackAnimationId: number | null = null;
    function playbackLoop(timestamp: number): void {
        if (!isPlaying)
            return;
        if (lastPlaybackTime === 0)
            lastPlaybackTime = timestamp;
        const deltaTime = (timestamp - lastPlaybackTime) / 1000;
        lastPlaybackTime = timestamp;

        // When narration is active and audio is playing (not scrubbing), let the audio
        // element be the master clock. This prevents choppy audio on mobile where frame
        // drops would otherwise cause repeated seeks. The camera follows the audio.
        // During scrubbing, skip this — let the user control progress directly.
        const activeSegment = narrationEnabled && narrationAudio && !narrationAudio.paused && !narrationScrubbing
            ? narrationSegments.find(s => {
                const p100 = currentProgress * 100;
                return p100 >= s.progressStart && p100 <= s.progressEnd;
            })
            : null;

        if (activeSegment && narrationAudio) {
            // Derive progress from audio's current time (audio is the source of truth)
            const audioDuration = activeSegment.audioEnd - activeSegment.audioStart;
            if (audioDuration > 0) {
                const audioRatio = (narrationAudio.currentTime - activeSegment.audioStart) / audioDuration;
                const progressRange = (activeSegment.progressEnd - activeSegment.progressStart) / 100;
                const newProgress = (activeSegment.progressStart / 100) + audioRatio * progressRange;
                currentProgress = Math.max(0, Math.min(1, newProgress));
                targetProgress = currentProgress;
            }
        } else {
            // No narration or between segments — use frame-based deltaTime as before
            const effectiveSpeed = narrationEnabled
                ? getNarrationPlaybackSpeed(currentProgress, playbackSpeed)
                : playbackSpeed;
            // Update both currentProgress and targetProgress to keep inertia system in sync
            const progressIncrement = effectiveSpeed * deltaTime * playbackDirection;
            currentProgress += progressIncrement;
            targetProgress += progressIncrement;
        }
        // Handle end of tour based on loop mode
        if (currentProgress >= 1) {
            switch (loopMode) {
                case 'loop':
                    // Smooth wrap: let progress continue past 1 and modulo in updateCameraFromProgress
                    currentProgress = currentProgress % 1;
                    targetProgress = targetProgress % 1;
                    break;
                case 'pingpong':
                    currentProgress = 1;
                    targetProgress = 1;
                    playbackDirection = -1; // Reverse direction
                    break;
                case 'none':
                default:
                    currentProgress = 1;
                    targetProgress = 1;
                    pause(); // Stop playback
                    events.emit('playbackComplete');
                    return;
            }
        }
        else if (currentProgress <= 0) {
            // Only relevant for pingpong mode going backward
            switch (loopMode) {
                case 'pingpong':
                    currentProgress = 0;
                    targetProgress = 0;
                    playbackDirection = 1; // Reverse back to forward
                    break;
                default:
                    currentProgress = 0;
                    targetProgress = 0;
                    break;
            }
        }
        updateCameraFromProgress(currentProgress);
        playbackAnimationId = requestAnimationFrame(playbackLoop);
    }
    function play(): void {
        // Exit staging first — same dismissal path next/back use. Opening a
        // staging photo freezes the tour driver (waypointControlEnabled=false)
        // and pauses autoplay; playing without unfreezing would advance the
        // camera behind the still-visible image. Idempotent when no skin is active.
        exitVirtualStaging?.();
        // An explicit play (public API, play button) never waits for detail and
        // dismisses the "Adding in more detail" status.
        autoplayDetailGate.cancel();
        playRequestedBeforeInitialAutoplay = true;
        initialPlaybackGate.requestPlayback();
    }
    /**
     * The scene's own initial autoplay. Identical to play() unless the scene asks to wait for
     * full detail (autoPlayWaitForFullDetail) — then the camera holds after the preloader until
     * LOD streaming settles (or the visitor presses "Start the tour anyways").
     */
    function requestInitialAutoplay(): void {
        const waitForDetail = shouldWaitForDetailBeforeAutoplay({
            sceneAutoPlay: config.autoPlay,
            optionEnabled: config.autoPlayWaitForFullDetail,
            isLodScene: !!(config.lodMetaUrl || config.mobileLodMetaUrl),
            isEditor: isEditorMode,
            isFrameSequence: !!frameSequencePlayer,
        });
        if (!waitForDetail || playRequestedBeforeInitialAutoplay || isPlaying) {
            play();
            return;
        }
        exitVirtualStaging?.();
        autoplayDetailGate.arm();
        initialPlaybackGate.requestPlayback();
    }
    /**
     * Whether autoplay is running or the initial autoplay is still pending behind the detail
     * wait. Visitor actions that pause a running autoplay (next/prev, mode switch, FPV, staging,
     * camera hold, capture) also cancel a pending one, so the tour never starts on its own after
     * the visitor has taken control.
     */
    function isAutoplayEngaged(): boolean {
        return isPlaying || autoplayDetailGate.isEngaged();
    }
    function startPlayback(): void {
        if (isDestroyed) return;
        // 4DGS frame sequence playback
        if (frameSequencePlayer) {
            frameSequencePlayer.play();
            events.emit('playbackStart');
            return;
        }
        // Waypoint playback
        if (isPlaying || !config.waypoints || config.waypoints.length < 2)
            return;
        isPlaying = true;
        lastPlaybackTime = 0;
        playbackDirection = 1; // Always start going forward
        events.emit('playbackStart');
        playbackAnimationId = requestAnimationFrame(playbackLoop);
    }
    function pause(): void {
        autoplayDetailGate.cancel();
        playRequestedBeforeInitialAutoplay = false;
        initialPlaybackGate.cancelPendingPlayback();
        // 4DGS frame sequence pause
        if (frameSequencePlayer) {
            frameSequencePlayer.pause();
            events.emit('playbackStop');
            return;
        }
        // Waypoint pause
        isPlaying = false;
        if (playbackAnimationId) {
            cancelAnimationFrame(playbackAnimationId);
            playbackAnimationId = null;
        }
        // Cancel any pending narration scrub resume
        narrationScrubbing = false;
        if (narrationScrubResumeTimer) {
            clearTimeout(narrationScrubResumeTimer);
            narrationScrubResumeTimer = null;
        }
        // Pause narration audio when playback stops
        if (narrationAudio && !narrationAudio.paused) {
            narrationAudio.pause();
        }
        if (narrationMusicAudio && !narrationMusicAudio.paused) {
            narrationMusicAudio.pause();
        }
        events.emit('playbackStop');
    }
    function stop(): void {
        autoplayDetailGate.cancel();
        playRequestedBeforeInitialAutoplay = false;
        initialPlaybackGate.cancelPendingPlayback();
        // 4DGS frame sequence stop
        if (frameSequencePlayer) {
            frameSequencePlayer.stop();
            events.emit('playbackStop');
            return;
        }
        // Waypoint stop
        pause();
        setProgress(0);
    }
    // Scroll wheel support
    const scrollCanvas = app.graphicsDevice.canvas as HTMLCanvasElement;
    // Any pointer/wheel contact with the canvas counts as scene interaction for
    // the session, so portal-destination viewers skip the controls onboarding.
    // The canvas is removed on portal navigation/destroy, taking these with it.
    scrollCanvas.addEventListener('pointerdown', markSceneInteractionForOnboarding, { capture: true });
    scrollCanvas.addEventListener('wheel', markSceneInteractionForOnboarding, { capture: true, passive: true });
    scrollCanvas.addEventListener('wheel', (e: WheelEvent) => {
        if (!waypointControlEnabled)
            return;
        e.preventDefault();
        // If narration is playing, pause audio so user can scrub freely.
        // Audio will resume after scroll idle (NARRATION_SCRUB_RESUME_DELAY).
        if (narrationEnabled && isPlaying && narrationAudio) {
            startNarrationScrub();
            scheduleNarrationScrubResume();
        }
        // Adjust progress based on scroll using configurable speed and amount
        // Match legacy BabylonJS behavior where scroll speed varies with waypoint count
        const scrollSpeedSetting = config.scrollSpeed || 0.1;
        const scrollAmountSetting = config.scrollAmount || 100;
        // Legacy formula: scrollTarget += deltaY * scrollSpeed (where scrollTarget is 0 to pathLength-1)
        // pathLength = segment count * 20 for Catmull-Rom spline with 20 subdivisions
        // Convert to 0-1 progress: progressIncrement = (deltaY * scrollSpeed) / pathLength
        const numWaypoints = config.waypoints?.length || 2;
        // In loop mode: N segments (closed ring). In non-loop mode: N-1 segments (open path).
        const numSegs = loopMode === 'loop' ? numWaypoints : numWaypoints - 1;
        const effectivePathLength = Math.max(20, numSegs * 20); // Min 20 to avoid division issues
        const normalizedDelta = Math.abs(e.deltaY) / 100; // Normalize wheel delta
        const baseIncrement = (normalizedDelta * 100 * scrollSpeedSetting * (scrollAmountSetting / 100)) / effectivePathLength;
        const scrollIncrement = e.deltaY > 0 ? baseIncrement : -baseIncrement;
        // Update target progress - the update loop will lerp currentProgress toward it (creates inertia)
        if (loopMode === 'loop') {
            // Wrap around for smooth looping
            targetProgress = ((targetProgress + scrollIncrement) % 1 + 1) % 1;
        } else {
            targetProgress = Math.max(0, Math.min(1, targetProgress + scrollIncrement));
        }
    }, { passive: false });
    // Elastic user rotation - track pointer drag for look-around in tour mode
    // Use capture phase to get events before PlayCanvas input system
    scrollCanvas.addEventListener('pointerdown', (e: PointerEvent) => {
        if (!waypointControlEnabled || gizmoInputSuppressed)
            return; // Only in tour/waypoint mode, skip during gizmo drag
        isUserDragging = true;
        lastPointerX = e.clientX;
        lastPointerY = e.clientY;
    }, { capture: true });
    scrollCanvas.addEventListener('pointermove', (e: PointerEvent) => {
        if (!waypointControlEnabled || !isUserDragging || gizmoInputSuppressed)
            return;
        const deltaX = e.clientX - lastPointerX;
        const deltaY = e.clientY - lastPointerY;
        lastPointerX = e.clientX;
        lastPointerY = e.clientY;
        // Convert drag delta to rotation offset in degrees (yaw and pitch only, no roll).
        // First-person by default; Invert Drag Direction (cameraControls.invertRotation is the live
        // single source of truth for the scene's invertCameraRotation) flips both axes so the scene
        // follows the pointer, matching explore and walk.
        const lookSign = lookAxisSign(cameraControls.invertRotation);
        const yawDelta = -deltaX * lookSign * USER_ROTATION_SENSITIVITY;
        const pitchDelta = -deltaY * lookSign * USER_ROTATION_SENSITIVITY;
        // Accumulate yaw and pitch offsets (Euler angles prevent roll drift)
        userYawOffset += yawDelta;
        userPitchOffset += pitchDelta;
        // Clamp pitch to prevent camera flipping (and to stay short of straight up/down)
        userPitchOffset = targetCameraRotation
            ? clampTourPitch(targetCameraRotation, userPitchOffset, MAX_PITCH_OFFSET)
            : Math.max(-MAX_PITCH_OFFSET, Math.min(MAX_PITCH_OFFSET, userPitchOffset));
    }, { capture: true });
    scrollCanvas.addEventListener('pointerup', () => {
        isUserDragging = false;
    }, { capture: true });
    scrollCanvas.addEventListener('pointerleave', () => {
        isUserDragging = false;
    }, { capture: true });
    // =====================================================
    // HOTSPOT SYSTEM
    // =====================================================
    const hotspotEntities: ViewerRuntimeEntity[] = [];
    const portalEntities: ViewerRuntimeEntity[] = [];
    // "Keep same size on screen" hotspot and portal icons. Scaled just before each frame is
    // drawn, from the camera pose that frame uses, so nothing between frames (picking, the editor)
    // sees a size other than the one on screen. A still camera writes nothing.
    const updateMarkerScreenSizes = (): void => {
        // An editor gizmo drag scales the entity itself: hold the multiplier so the drag result
        // can be divided by it (getHotspot/PortalScreenSizeFactor) instead of multiplied again.
        if (gizmoInputSuppressed) return;
        const cameraPos = camera.getPosition();
        const fov = camera.camera?.fov;
        // Distances in scene units, so an AR-scaled scene keeps the same proportions.
        const sceneScale = Math.abs(sceneContent.getLocalScale().x) || 1;
        for (const entity of [...hotspotEntities, ...portalEntities]) {
            // Collision planes are scene geometry, never distance-scaled icons.
            const fields = entity.hotspotData || (entity.portalData?.type !== 'plane' ? entity.portalData : undefined);
            const settings = resolveScreenSizeSettings(fields);
            if (!settings) {
                if (entity.screenSizeState) {
                    const restored = releaseScreenSize(entity.getLocalScale(), entity.screenSizeState);
                    entity.setLocalScale(restored[0], restored[1], restored[2]);
                    entity.screenSizeState = undefined;
                }
                continue;
            }
            const distance = cameraPos.distance(entity.getPosition()) / sceneScale;
            const factor = screenSizeFactor(distance, settings, fov, defaultFOV);
            const step = stepScreenSize(entity.getLocalScale(), entity.screenSizeState ?? null, factor);
            entity.screenSizeState = step.state;
            if (step.changed) entity.setLocalScale(step.scale[0], step.scale[1], step.scale[2]);
        }
    };
    app.on('prerender', updateMarkerScreenSizes);
    // =====================================================
    // MIRROR PLANE SYSTEM (Planar Reflections)
    // =====================================================
    const mirrorPlaneEntities: MirrorPlaneRuntimeEntity[] = [];
    // Track per-mirror cleanup callbacks for prerender handlers
    const mirrorPrerenderCleanups: Map<string, () => void> = new Map();
    // Shared zero-plane for disabling the gsplat mirror clip
    const _mirrorClipPlaneOff = new Float32Array([0, 0, 0, 0]);
    let _mirrorClipShaderApplied = false;
    // WebGPU: the GPU-sort renderer's projection binds each reflection camera's plane (gsplatClipPlane.ts).
    let _gpuSortClipPatched = false;
    let _mainCameraCalcProjWrapped = false;
    // Remember the camera we patched and its original calculateProjection so the
    // wrap can be cleanly removed on teardown — otherwise re-wrapping would
    // capture the already-wrapped fn as "original" and nest indefinitely.
    let _mainCameraWrapInfo: { cam: CameraComponentProjectionHook; orig: ProjectionCallback | null } | null = null;
    // Several gsplat effects (relighting, reveals, transitions) prepend the
    // mirrorClip snippet into gsplatModifyVS, so any of them compiles a shader
    // that declares `uniform uMirrorClipPlane`. If no mirror ever calls
    // ensureMirrorClipShader(), the uniform is never set and PlayCanvas warns
    // every frame. Seed the device scope with the disabled plane up front.
    app.graphicsDevice.scope.resolve('uMirrorClipPlane').setValue(_mirrorClipPlaneOff);

    // Dedicated layer for mirror plane meshes — kept OFF the reflection cameras to avoid feedback loops.
    // The main camera gets this layer added so it can render mirror planes; reflection cameras only
    // render LAYERID_WORLD + LAYERID_SKYBOX, so they never encounter the mirror mesh material that
    // samples from their own render target.
    const mirrorMeshLayer = new pc.Layer({ name: 'MirrorMeshLayer' });
    // Dedicated layer for hotspots, portals, and custom meshes so they depth-sort correctly
    // against gsplats (which render in the World layer). Same technique as mirror planes.
    // Translucency note: this layer is composited after World's OPAQUE sub-layer, i.e. BEFORE the
    // transparent sub-layer where the splats draw, which is right for opaque meshes (they write
    // depth and the splats test against it) but wrong for translucent ones. Entities below full
    // opacity move to TranslucentEntityLayer (below), which draws after the splats and fades each
    // fragment against the splats' depth (utils/materialOpacity.ts, splatDepthPass.ts).
    const hotspotMeshLayer = new pc.Layer({ name: 'HotspotMeshLayer' });
    // Insert mirror and hotspot layers right after the World layer's opaque sub-layer so they write
    // depth before the splats, translucent entities and particles test against it.
    const worldLayerIdx = app.scene.layers.getOpaqueIndex(app.scene.layers.getLayerById(pc.LAYERID_WORLD)!);
    if (worldLayerIdx >= 0) {
        app.scene.layers.insert(mirrorMeshLayer, worldLayerIdx + 1);
        app.scene.layers.insert(hotspotMeshLayer, worldLayerIdx + 2);
    } else {
        app.scene.layers.push(mirrorMeshLayer);
        app.scene.layers.push(hotspotMeshLayer);
    }
    // Hotspots, portals and custom meshes below full opacity. Its opaque sub-layer sits with
    // HotspotMeshLayer (before the splats) so any opaque parts of a mixed entity still render as
    // before; its transparent sub-layer draws right after the splats, where the translucent
    // materials blend over them and fade out behind splat surfaces using the splat depth map.
    const translucentEntityLayer = new pc.Layer({ name: 'TranslucentEntityLayer', transparentSortMode: pc.SORTMODE_BACK2FRONT });
    {
        const worldLayer = app.scene.layers.getLayerById(pc.LAYERID_WORLD)!;
        const hotspotOpaqueIdx = app.scene.layers.getOpaqueIndex(hotspotMeshLayer);
        app.scene.layers.insertOpaque(translucentEntityLayer, hotspotOpaqueIdx >= 0 ? hotspotOpaqueIdx + 1 : app.scene.layers.layerList.length);
        const worldTransparentIdx = app.scene.layers.getTransparentIndex(worldLayer);
        app.scene.layers.insertTransparent(translucentEntityLayer, worldTransparentIdx >= 0 ? worldTransparentIdx + 1 : app.scene.layers.layerList.length);
        // Particles draw after the splats and translucent entities, before Immediate / UI. That also
        // keeps them in CameraFrame's scene pass (it ends at LAYERID_IMMEDIATE): at the end of the
        // list they fell into its after-pass, drawn on the backbuffer after compose, with no depth.
        const layers = app.scene.layers;
        layers.remove(particleLayer);
        const translucentOpaqueIdx = layers.getOpaqueIndex(translucentEntityLayer);
        layers.insertOpaque(particleLayer, translucentOpaqueIdx >= 0 ? translucentOpaqueIdx + 1 : layers.layerList.length);
        const translucentTransparentIdx = layers.getTransparentIndex(translucentEntityLayer);
        layers.insertTransparent(particleLayer, translucentTransparentIdx >= 0 ? translucentTransparentIdx + 1 : layers.layerList.length);
    }
    // Add mirrorMeshLayer, hotspotMeshLayer and translucentEntityLayer to the main camera — right after LAYERID_WORLD
    if (camera.camera) {
        const mainCamLayers = [...camera.camera.layers];
        const worldCamIdx = mainCamLayers.indexOf(pc.LAYERID_WORLD);
        if (worldCamIdx >= 0) {
            mainCamLayers.splice(worldCamIdx + 1, 0, mirrorMeshLayer.id, hotspotMeshLayer.id, translucentEntityLayer.id);
        } else {
            mainCamLayers.push(mirrorMeshLayer.id, hotspotMeshLayer.id, translucentEntityLayer.id);
        }
        camera.camera.layers = mainCamLayers;
    }
    // Splat depth for translucent entities: only rendered while the translucent layer holds an
    // enabled, visible (entity opacity above ~0) mesh instance, and skipped on frames where none
    // of them is on screen. Freed after a couple of idle seconds, reallocated on demand.
    // Water surfaces read the same depth map (see effects/water); bound once the water system exists.
    let waterNeedsSplatDepth: () => boolean = () => false;
    // The splat draw split at the water while clouds / splat weather stand over it (created with
    // the clouds, see "CLOUD LAYERS"); the water then reads a map of the splats behind it only.
    let waterSplatSplit: WaterSplatSplit | null = null;
    // Shadows (created with the lights, see "SHADOWS" below). Declared here because the splat
    // depth pass also serves the shadow ground plane.
    let shadowSystem: ShadowSystem | null = null;
    const splatDepth = camera.camera
        ? installSplatDepthPass(app, camera.camera, app.scene.layers.getLayerById(pc.LAYERID_WORLD)!, translucentEntityLayer, {
            scale: isMobileDevice() ? 0.5 : 1,
            // isNeeded runs while the frame graph is built, before this frame's culling: any playing,
            // enabled emitter counts. alsoNeeded runs in the pass, after culling: one on screen.
            isNeeded: () => hasVisibleEntityTranslucency(translucentEntityLayer.meshInstances)
                || hasVisibleParticles(particleLayer.meshInstances)
                || !!shadowSystem?.needsSplatDepth(),
            alsoNeeded: () => (!!camera.camera && particlesOnScreen(particleLayer, camera.camera.camera))
                || !!shadowSystem?.needsSplatDepth(),
            waterNeeded: () => waterNeedsSplatDepth(),
            waterSplit: () => waterSplatSplit,
        })
        : null;
    /**
     * Move render components between HotspotMeshLayer and TranslucentEntityLayer to match their
     * materials' entity translucency. Runs on the next update after any material crosses the
     * full-opacity threshold (materials are often created before they are attached to an entity,
     * and GLB hierarchies instantiate asynchronously), and always before that frame renders.
     */
    let translucentLayersDirty = false;
    function syncTranslucentEntityLayers(): void {
        translucentLayersDirty = false;
        const renders = app.root.findComponents('render') as pc.RenderComponent[];
        let moved = 0;
        for (const render of renders) {
            const layers = render.layers;
            const onOpaque = layers.includes(hotspotMeshLayer.id);
            const onTranslucent = layers.includes(translucentEntityLayer.id);
            if (!onOpaque && !onTranslucent) continue;
            const wantTranslucent = render.meshInstances.some((mi) => isEntityTranslucentMaterial(mi.material));
            if (wantTranslucent === onTranslucent && onOpaque !== wantTranslucent) continue;
            const from = wantTranslucent ? hotspotMeshLayer.id : translucentEntityLayer.id;
            const to = wantTranslucent ? translucentEntityLayer.id : hotspotMeshLayer.id;
            const next = layers.filter((id) => id !== from && id !== to);
            next.push(to);
            render.layers = next;
            moved++;
        }
        if (moved > 0) renderScheduler.requestFrame('entity-translucency');
    }
    app.on('update', () => {
        if (translucentLayersDirty) syncTranslucentEntityLayers();
    });
    /**
     * Initial / edited opacity of a hotspot sphere or portal sphere / panorama globe. A STATIC
     * value at or above 0.95 renders fully opaque, as these shapes always have in the viewer
     * (so saved scenes keep their look); animated opacity uses the full range.
     */
    function resolveSphereEntityOpacity(config: EntityOpacityConfigLike, scrollPercent: number): number {
        const opacity = resolveEntityOpacity(config, scrollPercent);
        return config.opacityMode === 'animated' && config.opacityAnimation ? opacity : snapLegacySphereOpacity(opacity);
    }
    /** Entity opacity for one material; flags a layer sync when it crosses the full-opacity threshold. */
    function applyEntityOpacity(material: pc.StandardMaterial, opacity: number): void {
        const change = applyMaterialOpacity(material, opacity);
        if (change === 'unchanged') return;
        if (change === 'switched') translucentLayersDirty = true;
        renderScheduler.requestFrame('entity-opacity');
    }

    function createSingleMirrorPlane(data: MirrorPlaneData, index: number): pc.Entity {
        const id = data.id || `mirror-${Date.now()}-${index}`;
        const resolution = data.resolution ?? 0.5;
        const intensity = data.intensity ?? 1.0;
        const tintHex = data.tint || '#ffffff';

        // Parse tint color
        const tintR = parseInt(tintHex.slice(1, 3), 16) / 255;
        const tintG = parseInt(tintHex.slice(3, 5), 16) / 255;
        const tintB = parseInt(tintHex.slice(5, 7), 16) / 255;

        // Create parent entity for transform (gizmo attaches here).
        // Saved scenes can hold BabylonJS-serialized vectors ({_x,_y,_z}) or
        // already-normalized ones ({x,y,z}); read either via finiteSceneVec3 so
        // mirrors don't vanish on published scenes saved before normalization.
        const entity = new pc.Entity(id) as MirrorPlaneRuntimeEntity;
        const mirrorPos = playCanvasPositionFromSceneVec3(data.position);
        entity.setPosition(mirrorPos.x, mirrorPos.y, mirrorPos.z);

        // Convert BabylonJS Euler radians → PlayCanvas degrees with Z negation
        const mirrorRot = finiteSceneVec3(data.rotation);
        const rx = mirrorRot.x * (180 / Math.PI);
        const ry = mirrorRot.y * (180 / Math.PI);
        const rz = -mirrorRot.z * (180 / Math.PI);
        entity.setEulerAngles(rx, ry, rz);
        const mirrorScale = finiteSceneVec3(data.scale, { x: 2, y: 1, z: 2 });
        entity.setLocalScale(mirrorScale.x, mirrorScale.y, mirrorScale.z);

        // Mesh child for the mirror surface. Two paths:
        //  - Default rectangular mirror: plane primitive rotated 90° X so its surface faces parent +Z.
        //  - Irregular footprint: custom polygon mesh built directly in parent local space (vertical layout),
        //    so polygon vertices (u, v) map to (x, y, 0) and the surface naturally faces +Z. No child rotation.
        const sanitizedFootprint = sanitizeFootprint(data.footprint);
        const meshChild = new pc.Entity(`${id}-mesh`);
        let placeholderMaterial: pc.StandardMaterial | null = null;
        if (sanitizedFootprint && sanitizedFootprint.length >= 3) {
            const customMesh = buildPolygonMesh(app.graphicsDevice, sanitizedFootprint, VERTICAL_LAYOUT);
            if (customMesh) {
                placeholderMaterial = new pc.StandardMaterial();
                const mi = new pc.MeshInstance(customMesh, placeholderMaterial);
                meshChild.addComponent('render', {
                    meshInstances: [mi],
                    castShadows: false,
                    receiveShadows: false,
                    layers: [mirrorMeshLayer.id],
                });
                entity._mirrorCustomMesh = customMesh;
            } else {
                meshChild.setLocalEulerAngles(90, 0, 0);
                meshChild.addComponent('render', {
                    type: 'plane',
                    castShadows: false,
                    receiveShadows: false,
                    layers: [mirrorMeshLayer.id],
                });
            }
        } else {
            meshChild.setLocalEulerAngles(90, 0, 0);
            meshChild.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [mirrorMeshLayer.id],
            });
        }
        entity.addChild(meshChild);

        // Create reflection render target
        const rtWidth = Math.max(64, Math.floor(app.graphicsDevice.width * resolution));
        const rtHeight = Math.max(64, Math.floor(app.graphicsDevice.height * resolution));
        const reflectionTexture = new pc.Texture(app.graphicsDevice, {
            width: rtWidth,
            height: rtHeight,
            format: pc.PIXELFORMAT_RGBA8,
            mipmaps: false,
            minFilter: pc.FILTER_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        const renderTarget = new pc.RenderTarget({
            colorBuffer: reflectionTexture,
            depth: true,
            origin: pc.RENDERTARGET_ORIGIN_BOTTOM,
        });

        // Create reflection camera
        const reflCamEntity = new pc.Entity(`${id}-reflcam`);
        reflCamEntity.addComponent('camera', {
            priority: -100 - index,
            renderTarget: renderTarget,
            // Render world layer but exclude mirror mesh layer to prevent self-reflection
            layers: [pc.LAYERID_WORLD, pc.LAYERID_SKYBOX],
            flipFaces: true,
        });
        app.root.addChild(reflCamEntity);

        // Create custom shader material for the mirror plane
        const vsCode = `
            attribute vec3 aPosition;
            attribute vec3 aNormal;
            uniform mat4 matrix_model;
            uniform mat4 matrix_viewProjection;
            varying vec4 vScreenPos;
            varying vec3 vWorldNormal;
            varying vec3 vWorldPos;
            void main() {
                vec4 worldPos = matrix_model * vec4(aPosition, 1.0);
                vWorldPos = worldPos.xyz;
                vWorldNormal = normalize((matrix_model * vec4(aNormal, 0.0)).xyz);
                gl_Position = matrix_viewProjection * worldPos;
                vScreenPos = gl_Position;
            }
        `;
        const fsCode = `
            precision highp float;
            varying vec4 vScreenPos;
            varying vec3 vWorldNormal;
            varying vec3 vWorldPos;
            uniform sampler2D uReflectionMap;
            uniform float uIntensity;
            uniform vec3 uTint;
            uniform vec3 uCameraPos;
            void main() {
                // Check if viewing the back face — show black (non-reflective side)
                vec3 viewDir = normalize(uCameraPos - vWorldPos);
                vec3 normal = normalize(vWorldNormal);
                float facing = dot(viewDir, normal);
                if (facing < 0.0) {
                    gl_FragColor = vec4(0.02, 0.02, 0.02, 1.0);
                    return;
                }
                // Screen-space UVs from clip coordinates with horizontal flip
                vec2 screenUV = vScreenPos.xy / vScreenPos.w * 0.5 + 0.5;
                screenUV.x = 1.0 - screenUV.x;
                vec4 reflColor = texture2D(uReflectionMap, screenUV);
                // Fresnel: stronger reflection at grazing angles
                float fresnel = 1.0 - abs(facing);
                fresnel = mix(0.6, 1.0, fresnel * fresnel);
                vec3 finalColor = reflColor.rgb * uTint * uIntensity * fresnel;
                gl_FragColor = vec4(finalColor, 1.0);
            }
        `;

        // WGSL counterparts so the mirror works on WebGPU as well.
        // PlayCanvas's WebGPU path cannot transpile arbitrary GLSL without twgsl, so we
        // ship native WGSL here. Conventions match the planar-reflection script:
        // - `attribute` / `varying` declared at module scope (NOT inside structs)
        // - `uniform NAME: TYPE` declarations, accessed as `uniform.NAME`
        // - fragment uses `output.color` with `FragmentOutput` struct
        const vsWGSL = /* wgsl */ `
            attribute aPosition: vec3f;
            attribute aNormal: vec3f;
            uniform matrix_model: mat4x4f;
            uniform matrix_viewProjection: mat4x4f;
            varying vScreenPos: vec4f;
            varying vWorldNormal: vec3f;
            varying vWorldPos: vec3f;
            @vertex
            fn vertexMain(input: VertexInput) -> VertexOutput {
                var output: VertexOutput;
                let worldPos: vec4f = uniform.matrix_model * vec4f(aPosition, 1.0);
                output.vWorldPos = worldPos.xyz;
                output.vWorldNormal = normalize((uniform.matrix_model * vec4f(aNormal, 0.0)).xyz);
                output.position = uniform.matrix_viewProjection * worldPos;
                output.vScreenPos = output.position;
                return output;
            }
        `;
        const fsWGSL = /* wgsl */ `
            varying vScreenPos: vec4f;
            varying vWorldNormal: vec3f;
            varying vWorldPos: vec3f;
            var uReflectionMap: texture_2d<f32>;
            var uReflectionMapSampler: sampler;
            uniform uIntensity: f32;
            uniform uTint: vec3f;
            uniform uCameraPos: vec3f;
            @fragment
            fn fragmentMain(input: FragmentInput) -> FragmentOutput {
                var output: FragmentOutput;
                let viewDir: vec3f = normalize(uniform.uCameraPos - vWorldPos);
                let normal: vec3f = normalize(vWorldNormal);
                let facing: f32 = dot(viewDir, normal);
                if (facing < 0.0) {
                    output.color = vec4f(0.02, 0.02, 0.02, 1.0);
                    return output;
                }
                var screenUV: vec2f = vScreenPos.xy / vScreenPos.w * 0.5 + vec2f(0.5);
                screenUV.x = 1.0 - screenUV.x;
                // textureSampleLevel (not textureSample): WGSL requires textureSample to be in
                // uniform control flow, but the back-face early-return above breaks that. The
                // reflection texture has no mipmaps anyway, so explicit mip 0 is correct.
                let reflColor: vec4f = textureSampleLevel(uReflectionMap, uReflectionMapSampler, screenUV, 0.0);
                let fresnelRaw: f32 = 1.0 - abs(facing);
                let fresnel: f32 = mix(0.6, 1.0, fresnelRaw * fresnelRaw);
                let finalColor: vec3f = reflColor.rgb * uniform.uTint * uniform.uIntensity * fresnel;
                output.color = vec4f(finalColor, 1.0);
                return output;
            }
        `;

        const mirrorMaterial = new pc.ShaderMaterial({
            // Shared by every mirror: the code is the same and the uniforms are per material.
            // A per-mirror name compiled and cached one more program for every mirror ever
            // created, and the program cache is never released on removal.
            uniqueName: 'StorySplatMirrorShader',
            vertexGLSL: vsCode,
            fragmentGLSL: fsCode,
            vertexWGSL: vsWGSL,
            fragmentWGSL: fsWGSL,
            attributes: {
                aPosition: pc.SEMANTIC_POSITION,
                aNormal: pc.SEMANTIC_NORMAL,
            },
        });
        mirrorMaterial.setParameter('uReflectionMap', reflectionTexture);
        mirrorMaterial.setParameter('uIntensity', intensity);
        mirrorMaterial.setParameter('uTint', [tintR, tintG, tintB]);
        mirrorMaterial.setParameter('uCameraPos', [0, 0, 0]);
        mirrorMaterial.cull = pc.CULLFACE_NONE;
        mirrorMaterial.depthTest = true;
        mirrorMaterial.depthWrite = true;
        mirrorMaterial.update();

        // Apply material to the child plane's mesh instances
        if (meshChild.render) {
            meshChild.render.meshInstances.forEach(mi => {
                mi.material = mirrorMaterial;
            });
        }
        placeholderMaterial?.destroy();

        // Store material ref on entity for cleanup
        entity._mirrorMaterial = mirrorMaterial;
        entity._mirrorReflCam = reflCamEntity;
        entity._mirrorRenderTarget = renderTarget;
        entity._mirrorReflTexture = reflectionTexture;
        entity._mirrorData = data;

        // Pre-render handler: mirror camera through the plane each frame
        // Pre-allocate scratch objects outside the hot path to avoid GC pressure
        const _mirrorPlaneNormal = new pc.Vec3();
        const _mirrorReflPos = new pc.Vec3();
        const _mirrorReflTarget = new pc.Vec3();
        const _mirrorReflUp = new pc.Vec3();
        const _mirrorReflMat = new pc.Mat4();
        const _thisClipPlane = new Float32Array(4);
        setSplatClipPlane(reflCamEntity, _thisClipPlane);
        const _mirrorCullSphere = new pc.BoundingSphere();
        // Reflection-camera culling hysteresis: enable immediately when the mirror comes into
        // view, but only disable after it's been out of view this many consecutive frames.
        // Disabling removes the camera from the composition and the engine tears down its
        // per-camera gsplat work buffer/sorter — rapid toggling at screen edges would cause
        // allocation churn and a few frames of unsorted splats in the reflection.
        const MIRROR_DISABLE_AFTER_FRAMES = 45;
        let mirrorOutOfViewFrames = 0;

        const onPrerender = () => {
            const mainCam = camera.camera!;
            const mainCamPos = camera.getPosition();

            // Get plane world position and normal. The mesh child is rotated 90°X so the plane
            // faces the parent's +Z local axis. In PlayCanvas, entity.forward = -Z, so the
            // plane normal pointing toward the reflective side is -entity.forward.
            const planePos = entity.getPosition();
            const fwd = entity.forward;
            _mirrorPlaneNormal.set(-fwd.x, -fwd.y, -fwd.z);
            // PlayCanvas plane distance convention: N·P + distance = 0 → distance = -N·Q
            const d = _mirrorPlaneNormal.dot(planePos);
            // The splat clip plane, current before anything renders: WebGPU's GPU-sort renderer
            // projects the reflection camera's splats (gsplatClipPlane.ts) before its
            // calculateProjection below runs.
            _thisClipPlane[0] = _mirrorPlaneNormal.x;
            _thisClipPlane[1] = _mirrorPlaneNormal.y;
            _thisClipPlane[2] = _mirrorPlaneNormal.z;
            _thisClipPlane[3] = d;

            // ── Reflection-camera culling ─────────────────────────────────
            // The reflection camera is a full second World+Skybox render (plus its own unified
            // gsplat work-buffer/sort pipeline) every frame — skip it whenever the mirror can't
            // be seen: mirror entity disabled, plane back-facing the camera (the surface shader
            // already renders the back side flat black), or plane outside the view frustum.
            // The frustum here is last frame's (updated during render cull), so the sphere is
            // padded; worst case is one frame of stale reflection on re-entry. In XR the main
            // frustum doesn't cover both eye views — keep the reflection always-on there.
            let wantReflection = entity.enabled;
            if (wantReflection && !isInXR) {
                // Small negative tolerance: near-grazing fragments can compute facing > 0 while
                // the camera center is slightly behind the plane — keep rendering there so they
                // never sample a stale reflection.
                const facingFront = _mirrorPlaneNormal.dot(mainCamPos) - d > -0.1;
                let inFrustum = true;
                const cullMi = meshChild.render?.meshInstances?.[0];
                if (cullMi) {
                    const aabb = cullMi.aabb;
                    _mirrorCullSphere.center.copy(aabb.center);
                    _mirrorCullSphere.radius = aabb.halfExtents.length() * 1.2 + 0.5;
                    inFrustum = mainCam.frustum.containsSphere(_mirrorCullSphere) > 0;
                }
                wantReflection = facingFront && inFrustum;
            }
            if (wantReflection) {
                mirrorOutOfViewFrames = 0;
                if (!reflCamEntity.enabled) reflCamEntity.enabled = true;
            } else if (reflCamEntity.enabled && ++mirrorOutOfViewFrames >= MIRROR_DISABLE_AFTER_FRAMES) {
                reflCamEntity.enabled = false;
            }

            // Keep the mirror SURFACE shader inputs fresh even while the reflection camera is
            // culled: the back-face-dark branch needs a live uCameraPos (a stale one could let
            // back-side pixels compute facing > 0 and sample a stale reflection texture).
            mirrorMaterial.setParameter('uCameraPos', [mainCamPos.x, mainCamPos.y, mainCamPos.z]);
            ensureMirrorClipShader();
            ensureMainCameraClipClear();
            if (!_gpuSortClipPatched) _gpuSortClipPatched = patchGpuSortClip(app);

            // Reflection transform/projection work only matters while the camera renders.
            if (!reflCamEntity.enabled) return;

            const reflCam = reflCamEntity.camera!;

            // Copy main camera settings
            reflCam.fov = mainCam.fov;
            reflCam.nearClip = mainCam.nearClip;
            reflCam.farClip = mainCam.farClip;
            // The scene's background colour, so the reflection's sky matches (it was the camera
            // default grey). Skybox scenes draw the skybox layer over it anyway.
            const mainClear = mainCam.clearColor;
            const reflClear = reflCam.clearColor;
            if (reflClear.r !== mainClear.r || reflClear.g !== mainClear.g || reflClear.b !== mainClear.b || reflClear.a !== mainClear.a) {
                reflCam.clearColor = mainClear;
            }

            // Build reflection matrix using PlayCanvas's Mat4.setReflection().
            _mirrorReflMat.setReflection(_mirrorPlaneNormal, -d);

            // Reflect camera position, target, and up through the mirror plane.
            _mirrorReflMat.transformPoint(mainCamPos, _mirrorReflPos);
            reflCamEntity.setPosition(_mirrorReflPos);

            // Reflect the main camera's look-at target (1 unit in front)
            _mirrorReflTarget.copy(mainCamPos).add(camera.forward);
            _mirrorReflMat.transformPoint(_mirrorReflTarget, _mirrorReflTarget);

            // Reflect the up vector through the plane for a stable frame.
            // The VP-based UV projection in the shader handles any orientation correctly,
            // so using the reflected up (unlike screen-space UVs) produces stable results.
            const mainCamUp = camera.up;
            _mirrorReflUp.set(mainCamUp.x, mainCamUp.y, mainCamUp.z);
            _mirrorReflMat.transformVector(_mirrorReflUp, _mirrorReflUp);
            reflCamEntity.lookAt(_mirrorReflTarget, _mirrorReflUp);

            // ── Oblique near-clip plane ──────────────────────────────────
            // Replace the standard near plane with the mirror plane itself.
            // This perfectly clips everything on the wrong side of the mirror
            // (e.g. wall splats behind the mirror) regardless of distance.
            //
            // Algorithm (Eric Lengyel's oblique frustum clipping):
            // 1. Transform the mirror plane into the reflection camera's view space
            // 2. Modify the third row of the projection matrix so the near plane
            //    coincides with the mirror plane
            reflCam.calculateProjection = (projMatrix: pc.Mat4) => {
                // Start with the standard perspective projection
                const aspect = app.graphicsDevice.width / app.graphicsDevice.height;
                projMatrix.setPerspective(reflCam.fov, aspect, reflCam.nearClip, reflCam.farClip);

                // ── Gsplat mirror clip: SET for this reflection camera ────
                // Use device.scope to set the uniform DIRECTLY on the GPU,
                // bypassing the material parameter system which may not flush
                // between cameras in the same frame.
                app.graphicsDevice.scope.resolve('uMirrorClipPlane').setValue(_thisClipPlane);

                // ── Oblique near-clip plane (for mesh geometry) ──────────
                const viewMatrix = reflCamEntity.camera!.viewMatrix;
                const vm = viewMatrix.data;

                const clipPlaneX = vm[0] * _mirrorPlaneNormal.x + vm[4] * _mirrorPlaneNormal.y + vm[8] * _mirrorPlaneNormal.z;
                const clipPlaneY = vm[1] * _mirrorPlaneNormal.x + vm[5] * _mirrorPlaneNormal.y + vm[9] * _mirrorPlaneNormal.z;
                const clipPlaneZ = vm[2] * _mirrorPlaneNormal.x + vm[6] * _mirrorPlaneNormal.y + vm[10] * _mirrorPlaneNormal.z;
                const clipPlaneW = _mirrorReflPos.x * _mirrorPlaneNormal.x + _mirrorReflPos.y * _mirrorPlaneNormal.y + _mirrorReflPos.z * _mirrorPlaneNormal.z - d;

                if (clipPlaneZ >= 0) return;

                const pm = projMatrix.data;
                const qx = (Math.sign(clipPlaneX) + pm[8]) / pm[0];
                const qy = (Math.sign(clipPlaneY) + pm[9]) / pm[5];
                const qz = -1.0;
                const qw = (1.0 + pm[10]) / pm[14];
                const dotCQ = clipPlaneX * qx + clipPlaneY * qy + clipPlaneZ * qz + clipPlaneW * qw;
                if (Math.abs(dotCQ) < 1e-6) return;
                const scale = 1.0 / dotCQ;
                pm[2] = clipPlaneX * scale;
                pm[6] = clipPlaneY * scale;
                pm[10] = clipPlaneZ * scale;
                pm[14] = clipPlaneW * scale;
            };
        };

        app.on('prerender', onPrerender);

        // Store cleanup callback
        mirrorPrerenderCleanups.set(id, () => {
            app.off('prerender', onPrerender);
        });

        sceneContent.addChild(entity);
        mirrorPlaneEntities.push(entity);

        console.log(`[StorySplat Viewer] Created mirror plane: ${data.name || id} (resolution: ${resolution}, intensity: ${intensity})`);
        return entity;
    }

    // ── Gsplat mirror clip plane helpers ─────────────────────────────
    // Ensure the gsplat material has the mirror clip shader chunk.
    // Called from each mirror's onPrerender so it retries until the gsplat material exists.
    // Re-checks every frame because effect scripts (relighting, reveal, etc.) may
    // delete/replace the gsplatModifyVS chunk when they are toggled on/off.
    function ensureMirrorClipShader(): void {
        const gsplatMat = (app.scene as any).gsplat?.material;
        if (!gsplatMat) return; // gsplat not loaded yet, will retry next frame
        const device = app.graphicsDevice;
        const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
        const chunks = gsplatMat.getShaderChunks(lang);
        if (!chunks.has('gsplatModifyVS')) {
            chunks.set('gsplatModifyVS', lang === 'wgsl' ? mirrorClipShaderWGSL : mirrorClipShaderGLSL);
            gsplatMat.update();
        }
        if (!_mirrorClipShaderApplied) {
            // Initialize the device-scope uniform to disabled (only once)
            device.scope.resolve('uMirrorClipPlane').setValue(_mirrorClipPlaneOff);
            _mirrorClipShaderApplied = true;
        }
    }

    // Wrap the main camera's calculateProjection to clear the gsplat clip plane
    // before the main camera renders. This ensures gsplats render normally for the
    // main camera (reflection cameras set the clip in their own calculateProjection).
    function ensureMainCameraClipClear(): void {
        if (_mainCameraCalcProjWrapped || !camera.camera) return;
        _mainCameraCalcProjWrapped = true;

        const mainCam = camera.camera!;
        const projectionHook = mainCam as CameraComponentProjectionHook;
        const origCalcProj = projectionHook.calculateProjection;
        _mainCameraWrapInfo = { cam: projectionHook, orig: origCalcProj };

        projectionHook.calculateProjection = (projMatrix: pc.Mat4, view: number) => {
            if (origCalcProj) {
                origCalcProj(projMatrix, view);
            } else {
                // Standard perspective projection
                const aspect = app.graphicsDevice.width / app.graphicsDevice.height;
                projMatrix.setPerspective(mainCam.fov, aspect, mainCam.nearClip, mainCam.farClip);
            }

            // Clear the gsplat clip plane via device scope so the main camera
            // renders without clipping (same mechanism as the SET in reflection cameras)
            app.graphicsDevice.scope.resolve('uMirrorClipPlane').setValue(_mirrorClipPlaneOff);
        };
    }

    // Restore the patched camera's original calculateProjection. Restoring the
    // exact camera we wrapped (not the current camera.camera, which may have been
    // swapped) keeps this safe even after a camera-mode change.
    function unwrapMainCameraClipClear(): void {
        if (_mainCameraWrapInfo) {
            _mainCameraWrapInfo.cam.calculateProjection = _mainCameraWrapInfo.orig;
            _mainCameraWrapInfo = null;
        }
        _mainCameraCalcProjWrapped = false;
    }

    function createMirrorPlanes(): void {
        if (!config.mirrorPlanes || config.mirrorPlanes.length === 0) return;
        console.log(`[StorySplat Viewer] Creating ${config.mirrorPlanes.length} mirror plane(s)...`);
        config.mirrorPlanes.forEach((mp, index) => {
            const entity = createSingleMirrorPlane(mp, index);
            // Register in editor map so the sync effect doesn't create duplicates
            if (isEditorMode && mp.id) {
                editorMirrorPlaneMap.set(mp.id, entity);
            }
        });
    }

    function destroySingleMirrorPlane(id: string): void {
        const cleanup = mirrorPrerenderCleanups.get(id);
        if (cleanup) {
            cleanup();
            mirrorPrerenderCleanups.delete(id);
        }
        const entity = (editorMirrorPlaneMap.get(id) || mirrorPlaneEntities.find(e => e.name === id)) as MirrorPlaneRuntimeEntity | undefined;
        if (entity) {
            const reflCam = entity._mirrorReflCam;
            const rt = entity._mirrorRenderTarget;
            const tex = entity._mirrorReflTexture;
            const mat = entity._mirrorMaterial;
            const customMesh = entity._mirrorCustomMesh;
            if (reflCam) {
                // PlayCanvas' gsplat director keeps this camera's sort data until its next
                // composition update (during render), and the streaming pass before it reads the
                // camera's fov: destroying the entity right away threw there (camera component
                // gone) and dropped a frame. Take it out of the composition now and destroy it
                // once a frame has rendered without it.
                reflCam.enabled = false;
                app.once('postrender', () => reflCam.destroy());
                app.renderNextFrame = true;
            }
            if (rt) rt.destroy();
            if (tex) tex.destroy();
            if (mat) mat.destroy();
            if (customMesh) customMesh.destroy();
            entity.destroy();
            editorMirrorPlaneMap.delete(id);
            const idx = mirrorPlaneEntities.indexOf(entity);
            if (idx >= 0) mirrorPlaneEntities.splice(idx, 1);
        }
    }

    // =====================================================
    // WATER PLANE SYSTEM (Animated water surface for pools/ponds)
    // =====================================================
    // The water itself (shader, tiers, reflections, refraction) lives in effects/water.
    // `?waterQuality=high|low|opaque` forces a tier (diagnostics, like `?render=always`).
    const waterQualityOverride = (() => {
        try {
            const value = new URLSearchParams(window.location.search).get('waterQuality');
            return value === 'high' || value === 'low' || value === 'opaque' ? value : null;
        } catch {
            return null;
        }
    })();
    const waterModelBoxes: pc.BoundingBox[] = [];
    const waterSystem = new WaterSystem({
        app,
        camera,
        sceneContent,
        opaqueLayer: mirrorMeshLayer,
        translucentLayer: translucentEntityLayer,
        qualityInputs: () => ({
            mobile: usesMobileRenderProfile() || config.forceMobilePreview === true,
            performanceMode,
            xr: isInXR,
            forced: waterQualityOverride,
        }),
        cameraFrame: () => cameraFrame,
        ensureSplatClip: () => {
            ensureMirrorClipShader();
            ensureMainCameraClipClear();
        },
        requestFrame: (reason) => renderScheduler.requestFrame(reason),
        // the opaque models (HotspotMeshLayer; translucent ones write no depth): one standing in
        // the water makes it take the scene depth map
        modelBounds: () => {
            waterModelBoxes.length = 0;
            customMeshEntities.forEach((meshData) => {
                if (!meshData.entity.enabled) return;
                for (const render of meshData.entity.findComponents('render') as pc.RenderComponent[]) {
                    if (!render.enabled || !render.entity.enabled || !render.layers.includes(hotspotMeshLayer.id)) continue;
                    for (const mi of render.meshInstances) waterModelBoxes.push(mi.aabb);
                }
            });
            return waterModelBoxes;
        },
        toPlayCanvasPosition: (value) => playCanvasPositionFromSceneVec3(value),
        finiteVec3: (value, fallback) => finiteSceneVec3(value, fallback),
    });
    waterNeedsSplatDepth = () => waterSystem.needsSplatDepth();
    // Water animates only while it is on screen: on-demand rendering idles otherwise.
    renderScheduler.addSource({ name: 'water', active: () => waterSystem.isAnimatingOnScreen() });

    function createSingleWaterPlane(data: WaterPlaneData, index: number): pc.Entity {
        const entity = waterSystem.add(data, index);
        console.log(`[StorySplat Viewer] Created water plane: ${data.name || entity.name} (${waterSystem.qualityTier} quality)`);
        return entity;
    }

    function createWaterPlanes(): void {
        if (!waterEffectsEnabled) return;
        if (!config.waterPlanes || config.waterPlanes.length === 0) return;
        console.log(`[StorySplat Viewer] Creating ${config.waterPlanes.length} water plane(s)...`);
        config.waterPlanes.forEach((wp, index) => {
            const entity = createSingleWaterPlane(wp, index);
            if (isEditorMode && wp.id) {
                editorWaterPlaneMap.set(wp.id, entity);
            }
        });
    }

    function destroySingleWaterPlane(id: string): void {
        waterSystem.remove(id);
        editorWaterPlaneMap.delete(id);
    }

    // =====================================================
    // CLOUD LAYERS (procedural, animated gaussian-splat clouds)
    // =====================================================
    // Generation, animation, lighting and cloud shadows live in effects/clouds. The clouds are
    // unified splats, so they sort and blend with the scan. `?cloudQuality=high|medium|low` forces
    // a tier (diagnostics).
    const cloudQualityOverride = (() => {
        try {
            const value = new URLSearchParams(window.location.search).get('cloudQuality');
            return value === 'high' || value === 'medium' || value === 'low' ? value : null;
        } catch {
            return null;
        }
    })();
    const cloudSystem = new CloudSystem({
        app,
        camera,
        sceneContent,
        qualityInputs: () => ({
            mobile: usesMobileRenderProfile() || config.forceMobilePreview === true,
            performanceMode,
            xr: isInXR,
            forced: cloudQualityOverride,
        }),
        requestFrame: (reason) => renderScheduler.requestFrame(reason),
        relighting: () => ({
            enabled: relightingEnabled && config.splatRelighting?.enabled === true,
            fade: relightingScript?.relightFade ?? 0,
        }),
        toPlayCanvasPosition: (value) => playCanvasPositionFromSceneVec3(value),
        finiteVec3: (value, fallback) => finiteSceneVec3(value, fallback),
        // The editor regenerates once edits settle (sliders and gizmo drags stay smooth).
        regenerateDelayMs: isEditorMode ? 180 : undefined,
        // Cloud shadows also land on models that receive shadows and on the shadow ground plane,
        // and dim the water's sun glint (only while some layer casts shadows).
        meshReceivers: () => {
            const roots: pc.Entity[] = [];
            customMeshEntities.forEach((meshData) => {
                if (meshData.config.receiveShadows !== false && meshData.entity) roots.push(meshData.entity);
            });
            // the shadow ground plane (effects/shadows/shadowGroundPlane.ts), while it is shown
            const ground = sceneContent.findByName('ShadowGroundPlane') as pc.Entity | null;
            if (ground?.enabled) roots.push(ground);
            return roots;
        },
        onMeshShadows: (on) => waterSystem.setCloudShadows(on),
        // Layers with followSceneWind drift with the scene wind (effects/wind).
        sceneWind: () => windSystem.current(),
    });
    // Clouds animate only while they are on screen: on-demand rendering idles otherwise.
    renderScheduler.addSource({ name: 'clouds', active: () => cloudSystem.isAnimatingOnScreen() });

    // Splats in front of water (effects/water/waterSplatSplit.ts): while water is on screen the
    // splat draw is split at the water, so whatever stands in front of it (the scan's own soft
    // parts, such as a canopy over a pond, clouds, splat weather) blends over it with its real
    // alpha, as splats do over a mirror. Installed only while the scene has water.
    if (camera.camera) {
        waterSplatSplit = new WaterSplatSplit({
            app,
            camera,
            worldLayer: app.scene.layers.getLayerById(pc.LAYERID_WORLD)!,
            waterLayer: waterSystem.layer,
            planes: () => waterSystem.splitPlanes(),
            wanted: () => waterSystem.size > 0,
            // CPU sort (WebGL2): the water's depth map culls with the shared splat vertex clip,
            // and the split runs only within a splat budget (halved on phones / in performance mode)
            ensureVertexClip: () => {
                ensureMirrorClipShader();
                ensureMainCameraClipClear();
            },
            lowPower: () => usesMobileRenderProfile() || config.forceMobilePreview === true || performanceMode,
            cpuSortSplatLimit: getPerfOverrides().waterSplitCpuSortSplatLimit,
            cpuSortMinScreenFraction: getPerfOverrides().waterSplitCpuSortMinScreenFraction,
        });
    }

    function createSingleCloudLayer(data: CloudLayerData, index: number): pc.Entity {
        const entity = cloudSystem.add(data, index);
        console.log(`[StorySplat Viewer] Created cloud layer: ${data.name || entity.name} (${cloudSystem.qualityTier} quality)`);
        return entity;
    }

    function createCloudLayers(): void {
        if (!Array.isArray(config.cloudLayers) || config.cloudLayers.length === 0) return;
        // a layer with a keyframed size generates for the largest size its keyframes reach
        cloudSystem.setAnimations(config.entityAnimations);
        console.log(`[StorySplat Viewer] Creating ${config.cloudLayers.length} cloud layer(s)...`);
        config.cloudLayers.forEach((layer, index) => {
            if (!layer || typeof layer !== 'object') return;
            const entity = createSingleCloudLayer(layer, index);
            if (isEditorMode && layer.id) editorCloudLayerMap.set(layer.id, entity);
        });
    }

    function destroySingleCloudLayer(id: string): void {
        cloudSystem.remove(id);
        editorCloudLayerMap.delete(id);
    }

    // The scan's baked sky (effects/clouds/scanSky.ts): its density grid keeps clouds out of the
    // scan (clearance), and 'replace' fades its sky splats so clouds show in front of the skybox
    // or a procedural sky backdrop.
    let scanSkyCenter: pc.Vec3 | null = null;
    const scanSky = new ScanSkyController({
        app,
        scanEntities: () => (splatEntity?.gsplat ? [splatEntity] : []),
        defaultCenter: () => scanSkyCenter ?? camera.getPosition(),
        toPlayCanvasPosition: (value) => playCanvasPositionFromSceneVec3(value),
        requestFrame: (reason) => renderScheduler.requestFrame(reason),
    });
    scanSky.setSettings(config.scanSky);
    // `?scanSkyPreview=1` tints what 'replace' removes (magenta) instead of removing it (diagnostics)
    try {
        if (new URLSearchParams(window.location.search).get('scanSkyPreview') === '1') scanSky.setPreview(true);
    } catch { /* no window.location (tests) */ }
    const proceduralSky = new ProceduralSky(app, camera);
    let proceduralSkyShown = false;
    app.on('update', () => {
        if (!splatEntity) return;
        // the capture point defaults to where the camera started
        if (!scanSkyCenter) scanSkyCenter = camera.getPosition().clone();
        // A scene without clouds, a replaced sky or the preview does none of this: no density
        // grid, no texture, no modifier on the scan, no backdrop, no frames.
        const clouds = cloudSystem.layerCount > 0;
        scanSky.update(clouds);
        if (clouds) cloudSystem.setScanGrid(scanSky.binding());
        const s = scanSky.resolved;
        const replace = s.mode === 'replace' && s.strength > 0;
        if (!replace && !proceduralSkyShown) return;
        const userSky = !!(app.scene.skybox || currentSkyboxEntity);
        proceduralSkyShown = replace && !userSky && s.backdrop !== 'none' && !splatEditActive;
        proceduralSky.set(proceduralSkyShown ? s.backdrop : null, cloudSystem.sunDirection());
    });
    app.on('prerender', () => proceduralSky.follow());

    // Splat Edit draws the raw scan on its own layer, before World's splats: clouds and weather
    // (splats too) would paint over it, so they are hidden while a session is open, and so is the
    // procedural sky backdrop (above). Splat models stay: the edit renderer writes the scan's
    // depth for them (EditRenderer.depthLayer).
    app.on(SPLAT_EDIT_ACTIVE_EVENT, (active: boolean) => {
        splatEditActive = !!active;
        cloudSystem.setSuspended(splatEditActive);
        const weather = weatherInstance?.entity as (pc.Entity & { __splatEditHidden?: boolean; __panoramaHidden?: boolean }) | undefined;
        if (weather && splatEditActive && weather.enabled) {
            weather.enabled = false;
            weather.__splatEditHidden = true;
        } else if (weather && !splatEditActive && weather.__splatEditHidden) {
            delete weather.__splatEditHidden;
            // still inside a 360° panorama: its exit brings the weather back
            if (panoramaModeEnabled) weather.__panoramaHidden = true;
            else weather.enabled = true;
        }
        renderScheduler.requestFrame('splat-edit');
    });

    /** A new cloud layer for this scan: above its objects; inside its baked sky unless that is replaced. */
    function suggestCloudLayer(preset: CloudLayerData['preset'] = 'cumulus'): Pick<CloudLayerData, 'area' | 'position' | 'rotation' | 'scale' | 'cloudScale'> {
        scanSky.demand();
        const shell = scanSky.shell();
        const toScene = (x: number, y: number, z: number) => ({ x: round2(x), y: round2(y), z: round2(-z) });
        const grid = scanSky.binding();
        if (shell && scanSky.resolved.mode === 'keep' && shell.shellDistance && preset !== 'fog') {
            // inside the baked sky: a dome over the capture point, above the objects, well short of the shell
            const d = shell.shellDistance;
            const altitude = Math.max(shell.denseTop + d * 0.12, shell.ground + d * 0.42);
            const thickness = d * (preset === 'storm' ? 0.3 : preset === 'cirrus' ? 0.06 : 0.12);
            return {
                area: 'dome',
                position: toScene(shell.center[0], Math.min(altitude + thickness / 2, shell.ground + d * 0.7), shell.center[2]),
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: round2(d * 1.6), y: round2(thickness), z: round2(d * 1.6) },
                cloudScale: round2(d * (preset === 'overcast' ? 0.3 : preset === 'cirrus' ? 0.25 : preset === 'storm' ? 0.22 : 0.14)),
            };
        }
        // a box over the scan's central content, or (sky replaced / no baked sky) a real sky height
        const min = grid ? grid.origin : [-20, 0, -20];
        const max = grid ? [grid.origin[0] + grid.dims[0] * grid.cell, grid.origin[1] + grid.dims[1] * grid.cell, grid.origin[2] + grid.dims[2] * grid.cell] : [20, 10, 20];
        const top = shell ? Math.max(shell.denseTop, shell.ground) : max[1];
        const bounds = { min: { x: min[0], y: shell?.ground ?? min[1], z: -max[2] }, max: { x: max[0], y: top, z: -min[2] } };
        const layer = defaultCloudLayerFor(bounds, preset ?? 'cumulus');
        return { area: 'box', ...layer };
    }
    function round2(v: number): number {
        return Math.round(v * 100) / 100;
    }

    // =====================================================
    // PORTAL SYSTEM (Scene-to-Scene Navigation)
    // =====================================================
    // Portal confirmation popup (optional)
    const portalPopupEl = (uiElements as UIElementsWithPortalPopup).portalPopup;
    let pendingPortal: PortalData | null = null;
    const hidePortalPopup = () => {
        if (!portalPopupEl)
            return;
        portalPopupEl.classList.remove('visible');
        pendingPortal = null;
    };
    const showPortalPopup = (portal: PortalData) => {
        if (!portalPopupEl)
            return;
        const titleEl = portalPopupEl.querySelector('.storysplat-portal-popup-title') as HTMLElement | null;
        if (titleEl) {
            if (portal.title) {
                titleEl.textContent = portal.title;
            }
            else if (portal.targetSceneName) {
                titleEl.textContent = `${getButtonLabel(currentButtonLabels, 'switchScenes').replace('?', '')} ${portal.targetSceneName}?`;
            }
            else {
                titleEl.textContent = getButtonLabel(currentButtonLabels, 'switchScenes');
            }
        }
        pendingPortal = portal;
        portalPopupEl.classList.add('visible');
    };
    if (portalPopupEl) {
        const confirmBtn = portalPopupEl.querySelector('.storysplat-portal-popup-confirm') as HTMLElement | null;
        const cancelBtn = portalPopupEl.querySelector('.storysplat-portal-popup-cancel') as HTMLElement | null;
        confirmBtn?.addEventListener('click', () => {
            if (pendingPortal) {
                const portal = pendingPortal;
                hidePortalPopup();
                handlePortalNavigation(portal);
            }
        });
        cancelBtn?.addEventListener('click', () => {
            hidePortalPopup();
        });
    }
    // Parse hex color to PlayCanvas Color
    function parseColor(hex: string): pc.Color {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        return new pc.Color(r, g, b);
    }
    // =====================================================
    // SPATIAL AUDIO SYSTEM
    // =====================================================
    // HotspotAudioElements removed — hotspot audio now uses PlayCanvas sound slots
    interface VideoSpatialAudio {
        audioCtx: AudioContext;
        source: MediaElementAudioSourceNode;
        panner: PannerNode;
    }
    interface WaypointAudioData {
        entity: pc.Entity;
        waypointIndex: number;
        config: LegacyWaypointAudioConfig;
        slotId: string;
        playing: boolean;
        // Now unused: the autoplay decision was moved inline into the
        // `audioAsset.ready()` callback so it fires exactly once when the
        // asset finishes loading. The field is kept on the interface (and
        // initialized to false) only to avoid a public-shape change while
        // we settle on the new audio rebuild flow.
        autoplayTriggered: boolean;
        assetReady: boolean;
        asset?: pc.Asset;
        // Tracks whether the camera was inside the proximity trigger radius last
        // frame. Without this, a non-looping clip that finishes while the camera
        // is still in range gets re-triggered on the next frame (audioData.playing
        // flips back to false) — i.e. it loops even though loop=false.
        inProximity?: boolean;
    }
    // Track all audio for muting
    const allAudioContexts: AudioContext[] = [];
    const allAudioElements: HTMLAudioElement[] = [];
    // Carry the mute state across portal teleports: the cross-scene ambient bed survives viewer
    // recreation and keeps its own muted flag, so if the user muted in a prior scene, start this
    // viewer muted too (otherwise the mute button would reset to "unmuted" while the bed stays muted).
    let globalMuted = crossSceneAudio.isActive() ? crossSceneAudio.isMuted() : false;
    const storedVolumes = new Map<HTMLAudioElement, number>();

    function trackSlotPlaybackState(
        slot: pc.SoundSlot | undefined,
        setPlaying: (playing: boolean) => void
    ): void {
        if (!slot) {
            return;
        }
        const component = slot._component;
        slot.on('play', (instance: pc.SoundInstance) => {
            setPlaying(true);
            audioReactiveInstance?.trackPlayCanvasInstance(instance, component);
        });
        slot.on('resume', (instance: pc.SoundInstance) => {
            setPlaying(true);
            audioReactiveInstance?.trackPlayCanvasInstance(instance, component);
        });
        slot.on('pause', (instance: pc.SoundInstance) => {
            setPlaying(false);
            audioReactiveInstance?.untrackPlayCanvasInstance(instance);
        });
        slot.on('stop', (instance: pc.SoundInstance) => {
            setPlaying(false);
            audioReactiveInstance?.untrackPlayCanvasInstance(instance);
        });
        slot.on('end', (instance: pc.SoundInstance) => {
            setPlaying(false);
            audioReactiveInstance?.untrackPlayCanvasInstance(instance);
        });
    }

    // ── Guided Narration System ──────────────────────────────
    let narrationAudio: HTMLAudioElement | null = null;
    let narrationMusicAudio: HTMLAudioElement | null = null;
    let narrationSegments = config.narrationTrack?.segments || [];
    let narrationEnabled = hasNarration;

    function cleanupNarrationAudio(): void {
        // Clean up existing
        if (narrationAudio) {
            narrationAudio.pause();
            narrationAudio.src = '';
            const idx = allAudioElements.indexOf(narrationAudio);
            if (idx >= 0) allAudioElements.splice(idx, 1);
            storedVolumes.delete(narrationAudio);
            narrationAudio = null;
        }
        if (narrationMusicAudio) {
            narrationMusicAudio.pause();
            narrationMusicAudio.src = '';
            const idx = allAudioElements.indexOf(narrationMusicAudio);
            if (idx >= 0) allAudioElements.splice(idx, 1);
            storedVolumes.delete(narrationMusicAudio);
            narrationMusicAudio = null;
        }
    }

    function initNarrationAudio(trackConfig: { audioUrl: string; volume?: number; backgroundMusicUrl?: string; backgroundMusicVolume?: number; segments: Array<{ id: string; audioStart: number; audioEnd: number; progressStart: number; progressEnd: number }> ; enabled: boolean }) {
        cleanupNarrationAudio();
        narrationSegments = trackConfig.segments || [];
        narrationEnabled = trackConfig.enabled && !!trackConfig.audioUrl;
        if (narrationEnabled && trackConfig.audioUrl) {
            narrationAudio = new Audio();
            // CORS-enable only when the audio-reactive effect needs to analyse this element —
            // a tainted cross-origin element reads as silence through createMediaElementSource,
            // but forcing crossOrigin on non-CORS servers would break playback entirely.
            if (effectiveAudioReactiveConfig?.enabled) narrationAudio.crossOrigin = 'anonymous';
            narrationAudio.src = trackConfig.audioUrl;
            narrationAudio.preload = 'auto';
            narrationAudio.volume = trackConfig.volume ?? 1;
            allAudioElements.push(narrationAudio);
            storedVolumes.set(narrationAudio, narrationAudio.volume);

            if (trackConfig.backgroundMusicUrl) {
                narrationMusicAudio = new Audio();
                if (effectiveAudioReactiveConfig?.enabled) narrationMusicAudio.crossOrigin = 'anonymous';
                narrationMusicAudio.src = trackConfig.backgroundMusicUrl;
                narrationMusicAudio.preload = 'auto';
                narrationMusicAudio.loop = true;
                narrationMusicAudio.volume = trackConfig.backgroundMusicVolume ?? 0.18;
                allAudioElements.push(narrationMusicAudio);
                storedVolumes.set(narrationMusicAudio, narrationMusicAudio.volume);
            }
        }
    }

    // Initialize from config at startup
    if (narrationEnabled && config.narrationTrack) {
        initNarrationAudio(config.narrationTrack as Parameters<typeof initNarrationAudio>[0]);
    }

    // ── Audio-Reactive Splat Effect ──────────────────────────
    // The splat pulses/ripples/warps/hue-cycles to the scene audio (or mic).
    // Plain per-frame driver (weather pattern): resolves the unified splat
    // material each frame, so it survives splat swaps and scene transitions.
    // NOTE: declared after the narration `let`s above — getAudioElements closes over them.
    let audioReactiveInstance: GsplatAudioReactive | null = null;
    const applyAudioReactive = (arConfig: AudioReactiveConfig | undefined) => {
        if (!arConfig?.enabled) {
            if (audioReactiveInstance) {
                audioReactiveInstance.destroy();
                audioReactiveInstance = null;
            }
            return;
        }
        if (!audioReactiveInstance) {
            audioReactiveInstance = new GsplatAudioReactive(app, {
                getSplatEntity: () => splatEntity,
                getAudioElements: () => [crossSceneAudio.getElement(), narrationAudio, narrationMusicAudio],
                getAdditionalSpatialAudioSources: () => hotspotEntities.flatMap((entity) => {
                    const spatial = entity.videoSpatialAudio;
                    const video = entity.videoElement;
                    if (!spatial || !video || video.paused || video.ended || video.muted || !entity.enabled) return [];
                    return [{
                        node: spatial.panner,
                        context: spatial.audioCtx,
                        entity,
                        radius: spatial.panner.maxDistance,
                    }];
                }),
            });
        }
        audioReactiveInstance.applyConfig(arConfig);
    };
    const audioReactiveUpdateHandler = (dt: number) => {
        if (audioReactiveInstance && !panoramaModeEnabled) audioReactiveInstance.update(dt);
    };
    app.on('update', audioReactiveUpdateHandler);
    if (effectiveAudioReactiveConfig?.enabled) {
        applyAudioReactive(effectiveAudioReactiveConfig);
    }
    if (uiElements.microphoneButton) {
        const cleanupMicrophoneControl = bindViewerMicrophoneControl(
            uiElements.microphoneButton,
            audioAnalyserBus,
        );
        const previousUiCleanup = uiElements._cleanup;
        uiElements._cleanup = () => {
            cleanupMicrophoneControl();
            if (previousUiCleanup) previousUiCleanup();
        };
    }

    // Cross-scene ambient audio bed — persists across portal teleports via the module-scope
    // crossSceneAudio singleton (which survives viewer recreation). Idempotent: an entry scene's bed
    // keeps playing seamlessly through teleports; a scene that omits it leaves the current bed alone;
    // a scene with a different url crossfades to it. Starts on the first user gesture (autoplay rules).
    if (config.crossSceneAudio?.enabled && config.crossSceneAudio.url) {
        crossSceneAudio.ensure(config.crossSceneAudio.url, {
            volume: config.crossSceneAudio.volume,
            loop: config.crossSceneAudio.loop,
            fade: config.crossSceneAudio.fade,
        });
        crossSceneAudio.setMuted(globalMuted);
    }

    /**
     * Get the audio time and play state for a given tour progress (0-1).
     * Segments use 0-100 range internally, so we convert.
     */
    function getNarrationStateForProgress(progress: number): { time: number; shouldPlay: boolean; volume: number } {
        const progress100 = progress * 100;
        const segment = narrationSegments.find(
            s => progress100 >= s.progressStart && progress100 <= s.progressEnd
        );
        if (!segment) return { time: 0, shouldPlay: false, volume: 1 };
        const progressRange = segment.progressEnd - segment.progressStart;
        if (progressRange <= 0) return { time: segment.audioStart, shouldPlay: true, volume: (segment as any).volume ?? 1 };
        const ratio = (progress100 - segment.progressStart) / progressRange;
        const time = segment.audioStart + ratio * (segment.audioEnd - segment.audioStart);
        return { time, shouldPlay: true, volume: (segment as any).volume ?? 1 };
    }

    /**
     * Get the effective playback speed when narration drives the camera.
     * Returns the narration-adjusted speed, or the base speed if outside segments.
     */
    function getNarrationPlaybackSpeed(progress: number, baseSpeed: number): number {
        if (!narrationEnabled || !narrationSegments.length) return baseSpeed;
        const progress100 = progress * 100;
        const segment = narrationSegments.find(
            s => progress100 >= s.progressStart && progress100 <= s.progressEnd
        );
        if (!segment) return baseSpeed; // Gap between segments = base speed
        const progressRange = (segment.progressEnd - segment.progressStart) / 100; // 0-1 scale
        const audioDuration = segment.audioEnd - segment.audioStart;
        if (audioDuration <= 0) return baseSpeed;
        // Speed = progress per second to traverse this segment in exactly audioDuration seconds
        return progressRange / audioDuration;
    }

    /**
     * Sync narration audio to current tour progress.
     * Called on each progressUpdate event.
     */
    function syncNarrationToProgress(progress: number): void {
        if (!narrationAudio || !narrationEnabled) return;
        // Only play narration when the tour is actively playing (autoplay)
        // Don't play during manual scroll, orbit, fly, or walk modes
        if (!isPlaying || currentCameraMode !== 'tour') {
            if (!narrationAudio.paused) narrationAudio.pause();
            if (narrationMusicAudio && !narrationMusicAudio.paused) narrationMusicAudio.pause();
            return;
        }
        const { time, shouldPlay, volume } = getNarrationStateForProgress(progress);
        if (!shouldPlay) {
            if (!narrationAudio.paused) narrationAudio.pause();
            if (narrationMusicAudio && !narrationMusicAudio.paused) narrationMusicAudio.pause();
            return;
        }
        // Apply per-segment volume (multiplied by track volume)
        const trackVolume = storedVolumes.get(narrationAudio) ?? 1;
        narrationAudio.volume = globalMuted ? 0 : trackVolume * volume;

        if (narrationAudio.paused) {
            // Starting playback — seek to correct position then play
            narrationAudio.currentTime = time;
            if (!globalMuted) {
                narrationAudio.play().catch(() => {});
            }
        }
        if (narrationMusicAudio && narrationMusicAudio.paused && !globalMuted) {
            narrationMusicAudio.play().catch(() => {});
        }
        // When audio is already playing, do NOT seek on every frame.
        // The playbackLoop derives progress FROM audio.currentTime (audio-as-master-clock),
        // so the two stay in sync without seeking. Only seek if drift is extreme (e.g. after
        // a manual scrub or segment transition), using a generous threshold to avoid choppy
        // audio from frequent seeks on mobile.
        else {
            const drift = Math.abs(narrationAudio.currentTime - time);
            if (drift > 0.5) {
                narrationAudio.currentTime = time;
            }
        }
    }

    // Waypoint audio tracking
    const waypointAudioMap = new Map<string, WaypointAudioData>();
    /**
     * Stop all hotspot audio (spatial and non-spatial) and pause any popup media.
     * Called on waypoint transitions and when closing the hotspot popup.
     */
    function stopAllHotspotAudio(): void {
        hotspotEntities.forEach((entity) => {
            const slotId = (entity as ViewerRuntimeEntity).hotspotAudioSlotId;
            if (slotId) {
                // Skip autoplay audio hotspots — they should keep playing across waypoints
                const hd = (entity as ViewerRuntimeEntity).hotspotData;
                if (hd?.audioTriggerMode === 'autoplay') return;
                const slot = entity.sound?.slot(slotId);
                if (slot && (slot.isPlaying || slot.isPaused)) {
                    slot.stop();
                    (entity as ViewerRuntimeEntity).hotspotAudioPlaying = false;
                }
            }
            // Reset trigger state so proximity/hover/autoplay hotspots can re-trigger
            (entity as ViewerRuntimeEntity).wasInProximity = false;
            (entity as ViewerRuntimeEntity)._audioHoverPlaying = false;
            // Also stop any video hotspots that are playing (except autoplay videos which play forever)
            if (entity.videoElement) {
                if (entity.mediaTriggerMode === 'autoplay') {
                    // Autoplay videos play forever — never auto-pause
                } else {
                    const video = entity.videoElement as HTMLVideoElement;
                    if (!video.paused) {
                        video.pause();
                    }
                    // Reset isVideoPlaying so scroll/proximity can restart
                    entity.isVideoPlaying = false;
                }
            }
            // Reset proximity state so audio can re-trigger on re-entry
            entity.wasInProximity = false;
        });
    }
    /**
     * Stop any media playing inside the hotspot popup (videos, iframes).
     */
    function stopHotspotPopupMedia(containerEl: HTMLElement): void {
        const popup = containerEl.querySelector('.storysplat-hotspot-popup') as HTMLElement;
        if (!popup)
            return;
        // Pause any popup video elements
        popup.querySelectorAll('video').forEach(v => { v.pause(); v.currentTime = 0; });
        // Clear iframe src to stop YouTube embeds etc
        popup.querySelectorAll('iframe').forEach(iframe => { iframe.src = ''; });
        // Destroy model viewer mini app if present
        const modelCleanup = (popup as any).__modelViewerCleanup;
        if (modelCleanup) {
            modelCleanup();
            delete (popup as any).__modelViewerCleanup;
        }
    }
    // =====================================================
    // PARTICLE SYSTEM
    // =====================================================
    const particleEntities: Map<string, pc.Entity> = new Map();
    const particleTextures: Map<string, pc.Texture> = new Map();
    const BUILTIN_PARTICLE_TEXTURES = new Set(['flare', 'circle', 'spark', 'rain', 'smoke', 'snowflake']);
    /**
     * Draw a built-in particle shape onto a 2D canvas context. All textures are
     * authored with a proper RGBA alpha channel so they work under any blend mode.
     */
    function drawParticleShape(ctx: CanvasRenderingContext2D, name: string, size: number): void {
        switch (name) {
            case 'circle': {
                const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
                g.addColorStop(0, 'rgba(255,255,255,1)');
                g.addColorStop(0.5, 'rgba(255,255,255,0.5)');
                g.addColorStop(1, 'rgba(255,255,255,0)');
                ctx.fillStyle = g;
                ctx.fillRect(0, 0, size, size);
                return;
            }
            case 'flare': {
                const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
                g.addColorStop(0, 'rgba(255,255,255,1)');
                g.addColorStop(0.1, 'rgba(255,250,220,0.95)');
                g.addColorStop(0.35, 'rgba(255,200,110,0.5)');
                g.addColorStop(1, 'rgba(255,150,50,0)');
                ctx.fillStyle = g;
                ctx.fillRect(0, 0, size, size);
                return;
            }
            case 'spark': {
                ctx.translate(size / 2, size / 2);
                const core = ctx.createRadialGradient(0, 0, 0, 0, 0, size / 3);
                core.addColorStop(0, 'rgba(255,255,255,1)');
                core.addColorStop(0.25, 'rgba(255,255,255,0.55)');
                core.addColorStop(1, 'rgba(255,255,255,0)');
                ctx.fillStyle = core;
                ctx.fillRect(-size / 2, -size / 2, size, size);
                ctx.globalCompositeOperation = 'lighter';
                const streak = (rot: number, len: number, thick: number) => {
                    ctx.save();
                    ctx.rotate(rot);
                    const lg = ctx.createLinearGradient(-len, 0, len, 0);
                    lg.addColorStop(0, 'rgba(255,255,255,0)');
                    lg.addColorStop(0.5, 'rgba(255,255,255,1)');
                    lg.addColorStop(1, 'rgba(255,255,255,0)');
                    ctx.fillStyle = lg;
                    ctx.fillRect(-len, -thick / 2, len * 2, thick);
                    ctx.restore();
                };
                streak(0, size / 2, 2);
                streak(Math.PI / 2, size / 2, 2);
                streak(Math.PI / 4, size / 3, 1.5);
                streak(-Math.PI / 4, size / 3, 1.5);
                return;
            }
            case 'rain': {
                const lg = ctx.createLinearGradient(0, 0, 0, size);
                lg.addColorStop(0, 'rgba(210,230,255,0)');
                lg.addColorStop(0.5, 'rgba(225,240,255,1)');
                lg.addColorStop(1, 'rgba(210,230,255,0)');
                ctx.fillStyle = lg;
                const streakWidth = Math.max(4, size / 32);
                ctx.fillRect(size / 2 - streakWidth / 2, 0, streakWidth, size);
                ctx.globalCompositeOperation = 'destination-in';
                const hg = ctx.createLinearGradient(0, 0, size, 0);
                hg.addColorStop(0, 'rgba(255,255,255,0)');
                hg.addColorStop(0.5, 'rgba(255,255,255,1)');
                hg.addColorStop(1, 'rgba(255,255,255,0)');
                ctx.fillStyle = hg;
                ctx.fillRect(0, 0, size, size);
                return;
            }
            case 'smoke': {
                const base = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
                base.addColorStop(0, 'rgba(255,255,255,0.8)');
                base.addColorStop(0.3, 'rgba(255,255,255,0.4)');
                base.addColorStop(0.7, 'rgba(255,255,255,0.15)');
                base.addColorStop(1, 'rgba(255,255,255,0)');
                ctx.fillStyle = base;
                ctx.fillRect(0, 0, size, size);
                const puffs: Array<[number, number, number, number]> = [
                    [size * 0.4, size * 0.45, size * 0.28, 0.18],
                    [size * 0.6, size * 0.55, size * 0.24, 0.14],
                    [size * 0.5, size * 0.35, size * 0.18, 0.1]
                ];
                for (const [x, y, r, a] of puffs) {
                    const pg = ctx.createRadialGradient(x, y, 0, x, y, r);
                    pg.addColorStop(0, `rgba(255,255,255,${a})`);
                    pg.addColorStop(1, 'rgba(255,255,255,0)');
                    ctx.fillStyle = pg;
                    ctx.fillRect(0, 0, size, size);
                }
                return;
            }
            case 'snowflake': {
                ctx.translate(size / 2, size / 2);
                const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, size / 2);
                glow.addColorStop(0, 'rgba(255,255,255,0.35)');
                glow.addColorStop(0.5, 'rgba(255,255,255,0.08)');
                glow.addColorStop(1, 'rgba(255,255,255,0)');
                ctx.fillStyle = glow;
                ctx.fillRect(-size / 2, -size / 2, size, size);
                ctx.strokeStyle = 'rgba(255,255,255,0.95)';
                ctx.lineCap = 'round';
                const armLen = size * 0.42;
                for (let i = 0; i < 6; i++) {
                    ctx.save();
                    ctx.rotate((i * Math.PI) / 3);
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.lineTo(armLen, 0);
                    ctx.stroke();
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(armLen * 0.55, 0);
                    ctx.lineTo(armLen * 0.75, armLen * 0.22);
                    ctx.moveTo(armLen * 0.55, 0);
                    ctx.lineTo(armLen * 0.75, -armLen * 0.22);
                    ctx.stroke();
                    ctx.restore();
                }
                return;
            }
            default: {
                const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
                g.addColorStop(0, 'rgba(255,255,255,1)');
                g.addColorStop(1, 'rgba(255,255,255,0)');
                ctx.fillStyle = g;
                ctx.fillRect(0, 0, size, size);
            }
        }
    }
    /**
     * Build a procedural particle texture (cached).
     */
    function createProceduralParticleTexture(name: string): pc.Texture {
        const cached = particleTextures.get(name);
        if (cached) return cached;
        const size = 128;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get 2D canvas context for particle texture');
        drawParticleShape(ctx, name, size);
        const texture = new pc.Texture(app.graphicsDevice, {
            name: `particle-${name}`,
            width: size,
            height: size,
            format: pc.PIXELFORMAT_RGBA8,
            mipmaps: true,
            minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE
        });
        texture.setSource(canvas);
        particleTextures.set(name, texture);
        return texture;
    }
    /**
     * Load a custom particle texture from a URL (with caching).
     */
    function loadCustomParticleTexture(cacheKey: string, url: string): Promise<pc.Texture> {
        return new Promise((resolve, reject) => {
            const cached = particleTextures.get(cacheKey);
            if (cached) {
                resolve(cached);
                return;
            }
            const asset = new pc.Asset(cacheKey, 'texture', { url });
            asset.on('load', () => {
                if (isDestroyed) {
                    reject(new Error('Viewer destroyed'));
                    return;
                }
                const texture = asset.resource as pc.Texture;
                particleTextures.set(cacheKey, texture);
                resolve(texture);
            });
            asset.on('error', (err: unknown) => {
                console.error(`[Particle] Failed to load custom texture: ${cacheKey}`, err);
                reject(err);
            });
            app.assets.add(asset);
            app.assets.load(asset);
        });
    }
    /**
     * Create a particle system entity (matching HTML export createParticleSystem)
     * Enhanced with full feature parity: direction1/direction2, box emitter positioning,
     * scaleX/Y, colorDead, angular speed range, initial rotation range
     */
    function createParticleSystemEntity(psConfig: LegacyParticleConfig): pc.Entity {
        const entity = new pc.Entity(psConfig.name || 'particle-system');
        // Helper to safely get position coordinates
        const getPos = (vec: LegacyVec3 | undefined, defaultVal = 0) => ({
            x: vec?._x ?? vec?.x ?? defaultVal,
            y: vec?._y ?? vec?.y ?? defaultVal,
            z: vec?._z ?? vec?.z ?? defaultVal
        });
        // Helper to safely get color
        const getColor = (color: LegacyColor | undefined): Required<LegacyColor> => ({
            r: color?.r ?? 1,
            g: color?.g ?? 1,
            b: color?.b ?? 1,
            a: color?.a ?? 1
        });
        // Read emitterPosition (legacy BabylonJS format) or position (standard ParticleConfig format)
        const emitterPos = psConfig.emitterPosition
            ? getPos(psConfig.emitterPosition, 0)
            : { x: psConfig.position?.x ?? 0, y: psConfig.position?.y ?? 0, z: psConfig.position?.z ?? 0 };
        const gravity = getPos(psConfig.gravity, 0);
        const color1 = getColor(psConfig.color1);
        const color2 = getColor(psConfig.color2);
        const colorDead = getColor(psConfig.colorDead);
        // Get direction1/direction2 for directional emission (matching HTML export)
        const direction1 = getPos(psConfig.direction1, 0);
        const direction2 = getPos(psConfig.direction2, 0);
        // PlayCanvas only has a single `lifetime` property (no random range).
        // BabylonJS has minLifeTime/maxLifeTime. We use maxLifeTime as the particle lifespan.
        // The minLifeTime variance is lost — a known PlayCanvas limitation.
        const maxLifeTime = psConfig.maxLifeTime ?? 3.0;
        // Determine emitter shape and calculate extents
        let emitterShape: number = pc.EMITTERSHAPE_BOX;
        let emitterExtents = new pc.Vec3(0.1, 0.1, 0.1);
        let emitterOffset = new pc.Vec3(0, 0, 0);
        if (psConfig.emitterType === 'sphere' || psConfig.emitterShape === 'sphere') {
            emitterShape = pc.EMITTERSHAPE_SPHERE;
            const radius = psConfig.emitterRadius || 0.1;
            emitterExtents = new pc.Vec3(radius, radius, radius);
        }
        else if (psConfig.emitterShape === 'box' && psConfig.emitBoxMin && psConfig.emitBoxMax) {
            // Box emitter with min/max emit box (matching HTML export)
            emitterShape = pc.EMITTERSHAPE_BOX;
            const boxMin = getPos(psConfig.emitBoxMin, 0);
            const boxMax = getPos(psConfig.emitBoxMax, 0);
            // PlayCanvas emitterExtents = full box size (not half-extents)
            // BabylonJS minEmitBox/maxEmitBox define the min/max corners
            emitterExtents = new pc.Vec3(Math.abs(boxMax.x - boxMin.x), Math.abs(boxMax.y - boxMin.y), Math.abs(boxMax.z - boxMin.z));
            // Calculate center offset from emitter position
            emitterOffset = new pc.Vec3((boxMin.x + boxMax.x) / 2, (boxMin.y + boxMax.y) / 2, -(boxMin.z + boxMax.z) / 2 // Negate Z for coordinate conversion
            );
        }
        else if (psConfig.emitterShape === 'point') {
            // Point emitter - use very small extents
            emitterShape = pc.EMITTERSHAPE_BOX;
            emitterExtents = new pc.Vec3(0.01, 0.01, 0.01);
        }
        else {
            // Default: if emitterExtents are provided, use them. Otherwise treat as point emitter
            // (BabylonJS defaults to point emitter when emitterShape is undefined)
            emitterExtents = new pc.Vec3(
                psConfig.emitterExtents?.x || psConfig.emitterRadius || 0.01,
                psConfig.emitterExtents?.y || psConfig.emitterRadius || 0.01,
                psConfig.emitterExtents?.z || psConfig.emitterRadius || 0.01
            );
        }
        // Determine blend mode (matching HTML export blendModeMap)
        // Default to BLEND_ADDITIVE (src+dst) — makes black backgrounds transparent
        let blendMode: number = pc.BLEND_ADDITIVE;
        const blendModeStr = psConfig.blendMode || '';
        if (blendModeStr === 'BLENDMODE_STANDARD' || blendModeStr === 'normal' || blendModeStr === 'alpha') {
            blendMode = pc.BLEND_NORMAL;
        }
        else if (blendModeStr === 'BLENDMODE_MULTIPLY' || blendModeStr === 'multiply') {
            blendMode = pc.BLEND_MULTIPLICATIVE;
        }
        else if (blendModeStr === 'BLENDMODE_ONEONE' || blendModeStr === 'BLENDMODE_ADD') {
            // BabylonJS BLENDMODE_ADD = ONE,ONE (pure additive, no alpha weighting)
            blendMode = pc.BLEND_ADDITIVE;
        }
        else if (blendModeStr === 'BLENDMODE_MULTIPLYADD') {
            blendMode = pc.BLEND_ADDITIVEALPHA;
        }
        // Build world velocity graph with gravity
        const gravityX = gravity.x || 0;
        const gravityY = gravity.y || 0; // Don't default to -9.8, use config value
        const gravityZ = -(gravity.z || 0); // Negate Z for coordinate conversion
        // Calculate accumulated velocity at end of lifetime due to gravity
        const endVelX = gravityX * maxLifeTime;
        const endVelY = gravityY * maxLifeTime;
        const endVelZ = gravityZ * maxLifeTime;
        // Get angular speed range - convert from BabylonJS radians/sec to PlayCanvas degrees/sec
        const RAD2DEG = 180 / Math.PI;
        const minAngularSpeed = (psConfig.minAngularSpeed ?? 0) * RAD2DEG;
        const maxAngularSpeed = (psConfig.maxAngularSpeed ?? (psConfig.angularSpeed ?? 0)) * RAD2DEG;
        // Get initial rotation range - convert from BabylonJS radians to PlayCanvas degrees
        const minInitialRotation = (psConfig.minInitialRotation ?? 0) * RAD2DEG;
        const maxInitialRotation = (psConfig.maxInitialRotation ?? 0) * RAD2DEG;
        // Get scale ranges — apply 0.1x factor so editor values map to reasonable PlayCanvas sizes
        const PARTICLE_SIZE_FACTOR = 0.1;
        const minSize = (psConfig.minSize ?? 0.1) * PARTICLE_SIZE_FACTOR;
        const maxSize = (psConfig.maxSize ?? 0.5) * PARTICLE_SIZE_FACTOR;
        const minScaleX = psConfig.minScaleX ?? 1;
        const maxScaleX = psConfig.maxScaleX ?? 1;
        const minScaleY = psConfig.minScaleY ?? 1;
        const maxScaleY = psConfig.maxScaleY ?? 1;
        // Calculate emit power range
        const minEmitPower = psConfig.minEmitPower ?? 1;
        const maxEmitPower = psConfig.maxEmitPower ?? 2;
        // Build direction-based velocity using direction1/direction2 as min/max spread
        // BabylonJS: each particle picks random direction between direction1 and direction2
        // PlayCanvas: localVelocityGraph = min bounds, localVelocityGraph2 = max bounds
        const dir1X = direction1.x;
        const dir1Y = direction1.y;
        const dir1Z = -direction1.z; // Negate Z for coordinate conversion
        const dir2X = direction2.x;
        const dir2Y = direction2.y;
        const dir2Z = -direction2.z; // Negate Z for coordinate conversion
        const hasDirectionalVelocity = (dir1X !== 0 || dir1Y !== 0 || dir1Z !== 0 ||
            dir2X !== 0 || dir2Y !== 0 || dir2Z !== 0);
        const isSphereEmitter = emitterShape === pc.EMITTERSHAPE_SPHERE;
        // BabylonJS emitRate and ParticleConfig.rate are both particles-per-second.
        // PlayCanvas rate = seconds between births (inverse).
        const emitRate = psConfig.emitRate || psConfig.rate || 50;
        const pcRate = 1 / emitRate;
        const derivedNumParticles = Math.ceil(emitRate * maxLifeTime * 2);
        // Create particle system with enhanced parameters (full feature parity)
        entity.addComponent('particlesystem', {
            numParticles: psConfig.numParticles || Math.max(derivedNumParticles, 100),
            lifetime: maxLifeTime,
            rate: pcRate,
            emitterShape: emitterShape,
            emitterExtents: emitterExtents,
            emitterRadius: psConfig.emitterRadius || 0.1,
            // Initial rotation range (degrees)
            startAngle: minInitialRotation,
            startAngle2: maxInitialRotation,
            // Speed: sphere emitters use radial speed only (BabylonJS ignores direction1/direction2
            // for sphere emitters). Box/point emitters use directional velocity from direction vectors.
            ...(isSphereEmitter ? {
                radialSpeedGraph: new pc.Curve([0, minEmitPower]),
                radialSpeedGraph2: new pc.Curve([0, maxEmitPower])
            } : {
                localVelocityGraph: new pc.CurveSet([
                    [0, dir1X * minEmitPower],
                    [0, dir1Y * minEmitPower],
                    [0, dir1Z * minEmitPower]
                ]),
                localVelocityGraph2: new pc.CurveSet([
                    [0, dir2X * maxEmitPower],
                    [0, dir2Y * maxEmitPower],
                    [0, dir2Z * maxEmitPower]
                ])
            }),
            // World velocity (gravity effect over lifetime)
            velocityGraph: new pc.CurveSet([
                [0, 0, 1, endVelX],
                [0, 0, 1, endVelY],
                [0, 0, 1, endVelZ]
            ]),
            // Scale: BabylonJS uses random size * random scaleXY per particle.
            // PlayCanvas only supports uniform scale, so use min/max of both axes.
            scaleGraph: new pc.Curve([0, minSize * Math.min(minScaleX, minScaleY)]),
            scaleGraph2: new pc.Curve([0, maxSize * Math.max(maxScaleX, maxScaleY)]),
            // Rotation speed range (degrees/sec)
            rotationSpeedGraph: new pc.Curve([0, minAngularSpeed]),
            ...(maxAngularSpeed !== minAngularSpeed ? {
                rotationSpeedGraph2: new pc.Curve([0, maxAngularSpeed])
            } : {}),
            // Color over lifetime: BabylonJS interpolates color1→color2 over full lifetime,
            // colorDead snaps at death. Map color2 at t=0.95 with colorDead at t=1.0.
            colorGraph: new pc.CurveSet([
                [0, color1.r, 0.95, color2.r, 1, colorDead.r],
                [0, color1.g, 0.95, color2.g, 1, colorDead.g],
                [0, color1.b, 0.95, color2.b, 1, colorDead.b]
            ]),
            // Alpha over lifetime: color1.a → color2.a over lifetime, colorDead.a at death
            alphaGraph: new pc.Curve([
                0, color1.a ?? 1,
                0.95, color2.a ?? 1,
                1, colorDead.a ?? 0
            ]),
            // Rendering settings
            blendType: blendMode,
            depthWrite: psConfig.depthWrite ?? false,
            depthSoftening: psConfig.softParticles ?? 0,
            lighting: psConfig.lighting ?? false,
            halfLambert: psConfig.halfLambert ?? false,
            alignToMotion: psConfig.alignToMotion ?? false,
            stretch: psConfig.stretch || 0,
            preWarm: psConfig.preWarm ?? false,
            loop: psConfig.loop ?? true,
            autoPlay: psConfig.autoPlay ?? true,
            // Sorting and orientation
            sort: psConfig.sort ?? 0,
            orientation: psConfig.orientation ?? 0,
            // Dedicated Particles layer: after the splats, depth-tested against models, faded behind splats
            layers: [particleLayer.id]
        });
        // Set local space mode
        if (entity.particlesystem) {
            entity.particlesystem.localSpace = psConfig.localSpace ?? false;
        }
        // Drift with the scene wind (world velocity on top of gravity), see particleWind.
        if (psConfig.followSceneWind === true && entity.particlesystem) {
            particleWind.set(entity, { end: { x: endVelX, y: endVelY, z: endVelZ }, key: '0,0', at: -Infinity });
        }
        // Set position (negate Z for BabylonJS -> PlayCanvas coordinate conversion)
        // Add emitter offset for box emitter center positioning
        // Note: translationPivot is a BabylonJS texture rendering pivot (UV space), not a position offset
        entity.setPosition(emitterPos.x + emitterOffset.x, emitterPos.y + emitterOffset.y, -emitterPos.z + emitterOffset.z);
        // Apply renderingGroupId via layer assignment (BabylonJS uses 0-3 for z-ordering)
        // PlayCanvas uses layers - map renderingGroupId to appropriate layer
        const renderingGroupId = psConfig.renderingGroupId ?? 3;
        if (entity.particlesystem && renderingGroupId !== undefined) {
            // In PlayCanvas, higher drawOrder values render on top
            // Map BabylonJS renderingGroupId (0-3) to PlayCanvas draw order
            entity.particlesystem.drawOrder = renderingGroupId;
        }
        console.log(`[Particle] Entity configured at position: (${emitterPos.x + emitterOffset.x}, ${emitterPos.y + emitterOffset.y}, ${-emitterPos.z + emitterOffset.z})`);
        console.log(`[Particle] Emitter shape: ${psConfig.emitterShape || 'box'}, extents: ${emitterExtents.x}, ${emitterExtents.y}, ${emitterExtents.z}`);
        console.log(`[Particle] Gravity: (${gravityX}, ${gravityY}, ${gravityZ}), Lifetime: ${maxLifeTime}s`);
        console.log(`[Particle] Colors: start=${JSON.stringify(color1)}, mid=${JSON.stringify(color2)}, dead=${JSON.stringify(colorDead)}`);
        console.log(`[Particle] Angular speed: ${minAngularSpeed.toFixed(1)} - ${maxAngularSpeed.toFixed(1)} deg/s, Initial rotation: ${minInitialRotation.toFixed(1)} - ${maxInitialRotation.toFixed(1)} deg`);
        console.log(`[Particle] Scale: ${minSize}*${minScaleX} - ${maxSize}*${maxScaleY}, EmitPower: ${minEmitPower}-${maxEmitPower}, RenderingGroupId: ${renderingGroupId}`);
        console.log(`[Particle] Rate: ${emitRate} particles/sec → PC rate=${pcRate.toFixed(4)}s/particle, numParticles=${psConfig.numParticles || Math.max(derivedNumParticles, 100)}`);
        return entity;
    }
    /**
     * Initialize all particle systems from config
     */
    async function initParticleSystems(): Promise<void> {
        console.log('═══════════════════════════════════════');
        console.log('🎆 PARTICLE SYSTEM INITIALIZATION');
        console.log('═══════════════════════════════════════');
        console.log('[Particle] config.particles:', config.particles);
        console.log('[Particle] Type:', typeof config.particles);
        console.log('[Particle] Is Array:', Array.isArray(config.particles));
        if (!config.particles || config.particles.length === 0) {
            console.log('[Particle] ⚠️ No particle systems to create (particles array is empty or undefined)');
            console.log('═══════════════════════════════════════');
            return;
        }
        console.log(`[Particle] 📋 Found ${config.particles.length} particle system(s) to create`);
        for (let i = 0; i < config.particles.length; i++) {
            const ps: ParticleConfig = config.particles[i];
            console.log(`[Particle] --- Particle System ${i + 1}/${config.particles.length} ---`);
            console.log('[Particle] Raw config:', JSON.stringify(ps, null, 2));
            try {
                // Resolve texture: custom URL, built-in procedural, or fallback.
                const textureName = ps.particleTexture || 'flare';
                let texture: pc.Texture;
                if (textureName === 'custom' && ps.customTextureUrl) {
                    const cacheKey = `custom_${ps.customTextureUrl}`;
                    console.log(`[Particle] Custom texture: ${ps.customTextureUrl.substring(0, 60)}...`);
                    texture = await loadCustomParticleTexture(cacheKey, ps.customTextureUrl);
                }
                else {
                    const resolvedName = BUILTIN_PARTICLE_TEXTURES.has(textureName) ? textureName : 'flare';
                    console.log(`[Particle] Texture: ${resolvedName} (procedural)`);
                    texture = createProceduralParticleTexture(resolvedName);
                }
                console.log('[Particle] ✅ Texture ready:', texture.name);
                // Create particle system entity
                console.log('[Particle] Creating entity...');
                const entity = createParticleSystemEntity(ps);
                console.log('[Particle] ✅ Entity created:', entity.name);
                // Set the texture on the particle system
                if (entity.particlesystem) {
                    entity.particlesystem.colorMap = texture;
                    console.log('[Particle] ✅ Texture applied to particle system');
                    console.log('[Particle] Particle system properties:', {
                        numParticles: entity.particlesystem.numParticles,
                        lifetime: entity.particlesystem.lifetime,
                        rate: entity.particlesystem.rate,
                        loop: entity.particlesystem.loop,
                        autoPlay: entity.particlesystem.autoPlay
                    });
                }
                else {
                    console.warn('[Particle] ⚠️ Entity has no particlesystem component!');
                }
                // Add to scene
                sceneContent.addChild(entity);
                console.log('[Particle] ✅ Entity added to scene');
                // Log entity position
                const pos = entity.getPosition();
                console.log(`[Particle] Position: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
                // Store reference
                const safeName = (ps.id || ps.name || `particle-${particleEntities.size}`).replace(/[^a-zA-Z0-9]/g, '_');
                particleEntities.set(safeName, entity);
                console.log(`[Particle] ✅ SUCCESS: Created "${ps.name || safeName}"`);
            }
            catch (err: unknown) {
                const errObj = err instanceof Error ? err : { message: String(err), stack: '' };
                console.error(`[Particle] ❌ FAILED to create particle system: ${ps.name}`);
                console.error(`[Particle] Error message: ${errObj.message || 'No message'}`);
                console.error(`[Particle] Error stack: ${errObj.stack || 'No stack'}`);
                console.error(`[Particle] Error object:`, err);
            }
        }
        console.log('═══════════════════════════════════════');
        console.log(`[Particle] 🎉 COMPLETE: ${particleEntities.size}/${config.particles.length} particle systems created`);
        console.log('[Particle] Active particle systems:', Array.from(particleEntities.keys()));
        console.log('═══════════════════════════════════════');
    }
    /**
     * Stop all particle systems
     */
    function stopAllParticles(): void {
        particleEntities.forEach((entity) => {
            if (entity.particlesystem) {
                entity.particlesystem.stop();
            }
        });
    }
    /**
     * Start all particle systems
     */
    function startAllParticles(): void {
        particleEntities.forEach((entity) => {
            if (entity.particlesystem) {
                entity.particlesystem.play();
            }
        });
    }
    /**
     * Toggle a specific particle system by ID
     */
    function toggleParticle(id: string): void {
        const entity = particleEntities.get(id);
        if (entity) {
            entity.enabled = !entity.enabled;
        }
    }
    // =====================================================
    // CUSTOM MESH SYSTEM (3D Models - GLB/GLTF)
    // =====================================================
    interface AnimBaseLayerLike {
        states?: string[];
        play?: (state?: string) => void;
    }
    interface AnimComponentLike {
        playing?: boolean;
        speed?: number;
        loop?: boolean;
        baseLayer?: AnimBaseLayerLike;
        animations?: Record<string, unknown>;
        play?: (clip?: string, blendTime?: number) => void;
        pause?: () => void;
        loadStateGraph?: (stateGraph: unknown) => void;
        assignAnimation?: (path: string, asset: unknown, layer?: string) => void;
    }
    interface AnimInfo {
        type: 'pc-anim' | 'anim' | 'animation' | 'glb-skeleton';
        component?: AnimComponentLike;
        node?: pc.Entity;
        modelEntity?: AnimatedEntity;
        asset?: pc.Asset;
        animations?: AnimationResourceLike[];
        animationNames?: string[];
        animComponent?: AnimComponentLike;
        isPlaying?: boolean;
        startTime?: number;
        updateHandler?: ((dt: number) => void) | null;
        currentTime?: number;
    }
    type AnimatedEntity = Omit<pc.Entity, 'anim' | 'animation'> & {
        anim?: AnimComponentLike;
        animation?: AnimComponentLike;
    };
    interface AnimationResourceLike {
        name?: string;
        resource?: {
            name?: string;
            _name?: string;
        };
        _name?: string;
        _duration?: number;
        duration?: number;
        _curves?: Array<{
            evaluate?: (time: number) => unknown;
        } | null>;
        _paths?: Array<{
            entityPath?: string[];
            component?: string;
            propertyPath?: string[];
        }>;
    }
    interface ContainerResourceWithAnimations extends Omit<pc.ContainerResource, 'animations'> {
        animations?: AnimationResourceLike[];
    }
    interface CustomMeshData {
        entity: pc.Entity;
        config: CustomMeshConfigLike;
        modelAsset?: pc.Asset;
        animComponent?: AnimComponentLike;
        allAnimComponents?: AnimInfo[];
        audioSlotId?: string;
        /** Interaction audio, released with the mesh (see releaseCustomMeshAssets). */
        audioAsset?: pc.Asset;
        audioAssetReady: boolean;
        isAnimPlaying: boolean;
        audioPlaying: boolean;
        /**
         * Set when the mesh is removed (editor removal or rebuild, viewer teardown): a model still
         * loading then drops its asset instead of showing. A flag rather than "is this still the
         * map entry for its id": two models that share an id (possible in an old or hand-edited
         * scene) must both show, as they always did.
         */
        removed?: boolean;
    }
    const customMeshEntities: Map<string, CustomMeshData> = new Map();
    /**
     * Release a custom mesh's registry assets once its entry has left customMeshEntities.
     * PlayCanvas' resource loader caches one resource per URL, so meshes loaded from the same
     * URL share a single container: unloading it destroys that container's meshes, materials
     * and textures. It is only unloaded once no remaining custom mesh uses the same URL.
     */
    function releaseCustomMeshAssets(meshData: CustomMeshData): void {
        // Viewer teardown unloads every registry asset itself (app.destroy).
        if (isDestroyed) return;
        const modelAsset = meshData.modelAsset;
        if (modelAsset) {
            app.assets.remove(modelAsset);
            const fileUrl = (asset: pc.Asset) => (asset.file as { url?: string } | null)?.url;
            const url = fileUrl(modelAsset);
            let shared = false;
            customMeshEntities.forEach((other) => {
                const otherAsset = other.modelAsset;
                if (otherAsset && otherAsset !== modelAsset && otherAsset.type === modelAsset.type && fileUrl(otherAsset) === url) {
                    shared = true;
                }
            });
            if (!shared) modelAsset.unload();
        }
        if (meshData.audioAsset) {
            app.assets.remove(meshData.audioAsset);
            meshData.audioAsset.unload();
            meshData.audioAsset = undefined;
        }
        // Per-mesh material clones made by the opacity path (applyOpacityToCustomMesh).
        const opacityClones = customMeshOpacityCache.get(meshData.entity);
        if (opacityClones) {
            customMeshOpacityCache.delete(meshData.entity);
            opacityClones.materials.forEach((material) => material.destroy());
        }
    }
    /**
     * Helper to play an animation component using various methods
     * PlayCanvas has different ways to start animations depending on the setup
     */
    function playAnimComponent(anim: AnimComponentLike): void {
        try {
            // Method 1: Set playing property (simple animations)
            anim.playing = true;
            // Method 2: Use baseLayer.play() if available (Anim component)
            if (anim.baseLayer && typeof anim.baseLayer.play === 'function') {
                // Get the first available state/clip name
                const states = anim.baseLayer.states;
                if (states && states.length > 0) {
                    const firstState = states[0];
                    if (firstState && firstState !== 'START' && firstState !== 'END' && firstState !== 'ANY') {
                        anim.baseLayer.play(firstState);
                        console.log('[CustomMesh] Playing animation state:', firstState);
                    }
                }
            }
            // Method 3: Direct play() method
            if (typeof anim.play === 'function') {
                anim.play();
            }
            // Method 4: Set speed to ensure animation runs
            if (anim.speed !== undefined) {
                anim.speed = 1;
            }
            console.log('[CustomMesh] Animation started, playing:', anim.playing);
        }
        catch (err) {
            console.error('[CustomMesh] Error playing animation:', err);
        }
    }
    /**
     * Helper to pause an animation component
     */
    function pauseAnimComponent(anim: AnimComponentLike): void {
        try {
            anim.playing = false;
            if (anim.speed !== undefined) {
                anim.speed = 0;
            }
            if (typeof anim.pause === 'function') {
                anim.pause();
            }
        }
        catch (err) {
            console.error('[CustomMesh] Error pausing animation:', err);
        }
    }
    /**
     * Sample an animation track at a specific time
     * Handles keyframe interpolation for position, rotation, and scale
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function sampleAnimationTrack(track: Record<string, any>, time: number): unknown {
        try {
            const keys: Record<string, any>[] = track.keys || track.keyframes || [];
            if (keys.length === 0)
                return null;
            // Find surrounding keyframes
            let prevKey = keys[0];
            let nextKey = keys[keys.length - 1];
            for (let i = 0; i < keys.length - 1; i++) {
                const currentTime = keys[i].time ?? keys[i][0];
                const nextTime = keys[i + 1].time ?? keys[i + 1][0];
                if (time >= currentTime && time < nextTime) {
                    prevKey = keys[i];
                    nextKey = keys[i + 1];
                    break;
                }
            }
            // Calculate interpolation factor
            const prevTime = prevKey.time ?? prevKey[0] ?? 0;
            const nextTime = nextKey.time ?? nextKey[0] ?? 0;
            const duration = nextTime - prevTime;
            const t = duration > 0 ? (time - prevTime) / duration : 0;
            // Get values (handle both object and array formats)
            const prevValue = prevKey.value ?? prevKey.slice?.(1) ?? prevKey;
            const nextValue = nextKey.value ?? nextKey.slice?.(1) ?? nextKey;
            // Interpolate based on track type
            const trackType: string = track.type || track.property || 'position';
            if (trackType.includes('rotation') || trackType.includes('quaternion')) {
                // Quaternion SLERP interpolation
                if (Array.isArray(prevValue) && prevValue.length >= 4) {
                    return slerpQuat(prevValue, nextValue, t);
                }
                return prevValue;
            }
            else {
                // Linear interpolation for position/scale
                if (Array.isArray(prevValue)) {
                    return prevValue.map((v: number, i: number) => {
                        const nv = nextValue[i] ?? v;
                        return v + (nv - v) * t;
                    });
                }
                return prevValue;
            }
        }
        catch (e) {
            return null;
        }
    }
    /**
     * Simple quaternion SLERP
     */
    function slerpQuat(a: number[], b: number[], t: number): number[] {
        // Calculate cosine of angle
        let cosom = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
        // Adjust signs if necessary
        const bAdj = [...b];
        if (cosom < 0) {
            cosom = -cosom;
            bAdj[0] = -bAdj[0];
            bAdj[1] = -bAdj[1];
            bAdj[2] = -bAdj[2];
            bAdj[3] = -bAdj[3];
        }
        // Calculate coefficients
        let scale0: number, scale1: number;
        if (1 - cosom > 0.0001) {
            const omega = Math.acos(cosom);
            const sinom = Math.sin(omega);
            scale0 = Math.sin((1 - t) * omega) / sinom;
            scale1 = Math.sin(t * omega) / sinom;
        }
        else {
            // Use linear interpolation for very close quaternions
            scale0 = 1 - t;
            scale1 = t;
        }
        return [
            scale0 * a[0] + scale1 * bAdj[0],
            scale0 * a[1] + scale1 * bAdj[1],
            scale0 * a[2] + scale1 * bAdj[2],
            scale0 * a[3] + scale1 * bAdj[3]
        ];
    }
    /**
     * Apply an animation value to a target entity
     */
    function applyAnimationValue(rootEntity: pc.Entity, targetPath: string, trackType: string, value: unknown): void {
        try {
            // Parse the target path (e.g., "Bone001/position" or just "position")
            const parts = targetPath.split('/');
            const property = parts.pop() || targetPath;
            // Find the target node
            let target: pc.Entity | null = rootEntity;
            if (parts.length > 0) {
                const nodeName = parts.join('/');
                target = rootEntity.findByName(nodeName) as pc.Entity || rootEntity;
            }
            if (!target)
                return;
            // Apply the value based on property type
            const normalizedProp = property.toLowerCase();
            if (normalizedProp.includes('position') || normalizedProp.includes('translation')) {
                if (Array.isArray(value) && value.length >= 3) {
                    target.setLocalPosition(value[0], value[1], value[2]);
                }
            }
            else if (normalizedProp.includes('rotation') || normalizedProp.includes('quaternion')) {
                if (Array.isArray(value) && value.length >= 4) {
                    target.setLocalRotation(new pc.Quat(value[0], value[1], value[2], value[3]));
                }
                else if (Array.isArray(value) && value.length >= 3) {
                    // Euler angles
                    target.setLocalEulerAngles(value[0], value[1], value[2]);
                }
            }
            else if (normalizedProp.includes('scale')) {
                if (Array.isArray(value) && value.length >= 3) {
                    target.setLocalScale(value[0], value[1], value[2]);
                }
            }
        }
        catch (e) {
            // Silently ignore - animation track may reference non-existent node
        }
    }
    /**
     * Enhanced animation player that handles both anim and animation components
     * Also handles the new format with type info including GLB animations
     */
    function playAnimComponentV2(animInfo: AnimInfo): void {
        const { type, component, node, animations } = animInfo;
        console.log('[CustomMesh] Playing animation, type:', type, 'node:', node?.name, 'animations:', animations?.length);
        try {
            if (type === 'pc-anim') {
                // Simple PlayCanvas anim component - matches HTML export approach
                console.log('[CustomMesh] Using simple pc-anim approach (like HTML export)');
                if (!component) return;
                component.playing = true;
                animInfo.isPlaying = true;
                console.log('[CustomMesh] Animation started - playing:', component.playing);
            }
            else if (type === 'animation') {
                // Legacy Animation component
                if (!component) return;
                component.playing = true;
                component.loop = true;
                if (typeof component.play === 'function') {
                    // Play the first animation clip
                    const clips = Object.keys(component.animations || {});
                    if (clips.length > 0) {
                        component.play(clips[0], 1);
                        console.log('[CustomMesh] Playing legacy animation clip:', clips[0]);
                    }
                    else {
                        component.play();
                    }
                }
            }
            else if (type === 'glb-skeleton') {
                // GLB skeleton animation - use PlayCanvas AnimComponent
                console.log('[CustomMesh] Starting GLB skeleton animation playback');
                const { modelEntity, animations: animList } = animInfo;
                if (!modelEntity) return;
                if (animList && animList.length > 0) {
                    try {
                        // Add AnimComponent if not exists
                        if (!modelEntity.anim) {
                            modelEntity.addComponent('anim', {
                                activate: true,
                                speed: 1
                            });
                        }
                        if (modelEntity.anim) {
                            const animComp = modelEntity.anim as unknown as AnimComponentLike;
                            // Assign each animation from the container
                            // containerResource.animations are Asset objects — .resource is the AnimTrack
                            animList.forEach((animAsset: AnimationResourceLike, i: number) => {
                                const animTrack = animAsset.resource || animAsset;
                                const animName = animTrack._name || animTrack.name || animAsset._name || animAsset.name || `Anim_${i}`;
                                console.log('[CustomMesh] Assigning animation track:', animName, 'type:', typeof animTrack);
                                try {
                                    animComp.assignAnimation?.(animName, animTrack);
                                } catch (assignErr) {
                                    console.warn('[CustomMesh] assignAnimation failed for', animName, assignErr);
                                }
                            });
                            // Play the animation
                            modelEntity.anim.playing = true;
                            modelEntity.anim.speed = 1;
                            animInfo.animComponent = modelEntity.anim;
                            animInfo.isPlaying = true;
                            console.log('[CustomMesh] GLB animation playing via AnimComponent');
                        }
                    } catch (err) {
                        console.error('[CustomMesh] Error setting up GLB animation:', err);
                    }
                }
            }
            else if (type === 'anim' && component) {
                // New Anim component
                component.playing = true;
                component.speed = 1;
                // Try to play via baseLayer
                if (component.baseLayer) {
                    const states = component.baseLayer.states || [];
                    const playableState = states.find((s: string) => s !== 'START' && s !== 'END' && s !== 'ANY');
                    if (playableState) {
                        component.baseLayer.play?.(playableState);
                        console.log('[CustomMesh] Playing anim state:', playableState);
                    }
                }
            }
            // Only log component state if component exists (not for glb-skeleton)
            if (component) {
                console.log('[CustomMesh] Animation component state - playing:', component.playing, 'speed:', component.speed);
            }
        }
        catch (err) {
            console.error('[CustomMesh] Error in playAnimComponentV2:', err);
        }
    }
    /**
     * Enhanced animation pauser
     */
    function pauseAnimComponentV2(animInfo: AnimInfo): void {
        const { type, component } = animInfo;
        if (!component && type !== 'glb-skeleton') return;
        try {
            if (type === 'pc-anim' && component) {
                // Simple PlayCanvas anim component - matches HTML export approach
                component.playing = false;
                animInfo.isPlaying = false;
                console.log('[CustomMesh] pc-anim animation paused');
            }
            else if (type === 'glb-skeleton') {
                // Stop GLB skeleton animation
                animInfo.isPlaying = false;
                // If we have an AnimComponent, pause it
                if (animInfo.animComponent) {
                    animInfo.animComponent.playing = false;
                    console.log('[CustomMesh] GLB skeleton animation paused via AnimComponent');
                }
                // Remove update handler if using manual playback
                if (animInfo.updateHandler) {
                    app.off('update', animInfo.updateHandler);
                    animInfo.updateHandler = null;
                    console.log('[CustomMesh] GLB manual animation paused');
                }
            }
            else if (component) {
                component.playing = false;
                component.speed = 0;
                if (type === 'animation' && typeof component.pause === 'function') {
                    component.pause();
                }
            }
        }
        catch (err) {
            console.error('[CustomMesh] Error pausing animation:', err);
        }
    }
    /**
     * Load and create a custom mesh entity (GLB/GLTF model)
     * Matches HTML export customMeshSystem.ts
     */
    function loadCustomMesh(meshConfig: CustomMeshConfigLike, index: number): pc.Entity | null {
        console.log('[CustomMesh] Loading mesh', index, ':', meshConfig.name, meshConfig);
        // Validate modelUrl before attempting to load
        if (!meshConfig.modelUrl || typeof meshConfig.modelUrl !== 'string' || meshConfig.modelUrl.trim() === '') {
            console.warn('[CustomMesh] Skipping mesh', meshConfig.name, '- no valid modelUrl provided. Config:', meshConfig);
            return null;
        }
        // Create container entity (tagged 'custom-mesh' for SelectionManager walk-up)
        const entity = new pc.Entity('custom-mesh-' + index);
        entity.tags.add('custom-mesh');
        // Position (negate Z for BabylonJS -> PlayCanvas coordinate conversion)
        const pos = playCanvasPositionFromSceneVec3(meshConfig.position);
        entity.setPosition(pos.x, pos.y, pos.z);
        // Rotation (BJS YXZ Euler radians → PC quaternion)
        if (meshConfig.rotation) {
            const rot = meshConfig.rotation;
            entity.setRotation(bjsRotationToPC(rot._x ?? rot.x ?? 0, rot._y ?? rot.y ?? 0, rot._z ?? rot.z ?? 0));
        }
        // Scale
        if (meshConfig.scale) {
            const scale = meshConfig.scale;
            entity.setLocalScale(scale._x ?? scale.x ?? 1, scale._y ?? scale.y ?? 1, scale._z ?? scale.z ?? 1);
        }
        // Determine asset type from file extension — splat formats use 'gsplat', everything else uses 'container' (GLB/GLTF)
        const modelUrl = meshConfig.modelUrl.trim();
        const splatExtensions = ['.splat', '.ply', '.sog', '.spz', '.compressed.ply'];
        // For blob URLs, fall back to modelName for extension detection
        const nameForExtDetection = modelUrl.startsWith('blob:')
            ? (meshConfig.modelName || meshConfig.name || '')
            : modelUrl.split('?')[0].split('#')[0]; // strip query/fragment
        const lowerName = nameForExtDetection.toLowerCase();
        const isSplatFormat = splatExtensions.some(ext => lowerName.endsWith(ext));
        const assetType = isSplatFormat ? 'gsplat' : 'container';
        console.log('[CustomMesh] Loading model from URL:', modelUrl, '| format:', assetType, '| detected from:', nameForExtDetection);
        // The engine picks its parser from the file name, which a blob: URL lacks,
        // so give an unsaved local file its real name.
        const modelFile = modelUrl.startsWith('blob:') && nameForExtDetection
            ? { url: modelUrl, filename: nameForExtDetection }
            : { url: modelUrl };
        const modelAsset = new pc.Asset('mesh-model-' + index, assetType, modelFile);
        app.assets.add(modelAsset);
        // Store mesh data reference (including asset for cleanup on removal)
        const meshId = meshConfig.id || `mesh-${index}`;
        // Selections name a splat model by its mesh id; a KHR GLB loaded as the scene's model is the main scan.
        registerSplatObject(entity, meshId === '__primary-mesh__' ? MAIN_SPLAT_OBJECT_ID : meshId);
        const meshData: CustomMeshData = {
            entity,
            config: meshConfig,
            modelAsset,
            audioAssetReady: false,
            isAnimPlaying: false,
            audioPlaying: false
        };
        customMeshEntities.set(meshId, meshData);
        // Splat assets are handled differently — add gsplat component directly
        if (isSplatFormat) {
            // Wind-reactive splat object (trees separated from the scan, ...): the wind system
            // installs its work-buffer modifier once the gsplat component exists.
            if (meshConfig.wind) windSystem.setObject(meshId, entity, meshConfig.wind);
            modelAsset.ready(() => {
                // Removed (editor removal or rebuild, viewer teardown) while loading.
                if (meshData.removed) {
                    console.log('[CustomMesh] Entity destroyed before splat loaded, skipping:', meshConfig.name);
                    releaseCustomMeshAssets(meshData);
                    return;
                }
                console.log('[CustomMesh] Splat loaded as custom mesh:', meshConfig.name);
                entity.addComponent('gsplat', { asset: modelAsset, unified: true });
            });
            modelAsset.on('error', (err: unknown) => {
                console.error('[CustomMesh] Failed to load splat mesh:', meshConfig.name, err);
                app.assets.remove(modelAsset);
            });
            app.assets.load(modelAsset);
        } else {
        modelAsset.ready((asset: pc.Asset) => {
            try {
                // Guard: if the mesh was removed (editor removal or rebuild, viewer teardown)
                // before the asset finished loading, bail out and drop the loaded container
                // unless a live mesh shares it.
                if (meshData.removed) {
                    console.log('[CustomMesh] Entity destroyed before model loaded, skipping:', meshConfig.name);
                    releaseCustomMeshAssets(meshData);
                    return;
                }
                console.log('[CustomMesh] Model loaded:', meshConfig.name);
                // Validate asset resource before instantiation
                if (!asset || !asset.resource) {
                    console.error('[CustomMesh] Invalid asset resource for mesh:', meshConfig.name);
                    return;
                }
                // Instantiate the model
                const containerResource = asset.resource as ContainerResourceWithAnimations | undefined;
                const modelEntity = containerResource?.instantiateRenderEntity();
                if (!modelEntity) {
                    console.error('[CustomMesh] Failed to instantiate render entity for mesh:', meshConfig.name);
                    return;
                }
                entity.addChild(modelEntity);
                // Store reference to model entity for animation
                (entity as ViewerRuntimeEntity).modelEntity = modelEntity;
                // IMPORTANT: Recursively enable all children in the hierarchy
                // Some GLB/GLTF files have nodes disabled by default
                function enableAllChildren(node: pc.Entity): void {
                    node.enabled = true;
                    if (node.children) {
                        node.children.forEach((child) => {
                            if (child instanceof pc.Entity) {
                                enableAllChildren(child);
                            }
                        });
                    }
                }
                enableAllChildren(modelEntity);
                // Count children for debugging
                let childCount = 0;
                modelEntity.forEach(() => { childCount++; });
                console.log('[CustomMesh] Enabled all children for:', meshConfig.name, '- Total nodes:', childCount);
                // PlayCanvas instantiates KHR_gaussian_splatting primitives as gsplat
                // components alongside ordinary render components in the same GLB.
                const gsplatNodes: pc.Entity[] = [];
                modelEntity.forEach((node) => {
                    const entityNode = node as pc.Entity;
                    if (entityNode.gsplat) gsplatNodes.push(entityNode);
                });
                if (gsplatNodes.length > 0) {
                    console.log('[CustomMesh] KHR_gaussian_splatting nodes:', meshConfig.name, gsplatNodes.length);
                    for (const splatNode of gsplatNodes) {
                        splatNode.gsplat?.material?.setParameter('alphaClip', 0.01);
                    }
                }
                // Tame GLB materials to prevent overblown/too-shiny appearance
                // PlayCanvas PBR can appear brighter than BabylonJS with the same lighting setup
                // Reduce specular contribution on all materials in the loaded model
                function tameGlbMaterials(node: pc.Entity): void {
                    const r = node.render;
                    if (r && r.meshInstances) {
                        for (const mi of r.meshInstances) {
                            const mat = mi.material as pc.StandardMaterial | undefined;
                            if (mat && typeof mat.update === 'function') {
                                // Reduce specular intensity to prevent shiny look
                                if (mat.specular) {
                                    mat.specular.mulScalar(0.3);
                                }
                                // Increase roughness slightly for less mirror-like reflections
                                if (mat.gloss !== undefined) {
                                    mat.gloss = Math.min(mat.gloss, 60);
                                }
                                mat.update();
                            }
                        }
                    }
                    if (node.children) {
                        node.children.forEach((child) => {
                            if (child instanceof pc.Entity) tameGlbMaterials(child);
                        });
                    }
                }
                tameGlbMaterials(modelEntity);
                // Move all render components to the hotspot mesh layer for proper depth sorting with gsplats
                function setLayerRecursive(node: pc.Entity): void {
                    if (node.render) {
                        node.render.layers = [hotspotMeshLayer.id];
                    }
                    if (node.children) {
                        node.children.forEach((child) => {
                            if (child instanceof pc.Entity) setLayerRecursive(child);
                        });
                    }
                }
                setLayerRecursive(modelEntity);
                // Apply initial opacity: a keyframe animation's value (set before the GLB finished
                // loading), else the static value or the animated value at the current tour
                // progress (applied immediately so there is no flash of full visibility)
                const initialMeshOpacity = (entity as ViewerRuntimeEntity).animationOpacity ?? resolveEntityOpacity(meshConfig, currentProgress * 100);
                if (initialMeshOpacity < FULL_OPACITY_THRESHOLD) {
                    applyOpacityToCustomMesh(entity, initialMeshOpacity);
                    console.log('[CustomMesh] Applied initial opacity:', initialMeshOpacity, 'to', meshConfig.name);
                }
                // Unlit models ignore scene lights and ambient (flat albedo). Applied after the
                // opacity pass so the recipe lands on the per-mesh material clones it creates.
                if (meshConfig.unlit) {
                    setEntityUnlit(modelEntity, true);
                }
                // Setup billboarding if enabled (with range support)
                if (meshConfig.billboard) {
                    // Store original rotation for when billboard is disabled
                    const originalRotation = entity.getEulerAngles().clone();
                    // Initialize billboard active state
                    (entity as ViewerRuntimeEntity)._billboardActive = !meshConfig.billboardRange; // Active by default if no range
                    // Create update handler to make mesh face camera when billboard is active.
                    // setEulerAngles dirties the whole GLB subtree (recursive _dirtyWorld + AABB
                    // invalidation), so only write on the active→inactive transition and when the
                    // yaw actually changed — mirrors the hotspot billboard's restore-once pattern.
                    let billboardWasActive = false;
                    let billboardLastYaw = NaN;
                    const meshBillboardUpdateFn = () => {
                        if (!entity.enabled)
                            return;
                        // Check if billboard should be active (controlled by updateCustomMeshVisibility)
                        const billboardActive = (entity as ViewerRuntimeEntity)._billboardActive;
                        if (!billboardActive) {
                            // Restore original rotation once when billboard deactivates
                            if (billboardWasActive) {
                                billboardWasActive = false;
                                billboardLastYaw = NaN; // force a fresh write on reactivation
                                entity.setEulerAngles(originalRotation.x, originalRotation.y, originalRotation.z);
                            }
                            return;
                        }
                        billboardWasActive = true;
                        const camPos = camera.getPosition();
                        const meshPos = entity.getPosition();
                        // Calculate angle to camera (Y-axis rotation only for billboard)
                        const dx = camPos.x - meshPos.x;
                        const dz = camPos.z - meshPos.z;
                        const angle = Math.atan2(dx, dz) * (180 / Math.PI);
                        if (Math.abs(angle - billboardLastYaw) < 0.01)
                            return; // yaw unchanged — skip the subtree-dirtying write
                        billboardLastYaw = angle;
                        // Re-read X/Z each frame (NOT cached at setup): the editor's
                        // updateCustomMesh sets rotation live and tilt edits must survive.
                        const currentRot = entity.getEulerAngles();
                        entity.setEulerAngles(currentRot.x, angle, currentRot.z);
                    };
                    app.on('update', meshBillboardUpdateFn);
                    // The handler pins the whole model hierarchy: drop it with the mesh.
                    entity.once('destroy', () => app.off('update', meshBillboardUpdateFn));
                    console.log('[CustomMesh] Billboard enabled for:', meshConfig.name, meshConfig.billboardRange ? '(with range control)' : '(always active)');
                }
                // Handle animations - ALWAYS check for GLB animations regardless of interaction settings
                // Log what's available in the asset for debugging
                const hasGlbAnimations = (containerResource?.animations?.length ?? 0) > 0;
                console.log('[CustomMesh] Asset resource for', meshConfig.name, ':', {
                    hasAnimations: hasGlbAnimations,
                    animationCount: containerResource?.animations?.length || 0,
                    animations: containerResource?.animations?.map((a) => a?.name || a?.resource?.name || 'unnamed') || [],
                    interactionConfig: meshConfig.interaction
                });
                // Setup animations if the model has them
                // APPROACH: Match HTML export - prioritize modelEntity.anim first, then fallback
                if (hasGlbAnimations || (meshConfig.interaction && meshConfig.interaction.playModelAnimation)) {
                    const allAnimComponents: AnimInfo[] = [];
                    // PRIORITY 1: Check if PlayCanvas auto-created an anim component (like HTML export expects)
                    const animComponent = (modelEntity as AnimatedEntity).anim;
                    if (animComponent) {
                        console.log('[CustomMesh] Found modelEntity.anim component - using simple approach');
                        allAnimComponents.push({
                            type: 'pc-anim',
                            component: animComponent,
                            modelEntity: modelEntity as unknown as AnimatedEntity
                        });
                    }
                    // PRIORITY 2: Search hierarchy for anim/animation components
                    if (allAnimComponents.length === 0) {
                        function findAllAnimComponents(node: AnimatedEntity, depth: number = 0): void {
                            // Check for new Anim component
                            if (node.anim && !allAnimComponents.find((a) => a.component === node.anim)) {
                                allAnimComponents.push({ type: 'anim', component: node.anim, node: node as unknown as pc.Entity });
                                console.log('[CustomMesh] Found existing ANIM component on:', node.name);
                            }
                            // Check for legacy Animation component
                            if (node.animation) {
                                allAnimComponents.push({ type: 'animation', component: node.animation, node: node as unknown as pc.Entity });
                                console.log('[CustomMesh] Found ANIMATION (legacy) component on:', node.name);
                            }
                            if (node.children) {
                                node.children.forEach((child) => {
                                    if (child instanceof pc.Entity) {
                                        findAllAnimComponents(child as AnimatedEntity, depth + 1);
                                    }
                                });
                            }
                        }
                        findAllAnimComponents(modelEntity as AnimatedEntity);
                    }
                    // PRIORITY 3: Fall back to manual GLB skeleton animation approach
                    if (allAnimComponents.length === 0 && hasGlbAnimations) {
                        const animations = containerResource?.animations || [];
                        console.log('[CustomMesh] No anim component found - falling back to GLB skeleton approach');
                        console.log('[CustomMesh] GLB has', animations.length, 'embedded animations');
                        try {
                            allAnimComponents.push({
                                type: 'glb-skeleton',
                                modelEntity: modelEntity as unknown as AnimatedEntity,
                                asset: asset,
                                animations: animations,
                                animationNames: animations.map((a) => a.resource?.name || a.name || 'Animation'),
                                isPlaying: false,
                                currentTime: 0
                            });
                            console.log('[CustomMesh] GLB skeleton animations stored:', animations.map((a) => a.resource?.name || a.name));
                        }
                        catch (err) {
                            console.error('[CustomMesh] Error setting up GLB animations:', err);
                        }
                    }
                    if (allAnimComponents.length > 0) {
                        // Store all animation components (now with type info)
                        meshData.allAnimComponents = allAnimComponents;
                        meshData.animComponent = allAnimComponents[0].component;
                        console.log('[CustomMesh] Total animation components for', meshConfig.name, ':', allAnimComponents.length, '- Types:', allAnimComponents.map((a) => a.type).join(', '));
                        // Auto-play if enabled OR if animationAutoPlay is set
                        const shouldAutoPlay = meshConfig.interaction?.animationAutoPlay;
                        if (shouldAutoPlay) {
                            allAnimComponents.forEach((animInfo) => {
                                playAnimComponentV2(animInfo);
                            });
                            meshData.isAnimPlaying = true;
                            console.log('[CustomMesh] Auto-playing all animations for:', meshConfig.name);
                        }
                    }
                    else {
                        console.warn('[CustomMesh] No animation components could be set up for mesh:', meshConfig.name);
                    }
                }
                else {
                    console.log('[CustomMesh] No animations to setup for:', meshConfig.name, '(no GLB animations and playModelAnimation not enabled)');
                }
                // Setup spatial audio if configured
                if (meshConfig.interaction && meshConfig.interaction.playAudio && meshConfig.interaction.audioUrl) {
                    setupMeshAudio(entity, meshConfig, meshData);
                }
                // Setup click interaction
                if (meshConfig.interaction) {
                    setupMeshClickInteraction(entity, meshConfig, meshData);
                }
                console.log('[CustomMesh] Mesh fully initialized:', meshConfig.name);
            }
            catch (err) {
                console.error('[CustomMesh] Error processing loaded mesh:', meshConfig.name, err);
            }
        });
        modelAsset.on('error', (err: unknown) => {
            console.error('[CustomMesh] Failed to load mesh:', meshConfig.name, err);
            // Remove failed asset to prevent further issues
            app.assets.remove(modelAsset);
        });
        app.assets.load(modelAsset);
        } // end else (container/GLB path)
        // Visibility range setup
        if (meshConfig.visibilityRange) {
            (entity as ViewerRuntimeEntity).visibilityRange = meshConfig.visibilityRange;
            entity.enabled = meshConfig.enabled !== false;
        }
        else {
            entity.enabled = meshConfig.enabled !== false;
        }
        // Opacity configuration
        (entity as ViewerRuntimeEntity).opacityConfig = {
            mode: meshConfig.opacityMode || 'static',
            value: meshConfig.opacity !== undefined ? meshConfig.opacity : 1
        };
        sceneContent.addChild(entity);
        return entity;
    }
    /**
     * Setup spatial audio for mesh
     */
    function setupMeshAudio(entity: pc.Entity, meshConfig: CustomMeshConfigLike, meshData: CustomMeshData): void {
        const audioConfig = meshConfig.interaction;
        if (!audioConfig) return;
        const meshId = meshConfig.id || meshConfig.name;
        const slotId = `mesh-audio-${meshId}`;
        // Add sound component to entity
        entity.addComponent('sound', {
            positional: audioConfig.audioSpatial || false,
            distanceModel: audioConfig.audioDistanceModel || 'exponential',
            refDistance: finitePositiveSceneNumber(audioConfig.audioRefDistance, 1),
            maxDistance: finitePositiveSceneNumber(audioConfig.audioMaxDistance, 100),
            rollOffFactor: finiteNonNegativeSceneNumber(audioConfig.audioRolloffFactor, 1),
            slots: {
                [slotId]: {
                    name: slotId,
                    loop: audioConfig.audioLoop || false,
                    autoPlay: false,
                    volume: finiteNonNegativeSceneNumber(audioConfig.audioVolume, 1),
                    pitch: 1
                }
            }
        });
        meshData.audioSlotId = slotId;
        // Load audio asset
        const audioAsset = new pc.Asset(`mesh-audio-asset-${meshId}`, 'audio', { url: audioConfig.audioUrl });
        meshData.audioAsset = audioAsset;
        app.assets.add(audioAsset);
        audioAsset.ready(() => {
            if (meshData.audioAsset !== audioAsset) {
                // The mesh was removed while its audio loaded: drop the decoded audio.
                audioAsset.unload();
                return;
            }
            const slot = entity.sound?.slot(slotId);
            if (slot) {
                slot.asset = audioAsset.id;
                trackSlotPlaybackState(slot, (playing) => {
                    meshData.audioPlaying = playing;
                });
                const triggerMode = audioConfig.audioTriggerMode || audioConfig.activationMode || 'click';
                if (triggerMode === 'autoplay' && !slot.isPlaying) {
                    slot.play();
                    meshData.audioPlaying = true;
                }
                // Late proximity start: if camera was already inside radius before asset loaded
                if (triggerMode === 'proximity' && !slot.isPlaying) {
                    const cameraPos = camera.getPosition();
                    const dist = cameraPos.distance(entity.getPosition());
                    const triggerRadius = finitePositiveSceneNumber(audioConfig.audioProximityRadius ?? audioConfig.audioMaxDistance, 1);
                    if (Number.isFinite(dist) && dist <= triggerRadius) {
                        slot.play();
                        meshData.audioPlaying = true;
                        console.log('[CustomMesh] Audio started (late proximity):', meshConfig.name);
                    }
                }
            }
            meshData.audioAssetReady = true;
            console.log('[CustomMesh] Audio loaded for mesh:', meshConfig.name, 'Spatial:', audioConfig.audioSpatial);
        });
        audioAsset.on('error', (err: unknown) => {
            console.error('[CustomMesh] Failed to load mesh audio:', meshConfig.name, err);
        });
        app.assets.load(audioAsset);
    }
    /**
     * Check if a ray intersects any mesh render component in the entity hierarchy
     */
    function rayIntersectsMeshHierarchy(entity: pc.Entity, rayOrigin: pc.Vec3, rayDir: pc.Vec3): boolean {
        // Check this entity's render components
        const render = (entity as ViewerRuntimeEntity).render;
        if (render && render.meshInstances) {
            for (const mi of render.meshInstances) {
                if (mi.aabb) {
                    const intersection = rayIntersectsAABB(rayOrigin, rayDir, mi.aabb);
                    if (intersection)
                        return true;
                }
            }
        }
        // Check model component if present
        const model = (entity as ViewerRuntimeEntity).model;
        if (model && model.meshInstances) {
            for (const mi of model.meshInstances) {
                if (mi.aabb) {
                    const intersection = rayIntersectsAABB(rayOrigin, rayDir, mi.aabb);
                    if (intersection)
                        return true;
                }
            }
        }
        // Recursively check children
        const children = entity.children;
        if (children) {
            for (const child of children) {
                if (child instanceof pc.Entity && rayIntersectsMeshHierarchy(child, rayOrigin, rayDir)) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Ray-AABB intersection test
     */
    function rayIntersectsAABB(rayOrigin: pc.Vec3, rayDir: pc.Vec3, aabb: {
        getMin?: () => LegacyVec3 & {
            data?: number[];
        };
        getMax?: () => LegacyVec3 & {
            data?: number[];
        };
        min?: LegacyVec3 & {
            data?: number[];
        };
        max?: LegacyVec3 & {
            data?: number[];
        };
    }): boolean {
        const min = aabb.getMin ? aabb.getMin() : aabb.min;
        const max = aabb.getMax ? aabb.getMax() : aabb.max;
        if (!min || !max)
            return false;
        let tmin = -Infinity;
        let tmax = Infinity;
        const axes = ['x', 'y', 'z'] as const;
        const readAxis = (value: LegacyVec3 & {
            data?: number[];
        }, axis: 'x' | 'y' | 'z', index: number): number => {
            const byAxis = value[axis];
            if (typeof byAxis === 'number')
                return byAxis;
            const byUnderscore = value[`_${axis}` as '_x' | '_y' | '_z'];
            if (typeof byUnderscore === 'number')
                return byUnderscore;
            return value.data?.[index] ?? 0;
        };
        for (let i = 0; i < 3; i++) {
            const axis = axes[i];
            const origin = rayOrigin[axis];
            const dir = rayDir[axis];
            const minVal = readAxis(min, axis, i);
            const maxVal = readAxis(max, axis, i);
            if (Math.abs(dir) < 1e-8) {
                if (origin < minVal || origin > maxVal)
                    return false;
            }
            else {
                let t1 = (minVal - origin) / dir;
                let t2 = (maxVal - origin) / dir;
                if (t1 > t2)
                    [t1, t2] = [t2, t1];
                tmin = Math.max(tmin, t1);
                tmax = Math.min(tmax, t2);
                if (tmin > tmax)
                    return false;
            }
        }
        return tmax >= 0;
    }
    /**
     * Check if a custom mesh has any active interactions
     * Matches HTML export's hasInteractions function
     */
    function hasInteractions(customMesh: CustomMeshConfigLike): boolean {
        const interaction = customMesh.interaction;
        if (!interaction)
            return false;
        return !!(interaction.triggerUIPopup ||
            interaction.playAudio ||
            interaction.playModelAnimation ||
            interaction.triggerDirectLink ||
            interaction.linkedActions?.length ||
            interaction.cutaway ||
            interaction.splatSwap);
    }
    /**
     * Display mesh content using the hotspot popup system
     * Matches HTML export's displayMeshContent function
     */
    function displayMeshContent(customMesh: CustomMeshConfigLike): void {
        if (!customMesh.interaction)
            return;
        // Create a hotspot-like object to use with the hotspot popup system
        const hotspotData = {
            id: `mesh-content-${customMesh.id}`,
            title: customMesh.interaction.title || customMesh.name,
            information: customMesh.interaction.information,
            informationHtml: customMesh.interaction.informationHtml,
            photoUrl: customMesh.interaction.photoUrl,
            iframeUrl: customMesh.interaction.iframeUrl,
            externalLinkUrl: customMesh.interaction.externalLinkUrl,
            externalLinkText: customMesh.interaction.externalLinkText,
            // Style options
            backgroundColor: customMesh.interaction.backgroundColor || '#000000',
            textColor: customMesh.interaction.textColor || '#ffffff',
        };
        // VR: on the 3D info panel, like a hotspot's (a headset can't show page UI).
        if (inVrSession()) {
            showARContent(hotspotData);
            return;
        }
        // The hotspot popup (title, text, photo, web page, link), styled like a hotspot's. Without the
        // viewer UI there is no popup element: the plain model popup. (This used to look for a
        // `showHotspotPopup` the UI never had, so every model popup showed its name alone.)
        if (container.querySelector('.storysplat-hotspot-popup')) {
            showHotspotPopup(container, hotspotData as HotspotData, currentButtonLabels);
        }
        else {
            showMeshPopup(customMesh);
        }
    }
    /**
     * Hide mesh content popup
     */
    function hideMeshContent(): void {
        if (inVrSession()) {
            hideARContent();
            return;
        }
        // Hide (not remove: hotspots share it) the hotspot popup
        hideHotspotPopup(container);
        // Remove mesh popup if visible
        const meshPopup = document.querySelector('.storysplat-mesh-popup');
        if (meshPopup)
            meshPopup.remove();
    }
    /**
     * Handle direct link trigger for a mesh
     * Matches HTML export's handleDirectLink function
     */
    function handleDirectLink(customMesh: CustomMeshConfigLike): void {
        if (!customMesh.interaction?.triggerDirectLink || !customMesh.interaction.directLinkUrl)
            return;
        window.open(customMesh.interaction.directLinkUrl, '_blank');
    }
    // ── Delegated mesh interaction dispatch ─────────────────────────────────────────────────
    // ONE canvas click/mousemove/mouseleave listener set serves ALL interactive custom meshes
    // (previously one set PER mesh, each re-reading the canvas rect and re-deriving the ray).
    // The ray is computed once per event into shared scratches; each mesh then runs its own hit
    // test (including its per-mesh distance fallback) and its own transition callbacks, in
    // registration order — preserving the last-writer cursor/hover semantics of the per-mesh
    // listeners, including overlapping meshes all firing.
    interface InteractiveMeshEntry {
        entity: pc.Entity;
        /** Hit test against the shared per-event ray scratches (_meshRayFrom/_meshRayDir). */
        hitTest: () => boolean;
        onHoverIn: () => void;
        onHoverOut: () => void;
        onClick: () => void;
        isHovering: boolean;
        /** Some of its interactions run on hover: in VR a select toggles them (there is no hover). */
        hoverDriven: boolean;
    }
    const interactiveMeshRegistry: InteractiveMeshEntry[] = [];
    const _meshRayFrom = new pc.Vec3();
    const _meshRayTo = new pc.Vec3();
    const _meshRayDir = new pc.Vec3();
    const _meshRayTmp1 = new pc.Vec3();
    const _meshRayTmp2 = new pc.Vec3();
    let meshDelegatedListenersBound = false;
    const computeMeshInteractionRay = (clientX: number, clientY: number): void => {
        const canvasForMesh = app.graphicsDevice.canvas as HTMLCanvasElement;
        const rect = canvasForMesh.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        camera.camera!.screenToWorld(x, y, camera.camera!.nearClip, _meshRayFrom);
        camera.camera!.screenToWorld(x, y, camera.camera!.farClip, _meshRayTo);
        _meshRayDir.sub2(_meshRayTo, _meshRayFrom).normalize();
    };
    const delegatedMeshClickHandler = (e: MouseEvent) => {
        if (interactiveMeshRegistry.length === 0) return;
        // Measuring: a click places a point, it doesn't trigger the mesh (as for hotspots).
        if (isMeasureModeActive) return;
        computeMeshInteractionRay(e.clientX, e.clientY);
        for (const entry of interactiveMeshRegistry) {
            if (entry.hitTest()) entry.onClick();
        }
    };
    /** The interactive models a world-space ray hits (a VR controller's): scratches as for clicks. */
    const interactiveMeshesAlong = (from: pc.Vec3, dir: pc.Vec3): InteractiveMeshEntry[] => {
        if (interactiveMeshRegistry.length === 0) return [];
        _meshRayFrom.copy(from);
        _meshRayDir.copy(dir);
        return interactiveMeshRegistry.filter((entry) => entry.entity.enabled && entry.hitTest());
    };
    /** Whether a tap at this page point lands on an interactive model (8th Wall AR placement leaves it alone). */
    const interactiveMeshAtClientPoint = (clientX: number, clientY: number): boolean => {
        if (interactiveMeshRegistry.length === 0) return false;
        computeMeshInteractionRay(clientX, clientY);
        return interactiveMeshRegistry.some((entry) => entry.entity.enabled && entry.hitTest());
    };
    const delegatedMeshHoverHandler = (e: MouseEvent) => {
        if (interactiveMeshRegistry.length === 0) return;
        computeMeshInteractionRay(e.clientX, e.clientY);
        for (const entry of interactiveMeshRegistry) {
            const nowHovering = entry.hitTest();
            if (nowHovering && !entry.isHovering) {
                entry.isHovering = true;
                entry.onHoverIn();
            } else if (!nowHovering && entry.isHovering) {
                entry.isHovering = false;
                entry.onHoverOut();
            }
        }
    };
    const delegatedMeshLeaveHandler = () => {
        for (const entry of interactiveMeshRegistry) {
            if (entry.isHovering) {
                entry.isHovering = false;
                entry.onHoverOut();
            }
        }
    };
    /** Register a mesh's interaction entry; binds the delegated listeners lazily on first use so
     *  they dispatch AFTER the earlier-registered hotspot/portal mousemove handler (the
     *  anyMeshHovered cursor-precedence interplay depends on that order). */
    function registerInteractiveMesh(entry: InteractiveMeshEntry): void {
        interactiveMeshRegistry.push(entry);
        (entry.entity as ViewerRuntimeEntity).meshInteractionEntry = entry;
        if (!meshDelegatedListenersBound) {
            meshDelegatedListenersBound = true;
            const canvasForMesh = app.graphicsDevice.canvas as HTMLCanvasElement;
            canvasForMesh.addEventListener('click', delegatedMeshClickHandler);
            canvasForMesh.addEventListener('mousemove', delegatedMeshHoverHandler);
            canvasForMesh.addEventListener('mouseleave', delegatedMeshLeaveHandler);
        }
    }
    /**
     * Setup hover and click interactions for mesh
     * Enhanced to match HTML export's setupMeshInteractions with per-interaction trigger modes
     */
    function setupMeshClickInteraction(entity: pc.Entity, meshConfig: CustomMeshConfigLike, meshData: CustomMeshData): void {
        // Skip if no active interactions
        if (!hasInteractions(meshConfig)) {
            console.log('[CustomMesh] Skipping interaction setup - no active interactions for:', meshConfig.name);
            return;
        }
        // Get the global activation mode (fallback for per-interaction modes)
        const activationMode = meshConfig.interaction?.activationMode || 'click';
        // Cursor writes go through the shared canvas (delegated dispatch computes the ray).
        const canvasForMesh = app.graphicsDevice.canvas as HTMLCanvasElement;
        // Hit test against the shared per-event ray (computed once by the delegated handler).
        // Allocation-free: fallback math runs in shared scratches.
        const hitTest = (): boolean => {
            const from = _meshRayFrom, dir = _meshRayDir;
            // Get the model entity (child of container entity)
            const modelEntity = (entity as ViewerRuntimeEntity).modelEntity as pc.Entity | undefined;
            // Check intersection with model entity hierarchy (includes all children)
            if (modelEntity && rayIntersectsMeshHierarchy(modelEntity, from, dir)) {
                return true;
            }
            // Fallback: also check the container entity itself
            if (rayIntersectsMeshHierarchy(entity, from, dir)) {
                return true;
            }
            // Final fallback: distance-based check for small or simple meshes
            const meshPos = entity.getPosition();
            const toMesh = _meshRayTmp1.sub2(meshPos, from);
            const t = toMesh.dot(dir);
            if (t > 0) {
                const closestPoint = _meshRayTmp2.copy(dir).mulScalar(t).add(from);
                const distance = closestPoint.distance(meshPos);
                const scale = entity.getLocalScale();
                const hitRadius = Math.max(scale.x, scale.y, scale.z) * 1.5; // Larger radius for fallback
                if (distance < hitRadius) {
                    return true;
                }
            }
            return false;
        };
        // Get per-interaction trigger modes (matches HTML export pattern)
        const popupTriggerMode = meshConfig.interaction?.popupTriggerMode || activationMode;
        const audioTriggerMode = meshConfig.interaction?.audioTriggerMode || activationMode;
        const animationTriggerMode = meshConfig.interaction?.animationTriggerMode || activationMode;
        const directLinkTriggerMode = meshConfig.interaction?.directLinkTriggerMode || activationMode;
        const hasPointerDrivenInteraction =
            ((popupTriggerMode === 'click' || popupTriggerMode === 'hover') && !!meshConfig.interaction?.triggerUIPopup) ||
            ((audioTriggerMode === 'click' || audioTriggerMode === 'hover') && !!meshConfig.interaction?.playAudio) ||
            ((animationTriggerMode === 'click' || animationTriggerMode === 'hover') && !!meshConfig.interaction?.playModelAnimation) ||
            ((directLinkTriggerMode === 'click' || directLinkTriggerMode === 'hover') && !!meshConfig.interaction?.triggerDirectLink) ||
            !!meshConfig.interaction?.linkedActions?.length ||
            !!meshConfig.interaction?.cutaway ||
            !!meshConfig.interaction?.splatSwap;
        if (!hasPointerDrivenInteraction) {
            return;
        }
        // Linked actions on OTHER hotspots/models, and the model's cutaway and splat swap, run on
        // its activation: click, or hover-in when its activationMode is 'hover' (see linkedActions.ts).
        const runMeshLinkedActions = () => {
            runClickCutaway(meshConfig.interaction?.cutaway);
            runClickSplatSwap(meshConfig.interaction?.splatSwap);
            const actions = meshConfig.interaction?.linkedActions;
            if (!actions?.length) return;
            void linkedActionRunner.run(actions, meshConfig.id ? { kind: 'model', id: meshConfig.id } : undefined);
            renderScheduler.requestFrame('linked-action');
        };
        /**
         * Handle HOVER IN interactions
         * Matches HTML export's OnPointerOverTrigger behavior
         */
        const handleHoverIn = () => {
            // Change cursor to pointer and flag mesh as hovered
            canvasForMesh.style.cursor = 'pointer';
            anyMeshHovered = true;
            hoveringInteractable = true;
            // UI Popup - trigger on hover if popupTriggerMode is 'hover'
            if (popupTriggerMode === 'hover' && meshConfig.interaction?.triggerUIPopup) {
                displayMeshContent(meshConfig);
            }
            // Audio - trigger on hover if audioTriggerMode is 'hover'
            if (audioTriggerMode === 'hover' && meshConfig.interaction?.playAudio && meshData.audioSlotId) {
                const slot = entity.sound?.slot(meshData.audioSlotId);
                if (slot && !slot.isPlaying) {
                    slot.play();
                    meshData.audioPlaying = true;
                    console.log('[CustomMesh] Playing audio on hover for:', meshConfig.name);
                }
            }
            // Animation - trigger on hover if animationTriggerMode is 'hover'
            if (animationTriggerMode === 'hover' && meshConfig.interaction?.playModelAnimation) {
                const allAnimComponents = meshData.allAnimComponents;
                if (allAnimComponents && allAnimComponents.length > 0 && !meshData.isAnimPlaying) {
                    allAnimComponents.forEach((animInfo) => playAnimComponentV2(animInfo));
                    meshData.isAnimPlaying = true;
                    console.log('[CustomMesh] Playing animation on hover for:', meshConfig.name);
                }
            }
            // Direct link - trigger on hover if directLinkTriggerMode is 'hover'
            if (directLinkTriggerMode === 'hover' && meshConfig.interaction?.triggerDirectLink) {
                handleDirectLink(meshConfig);
            }
            if (activationMode === 'hover') runMeshLinkedActions();
        };
        /**
         * Handle HOVER OUT interactions
         * Matches HTML export's OnPointerOutTrigger behavior
         */
        const handleHoverOut = () => {
            // Reset cursor and clear mesh hover flag
            canvasForMesh.style.cursor = '';
            anyMeshHovered = false;
            hoveringInteractable = false;
            // UI Popup - hide on hover out if popupTriggerMode is 'hover'
            if (popupTriggerMode === 'hover' && meshConfig.interaction?.triggerUIPopup) {
                hideMeshContent();
            }
            // Audio - stop on hover out if audioTriggerMode is 'hover'
            if (audioTriggerMode === 'hover' && meshConfig.interaction?.playAudio && meshData.audioSlotId) {
                const slot = entity.sound?.slot(meshData.audioSlotId);
                if (slot && slot.isPlaying) {
                    slot.stop();
                    meshData.audioPlaying = false;
                    console.log('[CustomMesh] Stopped audio on hover out for:', meshConfig.name);
                }
            }
            // Animation - pause on hover out if animationTriggerMode is 'hover'
            if (animationTriggerMode === 'hover' && meshConfig.interaction?.playModelAnimation) {
                const allAnimComponents = meshData.allAnimComponents;
                if (allAnimComponents && allAnimComponents.length > 0 && meshData.isAnimPlaying) {
                    allAnimComponents.forEach((animInfo) => pauseAnimComponentV2(animInfo));
                    meshData.isAnimPlaying = false;
                    console.log('[CustomMesh] Paused animation on hover out for:', meshConfig.name);
                }
            }
        };
        /**
         * Handle CLICK interactions
         * Matches HTML export's OnPickTrigger behavior with toggle support
         */
        const handleClick = () => {
            console.log('[CustomMesh] Clicked mesh:', meshConfig.name);
            // UI Popup - trigger on click if popupTriggerMode is 'click'
            if (popupTriggerMode === 'click' && meshConfig.interaction?.triggerUIPopup) {
                displayMeshContent(meshConfig);
            }
            // Animation - toggle on click if animationTriggerMode is 'click'
            if (animationTriggerMode === 'click' && meshConfig.interaction?.playModelAnimation) {
                const allAnimComponents = meshData.allAnimComponents;
                if (allAnimComponents && allAnimComponents.length > 0) {
                    if (meshData.isAnimPlaying) {
                        allAnimComponents.forEach((animInfo) => pauseAnimComponentV2(animInfo));
                        meshData.isAnimPlaying = false;
                        console.log('[CustomMesh] Paused', allAnimComponents.length, 'animations on click for:', meshConfig.name);
                    }
                    else {
                        allAnimComponents.forEach((animInfo) => playAnimComponentV2(animInfo));
                        meshData.isAnimPlaying = true;
                        console.log('[CustomMesh] Playing', allAnimComponents.length, 'animations on click for:', meshConfig.name);
                    }
                }
            }
            // Audio - toggle on click if audioTriggerMode is 'click'
            if (audioTriggerMode === 'click' && meshConfig.interaction?.playAudio && meshData.audioSlotId) {
                const slot = entity.sound?.slot(meshData.audioSlotId);
                const audioClickBehavior = meshConfig.interaction?.audioClickBehavior || 'stop-restart';
                if (slot) {
                    if (slot.isPlaying) {
                        if (audioClickBehavior === 'stop-restart') {
                            slot.stop();
                            console.log('[CustomMesh] Stopped audio on click for:', meshConfig.name);
                        }
                        else {
                            slot.pause();
                            console.log('[CustomMesh] Paused audio on click for:', meshConfig.name);
                        }
                        meshData.audioPlaying = false;
                    }
                    else {
                        if (audioClickBehavior === 'pause-resume' && slot.isPaused) {
                            slot.resume();
                            console.log('[CustomMesh] Resumed audio on click for:', meshConfig.name);
                        }
                        else {
                            slot.play();
                            console.log('[CustomMesh] Playing audio on click for:', meshConfig.name);
                        }
                        meshData.audioPlaying = true;
                    }
                }
            }
            // Direct link - trigger on click if directLinkTriggerMode is 'click'
            if (directLinkTriggerMode === 'click' && meshConfig.interaction?.triggerDirectLink) {
                handleDirectLink(meshConfig);
            }
            if (activationMode !== 'hover') runMeshLinkedActions();
        };
        // Register with the delegated dispatcher (one shared listener set for all meshes).
        registerInteractiveMesh({
            entity,
            hitTest,
            onHoverIn: handleHoverIn,
            onHoverOut: handleHoverOut,
            onClick: handleClick,
            isHovering: false,
            hoverDriven: activationMode === 'hover' || [popupTriggerMode, audioTriggerMode, animationTriggerMode, directLinkTriggerMode].includes('hover'),
        });
    }
    /**
     * Show mesh popup
     */
    function showMeshPopup(meshConfig: CustomMeshConfigLike): void {
        // Remove existing popup if any
        const existingPopup = document.querySelector('.storysplat-mesh-popup');
        if (existingPopup)
            existingPopup.remove();
        const popup = document.createElement('div');
        popup.className = 'storysplat-mesh-popup';
        popup.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.9);
      padding: 30px;
      border-radius: 12px;
      max-width: 600px;
      max-height: 80vh;
      overflow: auto;
      z-index: 100001;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
        const title = document.createElement('h2');
        title.textContent = meshConfig.interaction?.title || meshConfig.name || 'Custom Mesh';
        title.style.cssText = 'margin-top: 0; margin-bottom: 16px;';
        popup.appendChild(title);
        const text = meshConfig.interaction?.information || meshConfig.interaction?.popupContent;
        if (text) {
            const content = document.createElement('p');
            content.textContent = text;
            content.style.cssText = 'margin: 0; line-height: 1.6;';
            popup.appendChild(content);
        }
        const closeBtn = document.createElement('button');
        closeBtn.textContent = `\u00d7 ${getButtonLabel(currentButtonLabels, 'close')}`;
        closeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(255, 255, 255, 0.2);
      border: none;
      color: white;
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 16px;
    `;
        closeBtn.onclick = () => popup.remove();
        popup.appendChild(closeBtn);
        document.body.appendChild(popup);
    }
    /**
     * Check if a value is within a visibility range, handling wrap-around
     * when start > end in loop mode (e.g., start=80, end=20 means 80→100→0→20).
     */
    function isInVisibilityRange(value: number, start: number, end: number): boolean {
        if (start <= end) {
            // Normal range: start=20, end=80 → visible when 20 <= value <= 80
            return value >= start && value <= end;
        }
        // Wrap-around range (only meaningful in loop mode):
        // start=80, end=20 → visible when value >= 80 OR value <= 20
        if (loopMode === 'loop') {
            return value >= start || value <= end;
        }
        // Non-loop mode: start > end makes no sense, treat as never visible
        return false;
    }

    /**
     * Update mesh visibility based on current waypoint/progress
     * Called when waypoint changes or progress updates
     */
    function updateCustomMeshVisibility(): void {
        const scrollPercent = currentProgress * 100;
        const waypointIndex = getWaypointIndexForProgress(currentProgress);
        customMeshEntities.forEach((meshData) => {
            const { entity, config: meshConfig } = meshData;
            const range = (entity as ViewerRuntimeEntity).visibilityRange;
            if (range) {
                let visible = true;
                if (range.type === 'waypoint') {
                    visible = isInVisibilityRange(waypointIndex, range.start, range.end);
                }
                else if (range.type === 'percentage') {
                    visible = isInVisibilityRange(scrollPercent, range.start, range.end);
                }
                entity.enabled = visible && !hiddenByOverride(entity as ViewerRuntimeEntity);
            }
            // Update opacity if animated (clamped outside the window, so jumping straight past
            // the end still lands on endOpacity). A keyframe animation's opacity wins.
            if (meshConfig.opacityMode === 'animated' && meshConfig.opacityAnimation && (entity as ViewerRuntimeEntity).animationOpacity === undefined) {
                applyOpacityToCustomMesh(entity, resolveAnimatedOpacity(meshConfig.opacityAnimation, scrollPercent));
            }
            // Update billboard state based on range (matches HTML export lines 664-674)
            if (meshConfig.billboard && meshConfig.billboardRange) {
                const bRange = meshConfig.billboardRange;
                let billboardActive = false;
                if (bRange.type === 'percentage') {
                    billboardActive = scrollPercent >= bRange.start && scrollPercent <= bRange.end;
                }
                else if (bRange.type === 'waypoint') {
                    billboardActive = waypointIndex >= bRange.start && waypointIndex <= bRange.end;
                }
                // Store billboard state for the update loop to use
                (entity as ViewerRuntimeEntity)._billboardActive = billboardActive;
            }
            else if (meshConfig.billboard) {
                // Billboard always active if no range specified
                (entity as ViewerRuntimeEntity)._billboardActive = true;
            }
        });
    }
    /**
     * Apply an entity opacity to every render material under a custom mesh.
     * Below full opacity the materials blend and the mesh moves to TranslucentEntityLayer, which
     * draws after the splats and fades against their depth (see utils/materialOpacity.ts), so the
     * mesh composites correctly in front of AND behind the splats; the GLB's own blend modes and
     * depth flags are restored when the opacity returns to 1.
     *
     * Runs every progressUpdate frame for animated-opacity meshes during tour scroll, so after
     * the first full application the cloned materials are cached per entity — the steady state
     * is an epsilon check (skip) or opacity-uniform-only writes, not a full GLB hierarchy walk with
     * blend/depth-flag reassignment + meshInstance key churn per frame.
     */
    const customMeshOpacityCache = new WeakMap<pc.Entity, { materials: pc.StandardMaterial[]; lastOpacity: number }>();
    function applyOpacityToCustomMesh(entity: pc.Entity, opacity: number): void {
        const cached = customMeshOpacityCache.get(entity);
        if (cached) {
            // Bounded error: lastOpacity only advances when applied, so drift can't accumulate.
            // Never skip the step that lands on fully opaque or fully invisible, though: a fade
            // ending 0.9985 -> 1 would otherwise leave the mesh a hair translucent (depth write
            // off, splat depth pass running every frame) or a hair visible at 0.0015 -> 0.
            const landsOnEndpoint = (opacity >= FULL_OPACITY_THRESHOLD || opacity <= INVISIBLE_OPACITY_THRESHOLD)
                && opacity !== cached.lastOpacity;
            if (Math.abs(opacity - cached.lastOpacity) < 0.002 && !landsOnEndpoint) return;
            cached.lastOpacity = opacity;
            for (const mat of cached.materials) {
                applyEntityOpacity(mat, opacity);
            }
            return;
        }
        // Nothing on this entity has been made translucent yet, so full opacity is already what it shows.
        if (opacity >= FULL_OPACITY_THRESHOLD) return;
        const materials = new Set<pc.StandardMaterial>();
        function applyToEntity(ent: pc.Entity): void {
            if (ent.render && ent.render.meshInstances) {
                ent.render.meshInstances.forEach((meshInstance) => {
                    if (meshInstance.material) {
                        // Clone material if it's shared to avoid affecting other instances
                        const stdMat = meshInstance.material as pc.StandardMaterial & { _isCloned?: boolean };
                        if (!stdMat._isCloned) {
                            const cloned = meshInstance.material.clone() as pc.StandardMaterial & { _isCloned?: boolean };
                            cloned._isCloned = true;
                            meshInstance.material = cloned;
                        }
                        applyEntityOpacity(meshInstance.material as pc.StandardMaterial, opacity);
                        materials.add(meshInstance.material as pc.StandardMaterial);
                    }
                });
            }
            // Apply to children
            ent.children.forEach((child) => {
                if (child instanceof pc.Entity) {
                    applyToEntity(child);
                }
            });
        }
        applyToEntity(entity);
        // Cache only once the walk found real materials: the GLB hierarchy instantiates ASYNC
        // after the entity is registered, so caching an early empty walk would permanently
        // freeze animated opacity for this mesh.
        if (materials.size > 0) {
            customMeshOpacityCache.set(entity, { materials: [...materials], lastOpacity: opacity });
        }
    }
    /**
     * Initialize all custom meshes from config
     */
    async function initCustomMeshes(): Promise<void> {
        if (!config.customMeshes || config.customMeshes.length === 0) {
            console.log('[StorySplat Viewer] No custom meshes to create');
            return;
        }
        console.log(`[StorySplat Viewer] Creating ${config.customMeshes.length} custom meshes...`);
        console.log('[StorySplat Viewer] All custom mesh configs:', config.customMeshes);
        // Defer mesh loading to ensure PlayCanvas is fully initialized
        // This helps avoid race conditions with the asset loader
        setTimeout(() => {
            let loadedCount = 0;
            let skippedCount = 0;
            config.customMeshes!.forEach((meshConfig, index: number) => {
                console.log(`[CustomMesh] Processing mesh ${index}:`, {
                    name: meshConfig.name,
                    enabled: meshConfig.enabled,
                    hasModelUrl: !!meshConfig.modelUrl,
                    modelUrl: meshConfig.modelUrl?.substring(0, 100) + '...'
                });
                if (meshConfig.enabled !== false) {
                    try {
                        const entity = loadCustomMesh(meshConfig, index);
                        if (entity) {
                            loadedCount++;
                        }
                        else {
                            skippedCount++;
                            console.warn(`[CustomMesh] Mesh ${meshConfig.name} returned null (likely missing modelUrl)`);
                        }
                    }
                    catch (err) {
                        console.error('[CustomMesh] Error loading mesh', meshConfig.name, ':', err);
                        skippedCount++;
                    }
                }
                else {
                    console.log('[CustomMesh] Skipping disabled mesh:', meshConfig.name, '(enabled =', meshConfig.enabled, ')');
                    skippedCount++;
                }
            });
            console.log(`[CustomMesh] Summary: ${loadedCount} loaded, ${skippedCount} skipped`);
        }, 100);
        console.log(`[StorySplat Viewer] ${config.customMeshes.length} custom meshes queued for loading`);
    }
    /**
     * Cleanup all custom meshes
     */
    function cleanupCustomMeshes(): void {
        const canvasForCleanup = app.graphicsDevice.canvas as HTMLCanvasElement;
        customMeshEntities.forEach((meshData, key) => {
            const entity = meshData.entity;
            meshData.removed = true;
            windSystem.removeObject(key);
            removeCustomMeshCanvasListeners(entity as ViewerRuntimeEntity, canvasForCleanup);
            entity.destroy();
        });
        customMeshEntities.clear();
    }
    /** Remove a custom mesh's interaction registry entry (and, when the registry empties, the
     *  shared delegated canvas listeners). Leaving entries registered kept per-mousemove raycasts
     *  running against destroyed entities — and the distance-based raycast fallback still PASSES
     *  on destroyed entities, so stale click handlers could re-fire (duplicate window.open per
     *  editor edit cycle). Legacy per-mesh handler fields are detached too for safety. */
    function removeCustomMeshCanvasListeners(entity: ViewerRuntimeEntity, canvasEl: HTMLCanvasElement): void {
        const entry = entity.meshInteractionEntry as InteractiveMeshEntry | undefined;
        if (entry) {
            const idx = interactiveMeshRegistry.indexOf(entry);
            if (idx >= 0) interactiveMeshRegistry.splice(idx, 1);
            entity.meshInteractionEntry = undefined;
            if (interactiveMeshRegistry.length === 0 && meshDelegatedListenersBound) {
                meshDelegatedListenersBound = false;
                canvasEl.removeEventListener('click', delegatedMeshClickHandler);
                canvasEl.removeEventListener('mousemove', delegatedMeshHoverHandler);
                canvasEl.removeEventListener('mouseleave', delegatedMeshLeaveHandler);
            }
        }
        if (entity.meshClickHandler) canvasEl.removeEventListener('click', entity.meshClickHandler);
        if (entity.meshHoverHandler) canvasEl.removeEventListener('mousemove', entity.meshHoverHandler);
        if (entity.meshLeaveHandler) canvasEl.removeEventListener('mouseleave', entity.meshLeaveHandler);
    }
    // Listen for progress updates to update mesh visibility
    events.on('progressUpdate', () => {
        updateCustomMeshVisibility();
    });
    // =====================================================
    // SKYBOX SYSTEM
    // =====================================================
    let currentSkyboxEntity: pc.Entity | null = null;
    let skyboxFollowCameraHandler: ((cameraComponent: pc.CameraComponent) => void) | null = null;
    // Horizon offset in DEGREES, applied by reprojecting the sphere skybox's UVs
    // (Unreal-style): the texture's horizon ring is remapped to a higher/lower
    // elevation while zenith and nadir stay pinned, so the squeezed/stretched
    // bands land out of sight below the ground.
    let currentSkyboxVerticalOffset = config.skybox?.verticalOffset ?? config.skyboxVerticalOffset ?? 0;
    // Sphere mesh + pristine UVs kept around so the offset can be re-applied
    // live (slider drags) by rewriting just the UV stream — no rebuild.
    let skyboxSphereMesh: pc.Mesh | null = null;
    let skyboxSphereBaseUvs: number[] | null = null;
    // True while a native→sphere rebuild (triggered by a vertical-offset change) is in flight,
    // so rapid slider ticks don't queue duplicate initSkybox() runs.
    let skyboxOffsetRebuildPending = false;
    // Save original skybox config so we can restore after splat-swap skybox overrides
    const originalSkyboxUrl = config.skybox?.url || config.skyboxUrl || null;
    const originalSkyboxRotation = config.skybox?.rotation ?? config.skyboxRotation ?? 0;
    const originalSkyboxVerticalOffset = config.skybox?.verticalOffset ?? config.skyboxVerticalOffset ?? 0;
    // Skybox images still loading: a captured video frame waits for the ones its swaps applied.
    let skyboxLoadsPending = 0;
    let skyboxIdleWaiters: Array<() => void> = [];
    /** Count one skybox load; the returned function (idempotent) marks it done. */
    function trackSkyboxLoad(): () => void {
        skyboxLoadsPending++;
        let done = false;
        return () => {
            if (done) return;
            done = true;
            skyboxLoadsPending = Math.max(0, skyboxLoadsPending - 1);
            if (skyboxLoadsPending === 0 && skyboxIdleWaiters.length) {
                const waiters = skyboxIdleWaiters;
                skyboxIdleWaiters = [];
                waiters.forEach((resolve) => resolve());
            }
        };
    }
    function whenSkyboxIdle(): Promise<void> {
        return skyboxLoadsPending === 0 ? Promise.resolve() : new Promise<void>((resolve) => skyboxIdleWaiters.push(resolve));
    }
    /**
     * Initialize skybox from config using PlayCanvas native skybox pipeline.
     * Loads equirectangular image → reprojects to cubemap → sets scene.envAtlas.
     * Falls back to a textured sphere if the native pipeline fails.
     *
     * Supports both nested (config.skybox.url) and flat (config.skyboxUrl) formats.
     */
    function initSkybox(preloadedImg?: HTMLImageElement): void {
        // Support both config formats: nested object and flat properties
        const skyboxUrl = config.skybox?.url || config.skyboxUrl;
        if (!skyboxUrl) {
            console.log('[StorySplat Viewer] No skybox configured');
            return;
        }
        // Get rotation from either format (value is in radians)
        const skyboxRotation = config.skybox?.rotation ?? config.skyboxRotation ?? 0;
        const skyboxVerticalOffset = config.skybox?.verticalOffset ?? config.skyboxVerticalOffset ?? 0;
        const skyboxIntensity = config.skybox?.intensity ?? 1.0;
        const enableIBL = config.skybox?.enableIBL ?? true;
        console.log('[StorySplat Viewer] Creating skybox:', skyboxUrl, 'rotation:', skyboxRotation, 'rad =', skyboxRotation * (180 / Math.PI), 'deg', 'IBL:', enableIBL);
        if (is8thWallARSuspendingSkybox()) {
            deferredSkyboxApply = () => initSkybox(preloadedImg);
            arInfo('Deferring skybox init while 8th Wall AR is active');
            return;
        }
        // Use the preloaded image if provided (skips a second network fetch);
        // otherwise kick off a fresh load.
        const skyboxImg = preloadedImg ?? new Image();
        // iOS Safari crashes inside pc.reprojectTexture / pc.EnvLighting.generateAtlas
        // for reasons that have nothing to do with image size — even an 8KB source
        // can trigger it. The textured-sphere fallback covers the same visual
        // requirement (equirect image around the camera) with a single 2D texture
        // and gives us a flat IBL approximation via ambientLight.
        // A horizon offset also forces the sphere: the native skybox samples a
        // cubemap by pure direction with no hook to warp the projection, while
        // the sphere's UVs can be reprojected to move the horizon.
        const forceFallback = isMobileDevice() || skyboxVerticalOffset !== 0;
        const applyPipeline = async () => {
            if (isDestroyed) return;
            if (is8thWallARSuspendingSkybox()) {
                deferredSkyboxApply = runPipeline;
                arInfo('Deferring skybox apply while 8th Wall AR is active');
                return;
            }
            // On WebGPU, `texture.setSource(<img>)` routes through
            // `device.queue.copyExternalImageToTexture`, which silently produces a
            // black texture if the <img> isn't fully decoded yet. `<img>.onload`
            // resolves earlier than decode completes — WebGL2's `texImage2D` masks
            // this by copying eagerly from the partially-decoded bitmap, but WebGPU
            // does not. Decoding into an ImageBitmap first guarantees the upload
            // sees real pixels on both backends. This must cover the sphere
            // fallback too, not just the native pipeline: a horizon offset forces
            // the sphere path, and an undecoded upload there is exactly the
            // "moving the Horizon Offset slider turns the sky black" bug.
            let source: HTMLImageElement | ImageBitmap = skyboxImg;
            try {
                source = await createImageBitmap(skyboxImg);
            } catch (e) {
                // Fall through with the <img>; the pipeline still works on WebGL2
                // and most WebGPU cases. Worst case the engine produces a black
                // skybox and the user gets the existing log.
                console.warn('[StorySplat Viewer] createImageBitmap failed, using raw <img>:', e);
                // Best effort: wait for the browser's own decode so even a raw
                // <img> upload sees real pixels on WebGPU.
                try { await skyboxImg.decode(); } catch { /* non-fatal */ }
            }
            if (isDestroyed) return;
            // Re-check AR suspension — it may have started while decoding.
            if (is8thWallARSuspendingSkybox()) {
                deferredSkyboxApply = runPipeline;
                arInfo('Deferring skybox apply while 8th Wall AR is active');
                return;
            }
            // Clean up any previous skybox only when the replacement is ready to apply.
            cleanupSkybox();
            if (forceFallback) {
                console.log('[StorySplat Viewer] Sphere skybox in use (mobile or vertical offset — native pipeline skipped)');
                initSkyboxFallback(skyboxUrl, source, skyboxRotation, skyboxIntensity, enableIBL, skyboxVerticalOffset);
                return;
            }
            try {
                // 1. Upload the decoded equirect bitmap into a 2D source texture.
                //    RGBA8 + no explicit type ⇒ encoding='srgb', which matches the
                //    sRGB-encoded bytes the browser hands us from JPG/PNG decode.
                const equirect = new pc.Texture(app.graphicsDevice, {
                    width: source.width,
                    height: source.height,
                    format: pc.PIXELFORMAT_RGBA8,
                    mipmaps: false,
                    minFilter: pc.FILTER_LINEAR,
                    magFilter: pc.FILTER_LINEAR,
                    addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
                    projection: pc.TEXTUREPROJECTION_EQUIRECT
                });
                equirect.setSource(source as unknown as HTMLImageElement);
                // 2. Run PlayCanvas's canonical "equirect → skybox + envAtlas"
                //    pipeline. The previous code skipped the generateLightingSource
                //    step and passed a raw LDR, no-mipmap cubemap directly into
                //    generateAtlas. That happened to render on WebGL2 but produces
                //    a black atlas on WebGPU, because the prefilter convolutions
                //    inside generateAtlas rely on a mipmapped HDR source cubemap
                //    (see playcanvas/engine examples/src/examples/graphics/sky.example.mjs).
                //
                //    generateSkyboxCubemap → high-res RGBP cubemap used for the
                //      visible sky (one entry per face, no mipmaps).
                //    generateLightingSource → smaller HDR cubemap WITH mipmaps,
                //      sized for prefiltering.
                //    generateAtlas → equirect-projected RGBP envAtlas containing
                //      packed mip chain + prefiltered reflections + ambient.
                const skyboxCube = pc.EnvLighting.generateSkyboxCubemap(equirect);
                const lighting = pc.EnvLighting.generateLightingSource(equirect);
                const atlas = pc.EnvLighting.generateAtlas(lighting);
                lighting.destroy();
                equirect.destroy();
                // 3. Apply to scene — assigning both `skybox` and `envAtlas` gives
                //    the engine the high-resolution cubemap for visible sky and the
                //    prefiltered atlas for IBL.
                app.scene.skybox = skyboxCube;
                app.scene.envAtlas = atlas;
                app.scene.skyboxMip = 0;
                app.scene.skyboxIntensity = skyboxIntensity;
                // 5. Apply rotation
                // Negate rotation: BabylonJS (left-handed) positive Y = CW from above,
                // PlayCanvas (right-handed) positive Y = CCW from above
                if (skyboxRotation !== 0) {
                    const rotQuat = new pc.Quat();
                    rotQuat.setFromEulerAngles(0, -skyboxRotation * (180 / Math.PI), 0);
                    app.scene.skyboxRotation = rotQuat;
                }
                // 6. IBL ambient lighting
                if (enableIBL) {
                    setBaseAmbient(0.3 * skyboxIntensity, 0.3 * skyboxIntensity, 0.35 * skyboxIntensity);
                    console.log('[StorySplat Viewer] IBL ambient lighting applied:', skyboxIntensity);
                }
                console.log('[StorySplat Viewer] Native skybox applied successfully');
                skyboxOffsetRebuildPending = false;
                // Native skybox/IBL changes do not dirty a scene node. A late download or
                // decode must repaint immediately even after the camera has gone idle.
                renderScheduler.requestFrame('skybox-loaded');
            } catch (err) {
                console.warn('[StorySplat Viewer] Native skybox pipeline failed, using sphere fallback:', err);
                initSkyboxFallback(skyboxUrl, source, skyboxRotation, skyboxIntensity, enableIBL, skyboxVerticalOffset);
            }
        };
        const loadDone = trackSkyboxLoad();
        const runPipeline = async () => {
            try {
                await applyPipeline();
            } finally {
                loadDone();
            }
        };
        if (preloadedImg) {
            // Image is already downloaded and decoded — run pipeline immediately.
            runPipeline();
        } else {
            skyboxImg.crossOrigin = 'anonymous';
            skyboxImg.onload = runPipeline;
            skyboxImg.onerror = (err) => {
                console.error('[StorySplat Viewer] Failed to load skybox texture:', skyboxUrl, err);
                loadDone();
            };
            skyboxImg.src = skyboxUrl;
        }
    }
    /** Clean up all skybox state (native scene skybox + fallback entity). */
    function cleanupSkybox(): void {
        if (!isDestroyed && is8thWallARSuspendingSkybox()) {
            deferredSkyboxApply = cleanupSkybox;
            arInfo('Deferring skybox cleanup while 8th Wall AR is active');
            return;
        }
        deferredSkyboxApply = null;
        skyboxOffsetRebuildPending = false;
        const hadSkybox = !!(app.scene.skybox || app.scene.envAtlas || currentSkyboxEntity);
        // Remove native skybox — clear both the high-res cubemap and the IBL atlas.
        app.scene.skybox = null as unknown as pc.Texture;
        app.scene.envAtlas = null as unknown as pc.Texture;
        // Remove fallback sphere entity (destroying it releases the mesh)
        if (currentSkyboxEntity) {
            currentSkyboxEntity.destroy();
            currentSkyboxEntity = null;
        }
        skyboxSphereMesh = null;
        skyboxSphereBaseUvs = null;
        if (skyboxFollowCameraHandler) {
            app.scene.off('prerender', skyboxFollowCameraHandler);
            skyboxFollowCameraHandler = null;
        }
        if (hadSkybox && !isDestroyed) renderScheduler.requestFrame('skybox-removed');
    }
    /**
     * Reproject the sphere skybox's UVs so the texture's horizon sits at
     * `offsetDeg` degrees of elevation instead of 0 (Unreal-style horizon
     * shift). Each vertex at elevation `e` samples the texture at the
     * elevation of the biased direction `d − (0, sin(offset), 0)`:
     * zenith and nadir stay pinned, the horizon ring moves up (+) or
     * down (−), and the stretched band ends up hidden below the ground.
     *
     * Only the UV stream is rewritten, so this is cheap enough to run on
     * every slider tick.
     */
    function applySkyboxHorizonOffset(offsetDeg: number): void {
        if (!skyboxSphereMesh || !skyboxSphereBaseUvs) return;
        // ±90° would push the horizon through the zenith/nadir where the
        // mapping degenerates — clamp just short of it.
        const clampedDeg = Math.max(-89, Math.min(89, offsetDeg));
        const bias = Math.sin(clampedDeg * (Math.PI / 180));
        const uvs = skyboxSphereBaseUvs.slice();
        if (bias !== 0) {
            for (let i = 1; i < uvs.length; i += 2) {
                // SphereGeometry V is linear in polar angle: 0 at the top
                // pole, 1 at the bottom — so elevation e = (0.5 − v)·π.
                const e = (0.5 - uvs[i]) * Math.PI;
                const eWarped = Math.atan2(Math.sin(e) - bias, Math.cos(e));
                uvs[i] = 0.5 - eWarped / Math.PI;
            }
        }
        skyboxSphereMesh.setUvs(0, uvs);
        skyboxSphereMesh.update(pc.PRIMITIVE_TRIANGLES, false);
    }
    /**
     * Fallback: textured sphere skybox for environments where the native
     * PlayCanvas pipeline (reprojectTexture / EnvLighting) is unavailable.
     */
    function initSkyboxFallback(
        skyboxUrl: string,
        skyboxImg: HTMLImageElement | ImageBitmap,
        rotation: number,
        intensity: number,
        enableIBL: boolean,
        verticalOffset: number = 0
    ): void {
        // Re-read config: every skybox caller updates config before initSkybox, and
        // the slider may have moved again while the image was still decoding.
        currentSkyboxVerticalOffset = config.skybox?.verticalOffset ?? config.skyboxVerticalOffset ?? verticalOffset;
        skyboxOffsetRebuildPending = false;
        const skyboxEntity = new pc.Entity('skybox-fallback');
        // Unlit material: zero diffuse/specular, emissive drives output
        const mat = new pc.StandardMaterial();
        mat.diffuse = new pc.Color(0, 0, 0);
        mat.emissive = new pc.Color(1, 1, 1);
        mat.emissiveIntensity = 1;
        mat.specular = new pc.Color(0, 0, 0);
        mat.cull = pc.CULLFACE_NONE;
        mat.depthTest = false;
        mat.depthWrite = false;
        mat.update();
        // Create texture from the already-loaded image
        const texture = new pc.Texture(app.graphicsDevice, {
            width: skyboxImg.width,
            height: skyboxImg.height,
            format: pc.PIXELFORMAT_RGBA8,
            mipmaps: true,
            minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE
        });
        texture.setSource(skyboxImg as unknown as HTMLImageElement);
        mat.emissiveMap = texture;
        mat.emissiveIntensity = intensity;
        mat.update();
        // Custom sphere mesh instead of the 'sphere' primitive: the horizon
        // reprojection rewrites V per vertex, so it needs enough latitude
        // bands for the warp to interpolate smoothly (the primitive's 16
        // bands visibly facet large offsets), and a mesh whose UV stream we
        // own and can update live.
        const sphereGeom = new pc.SphereGeometry({ latitudeBands: 64, longitudeBands: 64 });
        skyboxSphereBaseUvs = Array.from(sphereGeom.uvs ?? []);
        const sphereMesh = new pc.Mesh(app.graphicsDevice);
        sphereMesh.clear(true, false); // dynamic vertices — UVs rewritten on offset changes
        sphereMesh.setPositions(sphereGeom.positions ?? []);
        sphereMesh.setNormals(sphereGeom.normals ?? []);
        sphereMesh.setUvs(0, skyboxSphereBaseUvs);
        sphereMesh.setIndices(sphereGeom.indices ?? []);
        sphereMesh.update(pc.PRIMITIVE_TRIANGLES);
        skyboxSphereMesh = sphereMesh;
        skyboxEntity.addComponent('render', {
            meshInstances: [new pc.MeshInstance(sphereMesh, mat)],
            castShadows: false,
            receiveShadows: false
        });
        if (skyboxEntity.render!.meshInstances.length > 0) {
            skyboxEntity.render!.meshInstances[0].drawOrder = -10000;
            skyboxEntity.render!.meshInstances[0].pick = false; // Exclude from picker — prevents skybox depth overwriting gsplat depth
        }
        // Apply the current horizon offset by warping the UVs (no-op at 0).
        applySkyboxHorizonOffset(currentSkyboxVerticalOffset);
        // Negative X mirrors the panorama so it appears correctly from inside
        skyboxEntity.setLocalScale(-500, 500, 500);
        if (rotation !== 0) {
            skyboxEntity.setEulerAngles(0, -rotation * (180 / Math.PI), 0);
        }
        // Follow whichever camera is about to render so the skybox appears
        // infinite for BOTH the main camera and any mirror reflection cameras.
        // Fires per-camera before that camera renders its frame.
        skyboxFollowCameraHandler = (cameraComponent: pc.CameraComponent) => {
            const camPos = cameraComponent.entity.getPosition();
            skyboxEntity.setPosition(camPos.x, camPos.y, camPos.z);
        };
        app.scene.on('prerender', skyboxFollowCameraHandler);
        sceneContent.addChild(skyboxEntity);
        currentSkyboxEntity = skyboxEntity;
        // IBL ambient lighting
        if (enableIBL) {
            setBaseAmbient(0.3 * intensity, 0.3 * intensity, 0.35 * intensity);
        }
        console.log('[StorySplat Viewer] Skybox fallback sphere applied');
        renderScheduler.requestFrame('skybox-loaded');
    }
    /**
     * Replace or remove the skybox at runtime (editor API).
     */
    function setSkyboxRuntime(url: string | null, rotation?: number, preloadedImg?: HTMLImageElement, verticalOffset?: number): void {
        if (url) {
            // Preserve the scene's vertical offset when callers (e.g. per-splat
            // skybox swaps) don't specify one.
            const offset = verticalOffset ?? config.skybox?.verticalOffset ?? config.skyboxVerticalOffset ?? 0;
            // Update config so initSkybox reads the new values
            config.skybox = { url, rotation: rotation ?? 0, verticalOffset: offset };
            config.skyboxUrl = url;
            config.skyboxRotation = rotation ?? 0;
            config.skyboxVerticalOffset = offset;
            initSkybox(preloadedImg);
        } else {
            config.skybox = undefined;
            config.skyboxUrl = undefined;
            cleanupSkybox();
        }
    }

    /**
     * Skybox path for panorama portal mode — routes through the same initSkybox
     * the regular scene skybox uses, which picks the right implementation per
     * device: native cubemap + envAtlas on desktop (visually correct, already
     * proven on real scenes), textured-sphere fallback on mobile (iOS Safari
     * crashes inside pc.reprojectTexture / EnvLighting.generateAtlas).
     * Since panorama mode hides the splat, IBL is meaningless — disable it so
     * we don't pump ambient light from the panorama into the (hidden) scene.
     */
    function setPanoramaSkybox(url: string, preloadedImg: HTMLImageElement): void {
        // No vertical offset in panorama mode — the viewer stands at the panorama's center.
        config.skybox = { url, rotation: 0, verticalOffset: 0, intensity: 1.0, enableIBL: false };
        config.skyboxUrl = url;
        config.skyboxRotation = 0;
        config.skyboxVerticalOffset = 0;
        initSkybox(preloadedImg);
    }
    // =====================================================
    // CUSTOM LIGHTING SYSTEM
    // =====================================================
    const lightEntities: pc.Entity[] = [];
    // Runtime light map: always populated (not just editor mode) so entity animation
    // lookups work correctly without relying on fragile positional array indices.
    const runtimeLightMap = new Map<string, pc.Entity>();
    /** Each light's configured settings by id (editor edits merged in): what animated values restore to. */
    const lightBaseConfigs = new Map<string, Partial<LightConfigLike>>();
    /** Layers a scene light must belong to so it reaches custom meshes as well as the splats' World layer. */
    const MESH_LIGHT_LAYER_IDS = [hotspotMeshLayer.id, translucentEntityLayer.id];
    /**
     * Put the first MAX_MESH_LIGHTS scene lights on the custom-mesh layer too. A PlayCanvas
     * light only illuminates mesh instances on layers it belongs to, and custom GLB/GLTF
     * meshes render on HotspotMeshLayer (see loadCustomMesh), so without this the authored
     * lights never reached 3D models even though the splats were already relit by them.
     * Called after every lightEntities mutation. The relighting sync reads the same
     * entities (position/colour/intensity/range/cone) and is unaffected by layer membership.
     */
    function syncMeshLightLayers(): void {
        const changed = syncLightLayersForMeshes(lightEntities.map((entity) => entity.light ?? null), pc.LAYERID_WORLD, MESH_LIGHT_LAYER_IDS);
        if (changed > 0) renderScheduler.requestFrame('mesh-lights');
    }
    /** Route every legacy ambient writer through one place so relighting can override and restore it. */
    function setBaseAmbient(r: number, g: number, b: number): void {
        baseAmbient.set(r, g, b);
        baseAmbientSet = true;
        syncMeshAmbient();
    }
    /**
     * Give 3D models the same ambient term the splat relighting shader applies
     * (ambientColor × ambientIntensity) while relighting is enabled for the scene;
     * otherwise show the legacy ambient. Compare-and-set, safe to call every frame.
     * Gaussian splats never read scene.ambientLight, so this cannot change their look.
     */
    function syncMeshAmbient(): void {
        const relightingActive = relightingEnabled && config.splatRelighting?.enabled === true && !!relightingScript;
        const target = resolveMeshAmbient({
            relightingActive,
            relightAmbient: relightingScript
                ? { r: relightingScript.ambientR, g: relightingScript.ambientG, b: relightingScript.ambientB }
                : null,
            baseAmbient,
            baseAmbientSet,
        });
        if (ambientEquals(app.scene.ambientLight, target)) return;
        app.scene.ambientLight = new pc.Color(target.r, target.g, target.b);
        renderScheduler.requestFrame('mesh-ambient');
    }
    /**
     * Convert hex color to PlayCanvas Color
     */
    function hexToColorForLights(hex: string): pc.Color {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            return new pc.Color(parseInt(result[1], 16) / 255, parseInt(result[2], 16) / 255, parseInt(result[3], 16) / 255);
        }
        return new pc.Color(1, 1, 1); // Default white
    }
    /**
     * Create point light
     */
    function createPointLight(lightConfig: LightConfigLike): pc.Entity {
        const entity = new pc.Entity(lightConfig.name || 'Point Light');
        entity.addComponent('light', {
            type: 'point',
            color: hexToColorForLights(lightConfig.color || '#ffffff'),
            intensity: lightConfig.intensity ?? 1,
            range: lightConfig.range || 10,
            castShadows: lightConfig.castShadows || false
        });
        // Position (negate Z for coordinate conversion)
        const pos = lightConfig.position;
        if (pos) {
            entity.setPosition(pos._x ?? pos.x ?? 0, pos._y ?? pos.y ?? 0, -(pos._z ?? pos.z ?? 0));
        }
        entity.enabled = lightConfig.enabled !== false;
        return entity;
    }
    /**
     * Create directional light
     */
    function createDirectionalLightEntity(lightConfig: LightConfigLike): pc.Entity {
        const entity = new pc.Entity(lightConfig.name || 'Directional Light');
        entity.addComponent('light', {
            type: 'directional',
            color: hexToColorForLights(lightConfig.color || '#ffffff'),
            intensity: lightConfig.intensity ?? 1,
            castShadows: lightConfig.castShadows || false
        });
        // Position (negate Z for coordinate conversion)
        const pos = lightConfig.position;
        if (pos) {
            entity.setPosition(pos._x ?? pos.x ?? 0, pos._y ?? pos.y ?? 0, -(pos._z ?? pos.z ?? 0));
        }
        // The light shines along the authored direction (see effects/shadows/lightDirection).
        orientAuthoredLight(entity, lightConfig, { x: 0, y: -1, z: -1 });
        entity.enabled = lightConfig.enabled !== false;
        return entity;
    }
    /**
     * Point a directional or spot light where the config says. Authored data describes a
     * rotation whose forward axis points along the light (a BJS rotation, or a direction
     * vector); PlayCanvas lights shine down -Y, so the entity gets that rotation turned onto
     * -Y (lightRotationFromForward). Before this, lights were oriented by their forward axis,
     * which lit models and cast shadows 90 degrees off (and undefined for straight down).
     */
    function orientAuthoredLight(entity: pc.Entity, lightConfig: LightConfigLike, fallbackDirection: { x: number; y: number; z: number }): void {
        const rot = lightConfig.rotation;
        if (rot) {
            entity.setRotation(lightRotationFromForward(bjsRotationToPC(rot._x ?? rot.x ?? 0, rot._y ?? rot.y ?? 0, rot._z ?? rot.z ?? 0)));
            return;
        }
        const dir = lightConfig.direction;
        const d = dir
            ? { x: dir._x ?? dir.x ?? 0, y: dir._y ?? dir.y ?? -1, z: -(dir._z ?? dir.z ?? 0) }
            : { x: fallbackDirection.x, y: fallbackDirection.y, z: -fallbackDirection.z };
        entity.setRotation(lightRotationFromDirection(d));
    }
    /**
     * Create hemispheric light (approximated)
     */
    function createHemisphericLight(lightConfig: LightConfigLike): pc.Entity {
        const entity = new pc.Entity(lightConfig.name || 'Hemispheric Light');
        entity.addComponent('light', {
            type: 'directional',
            color: hexToColorForLights(lightConfig.color || '#ffffff'),
            intensity: lightConfig.intensity ?? 1,
            castShadows: false
        });
        // Position
        const pos = lightConfig.position;
        if (pos) {
            entity.setPosition(pos._x ?? pos.x ?? 0, pos._y ?? pos.y ?? 0, -(pos._z ?? pos.z ?? 0));
        }
        // Point upwards for hemispheric effect
        entity.setEulerAngles(-90, 0, 0);
        entity.enabled = lightConfig.enabled !== false;
        // If ground color is specified, adjust scene ambient
        if (lightConfig.groundColor) {
            const groundColor = hexToColorForLights(lightConfig.groundColor);
            const skyColor = hexToColorForLights(lightConfig.color || '#ffffff');
            setBaseAmbient((skyColor.r + groundColor.r * 0.3) / 1.3, (skyColor.g + groundColor.g * 0.3) / 1.3, (skyColor.b + groundColor.b * 0.3) / 1.3);
        }
        return entity;
    }
    /**
     * Create ambient light
     */
    function createAmbientLight(lightConfig: LightConfigLike): null {
        const color = hexToColorForLights(lightConfig.color || '#404040');
        const intensity = lightConfig.intensity ?? 0.4;
        setBaseAmbient(color.r * intensity, color.g * intensity, color.b * intensity);
        console.log('[StorySplat Viewer] Set ambient light:', lightConfig.name || 'Ambient Light');
        return null;
    }
    /**
     * Create spot light
     */
    function createSpotLight(lightConfig: LightConfigLike): pc.Entity {
        const entity = new pc.Entity(lightConfig.name || 'Spot Light');
        // Convert angles from radians to degrees if they come from the editor (stored in radians)
        // PlayCanvas expects degrees for cone angles
        const radToDeg = 180 / Math.PI;
        const angleRad = lightConfig.angle ?? (45 * Math.PI / 180); // Default to 45 degrees in radians
        const angleDeg = angleRad * radToDeg;
        // Inner cone angle is typically smaller than outer - use exponent to derive it
        const exponent = lightConfig.exponent ?? 2;
        const innerAngleDeg = lightConfig.innerConeAngle ?? lightConfig.innerAngle ?? (angleDeg * 0.8);
        const outerAngleDeg = lightConfig.outerConeAngle ?? lightConfig.outerAngle ?? angleDeg;
        entity.addComponent('light', {
            type: 'spot',
            color: hexToColorForLights(lightConfig.color || '#ffffff'),
            intensity: lightConfig.intensity ?? 1,
            range: lightConfig.range || 10,
            innerConeAngle: innerAngleDeg,
            outerConeAngle: outerAngleDeg,
            castShadows: lightConfig.castShadows || false,
            shadowBias: lightConfig.shadowBias ?? 0.05,
            normalOffsetBias: lightConfig.normalOffsetBias ?? 0.05
        });
        // Position (negate Z for coordinate conversion)
        const pos = lightConfig.position;
        if (pos) {
            entity.setPosition(pos._x ?? pos.x ?? 0, pos._y ?? pos.y ?? 0, -(pos._z ?? pos.z ?? 0));
        }
        // Default: point straight down
        orientAuthoredLight(entity, lightConfig, { x: 0, y: -1, z: 0 });
        entity.enabled = lightConfig.enabled !== false;
        return entity;
    }
    /**
     * Initialize all custom lights from config
     */
    function initCustomLights(): void {
        if (!config.lights || config.lights.length === 0) {
            console.log('[StorySplat Viewer] No custom lights to create');
            return;
        }
        console.log(`[StorySplat Viewer] Creating ${config.lights.length} custom lights...`);
        config.lights.forEach((lightConfig, index: number) => {
            let entity: pc.Entity | null = null;
            switch (lightConfig.type) {
                case 'point':
                    entity = createPointLight(lightConfig);
                    break;
                case 'directional':
                    entity = createDirectionalLightEntity(lightConfig);
                    break;
                case 'hemispheric':
                    entity = createHemisphericLight(lightConfig);
                    break;
                case 'ambient':
                    createAmbientLight(lightConfig);
                    break;
                case 'spot':
                    entity = createSpotLight(lightConfig);
                    break;
                default:
                    console.warn('[StorySplat Viewer] Unknown light type:', lightConfig.type);
                    return;
            }
            if (entity) {
                sceneContent.addChild(entity);
                lightEntities.push(entity);
                const lightId = lightConfig.id || lightConfig.name || `light-${index}`;
                runtimeLightMap.set(lightId, entity);
                lightBaseConfigs.set(lightId, { ...lightConfig });
                console.log(`[StorySplat Viewer] Created ${lightConfig.type} light:`, lightConfig.name || `Light ${index}`);
            }
        });
        syncMeshLightLayers();
        console.log('[StorySplat Viewer] Lighting setup complete');
    }

    // =====================================================
    // SPLAT RELIGHTING SYSTEM
    // =====================================================

    // Pre-allocated array for collecting light data each frame
    type RelightLightDescriptor = { position: pc.Vec3; color: pc.Color; intensity: number; range: number; direction?: { x: number; y: number; z: number }; cosInnerAngle?: number; cosOuterAngle?: number; shadowSlot?: number; localShadowSlot?: number; cloudSun?: boolean };
    const _relightLightData: Array<RelightLightDescriptor> = [];
    // Pooled descriptors + change detection: descriptors are reused across frames (position/color
    // hold live entity-internal references, refreshed by the per-frame getPosition/color reads),
    // and updateLights is only pushed to a script when the light VALUES changed since the version
    // it last applied. Value comparison (not events) — playground balls and editor gizmo drags
    // move lights without any event. A script's applied version advances ONLY once its material
    // exists (updateLights is a no-op on a null material — marking clean earlier would leave the
    // splat permanently unlit), and late-joining swap-entity scripts start unversioned so they
    // receive current data on join.
    const _relightDescriptorPool: RelightLightDescriptor[] = [];
    const _relightSpotDir = new pc.Vec3();
    const _relightLastState: number[] = [];
    let _relightDataVersion = 0;
    const _relightAppliedVersion = new WeakMap<object, number>();
    const DEFAULT_RELIGHT_AMBIENT_INTENSITY = 1;

    function applyRelightingAmbient(script: RelightingEffectScript, relightConfig?: Partial<SplatRelightingConfig>): void {
        const ambientColor = relightConfig?.ambientColor || '#ffffff';
        const rawIntensity = relightConfig?.ambientIntensity;
        // Honor the configured ambient intensity exactly, INCLUDING 0 — an ambient
        // of 0 is the dramatic "only lit where a light hits" look creators expect.
        // Only fall back to the default when no value is provided at all
        // (undefined / NaN), not when it's an explicit 0.
        const ambientIntensity = typeof rawIntensity === 'number' && Number.isFinite(rawIntensity)
            ? rawIntensity
            : DEFAULT_RELIGHT_AMBIENT_INTENSITY;

        script.setAmbientColor(ambientColor);
        script.ambientR *= ambientIntensity;
        script.ambientG *= ambientIntensity;
        script.ambientB *= ambientIntensity;
    }

    /**
     * Initialize splat relighting effect.
     * Auto-enabled when lights exist in the scene (unless explicitly disabled).
     */
    /** `forClouds`: only cloud shadows need the receiving shader (it then starts at fade 0 without lights). */
    function initSplatRelighting(forClouds = false): void {
        if (!splatEntity) return;

        const relightConfig = config.splatRelighting;
        const hasLights = lightEntities.length > 0;

        console.log('[StorySplat Viewer] initSplatRelighting:', {
            relightConfig,
            hasLights,
            lightCount: lightEntities.length,
            splatEntity: !!splatEntity
        });

        // Need lights to do anything (cloud shadows excepted: they darken the colour at fade 0)
        if (!hasLights && !forClouds) {
            console.log('[StorySplat Viewer] No lights, skipping splat relighting');
            return;
        }

        // Ensure script component exists
        if (!splatEntity.script) {
            splatEntity.addComponent('script');
        }

        const GsplatRelightingClass = getGsplatRelightingClass();
        const scriptComp = splatEntity.script as ScriptComponentWithCreate | undefined;
        relightingScript = (scriptComp?.create?.(GsplatRelightingClass) as RelightingEffectScript | undefined) ?? null;

        if (relightingScript) {
            applyRelightingAmbient(relightingScript, relightConfig);

            // Determine starting fade:
            // - Editor mode: always on when enabled (author needs to see the effect)
            // - Viewer mode + no toggle: always on
            // - Viewer mode + toggle + viewerDefaultOn: on
            // - Viewer mode + toggle + !viewerDefaultOn: off
            let startFade = 0.0;
            if (relightConfig?.enabled === true && hasLights) {
                if (isEditorMode) {
                    startFade = 1.0; // Editor always shows relighting when enabled
                } else if (relightConfig?.allowViewerToggle) {
                    startFade = relightConfig?.viewerDefaultOn ? 1.0 : 0.0;
                } else {
                    startFade = 1.0; // No toggle = always on
                }
            }
            relightingScript.setRelightFade(startFade);
            relightingScript.enabled = true;
            relightingEnabled = true;
            allRelightingScripts.add(relightingScript);
            console.log('[StorySplat Viewer] Splat relighting initialized, startFade:', startFade);
        }
    }

    /**
     * Add a relighting script to a swap entity so the wipe transition can
     * combine shaders and lights affect the new splat after swap.
     */
    function addRelightingToEntity(entity: pc.Entity): void {
        if (!relightingEnabled || !relightingScript) return;

        // Already has one?
        if ((entity.script as any)?.gsplatRelighting) return;

        if (!entity.script) {
            entity.addComponent('script');
        }

        const GsplatRelightingClass = getGsplatRelightingClass();
        const scriptComp = entity.script as ScriptComponentWithCreate | undefined;
        const script = (scriptComp?.create?.(GsplatRelightingClass) as RelightingEffectScript | undefined) ?? null;

        if (script) {
            // Copy settings from the primary relighting script
            script.ambientR = relightingScript.ambientR;
            script.ambientG = relightingScript.ambientG;
            script.ambientB = relightingScript.ambientB;
            script.relightFade = relightingScript.relightFade;
            script.enabled = true;
            allRelightingScripts.add(script);
            console.log('[Relighting] Added relighting script to swap entity:', entity.name);
        }
    }

    /**
     * Sync light positions/colors from PlayCanvas light entities to the relighting shader.
     * Called every frame when relighting is active.
     */
    let _relightLightSyncDetailLogged = false;
    function syncRelightingLights(): void {
        if (allRelightingScripts.size === 0) return;

        let relightLightsChanged = false;
        const prevCount = _relightLightData.length;
        _relightLightData.length = 0;

        for (const entity of lightEntities) {
            if (!entity.enabled || !entity.light) continue;

            const light = entity.light;
            const pos = entity.getPosition();

            // Hemispheric lights are created as 'directional' type in PlayCanvas.
            const rawType = light.type;
            const isDirectional = rawType === 'directional';
            const range = isDirectional ? 0 : (light.range || 10);

            if (!_relightLightSyncDetailLogged) {
                console.log('[Relighting] Light:', entity.name,
                    'rawType:', rawType, '(typeof:', typeof rawType, ')',
                    'isDir:', isDirectional,
                    'color:', light.color.r.toFixed(3), light.color.g.toFixed(3), light.color.b.toFixed(3),
                    'intensity:', light.intensity,
                    'range:', range,
                    'pos:', pos.x.toFixed(2), pos.y.toFixed(2), pos.z.toFixed(2));
            }

            const isSpot = rawType === 'spot';
            // The direction the light shines: its -Y axis (effects/shadows/lightDirection).
            const fwd = isSpot ? lightDirectionOf(entity, _relightSpotDir) : undefined;
            // Directional lights whose shadow map the splats sample (-1 = none), and spot / point
            // lights whose atlas shadow they sample.
            const shadowSlot = isDirectional && shadowSystem ? shadowSystem.slotOf(entity) : -1;
            const localShadowSlot = !isDirectional && range > 0 && shadowSystem ? shadowSystem.localSlotOf(entity) : -1;

            // Reuse a pooled descriptor. Non-spot lights explicitly clear the spot fields —
            // updateLights branches on `direction` truthiness, so stale pooled spot data would
            // corrupt point lights.
            const di = _relightLightData.length;
            let desc = _relightDescriptorPool[di];
            if (!desc) {
                desc = { position: pos, color: light.color, intensity: 0, range: 0 };
                _relightDescriptorPool[di] = desc;
            }
            desc.position = pos;
            desc.color = light.color;
            desc.intensity = light.intensity;
            desc.range = range;
            desc.shadowSlot = shadowSlot;
            desc.localShadowSlot = localShadowSlot;
            // The directional light the clouds take as their sun: cloud shadows remove its light.
            const cloudSun = isDirectional && cloudSystem.size > 0 && cloudSystem.sunEntity() === entity;
            desc.cloudSun = cloudSun;
            if (isSpot && fwd) {
                const dir = desc.direction ?? (desc.direction = { x: 0, y: 0, z: 0 });
                dir.x = fwd.x; dir.y = fwd.y; dir.z = fwd.z;
                desc.cosInnerAngle = Math.cos((light.innerConeAngle ?? 36) * Math.PI / 180);
                desc.cosOuterAngle = Math.cos((light.outerConeAngle ?? 45) * Math.PI / 180);
            } else {
                desc.direction = undefined;
                desc.cosInnerAngle = undefined;
                desc.cosOuterAngle = undefined;
            }
            _relightLightData.push(desc);

            // Change detection: compare-and-store this light's values into the flat state array
            // (stride 17: type, pos×3, color×3, intensity, range, dir×3, cosInner, cosOuter, shadow
            // slot, spot / point shadow slot, clouds' sun).
            const si = di * 17;
            const typeCode = isSpot ? 2 : (isDirectional ? 1 : 0);
            const fx = desc.direction?.x ?? 0, fy = desc.direction?.y ?? 0, fz = desc.direction?.z ?? 0;
            const cosIn = desc.cosInnerAngle ?? 0, cosOut = desc.cosOuterAngle ?? 0;
            if (_relightLastState[si] !== typeCode || _relightLastState[si + 1] !== pos.x ||
                _relightLastState[si + 2] !== pos.y || _relightLastState[si + 3] !== pos.z ||
                _relightLastState[si + 4] !== light.color.r || _relightLastState[si + 5] !== light.color.g ||
                _relightLastState[si + 6] !== light.color.b || _relightLastState[si + 7] !== light.intensity ||
                _relightLastState[si + 8] !== range || _relightLastState[si + 9] !== fx ||
                _relightLastState[si + 10] !== fy || _relightLastState[si + 11] !== fz ||
                _relightLastState[si + 12] !== cosIn || _relightLastState[si + 13] !== cosOut ||
                _relightLastState[si + 14] !== shadowSlot || _relightLastState[si + 15] !== localShadowSlot ||
                _relightLastState[si + 16] !== (cloudSun ? 1 : 0)) {
                _relightLastState[si] = typeCode;
                _relightLastState[si + 1] = pos.x; _relightLastState[si + 2] = pos.y; _relightLastState[si + 3] = pos.z;
                _relightLastState[si + 4] = light.color.r; _relightLastState[si + 5] = light.color.g; _relightLastState[si + 6] = light.color.b;
                _relightLastState[si + 7] = light.intensity; _relightLastState[si + 8] = range;
                _relightLastState[si + 9] = fx; _relightLastState[si + 10] = fy; _relightLastState[si + 11] = fz;
                _relightLastState[si + 12] = cosIn; _relightLastState[si + 13] = cosOut;
                _relightLastState[si + 14] = shadowSlot;
                _relightLastState[si + 15] = localShadowSlot;
                _relightLastState[si + 16] = cloudSun ? 1 : 0;
                relightLightsChanged = true;
            }
        }

        if (!_relightLightSyncDetailLogged && _relightLightData.length > 0) {
            _relightLightSyncDetailLogged = true;
            console.log('[Relighting] Total lights synced:', _relightLightData.length);
        }

        // Bump the data version on any value/count change; scripts then re-apply only when their
        // last-applied version is stale (late-joining scripts have no entry → always stale).
        if (relightLightsChanged || _relightLightData.length !== prevCount) {
            _relightDataVersion++;
            // Relighting repaints the splats without moving a node, and the relighting script is
            // treated as static by the on-demand graph probe (it is always enabled and normally
            // just re-pushes unchanged uniforms). This is the authoritative signal that the light
            // values actually changed — colour, intensity, range, cone angles, position or count —
            // so ask for a frame here whatever drove the change.
            renderScheduler.requestFrame('relighting');
        }
        // Push light data to relighting scripts (primary + swap entities) that haven't applied
        // this version yet. Applied-version advances only once the script's material exists —
        // updateLights no-ops on a null material, and the every-frame retry is what eventually
        // lights a script whose material resolves late.
        for (const script of allRelightingScripts) {
            if (!script.enabled) continue;
            if (_relightAppliedVersion.get(script) === _relightDataVersion) continue;
            script.updateLights(_relightLightData);
            if (script.material) {
                _relightAppliedVersion.set(script, _relightDataVersion);
            }
        }
    }

    // =====================================================
    // SHADOWS (effects/shadows)
    // =====================================================
    // Models and (optionally) the splats cast; the splats and models receive. Settings come
    // from config.splatRelighting (scene) and each light's config; the system reads them every
    // frame, so editor changes and keyframed strength apply without a rebuild.
    {
        let gsplatEntities: pc.Entity[] | null = null;
        const gsplatSystem = app.systems.gsplat as unknown as { on?: (e: string, fn: () => void) => void } | undefined;
        gsplatSystem?.on?.('add', () => { gsplatEntities = null; });
        gsplatSystem?.on?.('remove', () => { gsplatEntities = null; });
        const lightEntries: Array<{ entity: pc.Entity; config: Partial<LightConfigLike> }> = [];
        const meshCasters: Array<{ root: pc.Entity; castShadows?: boolean; receiveShadows?: boolean }> = [];
        const receiverMaterials: pc.Material[] = [];
        let cloudShadowsBound = false;
        shadowSystem = new ShadowSystem({
            app,
            camera: () => camera.camera ?? null,
            sceneConfig: () => {
                const cfg = config.splatRelighting;
                const animated = sceneAnimationOverrides.relighting.get('relighting.shadowIntensity');
                return animated === undefined ? cfg : { ...cfg, shadowIntensity: animated };
            },
            lights: () => {
                lightEntries.length = 0;
                runtimeLightMap.forEach((entity, id) => {
                    if (entity.light) lightEntries.push({ entity, config: lightBaseConfigs.get(id) ?? {} });
                });
                return lightEntries;
            },
            splatEntities: () => {
                if (!gsplatEntities) {
                    gsplatEntities = (app.root.findComponents('gsplat') as pc.GSplatComponent[])
                        .filter((c) => c.layers.includes(pc.LAYERID_WORLD))
                        .map((c) => c.entity);
                }
                return gsplatEntities;
            },
            meshCasters: () => {
                meshCasters.length = 0;
                customMeshEntities.forEach((meshData) => {
                    meshCasters.push({ root: meshData.entity, castShadows: meshData.config.castShadows, receiveShadows: meshData.config.receiveShadows });
                });
                for (const ball of playgroundBalls) meshCasters.push({ root: ball.entity });
                return meshCasters;
            },
            receiverMaterials: () => {
                receiverMaterials.length = 0;
                for (const script of allRelightingScripts) if (script.enabled && script.material) receiverMaterials.push(script.material);
                return receiverMaterials;
            },
            relighting: () => ({
                enabled: relightingEnabled && config.splatRelighting?.enabled === true,
                fade: relightingScript?.relightFade ?? 0,
            }),
            tierInput: () => ({ mobile: usesMobileRenderProfile(), performanceMode }),
            requestFrame: (reason) => renderScheduler.requestFrame(reason),
            worldLayer: app.scene.layers.getLayerById(pc.LAYERID_WORLD)!,
            afterSplatsLayer: translucentEntityLayer,
            sceneRoot: sceneContent,
            splatVersion: readSplatWorldVersion,
            // Splats swaying in the wind redraw the maps they cast into (throttled there).
            splatMotion: () => windSystem.motionVersion,
        });
        app.on('update', () => {
            // The shader first, so the shadow system binds this frame's maps to an enabled
            // script's material (a material that left its list gets the empty placeholders).
            ensureRelightingForShadows();
            shadowSystem?.update();
            // Cloud shadows on the scan ride on the same receiving shader.
            if (cloudSystem.size > 0 || cloudShadowsBound) {
                receiverMaterials.length = 0;
                for (const script of allRelightingScripts) if (script.enabled && script.material) receiverMaterials.push(script.material);
                // Relighting removes the sun's light under a cloud; otherwise (off, toggled off by the
                // visitor, or no lights) the cloud shadow darkens the colour like shadow-only mode.
                const relightingLit = relightingEnabled && config.splatRelighting?.enabled === true && lightEntities.length > 0 && (relightingScript?.relightFade ?? 0) > 0.001;
                cloudSystem.bindShadowReceivers(receiverMaterials, relightingLit);
                cloudShadowsBound = cloudSystem.size > 0;
            }
        });
    }
    /** Sum of the streamed splat worlds' versions: changes when LOD streaming swaps chunks in or out. */
    function readSplatWorldVersion(): number {
        try {
            const director = (app.renderer as unknown as {
                gsplatDirector?: { camerasMap?: Map<unknown, { layersMap?: Map<unknown, { gsplatManager?: { world?: { currentVersion?: number } } }> }> };
            })?.gsplatDirector;
            let version = 0;
            director?.camerasMap?.forEach((cd) => cd.layersMap?.forEach((ld) => {
                version += ld?.gsplatManager?.world?.currentVersion ?? 0;
            }));
            return version;
        } catch { return 0; }
    }
    /**
     * Shadows on the splats run in the relighting shader, so it must stay on (at fade 0, which
     * leaves the colours untouched) while shadows are on even if relighting itself is off.
     */
    function ensureRelightingForShadows(): void {
        if (!splatEntity) return;
        const forShadows = !!shadowSystem && lightEntities.length > 0 && shadowSystem.wantsSplatReceiving();
        // Cloud shadows need the receiving shader even in a scene without lights or shadows.
        const forClouds = cloudSystem.wantsShadowReceiving();
        if (!forShadows && !forClouds) return;
        if (!relightingScript) {
            initSplatRelighting(forClouds);
            return;
        }
        for (const script of allRelightingScripts) {
            if (!script.enabled) {
                script.setRelightFade(0);
                script.enabled = true;
            }
        }
    }

    // =====================================================
    // PLAYGROUND MODE — Light Balls
    // =====================================================

    interface PlaygroundBall {
        entity: pc.Entity;
        velocity: pc.Vec3;
        age: number;
        lifetime: number;
        baseIntensity: number;
        radius: number;
    }

    const BALL_COLORS: [number, number, number][] = [
        [1.0, 0.2, 0.1], [0.1, 0.6, 1.0], [0.2, 1.0, 0.3],
        [1.0, 0.8, 0.1], [0.8, 0.2, 1.0], [1.0, 0.4, 0.7], [0.1, 1.0, 0.9],
    ];
    const playgroundBalls: PlaygroundBall[] = [];
    const playgroundMaterials: pc.StandardMaterial[] = [];
    let playgroundActive = false;
    const PLAYGROUND_GRAVITY = 9.8;
    const PLAYGROUND_BOUNCE = 0.6;
    const PLAYGROUND_BALL_RADIUS = 0.15;
    const PLAYGROUND_LIGHT_RANGE = 8;
    const PLAYGROUND_LIGHT_INTENSITY = 2;
    const PLAYGROUND_LIFETIME = 12;
    const PLAYGROUND_MAX_BALLS = 16;

    function getPlaygroundMaterial(index: number): pc.StandardMaterial {
        if (playgroundMaterials.length === 0) {
            for (const [r, g, b] of BALL_COLORS) {
                const mat = new pc.StandardMaterial();
                mat.diffuse.set(0, 0, 0);
                mat.specular.set(0, 0, 0);
                mat.emissive.set(r, g, b);
                mat.emissiveIntensity = 4;
                mat.useLighting = false;
                mat.update();
                playgroundMaterials.push(mat);
            }
        }
        return playgroundMaterials[index % playgroundMaterials.length];
    }

    const METALLIC_COLORS: [number, number, number][] = [
        [0.85, 0.85, 0.88], // silver
        [0.83, 0.69, 0.22], // gold
        [0.72, 0.45, 0.20], // copper
        [0.56, 0.57, 0.58], // steel
        [0.66, 0.66, 0.68], // chrome
    ];
    const metallicMaterials: pc.StandardMaterial[] = [];
    function getMetallicMaterial(index: number): pc.StandardMaterial {
        if (metallicMaterials.length === 0) {
            for (const [r, g, b] of METALLIC_COLORS) {
                const mat = new pc.StandardMaterial();
                mat.diffuse.set(r, g, b);
                mat.specular.set(1, 1, 1);
                mat.metalness = 0.9;
                mat.gloss = 0.8;
                mat.useMetalness = true;
                mat.update();
                metallicMaterials.push(mat);
            }
        }
        return metallicMaterials[index % metallicMaterials.length];
    }

    /**
     * Get collision entities from CharacterController or CameraControls.
     */
    function getCollisionEntities(): pc.Entity[] {
        if (characterController) return characterController.collisionMeshEntities;
        // CameraControls stores collision entities privately — access them directly
        return (cameraControls as any)._collisionEntities ?? [];
    }

    /**
     * AABB collision test for a ball against scene collision meshes.
     * Returns the surface Y if the ball is above a collision mesh, else null.
     */
    function ballGroundCheck(pos: pc.Vec3, radius: number): number | null {
        let highestY: number | null = null;

        for (const entity of getCollisionEntities()) {
            const ePos = entity.getPosition();
            const eScale = entity.getLocalScale();
            const bounds = (entity as any)._collisionBounds as pc.BoundingBox | undefined;

            let cx: number, cy: number, cz: number;
            let hw: number, hh: number, hd: number;
            if (bounds) {
                cx = bounds.center.x; cy = bounds.center.y; cz = bounds.center.z;
                hw = bounds.halfExtents.x; hh = bounds.halfExtents.y; hd = bounds.halfExtents.z;
            } else {
                cx = ePos.x; cy = ePos.y; cz = ePos.z;
                hw = eScale.x / 2; hh = eScale.y / 2; hd = eScale.z / 2;
            }

            // Check if ball is within the horizontal footprint
            if (Math.abs(pos.x - cx) < hw + radius && Math.abs(pos.z - cz) < hd + radius) {
                const topY = cy + hh;
                if (highestY === null || topY > highestY) {
                    highestY = topY;
                }
            }
        }

        // Voxel octree ground check using splat entity transform
        const voxel = characterController?.voxelCollisionInstance;
        if (voxel && splatEntity) {
            const invMat = new pc.Mat4();
            invMat.copy(splatEntity.getWorldTransform()).invert();
            const filePos = new pc.Vec3();
            invMat.transformPoint(pos, filePos);
            const fileGroundY = voxel.findGround(filePos.x, filePos.z, filePos.y + radius * 2);
            if (fileGroundY !== null) {
                // Transform file-space ground back to world Y
                const m = splatEntity.getWorldTransform().data;
                const worldGroundY = m[1] * filePos.x + m[5] * fileGroundY + m[9] * filePos.z + m[13];
                if (highestY === null || worldGroundY > highestY) {
                    highestY = worldGroundY;
                }
            }
        } else if (voxel) {
            // Fallback: no splat entity, use findGroundWorld (legacy path)
            const voxelGround = voxel.findGroundWorld(pos.x, pos.z, pos.y, radius * 2);
            if (voxelGround !== null) {
                if (highestY === null || voxelGround > highestY) {
                    highestY = voxelGround;
                }
            }
        }

        return highestY;
    }

    /**
     * AABB wall collision — push ball out of overlapping collision meshes horizontally.
     */
    function ballWallCollision(pos: pc.Vec3, velocity: pc.Vec3, radius: number): void {
        for (const entity of getCollisionEntities()) {
            const ePos = entity.getPosition();
            const eScale = entity.getLocalScale();
            const meshType = (entity as any)._collisionMeshType as string | undefined;
            if (meshType === 'floor' || meshType === 'plane') continue;

            const bounds = (entity as any)._collisionBounds as pc.BoundingBox | undefined;
            let cx: number, cy: number, cz: number;
            let hw: number, hh: number, hd: number;
            if (bounds) {
                cx = bounds.center.x; cy = bounds.center.y; cz = bounds.center.z;
                hw = bounds.halfExtents.x; hh = bounds.halfExtents.y; hd = bounds.halfExtents.z;
            } else {
                cx = ePos.x; cy = ePos.y; cz = ePos.z;
                hw = eScale.x / 2; hh = eScale.y / 2; hd = eScale.z / 2;
            }

            const dx = pos.x - cx;
            const dy = pos.y - cy;
            const dz = pos.z - cz;

            // Check overlap
            const overlapX = hw + radius - Math.abs(dx);
            const overlapY = hh + radius - Math.abs(dy);
            const overlapZ = hd + radius - Math.abs(dz);

            if (overlapX > 0 && overlapY > 0 && overlapZ > 0) {
                // Push out along smallest overlap axis
                if (overlapX <= overlapY && overlapX <= overlapZ) {
                    pos.x += Math.sign(dx) * overlapX;
                    velocity.x = -velocity.x * PLAYGROUND_BOUNCE;
                } else if (overlapZ <= overlapY) {
                    pos.z += Math.sign(dz) * overlapZ;
                    velocity.z = -velocity.z * PLAYGROUND_BOUNCE;
                } else {
                    pos.y += Math.sign(dy) * overlapY;
                    if (dy > 0) velocity.y = Math.abs(velocity.y) * PLAYGROUND_BOUNCE;
                    else velocity.y = -Math.abs(velocity.y) * PLAYGROUND_BOUNCE;
                }
            }
        }

        // Voxel octree wall collision — push-out + bounce
        const voxelW = characterController?.voxelCollisionInstance;
        if (voxelW) {
            let fx: number, fy: number, fz: number;
            let wx: number, wy: number, wz: number;
            if (splatEntity) {
                const invMat = new pc.Mat4();
                invMat.copy(splatEntity.getWorldTransform()).invert();
                const fp = new pc.Vec3();
                invMat.transformPoint(pos, fp);
                fx = fp.x; fy = fp.y; fz = fp.z;
            } else {
                fx = pos.x; fy = -pos.y; fz = -pos.z;
            }
            const pushOut = voxelW.querySphere(fx, fy, fz, radius);
            if (pushOut) {
                if (splatEntity) {
                    const m = splatEntity.getWorldTransform().data;
                    wx = m[0] * pushOut.x + m[4] * pushOut.y + m[8] * pushOut.z;
                    wy = m[1] * pushOut.x + m[5] * pushOut.y + m[9] * pushOut.z;
                    wz = m[2] * pushOut.x + m[6] * pushOut.y + m[10] * pushOut.z;
                } else {
                    wx = pushOut.x; wy = -pushOut.y; wz = -pushOut.z;
                }
                pos.x += wx;
                pos.y += wy;
                pos.z += wz;

                // Bounce velocity along push-out direction
                const len = Math.sqrt(wx * wx + wy * wy + wz * wz);
                if (len > 1e-6) {
                    const nx = wx / len;
                    const ny = wy / len;
                    const nz = wz / len;
                    const vDotN = velocity.x * nx + velocity.y * ny + velocity.z * nz;
                    if (vDotN < 0) {
                        velocity.x -= (1 + PLAYGROUND_BOUNCE) * vDotN * nx;
                        velocity.y -= (1 + PLAYGROUND_BOUNCE) * vDotN * ny;
                        velocity.z -= (1 + PLAYGROUND_BOUNCE) * vDotN * nz;
                    }
                }
            }
        }
    }

    function spawnPlaygroundBall(): void {
        if (playgroundBalls.length >= PLAYGROUND_MAX_BALLS) {
            const oldest = playgroundBalls.shift()!;
            removePlaygroundBall(oldest);
        }

        const colorIdx = Math.floor(Math.random() * BALL_COLORS.length);
        const [r, g, b] = BALL_COLORS[colorIdx];

        // Spawn from camera position + forward
        const camPos = camera.getPosition();
        const camFwd = camera.forward;
        const spawnPos = new pc.Vec3(
            camPos.x + camFwd.x * 2,
            camPos.y + camFwd.y * 2,
            camPos.z + camFwd.z * 2
        );

        console.log('[Playground] Spawning ball #' + (playgroundBalls.length + 1),
            'cam:', camPos.x.toFixed(2), camPos.y.toFixed(2), camPos.z.toFixed(2),
            'fwd:', camFwd.x.toFixed(2), camFwd.y.toFixed(2), camFwd.z.toFixed(2),
            'spawn:', spawnPos.x.toFixed(2), spawnPos.y.toFixed(2), spawnPos.z.toFixed(2),
            'color:', [r.toFixed(1), g.toFixed(1), b.toFixed(1)],
            'collisionEntities:', getCollisionEntities().length,
            'lightEntities:', lightEntities.length,
            'relightingEnabled:', relightingEnabled);

        const entity = new pc.Entity('playground-ball');
        entity.addComponent('render', {
            type: 'sphere',
            material: getPlaygroundMaterial(colorIdx),
            castShadows: false // the shadow system turns it on while a light casts
        });
        entity.setLocalScale(PLAYGROUND_BALL_RADIUS * 2, PLAYGROUND_BALL_RADIUS * 2, PLAYGROUND_BALL_RADIUS * 2);
        entity.addComponent('light', {
            type: 'point',
            color: new pc.Color(r, g, b),
            intensity: PLAYGROUND_LIGHT_INTENSITY,
            range: PLAYGROUND_LIGHT_RANGE,
            castShadows: false
        });
        entity.setPosition(spawnPos);
        sceneContent.addChild(entity);
        lightEntities.push(entity);
        syncMeshLightLayers();

        // Launch forward + slightly up
        const velocity = new pc.Vec3(
            camFwd.x * 8 + (Math.random() - 0.5) * 2,
            camFwd.y * 8 + 3,
            camFwd.z * 8 + (Math.random() - 0.5) * 2
        );

        playgroundBalls.push({
            entity, velocity, age: 0,
            lifetime: PLAYGROUND_LIFETIME,
            baseIntensity: PLAYGROUND_LIGHT_INTENSITY,
            radius: PLAYGROUND_BALL_RADIUS
        });
    }

    function spawnShadowBall(): void {
        // Auto-enable shadows if needed (mirrors relighting auto-enable in startPlayground)
        if (!config.splatRelighting?.shadowsEnabled) {
            console.log('[Playground] Auto-enabling shadows for shadow ball testing');
            config.splatRelighting = { ...config.splatRelighting, shadowsEnabled: true };
        }

        if (playgroundBalls.length >= PLAYGROUND_MAX_BALLS) {
            const oldest = playgroundBalls.shift()!;
            removePlaygroundBall(oldest);
        }

        const colorIdx = Math.floor(Math.random() * METALLIC_COLORS.length);
        const camPos = camera.getPosition();
        const camFwd = camera.forward;
        const spawnPos = new pc.Vec3(
            camPos.x + camFwd.x * 2,
            camPos.y + camFwd.y * 2,
            camPos.z + camFwd.z * 2
        );

        console.log('[Playground] G pressed — spawning shadow ball at',
            spawnPos.x.toFixed(2), spawnPos.y.toFixed(2), spawnPos.z.toFixed(2));

        const entity = new pc.Entity('playground-shadow-ball');
        entity.addComponent('render', {
            type: 'sphere',
            material: getMetallicMaterial(colorIdx),
            castShadows: true
        });
        entity.setLocalScale(PLAYGROUND_BALL_RADIUS * 2, PLAYGROUND_BALL_RADIUS * 2, PLAYGROUND_BALL_RADIUS * 2);
        // No light component — just a solid metallic ball for shadow testing
        entity.setPosition(spawnPos);
        sceneContent.addChild(entity);

        const velocity = new pc.Vec3(
            camFwd.x * 8 + (Math.random() - 0.5) * 2,
            camFwd.y * 8 + 3,
            camFwd.z * 8 + (Math.random() - 0.5) * 2
        );

        playgroundBalls.push({
            entity, velocity, age: 0,
            lifetime: PLAYGROUND_LIFETIME,
            baseIntensity: 0,
            radius: PLAYGROUND_BALL_RADIUS
        });
    }

    function updatePlaygroundBalls(dt: number): void {
        if (playgroundBalls.length === 0) return;

        const toRemove: number[] = [];
        for (let i = 0; i < playgroundBalls.length; i++) {
            const ball = playgroundBalls[i];
            ball.age += dt;
            if (ball.age >= ball.lifetime) { toRemove.push(i); continue; }

            // Gravity
            ball.velocity.y -= PLAYGROUND_GRAVITY * dt;
            // Damping
            ball.velocity.x *= 1 - 0.3 * dt;
            ball.velocity.z *= 1 - 0.3 * dt;

            // Integrate
            const pos = ball.entity.getPosition().clone();
            pos.x += ball.velocity.x * dt;
            pos.y += ball.velocity.y * dt;
            pos.z += ball.velocity.z * dt;

            // Ground collision check
            const groundY = ballGroundCheck(pos, ball.radius);
            if (groundY !== null) {
                const minY = groundY + ball.radius;
                if (pos.y < minY && ball.velocity.y < 0) {
                    pos.y = minY;
                    ball.velocity.y = -ball.velocity.y * PLAYGROUND_BOUNCE;
                    // Kill tiny bounces
                    if (Math.abs(ball.velocity.y) < 0.3) ball.velocity.y = 0;
                }
            }

            // Wall collision
            ballWallCollision(pos, ball.velocity, ball.radius);

            ball.entity.setPosition(pos.x, pos.y, pos.z);

            // Fade out light in last 2s
            const remaining = ball.lifetime - ball.age;
            if (remaining < 2 && ball.entity.light) {
                ball.entity.light.intensity = ball.baseIntensity * (remaining / 2);
            }
        }

        for (let i = toRemove.length - 1; i >= 0; i--) {
            removePlaygroundBall(playgroundBalls[toRemove[i]]);
            playgroundBalls.splice(toRemove[i], 1);
        }
    }

    function removePlaygroundBall(ball: PlaygroundBall): void {
        const idx = lightEntities.indexOf(ball.entity);
        if (idx >= 0) lightEntities.splice(idx, 1);
        ball.entity.destroy();
        syncMeshLightLayers();
    }

    function startPlayground(): void {
        if (playgroundActive) return;
        playgroundActive = true;

        // Auto-enable relighting if needed
        if (!relightingEnabled && splatEntity) {
            config.splatRelighting = { ...config.splatRelighting, enabled: true };
            initSplatRelighting();
            // Set fade to 1 so balls are visible
            for (const s of allRelightingScripts) s.setRelightFade(1.0);
        }

        console.log('[Playground] Started — press F to spawn light balls, relightingEnabled:', relightingEnabled, 'splatEntity:', !!splatEntity);
    }

    function stopPlayground(): void {
        if (!playgroundActive) return;
        playgroundActive = false;

        // Remove all balls
        for (const ball of playgroundBalls) removePlaygroundBall(ball);
        playgroundBalls.length = 0;
        console.log('[Playground] Stopped');
    }

    // Single keydown handler for playground: Shift+P toggles, F spawns
    // Remove any stale handler from previous viewer instance (HMR / strict mode)
    if ((window as any).__storysplat_playground_handler) {
        window.removeEventListener('keydown', (window as any).__storysplat_playground_handler);
    }
    const _playgroundKeyHandler = (e: KeyboardEvent) => {
        // Don't capture shortcuts when typing in input fields
        const tag = (document.activeElement as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable) return;
        if (e.shiftKey && (e.ctrlKey || e.metaKey) && e.code === 'KeyP') {
            e.preventDefault();
            if (playgroundActive) {
                stopPlayground();
            } else {
                startPlayground();
            }
        } else if (e.code === 'KeyF' && playgroundActive) {
            console.log('[Playground] F pressed — spawning light ball');
            spawnPlaygroundBall();
        } else if (e.code === 'KeyG' && playgroundActive) {
            spawnShadowBall();
        }
    };
    (window as any).__storysplat_playground_handler = _playgroundKeyHandler;
    window.addEventListener('keydown', _playgroundKeyHandler);
    console.log('[Playground] Toggle registered — Ctrl+Shift+P to start/stop, F = light ball, G = shadow ball');

    // ── Performance HUD (F3 / Ctrl+Shift+M / ?perfhud=1) ─────────────────────────────────────────
    // Real-time diagnostics overlay: splat totals + live rendered count (unified manager), LOD
    // streaming state, collision/nav-mesh sizes, VRAM/heap, device caps. Enabled by default;
    // creators can disable per scene with uiOptions.perfHudEnabled = false.
    const perfHudAllowed = config.uiOptions?.perfHudEnabled !== false;
    /** Resolve the main splat's gsplat resource (same chain the pick raycaster uses). */
    const perfResolveSplatResource = (): { centers?: Float32Array; numSplats?: number; octree?: { fileResources?: Map<number, unknown>; files?: unknown[] } } | null => {
        const gs = splatEntity?.gsplat as unknown as {
            instance?: { resource?: unknown }; _instance?: { resource?: unknown };
            _placement?: { resource?: unknown }; resource?: unknown; asset?: { resource?: unknown };
        } | undefined;
        return (gs?.instance?.resource ?? gs?._instance?.resource ?? gs?._placement?.resource
            ?? gs?.resource ?? gs?.asset?.resource ?? null) as ReturnType<typeof perfResolveSplatResource>;
    };
    const buildPerfSnapshot = (): PerfSnapshot => {
        const snap: PerfSnapshot = {};

        // Versions — viewer npm semver (injected at build), the build timestamp, and the
        // host editor version when the embedder passes options.appVersion. Cheap and always
        // safe; surfaces in "Copy diagnostics" so bug reports name the exact builds involved.
        try {
            const viewerSemver = typeof __PACKAGE_VERSION__ !== 'undefined' ? __PACKAGE_VERSION__ : 'dev';
            snap.versions = {
                'viewer': `v${viewerSemver}`,
                'viewer build': _buildVersion,
                'editor': options.appVersion ? `v${options.appVersion}` : 'n/a',
            };
        } catch { snap.versions = { error: true }; }

        const device = app.graphicsDevice as pc.GraphicsDevice & {
            isWebGPU?: boolean; isWebGL2?: boolean; deviceName?: string; samples?: number;
            gpuAdapter?: { name?: string };
        };

        // Draw / engine stats (only fields the RELEASE engine build actually updates:
        // fps/ms via updateBasic, drawCalls.total via device._drawCallsPerFrame, vram via alloc tracking).
        try {
            snap.draw = {
                'draw calls': app.stats?.drawCalls?.total ?? null,
                'shaders (linked)': (app.stats?.shaders as { linked?: number } | undefined)?.linked ?? null,
            };
        } catch { snap.draw = { error: true }; }
        try {
            const st = renderScheduler.stats;
            snap.render = {
                'mode': onDemandRenderingActive ? 'on-demand' : (onDemandRenderingEnabled ? (onDemandContinuousReasons.length ? `continuous (${onDemandContinuousReasons.join(', ')})` : 'continuous (arming)') : 'continuous'),
                'last reason': st.lastReason,
                'rendered / skipped': `${st.rendered} / ${st.skipped}`,
                'heartbeats': st.heartbeats,
            };
        } catch { snap.render = { error: true }; }

        // Splats — totals from the resource, LIVE counts from the unified gsplat manager:
        // app.renderer.gsplatDirector.camerasMap → layersMap → gsplatManager.world.currentState.splats
        // (GSplatInfo[]): numSplats = per-placement resource total, activeSplats = splats ACTIVE in the
        // shared work buffer right now (LOD-interval aware) — i.e. what is actually being rendered.
        try {
            const res = perfResolveSplatResource();
            const isOctree = !!res?.octree;
            const total = res ? (res.numSplats ?? (res.centers ? Math.floor(res.centers.length / 3) : null)) : null;
            let rendered = 0, resident = 0, placements = 0, pending = 0, texSize = 0, copyUp = 0, copyTot = 0, found = false;
            const director = (app as unknown as { renderer?: { gsplatDirector?: { camerasMap?: Map<unknown, { layersMap?: Map<unknown, { gsplatManager?: unknown }> }> } } }).renderer?.gsplatDirector;
            director?.camerasMap?.forEach((camData) => {
                camData?.layersMap?.forEach((layerData) => {
                    const world = (layerData?.gsplatManager as { world?: {
                        currentState?: { splats?: { activeSplats?: number; numSplats?: number }[] };
                        pendingLoadCount?: number; bufferCopyUploaded?: number; bufferCopyTotal?: number;
                        workBuffer?: { textureSize?: number };
                    } } | undefined)?.world;
                    const splats = world?.currentState?.splats;
                    if (!splats) return;
                    found = true;
                    for (const s of splats) { rendered += s?.activeSplats ?? 0; resident += s?.numSplats ?? 0; placements++; }
                    pending += world?.pendingLoadCount ?? 0;
                    texSize = Math.max(texSize, world?.workBuffer?.textureSize ?? 0);
                    copyUp += world?.bufferCopyUploaded ?? 0; copyTot += world?.bufferCopyTotal ?? 0;
                });
            });
            const gsplat = (app.scene as unknown as { gsplat?: { currentRenderer?: unknown } }).gsplat;
            const rendererName = GSPLAT_RENDERER_NAME_BY_VALUE[String(gsplat?.currentRenderer)] ?? `unknown(${String(gsplat?.currentRenderer)})`;
            snap.splats = {
                'total (scene)': total,
                'rendered now': found ? rendered : null,
                'resident (placements)': found ? resident : null,
                'placements': found ? placements : null,
                'work buffer tex': found && texSize ? `${texSize}×${texSize}` : null,
                'renderer': rendererName,
            };
            if (isOctree) {
                const loaded = (res?.octree?.fileResources as Map<number, unknown> | undefined)?.size ?? null;
                const files = (res?.octree?.files as unknown[] | undefined)?.length ?? null;
                snap.lod = {
                    'chunks loaded / total': loaded !== null && files !== null ? `${loaded} / ${files}` : loaded,
                    'pending chunk loads': found ? pending : null,
                    'buffer copy (up/total)': found ? `${copyUp} / ${copyTot}` : null,
                };
            }
        } catch (e) { snap.splats = { error: String(e) }; }

        // Collision / nav mesh. Pick-grid stats are feature-detected (present on branches that ship
        // the splat pick grid; harmless n/a otherwise).
        try {
            const cc = characterController as unknown as {
                meshCollisionInstance?: { triangleCount?: number } | null;
                voxelCollisionInstance?: { queryCellSize?: () => number } | null;
                getCollisionSystem?: () => 'voxel' | 'mesh';
                getActiveCollisionSystem?: () => 'voxel' | 'mesh' | null;
                getSplatCollisionEnabled?: () => boolean;
                collisionEntities?: unknown[];
                _splatGrid?: { pointCount?: number; cellSize?: number } | null;
            } | null;
            const preferredCollision = cc?.getCollisionSystem?.() ?? null;
            const activeCollision = cc?.getActiveCollisionSystem?.() ?? null;
            const splatCollisionEnabled = cc?.getSplatCollisionEnabled?.() ?? null;
            const meshLoaded = !!cc?.meshCollisionInstance;
            const voxelLoaded = !!cc?.voxelCollisionInstance;
            const voxelCellSize = cc?.voxelCollisionInstance?.queryCellSize?.();
            const voxelCellLabel = typeof voxelCellSize === 'number' && Number.isFinite(voxelCellSize)
                ? voxelCellSize.toFixed(3)
                : '?';
            const meshState = meshLoaded ? (activeCollision === 'mesh' ? 'yes (active)' : 'yes (loaded)') : 'no';
            const voxelState = voxelLoaded
                ? `yes (${activeCollision === 'voxel' ? 'active' : 'loaded'}, cell ${voxelCellLabel})`
                : 'no';
            snap.collision = {
                'nav mesh triangles': cc?.meshCollisionInstance?.triangleCount ?? null,
                'mesh collision': meshState,
                'voxel collision': voxelState,
                'collision preference': preferredCollision,
                'active splat collision': activeCollision ?? 'none',
                'splat collision enabled': splatCollisionEnabled,
                'primitive colliders': cc?.collisionEntities?.length ?? null,
                'pick grid points': cc?._splatGrid?.pointCount ?? null,
            };
        } catch (e) { snap.collision = { error: String(e) }; }

        // Memory: JS heap (Chrome-only) + engine VRAM tracking (live in release builds).
        try {
            const mem = (performance as unknown as { memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number } }).memory;
            const vram = app.stats?.vram as unknown as { tex?: number; vb?: number; ib?: number; ub?: number; totalUsed?: number } | undefined;
            snap.memory = {
                'js heap': mem ? `${fmtBytes(mem.usedJSHeapSize)} / ${fmtBytes(mem.jsHeapSizeLimit)}` : null,
                'vram textures': fmtBytes(vram?.tex),
                'vram vb / ib': `${fmtBytes(vram?.vb)} / ${fmtBytes(vram?.ib)}`,
                'vram total': fmtBytes(vram?.totalUsed),
            };
        } catch (e) { snap.memory = { error: String(e) }; }

        // Device.
        try {
            const cssW = (canvas as HTMLCanvasElement).clientWidth, cssH = (canvas as HTMLCanvasElement).clientHeight;
            snap.device = {
                'api': device.isWebGPU ? 'WebGPU' : (device.isWebGL2 ? 'WebGL2' : 'WebGL1/other'),
                'gpu': device.deviceName ?? device.gpuAdapter?.name ?? 'unknown',
                'dpr': +(window.devicePixelRatio ?? 1).toFixed(2),
                'canvas css / buffer': `${cssW}×${cssH} / ${(canvas as HTMLCanvasElement).width}×${(canvas as HTMLCanvasElement).height}`,
                'msaa samples': device.samples ?? null,
                'mobile ua': /Mobi|Android|iPhone|iPad/.test(navigator.userAgent),
            };
        } catch (e) { snap.device = { error: String(e) }; }

        // Scene composition + camera.
        try {
            const pos = camera.getPosition();
            snap.scene = {
                'scene id': scene.sceneId || options.analytics?.sceneId || null,
                'camera mode': currentCameraMode,
                'camera pos': `${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}`,
                'waypoints': config.waypoints?.length ?? 0,
                'hotspots': hotspotEntities.length,
                'portals': portalEntities.length,
                'custom meshes': customMeshEntities.size,
                'lights': lightEntities.length,
                'particles': particleEntities.size,
                'mirrors': config.mirrorPlanes?.length ?? 0,
            };
        } catch (e) { snap.scene = { error: String(e) }; }

        return snap;
    };
    const perfHud = new PerfHud(app, container, buildPerfSnapshot);
    const togglePerfHud = (visible?: boolean): void => {
        if (!perfHudAllowed) {
            console.log('[PerfHud] Disabled for this scene (uiOptions.perfHudEnabled = false)');
            return;
        }
        perfHud.toggle(visible);
    };
    // Hotkey — F3 (preventDefault: browsers open find) or Ctrl+Shift+M. Same stale-handler
    // cleanup pattern as the playground hotkey (HMR / strict mode).
    if ((window as any).__storysplat_perfhud_handler) {
        window.removeEventListener('keydown', (window as any).__storysplat_perfhud_handler);
    }
    const _perfHudKeyHandler = (e: KeyboardEvent) => {
        const tag = (document.activeElement as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable) return;
        if (e.code === 'F3' || (e.shiftKey && (e.ctrlKey || e.metaKey) && e.code === 'KeyM')) {
            e.preventDefault();
            togglePerfHud();
        }
    };
    (window as any).__storysplat_perfhud_handler = _perfHudKeyHandler;
    window.addEventListener('keydown', _perfHudKeyHandler);
    // ?perfhud=1 forces it open on load (mobile debugging — no keyboard needed).
    try {
        if (perfHudAllowed && typeof window !== 'undefined'
            && /[?&]perfhud=(1|true)/.test(window.location.search)) {
            togglePerfHud(true);
        }
    } catch { /* SSR-safe */ }

    /**
     * Setup spatial audio for a hotspot using Web Audio API
     * Matches the HTML export's setupHotspotAudio function in hotspotSystem.ts
     */
    /**
     * Setup hotspot audio using PlayCanvas sound component (consistent with emitters/waypoints/meshes).
     * Adds a sound component to the hotspot entity with a slot for the audio.
     * Returns the slot ID, or null if no audio URL.
     */
    function setupHotspotAudio(entity: pc.Entity, hotspot: HotspotConfigLike): string | null {
        const label = hotspot.title || hotspot.id || 'untitled';
        if (!hotspot.audioUrl) {
            console.log(`[HotspotAudio] setup skipped (no audioUrl): ${label}`);
            return null;
        }
        const slotId = `hotspot-audio-${hotspot.id || Date.now()}`;
        const isSpatial = hotspot.audioSpatial || false;
        const distanceModel = getPlayCanvasDistanceModel(hotspot.audioDistanceModel || 'linear');
        // Add sound component if entity doesn't already have one
        if (!entity.sound) {
            entity.addComponent('sound', {
                positional: isSpatial,
                refDistance: finitePositiveSceneNumber(hotspot.audioRefDistance, 1),
                maxDistance: finitePositiveSceneNumber(hotspot.audioMaxDistance, 10),
                rollOffFactor: finiteNonNegativeSceneNumber(hotspot.audioRolloffFactor, 1),
                distanceModel: distanceModel,
                volume: finiteNonNegativeSceneNumber(hotspot.audioVolume, 1),
            });
        }
        // Add a slot for this hotspot's audio
        entity.sound?.addSlot(slotId, {
            volume: finiteNonNegativeSceneNumber(hotspot.audioVolume, 1),
            loop: hotspot.audioLoop || false,
            autoPlay: false,
            overlap: false,
        });
        // Load the audio asset. Use Asset.ready() instead of on('load') so already-loaded
        // assets still fire the callback, and listen for 'error' explicitly so we can see failures.
        const audioAsset = new pc.Asset(`hotspot-audio-${slotId}`, 'audio', { url: hotspot.audioUrl });
        // Released (removed from the registry and unloaded) with the hotspot entity, so each
        // editor rebuild does not leave a decoded audio buffer behind.
        const resources = ownMarkerResources(entity as ViewerRuntimeEntity);
        resources.audioAssets.push(audioAsset);
        const onReady = () => {
            if (isDestroyed) return;
            if (resources.released) {
                // Finished loading after the hotspot was removed: drop the decoded audio.
                audioAsset.unload();
                return;
            }
            const slot = entity.sound?.slot(slotId);
            if (slot) {
                slot.asset = audioAsset.id;
                trackSlotPlaybackState(slot, (playing) => {
                    (entity as ViewerRuntimeEntity).hotspotAudioPlaying = playing;
                });
                console.log(`[HotspotAudio] asset loaded + slot bound: ${label} (slotId=${slotId}, assetId=${audioAsset.id})`);
            } else {
                console.warn(`[HotspotAudio] asset loaded but slot missing: ${label} (slotId=${slotId})`);
            }
            (entity as ViewerRuntimeEntity).hotspotAudioReady = true;
            // If camera is already inside proximity radius, start playing now.
            const mediaTrigger = (entity as ViewerRuntimeEntity).mediaTriggerMode || 'click';
            const triggerMode = hotspot.audioTriggerMode || (mediaTrigger === 'proximity' ? 'proximity' : 'click');
            if (triggerMode === 'proximity' && (entity as ViewerRuntimeEntity).wasInProximity && slot && !slot.isPlaying) {
                slot.play();
                (entity as ViewerRuntimeEntity).hotspotAudioPlaying = true;
                console.log(`[HotspotAudio] late-proximity play: ${label}`);
            }
        };
        audioAsset.on('error', (err: unknown) => {
            console.error(`[HotspotAudio] asset error for ${label} (url=${hotspot.audioUrl}):`, err);
        });
        audioAsset.on('change', (_asset: unknown, property: string, newValue: unknown) => {
            if (property === 'loaded' || property === 'resource') {
                console.log(`[HotspotAudio] asset '${property}' changed for ${label}: ${String(newValue).slice(0, 60)}`);
            }
        });
        app.assets.add(audioAsset);
        audioAsset.ready(onReady);
        app.assets.load(audioAsset);
        console.log(`[HotspotAudio] setup: ${label}, url=${String(hotspot.audioUrl).slice(0, 80)}…, triggerMode=${hotspot.audioTriggerMode || '(default click)'}, spatial=${isSpatial}, slotId=${slotId}`);
        // If the asset never reaches ready within 5s, dump its state once so we can tell
        // a stalled <audio>-element decode apart from a fast-and-silent error.
        setTimeout(() => {
            if (isDestroyed) return;
            const ready = (entity as ViewerRuntimeEntity).hotspotAudioReady;
            if (ready) return;
            const a = audioAsset as pc.Asset & { loading?: boolean; loaded?: boolean; resource?: unknown };
            console.warn(`[HotspotAudio] STALL after 5s — ${label}: loaded=${a.loaded} loading=${a.loading} resource=${a.resource ? typeof a.resource : '(none)'} file=${JSON.stringify(audioAsset.file)}`);
        }, 5000);
        return slotId;
    }
    /**
     * Setup spatial audio for video hotspot using Web Audio API
     * Makes the video's audio 3D positional based on hotspot location
     */
    function setupVideoSpatialAudio(entity: pc.Entity, video: HTMLVideoElement, hotspot: HotspotConfigLike): VideoSpatialAudio | null {
        // Check if video should have spatial audio
        if (!hotspot.videoSpatialAudio && hotspot.videoMuted !== false) {
            // No spatial audio requested and video is muted
            return null;
        }
        try {
            // Create AudioContext for spatial audio
            const audioCtx = new (window.AudioContext || (window as WindowWithLegacyAudio).webkitAudioContext)();
            allAudioContexts.push(audioCtx);
            // Create source from video element
            const source = audioCtx.createMediaElementSource(video);
            // Create panner for 3D positioning
            const panner = audioCtx.createPanner();
            panner.panningModel = 'HRTF';
            panner.distanceModel = (hotspot.videoDistanceModel || 'linear') as DistanceModelType; // Match HTML export default
            panner.refDistance = finitePositiveSceneNumber(hotspot.videoRefDistance, 1);
            panner.maxDistance = finitePositiveSceneNumber(hotspot.videoMaxDistance, 100);
            panner.rolloffFactor = finiteNonNegativeSceneNumber(hotspot.videoRolloffFactor, 1);
            // Position panner at hotspot location
            const pos = entity.getPosition();
            if (isFiniteVec3(pos)) {
                panner.setPosition(pos.x, pos.y, pos.z);
            }
            // Connect: source -> panner -> destination
            source.connect(panner);
            panner.connect(audioCtx.destination);
            // Update panner and listener position each frame — but only while the video is
            // actually PLAYING with a running context (playVideoHotspot resumes the context on
            // play, so the handler self-corrects within a frame of playback starting), and only
            // when values actually changed (each AudioParam .value write is a cross-thread
            // message to the audio thread; an idle spatial video used to push ~600/sec).
            let lastPanX = NaN, lastPanY = NaN, lastPanZ = NaN;
            let lastCamX = NaN, lastCamY = NaN, lastCamZ = NaN;
            let lastFwdX = NaN, lastFwdY = NaN, lastFwdZ = NaN;
            let lastUpX = NaN, lastUpY = NaN, lastUpZ = NaN;
            const spatialAudioUpdateFn = () => {
                if (!entity || !entity.getPosition)
                    return;
                if (video.paused || audioCtx.state !== 'running')
                    return;
                // Update panner position to hotspot location. Per-frame value compare (NOT a
                // move-event subscription — hotspots can be dragged live in the editor and no
                // such event exists). Prefer the modern positionX AudioParams over the
                // deprecated setPosition where available.
                const hotspotPos = entity.getPosition();
                if (isFiniteVec3(hotspotPos) &&
                    (hotspotPos.x !== lastPanX || hotspotPos.y !== lastPanY || hotspotPos.z !== lastPanZ)) {
                    lastPanX = hotspotPos.x; lastPanY = hotspotPos.y; lastPanZ = hotspotPos.z;
                    if (panner.positionX) {
                        panner.positionX.value = hotspotPos.x;
                        panner.positionY.value = hotspotPos.y;
                        panner.positionZ.value = hotspotPos.z;
                    } else {
                        panner.setPosition(hotspotPos.x, hotspotPos.y, hotspotPos.z);
                    }
                }
                // Update listener position to camera
                if (camera && camera.getPosition) {
                    const camPos = camera.getPosition();
                    const camForward = camera.forward;
                    const camUp = camera.up;
                    if (!isFiniteVec3(camPos) || !isFiniteVec3(camForward) || !isFiniteVec3(camUp)) {
                        return;
                    }
                    const camChanged =
                        camPos.x !== lastCamX || camPos.y !== lastCamY || camPos.z !== lastCamZ ||
                        camForward.x !== lastFwdX || camForward.y !== lastFwdY || camForward.z !== lastFwdZ ||
                        camUp.x !== lastUpX || camUp.y !== lastUpY || camUp.z !== lastUpZ;
                    if (!camChanged)
                        return;
                    lastCamX = camPos.x; lastCamY = camPos.y; lastCamZ = camPos.z;
                    lastFwdX = camForward.x; lastFwdY = camForward.y; lastFwdZ = camForward.z;
                    lastUpX = camUp.x; lastUpY = camUp.y; lastUpZ = camUp.z;
                    if (audioCtx.listener.positionX) {
                        // Modern API
                        audioCtx.listener.positionX.value = camPos.x;
                        audioCtx.listener.positionY.value = camPos.y;
                        audioCtx.listener.positionZ.value = camPos.z;
                        audioCtx.listener.forwardX.value = camForward.x;
                        audioCtx.listener.forwardY.value = camForward.y;
                        audioCtx.listener.forwardZ.value = camForward.z;
                        audioCtx.listener.upX.value = camUp.x;
                        audioCtx.listener.upY.value = camUp.y;
                        audioCtx.listener.upZ.value = camUp.z;
                    }
                    else {
                        // Legacy API
                        audioCtx.listener.setPosition(camPos.x, camPos.y, camPos.z);
                        audioCtx.listener.setOrientation(camForward.x, camForward.y, camForward.z, camUp.x, camUp.y, camUp.z);
                    }
                }
            };
            app.on('update', spatialAudioUpdateFn);
            // The handler and the context live as long as the hotspot: without this, every editor
            // rebuild left a running AudioContext (pinning its video) and a per-frame handler.
            entity.once('destroy', () => {
                app.off('update', spatialAudioUpdateFn);
                const contextIndex = allAudioContexts.indexOf(audioCtx);
                if (contextIndex >= 0) allAudioContexts.splice(contextIndex, 1);
                if (audioCtx.state !== 'closed') audioCtx.close().catch(() => {});
                if ((entity as ViewerRuntimeEntity).videoSpatialAudio?.audioCtx === audioCtx) {
                    (entity as ViewerRuntimeEntity).videoSpatialAudio = null;
                }
            });
            console.log(`[Audio] Video spatial audio setup for hotspot: ${hotspot.title}, refDist=${panner.refDistance}, maxDist=${panner.maxDistance}`);
            return { audioCtx, source, panner };
        }
        catch (err) {
            console.warn('[Audio] Failed to setup video spatial audio:', err);
            return null;
        }
    }
    /**
     * Setup waypoint audio for audio interactions
     * Matches the HTML export's audioSystem.ts
     */
    // ── Deferred post-stream work ────────────────────────────────────────────
    // Non-critical downloads (proximity/entry audio, swap-splat preloads) used to start during
    // the initial LOD chunk fill, competing for bandwidth with the very streaming the preloader
    // reveal heuristic waits on. Queue them here; the preloader-hide paths call
    // settleInitialStream() (bounded by the 8s reveal cap). Editor stays eager (creator previews
    // must be instant) — callers gate on isEditorMode.
    const afterInitialStreamCallbacks: Array<() => void> = [];
    let initialStreamSettled = false;
    function runAfterInitialStream(fn: () => void): void {
        if (initialStreamSettled) { fn(); return; }
        afterInitialStreamCallbacks.push(fn);
    }
    function settleInitialStream(): void {
        if (initialStreamSettled) return;
        initialStreamSettled = true;
        for (const fn of afterInitialStreamCallbacks.splice(0)) {
            try { fn(); } catch (err) { console.warn('[StorySplat Viewer] Deferred post-stream task failed:', err); }
        }
    }

    function setupWaypointAudio(): void {
        // Tear down any existing waypoint audio entities so this function is safe to call
        // multiple times (e.g. when the editor pushes new waypoints via rebuildTourPath).
        if (waypointAudioMap.size > 0) {
            console.log(`[WaypointAudio] tearing down ${waypointAudioMap.size} existing audio entities before rebuild`);
            waypointAudioMap.forEach((audioData) => {
                try {
                    const slot = audioData.entity.sound?.slot(audioData.slotId);
                    slot?.stop();
                    audioData.entity.destroy();
                    // Remove the asset from the registry so stale ready/error listeners
                    // can't keep updating a future entry that happens to reuse the same audioId,
                    // and so we don't leak in-flight loads.
                    if (audioData.asset) {
                        try { app.assets.remove(audioData.asset); } catch { /* already removed */ }
                        audioData.asset.unload();
                    }
                } catch (err) {
                    console.warn('[WaypointAudio] error tearing down audio entity:', err);
                }
            });
            waypointAudioMap.clear();
        }
        if (!config.waypoints || config.waypoints.length === 0) {
            console.log('[WaypointAudio] setup: no waypoints, nothing to set up');
            return;
        }
        const totalAudioInteractions = config.waypoints.reduce((sum, wp) =>
            sum + (wp.interactions?.filter((i) => i.type === 'audio').length || 0), 0);
        console.log(`[WaypointAudio] setup: scanning ${config.waypoints.length} waypoints, ${totalAudioInteractions} audio interaction(s) total`);
        // Extract audio interactions from waypoints
        config.waypoints.forEach((waypoint, waypointIndex: number) => {
            if (waypoint.interactions && Array.isArray(waypoint.interactions)) {
                waypoint.interactions.forEach((interaction) => {
                    if (interaction.type === 'audio' && interaction.data) {
                        const data = interaction.data as LegacyWaypointAudioConfig;
                        if (!data.url) {
                            console.warn(`[WaypointAudio] WP${waypointIndex} (${waypoint.name || '(unnamed)'}) audio interaction ${interaction.id || ''} has no URL, skipping`);
                            return;
                        }
                        const audioId = String(interaction.id || `audio-${waypointIndex}`);
                        // Create audio entity
                        const audioEntity = new pc.Entity(`waypoint-audio-${audioId}`);
                        // Position at waypoint location (with Z negation for PlayCanvas)
                        // Coords may live on nested .position (with x/y/z or BabylonJS _x/_y/_z) or flat on the waypoint itself.
                        const np = (waypoint.position || {}) as LegacyVec3;
                        const wp = waypoint as LegacyVec3;
                        const position = playCanvasPositionFromSceneVec3({
                            x: np._x ?? np.x ?? wp._x ?? wp.x,
                            y: np._y ?? np.y ?? wp._y ?? wp.y,
                            z: np._z ?? np.z ?? wp._z ?? wp.z
                        }, { x: 0, y: 1.6, z: 0 });
                        audioEntity.setPosition(position.x, position.y, position.z);
                        // Configure sound component
                        const soundConfig = {
                            positional: data.spatialSound || false,
                            distanceModel: getPlayCanvasDistanceModel(data.distanceModel || 'exponential'),
                            maxDistance: finitePositiveSceneNumber(data.maxDistance, 1000),
                            refDistance: finitePositiveSceneNumber(data.refDistance, 1),
                            rollOffFactor: finiteNonNegativeSceneNumber(data.rolloffFactor, 1),
                            slots: {
                                [audioId]: {
                                    name: audioId,
                                    loop: data.loop || false,
                                    autoPlay: false,
                                    volume: finiteNonNegativeSceneNumber(data.volume, 1),
                                    pitch: 1
                                }
                            }
                        };
                        audioEntity.addComponent('sound', soundConfig);
                        // Build the data entry now so the asset listeners can close over it directly
                        // (instead of looking it up by audioId, which could resolve to a *future* entry
                        // if a teardown+recreate happened before this asset finished loading).
                        const audioAsset = new pc.Asset(`waypoint-audio-asset-${audioId}`, 'audio', { url: data.url });
                        const audioData: WaypointAudioData = {
                            entity: audioEntity,
                            waypointIndex,
                            config: data,
                            slotId: audioId,
                            playing: false,
                            autoplayTriggered: false,
                            assetReady: false,
                            asset: audioAsset,
                        };
                        waypointAudioMap.set(audioId, audioData);
                        sceneContent.addChild(audioEntity);
                        app.assets.add(audioAsset);
                        audioAsset.on('error', (err: unknown) => {
                            // Stale callback guard: if this entry was torn down (or replaced) before
                            // the error fired, the map either has no entry or a different one.
                            if (waypointAudioMap.get(audioId) !== audioData) return;
                            console.error(`[WaypointAudio] WP${waypointIndex} asset error (url=${data.url}):`, err);
                        });
                        audioAsset.ready(() => {
                            if (waypointAudioMap.get(audioId) !== audioData) return;
                            const slot = audioEntity.sound?.slot(audioId);
                            if (slot) {
                                slot.asset = audioAsset.id;
                                trackSlotPlaybackState(slot, (playing) => {
                                    if (waypointAudioMap.get(audioId) !== audioData) return;
                                    audioData.playing = playing;
                                });
                            } else {
                                console.warn(`[WaypointAudio] WP${waypointIndex} asset loaded but slot ${audioId} missing`);
                            }
                            audioData.assetReady = true;
                            if (data.triggerMode === 'autoplay' && slot) {
                                slot.play();
                                audioData.playing = true;
                                console.log(`[WaypointAudio] WP${waypointIndex} autoplay started: ${audioId}`);
                            }
                            console.log(`[WaypointAudio] WP${waypointIndex} asset loaded + ready: ${audioId} triggerMode=${data.triggerMode || '(legacy/entry)'} proximityRadius=${data.proximityRadius} spatial=${data.spatialSound}`);
                        });
                        // Only autoplay tracks need their PCM before the preloader hides; proximity
                        // and legacy entry tracks defer their download+decode past the initial LOD
                        // stream. Playback paths gate on assetReady and the proximity play-once gate
                        // doesn't advance while !assetReady, so late-ready assets still trigger.
                        if (isEditorMode || data.triggerMode === 'autoplay') {
                            app.assets.load(audioAsset);
                        } else {
                            runAfterInitialStream(() => {
                                // Stale guard: torn down / replaced before the stream settled
                                if (waypointAudioMap.get(audioId) !== audioData) return;
                                app.assets.load(audioAsset);
                            });
                        }
                        console.log(`[WaypointAudio] WP${waypointIndex} setup: id=${audioId} url=${String(data.url).slice(0, 80)}… triggerMode=${data.triggerMode || '(legacy)'} proximityRadius=${data.proximityRadius} loop=${data.loop} at pos=(${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`);
                    }
                });
            }
        });
        if (waypointAudioMap.size > 0) {
            console.log(`[StorySplat Viewer] Setup ${waypointAudioMap.size} waypoint audio sources`);
        }
    }
    // Map distance model string to PlayCanvas constant
    function getPlayCanvasDistanceModel(modelStr: string): string {
        switch (modelStr) {
            case 'linear':
                return 'linear';
            case 'inverse':
                return 'inverse';
            case 'exponential':
            default:
                return 'exponential';
        }
    }
    // Standalone audio emitters tracking
    interface AudioEmitterData {
        entity: pc.Entity;
        config: AudioEmitterConfig;
        slotId: string;
        playing: boolean;
        assetReady: boolean;
    }
    const audioEmitterMap = new Map<string, AudioEmitterData>();
    /**
     * Setup standalone audio emitters (spatial audio sources not attached to waypoints/hotspots)
     */
    function setupAudioEmitters(): void {
        const emitters = config.audioEmitters;
        if (!emitters || emitters.length === 0)
            return;
        console.log(`[Audio] Setting up ${emitters.length} standalone audio emitters`);
        emitters.forEach((emitter) => {
            if (!emitter.enabled) {
                console.log(`[Audio] Skipping disabled emitter: ${emitter.name || emitter.id}`);
                return;
            }
            if (!emitter.url) {
                console.warn(`[Audio] Emitter has no URL: ${emitter.name || emitter.id}`);
                return;
            }
            const audioId = emitter.id || `emitter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const scenePosition = finiteSceneVec3(emitter.position);
            const position = playCanvasPositionFromSceneVec3(scenePosition);
            // Create audio entity at emitter position
            const audioEntity = new pc.Entity(`audio-emitter-${audioId}`);
            audioEntity.setPosition(position.x, position.y, position.z);
            // Add sound component with proper spatial settings
            const distanceModel = getPlayCanvasDistanceModel(emitter.distanceModel || 'linear');
            const volume = finiteNonNegativeSceneNumber(emitter.volume, 0.5);
            audioEntity.addComponent('sound', {
                positional: emitter.spatialSound !== false,
                refDistance: finitePositiveSceneNumber(emitter.refDistance, 1),
                maxDistance: finitePositiveSceneNumber(emitter.maxDistance, 10),
                rollOffFactor: finiteNonNegativeSceneNumber(emitter.rolloffFactor, 1),
                distanceModel: distanceModel,
                volume
            });
            // Add sound slot
            // Note: positional is set on the sound component, not on individual slots
            audioEntity.sound?.addSlot(audioId, {
                volume,
                loop: emitter.loop !== false,
                autoPlay: false,
                overlap: false
            });
            // Load audio asset
            const audioAsset = new pc.Asset(`audio-emitter-${audioId}`, 'audio', {
                url: emitter.url
            });
            audioAsset.on('load', () => {
                if (isDestroyed)
                    return;
                // Set asset on slot
                const slot = audioEntity.sound?.slot(audioId);
                if (slot) {
                    slot.asset = audioAsset.id;
                }
                // Mark as ready
                const emitterData = audioEmitterMap.get(audioId);
                if (emitterData) {
                    if (slot) {
                        trackSlotPlaybackState(slot, (playing) => {
                            emitterData.playing = playing;
                        });
                    }
                    emitterData.assetReady = true;
                    // Start playing if trigger mode is autoplay (default for legacy scenes without triggerMode)
                    const triggerMode = emitter.triggerMode || (emitter.autoplay !== false ? 'autoplay' : 'proximity');
                    if (triggerMode === 'autoplay' && slot) {
                        console.log(`[Audio] Autoplay emitter: ${emitter.name || audioId}`);
                        slot.play();
                        emitterData.playing = true;
                    }
                }
                console.log(`[Audio] Emitter loaded: ${emitter.name || audioId}, triggerMode=${emitter.triggerMode || 'autoplay'}, spatial=${emitter.spatialSound !== false}, maxDistance=${finitePositiveSceneNumber(emitter.maxDistance, 10)}`);
            });
            app.assets.add(audioAsset);
            // Same deferral as waypoint audio: only autoplay emitters load during the initial
            // stream; proximity emitters download+decode after the preloader reveals.
            const effectiveTriggerMode = emitter.triggerMode || (emitter.autoplay !== false ? 'autoplay' : 'proximity');
            if (isEditorMode || effectiveTriggerMode === 'autoplay') {
                app.assets.load(audioAsset);
            } else {
                runAfterInitialStream(() => {
                    if (isDestroyed) return;
                    app.assets.load(audioAsset);
                });
            }
            // Add to scene
            sceneContent.addChild(audioEntity);
            // Store emitter data
            audioEmitterMap.set(audioId, {
                entity: audioEntity,
                config: emitter,
                slotId: audioId,
                playing: false,
                assetReady: false
            });
            console.log(`[Audio] Emitter created: ${emitter.name || audioId} at (${scenePosition.x}, ${scenePosition.y}, ${scenePosition.z})`);
        });
        if (audioEmitterMap.size > 0) {
            console.log(`[StorySplat Viewer] Setup ${audioEmitterMap.size} standalone audio emitters`);
        }
    }
    /**
     * Update audio emitter proximity (called every frame).
     * Non-spatial emitters still use distance for their trigger radius, but play globally.
     */
    function updateAudioEmitterProximity(): void {
        if (audioEmitterMap.size === 0)
            return;
        const cameraPos = camera.getPosition();
        if (!isFiniteVec3(cameraPos))
            return;
        audioEmitterMap.forEach((emitterData, audioId) => {
            const { entity, config, slotId, assetReady } = emitterData;
            // Skip if asset not ready
            if (!assetReady)
                return;
            const slot = entity.sound?.slot(slotId);
            if (!slot)
                return;
            const playing = emitterData.playing || slot.isPlaying;
            const emitterPos = entity.getPosition();
            if (!isFiniteVec3(emitterPos))
                return;
            const distance = cameraPos.distance(emitterPos);
            if (!Number.isFinite(distance))
                return;
            const triggerMode = config.triggerMode || (config.autoplay !== false ? 'autoplay' : 'proximity');
            const triggerRadius = finitePositiveSceneNumber(config.proximityRadius ?? config.maxDistance, 10);
            // Start playing when entering proximity
            if (distance <= triggerRadius && !playing) {
                // Autoplay emitters already started on load; proximity emitters start here
                if (triggerMode === 'proximity' && !slot.isPlaying) {
                    slot.play();
                    emitterData.playing = true;
                }
            }
            // Stop when leaving proximity (proximity mode with stopOnExit)
            if (distance > triggerRadius && playing && triggerMode === 'proximity') {
                const stopOnExit = config.stopOnExit ?? true;
                if (stopOnExit) {
                    slot.stop();
                    emitterData.playing = false;
                }
            }
        });
    }
    /**
     * Update custom mesh audio proximity.
     * Non-spatial mesh audio still uses distance as a trigger radius, but plays globally.
     */
    function updateCustomMeshAudioProximity(): void {
        if (customMeshEntities.size === 0) {
            return;
        }
        const cameraPos = camera.getPosition();
        if (!isFiniteVec3(cameraPos)) {
            return;
        }
        customMeshEntities.forEach((meshData) => {
            const { entity, config: meshConfig, audioSlotId } = meshData;
            const interaction = meshConfig.interaction;
            if (!interaction?.playAudio || !audioSlotId || !meshData.audioAssetReady) {
                return;
            }
            const triggerMode = interaction.audioTriggerMode || interaction.activationMode || 'click';
            if (triggerMode !== 'proximity') {
                return;
            }
            const slot = entity.sound?.slot(audioSlotId);
            if (!slot) {
                return;
            }
            const playing = meshData.audioPlaying || slot.isPlaying;
            const entityPos = entity.getPosition();
            if (!isFiniteVec3(entityPos)) {
                return;
            }
            const distance = cameraPos.distance(entityPos);
            if (!Number.isFinite(distance)) {
                return;
            }
            const triggerRadius = finitePositiveSceneNumber(interaction.audioProximityRadius ?? interaction.audioMaxDistance, 1);
            const stopOnExit = interaction.audioStopOnExit ?? true;
            if (distance <= triggerRadius && !playing) {
                slot.play();
                meshData.audioPlaying = true;
            }
            else if (distance > triggerRadius && playing && stopOnExit) {
                slot.stop();
                meshData.audioPlaying = false;
            }
        });
    }
    /**
     * Update waypoint audio on waypoint entry/exit.
     * For LEGACY scenes (no triggerMode), this is the primary playback trigger —
     * audio starts when the user reaches the waypoint, matching pre-triggerMode behavior.
     * Tracks with explicit triggerMode are handled by setupWaypointAudio() / updateWaypointAudioProximity().
     */
    function updateWaypointAudio(currentIndex: number, _previousIndex: number): void {
        if (waypointAudioMap.size === 0) return;
        waypointAudioMap.forEach((audioData, audioId) => {
            const { config, slotId, assetReady, waypointIndex } = audioData;
            if (!assetReady) return;
            // Only handle legacy tracks (no explicit triggerMode).
            // Tracks with triggerMode are handled by proximity or autoplay paths.
            if (config.triggerMode) return;
            const slot = audioData.entity.sound?.slot(slotId);
            if (!slot) return;
            if (waypointIndex === currentIndex) {
                // Entering this waypoint — start playback
                if (!audioData.playing && !slot.isPlaying) {
                    console.log(`[Audio] Waypoint-entry play (legacy): ${audioId}, wp=${currentIndex}`);
                    slot.play();
                    audioData.playing = true;
                }
            } else {
                // Left this waypoint — stop if stopOnExit (default true for legacy)
                const stopOnExit = config.stopOnExit ?? true;
                if ((audioData.playing || slot.isPlaying) && stopOnExit) {
                    console.log(`[Audio] Waypoint-exit stop (legacy): ${audioId}, wp=${waypointIndex}`);
                    slot.stop();
                    audioData.playing = false;
                }
            }
        });
    }
    /**
     * Update waypoint audio based on PROXIMITY to audio source
     * Plays when camera is within maxDistance of the audio source
     * Called every frame
     */
    function updateWaypointAudioProximity(): void {
        if (waypointAudioMap.size === 0)
            return;
        const cameraPos = camera.getPosition();
        if (!isFiniteVec3(cameraPos))
            return;
        waypointAudioMap.forEach((audioData, audioId) => {
            const { entity, config, slotId, assetReady } = audioData;
            // Skip if asset not ready yet
            if (!assetReady)
                return;
            const slot = entity.sound?.slot(slotId);
            if (!slot)
                return;
            // Legacy tracks (no triggerMode) are handled by updateWaypointAudio() on waypoint entry.
            // Only explicit triggerMode:'proximity' uses distance-based playback here.
            if (!config.triggerMode || config.triggerMode !== 'proximity') {
                return;
            }
            const playing = audioData.playing || slot.isPlaying;
            // Proximity-based playback — use proximityRadius (falls back to maxDistance for legacy scenes)
            const audioPos = entity.getPosition();
            if (!isFiniteVec3(audioPos))
                return;
            const distance = cameraPos.distance(audioPos);
            if (!Number.isFinite(distance))
                return;
            const triggerRadius = finitePositiveSceneNumber(config.proximityRadius ?? config.maxDistance, 1);
            const stopOnExit = config.stopOnExit ?? true;
            const inRange = distance <= triggerRadius;
            const wasInProximity = audioData.inProximity ?? false;
            // Play once on the outside→inside transition. Without this gate, a
            // non-looping clip that ends while the camera is still in range would
            // get re-triggered every frame because `playing` flips back to false.
            if (inRange && !wasInProximity && !playing) {
                console.log(`[WaypointAudio] proximity play: ${audioId} distance=${distance.toFixed(2)} radius=${triggerRadius}`);
                slot.play();
                audioData.playing = true;
            }
            // Stop when leaving proximity
            else if (!inRange && playing && stopOnExit) {
                console.log(`[WaypointAudio] proximity stop: ${audioId} distance=${distance.toFixed(2)}`);
                slot.stop();
                audioData.playing = false;
            }
            audioData.inProximity = inRange;
        });
    }
    // Track which waypoints are "active" (camera within trigger distance)
    const activeWaypointTriggers = new Set<number>();
    /**
     * Check waypoint trigger distances and execute/reverse interactions
     * Called every frame in the update loop
     */
    // Scratch for checkWaypointTriggerDistance — the per-frame loop used to allocate ~5 objects
    // per waypoint (input/fallback literals, two conversion objects, a Vec3). Reads
    // config.waypoints LIVE each frame (no position cache — the editor mutates waypoints in
    // place), with the same legacy _x/_y/_z + flat-coord + finite sanitization inlined.
    const _wpTriggerPos = new pc.Vec3();
    function checkWaypointTriggerDistance(): void {
        if (!config.waypoints?.length)
            return;
        const cameraPos = camera.getPosition();
        if (!isFiniteVec3(cameraPos))
            return;
        const waypoints = config.waypoints;
        for (let index = 0; index < waypoints.length; index++) {
            const wp = waypoints[index];
            // Coords may live on nested .position (with x/y/z or BabylonJS _x/_y/_z) or flat on the waypoint itself.
            const np = (wp.position || {}) as LegacyVec3;
            const wpFlat = wp as LegacyVec3;
            const rx = np._x ?? np.x ?? wpFlat._x ?? wpFlat.x;
            const ry = np._y ?? np.y ?? wpFlat._y ?? wpFlat.y;
            const rz = np._z ?? np.z ?? wpFlat._z ?? wpFlat.z;
            // Same sanitization as playCanvasPositionFromSceneVec3 (finite-or-0, negated z),
            // via finiteSceneNumber so legacy stringified coords keep coercing identically.
            _wpTriggerPos.set(
                finiteSceneNumber(rx, 0),
                finiteSceneNumber(ry, 0),
                -finiteSceneNumber(rz, 0)
            );
            const distance = cameraPos.distance(_wpTriggerPos);
            if (!Number.isFinite(distance))
                continue;
            const triggerDist = finitePositiveSceneNumber(wp.triggerDistance, 1.0);
            if (distance <= triggerDist) {
                // Entering trigger zone
                if (!activeWaypointTriggers.has(index)) {
                    activeWaypointTriggers.add(index);
                    console.log(`[StorySplat] Waypoint ${index} triggered (distance: ${distance.toFixed(2)}, threshold: ${triggerDist})`);
                    executeWaypointInteractions(wp, index);
                }
            }
            else {
                // Exiting trigger zone
                if (activeWaypointTriggers.has(index)) {
                    activeWaypointTriggers.delete(index);
                    console.log(`[StorySplat] Waypoint ${index} exited`);
                    reverseWaypointInteractions(wp, index);
                }
            }
        }
    }
    /**
     * Execute all interactions on a waypoint when entering trigger distance.
     * Info is deliberately not shown from proximity: a waypoint's name and info are the waypoint
     * banner, which shows in tour mode only (getWaypointBannerContent in viewerUI.ts). Info for
     * an area in any mode is an overlay with a region trigger.
     */
    function executeWaypointInteractions(waypoint: WaypointConfigLike, waypointIndex: number): void {
        if (!waypoint.interactions?.length)
            return;
        waypoint.interactions.forEach((interaction) => {
            // Nothing runs from proximity yet (waypoint audio has its own proximity trigger)
        });
    }
    /**
     * Reverse/stop interactions when exiting trigger distance
     */
    function reverseWaypointInteractions(waypoint: WaypointConfigLike, waypointIndex: number): void {
        if (!waypoint.interactions?.length)
            return;
        waypoint.interactions.forEach((interaction) => {
            // Nothing to reverse (see executeWaypointInteractions)
        });
    }
    /**
     * Global mute/unmute functions
     */
    function muteAllAudio(): void {
        if (globalMuted)
            return;
        // Mute hotspot audio (PlayCanvas slots)
        hotspotEntities.forEach((he) => {
            const slotId = (he as ViewerRuntimeEntity).hotspotAudioSlotId;
            if (slotId) {
                const slot = he.sound?.slot(slotId);
                if (slot) {
                    (slot as AudioSlotWithStoredVolume)._storedVolume = slot.volume;
                    slot.volume = 0;
                }
            }
        });
        // Mute narration audio
        allAudioElements.forEach(audio => {
            storedVolumes.set(audio, audio.volume);
            audio.volume = 0;
        });
        // Mute waypoint audio
        waypointAudioMap.forEach((audioData) => {
            const slot = audioData.entity.sound?.slot(audioData.slotId);
            if (slot) {
                (slot as AudioSlotWithStoredVolume)._storedVolume = slot.volume;
                slot.volume = 0;
            }
        });
        // Mute audio emitters
        audioEmitterMap.forEach((emitterData) => {
            const slot = emitterData.entity.sound?.slot(emitterData.slotId);
            if (slot) {
                (slot as AudioSlotWithStoredVolume)._storedVolume = slot.volume;
                slot.volume = 0;
            }
        });
        // Also mute any HTML audio/video elements
        document.querySelectorAll('audio, video').forEach((el) => {
            (el as HTMLMediaElement).muted = true;
        });
        crossSceneAudio.setMuted(true);
        globalMuted = true;
        console.log('[Audio] All audio muted');
    }
    function unmuteAllAudio(): void {
        if (!globalMuted)
            return;
        // Unmute hotspot audio (PlayCanvas slots)
        hotspotEntities.forEach((he) => {
            const slotId = (he as ViewerRuntimeEntity).hotspotAudioSlotId;
            if (slotId) {
                const slot = he.sound?.slot(slotId);
                if (slot) {
                    const storedVol = (slot as AudioSlotWithStoredVolume)._storedVolume;
                    slot.volume = storedVol !== undefined ? storedVol : 1;
                }
            }
        });
        // Unmute narration audio
        allAudioElements.forEach(audio => {
            const storedVol = storedVolumes.get(audio);
            audio.volume = storedVol !== undefined ? storedVol : 1;
        });
        // Unmute waypoint audio
        waypointAudioMap.forEach((audioData) => {
            const slot = audioData.entity.sound?.slot(audioData.slotId);
            if (slot) {
                const storedVol = (slot as AudioSlotWithStoredVolume)._storedVolume;
                slot.volume = storedVol !== undefined ? storedVol : 1;
            }
        });
        // Unmute audio emitters
        audioEmitterMap.forEach((emitterData) => {
            const slot = emitterData.entity.sound?.slot(emitterData.slotId);
            if (slot) {
                const storedVol = (slot as AudioSlotWithStoredVolume)._storedVolume;
                slot.volume = storedVol !== undefined ? storedVol : 1;
            }
        });
        // Also unmute any HTML audio/video elements
        document.querySelectorAll('audio, video').forEach((el) => {
            (el as HTMLMediaElement).muted = false;
        });
        crossSceneAudio.setMuted(false);
        globalMuted = false;
        console.log('[Audio] All audio unmuted');
    }
    function toggleMuteAll(): boolean {
        if (globalMuted) {
            unmuteAllAudio();
        }
        else {
            muteAllAudio();
        }
        return globalMuted;
    }
    /** Mute or unmute everything and show it on the mute button (the button and VR's Sound share this). */
    function applyMuteToggle(): boolean {
        const muted = toggleMuteAll();
        const button = uiElements.muteButton;
        if (button) {
            const unmutedIcon = button.querySelector('.storysplat-unmuted-icon') as HTMLElement | null;
            const mutedIcon = button.querySelector('.storysplat-muted-icon') as HTMLElement | null;
            if (unmutedIcon) unmutedIcon.style.display = muted ? 'none' : '';
            if (mutedIcon) mutedIcon.style.display = muted ? '' : 'none';
            button.setAttribute('aria-label', muted
                ? getButtonLabel(currentButtonLabels, 'unmute')
                : getButtonLabel(currentButtonLabels, 'mute'));
        }
        return muted;
    }
    // Track animated GIF textures for cleanup
    const animatedGifs: AnimatedGifTexture[] = [];
    /** GIF players createImageMaterial starts for a GIF image URL, by material, so the marker
     *  owning the material can stop its player when it is removed. */
    const imageMaterialGifs = new WeakMap<pc.StandardMaterial, AnimatedGifTexture>();
    // Create hotspot material for images - entity is hidden until texture loads.
    // Supports both static images and animated GIFs with transparency.
    // onTextureReady receives the texture size once the image (or GIF) has loaded, and nothing
    // after a load error (the material is then tinted red so the marker stays findable).
    function createImageMaterial(imageUrl: string, useLighting: boolean = false, opacity: number = 1, onTextureReady?: (size?: { width: number; height: number }) => void): pc.StandardMaterial {
        const material = new pc.StandardMaterial();
        // Straight-alpha blending (BLEND_NORMAL) — premultiplied was tried and caused
        // dark fringes because tonemap/gamma in the fragment shader distort the
        // premultiplied RGB/alpha relationship at semi-transparent edges.
        //
        // alphaTest = 0.5 cuts at the midpoint of the PNG's AA ramp, which is the one
        // contour mipmap averaging approximately preserves across mip levels. A
        // near-zero cutoff (0.01) discards only fully-transparent pixels and lets the
        // entire fringe write depth — an irregular per-pixel mask that reads as jagged
        // edges + halo when later depth-aware passes latch onto it. 0.5 gives a clean,
        // stable edge contour at all mip levels. The icon studio bleeds transparent
        // RGB to bgColor, so the edge at alpha=0.5 is bgColor on one side and scene
        // on the other — reads as a crisp anti-aliased outline.
        material.blendType = pc.BLEND_NORMAL;
        material.depthTest = true;
        material.depthWrite = true; // keep — layer order puts hotspots before skybox
        material.cull = pc.CULLFACE_NONE;
        material.twoSidedLighting = true;
        material.alphaTest = 0.5;
        // For unlit mode: disable all lighting calculations, use only emissive
        if (!useLighting) {
            // Disable diffuse lighting entirely
            material.diffuse = new pc.Color(0, 0, 0);
            // Full white emissive - texture will provide color
            material.emissive = new pc.Color(1, 1, 1);
            // Disable specular
            material.specular = new pc.Color(0, 0, 0);
            material.useLighting = false;
        }
        else {
            material.diffuse = new pc.Color(1, 1, 1);
            // Clear specular so a toggled-on "Use Lighting" image hotspot
            // doesn't pick up a default specular highlight across the icon.
            material.specular = new pc.Color(0, 0, 0);
        }
        material.opacity = 1;
        material.update();
        // Entity opacity goes through the translucency helper, which keeps the PNG alpha cutout + alphaTest intact
        applyEntityOpacity(material, opacity);
        // Check if this is a GIF - use AnimatedGifTexture for proper transparency and animation
        if (isGifUrl(imageUrl)) {
            console.log(`[Hotspot] Loading animated GIF: ${imageUrl}`);
            const gifTexture = new AnimatedGifTexture(app, imageUrl, {
                autoPlay: true,
                onReady: () => {
                    if (isDestroyed) {
                        console.log(`[Hotspot] Ignoring GIF load - viewer was destroyed: ${imageUrl}`);
                        gifTexture.destroy();
                        return;
                    }
                    if (gifTexture.texture) {
                        gifTexture.texture.premultiplyAlpha = false;
                        // Assign texture to material based on lighting mode
                        if (!useLighting) {
                            material.emissiveMap = gifTexture.texture;
                            material.opacityMap = gifTexture.texture;
                        }
                        else {
                            material.diffuseMap = gifTexture.texture;
                            material.opacityMap = gifTexture.texture;
                        }
                        material.opacityMapChannel = 'a';
                        material.update();
                        console.log(`[Hotspot] GIF texture loaded: ${imageUrl}, useLighting=${useLighting}`);
                        if (onTextureReady) {
                            onTextureReady({ width: gifTexture.texture.width, height: gifTexture.texture.height });
                        }
                    }
                },
                onError: (error) => {
                    console.error(`[Hotspot] Failed to load GIF: ${imageUrl}`, error);
                    // On error, make it slightly visible so user knows something is there
                    material.emissive = new pc.Color(1, 0, 0); // Red to indicate error
                    material.update();
                    applyEntityOpacity(material, 0.3);
                    if (onTextureReady) {
                        onTextureReady(); // Still call callback so entity becomes visible
                    }
                }
            });
            // Track for cleanup
            animatedGifs.push(gifTexture);
            imageMaterialGifs.set(material, gifTexture);
        }
        else {
            // Standard image loading with straight (non-premultiplied) alpha.
            // Using `createImageBitmap` for reliable cross-browser decoding.
            const assignTexture = (texture: pc.Texture) => {
                if (!useLighting) {
                    // Unlit: only use emissive map (no diffuse lighting)
                    material.emissiveMap = texture;
                    material.opacityMap = texture;
                }
                else {
                    // Lit: use diffuse map
                    material.diffuseMap = texture;
                    material.opacityMap = texture;
                }
                material.opacityMapChannel = 'a';
                material.update();
                console.log(`[Hotspot] Texture loaded for: ${imageUrl}, useLighting=${useLighting}`);
                renderScheduler.requestFrame('image-texture-loaded');
                if (onTextureReady) {
                    onTextureReady({ width: texture.width, height: texture.height });
                }
            };
            const handleError = (err: unknown) => {
                console.error(`[Hotspot] Failed to load texture: ${imageUrl}`, err);
                // On error, make it slightly visible so user knows something is there
                material.emissive = new pc.Color(1, 0, 0); // Red to indicate error
                material.update();
                applyEntityOpacity(material, 0.3);
                renderScheduler.requestFrame('image-texture-error');
                if (onTextureReady) {
                    onTextureReady(); // Still call callback so entity becomes visible
                }
            };
            if (typeof createImageBitmap === 'function') {
                fetch(imageUrl, { mode: 'cors' })
                    .then(res => {
                        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
                        return res.blob();
                    })
                    .then(blob => createImageBitmap(blob, {
                        premultiplyAlpha: 'none',
                        colorSpaceConversion: 'none'
                    }))
                    .then(bitmap => {
                        if (isDestroyed) {
                            console.log(`[Hotspot] Ignoring texture load - viewer was destroyed: ${imageUrl}`);
                            bitmap.close?.();
                            return;
                        }
                        const texture = new pc.Texture(app.graphicsDevice, {
                            width: bitmap.width,
                            height: bitmap.height,
                            format: pc.PIXELFORMAT_RGBA8,
                            mipmaps: true,
                            minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
                            magFilter: pc.FILTER_LINEAR,
                            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                            addressV: pc.ADDRESS_CLAMP_TO_EDGE
                        });
                        // Straight alpha — prevent PlayCanvas from premultiplying on upload
                        texture.premultiplyAlpha = false;
                        // Anisotropic filtering keeps oblique plane hotspots
                        // (e.g. an icon on a TV screen tilted off-axis) from
                        // picking an overly-low mip, which would exaggerate
                        // any residual edge fringing.
                        const maxAniso = (app.graphicsDevice as pc.GraphicsDevice & { maxAnisotropy?: number }).maxAnisotropy;
                        texture.anisotropy = typeof maxAniso === 'number' && maxAniso > 1 ? maxAniso : 16;
                        // PlayCanvas `setSource` accepts ImageBitmap at runtime
                        // but its type declaration only lists HTML*Element.
                        texture.setSource(bitmap as unknown as HTMLImageElement);
                        assignTexture(texture);
                    })
                    .catch(handleError);
            }
            else {
                // Fallback for environments without createImageBitmap
                // (very old Safari). Preserves the pre-existing behavior.
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    if (isDestroyed) {
                        console.log(`[Hotspot] Ignoring texture load - viewer was destroyed: ${imageUrl}`);
                        return;
                    }
                    const texture = new pc.Texture(app.graphicsDevice, {
                        width: img.width,
                        height: img.height,
                        format: pc.PIXELFORMAT_RGBA8,
                        mipmaps: true,
                        minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
                        magFilter: pc.FILTER_LINEAR,
                        addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                        addressV: pc.ADDRESS_CLAMP_TO_EDGE
                    });
                    // Match the modern createImageBitmap path. Prior `true` here was a
                    // latent double-premult bug: BLEND_NORMAL expects straight-alpha RGB.
                    texture.premultiplyAlpha = false;
                    const maxAniso = (app.graphicsDevice as pc.GraphicsDevice & { maxAnisotropy?: number }).maxAnisotropy;
                    texture.anisotropy = typeof maxAniso === 'number' && maxAniso > 1 ? maxAniso : 16;
                    texture.setSource(img);
                    assignTexture(texture);
                };
                img.onerror = handleError;
                img.src = imageUrl;
            }
        }
        return material;
    }
    /**
     * Apply composed rotation for plane entities (image/video/gif hotspots & portals).
     * PlayCanvas planes are horizontal (XZ) by default, so we need a 90° X rotation
     * to make them face forward. This must be composed WITH the user rotation, not
     * applied after it in local space (which would rotate in the wrong frame of
     * reference when the user rotation is non-zero).
     *
     * Matches BabylonJS behavior where the plane naturally faces forward and the
     * user rotation is on a parent TransformNode.
     */
    /**
     * Convert BabylonJS Euler angles (radians, YXZ intrinsic order, left-handed)
     * to a PlayCanvas quaternion (right-handed).
     *
     * BabylonJS mesh.rotation uses YXZ intrinsic order (RotationYawPitchRoll).
     * PlayCanvas setFromEulerAngles uses XYZ intrinsic order — these differ,
     * so passing angles directly causes wrong results when multiple axes are
     * non-zero. Instead we build the quaternion with the BJS formula, then
     * convert handedness by negating qx and qy (Z-axis flip).
     */
    function bjsRotationToPC(rx: number, ry: number, rz: number): pc.Quat {
        // BJS RotationYawPitchRoll: yaw=ry, pitch=rx, roll=rz
        const hy = ry / 2, hx = rx / 2, hz = rz / 2;
        const cy = Math.cos(hy), sy = Math.sin(hy);
        const cx = Math.cos(hx), sx = Math.sin(hx);
        const cz = Math.cos(hz), sz = Math.sin(hz);
        // Quaternion in BJS left-handed space (YXZ order)
        const qw = cy * cx * cz + sy * sx * sz;
        const qx = cy * sx * cz + sy * cx * sz;
        const qy = sy * cx * cz - cy * sx * sz;
        const qz = cy * cx * sz - sy * sx * cz;
        // Convert LH → RH (Z-flip): negate x and y components
        return new pc.Quat(-qx, -qy, qz, qw);
    }

    /**
     * Apply rotation to plane entities (image/video/gif hotspots & portals).
     * PlayCanvas planes are horizontal (XZ, normal +Y) by default.
     * BabylonJS planes face +Z (vertical, normal toward camera).
     * A 90° X rotation in PC space tilts the plane upright to face -Z
     * (PC forward = BJS +Z after the handedness flip).
     * The user rotation is composed on top via proper quaternion math.
     */
    function applyPlaneRotation(entity: pc.Entity, rxRad: number, ryRad: number, rzRad: number): void {
        const faceForward = new pc.Quat().setFromEulerAngles(90, 0, 0);
        const userRot = bjsRotationToPC(rxRad, ryRad, rzRad);
        entity.setRotation(new pc.Quat().mul2(userRot, faceForward));
    }

    /**
     * Build a leader-line entity as a thin cylinder stretched between two world
     * points. Uses a standard PlayCanvas cylinder + StandardMaterial — no custom
     * shaders, no custom geometry, guaranteed to render on all backends.
     */
    function buildLeaderLineEntity(
        hotspot: HotspotConfigLike | PortalConfigLike,
        hotspotWorldPos: pc.Vec3,
        markerShape: LeaderLineMarkerShape | null,
        // Stacked markers: distance along the line to the stack's edge (see markerStackLeaderEdge)
        stackEdge?: (dir: pc.Vec3) => number
    ): pc.Entity | null {
        if (!markerShape || !hotspot.leaderLineEnabled || !hotspot.leaderLineTarget) return null;
        const tgt = hotspot.leaderLineTarget;
        const targetPC = new pc.Vec3(tgt.x ?? 0, tgt.y ?? 0, -(tgt.z ?? 0));

        const color = parseColor(hotspot.leaderLineColor || '#ffffff');
        // Convert pixel width to world-space radius (approximate)
        const radius = (hotspot.leaderLineWidth ?? 2) * 0.005;

        const fullDiff = new pc.Vec3().sub2(targetPC, hotspotWorldPos);
        const fullLen = fullDiff.length();
        if (fullLen < 0.001) return null; // zero-length line

        const dir = fullDiff.clone().normalize();
        const startGap = Math.min(
            stackEdge ? stackEdge(dir) + radius * 2 : leaderLineStartGap(markerShape, hotspot.scale, radius),
            fullLen * 0.45
        );
        const lineStart = hotspotWorldPos.clone().add(dir.clone().mulScalar(startGap));

        // Midpoint & direction
        const mid = new pc.Vec3().add2(lineStart, targetPC).mulScalar(0.5);
        const diff = new pc.Vec3().sub2(targetPC, lineStart);
        const len = diff.length();
        if (len < 0.001) return null;

        const mat = new pc.StandardMaterial();
        // Unlit: full emissive so the line color is exactly what the user picked
        mat.diffuse = new pc.Color(0, 0, 0);
        mat.emissive = color;
        mat.useLighting = false;
        mat.blendType = pc.BLEND_NONE;
        mat.depthTest = true;
        mat.depthWrite = true;
        mat.cull = pc.CULLFACE_NONE;
        mat.update();

        const lineEntity = new pc.Entity(`leader-line-${hotspot.id || 'x'}`);
        lineEntity.addComponent('render', {
            type: 'cylinder',
            castShadows: false,
            receiveShadows: false,
            layers: [hotspotMeshLayer.id],
        });
        lineEntity.render!.meshInstances[0].material = mat;
        // Stash material ref for explicit cleanup (entity.destroy() doesn't destroy materials)
        (lineEntity as any)._leaderMat = mat;

        // Position at midpoint
        lineEntity.setPosition(mid);
        // Scale: X/Z = radius, Y = half-length (cylinder height = 1, scaled by len)
        lineEntity.setLocalScale(radius, len, radius);
        // Orient: default cylinder is along Y. Rotate so Y aligns with the line direction.
        const up = new pc.Vec3(0, 1, 0);
        const lineDir = diff.clone().normalize();
        const q = new pc.Quat();
        // Guard against antiparallel vectors (target directly below hotspot)
        // which produce a degenerate quaternion from setFromDirections
        if (up.dot(lineDir) < -0.9999) {
            q.setFromEulerAngles(180, 0, 0);
        } else {
            q.setFromDirections(up, lineDir);
        }
        lineEntity.setRotation(q);

        return lineEntity;
    }

    function syncLeaderLineVisibility(entity: ViewerRuntimeEntity): void {
        if (entity.leaderLineEntity) {
            entity.leaderLineEntity.enabled = entity.enabled;
        }
    }

    /**
     * Replace a marker's leader line after its config or transform changed (editor
     * mutation API). The new line takes the marker's current visibility, so editing a
     * hotspot or portal that is hidden by its visibility range does not reveal its line.
     */
    function rebuildLeaderLine(
        entity: ViewerRuntimeEntity,
        config: HotspotConfigLike | PortalConfigLike,
        markerShape: LeaderLineMarkerShape | null
    ): void {
        const oldLine = entity.leaderLineEntity;
        if (oldLine) {
            ((oldLine as any)._leaderMat as pc.StandardMaterial | undefined)?.destroy();
            oldLine.destroy();
        }
        const newLine = buildLeaderLineEntity(config, entity.getPosition().clone(), markerShape, markerStackLeaderEdge(entity));
        entity.leaderLineEntity = newLine ?? undefined;
        if (newLine) {
            sceneContent.addChild(newLine);
            syncLeaderLineVisibility(entity);
        }
    }

    function hasNonDefaultHotspotBillboardRange(hotspot: HotspotConfigLike): boolean {
        const hasRangeValue = hotspot.billboardRangeStart !== undefined || hotspot.billboardRangeEnd !== undefined;
        if (!hasRangeValue) return false;
        const rangeStart = hotspot.billboardRangeStart ?? 0;
        const rangeEnd = hotspot.billboardRangeEnd ?? 100;
        return rangeStart > 0 || rangeEnd < 100;
    }

    function hasDisplayableHotspotPopupContent(hotspot: HotspotConfigLike | HotspotData | null | undefined): boolean {
        // Text blocks are content even when an older scene left contentType as 'none'.
        if (Array.isArray(hotspot?.contentBlocks) && hotspot.contentBlocks.length > 0) return true;
        if (!hotspot || hotspot.contentType === 'none') return false;
        return Boolean(
            hotspot.title ||
            hotspot.information ||
            hotspot.photoUrl ||
            hotspot.popupVideoUrl ||
            hotspot.iframeUrl ||
            hotspot.externalLinkUrl ||
            hotspot.modelUrl ||
            hotspot.pdfUrl
        );
    }

    /** Where a video plane's media comes from: a video hotspot's or video portal's fields, or a stacked marker's video layer. */
    interface VideoPlaneSource {
        videoUrl: string;
        /** Original upload file name of videoUrl: identifies a WebM behind an opaque (blob:) URL. */
        videoName?: string;
        videoBackupUrl?: string;
        webmHasAlpha?: boolean;
        useIOSVideoAlphaMethod?: boolean;
        forceIOSVideoAlphaMethodForAllDevices?: boolean;
        iosMainVideoUrl?: string;
        iosMainVideoName?: string;
        alphaMaskVideoUrl?: string;
        videoLoop?: boolean;
        videoMuted?: boolean;
        mediaTriggerMode?: string;
    }
    interface VideoPlaneMedia {
        video: HTMLVideoElement;
        alphaVideo: HTMLVideoElement | null;
        material: pc.StandardMaterial;
        /** Main (and alpha mask) video textures, owned by the caller. */
        textures: pc.Texture[];
    }
    /**
     * Whether a video plane's own alpha blends over the splats. A transparent video's soft edges
     * and semi-transparent areas must show the splats behind it, so its material (and the "Tap to
     * Start" prompt in front of it, which has to draw after it) goes on the translucent-entity
     * path: TranslucentEntityLayer after the splats, splats in front fading it out through the
     * splat depth pass. Opaque videos stay in HotspotMeshLayer and cost nothing extra.
     */
    interface VideoPlaneBlend {
        set(on: boolean): void;
        /** Another material (the tap prompt) that must stay in the same layer as the video. */
        follow(material: pc.StandardMaterial): void;
    }
    const videoPlaneBlends = new WeakMap<HTMLVideoElement, VideoPlaneBlend>();
    function createVideoPlaneBlend(material: pc.StandardMaterial): VideoPlaneBlend {
        const materials = [material];
        let on = false;
        const apply = (target: pc.StandardMaterial) => {
            if (setTextureTranslucent(target, on) !== 'switched') return;
            translucentLayersDirty = true;
            renderScheduler.requestFrame('video-alpha-blend');
        };
        return {
            set(next) {
                if (next === on) return;
                on = next;
                materials.forEach(apply);
            },
            follow(target) {
                materials.push(target);
                if (on) apply(target);
            },
        };
    }
    /**
     * Video element(s), texture(s) and unlit material of a video plane, shared by video hotspots
     * and stacked-marker video layers: WebM embedded alpha, the iOS dual-video alpha method, the
     * backup URL, per-frame texture upload (steady-state only while playing and `owner` is
     * enabled; the handler goes with `owner`) and the first frame of click-to-play videos.
     * `onMetadata` reports the video's pixel size once it is known.
     */
    function createVideoPlaneMedia(
        source: VideoPlaneSource,
        owner: pc.Entity,
        opts: { logName?: string; uploadLabel: string; opacity: number; onMetadata?: (width: number, height: number) => void },
    ): VideoPlaneMedia {
        // Which videos play and where the transparency comes from: the iOS dual-video method
        // (main MP4 + grayscale mask; iOS Safari cannot decode VP9 alpha, and iPadOS Safari reports
        // a desktop UA) or the main video's own WebM alpha. WebM is recognised from Firebase /
        // CDN / Tauri / blob: URLs and the upload's file name, not only a URL ending in `.webm`
        // (utils/videoFormat.ts); an MP4 never takes the WebM-alpha path.
        const plan = planVideoPlaneAlpha(source, isIOSLikeDevice());
        const mainVideoUrl = plan.mainUrl;
        const alphaVideoUrl = plan.alphaMaskUrl;
        const hasWebMAlpha = plan.webmAlpha;
        // Helper to create video element and texture
        const createVideoAndTexture = (url: string, isAlpha: boolean, useRGBA: boolean = false) => {
            const v = document.createElement('video');
            v.src = url;
            v.loop = source.videoLoop !== false;
            v.crossOrigin = 'anonymous';
            v.playsInline = true;
            v.preload = 'metadata'; // Load metadata + first frame
            // Default muted state
            if (isAlpha) {
                v.muted = true; // Alpha video always muted
            }
            else {
                v.muted = source.videoMuted !== false;
            }
            // Autoplay handling
            if (source.mediaTriggerMode === 'autoplay') {
                v.autoplay = true;
                v.muted = true; // Autoplay requires muted (browser policy)
            }
            // Use RGBA format for WebM (alpha support) or standard RGB
            const t = new pc.Texture(app.graphicsDevice, {
                width: 1,
                height: 1,
                format: useRGBA ? pc.PIXELFORMAT_R8_G8_B8_A8 : pc.PIXELFORMAT_R8_G8_B8,
                mipmaps: false,
                minFilter: pc.FILTER_LINEAR,
                magFilter: pc.FILTER_LINEAR,
                addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                addressV: pc.ADDRESS_CLAMP_TO_EDGE
            });
            return { video: v, texture: t };
        };
        // Create main video and texture - use RGBA for WebM to support embedded alpha
        const main = createVideoAndTexture(mainVideoUrl, false, hasWebMAlpha);
        const video = main.video;
        const videoTexture = main.texture;
        const videoTextureUploadState: VideoTextureUploadState = { sourceAttached: false, warnedBackResource: false, pendingUpload: true };
        armVideoUploadOnFrameChange(video, videoTextureUploadState);
        // Add fallback to backup video URL if main fails (codec compatibility)
        if (source.videoBackupUrl) {
            const backupUrl = source.videoBackupUrl;
            video.onerror = () => {
                console.log(`[Hotspot] Main video failed, trying backup: ${backupUrl}`);
                video.src = backupUrl;
                video.load();
            };
        }
        // Create material - unlit (emissive only, no diffuse lighting)
        // Video should display as-is without scene lighting affecting brightness
        const material = new pc.StandardMaterial();
        material.useLighting = false;
        material.emissiveMap = videoTexture;
        material.emissive = new pc.Color(1, 1, 1);
        material.diffuse = new pc.Color(0, 0, 0);
        material.depthTest = true;
        material.depthWrite = true;
        material.cull = pc.CULLFACE_NONE; // Double-sided rendering
        material.twoSidedLighting = true; // Proper lighting on both sides
        // Default blend type for non-transparent videos
        material.blendType = pc.BLEND_NORMAL;
        material.alphaTest = 0.01;
        // WebM with embedded alpha - configure for transparency
        if (hasWebMAlpha) {
            material.opacityMap = videoTexture; // Alpha channel from same texture
            material.blendType = pc.BLEND_NORMAL;
            material.alphaTest = 0.01;
            console.log(`[Hotspot] WebM video with alpha enabled: ${opts.logName}`);
        }
        // Alpha video support (iOS dual-video method) - takes precedence over WebM embedded alpha
        // iOS uses a separate grayscale video where luminance = alpha (chroma key technique)
        let alphaVideo: HTMLVideoElement | null = null;
        let alphaTexture: pc.Texture | null = null;
        let alphaTextureUploadState: VideoTextureUploadState | null = null;
        if (alphaVideoUrl) {
            const alpha = createVideoAndTexture(alphaVideoUrl, true, false);
            alphaVideo = alpha.video;
            alphaTexture = alpha.texture;
            alphaTextureUploadState = { sourceAttached: false, warnedBackResource: false, pendingUpload: true };
            armVideoUploadOnFrameChange(alphaVideo, alphaTextureUploadState);
            material.opacityMap = alphaTexture;
            // CRITICAL: Use 'r' channel for opacity since iOS alpha videos store
            // alpha as grayscale luminance in RGB, not in actual alpha channel
            // This matches HTML export's getAlphaFromRGB = true behavior
            material.opacityMapChannel = 'r';
            material.blendType = pc.BLEND_NORMAL;
            material.alphaTest = 0.01;
            // Synchronize alpha video with main video
            video.addEventListener('play', () => {
                if (alphaVideo && alphaVideo.paused) {
                    alphaVideo.currentTime = video.currentTime;
                    alphaVideo.play().catch(console.warn);
                }
            });
            video.addEventListener('pause', () => {
                if (alphaVideo && !alphaVideo.paused) {
                    alphaVideo.pause();
                }
            });
            video.addEventListener('seeked', () => {
                if (alphaVideo) {
                    alphaVideo.currentTime = video.currentTime;
                }
            });
            console.log(`[Hotspot] iOS alpha mask video enabled: ${opts.logName}, alphaUrl=${alphaVideoUrl.substring(0, 50)}...`);
        }
        material.update();
        applyEntityOpacity(material, opts.opacity);
        // Transparent videos blend over the splats (VideoPlaneBlend). A mask always carries
        // alpha; a WebM does once a decoded frame shows a transparent pixel (most WebMs have no
        // alpha channel, and Safari decodes VP9 without it), so opaque videos stay as they were.
        const blend = createVideoPlaneBlend(material);
        videoPlaneBlends.set(video, blend);
        if (alphaVideoUrl) blend.set(true);
        let alphaProbe = hasWebMAlpha ? createVideoAlphaProbe() : null;
        const uploadMainFrame = (label: string) => {
            if (!uploadVideoTextureFrame(videoTexture, video, videoTextureUploadState, label) || !alphaProbe) return;
            const result = alphaProbe(video, performance.now());
            if (result === 'pending') return;
            alphaProbe = null;
            if (result === 'alpha') {
                blend.set(true);
                console.log(`[Hotspot] Transparent video blends over the splats: ${opts.logName}`);
            }
        };
        // Upload video frames: steady-state uploads only while PLAYING and the entity is
        // visible (a paused frame can't change except via seek/load/pause, which arm
        // pendingUpload). pendingUpload uploads run regardless of entity.enabled so a video
        // paused/seeked while hidden (visibilityRange) doesn't show a stale frame on re-show.
        const videoTextureUpdateFn = () => {
            if (videoTextureUploadState.pendingUpload || (!video.paused && owner.enabled)) {
                uploadMainFrame(`${opts.uploadLabel}:main`);
            }
            if (alphaVideo && alphaTexture && alphaTextureUploadState &&
                (alphaTextureUploadState.pendingUpload || (!alphaVideo.paused && owner.enabled))) {
                uploadVideoTextureFrame(alphaTexture, alphaVideo, alphaTextureUploadState, `${opts.uploadLabel}:alpha`);
            }
        };
        app.on('update', videoTextureUpdateFn);
        // Editor can destroy/recreate hotspots while the app lives — without this the dead
        // handler accumulates (same pattern as the billboard update handler).
        owner.once('destroy', () => {
            app.off('update', videoTextureUpdateFn);
            alphaProbe = null;
        });
        // Report the video's size (aspect ratio) once metadata arrives
        if (opts.onMetadata) {
            const onMetadata = opts.onMetadata;
            video.addEventListener('loadedmetadata', () => {
                const width = video.videoWidth;
                const height = video.videoHeight;
                if (width > 0 && height > 0) {
                    onMetadata(width, height);
                }
            });
        }
        // Force first frame display for click-triggered videos (matching HTML export behavior)
        // loadeddata fires when enough data is available to render the first frame
        if (source.mediaTriggerMode === 'click' || !source.mediaTriggerMode) {
            video.addEventListener('loadeddata', () => {
                // Seek to start to ensure first frame is decoded
                video.currentTime = 0;
                // Force texture upload to show the first frame on the plane
                uploadMainFrame(`${opts.uploadLabel}:first-frame`);
                if (alphaVideo && alphaTexture) {
                    alphaVideo.currentTime = 0;
                    if (alphaTextureUploadState) {
                        uploadVideoTextureFrame(alphaTexture, alphaVideo, alphaTextureUploadState, `${opts.uploadLabel}:alpha-first-frame`);
                    }
                }
                console.log(`[Hotspot] First frame loaded for: ${opts.logName}`);
            }, { once: true });
            // Trigger loading (some browsers need explicit load() call)
            video.load();
            if (alphaVideo) alphaVideo.load();
        }
        return { video, alphaVideo, material, textures: alphaTexture ? [videoTexture, alphaTexture] : [videoTexture] };
    }

    /**
     * "Tap to Start" / "Tap for Audio" prompt over a video plane (matching HTML export), for
     * click-triggered videos and unmuted autoplay videos; null for other trigger modes. The
     * prompt is a child of `parent`, slightly in front of it, and follows the video's state.
     */
    function createVideoTapOverlay(
        parent: pc.Entity,
        video: HTMLVideoElement,
        playback: { mediaTriggerMode?: string; videoMuted?: boolean },
        id: string | number,
        logName?: string,
    ): pc.Entity | null {
        if (!(playback.mediaTriggerMode === 'click' || !playback.mediaTriggerMode || (playback.mediaTriggerMode === 'autoplay' && playback.videoMuted === false))) {
            return null;
        }
        const overlayEntity = new pc.Entity('video-overlay-' + id);
        overlayEntity.addComponent('render', {
            type: 'plane',
            castShadows: false,
            receiveShadows: false,
            layers: [hotspotMeshLayer.id]
        });
        // Position slightly in front of the video plane along the plane's native normal (+Y)
        // PlayCanvas planes are XZ with +Y normal; applyPlaneRotation rotates to face forward
        overlayEntity.setLocalPosition(0, 0.02, 0);
        overlayEntity.setLocalScale(1, 1, 1);
        // Create overlay texture with play icon and text
        const overlayCanvas = document.createElement('canvas');
        overlayCanvas.width = 512;
        overlayCanvas.height = 512;
        const octx = overlayCanvas.getContext('2d')!;
        // Clear to transparent
        octx.clearRect(0, 0, 512, 512);
        // Semi-transparent dark background
        octx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        octx.fillRect(0, 0, 512, 512);
        // Draw play triangle icon
        octx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        octx.beginPath();
        octx.moveTo(220, 180);
        octx.lineTo(220, 300);
        octx.lineTo(320, 240);
        octx.closePath();
        octx.fill();
        // Draw text
        octx.font = 'bold 36px sans-serif';
        octx.textAlign = 'center';
        octx.textBaseline = 'middle';
        octx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        octx.shadowBlur = 8;
        octx.shadowOffsetX = 2;
        octx.shadowOffsetY = 2;
        octx.fillStyle = 'white';
        const overlayText = (playback.mediaTriggerMode === 'autoplay' && playback.videoMuted === false) ? 'Tap for Audio' : 'Tap to Start';
        octx.fillText(overlayText, 256, 350);
        const overlayTexture = new pc.Texture(app.graphicsDevice, {
            format: pc.PIXELFORMAT_R8_G8_B8_A8,
            mipmaps: false,
            minFilter: pc.FILTER_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE
        });
        overlayTexture.setSource(overlayCanvas);
        const overlayMat = new pc.StandardMaterial();
        overlayMat.useLighting = false;
        overlayMat.emissive = new pc.Color(1, 1, 1);
        overlayMat.emissiveMap = overlayTexture;
        overlayMat.diffuse = new pc.Color(0, 0, 0);
        overlayMat.opacityMap = overlayTexture;
        overlayMat.blendType = pc.BLEND_NORMAL;
        overlayMat.alphaTest = 0.01;
        overlayMat.depthTest = true;
        overlayMat.depthWrite = false;
        overlayMat.cull = pc.CULLFACE_NONE;
        overlayMat.update();
        // In front of a transparent video it must draw after it, in the same layer.
        videoPlaneBlends.get(video)?.follow(overlayMat);
        overlayEntity.render!.material = overlayMat;
        parent.addChild(overlayEntity);
        // Update overlay visibility based on video state
        const updateOverlayVisibility = () => {
            if (playback.mediaTriggerMode === 'autoplay' && playback.videoMuted === false) {
                overlayEntity.enabled = video.muted;
            } else {
                overlayEntity.enabled = video.paused;
            }
        };
        video.addEventListener('play', updateOverlayVisibility);
        video.addEventListener('pause', updateOverlayVisibility);
        video.addEventListener('volumechange', updateOverlayVisibility);
        // Initially visible (video starts paused)
        overlayEntity.enabled = true;
        console.log(`[Hotspot] Created tap-to-play overlay for: ${logName}`);
        return overlayEntity;
    }

    /**
     * Material and frame-by-frame player of a GIF plane, shared by GIF hotspots and stacked
     * marker GIF layers. The texture is attached before `onReady` runs.
     */
    function createGifMarkerMaterial(
        gifUrl: string,
        useLighting: boolean,
        initialOpacity: number,
        callbacks: { onReady: (gif: AnimatedGifTexture, texture: pc.Texture) => void; onError: (error: Error) => void },
    ): { material: pc.StandardMaterial; gif: AnimatedGifTexture } {
        const material = new pc.StandardMaterial();
        material.blendType = pc.BLEND_NORMAL;
        material.opacity = 1;
        material.depthTest = true;
        material.depthWrite = true;
        material.cull = pc.CULLFACE_NONE;
        material.twoSidedLighting = true;
        material.alphaTest = 0.01;
        if (!useLighting) {
            material.emissive = new pc.Color(1, 1, 1);
            material.diffuse = new pc.Color(0, 0, 0);
        }
        material.update();
        // Entity opacity via the translucency helper (keeps the GIF alpha cutout intact)
        applyEntityOpacity(material, initialOpacity);
        // Use AnimatedGifTexture for proper frame-by-frame animation with gifuct-js
        const gifTexture = new AnimatedGifTexture(app, gifUrl, {
            autoPlay: true,
            onReady: () => {
                if (isDestroyed) {
                    gifTexture.destroy();
                    return;
                }
                if (gifTexture.texture) {
                    gifTexture.texture.premultiplyAlpha = false;
                    if (!useLighting) {
                        material.emissiveMap = gifTexture.texture;
                    }
                    material.diffuseMap = gifTexture.texture;
                    material.opacityMap = gifTexture.texture;
                    material.opacityMapChannel = 'a';
                    material.update();
                    callbacks.onReady(gifTexture, gifTexture.texture);
                }
            },
            onError: callbacks.onError,
        });
        animatedGifs.push(gifTexture);
        return { material, gif: gifTexture };
    }

    /** Destroy the textures a marker material samples (each texture is destroyed once). */
    function destroyMaterialTextures(material: pc.StandardMaterial): void {
        const textures = new Set<pc.Texture>();
        for (const texture of [material.emissiveMap, material.diffuseMap, material.opacityMap]) {
            if (texture) textures.add(texture);
        }
        textures.forEach((texture) => texture.destroy());
    }

    /** Stop a GIF player and drop it from the viewer-wide list. */
    function releaseAnimatedGif(gif: AnimatedGifTexture): void {
        gif.destroy();
        const index = animatedGifs.indexOf(gif);
        if (index >= 0) animatedGifs.splice(index, 1);
    }

    /**
     * Stop a marker's <video> and free its decoder and buffered media. Removing the src
     * attribute and calling load() empties the element (assigning src = '' instead resolves to
     * the page URL and starts a request for it). The backup-URL fallback is detached first so
     * the emptied element does not start loading the backup video.
     */
    function releaseVideoElement(video: HTMLVideoElement): void {
        video.onerror = null;
        video.pause();
        video.removeAttribute('src');
        video.load();
    }

    /**
     * The resources record of a hotspot / portal marker, created on first use. The record is
     * released once, when the entity is destroyed: the editor removes and re-adds markers while
     * the app lives, so anything left behind accumulates per edit.
     */
    function ownMarkerResources(entity: ViewerRuntimeEntity): MarkerOwnedResources {
        let resources = entity.markerResources;
        if (!resources) {
            resources = { released: false, materials: [], textures: [], gifs: [], videos: [], audioAssets: [] };
            entity.markerResources = resources;
            entity.once('destroy', () => releaseMarkerResources(entity));
        }
        return resources;
    }

    function releaseMarkerResources(entity: ViewerRuntimeEntity): void {
        const resources = entity.markerResources;
        if (!resources || resources.released) return;
        resources.released = true;
        for (const video of resources.videos) releaseVideoElement(video);
        for (const gif of resources.gifs) releaseAnimatedGif(gif);
        for (const asset of resources.audioAssets) {
            app.assets.remove(asset);
            asset.unload();
        }
        try {
            for (const material of resources.materials) {
                const gif = imageMaterialGifs.get(material);
                if (gif) releaseAnimatedGif(gif);
                destroyMaterialTextures(material);
                material.destroy();
            }
            for (const texture of resources.textures) texture.destroy();
        } catch (error) {
            console.warn('[Marker] Failed to release marker resources:', error);
        }
        resources.materials.length = 0;
        resources.textures.length = 0;
        resources.gifs.length = 0;
        resources.videos.length = 0;
        resources.audioAssets.length = 0;
    }

    interface MarkerStackBuildOptions {
        spacing?: number;
        useLighting: boolean;
        opacity: number;
        /** Upload / log label, e.g. `hotspot:<id>`. */
        label: string;
        logName?: string;
        /** Playback of the stack's video layer (a portal passes autoplay + muted + loop). */
        video: { videoLoop?: boolean; videoMuted?: boolean; mediaTriggerMode?: string };
        /** Runs after every layout pass (initial, then each time a layer reports its size). */
        onLayout?: () => void;
    }
    /** A video layer gives up waiting for metadata after this long and keeps its provisional aspect. */
    const MARKER_STACK_VIDEO_SETTLE_MS = 4000;
    /**
     * Build a stacked marker (`type: 'stack'`) on `entity`: one plane child per layer, laid out
     * top to bottom by layoutMarkerStack in the entity's local plane frame. The caller gives the
     * entity the same transform as an image marker (local scale (x, z, y) + applyPlaneRotation),
     * so billboarding, the gizmo, opacity, visibility and leader lines treat the whole stack as
     * one marker. Layer media reuses the image / video / GIF marker paths. The entity stays
     * hidden until every layer has loaded, failed or timed out; each layer's real aspect ratio
     * re-runs the layout. Everything is released when the entity is destroyed.
     */
    function buildMarkerStack(entity: ViewerRuntimeEntity, layers: MarkerLayer[], options: MarkerStackBuildOptions): MarkerStackRuntime {
        const stack: MarkerStackRuntime = {
            layers: [],
            spacing: normalizeMarkerStackSpacing(options.spacing),
            layout: layoutMarkerStack([]),
            materials: [],
            released: false,
        };
        entity.markerStack = stack;
        entity.textureLoaded = false;
        entity.hiddenUntilTextureLoaded = true;
        entity.enabled = false;
        const relayout = () => {
            if (stack.released) return;
            stack.layout = layoutMarkerStack(stack.layers.map((rt) => ({
                width: rt.layer.width ?? 1,
                aspect: rt.aspect,
                offsetX: rt.layer.offsetX ?? 0,
            })), stack.spacing);
            stack.layout.layers.forEach((placement, i) => {
                // The entity is plane-framed: local X = right, local -Z = up, local +Y = toward the viewer.
                const child = stack.layers[i].entity;
                child.setLocalPosition(placement.x, placement.depth, -placement.y);
                child.setLocalScale(placement.width, 1, placement.height);
            });
            options.onLayout?.();
            renderScheduler.requestFrame('marker-stack-layout');
        };
        const settle = (rt: MarkerStackLayerRuntime, size?: { width: number; height: number }) => {
            if (stack.released) return;
            if (size && size.width > 0 && size.height > 0) rt.aspect = size.width / size.height;
            rt.settled = true;
            relayout();
            if (entity.hiddenUntilTextureLoaded && stack.layers.every((l) => l.settled)) {
                entity.textureLoaded = true;
                if ((!entity.visibilityRange || entity.shouldBeVisible) && !hiddenByOverride(entity)) {
                    entity.enabled = true;
                }
                entity.hiddenUntilTextureLoaded = false;
                syncLeaderLineVisibility(entity);
                renderScheduler.requestFrame('marker-stack-ready');
            }
        };
        layers.forEach((layer, index) => {
            const child = new pc.Entity(`marker-stack-layer-${index}`);
            child.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            // An image layer that points at a GIF animates like a GIF layer.
            const kind = layer.kind === 'image' && isGifUrl(layer.url) ? 'gif' : layer.kind;
            const rt: MarkerStackLayerRuntime = { layer, entity: child, aspect: MARKER_LAYER_PROVISIONAL_ASPECT[kind], settled: false };
            if (kind === 'video') {
                const media = createVideoPlaneMedia({
                    ...options.video,
                    videoUrl: layer.url,
                    videoName: layer.name,
                    videoBackupUrl: layer.videoBackupUrl,
                    webmHasAlpha: layer.webmHasAlpha,
                    useIOSVideoAlphaMethod: layer.useIOSVideoAlphaMethod,
                    forceIOSVideoAlphaMethodForAllDevices: layer.forceIOSVideoAlphaMethodForAllDevices,
                    iosMainVideoUrl: layer.iosMainVideoUrl,
                    iosMainVideoName: layer.iosMainVideoName,
                    alphaMaskVideoUrl: layer.alphaMaskVideoUrl,
                }, child, {
                    logName: options.logName,
                    uploadLabel: `${options.label}:layer-${index}`,
                    opacity: options.opacity,
                    onMetadata: (width, height) => settle(rt, { width, height }),
                });
                rt.material = media.material;
                rt.video = media.video;
                rt.alphaVideo = media.alphaVideo;
                rt.videoTextures = media.textures;
                // A video that never reports metadata (blocked, failed) must not keep the stack hidden.
                media.video.addEventListener('error', () => { if (!rt.settled) settle(rt); });
                setTimeout(() => { if (!rt.settled) settle(rt); }, MARKER_STACK_VIDEO_SETTLE_MS);
            }
            else if (kind === 'gif') {
                const gifPlane = createGifMarkerMaterial(layer.url, options.useLighting, options.opacity, {
                    onReady: (_gif, texture) => settle(rt, { width: texture.width, height: texture.height }),
                    onError: (error) => {
                        console.error(`[MarkerStack] Failed to load GIF layer: ${layer.url}`, error);
                        if (stack.released) return;
                        // Same cue as a failed image: faint red, so the layer stays findable.
                        gifPlane.material.emissive = new pc.Color(1, 0, 0);
                        gifPlane.material.update();
                        applyEntityOpacity(gifPlane.material, 0.3);
                        settle(rt);
                    },
                });
                rt.material = gifPlane.material;
                rt.gif = gifPlane.gif;
            }
            else {
                const material = createImageMaterial(layer.url, options.useLighting, options.opacity, (size) => {
                    // A stack released while its image was loading still owns the new texture.
                    if (stack.released) {
                        destroyMaterialTextures(material);
                        return;
                    }
                    settle(rt, size);
                });
                rt.material = material;
            }
            if (rt.material) {
                child.render!.material = rt.material;
                stack.materials.push(rt.material);
            }
            stack.layers.push(rt);
            entity.addChild(child);
        });
        entity.markerStackLayers = stack.layers.map((rt) => rt.entity);
        relayout();
        entity.once('destroy', () => releaseMarkerStack(entity));
        console.log(`[MarkerStack] Created ${layers.length}-layer stack for ${options.label} (${layers.map((l) => l.kind).join(', ')})`);
        return stack;
    }

    /**
     * Release everything a stacked marker owns: stop and detach its video, stop its GIF players,
     * close its spatial-audio context and destroy its textures and materials. Runs once, when
     * the marker entity is destroyed (editor rebuild / removal, viewer teardown).
     */
    function releaseMarkerStack(entity: ViewerRuntimeEntity): void {
        const stack = entity.markerStack;
        if (!stack || stack.released) return;
        stack.released = true;
        for (const rt of stack.layers) {
            for (const video of [rt.video, rt.alphaVideo]) {
                if (video) releaseVideoElement(video);
            }
            if (rt.gif) releaseAnimatedGif(rt.gif);
            try {
                rt.videoTextures?.forEach((texture) => texture.destroy());
                if (rt.material) {
                    destroyMaterialTextures(rt.material);
                    rt.material.destroy();
                }
                if (rt.overlay?.render?.material) {
                    const overlayMaterial = rt.overlay.render.material as pc.StandardMaterial;
                    destroyMaterialTextures(overlayMaterial);
                    overlayMaterial.destroy();
                }
            } catch (error) {
                console.warn('[MarkerStack] Failed to release layer resources:', error);
            }
        }
        const spatial = entity.videoSpatialAudio;
        if (spatial) {
            const contextIndex = allAudioContexts.indexOf(spatial.audioCtx);
            if (contextIndex >= 0) allAudioContexts.splice(contextIndex, 1);
            spatial.audioCtx.close().catch(() => {});
            entity.videoSpatialAudio = null;
        }
        stack.materials.length = 0;
    }

    /** Nearest hit (ray parameter) of a ray on any visible layer of a stacked marker, or null. */
    const _markerStackInverseWorld = new pc.Mat4();
    function rayHitMarkerStack(entity: ViewerRuntimeEntity, from: pc.Vec3, dir: pc.Vec3): number | null {
        const stack = entity.markerStack;
        if (!stack) return null;
        let best: number | null = null;
        for (const rt of stack.layers) {
            if (!rt.entity.enabled) continue;
            _markerStackInverseWorld.copy(rt.entity.getWorldTransform()).invert();
            const t = rayHitsUnitPlane(from, dir, _markerStackInverseWorld.data, 0.02);
            if (t !== null && (best === null || t < best)) best = t;
        }
        return best;
    }

    /**
     * Leader-line start for a stacked marker: where a line from the marker's pivot along `dir`
     * leaves the stack's bounds, using the marker's current world axes (plane frame: local X is
     * right, local -Z is up). Undefined for other markers.
     */
    function markerStackLeaderEdge(entity: ViewerRuntimeEntity): ((dir: pc.Vec3) => number) | undefined {
        const stack = entity.markerStack;
        if (!stack || stack.layers.length === 0) return undefined;
        return (dir: pc.Vec3) => {
            const world = entity.getWorldTransform();
            const right = world.getX(new pc.Vec3());
            const up = world.getZ(new pc.Vec3()).mulScalar(-1);
            const scaleRight = right.length();
            const scaleUp = up.length();
            if (scaleRight < 1e-9 || scaleUp < 1e-9) return 0;
            right.mulScalar(1 / scaleRight);
            up.mulScalar(1 / scaleUp);
            return markerStackEdgeDistance(stack.layout.bounds, { x: scaleRight, y: scaleUp }, dir.dot(right), dir.dot(up));
        };
    }

    /** Apply an entity opacity to every layer of a stacked marker. Returns false for other markers. */
    function applyMarkerStackOpacity(entity: ViewerRuntimeEntity, opacity: number): boolean {
        const stack = entity.markerStack;
        if (!stack) return false;
        for (const material of stack.materials) applyEntityOpacity(material, opacity);
        return true;
    }

    /** Billboard culling for every layer of a stacked marker (no-op for other markers). */
    function setMarkerStackCull(entity: ViewerRuntimeEntity, cull: number): void {
        for (const material of entity.markerStack?.materials ?? []) {
            material.cull = cull;
            material.update();
        }
    }

    /**
     * Create a single hotspot entity with full rendering (sphere, image, video, gif, billboard, etc.)
     * Used by both createHotspots() at init and the editor mutation API.
     */
    function createSingleHotspot(hotspot: HotspotConfigLike, index: number): pc.Entity {
        const entity = new pc.Entity(`hotspot-${hotspot.id || index}`);
        // Materials, textures, videos, GIF players and audio assets this marker creates are
        // released when the entity is destroyed (editor removal / rebuild, viewer teardown).
        const resources = ownMarkerResources(entity as ViewerRuntimeEntity);
        // Get position (negate Z for BabylonJS -> PlayCanvas coordinate conversion)
        const pos = playCanvasPositionFromSceneVec3(hotspot.position);
        entity.setPosition(pos.x, pos.y, pos.z);
        // Get scale
        const rawScale = hotspot.scale || { _x: 1, _y: 1, _z: 1 };
        const scaleObj = typeof rawScale === 'number' ? { x: rawScale, y: rawScale, z: rawScale } as LegacyVec3 : rawScale;
        const sx = scaleObj._x ?? scaleObj.x ?? 1;
        const sy = scaleObj._y ?? scaleObj.y ?? 1;
        const sz = scaleObj._z ?? scaleObj.z ?? 1;
        // Get rotation in radians (BabylonJS stores Euler angles in radians, YXZ order)
        const rot = hotspot.rotation || { _x: 0, _y: 0, _z: 0 };
        const rotXRad = rot._x ?? rot.x ?? 0;
        const rotYRad = rot._y ?? rot.y ?? 0;
        const rotZRad = rot._z ?? rot.z ?? 0;
        // Convert BJS rotation to PC quaternion (handles YXZ→RH conversion properly)
        entity.setRotation(bjsRotationToPC(rotXRad, rotYRad, rotZRad));
        // Stacked marker layers the viewer can draw (none → the default sphere below)
        const stackLayers = hotspot.type === 'stack' ? sanitizeMarkerStack(hotspot.markerStack) : [];
        // Create geometry based on type
        if (hotspot.type === 'sphere') {
            entity.addComponent('render', {
                type: 'sphere',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const material = new pc.StandardMaterial();
            const color = parseColor(hotspot.color || '#ffffff');
            const useLighting = hotspot.useLighting === true;
            if (useLighting) {
                material.diffuse = color;
                material.emissive = color.clone();
                (material.emissive as pc.Color).mulScalar(0.3);
            } else {
                // Unlit (default): full emissive so white looks white, not grey
                material.diffuse = new pc.Color(0, 0, 0);
                material.emissive = color;
                material.useLighting = false;
            }
            // Opaque, depth-writing base; the entity opacity (static, or animated at the current
            // progress) is applied as splat-depth-aware translucency so it composites correctly with the splats.
            material.opacity = 1;
            material.blendType = pc.BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
            applyEntityOpacity(material, resolveSphereEntityOpacity(hotspot, currentProgress * 100));
            entity.render!.material = material;
            (entity as ViewerRuntimeEntity).hotspotMaterial = material;
            resources.materials.push(material);
            entity.setLocalScale(sx * 0.2, sy * 0.2, sz * 0.2); // Scale down spheres
        }
        else if (hotspot.type === 'image' && hotspot.imageUrl) {
            entity.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            // Initial opacity: the static value, or the animated value at the current tour progress
            const initialOpacity = resolveEntityOpacity(hotspot, currentProgress * 100);
            // Store target opacity for animation system
            (entity as ViewerRuntimeEntity).targetOpacity = initialOpacity;
            (entity as ViewerRuntimeEntity).textureLoaded = false;
            // HIDE entity until texture is loaded to prevent WebGL errors
            (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = true;
            entity.enabled = false;
            // Check useLighting setting from hotspot (default to false = unlit)
            const useLighting = hotspot.useLighting === true;
            const material = createImageMaterial(hotspot.imageUrl, useLighting, initialOpacity, () => {
                // A hotspot removed while its image was loading still owns the new texture.
                if (resources.released) {
                    destroyMaterialTextures(material);
                    return;
                }
                // Callback when texture is ready - show the entity
                (entity as ViewerRuntimeEntity).textureLoaded = true;
                // Only enable if not hidden by visibility range
                if ((!(entity as ViewerRuntimeEntity).visibilityRange || (entity as ViewerRuntimeEntity).shouldBeVisible) && !hiddenByOverride(entity as ViewerRuntimeEntity)) {
                    entity.enabled = true;
                }
                (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = false;
                syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
            });
            entity.render!.material = material;
            resources.materials.push(material);
            entity.setLocalScale(sx, sz, sy);
            // Compose face-forward correction with user rotation (fixes 90° offset for non-zero rotations)
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            // Store material reference for opacity animation
            (entity as ViewerRuntimeEntity).hotspotMaterial = material;
            console.log(`[Hotspot] Created image hotspot: ${hotspot.title}, pos=(${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${(-pos.z).toFixed(2)}), rot=(${rotXRad.toFixed(3)}, ${rotYRad.toFixed(3)}, ${rotZRad.toFixed(3)}), scale=(${sx.toFixed(3)}, ${sy.toFixed(3)}, ${sz.toFixed(3)}), opacity=${initialOpacity}`);
        }
        else if (hotspot.type === 'video' && hotspot.videoUrl) {
            entity.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            // Aspect ratio scaling from video metadata
            const initialScale = { x: sx, y: sy, z: sz };
            const media = createVideoPlaneMedia({ ...hotspot, videoUrl: hotspot.videoUrl }, entity, {
                logName: hotspot.title,
                uploadLabel: `hotspot:${hotspot.id || hotspot.title || index}`,
                opacity: resolveEntityOpacity(hotspot, currentProgress * 100),
                onMetadata: (width, height) => {
                    const ratio = width / height;
                    // Only auto-adjust if scale is default (1,1)
                    if (initialScale.x === 1 && initialScale.y === 1) {
                        entity.setLocalScale(ratio * initialScale.y, initialScale.z, initialScale.y);
                    }
                },
            });
            const video = media.video;
            const material = media.material;
            entity.render!.material = material;
            resources.materials.push(material);
            resources.textures.push(...media.textures);
            resources.videos.push(video);
            if (media.alphaVideo) resources.videos.push(media.alphaVideo);
            entity.setLocalScale(sx, sz, sy);
            // Compose face-forward correction with user rotation
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            // Store video references for playback control
            (entity as ViewerRuntimeEntity).videoElement = video;
            (entity as ViewerRuntimeEntity).alphaVideoElement = media.alphaVideo;
            (entity as ViewerRuntimeEntity).hotspotMaterial = material;
            // Store media trigger mode and proximity settings
            (entity as ViewerRuntimeEntity).mediaTriggerMode = hotspot.mediaTriggerMode || 'click';
            (entity as ViewerRuntimeEntity).proximityDistance = hotspot.proximityDistance || 5;
            (entity as ViewerRuntimeEntity).isVideoPlaying = false;
            // Setup spatial audio for video if not muted
            if (hotspot.videoMuted !== true) {
                const videoSpatialAudio = setupVideoSpatialAudio(entity, video, hotspot);
                if (videoSpatialAudio) {
                    (entity as ViewerRuntimeEntity).videoSpatialAudio = videoSpatialAudio;
                }
            }
            // "Tap to Start" overlay for click-triggered videos (matching HTML export)
            const overlayEntity = createVideoTapOverlay(entity, video, hotspot, hotspot.id || index, hotspot.title);
            if (overlayEntity) {
                (entity as ViewerRuntimeEntity).videoOverlay = overlayEntity;
                const overlayMaterial = overlayEntity.render?.material as pc.StandardMaterial | undefined;
                if (overlayMaterial) resources.materials.push(overlayMaterial);
            }
            console.log(`[Hotspot] Created video hotspot: ${hotspot.title}, mode=${hotspot.mediaTriggerMode}, useAlpha=${!!media.alphaVideo}, spatialAudio=${!!(entity as ViewerRuntimeEntity).videoSpatialAudio}`);
        }
        else if (hotspot.type === 'gif' && hotspot.gifUrl) {
            // GIF hotspot implementation - uses AnimatedGifTexture for proper frame-by-frame animation
            entity.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            // Initial opacity: the static value, or the animated value at the current tour progress
            const initialOpacity = resolveEntityOpacity(hotspot, currentProgress * 100);
            // Store state for GIF animation
            (entity as ViewerRuntimeEntity).targetOpacity = initialOpacity;
            (entity as ViewerRuntimeEntity).textureLoaded = false;
            (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = true;
            entity.enabled = false;
            // Check useLighting setting from hotspot (default to false = unlit)
            const useLighting = hotspot.useLighting === true;
            const gifPlane = createGifMarkerMaterial(hotspot.gifUrl, useLighting, initialOpacity, {
                onReady: (gifTexture, texture) => {
                    if (resources.released) return;
                    const material = gifPlane.material;
                    entity.render!.material = material;
                    // Calculate aspect ratio from GIF dimensions
                    const tw = texture.width || 256;
                    const th = texture.height || 256;
                    const ratio = tw / th;
                    if (sx === 1 && sy === 1) {
                        entity.setLocalScale(ratio, sz, 1);
                    }
                    else {
                        entity.setLocalScale(sx, sz, sy);
                    }
                    // Compose face-forward correction with user rotation
                    applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
                    // Mark texture as loaded
                    (entity as ViewerRuntimeEntity).textureLoaded = true;
                    (entity as ViewerRuntimeEntity).gifTexture = texture;
                    (entity as ViewerRuntimeEntity).hotspotMaterial = material;
                    // Store the AnimatedGifTexture instance for cleanup
                    (entity as ViewerRuntimeEntity).animatedGifTexture = gifTexture;
                    // Enable entity if not hidden by visibility range
                    if ((!(entity as ViewerRuntimeEntity).visibilityRange || (entity as ViewerRuntimeEntity).shouldBeVisible) && !hiddenByOverride(entity as ViewerRuntimeEntity)) {
                        entity.enabled = true;
                    }
                    (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = false;
                    syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
                    console.log(`[Hotspot] Created GIF hotspot: ${hotspot.title}, opacity=${initialOpacity}, useLighting=${useLighting}`);
                },
                onError: (err) => {
                    if (resources.released) return;
                    console.error('[Hotspot] Failed to load GIF:', hotspot.gifUrl, err);
                    // Fallback to sphere
                    entity.addComponent('render', {
                        type: 'sphere',
                        castShadows: false,
                        receiveShadows: false,
                        layers: [hotspotMeshLayer.id]
                    });
                    const fallbackMaterial = new pc.StandardMaterial();
                    fallbackMaterial.diffuse = parseColor(hotspot.color || '#FF00FF');
                    fallbackMaterial.opacity = 0.8;
                    fallbackMaterial.blendType = pc.BLEND_NORMAL;
                    fallbackMaterial.update();
                    entity.render!.material = fallbackMaterial;
                    resources.materials.push(fallbackMaterial);
                    entity.setLocalScale(sx * 0.2, sy * 0.2, sz * 0.2);
                    entity.enabled = !hiddenByOverride(entity as ViewerRuntimeEntity);
                    (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = false;
                    syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
                }
            });
            resources.materials.push(gifPlane.material);
            resources.gifs.push(gifPlane.gif);
        }
        else if (stackLayers.length > 0) {
            // Stacked marker: the entity is the marker (plane-framed like an image hotspot) and each
            // layer is a plane child, so the stack shares one transform, billboard, opacity,
            // visibility range, click action and leader line.
            const runtime = entity as ViewerRuntimeEntity;
            const initialOpacity = resolveEntityOpacity(hotspot, currentProgress * 100);
            runtime.targetOpacity = initialOpacity;
            const stack = buildMarkerStack(runtime, stackLayers, {
                spacing: hotspot.markerStackSpacing,
                useLighting: hotspot.useLighting === true,
                opacity: initialOpacity,
                label: `hotspot:${hotspot.id || hotspot.title || index}`,
                logName: hotspot.title,
                video: { videoLoop: hotspot.videoLoop, videoMuted: hotspot.videoMuted, mediaTriggerMode: hotspot.mediaTriggerMode },
                onLayout: () => {
                    // Re-anchor the leader line to the stack's real size once its media has loaded.
                    if (!runtime.leaderLineEntity) return;
                    const current = (runtime.hotspotData ?? hotspot) as HotspotConfigLike;
                    rebuildLeaderLine(runtime, current, hotspotLeaderLineShape(current));
                },
            });
            entity.setLocalScale(sx, sz, sy);
            // Compose face-forward correction with user rotation
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            // The stack's video layer is the hotspot's video: every video-hotspot control
            // (trigger modes, click toggle, mute, linked actions) reads entity.videoElement.
            const videoLayer = stack.layers.find((rt) => rt.video);
            if (videoLayer?.video) {
                const video = videoLayer.video;
                runtime.videoElement = video;
                runtime.alphaVideoElement = videoLayer.alphaVideo ?? null;
                runtime.mediaTriggerMode = hotspot.mediaTriggerMode || 'click';
                runtime.proximityDistance = hotspot.proximityDistance || 5;
                runtime.isVideoPlaying = false;
                if (hotspot.videoMuted !== true) {
                    const videoSpatialAudio = setupVideoSpatialAudio(entity, video, hotspot);
                    if (videoSpatialAudio) {
                        runtime.videoSpatialAudio = videoSpatialAudio;
                    }
                }
                const overlayEntity = createVideoTapOverlay(videoLayer.entity, video, hotspot, `${hotspot.id || index}-stack`, hotspot.title);
                if (overlayEntity) {
                    runtime.videoOverlay = overlayEntity;
                    videoLayer.overlay = overlayEntity;
                }
            }
            console.log(`[Hotspot] Created stacked hotspot: ${hotspot.title}, layers=${stackLayers.length}, video=${!!videoLayer}, opacity=${initialOpacity}`);
        }
        else {
            // Default: sphere
            entity.addComponent('render', {
                type: 'sphere',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const material = new pc.StandardMaterial();
            const color = parseColor(hotspot.color || '#CC5833');
            const useLighting = hotspot.useLighting === true;
            if (useLighting) {
                material.diffuse = color;
                material.emissive = color.clone();
                (material.emissive as pc.Color).mulScalar(0.3);
            } else {
                material.diffuse = new pc.Color(0, 0, 0);
                material.emissive = color;
                material.useLighting = false;
            }
            // Opaque, depth-writing base; entity opacity is applied as splat-depth-aware translucency.
            material.opacity = 1;
            material.blendType = pc.BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
            applyEntityOpacity(material, resolveEntityOpacity(hotspot, currentProgress * 100));
            entity.render!.material = material;
            (entity as ViewerRuntimeEntity).hotspotMaterial = material;
            resources.materials.push(material);
            entity.setLocalScale(sx * 0.2, sy * 0.2, sz * 0.2);
        }
        // Add collision for raycasting
        entity.addComponent('collision', {
            type: hotspot.type === 'sphere' ? 'sphere' : 'box',
            radius: 0.1,
            halfExtents: new pc.Vec3(0.5, 0.5, 0.05)
        });
        // Store hotspot data for click handling
        (entity as ViewerRuntimeEntity).hotspotData = hotspot as HotspotData;
        // Setup hotspot audio via PlayCanvas sound slot
        const audioSlotId = setupHotspotAudio(entity, hotspot);
        if (audioSlotId) {
            (entity as ViewerRuntimeEntity).hotspotAudioSlotId = audioSlotId;
            console.log(`[StorySplat Viewer] Audio setup for hotspot: ${hotspot.title || 'Untitled'}, triggerMode: ${hotspot.audioTriggerMode || 'click'}`);
            // Autoplay trigger: start playing after asset loads
            if (hotspot.audioTriggerMode === 'autoplay') {
                // The slot will autoplay once the asset is ready — poll for it (max 30s)
                let autoplayRetries = 0;
                const checkReady = () => {
                    if (isDestroyed || resources.released) return;
                    const slot = entity.sound?.slot(audioSlotId);
                    if (slot && slot.asset !== undefined && (entity as ViewerRuntimeEntity).hotspotAudioReady) {
                        if (!slot.isPlaying) {
                            slot.play();
                            (entity as ViewerRuntimeEntity).hotspotAudioPlaying = true;
                        }
                        return; // Asset ready, stop polling
                    } else if (autoplayRetries++ < 300) {
                        setTimeout(checkReady, 100);
                    }
                };
                checkReady();
            }
            // Hover trigger
            if (hotspot.audioTriggerMode === 'hover') {
                (entity as ViewerRuntimeEntity)._audioHoverMode = true;
                hasAnyHoverAudioHotspots = true;
            }
        }
        // Billboard mode - make plane always face the camera (with optional range support)
        if (hotspot.billboard) {
            // Use CULLFACE_BACK (normal) — we rotate 180° on Y so the front face points at camera
            const mat = entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined;
            if (mat) {
                mat.cull = pc.CULLFACE_BACK;
                mat.update();
            }
            setMarkerStackCull(entity as ViewerRuntimeEntity, pc.CULLFACE_BACK);
            // Store original rotation as quaternion (Euler angles lose precision on round-trip)
            const originalRotation = entity.getRotation().clone();
            // Check if billboard range is specified
            const hasBillboardRange = hasNonDefaultHotspotBillboardRange(hotspot);
            // Initialize billboard active state
            (entity as ViewerRuntimeEntity)._billboardActive = !hasBillboardRange; // Active by default if no range
            (entity as ViewerRuntimeEntity)._billboardOriginalRotation = originalRotation;
            // Track both sides of the look-at, including changes inherited from a parent.
            const billboardPose = new BillboardPoseTracker();
            const billboardUpdateFn = () => {
                // Skip destroyed entities (handler may outlive entity in editor mode)
                if (!entity.parent && !sceneContent.findByName(entity.name)) return;
                // Only apply billboard if active (no range or within range)
                if ((entity as ViewerRuntimeEntity)._billboardActive) {
                    if (billboardPose.update(entity, camera.getPosition())) renderScheduler.requestFrame('hotspot-billboard');
                } else {
                    billboardPose.reset(); // re-apply on the next activation
                }
            };
            app.on('update', billboardUpdateFn);
            // Clean up handler when entity is destroyed (prevents handler accumulation in editor)
            entity.once('destroy', () => {
                app.off('update', billboardUpdateFn);
            });
        }
        // Visibility range
        if (hotspot.visibilityRange) {
            (entity as ViewerRuntimeEntity).visibilityRange = hotspot.visibilityRange;
            entity.enabled = false; // Start hidden
        }
        sceneContent.addChild(entity);
        // Force immediate billboard orientation now that entity is in scene hierarchy
        if (hotspot.billboard) {
            entity.lookAt(camera.getPosition());
            entity.rotateLocal(90, 180, 0);
        }
        // Leader line — drawn from hotspot world position to configured target
        const leaderLine = buildLeaderLineEntity(hotspot, entity.getPosition().clone(), hotspotLeaderLineShape(hotspot), markerStackLeaderEdge(entity as ViewerRuntimeEntity));
        if (leaderLine) {
            sceneContent.addChild(leaderLine);
            (entity as ViewerRuntimeEntity).leaderLineEntity = leaderLine;
            syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
        }
        if (hotspot.id && directoryState.isHotspotHidden(hotspot.id)) {
            (entity as ViewerRuntimeEntity).folderHidden = true;
            entity.enabled = false;
            syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
        }
        hotspotEntities.push(entity);
        console.log(`[StorySplat Viewer] Created hotspot: ${hotspot.title || 'Untitled'}`);
        return entity;
    }
    // Create all hotspots from config
    function createHotspots(): void {
        if (!config.hotspots || config.hotspots.length === 0) {
            console.log('[StorySplat Viewer] No hotspots to create');
            return;
        }
        console.log(`[StorySplat Viewer] Creating ${config.hotspots.length} hotspots...`);
        config.hotspots.forEach((hotspot, index: number) => {
            createSingleHotspot(hotspot, index);
        });
    }
    // Update hotspot visibility, opacity, and video playback based on progress
    function updateHotspotVisibility(): void {
        const scrollPercent = currentProgress * 100;
        const waypointIndex = getWaypointIndexForProgress(currentProgress);
        const cameraPos = camera.getPosition();
        hotspotEntities.forEach((entity) => {
            const hotspot = entity.hotspotData;
            if (!hotspot)
                return;
            // Determine if hotspot should be visible based on visibility range
            // alwaysVisible overrides visibility range
            let shouldBeVisible = true;
            if (hotspot.alwaysVisible) {
                shouldBeVisible = true;
            }
            else {
                const range = entity.visibilityRange;
                if (range) {
                    if (range.type === 'waypoint') {
                        shouldBeVisible = isInVisibilityRange(waypointIndex, range.start, range.end);
                    }
                    else if (range.type === 'percentage') {
                        shouldBeVisible = isInVisibilityRange(scrollPercent, range.start, range.end);
                    }
                }
            }
            // Store visibility state for texture load callback
            entity.shouldBeVisible = shouldBeVisible;
            // Update billboard state based on range (if billboard mode is enabled with range)
            if (hotspot.billboard && hasNonDefaultHotspotBillboardRange(hotspot)) {
                const rangeStart = hotspot.billboardRangeStart ?? 0;
                const rangeEnd = hotspot.billboardRangeEnd ?? 100;
                const billboardActive = scrollPercent >= rangeStart && scrollPercent <= rangeEnd;
                // Update billboard active state
                const wasBillboardActive = entity._billboardActive;
                entity._billboardActive = billboardActive;
                // Toggle cull mode when billboard state changes
                if (billboardActive !== wasBillboardActive) {
                    const mat = entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined;
                    if (mat) {
                        mat.cull = billboardActive ? pc.CULLFACE_FRONT : pc.CULLFACE_NONE;
                        mat.update();
                    }
                    setMarkerStackCull(entity, billboardActive ? pc.CULLFACE_FRONT : pc.CULLFACE_NONE);
                }
                // If billboard is deactivated and we have original rotation, restore it
                if (!billboardActive && entity._billboardOriginalRotation) {
                    entity.setRotation(entity._billboardOriginalRotation);
                }
            }
            // Only enable if: should be visible AND (not an image OR texture is loaded)
            if (entity.hiddenUntilTextureLoaded) {
                // Image hotspot waiting for texture - keep hidden
                entity.enabled = false;
            }
            else {
                entity.enabled = shouldBeVisible && !hiddenByOverride(entity);
            }
            // Keep the leader line's enabled state in sync with its owning hotspot
            syncLeaderLineVisibility(entity);
            // Handle video playback triggers (a video hotspot, or a stack's video layer). A linked
            // action's play/pause holds them until their condition next changes (linkedActions.ts),
            // so they don't undo it on the next frame.
            if (hotspotHasVideoPlane(hotspot, entity) && !isAutoTriggerHeld(entity, linkedMediaTriggerCondition(entity))) {
                const triggerMode = entity.mediaTriggerMode || 'click';
                // Proximity trigger: play when camera is within proximityDistance
                if (triggerMode === 'proximity') {
                    const hotspotPos = entity.getPosition();
                    const distance = cameraPos.distance(hotspotPos);
                    const proximityDistance = entity.proximityDistance || 5;
                    if (distance <= proximityDistance && !entity.isVideoPlaying) {
                        // Enter proximity - play video
                        playVideoHotspot(entity, hotspot);
                        console.log(`[Hotspot] Proximity play: ${hotspot.title}, distance=${distance.toFixed(2)}`);
                    }
                    else if (distance > proximityDistance && entity.isVideoPlaying) {
                        // Only pause on leave if pauseOnLeaveProximity is true (default)
                        if (hotspot.pauseOnLeaveProximity !== false) {
                            pauseVideoHotspot(entity);
                            console.log(`[Hotspot] Proximity pause: ${hotspot.title}, distance=${distance.toFixed(2)}`);
                        }
                    }
                }
                // Autoplay trigger: play on loop immediately, never auto-paused
                if (triggerMode === 'autoplay') {
                    if (shouldBeVisible && !entity.isVideoPlaying) {
                        playVideoHotspot(entity, hotspot);
                    }
                }
                // Scroll trigger: play when visible based on visibility range
                if (triggerMode === 'scroll') {
                    if (shouldBeVisible && !entity.isVideoPlaying) {
                        // Entered visibility range - play video
                        playVideoHotspot(entity, hotspot);
                        console.log(`[Hotspot] Scroll play: ${hotspot.title}, scroll=${scrollPercent.toFixed(1)}%`);
                    }
                    else if (!shouldBeVisible && entity.isVideoPlaying) {
                        // Left visibility range - pause video
                        pauseVideoHotspot(entity);
                        console.log(`[Hotspot] Scroll pause: ${hotspot.title}, scroll=${scrollPercent.toFixed(1)}%`);
                    }
                }
            }
            // Handle opacity animation (only if texture is loaded for image hotspots). Every writer of
            // entity opacity (this path, editor updateHotspot, texture errors) goes through the same
            // helper, which skips values within epsilon of the last one applied, so steady-state tour
            // motion outside the window costs a lookup per frame.
            if (hotspot.opacityMode === 'animated' && hotspot.opacityAnimation && entity.animationOpacity === undefined) {
                if ((hotspot.type === 'image' || entity.markerStack) && !entity.textureLoaded) {
                    return;
                }
                const animatedOpacity = resolveAnimatedOpacity(hotspot.opacityAnimation, scrollPercent);
                if (applyMarkerStackOpacity(entity, animatedOpacity)) {
                    return;
                }
                const material = entity.hotspotMaterial ?? (entity.render?.material as pc.StandardMaterial | undefined);
                if (material) {
                    applyEntityOpacity(material, animatedOpacity);
                }
            }
        });
        // The directory greys out hotspots their own settings hide right now (only while it is open).
        directoryPanel?.refreshAvailability();
    }
    // Helper to play/pause video (defined here so updateHotspotVisibility can use them)
    function playVideoHotspot(entity: ViewerRuntimeEntity, hotspot: HotspotConfigLike): Promise<void> | undefined {
        const video = entity.videoElement as HTMLVideoElement;
        const alphaVideo = entity.alphaVideoElement as HTMLVideoElement | null;
        if (!video)
            return undefined;
        // Set muted state based on hotspot settings (except for autoplay which is always muted initially)
        if (entity.mediaTriggerMode !== 'autoplay') {
            video.muted = hotspot.videoMuted !== false;
        }
        // Resume AudioContext for spatial audio (browser requires user interaction)
        if (entity.videoSpatialAudio && entity.videoSpatialAudio.audioCtx) {
            const audioCtx = entity.videoSpatialAudio.audioCtx as AudioContext;
            if (audioCtx.state === 'suspended') {
                audioCtx.resume().then(() => {
                    console.log('[Audio] Video spatial audio context resumed');
                }).catch(err => console.warn('[Audio] Failed to resume video audio context:', err));
            }
        }
        // Returned so a linked action can retry muted when the browser blocks sound.
        const played = Promise.resolve(video.play());
        played.catch(err => console.warn('Video play failed:', err));
        if (alphaVideo) {
            alphaVideo.play().catch(err => console.warn('Alpha video play failed:', err));
        }
        entity.isVideoPlaying = true;
        return played;
    }
    function pauseVideoHotspot(entity: ViewerRuntimeEntity): void {
        const video = entity.videoElement as HTMLVideoElement;
        const alphaVideo = entity.alphaVideoElement as HTMLVideoElement | null;
        if (!video)
            return;
        video.pause();
        if (alphaVideo) {
            alphaVideo.pause();
        }
        entity.isVideoPlaying = false;
    }
    // ── Linked actions ──────────────────────────────────────────────────────────────────
    // A hotspot's or model's activation can also drive a DIFFERENT hotspot or model (see
    // linkedActions.ts for the semantics). Targets are resolved by id each time an action runs,
    // so editor rebuilds and deleted targets need no bookkeeping here.
    /** The target's automatic media-trigger condition (null = click mode, no trigger). */
    function linkedMediaTriggerCondition(entity: ViewerRuntimeEntity): boolean | null {
        const mode = entity.mediaTriggerMode || 'click';
        if (mode === 'proximity') {
            return camera.getPosition().distance(entity.getPosition()) <= (entity.proximityDistance || 5);
        }
        if (mode === 'autoplay' || mode === 'scroll') return entity.shouldBeVisible !== false;
        return null;
    }
    function linkedHotspotTarget(entity: ViewerRuntimeEntity, hotspot: HotspotData): LinkedHotspotTarget {
        // Through the entity's videoElement, not hotspot.type: any marker carrying a video works.
        const video = entity.videoElement;
        const slotId = entity.hotspotAudioSlotId;
        const slot = () => (slotId ? entity.sound?.slot(slotId) ?? null : null);
        return {
            video: video ? {
                element: video,
                unmuteWhenPlaying: entity.mediaTriggerMode === 'autoplay',
                play: () => playVideoHotspot(entity, hotspot as HotspotConfigLike),
                pause: () => pauseVideoHotspot(entity),
                rewind: () => {
                    video.currentTime = 0;
                    if (entity.alphaVideoElement) entity.alphaVideoElement.currentTime = 0;
                },
                holdTrigger: () => holdAutoTrigger(entity, linkedMediaTriggerCondition(entity)),
            } : null,
            audio: slotId ? {
                clickBehavior: hotspot.audioClickBehavior || 'pause-resume',
                ready: () => !!entity.hotspotAudioReady && !!slot(),
                state: () => {
                    const s = slot();
                    return s?.isPlaying ? 'playing' : s?.isPaused ? 'paused' : 'stopped';
                },
                play: () => { slot()?.play(); entity.hotspotAudioPlaying = true; },
                resume: () => { slot()?.resume(); entity.hotspotAudioPlaying = true; },
                pause: () => { slot()?.pause(); entity.hotspotAudioPlaying = false; },
                stop: () => { slot()?.stop(); entity.hotspotAudioPlaying = false; },
            } : null,
            openPopup: () => {
                if (!hasDisplayableHotspotPopupContent(hotspot)) return false;
                if (isInXR && !isIn8thWallAR) showARContent(hotspot);
                else if (!(isEditorMode && editorEditingHotspotId === hotspot.id)) showHotspotPopup(container, hotspot, currentButtonLabels);
                return true;
            },
        };
    }
    function linkedModelTarget(meshData: CustomMeshData): LinkedModelTarget {
        const anims = meshData.allAnimComponents;
        return {
            // Same play/pause the model's own animation trigger uses (same clips, same looping).
            animation: anims && anims.length > 0 ? {
                isPlaying: () => meshData.isAnimPlaying,
                play: () => { anims.forEach((animInfo) => playAnimComponentV2(animInfo)); meshData.isAnimPlaying = true; },
                stop: () => { anims.forEach((animInfo) => pauseAnimComponentV2(animInfo)); meshData.isAnimPlaying = false; },
            } : null,
        };
    }
    const linkedActionRunner = createLinkedActionRunner({
        getHotspot: (id) => {
            const entity = hotspotEntities.find((e) => e.hotspotData?.id === id);
            return entity?.hotspotData ? linkedHotspotTarget(entity, entity.hotspotData) : null;
        },
        getModel: (id) => {
            const meshData = customMeshEntities.get(id);
            return meshData ? linkedModelTarget(meshData) : null;
        },
        // Keyframe animations, looked up live (the editor replaces the system's list as it edits).
        getAnimation: (id) => {
            const system = entityAnimationSystem;
            const info = system?.listAnimations().find((a) => a.id === id);
            if (!system || !info) return null;
            return {
                timeBased: info.timelineMode === 'independent',
                isPlaying: () => system.isAnimationPlaying(id),
                play: () => system.playAnimation(id),
                pause: () => system.pauseAnimation(id),
                restart: () => system.restartAnimation(id),
            };
        },
    });
    /** What a hotspot's activation does to other elements and the scene: its cutaway, its splat swap, then its linked actions. */
    function runHotspotActions(hotspot: { id?: string; linkedActions?: LinkedAction[]; cutaway?: CutawayAction; splatSwap?: SplatSwapAction } | null | undefined): void {
        runClickCutaway(hotspot?.cutaway);
        runClickSplatSwap(hotspot?.splatSwap);
        if (!hotspot?.linkedActions?.length) return;
        void linkedActionRunner.run(hotspot.linkedActions, hotspot.id ? { kind: 'hotspot', id: hotspot.id } : undefined);
        renderScheduler.requestFrame('linked-action');
    }
    // ── Swap splat on click (see SplatSwapAction) ──
    // A hotspot's or model's activation shows a splat the way a scene-menu swap item does
    // (goToSplat: the proximity areas let go, a chain swap waits for the tour to move on), while
    // its camera move, popup and media run as usual. Not in compare mode, which shows two splats
    // side by side, and not in the editor viewport, where a click is often a selection: the
    // editor previews the swap on demand instead.
    const reportedClickSwaps = new Set<string>();
    // The splat a click is loading: a second click (a double-click) doesn't download it again.
    let clickSwapLoadingUrl: string | null = null;
    function runClickSplatSwap(action: SplatSwapAction | undefined): void {
        if (isEditorMode || !action || isCompareModeEnabled) return;
        const swap = resolveClickSplatSwap(action, additionalSplats, getCurrentSplatUrl(), getPrimarySplatUrl(), mainSplatFiles());
        if (!swap) return;
        if (swap.kind === 'missing') {
            if (!reportedClickSwaps.has(swap.url)) {
                reportedClickSwaps.add(swap.url);
                console.warn(`[SplatSwap] Swap on click: ${swap.url} is not one of this scene's splat swaps (deleted?); skipped.`);
            }
            return;
        }
        if (swap.kind === 'original') {
            goToOriginalSplat();
            return;
        }
        if (clickSwapLoadingUrl === swap.url) return;
        const url = swap.url;
        clickSwapLoadingUrl = url;
        goToSplat(url)
            .catch((err) => console.warn('[SplatSwap] Swap on click failed:', err))
            .finally(() => { if (clickSwapLoadingUrl === url) clickSwapLoadingUrl = null; });
    }
    // ── Cutaway actions (see CutawayAction) ──
    // A click's own cutaway outranks where its camera action goes: a hotspot that teleports to
    // stop 5 with "cut at 30%" ends at 30%, whatever the stops it passes or the extra waypoint it
    // flies to say. Held from the click until the tour is no longer animating (checked each frame).
    // The editor viewport runs none of them: it has no Cutaway button to clear a cut, so the
    // editor previews them on demand instead (previewCutaway).
    let cutawayClickHold = false;
    /** The cutaway of a click (hover-in for hover activation): hotspots, models, menu items. */
    function runClickCutaway(action: CutawayAction | undefined): void {
        if (isEditorMode || !action) return;
        if (runCutawayAction(action)) cutawayClickHold = true;
    }
    /** The cutaway of a view the camera arrives at: tour stops and extra waypoints. */
    function runArrivalCutaway(action: CutawayAction | undefined): void {
        if (isEditorMode || !action || cutawayClickHold) return;
        runCutawayAction(action);
    }
    // Tour stops cut the scene as the tour arrives. The stop the tour opens on counts once the
    // scene is presented (the splat is measured then); the early synthetic waypointChange doesn't.
    let tourCutawayPresented = false;
    if (!isEditorMode) {
        events.on('waypointChange', ({ index }: { index: number }) => {
            if (!tourCutawayPresented || currentCameraMode !== 'tour') return;
            runArrivalCutaway(config.waypoints?.[index]?.cutaway);
        });
        app.on('update', () => {
            if (cutawayClickHold && !isAnimatingProgress) cutawayClickHold = false;
            if (tourCutawayPresented || !initialPlaybackGate.isPresentationReady()) return;
            tourCutawayPresented = true;
            if (currentCameraMode === 'tour') runArrivalCutaway(config.waypoints?.[currentWaypointIndex]?.cutaway);
        });
    }
    /**
     * Hide or show the hotspots of hidden folders (directory tick boxes, setHotspotFolderVisible):
     * a hidden one is switched off with its leader line and can't be clicked; a shown one gets back
     * the visibility its own settings give it. An open popup of a hidden hotspot closes.
     */
    function applyHotspotFolderVisibility(): void {
        let changed = false;
        for (const entity of hotspotEntities) {
            const id = entity.hotspotData?.id;
            const hidden = !!id && directoryState.isHotspotHidden(id);
            if ((entity.folderHidden === true) === hidden) continue;
            entity.folderHidden = hidden || undefined;
            entity.enabled = !hiddenByOverride(entity) && baseEntityEnabled(entity, 'hotspot');
            syncLeaderLineVisibility(entity);
            changed = true;
        }
        const popupEl = container.querySelector('.storysplat-hotspot-popup.visible') as HTMLElement | null;
        const openId = popupEl?.dataset.hotspotId;
        if (openId && directoryState.isHotspotHidden(openId)) {
            popupEl!.classList.remove('visible');
            (container.querySelector('.storysplat-hotspot-overlay') as HTMLElement | null)?.classList.remove('visible');
            stopHotspotPopupMedia(container);
        }
        if (changed) renderScheduler.requestFrame('hotspot-folders');
    }
    directoryState.onChange((changes) => {
        applyHotspotFolderVisibility();
        const visibleIds = directoryState.getVisibleFolderIds();
        for (const change of changes) events.emit('hotspotFolderVisibilityChange', { id: change.id, visible: change.visible, visibleIds });
    });
    // Listen for progress updates to update hotspot visibility
    events.on('progressUpdate', () => {
        updateHotspotVisibility();
    });
    // Also call initially after hotspots are created
    setTimeout(() => {
        updateHotspotVisibility();
    }, 100);
    // =====================================================
    // PORTAL CREATION AND RENDERING
    // =====================================================
    // Create a single portal entity with full rendering (extracted for editor reuse)
    /**
     * The colour sphere's look (Sphere marker and the no-media fallback sphere): the colour as
     * diffuse, half of it as emissive glow. Shared by creation and the editor's live colour edit
     * (updatePortal), so both draw the same sphere. The caller calls material.update().
     */
    function setPortalSphereColor(material: pc.StandardMaterial, color: string | undefined): void {
        const parsed = parseColor(color || '#9C27B0'); // Purple default for portals
        material.diffuse = parsed;
        material.emissive = parsed.clone().mulScalar(0.5);
    }
    function createSinglePortal(portal: PortalConfigLike, index: number): pc.Entity {
        const entity = new pc.Entity(`portal-${index}`);
        // Released when the entity is destroyed (editor removal / rebuild, viewer teardown).
        const resources = ownMarkerResources(entity as ViewerRuntimeEntity);
        // Get position (negate Z for BabylonJS -> PlayCanvas coordinate conversion)
        const pos = playCanvasPositionFromSceneVec3(portal.position);
        entity.setPosition(pos.x, pos.y, pos.z);
        // Get scale
        const rawPortalScale = portal.scale || { _x: 1, _y: 1, _z: 1 };
        const portalScaleObj = typeof rawPortalScale === 'number' ? { x: rawPortalScale, y: rawPortalScale, z: rawPortalScale } as LegacyVec3 : rawPortalScale;
        const sx = Math.abs(portalScaleObj._x ?? portalScaleObj.x ?? 1);
        const sy = Math.abs(portalScaleObj._y ?? portalScaleObj.y ?? 1);
        const sz = portalScaleObj._z ?? portalScaleObj.z ?? 1;
        // Get rotation in radians (BabylonJS stores Euler angles in radians, YXZ order)
        const rot = portal.rotation || { _x: 0, _y: 0, _z: 0 };
        const rotXRad = rot._x ?? rot.x ?? 0;
        const rotYRad = rot._y ?? rot.y ?? 0;
        const rotZRad = rot._z ?? rot.z ?? 0;
        // Convert BJS rotation to PC quaternion (handles YXZ→RH conversion properly)
        entity.setRotation(bjsRotationToPC(rotXRad, rotYRad, rotZRad));
        // Stacked marker layers the viewer can draw (none → the default sphere below)
        const stackLayers = portal.type === 'stack' ? sanitizeMarkerStack(portal.markerStack) : [];
        // Create geometry based on type (similar to hotspots)
        if (portal.type === 'sphere') {
            entity.addComponent('render', {
                type: 'sphere',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const material = new pc.StandardMaterial();
            setPortalSphereColor(material, portal.color);
            // Opaque, depth-writing base. Portals default to fully opaque (matching the editor); a saved
            // opacity below 1 is applied as splat-depth-aware translucency so the sphere composites
            // correctly with the splats on both sides.
            material.opacity = 1;
            material.blendType = pc.BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
            applyEntityOpacity(material, snapLegacySphereOpacity(portal.opacity ?? 1));
            entity.render!.material = material;
            (entity as ViewerRuntimeEntity).portalMaterial = material;
            resources.materials.push(material);
            entity.setLocalScale(sx * 0.2, sy * 0.2, sz * 0.2);
        }
        else if (portal.type === 'image' && portal.imageUrl) {
            entity.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const useLighting = portal.useLighting === true;
            const initialOpacity = portal.opacity ?? 1;
            (entity as ViewerRuntimeEntity).textureLoaded = false;
            (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = true;
            entity.enabled = false;
            const material = createImageMaterial(portal.imageUrl, useLighting, initialOpacity, () => {
                // A portal removed while its image was loading still owns the new texture.
                if (resources.released) {
                    destroyMaterialTextures(material);
                    return;
                }
                (entity as ViewerRuntimeEntity).textureLoaded = true;
                if ((!(entity as ViewerRuntimeEntity).visibilityRange || (entity as ViewerRuntimeEntity).shouldBeVisible) && !hiddenByOverride(entity as ViewerRuntimeEntity)) {
                    entity.enabled = true;
                }
                (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = false;
                syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
            });
            entity.render!.material = material;
            resources.materials.push(material);
            entity.setLocalScale(sx, sz, sy);
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            (entity as ViewerRuntimeEntity).portalMaterial = material;
            console.log(`[Portal] Created image portal: ${portal.title || portal.targetSceneName || 'Untitled'}`);
        }
        else if (portal.type === 'video' && portal.videoUrl) {
            // Video portal: muted, looping, autoplaying video plane. Shares the video plane media
            // with video hotspots and stack layers, so a transparent WebM shows its alpha here too.
            entity.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const media = createVideoPlaneMedia({
                videoUrl: portal.videoUrl,
                videoName: portal.videoName,
                webmHasAlpha: portal.webmHasAlpha,
                videoLoop: true,
                videoMuted: true,
                mediaTriggerMode: 'autoplay',
            }, entity, {
                logName: portal.title || portal.targetSceneName,
                uploadLabel: `portal:${portal.id || portal.title || index}`,
                opacity: portal.opacity ?? 1,
            });
            const video = media.video;
            const material = media.material;
            entity.render!.material = material;
            resources.materials.push(material);
            resources.textures.push(...media.textures);
            resources.videos.push(video);
            entity.setLocalScale(sx, sz, sy);
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            // Start playing when visible
            video.play().catch(e => console.log('[Portal] Video autoplay blocked:', e));
            (entity as ViewerRuntimeEntity).videoElement = video;
            (entity as ViewerRuntimeEntity).portalMaterial = material;
            console.log(`[Portal] Created video portal: ${portal.title || portal.targetSceneName || 'Untitled'}`);
        }
        else if (portal.type === 'gif' && portal.gifUrl) {
            // GIF portal - use AnimatedGifTexture if available, otherwise static image
            entity.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const useLighting = portal.useLighting === true;
            const initialOpacity = portal.opacity ?? 1;
            // Try to use animated GIF texture, fallback to static image
            const material = createImageMaterial(portal.gifUrl, useLighting, initialOpacity, () => {
                if (resources.released) {
                    destroyMaterialTextures(material);
                    return;
                }
                (entity as ViewerRuntimeEntity).textureLoaded = true;
                if ((!(entity as ViewerRuntimeEntity).visibilityRange || (entity as ViewerRuntimeEntity).shouldBeVisible) && !hiddenByOverride(entity as ViewerRuntimeEntity)) {
                    entity.enabled = true;
                }
                (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = false;
                syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
            });
            entity.render!.material = material;
            resources.materials.push(material);
            entity.setLocalScale(sx, sz, sy);
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            (entity as ViewerRuntimeEntity).textureLoaded = false;
            (entity as ViewerRuntimeEntity).hiddenUntilTextureLoaded = true;
            entity.enabled = false;
            (entity as ViewerRuntimeEntity).portalMaterial = material;
            console.log(`[Portal] Created GIF portal: ${portal.title || portal.targetSceneName || 'Untitled'}`);
        }
        else if (portal.type === 'plane') {
            entity.addComponent('render', {
                type: 'plane',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            if (isEditorMode) {
                // Opaque colored plane visible only in editor when selected
                const material = new pc.StandardMaterial();
                material.diffuse = new pc.Color(0.0, 0.8, 0.8);
                material.emissive = new pc.Color(0.0, 0.4, 0.4);
                material.opacity = 1;
                material.useLighting = false;
                material.cull = pc.CULLFACE_NONE;
                material.update();
                entity.render!.material = material;
                resources.materials.push(material);
                // Start hidden — shown when portal is selected for editing
                entity.render!.enabled = false;
            } else {
                // In viewer: no visible mesh for the collision plane itself
                entity.render!.enabled = false;
                // Create a popup child entity that fades in when player approaches
                const popupLabel = portal.title || portal.targetSceneName || '';
                if (popupLabel) {
                    const popupEntity = new pc.Entity('portal-popup');
                    popupEntity.addComponent('render', {
                        type: 'plane',
                        castShadows: false,
                        receiveShadows: false,
                        layers: [hotspotMeshLayer.id]
                    });
                    // Create canvas texture with gradient background and white text
                    const canvasWidth = 512;
                    const canvasHeight = 128;
                    const cvs = document.createElement('canvas');
                    cvs.width = canvasWidth;
                    cvs.height = canvasHeight;
                    const ctx = cvs.getContext('2d')!;
                    // Gradient background: transparent edges, dark center
                    const grad = ctx.createLinearGradient(0, 0, canvasWidth, 0);
                    grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
                    grad.addColorStop(0.15, 'rgba(0, 0, 0, 0.6)');
                    grad.addColorStop(0.5, 'rgba(0, 0, 0, 0.75)');
                    grad.addColorStop(0.85, 'rgba(0, 0, 0, 0.6)');
                    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
                    ctx.fillStyle = grad;
                    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
                    // Clean white text centered
                    ctx.fillStyle = '#ffffff';
                    ctx.font = '300 36px "Helvetica Neue", Helvetica, Arial, sans-serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(popupLabel, canvasWidth / 2, canvasHeight / 2);
                    const popupTexture = new pc.Texture(app.graphicsDevice, {
                        width: canvasWidth,
                        height: canvasHeight,
                        format: pc.PIXELFORMAT_RGBA8,
                        mipmaps: false,
                        minFilter: pc.FILTER_LINEAR,
                        magFilter: pc.FILTER_LINEAR,
                        addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                        addressV: pc.ADDRESS_CLAMP_TO_EDGE
                    });
                    popupTexture.setSource(cvs);
                    const popupMaterial = new pc.StandardMaterial();
                    popupMaterial.diffuse = new pc.Color(0, 0, 0);
                    popupMaterial.emissive = new pc.Color(1, 1, 1);
                    popupMaterial.emissiveMap = popupTexture;
                    popupMaterial.opacityMap = popupTexture;
                    popupMaterial.opacityMapChannel = 'a';
                    popupMaterial.useLighting = false;
                    popupMaterial.blendType = pc.BLEND_NORMAL;
                    popupMaterial.depthTest = true;
                    popupMaterial.depthWrite = false;
                    popupMaterial.cull = pc.CULLFACE_NONE;
                    popupMaterial.opacity = 0;
                    popupMaterial.update();
                    popupEntity.render!.material = popupMaterial;
                    resources.materials.push(popupMaterial);
                    // Position popup above the portal plane center, in local space
                    // Plane normal is local Y, so offset along Y to float above
                    popupEntity.setLocalPosition(0, 0.3, 0);
                    // Scale: wider than tall (4:1 aspect), relative size ~60% of portal width
                    popupEntity.setLocalScale(0.6, 1, 0.15);
                    // Billboard the popup to always face the camera
                    const popupBillboardFn = () => {
                        if (popupEntity.enabled && (entity as ViewerRuntimeEntity).portalPopupOpacity! > 0) {
                            popupEntity.lookAt(camera.getPosition());
                            popupEntity.rotateLocal(90, 180, 0);
                        }
                    };
                    app.on('update', popupBillboardFn);
                    popupEntity.once('destroy', () => {
                        app.off('update', popupBillboardFn);
                    });
                    entity.addChild(popupEntity);
                    // Suppress the 3D gradient popup mesh — it looked janky. The DOM-pinned pill
                    // (setPortalEnterLabel) is the only cue now; the entity is kept but DISABLED so its
                    // position still anchors the pill and its opacity signal still gates pill proximity.
                    popupEntity.enabled = false;
                    (entity as ViewerRuntimeEntity).portalPopupEntity = popupEntity;
                    (entity as ViewerRuntimeEntity).portalPopupMaterial = popupMaterial;
                    (entity as ViewerRuntimeEntity).portalPopupOpacity = 0;
                }
            }
            entity.setLocalScale(sx, sz, sy);
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            console.log(`[Portal] Created plane portal: ${portal.title || portal.targetSceneName || 'Untitled'}`);
        }
        else if (portal.type === 'panorama') {
            // 360° panorama portal — render as a small globe/sphere with distinct styling
            entity.addComponent('render', {
                type: 'sphere',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const material = new pc.StandardMaterial();
            // If panoramaUrl is set, load it as emissive texture on the sphere preview.
            // Also stash the decoded image in panoramaImageCache so activation
            // can skip the network + decode round-trip.
            if (portal.panoramaUrl) {
                material.diffuse = new pc.Color(0, 0, 0);
                material.emissive = new pc.Color(1, 1, 1);
                material.emissiveIntensity = 1;
                preloadPanoramaImage(portal.panoramaUrl).then((panoImg) => {
                    if (isDestroyed || resources.released) return;
                    const tex = new pc.Texture(app.graphicsDevice, {
                        width: panoImg.width,
                        height: panoImg.height,
                        format: pc.PIXELFORMAT_RGBA8,
                        mipmaps: true,
                        minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
                        magFilter: pc.FILTER_LINEAR,
                        addressU: pc.ADDRESS_REPEAT,
                        addressV: pc.ADDRESS_CLAMP_TO_EDGE
                    });
                    tex.setSource(panoImg);
                    material.emissiveMap = tex;
                    material.update();
                    (entity as ViewerRuntimeEntity).textureLoaded = true;
                    renderScheduler.requestFrame('panorama-preview-loaded');
                }).catch(() => {
                    // preview silently falls back to emissive white sphere
                });
            } else {
                // Fallback: distinctive globe color
                material.diffuse = new pc.Color(0.15, 0.55, 0.85);
                material.emissive = new pc.Color(0.1, 0.35, 0.55);
            }
            material.opacity = 1;
            material.blendType = pc.BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
            applyEntityOpacity(material, snapLegacySphereOpacity(portal.opacity ?? 1));
            entity.render!.material = material;
            (entity as ViewerRuntimeEntity).portalMaterial = material;
            resources.materials.push(material);
            entity.setLocalScale(sx * 0.25, sy * 0.25, sz * 0.25);
        }
        else if (stackLayers.length > 0) {
            // Stacked marker (same runtime as hotspot stacks). Its video layer plays like a video
            // portal: autoplay, muted, looping.
            const runtime = entity as ViewerRuntimeEntity;
            const stack = buildMarkerStack(runtime, stackLayers, {
                spacing: portal.markerStackSpacing,
                useLighting: portal.useLighting === true,
                opacity: portal.opacity ?? 1,
                label: `portal:${portal.id || portal.title || index}`,
                logName: portal.title || portal.targetSceneName,
                video: { videoLoop: true, videoMuted: true, mediaTriggerMode: 'autoplay' },
                onLayout: () => {
                    if (!runtime.leaderLineEntity) return;
                    const current = (runtime.portalData ?? portal) as PortalConfigLike;
                    rebuildLeaderLine(runtime, current, portalLeaderLineShape(current));
                },
            });
            entity.setLocalScale(sx, sz, sy);
            applyPlaneRotation(entity, rotXRad, rotYRad, rotZRad);
            const videoLayer = stack.layers.find((rt) => rt.video);
            if (videoLayer?.video) {
                runtime.videoElement = videoLayer.video;
                runtime.alphaVideoElement = videoLayer.alphaVideo ?? null;
                videoLayer.video.play().catch(e => console.log('[Portal] Video autoplay blocked:', e));
            }
            console.log(`[Portal] Created stacked portal: ${portal.title || portal.targetSceneName || 'Untitled'}, layers=${stackLayers.length}`);
        }
        else {
            // Default: sphere fallback
            entity.addComponent('render', {
                type: 'sphere',
                castShadows: false,
                receiveShadows: false,
                layers: [hotspotMeshLayer.id]
            });
            const material = new pc.StandardMaterial();
            setPortalSphereColor(material, portal.color);
            material.opacity = 1;
            material.blendType = pc.BLEND_NONE;
            material.depthTest = true;
            material.depthWrite = true;
            material.update();
            applyEntityOpacity(material, portal.opacity ?? 1);
            entity.render!.material = material;
            (entity as ViewerRuntimeEntity).portalMaterial = material;
            resources.materials.push(material);
            entity.setLocalScale(sx * 0.2, sy * 0.2, sz * 0.2);
        }
        // Add collision for raycasting (skip for invisible plane — not clickable)
        if (portal.type !== 'plane') {
            entity.addComponent('collision', {
                type: portal.type === 'sphere' ? 'sphere' : 'box',
                radius: 0.1,
                halfExtents: new pc.Vec3(0.5, 0.5, 0.05)
            });
        }
        // Store portal data for click handling
        (entity as ViewerRuntimeEntity).portalData = portal as PortalData;
        // Billboard mode
        if (portal.billboard) {
            // Use CULLFACE_BACK (normal) — we rotate 180° on Y so the front face points at camera
            const mat = entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined;
            if (mat) {
                mat.cull = pc.CULLFACE_BACK;
                mat.update();
            }
            setMarkerStackCull(entity as ViewerRuntimeEntity, pc.CULLFACE_BACK);
            const billboardPose = new BillboardPoseTracker();
            const billboardUpdateFn = () => {
                if (entity.enabled) {
                    if (billboardPose.update(entity, camera.getPosition())) renderScheduler.requestFrame('portal-billboard');
                } else {
                    billboardPose.reset();
                }
            };
            app.on('update', billboardUpdateFn);
            // Clean up handler when entity is destroyed (prevents handler accumulation in editor)
            entity.once('destroy', () => {
                app.off('update', billboardUpdateFn);
            });
        }
        // Visibility range
        if (portal.visibilityRange) {
            (entity as ViewerRuntimeEntity).visibilityRange = portal.visibilityRange;
            entity.enabled = false;
        }
        sceneContent.addChild(entity);
        // Force immediate billboard orientation now that entity is in scene hierarchy
        if (portal.billboard) {
            entity.lookAt(camera.getPosition());
            entity.rotateLocal(90, 180, 0);
        }
        // Leader line — drawn from portal world position to configured target
        // (none for invisible collision planes; see portalLeaderLineShape)
        const portalLeaderLine = buildLeaderLineEntity(portal, entity.getPosition().clone(), portalLeaderLineShape(portal), markerStackLeaderEdge(entity as ViewerRuntimeEntity));
        if (portalLeaderLine) {
            sceneContent.addChild(portalLeaderLine);
            (entity as ViewerRuntimeEntity).leaderLineEntity = portalLeaderLine;
            syncLeaderLineVisibility(entity as ViewerRuntimeEntity);
        }
        portalEntities.push(entity);
        console.log(`[StorySplat Viewer] Created portal: ${portal.title || portal.targetSceneName || 'Untitled'} -> ${portal.targetSceneId}`);
        return entity;
    }
    // Create all portals (similar to hotspots but for scene-to-scene navigation)
    function createPortals(): void {
        if (!config.portals || config.portals.length === 0) {
            console.log('[StorySplat Viewer] No portals to create');
            return;
        }
        // Filter out menu-only portals (no 3D presence)
        const renderablePortals = config.portals.filter(p => !p.menuOnly);
        console.log(`[StorySplat Viewer] Creating ${renderablePortals.length} portals (${config.portals.length - renderablePortals.length} menu-only)...`);
        renderablePortals.forEach((portal, index: number) => {
            createSinglePortal(portal, index);
        });
        // Kick off panorama image preload for every panorama portal — including
        // menu-only ones whose 3D preview never runs. preloadPanoramaImage dedupes
        // against the cache, so renderable portals don't double-fetch.
        for (const portal of config.portals) {
            if (portal.type === 'panorama' && portal.panoramaUrl) {
                preloadPanoramaImage(portal.panoramaUrl).catch(() => { /* logged on activation */ });
            }
        }
    }
    // Update portal visibility based on scroll progress (tour mode)
    function updatePortalVisibility(): void {
        const scrollPercent = currentProgress * 100;
        const waypointIndex = getWaypointIndexForProgress(currentProgress);
        portalEntities.forEach((entity) => {
            const portal = entity.portalData;
            if (!portal)
                return;
            // Determine if portal should be visible based on visibility range
            let shouldBeVisible = true;
            const range = entity.visibilityRange;
            if (range) {
                if (range.type === 'waypoint') {
                    shouldBeVisible = isInVisibilityRange(waypointIndex, range.start, range.end);
                }
                else if (range.type === 'percentage') {
                    shouldBeVisible = isInVisibilityRange(scrollPercent, range.start, range.end);
                }
            }
            entity.shouldBeVisible = shouldBeVisible;
            // Enable/disable based on visibility and texture loaded state
            if (entity.hiddenUntilTextureLoaded) {
                entity.enabled = false;
            }
            else {
                entity.enabled = shouldBeVisible && !hiddenByOverride(entity);
            }
            syncLeaderLineVisibility(entity);
        });
    }
    // Reused scratch sphere for testing whether a collision portal's plane is
    // within the camera frustum (avoids per-frame allocation).
    const _portalViewSphere = new pc.BoundingSphere();
    // Scratch for the per-portal collision test (fully reinitialized per portal iteration).
    const _portalInvRot = new pc.Quat();
    const _portalLocalPos = new pc.Vec3();
    // Check proximity and collision activation every frame (works in both tour and explore mode)
    function checkPortalSpatialActivation(): void {
        // Once a portal has triggered navigation, stop re-evaluating proximity so
        // the gradient popup / reticle cue stay cleared through the transfer.
        if (portalNavigationPending) return;
        const cameraPos = camera.getPosition();
        // In walk mode, treat the player as a vertical capsule (head → feet) instead
        // of a single point. The camera sits at the head, so a point check misses
        // plane portals authored at floor level — the typical placement for
        // doorway-style triggers. We sample 5 points along the body so portals at
        // any height (floor, chest, ceiling) trigger when walked through.
        const walkSampleCount = (currentCameraMode === 'walk' && characterController) ? 5 : 1;
        const playerHeight = characterController ? characterController.playerHeight : 0;
        // Track whether the player is near an invisible/collision portal so we can
        // recolor the reticle and show an "enter" label (the portal has no mesh).
        let nearCollisionPortal: { color: pc.Color; label: string; pos: pc.Vec3 } | null = null;
        portalEntities.forEach((entity) => {
            const portal = entity.portalData;
            if (!portal)
                return;
            const shouldBeVisible = entity.shouldBeVisible !== false;
            // Proximity activation check
            if (portal.activationMode === 'proximity' && shouldBeVisible) {
                const portalPos = entity.getPosition();
                const distance = cameraPos.distance(portalPos);
                const proximityDistance = portal.proximityDistance || 2;
                if (distance <= proximityDistance && !entity.proximityTriggered) {
                    entity.proximityTriggered = true;
                    console.log(`[Portal] Proximity triggered: ${portal.title || portal.targetSceneName}, navigating to scene ${portal.targetSceneId}`);
                    handlePortalNavigation(portal);
                }
                else if (distance > proximityDistance) {
                    entity.proximityTriggered = false;
                }
            }
            // Collision activation check (invisible plane portals)
            if (portal.activationMode === 'collision' && shouldBeVisible) {
                const portalPos = entity.getPosition();
                const portalScale = entity.getLocalScale();
                const portalRot = entity.getRotation();
                // Transform camera position into portal's local space
                const invRot = _portalInvRot.copy(portalRot).invert();
                // Plane bounds check (plane primitive is on XZ in local space). A stacked marker
                // covers its layout bounds (centred on the pivot vertically) instead of the unit plane.
                const stackBounds = entity.markerStack?.layout.bounds;
                const halfWidth = stackBounds
                    ? Math.abs(portalScale.x) * Math.max(Math.abs(stackBounds.minX), Math.abs(stackBounds.maxX))
                    : Math.abs(portalScale.x) / 2;
                const halfHeight = stackBounds
                    ? Math.abs(portalScale.z) * stackBounds.maxY
                    : Math.abs(portalScale.z) / 2;
                const collisionRadius = 0.5;
                // Sample player capsule (or single camera point in fly/orbit/tour).
                const localPos = _portalLocalPos;
                let touching = false;
                for (let i = 0; i < walkSampleCount; i++) {
                    const t = walkSampleCount === 1 ? 0 : i / (walkSampleCount - 1);
                    localPos.set(
                        cameraPos.x - portalPos.x,
                        (cameraPos.y - t * playerHeight) - portalPos.y,
                        cameraPos.z - portalPos.z
                    );
                    invRot.transformVector(localPos, localPos);
                    if (Math.abs(localPos.x) <= halfWidth + collisionRadius &&
                        Math.abs(localPos.z) <= halfHeight + collisionRadius &&
                        Math.abs(localPos.y) <= collisionRadius) {
                        touching = true;
                        break;
                    }
                }
                // Fade popup in/out based on distance to portal plane
                if (entity.portalPopupMaterial && entity.portalPopupEntity) {
                    const dist = cameraPos.distance(portalPos);
                    // Fade range: fully visible at fadeInEnd, invisible at fadeInStart
                    const portalSize = Math.max(Math.abs(portalScale.x), Math.abs(portalScale.z));
                    const fadeInStart = portalSize * 1.5 + 3;  // start fading in at this distance
                    const fadeInEnd = portalSize * 0.5 + 0.8;  // fully visible at this distance
                    // Only surface the popup when the user is actually looking toward the
                    // portal. An invisible (collision) portal has no mesh, so without this
                    // the title text would appear whenever the camera is near it — even
                    // when facing away. Test the portal plane's bounding sphere against the
                    // camera frustum (handles arbitrary plane orientation, unlike a plain
                    // forward dot-product). Sphere radius covers the plane's largest extent.
                    let inView = false;
                    if (camera.camera) {
                        _portalViewSphere.center.copy(portalPos);
                        _portalViewSphere.radius = Math.max(portalSize * 0.5, collisionRadius);
                        inView = camera.camera.frustum.containsSphere(_portalViewSphere) > 0;
                    }
                    let targetOpacity = 0;
                    if (inView) {
                        if (dist <= fadeInEnd) {
                            targetOpacity = 1;
                        } else if (dist < fadeInStart) {
                            targetOpacity = 1 - (dist - fadeInEnd) / (fadeInStart - fadeInEnd);
                        }
                    }
                    // Smooth lerp toward target; snap when within epsilon so it terminates and the
                    // material stops being dirtied every frame at steady state. The guard covers
                    // ONLY the material/enabled writes — the nearCollisionPortal block below must
                    // keep running each frame (it re-arms the reticle color + Enter label).
                    const currentOpacity = entity.portalPopupOpacity || 0;
                    const lerpSpeed = 0.08;
                    let newOpacity = currentOpacity + (targetOpacity - currentOpacity) * lerpSpeed;
                    if (Math.abs(targetOpacity - newOpacity) < 0.002) newOpacity = targetOpacity;
                    if (newOpacity !== currentOpacity) {
                        // Keep the opacity signal (drives the DOM pill's proximity fade + the
                        // nearCollisionPortal gate below) but never render the 3D gradient mesh — it's
                        // suppressed. No material write/update needed since the mesh stays disabled.
                        entity.portalPopupOpacity = newOpacity;
                        entity.portalPopupEntity.enabled = false;
                    }
                    // Once the popup is meaningfully visible, flag this portal as the
                    // nearest walkable one so the reticle/label can signal it.
                    if (newOpacity > 0.3 && !nearCollisionPortal) {
                        nearCollisionPortal = {
                            color: parseColor(portal.color || '#00E5FF'),
                            label: portal.title || portal.targetSceneName || 'Enter',
                            // Anchor the pill to the portal's 3D popup position (floats above the plane).
                            pos: entity.portalPopupEntity.getPosition().clone(),
                        };
                    }
                }
                if (touching && !entity.proximityTriggered) {
                    entity.proximityTriggered = true;
                    console.log(`[Portal] Collision triggered: ${portal.title || portal.targetSceneName}, navigating to scene ${portal.targetSceneId}`);
                    handlePortalNavigation(portal);
                } else if (!touching) {
                    entity.proximityTriggered = false;
                }
            }
        });
        // Reflect proximity to a walkable invisible portal on the reticle + the 3D-pinned pill.
        // Guard with !portalNavigationPending: when a portal triggers, handlePortalNavigation runs
        // clearPortalProximityCues() mid-loop (sets pending + clears the label) — without this guard
        // the label is re-shown here on the same frame and then sticks (the per-frame early-return
        // freezes it). That was the "preview doesn't clear when you go through" bug.
        if (nearCollisionPortal && !portalNavigationPending) {
            // Casts: nearCollisionPortal is assigned inside the forEach callback above, which TS's
            // flow analysis can't see (it narrows to never here) — same reason the original cast.
            reticlePortalColor = (nearCollisionPortal as { color: pc.Color }).color;
            setPortalEnterLabel(
                (nearCollisionPortal as { label: string }).label,
                (nearCollisionPortal as { pos: pc.Vec3 }).pos,
            );
        } else {
            reticlePortalColor = null;
            setPortalEnterLabel(null);
        }
    }
    // Run spatial checks every frame so they work in explore mode (not just tour scroll)
    app.on('update', checkPortalSpatialActivation);
    // Listen for progress updates to update portal visibility ranges
    events.on('progressUpdate', () => {
        updatePortalVisibility();
    });
    // Also call initially after portals are created
    setTimeout(() => {
        updatePortalVisibility();
    }, 100);
    // Raycast helper for portal detection
    // Shared scratch for the hover/click raycasts (raycastPortals + raycastHotspots): both run
    // per-mousemove; the ray is loop-invariant and was recomputed + allocated per entity.
    // _hoverRayDir is never mutated in-loop; per-entity math runs in the two tmp scratches.
    const _hoverRayFrom = new pc.Vec3();
    const _hoverRayTo = new pc.Vec3();
    const _hoverRayDir = new pc.Vec3();
    const _hoverRayTmp1 = new pc.Vec3();
    const _hoverRayTmp2 = new pc.Vec3();
    function raycastPortals(x: number, y: number): {
        entity: ViewerRuntimeEntity;
        portal: PortalConfigLike;
    } | null {
        const from = camera.camera!.screenToWorld(x, y, camera.camera!.nearClip, _hoverRayFrom);
        const to = camera.camera!.screenToWorld(x, y, camera.camera!.farClip, _hoverRayTo);
        return raycastPortalsAlong(from, _hoverRayDir.sub2(to, from).normalize());
    }
    /** The clickable portal nearest along a world-space ray (`dir` normalised): a screen click or a VR controller. */
    function raycastPortalsAlong(from: pc.Vec3, dir: pc.Vec3): {
        entity: ViewerRuntimeEntity;
        portal: PortalConfigLike;
    } | null {
        let closestHit: {
            entity: ViewerRuntimeEntity;
            distance: number;
        } | null = null;
        portalEntities.forEach(entity => {
            if (!entity.enabled)
                return;
            // Skip collision-only plane portals — they are not clickable
            if (entity.portalData?.type === 'plane')
                return;
            // Stacked marker: a click on any layer is a click on the portal
            if (entity.markerStack) {
                const t = rayHitMarkerStack(entity, from, dir);
                if (t !== null && (!closestHit || t < closestHit.distance)) {
                    closestHit = { entity, distance: t };
                }
                return;
            }
            const pos = entity.getPosition();
            const toEntity = _hoverRayTmp1.sub2(pos, from);
            const t = toEntity.dot(dir);
            if (t < 0)
                return;
            const closestPoint = _hoverRayTmp2.copy(dir).mulScalar(t).add(from);
            const distance = closestPoint.distance(pos);
            const entityScale = entity.getLocalScale();
            const hitRadius = Math.max(entityScale.x, entityScale.y, 0.3) * 0.6;
            if (distance < hitRadius) {
                if (!closestHit || t < closestHit.distance) {
                    closestHit = { entity, distance: t };
                }
            }
        });
        // TypeScript doesn't track assignments inside forEach callbacks
        const portalHitResult = closestHit as { entity: ViewerRuntimeEntity; distance: number } | null;
        if (portalHitResult !== null) {
            const entity = portalHitResult.entity;
            return { entity, portal: entity.portalData! as PortalConfigLike };
        }
        return null;
    }
    // Handle portal navigation (scene-to-scene) or panorama activation
    // Immediately clear any walkable-portal proximity cues (floating gradient/title
    // popup, recolored reticle, "enter" label). Navigation has several exit paths
    // (external handler, panorama, in-place swap) that otherwise leave the popup
    // gradient stuck on screen after the transfer.
    function clearPortalProximityCues(): void {
        portalNavigationPending = true;
        reticlePortalColor = null;
        setPortalEnterLabel(null);
        portalEntities.forEach((entity) => {
            if (entity.portalPopupMaterial && entity.portalPopupEntity) {
                entity.portalPopupOpacity = 0;
                entity.portalPopupMaterial.opacity = 0;
                entity.portalPopupMaterial.update();
                entity.portalPopupEntity.enabled = false;
            }
        });
    }
    // Editor-only: portals must not navigate (it would tear down the edit session) — flash a small
    // popup telling the creator where the portal points instead. See the isEditorMode gate below.
    function showEditorPortalPopup(portal: PortalConfigLike): void {
        const target = portal.targetSceneName || portal.title || portal.targetSceneId || 'another scene';
        container.querySelector('.storysplat-editor-portal-popup')?.remove();
        const el = document.createElement('div');
        el.className = 'storysplat-editor-portal-popup';
        el.style.cssText = 'position:absolute;left:50%;bottom:80px;transform:translateX(-50%);z-index:10000;background:rgba(17,24,20,0.92);color:#F2F0E9;border:1px solid rgba(204,88,51,0.6);border-radius:10px;padding:12px 18px;font:600 14px/1.4 system-ui,sans-serif;box-shadow:0 6px 24px rgba(0,0,0,0.4);pointer-events:none;max-width:80%;text-align:center;';
        const line1 = document.createElement('div');
        line1.textContent = `🚪 Portal → ${String(target)}`;
        const line2 = document.createElement('div');
        line2.textContent = 'Navigation is disabled while editing — this teleports in the published scene.';
        line2.style.cssText = 'opacity:0.7;font-weight:400;font-size:12px;margin-top:4px;';
        el.append(line1, line2);
        container.appendChild(el);
        setTimeout(() => el.remove(), 3500);
    }

    async function handlePortalNavigation(portal: PortalConfigLike, navOpts?: { fromPopstate?: boolean }): Promise<void> {
        // A portal rebuilds the viewer and all of its controllers. Capture FPV
        // before teardown so the target viewer can create a fresh drone
        // controller and keep the pilot in drone-sim controls.
        const resumeFpvAfterPortal = currentCameraMode === 'fpv';
        // The hop ends a VR session (the viewer is rebuilt): the next scene opens with the large
        // "Enter VR" prompt, so one select takes the visitor back in.
        const promptVrAfterPortal = inVrSession();
        clearPortalProximityCues();
        stopAutoMoveForward();
        // Editor: a scene-nav portal must NOT navigate away (it would tear down the editing session).
        // Show a popup with the target instead; it still teleports normally in the published viewer.
        if (isEditorMode && portal.type !== 'panorama' && !(portal.panoramaUrl && !portal.targetSceneId)) {
            showEditorPortalPopup(portal);
            return;
        }
        // Panorama portal: enter 360° panorama mode instead of navigating.
        // Accept either an explicit type or a panoramaUrl — legacy portals
        // created before the "360°" button existed kept type='sphere'.
        // Only hijack to panorama when there's no scene-nav target: portals
        // carrying BOTH fields are scene-nav portals that happen to have a
        // stale panoramaUrl from a prior experiment.
        if (portal.type === 'panorama' || (portal.panoramaUrl && !portal.targetSceneId)) {
            handlePanoramaPortalActivation(portal);
            return;
        }
        if (!portal.targetSceneId) {
            console.warn('[Portal] No target scene ID specified');
            return;
        }
        console.log(`[StorySplat Viewer] Portal activated: navigating to scene ${portal.targetSceneId}`);
        // Mark the scene we're leaving as "visited" this session (module-level; survives in-page
        // portal navigation, resets on refresh) so menus show a subtle been-here cue.
        markSceneVisited(scene.sceneId || options.analytics?.sceneId);
        // Check for external handlers BEFORE emitting (emit may trigger destroy)
        const hasExternalPortalHandler = events.listenerCount('portalActivated') > 0;
        // Emit event for external handlers (self-hosted exports, custom embeddings).
        // Wrap in try/catch: an external handler may synchronously destroy the viewer,
        // and we must not let any thrown error bubble up as a "Failed to load scene" popup.
        try {
            events.emit('portalActivated', {
                portalId: portal.id,
                targetSceneId: portal.targetSceneId,
                targetSceneName: portal.targetSceneName
            });
        } catch (emitErr) {
            console.warn('[Portal] External portalActivated handler threw:', emitErr);
        }
        // If external code has registered a portalActivated handler, let it
        // manage navigation. This is critical for self-hosted exports where
        // scene data is loaded from local files rather than the StorySplat API.
        if (hasExternalPortalHandler) {
            console.log('[Portal] External portal handler detected, deferring navigation');
            return;
        }
        // Defensive: if the viewer was destroyed during emit (e.g. by a handler
        // registered on a lazy-load wrapper whose listener isn't visible to
        // inner events.listenerCount), skip the internal fallback — its cleanup
        // would crash on the already-destroyed app.
        if (isDestroyed) {
            console.log('[Portal] Viewer destroyed during emit, skipping internal navigation');
            return;
        }
        // No external handler — use built-in API fetch for hosted scenes
        showPortalLoadingUI(container, portal.targetSceneName || portal.title || 'scene');
        try {
            // Fetch the new scene data from StorySplat API
            const sceneApiUrl = `https://discover.storysplat.com/api/scene/${portal.targetSceneId}`;
            console.log(`[Portal] Fetching scene from: ${sceneApiUrl}`);
            const response = await fetch(sceneApiUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch scene: ${response.status} ${response.statusText}`);
            }
            const result = await response.json();
            const newSceneData = result.data || result;
            // The API scene JSON may omit its own id; stamp the canonical id so visited-marking and
            // the synthesized "Current Space" item work on this scene too (not just analytics).
            if (newSceneData && !newSceneData.sceneId) newSceneData.sceneId = portal.targetSceneId;
            // The ambient bed survives this in-place viewer recreation. Carry its scene-source
            // shader settings along the same portal lineage only when the destination omitted
            // audioReactive. An explicit destination config (including enabled:false) wins.
            newSceneData.audioReactive = inheritPortalAudioReactiveConfig(
                newSceneData.audioReactive,
                effectiveAudioReactiveConfig,
                crossSceneAudio.isActive()
            );
            // Portal navigation (embeds / v2-html iframe): decide reload vs in-place.
            if (portalNav) {
                // (A) A full reload is unavoidable ONLY when the target pins a viewer
                // version DIFFERENT from the one this document is already running
                // (portalNav.runtimeVersion) — that's the sole case a bundle swap
                // can't be done in place. A target pinned to the SAME version (a
                // story chain sharing one pin), or a floating runtime (runtimeVersion
                // omitted — local dev / non-pinned embed), swaps in place and keeps
                // the cross-scene audio bed alive. Only the reload ends the bed.
                const targetLock = typeof (newSceneData as { lockedViewerVersion?: unknown })?.lockedViewerVersion === 'string'
                    ? (newSceneData as { lockedViewerVersion: string }).lockedViewerVersion.trim()
                    : '';
                const runtimeVersion = (portalNav.runtimeVersion || '').trim();
                const needsBundleSwap = !!targetLock && !!runtimeVersion && targetLock !== runtimeVersion;
                if (needsBundleSwap && typeof window !== 'undefined') {
                    window.location.assign(portalUrlFor(portal.targetSceneId));
                    return;
                }
                // (D) In-place swap keeps the JS context (and the cross-scene audio
                // module singleton) alive. Update the URL so deep-links + the back
                // button work — but never re-push on a back/forward (popstate)
                // navigation, whose history entry already exists.
                if (!navOpts?.fromPopstate && typeof history !== 'undefined') {
                    try {
                        history.pushState({ storysplatSceneId: portal.targetSceneId }, '', portalUrlFor(portal.targetSceneId));
                    } catch { /* pushState can throw in exotic embeddings — swap anyway */ }
                }
            }
            // The target scene may belong to a DIFFERENT creator (cross-owner portal). Use its real
            // owner (from the API meta) for analytics/bandwidth scoping — inheriting the entry scene's
            // ownerId would scope the meter to the wrong users/{uid}/ subtree (counting 0 bytes) while
            // still suppressing the legacy fallback, leaving cross-owner hops entirely unreported.
            const targetOwnerId: string | undefined = result?.meta?.ownerId || options.analytics?.ownerId;
            const targetAssetLeaseExpiresAtMs: number | undefined = Number.isSafeInteger(result?.meta?.assetLeaseExpiresAtMs)
                ? result.meta.assetLeaseExpiresAtMs
                : undefined;
            // Update loading UI with the actual scene name from the API
            if (newSceneData.name) {
                updatePortalLoadingText(container, newSceneData.name);
            }
            analyticsHandle?.beginPortalTransition();
            // Cleanup current scene (iOS memory management)
            cleanupForPortalNavigation();
            // Small delay for garbage collection (iOS)
            await new Promise(resolve => setTimeout(resolve, 100));
            // Create new viewer with the fetched scene data
            // Note: We're recreating in the same container, so we need to remove the current canvas first
            if (canvas && canvas.parentNode) {
                canvas.remove();
            }
            // Create new viewer (this will replace 'instance' reference for external code)
            // Skip lazy load since user is already in an active viewing session
            const portalViewerOptions: ViewerOptionsWithSessionRecorder = {
                lazyLoad: false,
                ...(options.disableAnalytics === true ? { disableAnalytics: true } : {}),
                ...(options.analytics ? { analytics: { ...options.analytics, sceneId: portal.targetSceneId, ownerId: targetOwnerId ?? options.analytics.ownerId } } : {}),
                ...(options.googleTagId ? { googleTagId: options.googleTagId } : {}),
                ...(analyticsHandle ? { __sessionRecorderHandle: analyticsHandle } : {}),
                ...(gaHandle ? { __googleAnalyticsHandle: gaHandle } : {}),
                ...(resumeFpvAfterPortal ? { __resumeFpvAfterPortal: true } : {}),
                ...(promptVrAfterPortal ? { vr: true } : {}),
                // Carry portal-nav config forward so the next teleport also swaps in
                // place + updates the URL (the recreated viewer re-stamps history).
                ...(portalNav ? { portalNavigation: portalNav } : {}),
                ...(targetOwnerId && targetAssetLeaseExpiresAtMs ? {
                    assetLease: {
                        sceneId: portal.targetSceneId,
                        ownerId: targetOwnerId,
                        expiresAtMs: targetAssetLeaseExpiresAtMs,
                        renewalEndpoint: PROD_ASSET_RENEWAL_ENDPOINT,
                    },
                } : {}),
            };
            const newViewer = await createViewer(container, newSceneData, portalViewerOptions);
            // Re-target scene attribution along with the viewer. The recorder is
            // forwarded through `__sessionRecorderHandle`, so the new viewer skips
            // recorder creation — without passing the destination ids here the
            // freshly-computed `analytics.sceneId` above is discarded and every
            // payload for the rest of the visit is stamped with the ORIGIN scene.
            analyticsHandle?.attachViewer(newViewer, options.analytics
                ? { sceneId: portal.targetSceneId, ownerId: targetOwnerId ?? options.analytics.ownerId }
                : undefined);
            // Same hand-off for GA: the forwarded handle is why the new viewer
            // skips gtag setup, so it has to be re-bound to the new viewer's
            // event bus or GA would stop reporting after the first teleport.
            gaHandle?.attachViewer(newViewer, {
                sceneId: portal.targetSceneId,
                sceneName: newSceneData.name || '',
            });
            // Hide loading UI
            hidePortalLoadingUI(container);
            console.log(`[Portal] Successfully navigated to scene: ${portal.targetSceneId}`);
            // Return the new viewer instance (for programmatic use)
            return;
        }
        catch (error) {
            console.error('[Portal] Navigation failed:', error);
            // Navigation didn't happen — re-enable portal proximity checks so the
            // user can retry instead of being stuck with frozen portals.
            portalNavigationPending = false;
            hidePortalLoadingUI(container);
            // Show error message
            const errorDiv = document.createElement('div');
            errorDiv.className = 'storysplat-portal-error';
            errorDiv.textContent = `Failed to load scene: ${(error as Error).message}`;
            Object.assign(errorDiv.style, {
                // Keep errors contained to the viewer container (important for embeds)
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0,0,0,0.9)',
                color: '#ff6b6b',
                padding: '20px',
                borderRadius: '8px',
                zIndex: '100004',
                fontFamily: 'system-ui, sans-serif'
            });
            container.appendChild(errorDiv);
            setTimeout(() => errorDiv.remove(), 3000);
        }
    }
    // Cleanup for portal navigation (memory management)
    function cleanupForPortalNavigation(): void {
        console.log('[Portal] Cleaning up current scene for navigation...');
        // Set destroyed flag to prevent async operations from completing
        isDestroyed = true;
        if (stereoFormat) stereoCarryOver.set(container, { format: stereoFormat, ownsFullscreen: stereoOwnsFullscreen });
        teardownStereo(true);
        // Hand the popstate listener to the recreated viewer (which registers its
        // own). NOT crossSceneAudio — that module singleton must survive the swap.
        if (portalNav && typeof window !== 'undefined') {
            window.removeEventListener('popstate', onPortalPopstate);
        }
        // Dispose this scene's bandwidth meter (flushes its remainder) before the
        // target scene creates its own. Leaving it registered would leak a global
        // fetch/XHR listener + 60s interval and keep counting the target scene's
        // same-owner bytes under the old sceneId (double-counting the hop).
        if (bandwidthReporter) {
            try { bandwidthReporter.dispose(); } catch { /* cleanup must not block navigation */ }
            bandwidthReporter = null;
        }
        // Stop playback
        pause();
        stop8thWallAR();
        xrTeardown?.();
        // Destroy compare mode (slider DOM + window-level listeners)
        destroyCompareMode();
        // The Layers menu and the visitor directory hold window listeners (resize, outside clicks).
        layersPanel?.destroy();
        layersPanel = null;
        directoryPanel?.destroy();
        directoryPanel = null;
        // Destroy entity animation system (timers + app.on handlers)
        if (entityAnimationSystem) {
            entityAnimationSystem.destroy();
            entityAnimationSystem = null;
        }
        // Stop all HTML audio elements (hotspot audio, etc.)
        allAudioElements.forEach(audio => {
            audio.pause();
            audio.src = '';
        });
        allAudioElements.length = 0;
        storedVolumes.clear();
        // Close all AudioContexts (spatial audio)
        allAudioContexts.forEach(ctx => {
            ctx.close().catch(() => {});
        });
        allAudioContexts.length = 0;
        // Stop all waypoint audio
        waypointAudioMap.forEach((audioData) => {
            const slot = audioData.entity.sound?.slot(audioData.slotId);
            if (slot) {
                slot.stop();
            }
            audioData.entity.destroy();
        });
        waypointAudioMap.clear();
        // Stop all audio emitters
        audioEmitterMap.forEach((emitterData) => {
            const slot = emitterData.entity.sound?.slot(emitterData.slotId);
            if (slot) {
                slot.stop();
            }
            emitterData.entity.destroy();
        });
        audioEmitterMap.clear();
        // Stop all hotspot videos
        hotspotEntities.forEach((entity) => {
            if (entity.videoElement) {
                entity.videoElement.pause();
                entity.videoElement.src = '';
            }
            if (entity.alphaVideoElement) {
                entity.alphaVideoElement.pause();
                entity.alphaVideoElement.src = '';
            }
        });
        // Cleanup animated GIFs
        animatedGifs.forEach(gif => gif.destroy());
        animatedGifs.length = 0;
        // Cleanup custom meshes
        cleanupCustomMeshes();
        // Destroy hotspot entities (and any attached leader lines + materials)
        hotspotEntities.forEach(entity => {
            const line = (entity as ViewerRuntimeEntity).leaderLineEntity;
            if (line) {
                ((line as any)._leaderMat as pc.StandardMaterial | undefined)?.destroy();
                line.destroy();
            }
            entity.destroy();
        });
        hotspotEntities.length = 0;
        // Destroy portal entities (and any attached leader lines + materials)
        portalEntities.forEach(entity => {
            const line = (entity as ViewerRuntimeEntity).leaderLineEntity;
            if (line) {
                ((line as any)._leaderMat as pc.StandardMaterial | undefined)?.destroy();
                line.destroy();
            }
            entity.destroy();
        });
        portalEntities.length = 0;
        // Destroy mirror plane entities (with full resource cleanup)
        mirrorPlaneEntities.slice().forEach(entity => {
            destroySingleMirrorPlane(entity.name);
        });
        mirrorPlaneEntities.length = 0;
        mirrorPrerenderCleanups.clear();
        _mirrorClipShaderApplied = false;
        unwrapMainCameraClipClear();
        // Destroy water plane entities (with full resource cleanup)
        waterSystem.clear();
        editorWaterPlaneMap.clear();
        cloudSystem.clear();
        editorCloudLayerMap.clear();
        // Destroy splat entity
        preloadedSplats.forEach((entity) => {
            const rlScript = (entity.script as any)?.gsplatRelighting as RelightingEffectScript | undefined;
            if (rlScript) allRelightingScripts.delete(rlScript);
            destroyEntitySafely(entity);
        });
        preloadedSplats.clear();
        if (splatEntity) {
            destroyEntitySafely(splatEntity);
            splatEntity = null;
        }
        // Destroy relighting scripts
        relightingScript = null;
        allRelightingScripts.clear();
        relightingEnabled = false;
        // Destroy playground balls and key handler
        stopPlayground();
        window.removeEventListener('keydown', _playgroundKeyHandler);
        // Destroy reticle
        reticleMat.destroy();
        reticleMesh.destroy();
        reticleEntity.destroy();
        canvasEl.removeEventListener('mousemove', reticleMouseMoveHandler);
        // Shadows: restore the engine's directional cull, remove the ground plane
        shadowSystem?.destroy();
        shadowSystem = null;
        // Destroy light entities
        lightEntities.forEach(entity => {
            entity.destroy();
        });
        lightEntities.length = 0;
        // Destroy particle system entities
        particleEntities.forEach((entity) => {
            entity.destroy();
        });
        particleEntities.clear();
        // Tear down weather (entity + GSplatContainer) and detach the per-frame handler
        if (weatherRebuildTimer) {
            clearTimeout(weatherRebuildTimer);
            weatherRebuildTimer = null;
            weatherPendingRebuild = null;
        }
        if (weatherInstance) {
            weatherInstance.destroy();
            weatherInstance = null;
            weatherStructuralKey = null;
        }
        app.off('update', weatherUpdateHandler);
        // Tear down the audio-reactive effect (shader chunk + mic) and detach its handler.
        // The analyser bus context itself stays alive (module scope — element sources are
        // irreversibly routed through it), but the analyser fan-in is released.
        if (audioReactiveInstance) {
            audioReactiveInstance.destroy();
            audioReactiveInstance = null;
        }
        app.off('update', audioReactiveUpdateHandler);
        audioAnalyserBus.releaseViewer();
        // Destroy procedural/cached particle textures
        particleTextures.forEach((texture) => {
            texture.destroy();
        });
        particleTextures.clear();
        // Destroy skybox entity + detach per-camera follow handler
        if (currentSkyboxEntity) {
            currentSkyboxEntity.destroy();
            currentSkyboxEntity = null;
        }
        if (skyboxFollowCameraHandler) {
            app.scene.off('prerender', skyboxFollowCameraHandler);
            skyboxFollowCameraHandler = null;
        }
        // Destroy frame sequence player
        if (frameSequencePlayer) {
            frameSequencePlayer.destroy();
            frameSequencePlayer = null;
        }
        // Cleanup CharacterController
        if (characterController) {
            characterController.destroy();
            characterController = null;
        }
        // Cleanup HTML Mesh Manager
        const portalHtmlMgr = (app as ViewerRuntimeApp).__htmlMeshManager;
        if (portalHtmlMgr) {
            portalHtmlMgr.destroy();
        }
        // Cleanup Custom Script System
        const portalScriptSys = (app as ViewerRuntimeApp).__customScriptSystem;
        if (portalScriptSys) {
            portalScriptSys.dispose();
        }
        // Remove window/document lifecycle listeners
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        teardownOnDemandRendering();
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('pageshow', handlePageShow);
        audioContextsSuspendedForVisibility.clear();
        audioContextVisibilitySuspendPending.clear();
        audioContextVisibilityResumeRequested.clear();
        // Remove UI elements
        clearTourLookHintTimer();
        if (uiElements.preloader) uiElements.preloader.remove();
        if (uiElements.scrollControls) uiElements.scrollControls.remove();
        if (uiElements.fullscreenButton) uiElements.fullscreenButton.remove();
        if (uiElements.helpButton) uiElements.helpButton.remove();
        if (uiElements.helpPanel) uiElements.helpPanel.remove();
        if (uiElements.waypointInfo) uiElements.waypointInfo.remove();
        if (uiElements.watermark) uiElements.watermark.remove();
        if (uiElements.wasdHint) uiElements.wasdHint.remove();
        if (uiElements.orbitHint) uiElements.orbitHint.remove();
        if (uiElements.doubleTapHint) uiElements.doubleTapHint.remove();
        if (uiElements.tourLookHint) uiElements.tourLookHint.remove();
        if (uiElements.lookZone) uiElements.lookZone.remove();
        if (uiElements.joystick) uiElements.joystick.remove();
        if (uiElements.portalPopup) uiElements.portalPopup.remove();
        if (uiElements.muteButton) uiElements.muteButton.remove();
        if (uiElements.microphoneButton) uiElements.microphoneButton.remove();
        if (uiElements.relightingButton) uiElements.relightingButton.remove();
        if (uiElements.waypointListContainer) uiElements.waypointListContainer.remove();
        if (uiElements.sceneMenuContainer) uiElements.sceneMenuContainer.remove();
        if (uiElements.fpsCounter) uiElements.fpsCounter.remove();
        // Release any open hotspot lightbox first: it locks document.body scroll and adds a
        // document keydown listener that are only freed by its own cleanup. Removing the popup
        // node directly would leave the host page permanently unscrollable after scene re-init.
        hideHotspotPopup(container);
        if (uiElements.hotspotPopup) uiElements.hotspotPopup.remove();
        if (uiElements.vrButton) uiElements.vrButton.remove();
        if (uiElements.arButton) uiElements.arButton.remove();
        if (uiElements.modeContainer) uiElements.modeContainer.remove();
        if (uiElements.exploreControls) uiElements.exploreControls.remove();
        // Cleanup measurement DOM elements
        if (_measureCleanupDom) { _measureCleanupDom(); _measureCleanupDom = null; }
        refreshMeasurePanelLabels = null;
        vrMeasurementSource = null;
        vrUi?.destroy();
        vrUi = null;
        if (_measurePicker) { _measurePicker.destroy(); _measurePicker = null; }
        if (_measureKeydownHandler) { document.removeEventListener('keydown', _measureKeydownHandler); _measureKeydownHandler = null; }
        if (_movementKeydownHandler) { document.removeEventListener('keydown', _movementKeydownHandler); _movementKeydownHandler = null; }
            if (panoramaRestoreTimer) {
                clearTimeout(panoramaRestoreTimer);
                panoramaRestoreTimer = null;
            }
            if (panoramaExitButton) {
                const escHandler = (panoramaExitButton as any)._escHandler;
                if (escHandler) document.removeEventListener('keydown', escHandler);
                panoramaExitButton.remove();
                panoramaExitButton = null;
            }
            if (uiElements._cleanup) { uiElements._cleanup(); uiElements._cleanup = undefined; }
        // Remove injected styles
        const styleEl = document.getElementById('storysplat-viewer-styles');
        if (styleEl) styleEl.remove();
        // Remove container class
        container.classList.remove('storysplat-viewer-container');
        // Remove FXAA from PostEffectQueue
        if (fxaaEffect && camera.camera) {
            try { camera.camera.postEffects.removeEffect(fxaaEffect); } catch (_e) { /* already removed */ }
            fxaaEffect = null;
        }
        // Destroy CameraFrame (post-processing)
        if (cameraFrame) {
            cameraFrame.destroy();
            cameraFrame = null;
        }
        // CameraControls owns input sources separate from app.keyboard.
        cameraControls.destroy();
        // Destroy the PlayCanvas app (cleans up all app.on() listeners, remaining entities, assets, render loop)
        app.destroy();
        console.log('[Portal] Cleanup complete');
    }
    // Show portal loading UI
    function showPortalLoadingUI(container: HTMLElement, sceneName: string): void {
        // Remove any existing loading UI
        const existing = container.querySelector('.storysplat-portal-loading');
        if (existing)
            existing.remove();
        // Ensure the overlay is positioned relative to the viewer container
        try {
            const pos = window.getComputedStyle(container).position;
            if (pos === 'static')
                container.style.position = 'relative';
        }
        catch {
            // Best-effort only
        }
        const loadingDiv = document.createElement('div');
        loadingDiv.className = 'storysplat-portal-loading';
        const spinner = document.createElement('div');
        spinner.className = 'storysplat-portal-spinner';
        const text = document.createElement('div');
        text.className = 'storysplat-portal-loading-text';
        text.textContent = getButtonLabel(currentButtonLabels, 'loadingScene').replace('{name}', sceneName);
        loadingDiv.appendChild(spinner);
        loadingDiv.appendChild(text);
        // Styles - use scene's uiColor for the spinner
        Object.assign(loadingDiv.style, {
            position: 'absolute',
            inset: '0',
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '100003',
            fontFamily: 'system-ui, sans-serif'
        });
        Object.assign(spinner.style, {
            width: '50px',
            height: '50px',
            border: '4px solid rgba(255, 255, 255, 0.2)',
            borderTop: `4px solid ${uiColor}`,
            borderRadius: '50%',
            animation: 'storysplat-portal-spin 1s linear infinite',
            marginBottom: '20px'
        });
        Object.assign(text.style, {
            color: '#ffffff',
            fontSize: '18px'
        });
        // Add keyframes for spinner animation
        if (!document.getElementById('storysplat-portal-styles')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'storysplat-portal-styles';
            styleEl.textContent = `
        @keyframes storysplat-portal-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
            document.head.appendChild(styleEl);
        }
        container.appendChild(loadingDiv);
    }
    // Update portal loading text with actual scene name (after API fetch)
    function updatePortalLoadingText(container: HTMLElement, sceneName: string): void {
        const textEl = container.querySelector('.storysplat-portal-loading-text');
        if (textEl) {
            textEl.textContent = getButtonLabel(currentButtonLabels, 'loadingScene').replace('{name}', sceneName);
        }
    }
    // Hide portal loading UI
    function hidePortalLoadingUI(container: HTMLElement): void {
        const loadingDiv = container.querySelector('.storysplat-portal-loading');
        if (loadingDiv) {
            loadingDiv.remove();
        }
    }
    // Raycast helper for hotspot detection
    const canvasEl = app.graphicsDevice.canvas as HTMLCanvasElement;
    // Track mouse position for reticle (store ref for cleanup)
    // Skip on mobile — no mouse, and reticle is disabled anyway
    const reticleMouseMoveHandler = (e: MouseEvent) => {
        // offsetX/Y == clientX - rect.left for a childless canvas target, minus the layout read.
        reticleScreenX = e.offsetX;
        reticleScreenY = e.offsetY;
    };
    if (!isMobile) {
        canvasEl.addEventListener('mousemove', reticleMouseMoveHandler);
    }
    // Measurement mode flag — hoisted so click/double-click handlers can check it
    let isMeasureModeActive = false;
    // Hoisted measurement cleanup refs (assigned inside the measurement setup block)
    let _measurePicker: pc.Picker | null = null;
    let _measureKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
    // Movement-cancel keydown listener (registered after load) — removed by both cleanup paths.
    let _movementKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
    let _measureCleanupDom: (() => void) | null = null;
    /** Re-renders the Measure panel in the current labels (setButtonLabels); set by the measurement tool. */
    let refreshMeasurePanelLabels: (() => void) | null = null;
    // Suppress clicks when the user is dragging to look around
    let clickSuppressed = false;
    let pointerDown = false;
    let pointerStartX = 0;
    let pointerStartY = 0;
    const dragThresholdPx = 5;
    canvasEl.addEventListener('pointerdown', (e: PointerEvent) => {
        if (e.button !== 0)
            return;
        pointerDown = true;
        clickSuppressed = false;
        pointerStartX = e.clientX;
        pointerStartY = e.clientY;
    });
    canvasEl.addEventListener('pointermove', (e: PointerEvent) => {
        if (!pointerDown)
            return;
        const dx = e.clientX - pointerStartX;
        const dy = e.clientY - pointerStartY;
        if ((dx * dx + dy * dy) > (dragThresholdPx * dragThresholdPx)) {
            clickSuppressed = true;
        }
    });
    const clearPointerDown = () => {
        pointerDown = false;
    };
    canvasEl.addEventListener('pointerup', clearPointerDown);
    canvasEl.addEventListener('pointercancel', clearPointerDown);
    function raycastHotspots(x: number, y: number): {
        entity: ViewerRuntimeEntity;
        hotspot: HotspotConfigLike;
    } | null {
        // Shared _hoverRay* scratch with raycastPortals — synchronous, never nested (the AR path
        // calls them sequentially), and the ray is computed once instead of per entity.
        const from = camera.camera!.screenToWorld(x, y, camera.camera!.nearClip, _hoverRayFrom);
        const to = camera.camera!.screenToWorld(x, y, camera.camera!.farClip, _hoverRayTo);
        return raycastHotspotsAlong(from, _hoverRayDir.sub2(to, from).normalize());
    }
    /** The hotspot nearest along a world-space ray (`dir` normalised): a screen click or a VR controller. */
    function raycastHotspotsAlong(from: pc.Vec3, dir: pc.Vec3): {
        entity: ViewerRuntimeEntity;
        hotspot: HotspotConfigLike;
    } | null {
        let closestHit: {
            entity: ViewerRuntimeEntity;
            distance: number;
        } | null = null;
        hotspotEntities.forEach(entity => {
            if (!entity.enabled)
                return;
            // Stacked marker: a click on any layer is a click on the hotspot
            if (entity.markerStack) {
                const t = rayHitMarkerStack(entity, from, dir);
                if (t !== null && (!closestHit || t < closestHit.distance)) {
                    closestHit = { entity, distance: t };
                }
                return;
            }
            const pos = entity.getPosition();
            const toEntity = _hoverRayTmp1.sub2(pos, from);
            const t = toEntity.dot(dir);
            if (t < 0)
                return;
            const closestPoint = _hoverRayTmp2.copy(dir).mulScalar(t).add(from);
            const distance = closestPoint.distance(pos);
            // Use the entity's actual scale to determine hit radius
            // Take the maximum of X and Y scale for a reasonable hit area
            const entityScale = entity.getLocalScale();
            const hitRadius = Math.max(entityScale.x, entityScale.y, 0.3) * 0.6; // Min 0.18 units, scaled by 0.6
            if (distance < hitRadius) {
                if (!closestHit || t < closestHit.distance) {
                    closestHit = { entity, distance: t };
                }
            }
        });
        // TypeScript doesn't track assignments inside forEach callbacks
        const hotspotHitResult = closestHit as { entity: ViewerRuntimeEntity; distance: number } | null;
        if (hotspotHitResult !== null) {
            const entity = hotspotHitResult.entity;
            return { entity, hotspot: entity.hotspotData! as HotspotConfigLike };
        }
        return null;
    }
    // Hover detection for cursor change and hover popups
    let currentHoverHotspot: HotspotConfigLike | null = null;
    // Re-entrancy guard for the API-open path below: a custom script that calls
    // triggerHotspot() from its own hotspotClick handler would recurse forever.
    let triggerHotspotEmitting = false;
    let hasAnyHoverAudioHotspots = false; // Fast-path: skip hover-audio iteration when no hotspots use it
    let isMouseOverPopup = false;
    // Track if any mesh is currently being hovered (prevents hotspot handler from overriding cursor)
    let anyMeshHovered = false;
    // Track when mouse is over the popup
    const popup = container.querySelector('.storysplat-hotspot-popup') as HTMLElement;
    const overlay = container.querySelector('.storysplat-hotspot-overlay') as HTMLElement;
    if (popup) {
        popup.addEventListener('mouseenter', () => {
            isMouseOverPopup = true;
        });
        popup.addEventListener('mouseleave', () => {
            isMouseOverPopup = false;
            // Close popup when mouse leaves the popup (for hover-activated hotspots)
            if (currentHoverHotspot && currentHoverHotspot.activationMode === 'hover') {
                popup.classList.remove('visible');
                if (overlay)
                    overlay.classList.remove('visible');
                currentHoverHotspot = null;
            }
        });
    }
    canvasEl.addEventListener('mousemove', (e: MouseEvent) => {
        // offsetX/Y are relative to the listener target (canvasEl has no children), identical to
        // clientX - rect.left without the per-event getBoundingClientRect (a potential layout read).
        const x = e.offsetX;
        const y = e.offsetY;
        // Check portals first
        const portalHit = raycastPortals(x, y);
        if (portalHit && portalHit.portal) {
            const portal = portalHit.portal;
            // Show pointer cursor for click-activated portals
            const effectiveActivationMode = portal.activationMode || 'click';
            if (effectiveActivationMode === 'click') {
                canvasEl.style.cursor = 'pointer';
            }
            else {
                canvasEl.style.cursor = 'default';
            }
            hoveringInteractable = true;
            return; // Portals take precedence over hotspots
        }
        const hit = raycastHotspots(x, y);
        if (hit && hit.hotspot) {
            const entity = hit.entity;
            const hotspot = hit.hotspot;
            // Show pointer cursor for interactive hotspots (default to 'click' if no activationMode set)
            const effectiveHotspotMode = hotspot.activationMode || 'click';
            if (effectiveHotspotMode === 'click' || effectiveHotspotMode === 'hover' || hotspot.type === 'video' || hotspotHasVideoPlane(hotspot, entity) || !!hotspot.linkedActions?.length || !!hotspot.cutaway || !!hotspot.splatSwap || !!hotspot.orbitView || !!findExtraWaypoint(config.extraWaypoints, hotspot.teleportExtraWaypoint)) {
                canvasEl.style.cursor = 'pointer';
                hoveringInteractable = true;
            }
            else {
                canvasEl.style.cursor = 'default';
                hoveringInteractable = false;
            }
            // Show popup on hover for hover-activated hotspots
            if (hotspot.activationMode === 'hover' && currentHoverHotspot !== hotspot) {
                currentHoverHotspot = hotspot;
                // A hover-activated hotspot runs its linked actions on hover-in (before its own
                // popup, so that popup wins), not on click.
                runHotspotActions(hotspot);
                const isBeingEdited = isEditorMode && editorEditingHotspotId === hotspot.id;
                if (!isBeingEdited && hasDisplayableHotspotPopupContent(hotspot)) {
                    showHotspotPopup(container, hotspot as HotspotData, currentButtonLabels);
                    // A hover-opened hotspot is an open like any other. Only the
                    // click path used to emit, so every hover-configured scene
                    // under-counted its hotspot opens to zero. Not in the editor:
                    // there `hotspotClick` TOGGLES the sidebar's hotspot preview,
                    // so emitting on hover would flip it as the mouse moves.
                    if (!isEditorMode) events.emit('hotspotClick', { hotspot });
                }
            }
            // Stop hover audio on any OTHER hotspot that was playing (handles moving between interactables)
            if (hasAnyHoverAudioHotspots) {
                hotspotEntities.forEach((other) => {
                    if (other !== entity && (other as ViewerRuntimeEntity)._audioHoverPlaying && (other as ViewerRuntimeEntity).hotspotAudioSlotId) {
                        const otherSlot = other.sound?.slot((other as ViewerRuntimeEntity).hotspotAudioSlotId!);
                        if (otherSlot?.isPlaying) otherSlot.stop();
                        (other as ViewerRuntimeEntity)._audioHoverPlaying = false;
                    }
                });
            }
            // Play audio on hover for hover-triggered audio
            if (hasAnyHoverAudioHotspots && entity._audioHoverMode && entity.hotspotAudioSlotId && !entity._audioHoverPlaying) {
                const slot = entity.sound?.slot(entity.hotspotAudioSlotId);
                if (slot && !slot.isPlaying && entity.hotspotAudioReady) {
                    slot.play();
                    entity._audioHoverPlaying = true;
                }
            }
        }
        else {
            // Don't reset cursor if a mesh is currently being hovered (mesh handler manages its own cursor)
            if (!anyMeshHovered) {
                canvasEl.style.cursor = 'default';
                hoveringInteractable = false;
            }
            // Stop hover audio on any hotspot that was playing
            if (hasAnyHoverAudioHotspots) hotspotEntities.forEach((he) => {
                if ((he as ViewerRuntimeEntity)._audioHoverPlaying && (he as ViewerRuntimeEntity).hotspotAudioSlotId) {
                    const slot = he.sound?.slot((he as ViewerRuntimeEntity).hotspotAudioSlotId!);
                    if (slot?.isPlaying) slot.stop();
                    (he as ViewerRuntimeEntity)._audioHoverPlaying = false;
                }
            });
            // Only hide popup if mouse is NOT over the popup element
            if (currentHoverHotspot && currentHoverHotspot.activationMode === 'hover' && !isMouseOverPopup) {
                const popupEl = container.querySelector('.storysplat-hotspot-popup') as HTMLElement;
                const overlayEl = container.querySelector('.storysplat-hotspot-overlay') as HTMLElement;
                if (popupEl)
                    popupEl.classList.remove('visible');
                if (overlayEl)
                    overlayEl.classList.remove('visible');
                currentHoverHotspot = null;
            }
        }
        // --- Segment hover (viewer-only tooltip, editor uses select-by-segment) ---
        if (segmentAttachment?.metadata?.segments?.length && !isEditorMode) {
            const w = canvasEl.clientWidth;
            const h = canvasEl.clientHeight;
            const ndcX = (x / w) * 2 - 1;
            const ndcY = 1 - (y / h) * 2;
            const camPos = camera.getPosition();
            const camFwd = camera.forward;
            const camRight = camera.right;
            const camUp = camera.up;
            const fovRad = (camera.camera!.fov * Math.PI) / 180;
            const aspect = w / h;
            const tanHalf = Math.tan(fovRad / 2);
            const rayDir = _segRayDir.set(
                camFwd.x + ndcX * aspect * tanHalf * camRight.x + ndcY * tanHalf * camUp.x,
                camFwd.y + ndcX * aspect * tanHalf * camRight.y + ndcY * tanHalf * camUp.y,
                camFwd.z + ndcX * aspect * tanHalf * camRight.z + ndcY * tanHalf * camUp.z
            ).normalize();
            let bestSegId = 0;
            let bestSeg: typeof segmentAttachment.metadata.segments[number] | null = null;
            let bestDist = Infinity;
            const threshold = 2.0;
            for (const seg of segmentAttachment.metadata.segments) {
                const cx = seg.centroid_3d[0];
                const cy = seg.centroid_3d[1];
                const cz = seg.centroid_3d[2];
                const dx = cx - camPos.x;
                const dy = cy - camPos.y;
                const dz = cz - camPos.z;
                const t = dx * rayDir.x + dy * rayDir.y + dz * rayDir.z;
                if (t < 0) continue;
                const closestX = camPos.x + t * rayDir.x;
                const closestY = camPos.y + t * rayDir.y;
                const closestZ = camPos.z + t * rayDir.z;
                const dist = Math.sqrt(
                    (closestX - cx) ** 2 + (closestY - cy) ** 2 + (closestZ - cz) ** 2
                );
                const scaledThreshold = threshold * Math.max(1, t * 0.1);
                if (dist < scaledThreshold && dist < bestDist) {
                    bestDist = dist;
                    bestSegId = seg.id;
                    bestSeg = seg;
                }
            }
            segmentAttachment.setHighlight(bestSegId);
            // Viewer-only tooltip — content rebuilt only when the hovered segment changes
            // (reference compare); the position/opacity writes stay per-event.
            if (bestSeg) {
                if (!segmentTooltipEl) {
                    segmentTooltipEl = document.createElement('div');
                    segmentTooltipEl.style.cssText = 'position:absolute;pointer-events:none;padding:4px 10px;background:rgba(0,0,0,0.75);color:#fff;border-radius:6px;font-size:13px;font-family:sans-serif;white-space:nowrap;z-index:9999;transform:translate(-50%,-120%);transition:opacity 0.15s;';
                    container.appendChild(segmentTooltipEl);
                }
                if (bestSeg !== _lastTooltipSeg) {
                    _lastTooltipSeg = bestSeg;
                    const [r, g, b] = bestSeg.avg_color;
                    segmentTooltipEl.textContent = '';
                    const swatch = document.createElement('span');
                    swatch.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:50%;background:rgb(${r},${g},${b});margin-right:6px;vertical-align:middle;`;
                    segmentTooltipEl.appendChild(swatch);
                    segmentTooltipEl.appendChild(document.createTextNode(`${bestSeg.name} (${bestSeg.gaussian_count.toLocaleString()} splats)`));
                }
                segmentTooltipEl.style.left = `${x}px`;
                segmentTooltipEl.style.top = `${y}px`;
                segmentTooltipEl.style.opacity = '1';
            } else if (segmentTooltipEl) {
                segmentTooltipEl.style.opacity = '0';
            }
        }
    });
    // Click handling for hotspots and portals
    canvasEl.addEventListener('click', (e: MouseEvent) => {
        // Skip hotspot/portal clicks while in measurement mode
        if (isMeasureModeActive) return;
        if (gizmoInputSuppressed) return;
        if (clickSuppressed) {
            clickSuppressed = false;
            return;
        }
        const rect = canvasEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        // Check for portal clicks first (portals take precedence)
        const portalHit = raycastPortals(x, y);
        const hit = activatePickedTarget(portalHit, () => raycastHotspots(x, y));
        // Single-tap refocus: when refocusTapMode is 'single' and nothing was hit, trigger refocus
        if (!hit && refocusTapMode === 'single') {
            if (currentCameraMode === 'explore' || currentCameraMode === 'walk' || currentCameraMode === 'orbit') {
                handleDoubleTap(x, y);
            }
        }
    });
    /**
     * Activate what a pick hit: a portal first (portals take precedence), then a hotspot with its
     * audio, video, linked actions, teleport and popup. Shared by canvas clicks and VR controller
     * selects. Returns whether anything was hit.
     */
    function activatePickedTarget(
        portalHit: ReturnType<typeof raycastPortals>,
        pickHotspot: () => ReturnType<typeof raycastHotspots>,
    ): boolean {
        if (portalHit !== null && portalHit.portal) {
            const portal = portalHit.portal;
            console.log('[StorySplat Viewer] Portal clicked:', portal.title || portal.targetSceneName);
            // In editor mode, emit event for sidebar selection instead of navigating
            if (isEditorMode) {
                events.emit('portalClick', { portal });
                return true;
            }
            // Handle click-activated portals
            const effectiveActivationMode = portal.activationMode || 'click';
            if (effectiveActivationMode === 'click') {
                const shouldConfirm = portal.confirmNavigation !== false
                    && (portal.title || portal.targetSceneName || portal.targetSceneId);
                // The confirmation popup is page UI a headset can't show: go straight there in XR.
                if (shouldConfirm && portalPopupEl && !isInXR) {
                    showPortalPopup(portal as PortalData);
                }
                else {
                    handlePortalNavigation(portal);
                }
            }
            return true; // Don't process hotspot clicks when portal is clicked
        }
        const hitResult = pickHotspot();
        if (hitResult !== null) activateHotspot(hitResult.entity, hitResult.hotspot);
        return hitResult !== null;
    }
    /**
     * Activate a hotspot: its audio, video, linked actions, camera action and popup. Shared by
     * canvas clicks, VR controller selects and the visitor directory (`fromDirectory`: a hover
     * hotspot opens like a click one, and one without a camera action is brought into view).
     */
    function activateHotspot(entity: ViewerRuntimeEntity, hotspot: HotspotConfigLike, options: { fromDirectory?: boolean } = {}): void {
        console.log('[StorySplat Viewer] Hotspot clicked:', hotspot.title, options.fromDirectory ? '(directory)' : '');
        events.emit('hotspotClick', options.fromDirectory ? { hotspot, source: 'directory' } : { hotspot });
        // Handle hotspot audio click behavior — only for explicit 'click' trigger mode,
        // or legacy scenes (no audioTriggerMode) that don't use media proximity (which implies audio proximity)
        const label = hotspot.title || hotspot.id || 'untitled';
        const slotId = entity.hotspotAudioSlotId;
        const ready = entity.hotspotAudioReady;
        const hasAudioUrl = !!hotspot.audioUrl;
        console.log(`[HotspotAudio] click trace: ${label} hasAudioUrl=${hasAudioUrl} slotId=${slotId || '(none)'} ready=${!!ready} mediaTrigger=${entity.mediaTriggerMode || '(default)'} audioTriggerMode=${hotspot.audioTriggerMode || '(default)'} clickBehavior=${hotspot.audioClickBehavior || '(default pause-resume)'} globalMuted=${globalMuted}`);
        if (!slotId) {
            if (hasAudioUrl) console.warn(`[HotspotAudio] click: bailing — hotspot has audioUrl but no slotId (setupHotspotAudio didn't return one): ${label}`);
        } else if (!ready) {
            console.warn(`[HotspotAudio] click: bailing — slot exists but asset never finished loading (hotspotAudioReady=false): ${label}. Check Network tab for ${hotspot.audioUrl}`);
        } else {
            const effectiveAudioTrigger = hotspot.audioTriggerMode || ((entity.mediaTriggerMode || 'click') === 'proximity' ? 'proximity' : 'click');
            if (effectiveAudioTrigger !== 'click') {
                console.log(`[HotspotAudio] click: skipping — effectiveAudioTrigger=${effectiveAudioTrigger} (not 'click'): ${label}`);
            } else {
                const slot = entity.sound?.slot(slotId);
                if (!slot) {
                    console.warn(`[HotspotAudio] click: bailing — slot ${slotId} missing on sound component (component=${!!entity.sound}): ${label}`);
                } else {
                    const pcAudioCtx = (app.systems.sound as { manager?: { context?: AudioContext } })?.manager?.context;
                    console.log(`[HotspotAudio] slot found: isPlaying=${slot.isPlaying} isPaused=${slot.isPaused} asset=${slot.asset} volume=${slot.volume} pcAudioCtxState=${pcAudioCtx?.state || '(unknown)'}`);
                    const audioClickBehavior = hotspot.audioClickBehavior || 'pause-resume';
                    const isPlaying = slot.isPlaying;
                    const isPaused = slot.isPaused;
                    if (!isPlaying && !isPaused) {
                        const ok = slot.play();
                        entity.hotspotAudioPlaying = true;
                        console.log(`[HotspotAudio] play() called (fresh): ${label} returned=${!!ok} nowPlaying=${slot.isPlaying}`);
                    }
                    else if (isPaused && audioClickBehavior === 'pause-resume') {
                        slot.resume();
                        entity.hotspotAudioPlaying = true;
                        console.log(`[HotspotAudio] resume() called: ${label} nowPlaying=${slot.isPlaying}`);
                    }
                    else if (isPaused && audioClickBehavior === 'stop-restart') {
                        slot.play();
                        entity.hotspotAudioPlaying = true;
                        console.log(`[HotspotAudio] play() called (restart from paused): ${label}`);
                    }
                    else if (isPlaying) {
                        if (audioClickBehavior === 'stop-restart') {
                            slot.stop();
                            entity.hotspotAudioPlaying = false;
                            console.log(`[HotspotAudio] stop() called: ${label}`);
                        } else {
                            slot.pause();
                            entity.hotspotAudioPlaying = false;
                            console.log(`[HotspotAudio] pause() called: ${label}`);
                        }
                    }
                }
            }
        }
        // Handle video toggle (for click or autoplay mode - autoplay needs click to unmute)
        if (entity.videoElement && (entity.mediaTriggerMode === 'click' || entity.mediaTriggerMode === 'autoplay')) {
            const video = entity.videoElement as HTMLVideoElement;
            const alphaVideo = entity.alphaVideoElement as HTMLVideoElement | null;
            if (entity.mediaTriggerMode === 'autoplay' && !video.paused && video.muted) {
                // Autoplay mode: click unmutes if already playing
                video.muted = hotspot.videoMuted === false ? false : false; // Unmute on click
                console.log('[Hotspot] Video unmuted by click');
            }
            else if (video.paused) {
                // Play video
                playVideoHotspot(entity, hotspot);
                console.log('[Hotspot] Video started');
            }
            else {
                // Pause video
                pauseVideoHotspot(entity);
                console.log('[Hotspot] Video paused');
            }
        }
        // Show popup for click-activated hotspots with content
        // Default to 'click' if activationMode is not set
        // From the directory, and in VR / AR, a hover hotspot opens like a click one (there is no
        // hover there: a controller select or a tap is all there is).
        const effectiveActivationMode = (options.fromDirectory || isInXR) && hotspot.activationMode === 'hover' ? 'click' : (hotspot.activationMode || 'click');
        // Linked actions run on click unless the hotspot activates on hover (they ran on
        // hover-in then). Here, inside the click gesture, so a linked video may play with sound.
        if (effectiveActivationMode !== 'hover') runHotspotActions(hotspot);
        // Show popup if there's any displayable content and contentType isn't 'none'
        const hasPopupContent = hasDisplayableHotspotPopupContent(hotspot);
        const showClickHotspotContent = () => {
            if (effectiveActivationMode !== 'click') return;
            if (!hasPopupContent) {
                // Don't close the popup this click's linked actions just opened.
                if (linkedActionsOpenPopup(hotspot.linkedActions)) return;
                stopHotspotPopupMedia(container);
                const popupEl = container.querySelector('.storysplat-hotspot-popup') as HTMLElement;
                const overlayEl = container.querySelector('.storysplat-hotspot-overlay') as HTMLElement;
                if (popupEl) popupEl.classList.remove('visible');
                if (overlayEl) overlayEl.classList.remove('visible');
                return;
            }
            // In VR the popup goes on the 3D info panel (a headset can't show page UI); 8th Wall AR
            // runs in the page, so it shows the usual popup with all of its content.
            if (isInXR && !isIn8thWallAR) {
                if (arContentVisible && arContentSource === hotspot) {
                    hideARContent();
                }
                else {
                    showARContent(hotspot as HotspotData);
                }
            }
            else if (!(isEditorMode && editorEditingHotspotId === hotspot.id)) {
                showHotspotPopup(container, hotspot as HotspotData, currentButtonLabels);
            }
        };
        // The click's camera action, first match wins (resolveHotspotCameraAction): a tour
        // teleport to a waypoint or percentage, then an extra waypoint, then the orbit view.
        const cameraAction = resolveHotspotCameraAction(hotspot, config.extraWaypoints);
        const teleportMode = hotspot.teleportMode || 'animate'; // Default to animate for smooth experience
        let calculatedTargetProgress: number | null = null;
        if (cameraAction.kind === 'waypoint') {
            // Teleport to specific waypoint
            const numWaypoints = config.waypoints?.length || 1;
            const targetIndex = Math.max(0, Math.min(cameraAction.index, numWaypoints - 1));
            calculatedTargetProgress = getProgressForWaypointIndex(targetIndex);
            console.log('[Hotspot] Teleporting to waypoint:', targetIndex, '(progress:', calculatedTargetProgress, ', mode:', teleportMode, ')');
        }
        else if (cameraAction.kind === 'percent') {
            // Teleport to specific percentage (0-100)
            calculatedTargetProgress = Math.max(0, Math.min(cameraAction.percent / 100, 1));
            console.log('[Hotspot] Teleporting to percent:', cameraAction.percent, '(progress:', calculatedTargetProgress, ', mode:', teleportMode, ')');
        }
        // "Fly to extra waypoint" / "Move camera to orbit view" travel in explore mode, in the
        // published scene and in the editor alike, except for the hotspot open in the editor's
        // hotspot editor (see hotspotClickMayMoveCamera). The popup opens when the camera
        // arrives, or when the visitor takes over the camera. In XR the headset owns the
        // camera: show the hotspot's content, don't fly anywhere.
        const mayMoveToView = hotspotClickMayMoveCamera(hotspot.id, { isInXR, isEditorMode, editingHotspotId: editorEditingHotspotId });
        const movedToExtraWaypoint = mayMoveToView && cameraAction.kind === 'extraWaypoint'
            && moveCameraToExtraWaypoint(cameraAction.waypoint, showClickHotspotContent, teleportMode === 'instant');
        const movedToOrbitView = mayMoveToView && cameraAction.kind === 'orbitView'
            && moveCameraToOrbitView(cameraAction.view, showClickHotspotContent);
        // Picked in the directory without a camera action of its own: bring it into view (the
        // orbit view around it, from the side the camera is on), then open it.
        const movedIntoView = mayMoveToView && !!options.fromDirectory && cameraAction.kind === 'none'
            && moveCameraToHotspotView(entity, showClickHotspotContent);
        if (isInXR) {
            // VR: go to the hotspot's view by teleport (the headset owns the camera, so no flight).
            if (xrSessionType === 'vr') {
                if (cameraAction.kind === 'extraWaypoint') {
                    if (teleportVrToExtraWaypoint(cameraAction.waypoint)) runArrivalCutaway(cameraAction.waypoint.cutaway);
                }
                else if (cameraAction.kind === 'orbitView') teleportVrToOrbitView(cameraAction.view);
                else if (cameraAction.kind === 'waypoint') goToVrStop(Math.max(0, Math.min(cameraAction.index, (config.waypoints?.length || 1) - 1)));
                else if (calculatedTargetProgress !== null) teleportVrToProgress(calculatedTargetProgress);
            }
            showClickHotspotContent();
        }
        else if (movedToExtraWaypoint) {
            console.log('[Hotspot] Flying to extra waypoint:', cameraAction.kind === 'extraWaypoint' ? cameraAction.waypoint.name || cameraAction.waypoint.id : '');
            // Its cutaway, unless this click brought its own (runHotspotActions holds it).
            if (cameraAction.kind === 'extraWaypoint') runArrivalCutaway(cameraAction.waypoint.cutaway);
        }
        else if (movedToOrbitView) {
            console.log('[Hotspot] Moving camera to orbit view');
        }
        else if (movedIntoView) {
            console.log('[Hotspot] Bringing it into view');
        }
        else if (calculatedTargetProgress !== null) {
            if (!waypointControlEnabled && waypointPositions.length >= 2) {
                resumeTourAtCurrentCamera();
            }
            if (teleportMode === 'instant') {
                // Instant jump - directly set position
                currentProgress = calculatedTargetProgress;
                targetProgress = calculatedTargetProgress; // Keep inertia system in sync
                updateCameraFromProgress(currentProgress);
                showClickHotspotContent();
            }
            else {
                // Animate (default) - smooth transition
                targetProgress = calculatedTargetProgress; // Keep inertia system in sync
                animateToProgress(calculatedTargetProgress, 800, showClickHotspotContent); // 800ms for smooth transition
            }
        }
        else {
            showClickHotspotContent();
        }
    }
    /** The directory's "bring it into view": orbit the marker (or fly to it when orbit is off). */
    function moveCameraToHotspotView(entity: ViewerRuntimeEntity, onDone: () => void): boolean {
        const marker = entity.getPosition();
        const from = camera.getPosition();
        const scale = entity.getScale();
        const zoom = cameraControls.zoomRange;
        const view = hotspotIntoView(
            { x: marker.x, y: marker.y, z: marker.z },
            { x: from.x, y: from.y, z: from.z },
            Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z)),
            { min: zoom.x, max: zoom.y },
        );
        if (!view) return false;
        const position = new pc.Vec3(view.position.x, view.position.y, view.position.z);
        const pivot = new pc.Vec3(view.pivot.x, view.pivot.y, view.pivot.z);
        return moveCameraToExploreView('orbit', position, pivot, onDone) || moveCameraToExploreView('fly', position, pivot, onDone);
    }
    /** A hotspot picked in the directory (or by openDirectory scripts): as if clicked in the scene. */
    function activateHotspotById(id: string, options: { fromDirectory?: boolean } = {}): boolean {
        const entity = hotspotEntities.find((e) => e.hotspotData?.id === id);
        const hotspot = entity?.hotspotData;
        if (!entity || !hotspot) return false;
        activateHotspot(entity, hotspot, options);
        return true;
    }
    /**
     * What a VR controller or hand ray points at, as the action its select runs on release: a
     * portal, a hotspot, a model, or (pointing at nothing) closing the 3D info panel, then leaving
     * a 360° panorama. Null lets the select teleport as usual.
     */
    function pickXrTarget(from: pc.Vec3, dir: pc.Vec3): (() => void) | null {
        if (dir.lengthSq() < 1e-8) return null;
        dir.normalize();
        const portalHit = raycastPortalsAlong(from, dir);
        if (portalHit?.portal) return () => { activatePickedTarget(portalHit, () => null); };
        const hotspotHit = raycastHotspotsAlong(from, dir);
        if (hotspotHit) return () => { activatePickedTarget(null, () => hotspotHit); };
        // 3D models with interactions: their click ones, and the hover ones toggled (no hover in VR).
        const models = interactiveMeshesAlong(from, dir);
        if (models.length) {
            return () => {
                for (const entry of models) {
                    entry.onClick();
                    if (!entry.hoverDriven) continue;
                    entry.isHovering = !entry.isHovering;
                    if (entry.isHovering) entry.onHoverIn();
                    else entry.onHoverOut();
                }
            };
        }
        if (arContentVisible) return () => hideARContent();
        // A 360° panorama's Exit button is page UI a headset can't show: a select at nothing leaves it.
        if (panoramaModeEnabled && !panoramaModeTransitioning) return () => setPanoramaMode(false);
        return null;
    }
    // Double-click/double-tap handler: walk-to in walk, fly-to in fly, focus in explore/orbit
    function handleDoubleTap(screenX: number, screenY: number): void {
        if (isInXR) return;
        // Dismiss hints on first use
        if (!doubleTapHintDismissed) {
            doubleTapHintDismissed = true;
            setDoubleTapHintVisible(uiElements, false);
        }
        // Only hide WASD hint if not in fly mode — fly mode keeps the hint visible
        if (cameraControls.mode !== 'fly') {
            setWasdHintVisible(uiElements, false);
        }
        if (currentCameraMode === 'walk') {
            handleWalkToClick(screenX, screenY);
            return;
        }
        if (currentCameraMode === 'explore' || currentCameraMode === 'orbit') {
            handleDoubleClickFocus(screenX, screenY);
        }
    }
    // Double-click/double-tap in walk mode: pick world point and walk there
    function handleWalkToClick(screenX: number, screenY: number): void {
        if (!characterController) return;
        try {
            // CPU surface raycast (CharacterController collision / occupancy grid) — NO GPU gsplat
            // pick, which disturbs the 2.20 unified work buffer and can hang the device. Same point
            // the reticle flashes at, so move and reticle stay in sync. See reticleRaycast.
            const worldPoint = reticleRaycast(screenX, screenY);
            if (worldPoint && isFinite(worldPoint.x) && isFinite(worldPoint.y) && isFinite(worldPoint.z)) {
                const camPos = camera.getPosition();
                const distance = camPos.distance(worldPoint);
                if (distance > 0.3 && distance < 500) {
                    flashReticleAt(worldPoint, screenX, screenY);
                    characterController.walkTo(worldPoint);
                }
            }
        } catch (err) {
            console.warn('[StorySplat Viewer] Walk-to pick failed:', err);
        }
    }
    // Double-click/double-tap to focus camera on clicked point (explore/orbit mode)
    function handleDoubleClickFocus(screenX: number, screenY: number): void {
        if (currentCameraMode !== 'explore' && currentCameraMode !== 'orbit')
            return;
        // Disable refocus while measuring
        if (isMeasureModeActive) return;
        // "Limit orbit rotation" keeps the pivot where the creator set the view: refocus is off,
        // so don't flash the white ring as if it were about to move.
        if (cameraControls.isOrbitPivotLocked()) return;
        console.log('[StorySplat Viewer] Double-click focus at:', screenX, screenY);
        // First check if we hit a hotspot
        const hotspotHit = raycastHotspots(screenX, screenY);
        if (hotspotHit) {
            const pos = hotspotHit.entity.getPosition();
            console.log('[StorySplat Viewer] Focusing on hotspot at:', pos.x, pos.y, pos.z);
            if (cameraControls.mode === 'fly') {
                cameraControls.flyTo(pos);
            } else {
                cameraControls.focus(pos, false);
            }
            return;
        }
        // Find the clicked surface point via a CPU surface raycast (CharacterController collision /
        // occupancy grid) — NO GPU gsplat pick, which disturbs the 2.20 unified work buffer and can
        // hang the device (DXGI_ERROR_DEVICE_HUNG). See reticleRaycast.
        try {
            const worldPoint = reticleRaycast(screenX, screenY);
            if (worldPoint) {
                // Validate coordinates are finite (depth buffer can return NaN/Infinity on sparse areas)
                if (!isFinite(worldPoint.x) || !isFinite(worldPoint.y) || !isFinite(worldPoint.z)) {
                    console.log('[StorySplat Viewer] Discarding non-finite pick result:', worldPoint.x, worldPoint.y, worldPoint.z);
                } else {
                    const cameraPos = camera.getPosition();
                    const distance = cameraPos.distance(worldPoint);
                    // Validate: must be at a reasonable distance (not too close = depth artifact, not too far = background noise)
                    if (distance > 0.3 && distance < 500) {
                        console.log('[StorySplat Viewer] Focusing on GSplat at:', worldPoint.x.toFixed(2), worldPoint.y.toFixed(2), worldPoint.z.toFixed(2), 'distance:', distance.toFixed(2));
                        flashReticleAt(worldPoint, screenX, screenY);
                        if (cameraControls.mode === 'fly') {
                            cameraControls.flyTo(worldPoint);
                        } else {
                            cameraControls.focus(worldPoint, false);
                        }
                        return;
                    }
                    console.log('[StorySplat Viewer] Discarding pick result — distance out of range:', distance.toFixed(2));
                }
            }
            // No valid world point from depth pick — do nothing.
            // The previous AABB-center fallback sent the camera toward the center of the
            // entire splat cloud rather than where the user clicked, causing wrong-direction movement.
            console.log('[StorySplat Viewer] No valid pick result at click point');
        }
        catch (err) {
            console.warn('[StorySplat Viewer] Picking failed:', err);
        }
    }
    // Desktop double-click handler (only when refocusTapMode is 'double')
    canvasEl.addEventListener('dblclick', (e: MouseEvent) => {
        if (isInXR) return;
        if (refocusTapMode !== 'double') return;
        const rect = canvasEl.getBoundingClientRect();
        handleDoubleTap(e.clientX - rect.left, e.clientY - rect.top);
    });
    // Mobile double-tap detection — must distinguish true single-finger double-taps
    // from multi-touch gesture finger lifts (pinch-zoom, dual-joystick, etc.).
    let lastTapTime = 0;
    let wasMultiTouch = false;
    const DOUBLE_TAP_THRESHOLD = 300;
    canvasEl.addEventListener('touchstart', (e: TouchEvent) => {
        if (isInXR) return;
        if (e.touches.length > 1) {
            wasMultiTouch = true;
        }
    });
    canvasEl.addEventListener('touchend', (e: TouchEvent) => {
        if (isInXR) return;
        if (e.changedTouches.length !== 1)
            return;
        // If this touchend is part of a multi-touch gesture (pinch, dual-finger fly),
        // don't count it as a tap. Reset the flag once all fingers are lifted.
        if (wasMultiTouch) {
            if (e.touches.length === 0) {
                wasMultiTouch = false;
            }
            lastTapTime = 0;
            return;
        }
        const now = Date.now();
        if (now - lastTapTime < DOUBLE_TAP_THRESHOLD) {
            if (refocusTapMode === 'double') {
                const touch = e.changedTouches[0];
                const rect = canvasEl.getBoundingClientRect();
                handleDoubleTap(touch.clientX - rect.left, touch.clientY - rect.top);
            }
            lastTapTime = 0;
        }
        else {
            lastTapTime = now;
        }
    });
    // Reset multi-touch flag on touchcancel (system gesture interruption)
    // to prevent wasMultiTouch staying true forever and breaking double-tap.
    canvasEl.addEventListener('touchcancel', (e: TouchEvent) => {
        if (isInXR) return;
        if (e.touches.length === 0) {
            wasMultiTouch = false;
            lastTapTime = 0;
        }
    });
    // Cancel auto-move / walk-to on keyboard movement input. Named + stored so BOTH cleanup
    // paths (portal navigation, destroy) can remove it — as an anonymous listener it pinned one
    // full dead-viewer closure per portal hop and kept calling setCameraMode on the destroyed app.
    _movementKeydownHandler = (e: KeyboardEvent) => {
        if (isDestroyed) return;
        // Skip when focus is in a text input (so typing doesn't trigger camera movement)
        const tag = (document.activeElement as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable) return;
        const moveKeys = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'];
        if (moveKeys.includes(e.code)) {
            if (autoMoveForward) stopAutoMoveForward();
            if (characterController) characterController.cancelWalkTo();
        }
        // Tour mode: pressing a movement key (not Space) drops the viewer into explore — the scene's
        // default explore sub-mode — so WASD "just works" without first clicking a mode button.
        // BUT only when an explore/walk mode actually exists: a tour-ONLY scene must stay locked to
        // tour (WASD/arrows do nothing) instead of breaking into fly.
        if (!isEditorMode && !isInXR && currentCameraMode === 'tour' &&
            (allowedModes.includes('explore') || allowedModes.includes('walk')) &&
            ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) {
            setCameraMode(initialSplatExploreMode === 'walk' ? 'walk' : 'explore');
        }
        // Shift+Ctrl+D: toggle collision mesh debug visualization
        if (e.shiftKey && e.ctrlKey && e.code === 'KeyD' && characterController) {
            e.preventDefault();
            const newState = !characterController.collisionDebugVisible;
            characterController.setCollisionDebug(newState);
            console.log(`[StorySplat Viewer] Collision debug: ${newState ? 'ON' : 'OFF'} (${characterController.collisionMeshEntities.length} meshes)`);
        }
    };
    document.addEventListener('keydown', _movementKeydownHandler);
    // Load the splat or frame sequence after app starts
    updateProgress(0.2);
    // Choose loader based on scene data
    const hasFrameSequence = scene.frameSequence && scene.frameSequence.frameUrls && scene.frameSequence.frameUrls.length > 0;
    const loadContent = hasFrameSequence ? loadFrameSequence : loadSplat;
    // Arm the early-skybox runner (see the preload block near the top). Everything initSkybox
    // depends on is initialized by this point in the body.
    runEarlySkybox = () => {
        if (skyboxInitStarted || isEditorMode || isDestroyed || !eagerSkyboxPreloadImg) return;
        skyboxInitStarted = true;
        initSkybox(eagerSkyboxPreloadImg);
    };
    if (eagerSkyboxImgReady) runEarlySkybox();
    loadContent().then(() => {
        // Non-LOD content is fully downloaded here; LOD scenes keep climbing 85→100% through
        // the reveal hold below. (No "Ready!" label — the bar just finishes when the loader hides.)
        const isLodSceneForProgress = !!(config.lodMetaUrl || config.mobileLodMetaUrl);
        if (!isLodSceneForProgress || isEditorMode) updateProgress(1.0);
        else updateProgress(0.85);
        // In editor mode, React useEffects handle entity creation (hotspots, lights,
        // particles, etc.) via the editor mutation API. Skip them here to avoid
        // double-creating entities. We still run initSplatRelighting because the
        // editor's setSplatRelighting needs the relighting script to exist.
        if (!isEditorMode) {
            // Create hotspots after splat is loaded
            createHotspots();
            // Optionally warm hotspot POPUP images (photoUrl + image content-block urls) so the first
            // popup opens instantly. Off by default (opt-in) — it costs bandwidth/memory up front.
            // Warms the same (non-CORS) HTTP cache entry the popup <img> uses; don't retain refs.
            if (config.uiOptions?.preloadHotspotImages && Array.isArray(config.hotspots)) {
                const seen = new Set<string>();
                const warm = (u?: string) => {
                    if (!u || seen.has(u)) return;
                    seen.add(u);
                    try { const img = new Image(); img.decoding = 'async'; img.src = u; img.decode?.().catch(() => {}); } catch { /* non-fatal */ }
                };
                for (const h of config.hotspots) {
                    warm((h as { photoUrl?: string }).photoUrl);
                    const blocks = (h as { contentBlocks?: Array<{ kind?: string; urls?: string[] }> }).contentBlocks;
                    if (Array.isArray(blocks)) {
                        for (const b of blocks) {
                            if (b?.kind === 'image' && Array.isArray(b.urls)) b.urls.forEach(warm);
                        }
                    }
                }
            }
            // Create portals for scene-to-scene navigation
            createPortals();
            // Create mirror planes (planar reflections)
            createMirrorPlanes();
            // Create water planes (animated water surfaces for broken pool/pond regions)
            createWaterPlanes();
            createCloudLayers();
            // Audio emitters / waypoint audio / particles / custom meshes are NOT visible in the
            // first splat frame — they run one frame later (see the deferred block below) so the
            // splat presents before their synchronous init cost.
            // Initialize skybox (skipped when the early-skybox path already ran on image load).
            // Pass the preload image through when it finished downloading so the pipeline skips a
            // second fetch; otherwise initSkybox does its own (browser-cached) load.
            if (!skyboxInitStarted) {
                skyboxInitStarted = true;
                const readyPreload = eagerSkyboxPreloadImg && eagerSkyboxPreloadImg.complete && eagerSkyboxPreloadImg.naturalWidth > 0
                    ? eagerSkyboxPreloadImg : undefined;
                initSkybox(readyPreload);
            }
            // Initialize custom lights
            initCustomLights();
        }
        // Initialize splat relighting (auto-enabled when lights exist)
        // Runs in both modes — editor needs the relighting script for setSplatRelighting
        initSplatRelighting();
        syncMeshAmbient();
        const isLodScene = !!(config.lodMetaUrl || config.mobileLodMetaUrl);
        // Preload the first additional splat upfront so the first swap is instant. On LOD scenes
        // this is a tens-of-MB download that competes with the chunk fill the preloader is
        // waiting on — defer it to the reveal there (bounded by the 8s cap; the preload entity
        // is created disabled, so nothing visual depends on when it lands).
        if (additionalSplats.length > 0 && additionalSplats[0].url) {
            if (isCompareModeEnabled && !isEditorMode) {
                // Compare mode (viewer only): await the after splat so both are ready
                // before the preloader hides. initCompareMode disables the reveal effect
                // so the splats appear immediately with the split.
                const initialCompareSplat = additionalSplats[0];
                const COMPARE_SHADER_READY_TIMEOUT_MS = 5000;
                const COMPARE_INITIAL_TOTAL_TIMEOUT_MS = 15000;
                let compareInitialLoadFinished = false;
                let compareInitSettled = false;
                let shaderReadyRaf = 0;
                let shaderReadyTimeout = 0;
                let initialCompareTotalTimeout = 0;

                const finishInitialCompareLoad = (
                    reason: 'ready' | 'shader-timeout' | 'overall-timeout' | 'load-error' | 'destroyed',
                ): void => {
                    if (compareInitialLoadFinished) return;
                    compareInitialLoadFinished = true;
                    if (shaderReadyRaf) cancelAnimationFrame(shaderReadyRaf);
                    if (shaderReadyTimeout) clearTimeout(shaderReadyTimeout);
                    if (initialCompareTotalTimeout) clearTimeout(initialCompareTotalTimeout);
                    if (reason === 'destroyed' || isDestroyed) return;
                    if (reason === 'shader-timeout') {
                        console.warn(`[CompareMode] Shader readiness timed out after ${COMPARE_SHADER_READY_TIMEOUT_MS}ms; revealing scene`);
                    } else if (reason === 'overall-timeout') {
                        console.warn(`[CompareMode] Initial compare load/readiness timed out after ${COMPARE_INITIAL_TOTAL_TIMEOUT_MS}ms; revealing scene`);
                    }
                    // Compare mode skips the LOD reveal hold, so finish the bar (and the public
                    // `progress` event) here — a LOD scene would otherwise stop at 85%.
                    updateProgress(1);
                    if (uiElements.preloader) {
                        void dismissInitialPreloader();
                    } else {
                        markInitialPresentationShown();
                    }
                    settleInitialStream();
                    emitSceneLoaded({
                        bandwidthUsed: 0,
                        isStorySplatHosted: isStorySplatHostedUrl(initialCompareSplat.url || ''),
                        url: initialCompareSplat.url,
                    });
                };

                initialCompareTotalTimeout = window.setTimeout(() => {
                    // If asset/manifest loading itself hung, invalidate its
                    // generation and drain any registered in-flight Asset before
                    // revealing the usable primary scene. If initialization had
                    // already committed, retain compare and merely stop waiting.
                    if (!compareInitSettled) {
                        destroyCompareMode();
                        isCompareModeEnabled = false;
                    }
                    finishInitialCompareLoad('overall-timeout');
                }, COMPARE_INITIAL_TOTAL_TIMEOUT_MS);

                void initCompareMode(initialCompareSplat.url, initialCompareSplat).then(() => {
                    compareInitSettled = true;
                    if (compareInitialLoadFinished) return;
                    // Material creation can lag entity enablement. Bound this poll
                    // with a wall-clock timer so a paused/stuck shader path cannot
                    // leave the preloader up forever. Keep the timer armed through
                    // the final extra RAF as well (background tabs may pause RAFs).
                    const waitForShaders = (): void => {
                        if (isDestroyed) {
                            finishInitialCompareLoad('destroyed');
                            return;
                        }
                        if (!compareSplitView || compareSplitView.isReady()) {
                            // One extra frame for the GPU to actually render.
                            shaderReadyRaf = requestAnimationFrame(() => finishInitialCompareLoad('ready'));
                        } else {
                            shaderReadyRaf = requestAnimationFrame(waitForShaders);
                        }
                    };
                    shaderReadyTimeout = window.setTimeout(
                        () => finishInitialCompareLoad('shader-timeout'),
                        COMPARE_SHADER_READY_TIMEOUT_MS,
                    );
                    shaderReadyRaf = requestAnimationFrame(waitForShaders);
                }).catch((error) => {
                    compareInitSettled = true;
                    console.error('[CompareMode] Initial compare load failed:', error);
                    destroyCompareMode();
                    isCompareModeEnabled = false;
                    finishInitialCompareLoad('load-error');
                });
            } else if (!isCompareModeEnabled) {
                if (isLodScene && !isEditorMode) {
                    runAfterInitialStream(() => {
                        if (isDestroyed) return;
                        preloadSplat(additionalSplats[0].url, additionalSplats[0]);
                    });
                } else {
                    preloadSplat(additionalSplats[0].url, additionalSplats[0]);
                }
            }
        }
        // Without viewer UI there is no overlay dismissal to trigger the reveal.
        if (!uiElements.preloader) startInitialReveal();
        // Hide preloader when splat is actually ready to render.
        // For LOD streaming, asset.ready() fires when the octree structure is parsed,
        // but actual chunk textures haven't downloaded yet. Hiding the preloader
        // immediately shows a black screen until the first chunks arrive (1-2s).
        // For non-LOD or scenes with reveal effects, use the original short delay.
        if (isCompareModeEnabled && !isEditorMode) {
            // Compare mode: preloader hidden by initCompareMode's .then() callback above
            // — skip the normal preloader-hide logic so both splats are ready first.
        } else if (isLodScene) {
            // Hold the loader until the LOD stream reaches decent detail (see waitForLodReveal —
            // MIN_HOLD_MS / REVEAL_RATIO / REVEAL_CAP_MS live there, shared with the editor API).
            // The bar keeps moving 85→100% through the hold with the same "Loading N%" label, so
            // the loader never reads as finished ("Ready!") while the screen is still up.
            void waitForLodReveal((fraction) => updateProgress(0.85 + 0.15 * fraction)).then(() => {
                if (isDestroyed) return;
                updateProgress(1);
                // Initial fill is done (or capped) — release the deferred downloads that were
                // held off the streaming window (proximity audio, swap preloads).
                settleInitialStream();
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        void dismissInitialPreloader();
                        startLodDetailMonitor();
                    });
                });
            });
        } else {
            // Non-LOD: primary splat is fully downloaded — nothing to protect, release deferrals.
            settleInitialStream();
            // Non-LOD or reveal effect present: original 200ms delay
            // (reveal animation provides the visual transition; non-LOD data is already loaded)
            setTimeout(() => {
                void dismissInitialPreloader();
            }, 200);
        }
        // Deferred secondary subsystems: nothing here is visible in the first splat frame (audio
        // is gesture-gated, particles/meshes/XR/HTML meshes join one frame later), so their
        // synchronous init cost runs AFTER the splat presents instead of in the same task the
        // splat becomes renderable.
        requestAnimationFrame(() => {
            if (isDestroyed) return;
            if (!isEditorMode) {
                // Setup waypoint audio (audio interactions on waypoints)
                setupWaypointAudio();
                // Setup standalone audio emitters
                setupAudioEmitters();
                // Initialize particle systems
                console.log('[StorySplat Viewer] 🎆 Calling initParticleSystems...');
                initParticleSystems();
                // Initialize custom meshes (3D models with animations, audio)
                initCustomMeshes();
            }
            // Setup XR (VR/AR) if enabled
            setupXR();
            // Setup HTML Meshes if configured at load. (The editor adds meshes
            // dynamically via editorInstance.addHtmlMesh, which creates the manager lazily —
            // see below — and owns every editor mesh. Creating them here too duplicated them:
            // a second manager replaced the editor's, leaving its meshes on screen, its overlay
            // and update listener running, and all of it out of reach of removeHtmlMesh.)
            if (!isEditorMode && config.htmlMeshes && config.htmlMeshes.length > 0) {
                console.log('[StorySplat Viewer] Setting up HTML meshes:', config.htmlMeshes.length);
                const htmlMeshManager = setupHtmlMeshes(app, config.htmlMeshes);
                // Store manager reference for cleanup
                (app as ViewerRuntimeApp).__htmlMeshManager = htmlMeshManager;
            }
        });
        // Drive HTML-mesh visibilityRange (and billboardRange) from tour progress.
        // Registered UNCONDITIONALLY and resolves the manager each tick, so it also
        // covers meshes added dynamically in the editor — the previous wiring only
        // ran when meshes already existed at initial load, so editor-added meshes
        // never updated their visibility on scroll.
        events.on('progressUpdate', () => {
            const mgr = (app as ViewerRuntimeApp).__htmlMeshManager;
            if (!mgr) return;
            const scrollPercent = currentProgress * 100;
            const waypointIndex = getWaypointIndexForProgress(currentProgress);
            mgr.updateVisibility(scrollPercent, waypointIndex, loopMode === 'loop');
        });
        // Custom Script System setup is deferred until after instance creation
        // so scripts receive the full ViewerInstance API
        // Check for URL parameters to support deep-linking
        // ?waypoint=2 will navigate to waypoint index 2
        // ?autoplay=true will start autoplay
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const waypointParam = urlParams.get('waypoint');
            const autoplayParam = urlParams.get('autoplay');
            if (waypointParam !== null && config.waypoints && config.waypoints.length > 0) {
                const targetIndex = parseInt(waypointParam, 10);
                if (!isNaN(targetIndex) && targetIndex >= 0 && targetIndex < config.waypoints.length) {
                    console.log('[StorySplat Viewer] URL param: navigating to waypoint', targetIndex);
                    goToWaypoint(targetIndex);
                }
            }
            if (autoplayParam === 'true' && !options.autoPlay && !config.autoPlay) {
                console.log('[StorySplat Viewer] URL param: starting autoplay');
                play();
            }
        }
        catch (e) {
            // URLSearchParams may not be available in some environments
            console.warn('[StorySplat Viewer] Could not parse URL parameters:', e);
        }
        // Stereo 3D: resume the format a portal hop carried over. Not in the editor, and only in
        // scenes that allow stereo (a hop into a scene without it lands in the normal view, and
        // the chain doesn't pick stereo back up at a later scene).
        const carried = stereoCarryOver.get(container);
        stereoCarryOver.delete(container);
        if (!isEditorMode && stereoEnabled && carried) {
            setStereoMode(carried.format);
            if (carried.ownsFullscreen && stereoFullscreenElement()) stereoOwnsFullscreen = true;
        }
        events.emit('ready');
        console.log('[StorySplat Viewer] Ready');
        // Show "Tap for audio" overlay if audio is present but blocked by the browser's autoplay
        // policy. Two independent blockers: (1) PlayCanvas / video AudioContext is suspended, or
        // (2) the cross-scene ambient bed is active but hasn't been unlocked by a gesture yet. A
        // scene whose ONLY audio is that bed still needs the prompt — so gate on either.
        const hasAudio = (config.audioEmitters && config.audioEmitters.length > 0) ||
            (config.narrationTrack && (config.narrationTrack as any).audioUrl) ||
            config.waypoints?.some((wp: any) => wp.interactions?.some((i: any) => i.type === 'audio')) ||
            config.hotspots?.some((h: any) => h.audioUrl) ||
            config.customMeshes?.some((m: any) => m.interaction?.playAudio && m.interaction?.audioUrl) ||
            (config.crossSceneAudio?.enabled && !!config.crossSceneAudio?.url) ||
            crossSceneAudio.isActive();
        if (hasAudio && uiElements.tapForAudio) {
            const pcAudioCtx = (app.systems.sound as any)?.manager?.context as AudioContext | undefined;
            const pcSuspended = !!pcAudioCtx && pcAudioCtx.state === 'suspended';
            const bedNeedsUnlock = crossSceneAudio.isActive() && !crossSceneAudio.isUnlocked();
            if (pcSuspended || bedNeedsUnlock) {
                uiElements.tapForAudio.style.display = '';
                uiElements.tapForAudio.classList.add('visible');
                const dismissTapOverlay = () => {
                    // Unlock the cross-scene ambient bed (autoplay-blocked until this gesture).
                    crossSceneAudio.unlock();
                    pcAudioCtx?.resume();
                    // Also resume any custom AudioContexts (video spatial audio)
                    allAudioContexts.forEach(ctx => { if (ctx.state === 'suspended') ctx.resume(); });
                    // Resume the audio-reactive analyser bus (module-scope context)
                    audioAnalyserBus.resume();
                    // Restart all autoplay audio that was blocked by the browser
                    // Hotspot audio
                    hotspotEntities.forEach(he => {
                        const slotId = (he as ViewerRuntimeEntity).hotspotAudioSlotId;
                        const hd = (he as ViewerRuntimeEntity).hotspotData;
                        if (slotId && hd?.audioTriggerMode === 'autoplay' && (he as ViewerRuntimeEntity).hotspotAudioReady) {
                            const slot = he.sound?.slot(slotId);
                            if (slot && !slot.isPlaying) {
                                slot.play();
                                (he as ViewerRuntimeEntity).hotspotAudioPlaying = true;
                            }
                        }
                    });
                    // Audio emitters
                    audioEmitterMap.forEach((emitterData) => {
                        const { config: emConfig, slotId, entity: emEntity, assetReady } = emitterData;
                        if (!assetReady) return;
                        const triggerMode = emConfig.triggerMode || (emConfig.autoplay !== false ? 'autoplay' : 'proximity');
                        if (triggerMode === 'autoplay') {
                            const slot = emEntity.sound?.slot(slotId);
                            if (slot && !slot.isPlaying) {
                                slot.play();
                                emitterData.playing = true;
                            }
                        }
                    });
                    // Waypoint audio
                    waypointAudioMap.forEach((audioData) => {
                        const { config: wpConfig, slotId, entity: wpEntity, assetReady } = audioData;
                        if (!assetReady) return;
                        const triggerMode = wpConfig.triggerMode || 'proximity';
                        if (triggerMode === 'autoplay') {
                            const slot = wpEntity.sound?.slot(slotId);
                            if (slot && !slot.isPlaying) {
                                slot.play();
                                audioData.playing = true;
                            }
                        }
                    });
                    // Custom mesh audio
                    customMeshEntities.forEach((meshData) => {
                        const { config: meshConfig, audioSlotId, entity: meshEntity } = meshData;
                        if (!audioSlotId) return;
                        const triggerMode = meshConfig.interaction?.audioTriggerMode || meshConfig.interaction?.activationMode || 'click';
                        if (triggerMode === 'autoplay') {
                            const slot = meshEntity.sound?.slot(audioSlotId);
                            if (slot && !slot.isPlaying) {
                                slot.play();
                                meshData.audioPlaying = true;
                            }
                        }
                    });
                    if (uiElements.tapForAudio) {
                        uiElements.tapForAudio.classList.remove('visible');
                        setTimeout(() => { if (uiElements.tapForAudio) uiElements.tapForAudio.style.display = 'none'; }, 500);
                    }
                    container.removeEventListener('click', dismissTapOverlay);
                    container.removeEventListener('touchstart', dismissTapOverlay);
                };
                container.addEventListener('click', dismissTapOverlay);
                container.addEventListener('touchstart', dismissTapOverlay);
            }
        }
        // Apply the default camera mode now that the scene is loaded.
        // This ensures explore mode properly initializes CameraControls with
        // syncFromPose to the first waypoint position. Without this, loading
        // directly into explore mode would leave CameraControls uninitialized.
        // In editor mode, skip this — the editor manages its own camera mode via
        // setCameraMode('explore') in onSceneReady. Running this here would race
        // with (and overwrite) the editor's mode, causing intermittent bugs like
        // WASD working in tour mode or minified waypoint paths.
        if (!isEditorMode) {
            setCameraMode(defaultMode);
            if (viewerOptions.__resumeFpvAfterPortal) {
                enterFpvMode();
            }
        }
        // For frame-sequence scenes, immediately override the orbit focus.
        // setCameraMode('explore') → setMode('orbit') internally calls
        // syncFromCamera() without a target, placing the orbit center 10 units
        // in front of the camera (behind the content). This makes orbit feel
        // inverted. Fix by re-syncing with the correct target right away.
        // The async pickers are already guarded to skip for frame sequences.
        if (frameSequencePlayer && cameraControls) {
            cameraControls.syncFromCamera(new pc.Vec3(0, 1, 0));
        }

        // Connect UI to viewer controls
        if (showUI) {
            connectUIToViewer(uiElements, {
                nextWaypoint,
                prevWaypoint,
                play,
                pause,
                isPlaying: () => isPlaying,
                getCurrentWaypointIndex: () => currentWaypointIndex,
                getWaypointCount: () => config.waypoints?.length || 0,
                getWaypoints: () => config.waypoints || [],
                setCameraMode,
                getCameraMode: () => currentCameraMode,
                shareCurrentView,
                setPerformanceMode,
                getPerformanceMode: () => performanceMode,
                on: (event, callback) => events.on(event, callback)
            }, defaultMode, currentButtonLabels, { container, hideProgressText: uiOpts.hideProgressText });
            // Wire up scene menu click handlers for portal navigation
            setupSceneMenuClickHandlers(uiElements, (portalId) => {
                const portal = config.portals?.find(p => p.id === portalId);
                if (portal) handlePortalNavigation(portal);
            }, (url) => {
                // Splat-swap link: swap the displayed splat IN PLACE (scene + menu stay
                // mounted). Use goToSplat exclusively — NOT handlePortalNavigation, which
                // would tear down the container and navigate away.
                if (url) void instance.goToSplat(url);
            }, (linkId) => {
                // A custom item's cutaway runs alongside its link, media popup or swap.
                runClickCutaway((config.uiOptions?.sceneMenuLinks ?? []).find((l) => l.id === linkId)?.cutaway);
            });
            // Wire up mute button
            if (uiElements.muteButton) {
                uiElements.muteButton.addEventListener('click', () => { applyMuteToggle(); });
                // Initialize the icon from globalMuted (which may have been carried in from a prior
                // scene via the cross-scene bed), so a teleport into a muted session shows the muted
                // icon instead of resetting to unmuted.
                const initUnmutedIcon = uiElements.muteButton.querySelector('.storysplat-unmuted-icon') as HTMLElement | null;
                const initMutedIcon = uiElements.muteButton.querySelector('.storysplat-muted-icon') as HTMLElement | null;
                if (initUnmutedIcon) initUnmutedIcon.style.display = globalMuted ? 'none' : '';
                if (initMutedIcon) initMutedIcon.style.display = globalMuted ? '' : 'none';
                uiElements.muteButton.setAttribute('aria-label', globalMuted
                    ? getButtonLabel(currentButtonLabels, 'unmute')
                    : getButtonLabel(currentButtonLabels, 'mute'));
            }
            // Wire up relighting toggle button
            if (uiElements.relightingButton && relightingScript) {
                const startOn = config.splatRelighting?.viewerDefaultOn ?? false;
                console.log('[StorySplat Viewer] Relighting toggle button wired up, startOn:', startOn);
                let relightingOn = startOn;
                let relightFadeTarget = startOn ? 1.0 : 0.0;
                // Set initial button visual state
                uiElements.relightingButton.classList.toggle('active', startOn);
                let relightFadeAnim: number | null = null;
                uiElements.relightingButton.addEventListener('click', () => {
                    // Block toggle while reveal intro is still running
                    if (revealScript && revealScript.enabled) {
                        console.log('[StorySplat Viewer] Relighting toggle blocked — reveal still active');
                        return;
                    }
                    relightingOn = !relightingOn;
                    relightFadeTarget = relightingOn ? 1.0 : 0.0;
                    console.log('[StorySplat Viewer] Relighting toggle:', relightingOn, 'scripts:', allRelightingScripts.size);
                    uiElements.relightingButton!.classList.toggle('active', relightingOn);
                    // Smooth fade animation — applies to ALL relighting scripts
                    if (relightFadeAnim !== null) cancelAnimationFrame(relightFadeAnim);
                    const animateFade = () => {
                        if (allRelightingScripts.size === 0) return;
                        // Use primary script to drive the animation timing
                        const current = relightingScript ? relightingScript.relightFade : relightFadeTarget;
                        const diff = relightFadeTarget - current;
                        if (Math.abs(diff) < 0.01) {
                            for (const s of allRelightingScripts) s.setRelightFade(relightFadeTarget);
                            relightFadeAnim = null;
                            return;
                        }
                        const newFade = current + diff * 0.15;
                        for (const s of allRelightingScripts) s.setRelightFade(newFade);
                        relightFadeAnim = requestAnimationFrame(animateFade);
                    };
                    relightFadeAnim = requestAnimationFrame(animateFade);
                });
            }
            // Wire up fisheye ("tiny planet") toggle button
            if (uiElements.fisheyeButton) {
                uiElements.fisheyeButton.addEventListener('click', () => {
                    const on = fisheyeCurrentAmount > 0;
                    applyFisheye(on ? 0 : 0.95);
                    uiElements.fisheyeButton!.classList.toggle('active', !on);
                });
            }
            // Cutaway toggle + right-edge plane slider
            if (uiElements.cutawayButton && uiElements.cutawaySlider && uiElements.cutawayPanel) {
                const { cutawayButton, cutawaySlider, cutawayPanel, cutawayFlipButton, cutawayLabel } = uiElements;
                syncCutawayUI = () => {
                    cutawayButton.classList.toggle('active', cutawayActive);
                    cutawayButton.setAttribute('aria-pressed', String(cutawayActive));
                    cutawayPanel.classList.toggle('visible', cutawayActive);
                    const level = Math.round(cutawayLevel * 1000);
                    if (Number(cutawaySlider.value) !== level) cutawaySlider.value = String(level);
                    if (cutawayLabel) cutawayLabel.textContent = `${Math.round(cutawayLevel * 100)}%`;
                };
                cutawayButton.addEventListener('click', () => setCutaway({ enabled: !cutawayActive }));
                cutawaySlider.addEventListener('input', () => setCutaway({ level: Number(cutawaySlider.value) / 1000 }));
                cutawayFlipButton?.addEventListener('click', () => {
                    setCutaway({ direction: cutawayDirection === 'above' ? 'below' : 'above' });
                });
                // Keep slider drags from reaching the camera controls behind the panel.
                for (const type of ['pointerdown', 'touchstart', 'wheel'] as const) {
                    cutawayPanel.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
                }
            }
            // Photo mode: camera button enters, the bar's buttons take the photo or leave.
            if (uiElements.photoButton && uiElements.photoBar && uiElements.photoTakeButton && uiElements.photoExitButton) {
                const { photoButton, photoBar, photoTakeButton, photoExitButton } = uiElements;
                syncPhotoUI = () => {
                    photoButton.setAttribute('aria-pressed', String(photoModeActive));
                };
                photoButton.addEventListener('click', () => setPhotoMode(true));
                photoExitButton.addEventListener('click', () => setPhotoMode(false));
                photoTakeButton.addEventListener('click', async () => {
                    if (photoTakeButton.hasAttribute('disabled')) return;
                    photoTakeButton.setAttribute('disabled', 'true');
                    try {
                        await takePhoto();
                        // Shutter flash after the frame is captured, so it never lands in the image.
                        const frame = container.querySelector('.storysplat-photo-frame');
                        if (frame) {
                            frame.classList.remove('flash');
                            void (frame as HTMLElement).offsetWidth;
                            frame.classList.add('flash');
                        }
                    } catch (error) {
                        console.warn('[StorySplat Viewer] Photo capture failed:', error);
                        showViewerToast(container, getButtonLabel(currentButtonLabels, 'shareFailed'));
                    } finally {
                        photoTakeButton.removeAttribute('disabled');
                    }
                });
                // Keep bar interaction from steering the camera behind it.
                for (const type of ['pointerdown', 'touchstart', 'wheel'] as const) {
                    photoBar.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
                }
                document.addEventListener('keydown', (event) => {
                    if (event.key === 'Escape' && photoModeActive) setPhotoMode(false);
                });
            }
            // Stereo 3D: the toolbar button opens the format menu.
            if (uiElements.stereoButton && uiElements.stereoMenu) {
                const { stereoButton, stereoMenu } = uiElements;
                const setStereoMenuOpen = (open: boolean) => {
                    if (open) uiElements.exclusiveTools?.closeOthers('stereo');
                    stereoMenu.classList.toggle('open', open);
                    stereoButton.setAttribute('aria-expanded', String(open));
                };
                uiElements.exclusiveTools?.register('stereo', () => setStereoMenuOpen(false));
                stereoButton.addEventListener('click', (event) => {
                    event.stopPropagation();
                    setStereoMenuOpen(!stereoMenu.classList.contains('open'));
                });
                stereoMenu.addEventListener('click', (event) => {
                    event.stopPropagation();
                    const item = (event.target as HTMLElement).closest<HTMLElement>('.storysplat-stereo-option');
                    if (!item) return;
                    if (item.dataset.stereoSwap) {
                        setStereoOptions({ swapEyes: !stereoSwapEyes });
                        return;
                    }
                    setStereoMenuOpen(false);
                    const value = item.dataset.stereoFormat;
                    setStereoMode(value && isStereoFormat(value) ? value : null);
                });
                // Keep menu interaction from steering the camera behind it.
                for (const type of ['pointerdown', 'touchstart', 'wheel'] as const) {
                    stereoMenu.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
                }
                const closeStereoMenuOutside = (event: MouseEvent) => {
                    if (!stereoMenu.classList.contains('open')) return;
                    const target = event.target as Node;
                    if (stereoMenu.contains(target) || stereoButton.contains(target)) return;
                    setStereoMenuOpen(false);
                };
                document.addEventListener('click', closeStereoMenuOutside);
                const previousUiCleanup = uiElements._cleanup;
                uiElements._cleanup = () => {
                    if (previousUiCleanup) previousUiCleanup();
                    document.removeEventListener('click', closeStereoMenuOutside);
                };
            }
            {
                // Button + menu state, whichever way stereo was switched (menu, host page, API).
                const { stereoButton, stereoMenu } = uiElements;
                syncStereoUI = () => {
                    stereoButton?.classList.toggle('active', stereoFormat !== null);
                    stereoMenu?.querySelectorAll<HTMLElement>('[data-stereo-format]').forEach((item) => {
                        item.setAttribute('aria-checked', String(item.dataset.stereoFormat === (stereoFormat ?? 'off')));
                    });
                    stereoMenu?.querySelector('[data-stereo-swap]')?.setAttribute('aria-checked', String(stereoSwapEyes));
                };
                // Escape leaves a split format, and so does leaving the fullscreen that stereo
                // itself asked for.
                const onStereoKeydown = (event: KeyboardEvent) => {
                    if (event.key === 'Escape' && isStereoSplit()) setStereoMode(null);
                };
                const onStereoFullscreenChange = () => {
                    if (stereoFullscreenElement() || !stereoOwnsFullscreen) return;
                    stereoOwnsFullscreen = false;
                    if (isStereoSplit()) setStereoMode(null);
                };
                document.addEventListener('keydown', onStereoKeydown);
                document.addEventListener('fullscreenchange', onStereoFullscreenChange);
                document.addEventListener('webkitfullscreenchange', onStereoFullscreenChange);
                const previousUiCleanup = uiElements._cleanup;
                uiElements._cleanup = () => {
                    if (previousUiCleanup) previousUiCleanup();
                    document.removeEventListener('keydown', onStereoKeydown);
                    document.removeEventListener('fullscreenchange', onStereoFullscreenChange);
                    document.removeEventListener('webkitfullscreenchange', onStereoFullscreenChange);
                };
            }
            // Wire up hotspot popup close button to stop audio/media
            const popupCloseBtn = container.querySelector('.storysplat-hotspot-popup-close');
            if (popupCloseBtn) {
                popupCloseBtn.addEventListener('click', () => {
                    stopAllHotspotAudio();
                    stopHotspotPopupMedia(container);
                });
            }
            // Setup waypoint list dropdown click handlers (tour waypoints, then extra waypoints)
            if (uiElements.waypointListContainer) {
                setupWaypointListClickHandlers(uiElements, (index) => {
                    console.log('[StorySplat Viewer] Waypoint list: jumping to waypoint', index);
                    goToTourWaypointFromUI(index);
                }, (id) => {
                    goToExtraWaypointById(id);
                });
                // Update active waypoint in the list when waypoint changes
                events.on('waypointChange', ({ index }: {
                    index: number;
                }) => {
                    updateWaypointListActive(uiElements, index);
                });
                // Set initial active state
                updateWaypointListActive(uiElements, 0);
            }
            // Emit initial progress to update UI
            events.emit('progressUpdate', { progress: currentProgress, index: currentWaypointIndex });
            // Emit initial waypoint change to show first waypoint info
            if (config.waypoints && config.waypoints.length > 0) {
                events.emit('waypointChange', {
                    index: 0,
                    waypoint: config.waypoints[0],
                    prevIndex: -1
                });
            }
        }
        // Start autoplay if enabled via options OR via scene config
        if (shouldRequestInitialPlayback(options.autoPlay, config.autoPlay, scene.frameSequence?.autoplay)) {
            requestInitialAutoplay();
        }
    }).catch(err => {
        console.error('[StorySplat Viewer] Failed to initialize:', err);
        initialPlaybackGate.cancelPendingPlayback();
        // Hide preloader even on error
        if (uiElements.preloader) {
            void hidePreloader(uiElements.preloader);
        }
        events.emit('error', err);
    });
    // Handle resize
    const handleResize = () => {
        app.resizeCanvas();
        renderScheduler.requestFrame('resize');
    };
    // Pause render + audio when the page is hidden (tab switch, OS sleep, mobile
    // background). Resume on visibility return. Avoids burning battery and audio
    // CPU on a tab no one is looking at.
    let wasPlayingBeforeHidden = false;
    const audioContextsSuspendedForVisibility = new Set<AudioContext>();
    const audioContextVisibilitySuspendPending = new Set<AudioContext>();
    const audioContextVisibilityResumeRequested = new Set<AudioContext>();
    const forEachVisibilityAudioContext = (callback: (ctx: AudioContext) => void) => {
        const contexts = new Set<AudioContext>();
        const playCanvasAudioContext = (app.systems.sound as { manager?: { context?: AudioContext } } | undefined)
            ?.manager?.context;
        if (playCanvasAudioContext) {
            contexts.add(playCanvasAudioContext);
        }
        allAudioContexts.forEach(ctx => contexts.add(ctx));
        contexts.forEach(callback);
    };
    const resumeAudioContextSuspendedForVisibility = (ctx: AudioContext) => {
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {}).finally(() => {
                audioContextsSuspendedForVisibility.delete(ctx);
                audioContextVisibilityResumeRequested.delete(ctx);
            });
            return;
        }
        if (audioContextVisibilitySuspendPending.has(ctx)) {
            audioContextVisibilityResumeRequested.add(ctx);
            return;
        }
        if (ctx.state === 'closed' || !audioContextVisibilitySuspendPending.has(ctx)) {
            audioContextsSuspendedForVisibility.delete(ctx);
            audioContextVisibilityResumeRequested.delete(ctx);
        }
    };
    const suspendAudioContextForVisibility = (ctx: AudioContext, autoResumeWhenVisible = true) => {
        if (ctx.state !== 'running') return;
        audioContextsSuspendedForVisibility.add(ctx);
        audioContextVisibilitySuspendPending.add(ctx);
        ctx.suspend().then(() => {
            const resumeRequested = audioContextVisibilityResumeRequested.has(ctx);
            if (
                audioContextsSuspendedForVisibility.has(ctx) &&
                (resumeRequested || (autoResumeWhenVisible && !document.hidden))
            ) {
                resumeAudioContextSuspendedForVisibility(ctx);
            }
        }).catch(() => {
            audioContextsSuspendedForVisibility.delete(ctx);
            audioContextVisibilityResumeRequested.delete(ctx);
        }).finally(() => {
            audioContextVisibilitySuspendPending.delete(ctx);
            if (ctx.state === 'closed') {
                audioContextsSuspendedForVisibility.delete(ctx);
                audioContextVisibilityResumeRequested.delete(ctx);
            }
        });
    };
    const resumeAfterVisibilityHidden = () => {
        audioContextsSuspendedForVisibility.forEach(ctx => {
            resumeAudioContextSuspendedForVisibility(ctx);
        });
        if (wasPlayingBeforeHidden) {
            play();
            wasPlayingBeforeHidden = false;
        }
        app.renderNextFrame = true;
    };
    const handleVisibilityChange = () => {
        if (document.hidden) {
            wasPlayingBeforeHidden = wasPlayingBeforeHidden || isPlaying;
            if (isPlaying) pause();
            forEachVisibilityAudioContext(ctx => {
                suspendAudioContextForVisibility(ctx);
            });
            return;
        }
        resumeAfterVisibilityHidden();
        renderScheduler.requestFrame('visible');
    };
    const handlePageHide = () => {
        wasPlayingBeforeHidden = wasPlayingBeforeHidden || isPlaying;
        if (isPlaying) pause();
        forEachVisibilityAudioContext(ctx => {
            suspendAudioContextForVisibility(ctx, false);
        });
    };
    const handlePageShow = () => {
        if (!document.hidden) {
            resumeAfterVisibilityHidden();
        }
    };
    // ---------------------------------------------------------------------------------
    // On-demand rendering: decide per frame, right before the engine's render check.
    // ---------------------------------------------------------------------------------
    const onDemandInputEvents = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'wheel', 'touchstart', 'touchmove', 'touchend', 'contextmenu'] as const;
    const onDemandKeyEvents = ['keydown', 'keyup'] as const;
    const noteRenderInput = () => renderScheduler.noteInput();
    let onDemandInputListenersAttached = false;
    const attachOnDemandInputListeners = () => {
        if (onDemandInputListenersAttached) return;
        onDemandInputListenersAttached = true;
        for (const type of onDemandInputEvents) container.addEventListener(type, noteRenderInput, { capture: true, passive: true });
        for (const type of onDemandKeyEvents) window.addEventListener(type, noteRenderInput, { capture: true, passive: true });
    };
    const detachOnDemandInputListeners = () => {
        if (!onDemandInputListenersAttached) return;
        onDemandInputListenersAttached = false;
        for (const type of onDemandInputEvents) container.removeEventListener(type, noteRenderInput, { capture: true } as EventListenerOptions);
        for (const type of onDemandKeyEvents) window.removeEventListener(type, noteRenderInput, { capture: true } as EventListenerOptions);
    };
    const gamepadInputActive = (): boolean => {
        try {
            const pads = navigator.getGamepads?.();
            if (!pads) return false;
            for (const pad of pads) {
                if (!pad) continue;
                if (pad.axes.some((a) => Math.abs(a) > 0.15)) return true;
                if (pad.buttons.some((b) => b.pressed)) return true;
            }
        } catch { /* gamepad API unavailable */ }
        return false;
    };
    const onDemandFrameRender = () => {
        if (!onDemandRenderingActive || isDestroyed) return;
        // XR and 8th Wall AR own the render loop (they set autoRender themselves).
        if (app.xr?.active || preArAutoRender !== null) { app.renderNextFrame = true; return; }
        if (app.autoRender) app.autoRender = false; // re-arm after an XR/AR session restored it
        const signals: string[] = [];
        try {
            const device = app.graphicsDevice;
            if (cameraPoseTracker.changed(camera, device.width, device.height)) signals.push('camera-moved');
            const graph = sceneGraphNeedsRender(app.root as unknown as Parameters<typeof sceneGraphNeedsRender>[0]);
            if (graph) signals.push(graph);
            if (gamepadInputActive()) renderScheduler.noteInput();
        } catch (err) {
            signals.push('probe-error'); // never let a probe failure freeze the picture
            console.warn('[Render] on-demand probe failed, rendering:', err);
        }
        if (renderScheduler.decide(signals).render) app.renderNextFrame = true;
    };
    const onGsplatFrameRequest = () => renderScheduler.requestFrame('gsplat');
    const onGsplatComponentChange = () => renderScheduler.requestFrame('gsplat-component');
    let onDemandSourceRemovers: Array<() => void> = [];
    function startOnDemandRendering(): void {
        if (onDemandRenderingActive || isDestroyed || !onDemandRenderingArmed || !onDemandRenderingEnabled) return;
        onDemandContinuousReasons = continuousRenderReasons(config, scene);
        if (onDemandContinuousReasons.length > 0 && !onDemandRenderingForced) {
            console.log('[Render] On-demand rendering off for this scene (animated features):', onDemandContinuousReasons.join(', '));
            return;
        }
        onDemandRenderingActive = true;
        cameraPoseTracker.reset();
        onDemandSourceRemovers = [
            renderScheduler.addSource({ name: 'tour-playing', active: () => isPlaying }),
            // Opacity/glow animate after the click and camera damping have stopped. The
            // source's grace also renders the final frame that disables the ring.
            renderScheduler.addSource({ name: 'reticle', active: () => reticleEntity.enabled }),
            renderScheduler.addSource({ name: 'lod-streaming', active: () => readLodPending() > 0 }),
            renderScheduler.addSource({ name: 'panorama-transition', active: () => panoramaModeTransitioning }),
            renderScheduler.addSource({ name: 'compare-mode', active: () => compareSplitView != null }),
            renderScheduler.addSource({ name: 'perf-hud', active: () => perfHud.isVisible }),
            renderScheduler.addSource({ name: 'reveal-effect', active: () => !!revealScript?.enabled }),
            // Animated GIF textures repaint without moving anything. `isGifUrl` also accepts
            // `image/gif` and `format=gif` URLs, which the config gate's extension test misses,
            // so ask the live textures instead of re-deriving the rule here.
            renderScheduler.addSource({ name: 'animated-gif', active: () => animatedGifs.some(gif => gif.playing) }),
        ];
        attachOnDemandInputListeners();
        const gsplatSystem = app.systems.gsplat as unknown as { on?: (e: string, fn: () => void) => void } | undefined;
        gsplatSystem?.on?.('frame:request', onGsplatFrameRequest);
        gsplatSystem?.on?.('add', onGsplatComponentChange);
        gsplatSystem?.on?.('remove', onGsplatComponentChange);
        app.on('framerender', onDemandFrameRender);
        // AR restores this saved mode on exit. Keep it in sync if on-demand is
        // enabled (including delayed initial arming) during an active AR session.
        if (preArAutoRender !== null) preArAutoRender = false;
        app.autoRender = !!app.xr?.active || preArAutoRender !== null;
        app.renderNextFrame = true;
        console.log('[Render] On-demand rendering active (grace 400 ms, heartbeat 1 s)');
    }
    function stopOnDemandRendering(): void {
        if (!onDemandRenderingActive) return;
        onDemandRenderingActive = false;
        for (const remove of onDemandSourceRemovers) remove();
        onDemandSourceRemovers = [];
        detachOnDemandInputListeners();
        const gsplatSystem = app.systems.gsplat as unknown as { off?: (e: string, fn: () => void) => void } | undefined;
        gsplatSystem?.off?.('frame:request', onGsplatFrameRequest);
        gsplatSystem?.off?.('add', onGsplatComponentChange);
        gsplatSystem?.off?.('remove', onGsplatComponentChange);
        app.off('framerender', onDemandFrameRender);
        // Do not let AR restore the old on-demand mode after its scheduler has
        // been removed; there would be nothing left to request subsequent frames.
        if (preArAutoRender !== null) preArAutoRender = true;
        app.autoRender = true;
        app.renderNextFrame = true;
        console.log('[Render] On-demand rendering stopped; rendering every frame');
    }
    function teardownOnDemandRendering(): void {
        try { stopOnDemandRendering(); } catch { /* app may already be destroyed */ }
    }
    const setOnDemandRendering = (enabled: boolean): void => {
        onDemandRenderingEnabled = !!enabled && !isEditorMode;
        if (onDemandRenderingEnabled) startOnDemandRendering(); else stopOnDemandRendering();
    };
    window.addEventListener('resize', handleResize);
    // Default: pause render+audio when tab is hidden. Creators can opt out
    // (e.g. kiosk displays) via scene.uiOptions.pauseOnHide = false.
    const pauseOnHide = scene.uiOptions?.pauseOnHide !== false;
    if (pauseOnHide) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('pageshow', handlePageShow);
    }
    // =====================================================
    // EDITOR MUTATION API (only exposed when editor: true)
    // =====================================================
    // Track editor hotspot/light entities by ID for mutation
    const editorHotspotMap = new Map<string, pc.Entity>();
    const editorLightMap = new Map<string, pc.Entity>();
    const editorParticleMap = new Map<string, pc.Entity>();
    const editorCustomMeshMap = new Map<string, pc.Entity>();
    const editorPortalMap = new Map<string, pc.Entity>();
    const editorCollisionMeshMap = new Map<string, pc.Entity>();
    // Models used as collision, loaded once per URL: the editor rebuilds every collider whenever
    // the list changes, and a scene-sized GLB shouldn't download again each time.
    const editorColliderModels = new Map<string, Promise<pc.Asset | null>>();
    const editorColliderMaterials = new Map<pc.Entity, pc.StandardMaterial>();
    /**
     * Load an uploaded model into a live collision entity: shown (translucent green), then given its
     * precise triangle collider, and only then handed to `onReady` to register for collision.
     */
    const loadEditorCustomCollider = (entity: pc.Entity, id: string, meshConfig: CollisionMeshConfig, onReady: () => void): void => {
        const url = meshConfig.customMeshUrl || meshConfig.modelUrl;
        if (!url) return;
        const fileName = colliderFileName(url, meshConfig.name);
        if (!isLoadableColliderFile(fileName)) {
            console.warn(`[Editor] Collision mesh "${fileName || url}" is not a GLB / glTF, so it has no collision. Convert it to GLB.`);
            return;
        }
        let model = editorColliderModels.get(url);
        if (!model) {
            model = new Promise<pc.Asset | null>((resolve) => {
                const asset = new pc.Asset(`collision-custom-${id}`, 'container', url.startsWith('blob:') ? { url, filename: fileName } : { url });
                asset.ready(() => resolve(asset));
                asset.once('error', (err) => {
                    console.error('[Editor] Failed to load collision mesh:', url, err);
                    app.assets.remove(asset);
                    editorColliderModels.delete(url);
                    resolve(null);
                });
                app.assets.add(asset);
                app.assets.load(asset);
            });
            editorColliderModels.set(url, model);
        }
        void model.then((asset) => {
            // Removed, or rebuilt under the same id, while loading.
            if (!asset || isDestroyed || editorCollisionMeshMap.get(id) !== entity) return;
            addContainerMeshes(entity, asset.resource as pc.ContainerResource);
            // Translucent, unlike the primitives: a model collider is usually scene-sized (a street,
            // a room), and the scan it is aligned to has to show through it.
            const material = new pc.StandardMaterial();
            material.diffuse = new pc.Color(0, 0.8, 0);
            material.opacity = 0.35;
            material.blendType = pc.BLEND_NORMAL;
            material.depthWrite = false;
            material.cull = pc.CULLFACE_NONE;
            material.useLighting = false;
            material.update();
            for (const render of entity.findComponents('render') as pc.RenderComponent[]) {
                for (const mi of render.meshInstances) mi.material = material;
            }
            editorColliderMaterials.set(entity, material);
            disableHelperShadows(entity);
            renderScheduler.requestFrame('collision-mesh');
            if (!attachTriangleCollider(entity)) {
                console.warn('[Editor] Collision mesh has no triangles, so it has no collision:', url);
                return;
            }
            onReady();
        });
    };
    const editorMirrorPlaneMap = new Map<string, pc.Entity>();
    const editorWaterPlaneMap = new Map<string, pc.Entity>();
    const editorCloudLayerMap = new Map<string, pc.Entity>();
    // Index existing hotspot entities by ID if in editor mode
    if (isEditorMode) {
        hotspotEntities.forEach((entity) => {
            const id = entity.hotspotData?.id;
            if (id)
                editorHotspotMap.set(id, entity);
        });
        lightEntities.forEach((entity, index: number) => {
            const lightConfig = config.lights?.[index];
            const id = lightConfig?.id || lightConfig?.name || `light-${index}`;
            editorLightMap.set(id, entity);
        });
        particleEntities.forEach((entity, id) => {
            editorParticleMap.set(id, entity);
        });
        portalEntities.forEach((entity) => {
            const id = entity.portalData?.id;
            if (id)
                editorPortalMap.set(id, entity);
        });
    }

    // =====================================================
    // ENTITY ANIMATION SYSTEM
    // =====================================================
    let entityAnimationSystem: EntityAnimationSystem | null = null;
    const entityLookup = (entityId: string, entityType: string): pc.Entity | null => {
        switch (entityType) {
            case 'hotspot':
                return editorHotspotMap.get(entityId) ||
                    hotspotEntities.find(e => e.hotspotData?.id === entityId) || null;
            case 'portal':
                return editorPortalMap.get(entityId) ||
                    portalEntities.find(e => e.portalData?.id === entityId) || null;
            case 'light':
                return editorLightMap.get(entityId) ||
                    runtimeLightMap.get(entityId) || null;
            case 'particle': {
                // particleEntities keys are sanitized (non-alphanumeric → '_')
                const safeParticleId = entityId.replace(/[^a-zA-Z0-9]/g, '_');
                return particleEntities.get(safeParticleId) || particleEntities.get(entityId) || null;
            }
            case 'audioEmitter':
                return audioEmitterMap.get(entityId)?.entity || null;
            case 'customMesh': {
                const meshData = customMeshEntities.get(entityId);
                return meshData?.entity || null;
            }
            case 'mirrorPlane':
                return editorMirrorPlaneMap.get(entityId) ||
                    mirrorPlaneEntities.find(e => e.name === entityId) || null;
            case 'waterPlane':
                return editorWaterPlaneMap.get(entityId) || waterSystem.entity(entityId);
            case 'cloudLayer':
                return editorCloudLayerMap.get(entityId) || cloudSystem.entity(entityId);
            case 'htmlMesh': {
                const manager = (app as ViewerRuntimeApp).__htmlMeshManager;
                return (manager?.getAllMeshes?.().get(entityId) as { entity?: pc.Entity } | undefined)?.entity ?? null;
            }
            default:
                return null;
        }
    };
    // Scene-wide animation targets — no pc.Entity. Fog mutations are applied directly to
    // app.scene.fog; weather mutates fields on the GsplatWeather instance, which reads them fresh
    // each frame via setParameter(). Focus/haze, post effects, relighting, sky/background and
    // audio-reactive keep the animated values as overrides on top of the config, so a rebuild
    // (region triggers, a late-created effect) keeps them, and every write is a uniform/property
    // set — never a shader rebuild. The editor clears a target's overrides when the creator edits
    // that config, so the panel's value shows until the timeline is scrubbed again.
    const sceneAnimationOverrides: Record<'postProcessing' | 'relighting' | 'environment' | 'audioReactive', Map<string, number>> = {
        postProcessing: new Map(),
        relighting: new Map(),
        environment: new Map(),
        audioReactive: new Map(),
    };
    const storeOverrides = (target: Map<string, number>, values: Map<string, number>) => {
        values.forEach((v, k) => target.set(k, v));
    };
    /** Smallest animated value for effects that switch shader variants (or rebuild passes) at 0. */
    const ABOVE_ZERO = 1e-4;
    function applyPostProcessingAnimation(): void {
        const o = sceneAnimationOverrides.postProcessing;
        const pp = config.postProcessing;
        if (!pp || o.size === 0) return;
        if (pp.exposure?.enabled && o.has('post.exposure')) app.scene.exposure = Math.max(0, o.get('post.exposure')!);
        const frame = cameraFrame;
        if (!frame || !frame.enabled) return;
        const has = (k: string) => o.has(k);
        const get = (k: string) => o.get(k)!;
        let touched = false;
        if (pp.grading?.enabled) {
            if (has('post.grading.brightness')) { frame.grading.brightness = Math.max(0, get('post.grading.brightness')); touched = true; }
            if (has('post.grading.contrast')) { frame.grading.contrast = Math.max(0, get('post.grading.contrast')); touched = true; }
            if (has('post.grading.saturation')) { frame.grading.saturation = Math.max(0, get('post.grading.saturation')); touched = true; }
            if (has('post.grading.tint.r') || has('post.grading.tint.g') || has('post.grading.tint.b')) {
                const t = frame.grading.tint;
                t.set(o.get('post.grading.tint.r') ?? t.r, o.get('post.grading.tint.g') ?? t.g, o.get('post.grading.tint.b') ?? t.b);
                touched = true;
            }
        }
        if (pp.colorEnhance?.enabled) {
            for (const key of ['shadows', 'highlights', 'midtones', 'vibrance', 'dehaze'] as const) {
                const prop = `post.colorEnhance.${key}`;
                if (has(prop)) { frame.colorEnhance[key] = get(prop); touched = true; }
            }
        }
        if (pp.bloom?.enabled && has('post.bloom.intensity')) {
            // bloom > 0 is what keeps the bloom pass alive; crossing 0 would rebuild the passes.
            frame.bloom.intensity = Math.max(ABOVE_ZERO, get('post.bloom.intensity'));
            touched = true;
        }
        if (pp.vignette?.enabled) {
            if (has('post.vignette.intensity')) { frame.vignette.intensity = Math.max(ABOVE_ZERO, get('post.vignette.intensity')); touched = true; }
            if (has('post.vignette.inner')) { frame.vignette.inner = Math.max(0, get('post.vignette.inner')); touched = true; }
            if (has('post.vignette.outer')) { frame.vignette.outer = Math.max(0, get('post.vignette.outer')); touched = true; }
            if (has('post.vignette.curvature')) { frame.vignette.curvature = Math.max(0.01, get('post.vignette.curvature')); touched = true; }
            if (has('post.vignette.color.r') || has('post.vignette.color.g') || has('post.vignette.color.b')) {
                const c = frame.vignette.color;
                c.set(o.get('post.vignette.color.r') ?? c.r, o.get('post.vignette.color.g') ?? c.g, o.get('post.vignette.color.b') ?? c.b);
                touched = true;
            }
        }
        if (pp.fringing?.enabled && has('post.fringing.intensity')) {
            frame.fringing.intensity = Math.max(ABOVE_ZERO, get('post.fringing.intensity'));
            touched = true;
        }
        if (pp.sharpness?.enabled && has('post.sharpness')) {
            frame.rendering.sharpness = Math.min(1, Math.max(ABOVE_ZERO, get('post.sharpness')));
            touched = true;
        }
        // CameraFrame.update() only rebuilds passes when a feature switches on/off; value
        // changes are compose-pass uniforms.
        if (touched) frame.update();
    }
    function readPostProcessingValues(): Record<string, number> {
        const pp = config.postProcessing ?? {};
        const rgb = (hex: string | undefined, fallback: string) => hexToAnimationRgb(hex) ?? hexToAnimationRgb(fallback)!;
        const grade = rgb(pp.grading?.tint, '#ffffff');
        const vig = rgb(pp.vignette?.color, '#000000');
        const values: Record<string, number> = {
            'post.exposure': pp.exposure?.value ?? 1,
            'post.grading.brightness': pp.grading?.brightness ?? 1,
            'post.grading.contrast': pp.grading?.contrast ?? 1,
            'post.grading.saturation': pp.grading?.saturation ?? 1,
            'post.grading.tint.r': grade.r, 'post.grading.tint.g': grade.g, 'post.grading.tint.b': grade.b,
            'post.colorEnhance.shadows': pp.colorEnhance?.shadows ?? 0,
            'post.colorEnhance.highlights': pp.colorEnhance?.highlights ?? 0,
            'post.colorEnhance.midtones': pp.colorEnhance?.midtones ?? 0,
            'post.colorEnhance.vibrance': pp.colorEnhance?.vibrance ?? 0,
            'post.colorEnhance.dehaze': pp.colorEnhance?.dehaze ?? 0,
            'post.bloom.intensity': pp.bloom?.intensity ?? 0.02,
            'post.vignette.intensity': pp.vignette?.intensity ?? 0.4,
            'post.vignette.inner': pp.vignette?.inner ?? 0.5,
            'post.vignette.outer': pp.vignette?.outer ?? 1,
            'post.vignette.curvature': pp.vignette?.curvature ?? 1.5,
            'post.vignette.color.r': vig.r, 'post.vignette.color.g': vig.g, 'post.vignette.color.b': vig.b,
            'post.fringing.intensity': pp.fringing?.intensity ?? 10,
            'post.sharpness': pp.sharpness?.value ?? 0.5,
        };
        sceneAnimationOverrides.postProcessing.forEach((v, k) => { values[k] = v; });
        return values;
    }
    function applyRelightingAnimation(): void {
        const o = sceneAnimationOverrides.relighting;
        if (o.size === 0) return;
        const cfg = config.splatRelighting;
        if (o.has('relighting.ambientIntensity') || o.has('relighting.ambientColor.r') || o.has('relighting.ambientColor.g') || o.has('relighting.ambientColor.b')) {
            const base = hexToAnimationRgb(cfg?.ambientColor) ?? { r: 1, g: 1, b: 1 };
            const rawIntensity = cfg?.ambientIntensity;
            const intensity = Math.max(0, o.get('relighting.ambientIntensity')
                ?? (typeof rawIntensity === 'number' && Number.isFinite(rawIntensity) ? rawIntensity : DEFAULT_RELIGHT_AMBIENT_INTENSITY));
            const r = Math.max(0, o.get('relighting.ambientColor.r') ?? base.r) * intensity;
            const g = Math.max(0, o.get('relighting.ambientColor.g') ?? base.g) * intensity;
            const b = Math.max(0, o.get('relighting.ambientColor.b') ?? base.b) * intensity;
            // Pushed to the shader every frame by the relighting script (uRelightAmbient).
            for (const script of allRelightingScripts) {
                script.ambientR = r;
                script.ambientG = g;
                script.ambientB = b;
            }
            syncMeshAmbient();
        }
        // relighting.shadowIntensity is read by the shadow system every frame (sceneConfig).
        renderScheduler.requestFrame('relighting');
    }
    function readRelightingValues(): Record<string, number> {
        const cfg = config.splatRelighting;
        const c = hexToAnimationRgb(cfg?.ambientColor) ?? { r: 1, g: 1, b: 1 };
        const rawIntensity = cfg?.ambientIntensity;
        const values: Record<string, number> = {
            'relighting.ambientIntensity': typeof rawIntensity === 'number' && Number.isFinite(rawIntensity) ? rawIntensity : DEFAULT_RELIGHT_AMBIENT_INTENSITY,
            'relighting.ambientColor.r': c.r, 'relighting.ambientColor.g': c.g, 'relighting.ambientColor.b': c.b,
            'relighting.shadowIntensity': resolveSceneShadows(cfg, []).strength,
        };
        sceneAnimationOverrides.relighting.forEach((v, k) => { values[k] = v; });
        return values;
    }
    const _animSkyboxQuat = new pc.Quat();
    function applyEnvironmentAnimation(): void {
        const o = sceneAnimationOverrides.environment;
        if (o.has('skybox.rotation')) {
            const degrees = -o.get('skybox.rotation')! * (180 / Math.PI);
            _animSkyboxQuat.setFromEulerAngles(0, degrees, 0);
            app.scene.skyboxRotation = _animSkyboxQuat;
            if (currentSkyboxEntity) currentSkyboxEntity.setEulerAngles(0, degrees, 0);
        }
        if ((o.has('background.color.r') || o.has('background.color.g') || o.has('background.color.b')) && camera.camera) {
            const c = camera.camera.clearColor;
            camera.camera.clearColor = new pc.Color(
                Math.max(0, o.get('background.color.r') ?? c.r),
                Math.max(0, o.get('background.color.g') ?? c.g),
                Math.max(0, o.get('background.color.b') ?? c.b),
                c.a,
            );
        }
        renderScheduler.requestFrame('entity-animation');
    }
    function readEnvironmentValues(): Record<string, number> {
        const values: Record<string, number> = {
            'skybox.rotation': config.skybox?.rotation ?? config.skyboxRotation ?? 0,
            'background.color.r': baseBackgroundColor.r, 'background.color.g': baseBackgroundColor.g, 'background.color.b': baseBackgroundColor.b,
        };
        sceneAnimationOverrides.environment.forEach((v, k) => { values[k] = v; });
        return values;
    }
    function applyAudioReactiveAnimation(): void {
        const o = sceneAnimationOverrides.audioReactive;
        if (!audioReactiveInstance || o.size === 0) return;
        const params: Record<string, number> = {};
        o.forEach((v, k) => { params[k.slice('audioReactive.'.length)] = v; });
        audioReactiveInstance.setAnimatedParams(params);
    }
    function readAudioReactiveValues(base = false): Record<string, number> | null {
        if (!audioReactiveInstance) return null;
        const params = base && effectiveAudioReactiveConfig ? resolveAudioReactiveConfig(effectiveAudioReactiveConfig) : audioReactiveInstance.getParams();
        if (!params) return null;
        const values: Record<string, number> = {};
        for (const key of ['master', 'pulse', 'wave', 'wobble', 'hueShift', 'waveScale', 'waveSpeed', 'hueSpeed'] as const) {
            values[`audioReactive.${key}`] = params[key];
        }
        return values;
    }
    function applyRegionLookAnimation(kind: RegionKind, values: Map<string, number>): void {
        storeOverrides(regionLookOverrides[kind], values);
        writeRegionLook(kind);
        // SplatRegionEffects pushes changed uniform values on its next prerender sync.
        renderScheduler.requestFrame('entity-animation');
    }
    function readRegionLookValues(kind: RegionKind): Record<string, number> {
        const look = resolveHazeLook(regionConfigFor(kind));
        const values: Record<string, number> = { 'haze.color.r': look.color.r, 'haze.color.g': look.color.g, 'haze.color.b': look.color.b, 'haze.amount': look.amount };
        regionLookOverrides[kind].forEach((v, k) => { values[k] = v; });
        return values;
    }
    function applyRegionShapeAnimation(kind: RegionKind, regionId: string, values: Map<string, number>): void {
        let o = regionShapeOverrides[kind].get(regionId);
        if (!o) {
            o = new Map();
            regionShapeOverrides[kind].set(regionId, o);
        }
        storeOverrides(o, values);
        writeRegionLook(kind);
        renderScheduler.requestFrame('entity-animation');
    }
    function readRegionShapeValues(kind: RegionKind, regionId: string): Record<string, number> | null {
        const cfg = regionConfigFor(kind);
        const region = Array.isArray(cfg?.regions) ? cfg!.regions.find((r) => r?.id === regionId) : undefined;
        if (!region) return null;
        const shape = animatedRegionShape(kind, region);
        return {
            'region.position.x': shape.position.x, 'region.position.y': shape.position.y, 'region.position.z': shape.position.z,
            'region.rotation.x': shape.rotation.x, 'region.rotation.y': shape.rotation.y, 'region.rotation.z': shape.rotation.z,
            'region.size.x': shape.size.x, 'region.size.y': shape.size.y, 'region.size.z': shape.size.z,
            'region.feather': shape.feather,
        };
    }
    /** Editor: the creator edited this target's config — show it until the timeline is scrubbed again. */
    function clearSceneAnimationOverrides(kind: 'haze' | keyof typeof sceneAnimationOverrides): void {
        if (kind === 'haze') {
            regionLookOverrides[kind].clear();
            regionShapeOverrides[kind].clear();
        } else sceneAnimationOverrides[kind].clear();
    }
    /**
     * Restore overridden properties to their configured values through the same appliers that
     * animate them: set the overrides to the base values, apply, then drop them.
     */
    function restoreThroughOverrides(target: Map<string, number>, properties: string[], base: Record<string, number> | null, apply: () => void): void {
        for (const p of properties) target.delete(p);
        if (base) {
            for (const p of properties) if (typeof base[p] === 'number') target.set(p, base[p]);
            apply();
            for (const p of properties) target.delete(p);
        }
        renderScheduler.requestFrame('entity-animation');
    }
    const sceneAnimationHandlers: import('./EntityAnimationSystem').SceneAnimationHandlers = {
        fog: {
            reset: () => applyFog(config.postProcessing?.fog),
            apply: (values) => {
                const fog = app.scene.fog as any;
                if (!fog) return;
                if (values.has('fog.density')) fog.density = values.get('fog.density')!;
                if (values.has('fog.start')) fog.start = values.get('fog.start')!;
                if (values.has('fog.end')) fog.end = values.get('fog.end')!;
                const r = values.get('fog.color.r');
                const g = values.get('fog.color.g');
                const b = values.get('fog.color.b');
                if (r !== undefined || g !== undefined || b !== undefined) {
                    fog.color.set(
                        r ?? fog.color.r,
                        g ?? fog.color.g,
                        b ?? fog.color.b
                    );
                }
            },
            readValues: () => {
                const fog = app.scene.fog as any;
                if (!fog) return null;
                return {
                    'fog.density': fog.density ?? 0.05,
                    'fog.start': fog.start ?? 10,
                    'fog.end': fog.end ?? 100,
                    'fog.color.r': fog.color?.r ?? 0.72,
                    'fog.color.g': fog.color?.g ?? 0.77,
                    'fog.color.b': fog.color?.b ?? 0.82,
                };
            }
        },
        weather: {
            reset: () => applyWeather(config.weather),
            apply: (values) => {
                if (!weatherInstance) return;
                let touched = false;
                if (values.has('weather.speed')) { weatherInstance.speed = values.get('weather.speed')!; touched = true; }
                if (values.has('weather.drift')) { weatherInstance.drift = values.get('weather.drift')!; touched = true; }
                if (values.has('weather.opacity')) { weatherInstance.opacity = values.get('weather.opacity')!; touched = true; }
                if (values.has('weather.elongate')) { weatherInstance.elongate = values.get('weather.elongate')!; touched = true; }
                if (values.has('weather.particleMinSize')) { weatherInstance.particleMinSize = values.get('weather.particleMinSize')!; touched = true; }
                if (values.has('weather.particleMaxSize')) { weatherInstance.particleMaxSize = values.get('weather.particleMaxSize')!; touched = true; }
                const r = values.get('weather.color.r');
                const g = values.get('weather.color.g');
                const b = values.get('weather.color.b');
                if (r !== undefined || g !== undefined || b !== undefined) {
                    // In-place mutation — reassigning the array allocates per-frame
                    // when keyframes are evaluated each tick.
                    if (r !== undefined) weatherInstance.color[0] = r;
                    if (g !== undefined) weatherInstance.color[1] = g;
                    if (b !== undefined) weatherInstance.color[2] = b;
                    touched = true;
                }
                // GsplatWeather caches scalar uniforms behind a dirty flag and
                // skips per-frame upload when clean. Direct field mutation
                // bypasses applyOptions(), so we must mark dirty explicitly,
                // otherwise animated values stop reaching the GPU after the
                // initial setup upload.
                if (touched) weatherInstance.markScalarsDirty();
            },
            readValues: () => {
                if (!weatherInstance) return null;
                return {
                    'weather.speed': weatherInstance.speed,
                    'weather.drift': weatherInstance.drift,
                    'weather.opacity': weatherInstance.opacity,
                    'weather.elongate': weatherInstance.elongate,
                    'weather.particleMinSize': weatherInstance.particleMinSize,
                    'weather.particleMaxSize': weatherInstance.particleMaxSize,
                    'weather.color.r': weatherInstance.color[0],
                    'weather.color.g': weatherInstance.color[1],
                    'weather.color.b': weatherInstance.color[2],
                };
            }
        },
        // Scene wind: direction, speed, gustiness, turbulence (uniforms only, never a recompile).
        wind: {
            apply: (values) => { windSystem.writeAnimatable(values); },
            readValues: () => windSystem.readAnimatable(),
            reset: (props) => windSystem.resetAnimatable(props),
        },
        haze: {
            apply: (values) => applyRegionLookAnimation('haze', values),
            readValues: () => readRegionLookValues('haze'),
            reset: (props) => { props.forEach((p) => regionLookOverrides.haze.delete(p)); writeRegionLook('haze'); renderScheduler.requestFrame('entity-animation'); },
        },
        hazeRegion: {
            apply: (values, regionId) => applyRegionShapeAnimation('haze', regionId, values),
            readValues: (regionId) => readRegionShapeValues('haze', regionId),
            reset: (props, regionId) => { props.forEach((p) => regionShapeOverrides.haze.get(regionId)?.delete(p)); writeRegionLook('haze'); renderScheduler.requestFrame('entity-animation'); },
        },
        // One region: its colour, colour strength and the look of the rest while it dims it, and
        // the transform of a region whose only area is a sphere / box. Uniform writes, never a
        // recompile. (Focus region, focus look and annotation layer keyframes migrate here on load.)
        region: regionAnimationHandler(regionsRuntime),
        postProcessing: {
            apply: (values) => {
                storeOverrides(sceneAnimationOverrides.postProcessing, values);
                applyPostProcessingAnimation();
                renderScheduler.requestFrame('entity-animation');
            },
            readValues: () => readPostProcessingValues(),
            reset: (props) => {
                props.forEach((p) => sceneAnimationOverrides.postProcessing.delete(p));
                restoreThroughOverrides(sceneAnimationOverrides.postProcessing, props, readPostProcessingValues(), applyPostProcessingAnimation);
            },
        },
        relighting: {
            apply: (values) => {
                storeOverrides(sceneAnimationOverrides.relighting, values);
                applyRelightingAnimation();
            },
            readValues: () => readRelightingValues(),
            reset: (props) => {
                props.forEach((p) => sceneAnimationOverrides.relighting.delete(p));
                restoreThroughOverrides(sceneAnimationOverrides.relighting, props, readRelightingValues(), applyRelightingAnimation);
            },
        },
        environment: {
            apply: (values) => {
                storeOverrides(sceneAnimationOverrides.environment, values);
                applyEnvironmentAnimation();
            },
            readValues: () => readEnvironmentValues(),
            reset: (props) => {
                props.forEach((p) => sceneAnimationOverrides.environment.delete(p));
                restoreThroughOverrides(sceneAnimationOverrides.environment, props, readEnvironmentValues(), applyEnvironmentAnimation);
            },
        },
        audioReactive: {
            apply: (values) => {
                storeOverrides(sceneAnimationOverrides.audioReactive, values);
                applyAudioReactiveAnimation();
            },
            readValues: () => readAudioReactiveValues(),
            reset: (props) => restoreThroughOverrides(sceneAnimationOverrides.audioReactive, props, readAudioReactiveValues(true), applyAudioReactiveAnimation),
        },
    };
    // Opacity and visibility of entities whose look other systems also write. Visibility is an
    // override flag the visibility-range / texture-load writers respect (animationHidden), so a
    // hidden entity stays hidden and a shown one gets back exactly the visibility it would
    // otherwise have. Opacity goes through the splat-depth-aware translucency path.
    function baseEntityEnabled(entity: ViewerRuntimeEntity, entityType: string, entityId?: string): boolean {
        switch (entityType) {
            case 'light':
                return (entityId ? lightBaseConfigs.get(entityId)?.enabled : undefined) !== false;
            case 'hotspot':
            case 'portal':
                return !entity.hiddenUntilTextureLoaded && entity.shouldBeVisible !== false && entity.folderHidden !== true;
            case 'customMesh': {
                const range = entity.visibilityRange;
                if (!range) return true;
                return range.type === 'waypoint'
                    ? isInVisibilityRange(getWaypointIndexForProgress(currentProgress), range.start, range.end)
                    : isInVisibilityRange(currentProgress * 100, range.start, range.end);
            }
            default:
                return true;
        }
    }
    // Viewer-owned looks: sphere marker color, mirror reflection, water surface.
    const paramData = (mat: pc.Material | undefined, name: string): unknown => (mat?.getParameter(name) as { data?: unknown } | undefined)?.data;
    const rgbOf = (hex: string | undefined, fallback: [number, number, number]): [number, number, number] => {
        const c = hexToAnimationRgb(hex);
        return c ? [c.r, c.g, c.b] : fallback;
    };
    function markerSphereMaterial(runtime: ViewerRuntimeEntity, entityType: string): pc.StandardMaterial | null {
        if (entityType === 'hotspot') return runtime.hotspotData?.type === 'sphere' || !runtime.hotspotData?.type ? runtime.hotspotMaterial ?? null : null;
        if (entityType === 'portal') return runtime.portalData?.type === 'sphere' ? runtime.portalMaterial ?? null : null;
        return null;
    }
    /** Same diffuse / emissive recipe as createSingleHotspot and the portal sphere. */
    function applyMarkerColor(runtime: ViewerRuntimeEntity, entityType: string, r: number, g: number, b: number): void {
        const material = markerSphereMaterial(runtime, entityType);
        if (!material) return;
        if (entityType === 'portal') {
            material.diffuse.set(r, g, b);
            material.emissive.set(r * 0.5, g * 0.5, b * 0.5);
        } else if (runtime.hotspotData?.useLighting === true) {
            material.diffuse.set(r, g, b);
            material.emissive.set(r * 0.3, g * 0.3, b * 0.3);
        } else {
            material.emissive.set(r, g, b);
        }
        material.update();
    }
    function markerBaseColor(runtime: ViewerRuntimeEntity, entityType: string): [number, number, number] {
        return entityType === 'portal'
            ? rgbOf(runtime.portalData?.color, [0.612, 0.153, 0.69])
            : rgbOf(runtime.hotspotData?.color, [0.8, 0.345, 0.2]);
    }
    function readViewerProperties(entity: pc.Entity, entityType: string): Record<string, number> | null {
        const runtime = entity as ViewerRuntimeEntity;
        if (entityType === 'hotspot' || entityType === 'portal') {
            const animated = runtime.animationMarkerColor;
            const [r, g, b] = animated ?? markerBaseColor(runtime, entityType);
            return { 'marker.color.r': r, 'marker.color.g': g, 'marker.color.b': b };
        }
        if (entityType === 'mirrorPlane') {
            const mirror = entity as MirrorPlaneRuntimeEntity;
            const mat = mirror._mirrorMaterial;
            const intensity = paramData(mat, 'uIntensity') as number | undefined;
            const tint = paramData(mat, 'uTint') as number[] | undefined;
            const [tr, tg, tb] = tint ?? rgbOf(mirror._mirrorData?.tint, [1, 1, 1]);
            return { 'mirror.intensity': intensity ?? mirror._mirrorData?.intensity ?? 1, 'mirror.tint.r': tr, 'mirror.tint.g': tg, 'mirror.tint.b': tb };
        }
        if (entityType === 'waterPlane') return waterSystem.readAnimatable(entity);
        if (entityType === 'cloudLayer') return cloudSystem.readAnimatable(entity);
        return null;
    }
    function writeViewerProperties(entity: pc.Entity, entityType: string, values: Map<string, number>): void {
        const runtime = entity as ViewerRuntimeEntity;
        const color = (prefix: string, current: ArrayLike<number>): [number, number, number] | null =>
            values.has(`${prefix}.r`) || values.has(`${prefix}.g`) || values.has(`${prefix}.b`)
                ? [
                    Math.max(0, values.get(`${prefix}.r`) ?? current[0]),
                    Math.max(0, values.get(`${prefix}.g`) ?? current[1]),
                    Math.max(0, values.get(`${prefix}.b`) ?? current[2]),
                ]
                : null;
        let touched = false;
        if (entityType === 'hotspot' || entityType === 'portal') {
            const next = color('marker.color', runtime.animationMarkerColor ?? markerBaseColor(runtime, entityType));
            if (next) {
                runtime.animationMarkerColor = next;
                applyMarkerColor(runtime, entityType, next[0], next[1], next[2]);
                touched = true;
            }
        } else if (entityType === 'mirrorPlane') {
            const mat = (entity as MirrorPlaneRuntimeEntity)._mirrorMaterial;
            if (mat) {
                if (values.has('mirror.intensity')) { mat.setParameter('uIntensity', Math.max(0, values.get('mirror.intensity')!)); touched = true; }
                const tint = color('mirror.tint', (paramData(mat, 'uTint') as number[] | undefined) ?? [1, 1, 1]);
                if (tint) { mat.setParameter('uTint', tint); touched = true; }
                if (touched) mat.update();
            }
        } else if (entityType === 'waterPlane') {
            touched = waterSystem.writeAnimatable(entity, values);
        } else if (entityType === 'cloudLayer') {
            touched = cloudSystem.writeAnimatable(entity, values);
        }
        if (touched) renderScheduler.requestFrame('entity-animation');
    }
    function resetViewerProperties(entity: pc.Entity, entityType: string, properties: string[]): void {
        const runtime = entity as ViewerRuntimeEntity;
        if ((entityType === 'hotspot' || entityType === 'portal') && properties.some((p) => p.startsWith('marker.color')) && runtime.animationMarkerColor) {
            runtime.animationMarkerColor = undefined;
            const [r, g, b] = markerBaseColor(runtime, entityType);
            applyMarkerColor(runtime, entityType, r, g, b);
        }
        if (entityType === 'mirrorPlane') {
            const mirror = entity as MirrorPlaneRuntimeEntity;
            const data = mirror._mirrorData;
            const mat = mirror._mirrorMaterial;
            if (mat && data) {
                if (properties.includes('mirror.intensity')) mat.setParameter('uIntensity', data.intensity ?? 1);
                if (properties.some((p) => p.startsWith('mirror.tint'))) mat.setParameter('uTint', rgbOf(data.tint, [1, 1, 1]));
                mat.update();
            }
        }
        if (entityType === 'waterPlane') waterSystem.resetAnimatable(entity, properties);
        if (entityType === 'cloudLayer') cloudSystem.resetAnimatable(entity, properties);
    }
    const entityPresentationHandlers: import('./EntityAnimationSystem').EntityPresentationHandlers = {
        setProperties: (entity, entityType, _entityId, values) => writeViewerProperties(entity, entityType, values),
        getProperties: (entity, entityType) => readViewerProperties(entity, entityType),
        setVisible: (entity, entityType, _entityId, visible) => {
            const runtime = entity as ViewerRuntimeEntity;
            runtime.animationHidden = !visible;
            // Shown = whatever the scene would show otherwise (visibility ranges, texture load);
            // a light keyed visible turns on even when its config has it off.
            const want = visible ? baseEntityEnabled(runtime, entityType) : false;
            if (entity.enabled !== want) {
                entity.enabled = want;
                if (entityType === 'hotspot' || entityType === 'portal') syncLeaderLineVisibility(runtime);
                renderScheduler.requestFrame('entity-animation');
            }
        },
        getVisible: (entity) => !(entity as ViewerRuntimeEntity).animationHidden && entity.enabled !== false,
        setOpacity: (entity, entityType, _entityId, opacity) => {
            const runtime = entity as ViewerRuntimeEntity;
            runtime.animationOpacity = opacity;
            if (entityType === 'customMesh') {
                applyOpacityToCustomMesh(entity, opacity);
                return;
            }
            if (applyMarkerStackOpacity(runtime, opacity)) return;
            const material = entityType === 'portal'
                ? (runtime.portalMaterial ?? (entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined))
                : (runtime.hotspotMaterial ?? (entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined));
            if (material) applyEntityOpacity(material, opacity);
        },
        resetProperties: (entity, entityType, entityId, properties) => {
            const runtime = entity as ViewerRuntimeEntity;
            if (properties.includes('visible')) {
                runtime.animationHidden = undefined;
                const want = baseEntityEnabled(runtime, entityType, entityId);
                if (entity.enabled !== want) {
                    entity.enabled = want;
                    if (entityType === 'hotspot' || entityType === 'portal') syncLeaderLineVisibility(runtime);
                }
            }
            if (properties.includes('opacity') && runtime.animationOpacity !== undefined) {
                runtime.animationOpacity = undefined;
                const progress = currentProgress * 100;
                if (entityType === 'customMesh') {
                    const meshConfig = customMeshEntities.get(entityId)?.config;
                    applyOpacityToCustomMesh(entity, meshConfig ? resolveEntityOpacity(meshConfig as EntityOpacityConfigLike, progress) : 1);
                } else if (entityType === 'hotspot' && runtime.hotspotData) {
                    const hotspot = runtime.hotspotData as EntityOpacityConfigLike & { type?: string };
                    const opacity = hotspot.type === 'sphere' ? resolveSphereEntityOpacity(hotspot, progress) : resolveEntityOpacity(hotspot, progress);
                    const material = runtime.hotspotMaterial ?? (entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined);
                    if (!applyMarkerStackOpacity(runtime, opacity) && material) applyEntityOpacity(material, opacity);
                } else if (entityType === 'portal') {
                    const material = runtime.portalMaterial ?? (entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined);
                    const opacity = runtime.portalData?.opacity ?? 1;
                    if (!applyMarkerStackOpacity(runtime, opacity) && material) applyEntityOpacity(material, opacity);
                }
            }
            const light = entity.light;
            const lightCfg = light ? lightBaseConfigs.get(entityId) : undefined;
            if (light && lightCfg) {
                if (properties.some((p) => p.startsWith('light.color'))) light.color = hexToColorForLights(lightCfg.color || '#ffffff');
                if (properties.includes('light.intensity')) light.intensity = lightCfg.intensity ?? 1;
                if (properties.includes('light.range')) light.range = lightCfg.range || 10;
                if (properties.includes('light.coneAngle')) {
                    // Same derivation as createSpotLight.
                    const angleDeg = (lightCfg.angle ?? (45 * Math.PI / 180)) * (180 / Math.PI);
                    light.outerConeAngle = lightCfg.outerConeAngle ?? lightCfg.outerAngle ?? angleDeg;
                    light.innerConeAngle = lightCfg.innerConeAngle ?? lightCfg.innerAngle ?? angleDeg * 0.8;
                }
            }
            if (properties.includes('audio.volume') && entity.sound) {
                entity.sound.volume = finiteNonNegativeSceneNumber(audioEmitterMap.get(entityId)?.config.volume, 0.5);
            }
            resetViewerProperties(entity, entityType, properties);
            renderScheduler.requestFrame('entity-animation');
        },
        getOpacity: (entity, entityType, entityId) => {
            const runtime = entity as ViewerRuntimeEntity;
            if (runtime.animationOpacity !== undefined) return runtime.animationOpacity;
            if (entityType === 'hotspot' && runtime.hotspotData) return resolveEntityOpacity(runtime.hotspotData as EntityOpacityConfigLike, currentProgress * 100);
            if (entityType === 'portal' && runtime.portalData) return runtime.portalData.opacity ?? 1;
            if (entityType === 'customMesh') {
                const meshConfig = customMeshEntities.get(entityId)?.config;
                if (meshConfig) return resolveEntityOpacity(meshConfig as EntityOpacityConfigLike, currentProgress * 100);
            }
            return 1;
        },
    };
    /** One factory so the published viewer and the editor build the system identically. */
    function createEntityAnimationSystem(animations: import('../types').EntityAnimationConfig[]): EntityAnimationSystem {
        return new EntityAnimationSystem(entityLookup, animations, sceneAnimationHandlers, {
            presentation: entityPresentationHandlers,
            onPlaybackEvent: (event) => {
                if (isEditorMode) return;
                events.emit(event.type === 'start' ? 'entityAnimationStart' : 'entityAnimationComplete', {
                    id: event.id,
                    name: event.name,
                    entityId: event.entityId,
                    entityType: event.entityType,
                });
            },
        });
    }
    if (config.entityAnimations && config.entityAnimations.length > 0) {
        entityAnimationSystem = createEntityAnimationSystem(config.entityAnimations);
        console.log(`[StorySplat Viewer] Entity animation system initialized with ${config.entityAnimations.length} animation(s)`);
    }
    // Waypoint triggers: an animation with triggerWaypoint restarts each time the tour arrives.
    // The editor timeline drives every animation itself, so it ignores tour arrivals.
    if (!isEditorMode) {
        events.on('waypointChange', ({ index }: { index: number }) => {
            // Tour arrivals only. The initial waypoint is handled on the first presented frame
            // (an early synthetic waypointChange fires even when the scene opens in explore).
            if (!entityAnimationsPresented || currentCameraMode !== 'tour') return;
            entityAnimationSystem?.handleWaypointChange(index);
        });
    }

    // ═══ Layers menu ═══
    // Lists the regions visitors switch (e.g. "Building History" → periods) with colour swatches;
    // clicks switch them through the runtime, which only rewrites uniforms (no shader recompile).
    // Mounted when a region is in the menu, rebuilt when the regions are edited (setRegions), and
    // taken down when the last one leaves it.
    const syncLayersPanel = (): void => {
        if (isDestroyed || !showUI) return;
        if (!regionsRuntime.hasMenu()) {
            if (layersPanel) {
                layersPanel.destroy();
                layersPanel = null;
                uiElements.layersButton = undefined;
                uiElements.layersPanel = undefined;
                syncToolbarVisibility(uiElements.toolbar);
            }
            return;
        }
        if (layersPanel) {
            layersPanel.rebuild();
            return;
        }
        if ((!uiElements.layersButton || !uiElements.layersPanel) && !createLayersChrome(uiElements, container, regionsRuntime.menu.title)) return;
        const areaScale = uiOpts.sceneScale ?? config.uiOptions?.sceneScale ?? 1;
        const areaUnit = uiOpts.sceneScaleUnit ?? config.uiOptions?.sceneScaleUnit ?? 'meters';
        layersPanel = mountLayersPanel({
            runtime: regionsRuntime.layersMenu,
            button: uiElements.layersButton!,
            panel: uiElements.layersPanel!,
            formatArea: (raw) => formatArea(raw, areaScale, areaUnit),
            // Only one top-left tool open at a time (help, measure, staging, scene menu).
            onOpen: () => uiElements.exclusiveTools?.closeOthers('layers'),
        });
        uiElements.exclusiveTools?.register('layers', () => layersPanel?.setOpen(false));
        syncToolbarVisibility(uiElements.toolbar);
    };
    syncLayersPanel();

    // ═══ Visitor directory ═══
    // A toolbar button and panel listing the hotspots by folder (with tick boxes that show or hide
    // each folder in the scene) and the extra waypoints as "Views". Shown when the creator turned it
    // on and it lists something. The Layers menu switches overlays; the directory is where to go.
    const goToExtraWaypointById = (id: string, instant = false): boolean => {
        // 8th Wall AR places the scene on the floor in front of the phone: no camera moves there.
        if (isIn8thWallAR) return false;
        const waypoint = findExtraWaypoint(config.extraWaypoints, id);
        if (!waypoint) {
            console.warn(`[StorySplat Viewer] goToExtraWaypoint(): no extra waypoint "${id}".`);
            return false;
        }
        let moved: boolean;
        if (isInXR) {
            moved = teleportVrToExtraWaypoint(waypoint);
        } else {
            exitVirtualStaging?.();
            moved = moveCameraToExtraWaypoint(waypoint, undefined, instant);
        }
        if (moved) runArrivalCutaway(waypoint.cutaway);
        return moved;
    };
    /** A tour waypoint picked from the waypoints menu or the directory: go there on the tour. */
    const goToTourWaypointFromUI = (index: number): void => {
        // Exit staging BEFORE the mode check: dismissal restores the driver that was frozen when
        // the photo opened, which must not race the tour handoff below.
        exitVirtualStaging?.();
        // If not in tour mode, switch to tour first (goToWaypoint requires waypointControlEnabled)
        if (currentCameraMode !== 'tour') {
            setCameraMode('tour');
        }
        goToWaypoint(index);
    };
    /** A directory view as getDirectory() reports it. */
    const directoryViewInfo = (v: DirectoryView) => ({ id: v.id, name: v.name, kind: v.kind, ...(v.kind === 'waypoint' ? { index: v.index } : {}) });
    const syncDirectoryPanel = (): void => {
        if (isDestroyed || !showUI) return;
        if (!directoryState.shouldShow()) {
            if (directoryPanel) {
                directoryPanel.destroy();
                directoryPanel = null;
                syncToolbarVisibility(uiElements.toolbar);
            }
            return;
        }
        if (directoryPanel) {
            directoryPanel.rebuild();
            return;
        }
        const chrome = createDirectoryChrome(uiElements.toolbar, container, directoryState.title);
        if (!chrome) return;
        directoryPanel = mountDirectoryPanel({
            state: directoryState,
            button: chrome.button,
            panel: chrome.panel,
            container,
            activateHotspot: (id) => { activateHotspotById(id, { fromDirectory: true }); },
            goToView: (view) => {
                if (view.kind === 'waypoint') goToTourWaypointFromUI(view.index);
                else goToExtraWaypointById(view.id);
            },
            // Greyed out while its own settings (visibility range, an animation) hide it.
            isHotspotAvailable: (id) => {
                const entity = hotspotEntities.find((e) => e.hotspotData?.id === id);
                return !entity || (entity.shouldBeVisible !== false && entity.animationHidden !== true);
            },
            // Only one top-left tool open at a time (help, measure, staging, layers, scene menu).
            onOpen: () => uiElements.exclusiveTools?.closeOthers('directory'),
            onOpenChange: (open) => events.emit('directoryOpenChange', { open }),
            onSelect: (item) => events.emit('directorySelect', item),
            labels: () => currentButtonLabels,
        });
        uiElements.exclusiveTools?.register('directory', () => directoryPanel?.setOpen(false));
        syncToolbarVisibility(uiElements.toolbar);
    };
    syncDirectoryPanel();

    // ═══ "Ask this scene" Guide ═══
    // Visitor Q&A chat: the Ask button opens a panel whose questions go to the sceneGuide
    // endpoint, which answers from the scene's own content. The conversation lives in memory
    // and goes with the viewer (destroy / portal hop).
    if (sceneGuide && uiElements.sceneGuideButton && uiElements.sceneGuidePanel) {
        const guideEndpoint = typeof options.sceneGuideEndpoint === 'string' && options.sceneGuideEndpoint.trim()
            ? options.sceneGuideEndpoint.trim()
            : DEFAULT_SCENE_GUIDE_ENDPOINT;
        const guideHandle = mountSceneGuide({
            guide: sceneGuide,
            button: uiElements.sceneGuideButton,
            panel: uiElements.sceneGuidePanel,
            container,
            endpoint: guideEndpoint,
            captureScreenshot: async () => encodeSceneGuideScreenshot(await capturePhoto()),
            // The stop the visitor is at, while they follow the tour.
            getWaypointIndex: () => (currentCameraMode === 'tour' && (config.waypoints?.length ?? 0) > 0 ? currentWaypointIndex : undefined),
            // The phone sheet covers the top-left tools' panels; close them as they close each other.
            onOpen: (asSheet) => { if (asSheet) uiElements.exclusiveTools?.closeOthers('sceneGuide'); },
        });
        const { sceneGuideButton, sceneGuidePanel } = uiElements;
        const previousUiCleanup = uiElements._cleanup;
        uiElements._cleanup = () => {
            if (previousUiCleanup) previousUiCleanup();
            guideHandle.destroy();
            sceneGuideButton.remove();
            sceneGuidePanel.remove();
        };
    }

    // ═══ Measurement Tape Measure Tool ═══
    if (uiOpts.measurementsEnabled && uiElements.measureButton && uiElements.measureCanvas) {
        const measureBtn = uiElements.measureButton;
        const measureOverlay = uiElements.measureCanvas;
        const measureScale = uiOpts.sceneScale ?? config.uiOptions?.sceneScale ?? 1;
        const measureUnit = uiOpts.sceneScaleUnit ?? config.uiOptions?.sceneScaleUnit ?? 'meters';
        const mColor = uiOpts.measurementColor ?? config.uiOptions?.measurementColor ?? '#FF9800';

        let isMeasureMode = false;
        let preMeasureRefocusTapMode: 'single' | 'double' | 'none' = 'single'; // saved refocus mode to restore on exit
        // Each segment: { from, to, locked } — `locked: true` marks editor-authored defaults
        // that viewer users can see but cannot delete.
        // `setId`: the measurement set a locked default belongs to (utils/measurementSets.ts).
        const measureSegments: { from: pc.Vec3; to: pc.Vec3; locked?: boolean; setId?: string }[] = [];
        // Area measurements: outlines clicked onto a surface (same picks, flags and lock rules as
        // the segments). The area is the fitted plane's projected area (utils/surfacePolygon).
        type MeasureArea = { points: pc.Vec3[]; locked?: boolean; label?: string; setId?: string };
        const measureAreas: MeasureArea[] = [];
        const areaDraft: pc.Vec3[] = [];
        /** Bumped whenever the outline being clicked is closed or dropped, so a slow pick from it is discarded. */
        let areaDraftGeneration = 0;
        let measureKind: 'distance' | 'area' = 'distance';
        // Hydrate editor-authored defaults shipped with the scene, gated on the editor's opt-in flag.
        // Backward-compat: if the older `defaultMeasurementsVisibleOnLoad` flag is on (from before the flag split),
        // treat that as implying "include defaults" so old scenes keep working without re-save.
        const includeFlag = uiOpts.includeDefaultMeasurementsInViewer
            ?? config.uiOptions?.includeDefaultMeasurementsInViewer;
        const legacyVisibleOnLoad = uiOpts.defaultMeasurementsVisibleOnLoad
            ?? config.uiOptions?.defaultMeasurementsVisibleOnLoad
            ?? false;
        const includeDefaults = includeFlag ?? legacyVisibleOnLoad;
        // When true, locked (editor-authored) default measurements stay visible on screen
        // even when measure mode is off. User-created (unlocked) measurements still disappear on exit.
        // Measurement sets the visitor ticks in the Measure panel stay drawn after it closes.
        const keepDefaultsVisible = ((uiOpts.alwaysShowDefaultMeasurements
            ?? config.uiOptions?.alwaysShowDefaultMeasurements
            ?? false) as boolean) || measurementSetPicker;
        function hasLockedSegments(): boolean {
            for (let i = 0; i < measureSegments.length; i++) if (measureSegments[i].locked) return true;
            return measureAreas.some(a => a.locked);
        }
        function shouldRenderLines(): boolean {
            return isMeasureMode || (keepDefaultsVisible && hasLockedSegments());
        }
        if (includeDefaults && Array.isArray(config.measurements) && config.measurements.length > 0) {
            for (const m of config.measurements) {
                if (!m || !m.pointA || !m.pointB) continue;
                measureSegments.push({
                    from: new pc.Vec3(m.pointA.x, m.pointA.y, m.pointA.z),
                    to: new pc.Vec3(m.pointB.x, m.pointB.y, m.pointB.z),
                    locked: true,
                    ...(typeof m.setId === 'string' && m.setId ? { setId: m.setId } : {}),
                });
            }
        }
        if (includeDefaults && Array.isArray(config.areaMeasurements)) {
            for (const a of config.areaMeasurements) {
                const points = cleanSurfacePoints(a?.points);
                if (points.length < 3 || !fitSurfacePolygon(points)) continue;
                measureAreas.push({
                    points: points.map(p => new pc.Vec3(p.x, p.y, p.z)),
                    locked: true,
                    ...(typeof a.label === 'string' && a.label ? { label: a.label } : {}),
                    ...(typeof a.setId === 'string' && a.setId ? { setId: a.setId } : {}),
                });
            }
        }
        // Measurement sets: saved measurements of a hidden set are not drawn (every set starts shown,
        // or every set hidden when the creator starts the visitors' sets unticked).
        const measureSets = measurementSetsState;
        /** Whether one measurement is drawn now: its set is shown (saved ones) / measuring (visitors'). */
        function measureItemShown(item: { locked?: boolean; setId?: string }): boolean {
            return measurementShown(item, isMeasureMode, measureSets);
        }
        // VR draws the saved measurements itself (this tool's overlay is page UI a headset can't
        // show): lines and value labels, rebuilt only when the measurements or their sets change.
        let measureGeometryVersion = 0;
        let vrGeometryVersion = -1;
        const vrGeometry: { color: string; segments: VrMeasurementGeometry['segments'][number][]; areas: VrMeasurementGeometry['areas'][number][] } = { color: mColor, segments: [], areas: [] };
        vrMeasurementSource = {
            shownByDefault: () => keepDefaultsVisible || autoShowDefaults,
            hasAny: () => hasLockedSegments(),
            geometry: () => {
                if (vrGeometryVersion !== measureGeometryVersion) {
                    vrGeometryVersion = measureGeometryVersion;
                    vrGeometry.segments = [];
                    vrGeometry.areas = [];
                    for (const seg of measureSegments) {
                        if (!seg.locked || !measureItemShown(seg)) continue;
                        vrGeometry.segments.push({ from: seg.from, to: seg.to, label: formatDistance(seg.from.distance(seg.to)) });
                    }
                    for (const area of measureAreas) {
                        if (!area.locked || !measureItemShown(area)) continue;
                        const center = new pc.Vec3();
                        for (const p of area.points) center.add(p);
                        center.mulScalar(1 / Math.max(1, area.points.length));
                        const fit = fitSurfacePolygon(area.points);
                        vrGeometry.areas.push({ points: area.points, center, label: `${area.label ? `${area.label}: ` : ''}${formatAreaLabel(fit?.area ?? 0)}` });
                    }
                }
                return vrGeometry;
            },
        };
        let pendingPoint: pc.Vec3 | null = null;
        let hoverScreenPos: { x: number; y: number } | null = null;
        // DOM elements for live preview
        const pointMarkers: HTMLElement[] = [];
        const labelDivs: HTMLElement[] = [];
        /** One label per rendered area, index-aligned with the areas drawn (like labelDivs). */
        const areaLabelDivs: HTMLElement[] = [];
        let previewLabel: HTMLElement | null = null;
        // Measurement list dropdown
        const measureListEl = uiElements.measureList || null;
        /** The panel's words: the scene's label overrides (setButtonLabels too), else English. */
        const ml = (key: keyof ViewerButtonLabels) => getButtonLabel(currentButtonLabels, key);

        /** Update the measurement list dropdown with current segments */
        function updateMeasurementList(): void {
            if (!measureListEl) return;
            // A visitor ticking a set re-renders the panel: keep the keyboard on that row.
            const focusedSetRow = measureListEl.contains(document.activeElement)
                ? (document.activeElement as HTMLElement).dataset?.measureSetId
                : undefined;
            measureListEl.innerHTML = '';
            // Header
            const header = document.createElement('div');
            header.className = 'storysplat-measure-list-header';
            const title = document.createElement('span');
            title.className = 'storysplat-measure-list-title';
            title.textContent = ml('measureTitle');
            header.appendChild(title);
            // Only show "Clear all" if there is at least one unlocked (user-created) measurement.
            const hasUnlocked = measureSegments.some(s => !s.locked) || measureAreas.some(a => !a.locked);
            if (hasUnlocked) {
                const clearBtn = document.createElement('button');
                clearBtn.className = 'storysplat-measure-list-clear';
                clearBtn.textContent = ml('measureClearAll');
                clearBtn.addEventListener('click', () => {
                    clearMeasurements();
                    rebuildMeasurementVisuals();
                });
                header.appendChild(clearBtn);
            }
            measureListEl.appendChild(header);
            // The measurement sets visitors tick (measurementSetsPicker.ts), above their own tools.
            const setPicker = measureSets && measurementSetPicker
                ? buildMeasurementSetPicker(measureSets, { heading: ml('measureSets'), other: ml('measureOtherSet'), all: ml('measureAllSets') })
                : null;
            measureListEl.classList.toggle('has-sets', !!setPicker);
            if (setPicker) measureListEl.appendChild(setPicker);
            // Distance / Area switch
            const kindRow = document.createElement('div');
            kindRow.className = 'storysplat-measure-kind';
            kindRow.setAttribute('role', 'radiogroup');
            kindRow.setAttribute('aria-label', ml('measureType'));
            for (const kind of ['distance', 'area'] as const) {
                const kindBtn = document.createElement('button');
                kindBtn.type = 'button';
                kindBtn.className = 'storysplat-measure-kind-btn' + (measureKind === kind ? ' active' : '');
                kindBtn.setAttribute('role', 'radio');
                kindBtn.setAttribute('aria-checked', measureKind === kind ? 'true' : 'false');
                kindBtn.textContent = ml(kind === 'distance' ? 'measureDistance' : 'measureArea');
                kindBtn.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    setMeasureKind(kind);
                });
                kindRow.appendChild(kindBtn);
            }
            measureListEl.appendChild(kindRow);
            // While an outline is being clicked: how to close it, and a touch-friendly Finish.
            if (measureKind === 'area' && areaDraft.length > 0) {
                const hint = document.createElement('div');
                hint.className = 'storysplat-measure-list-empty';
                hint.textContent = areaDraft.length >= 3
                    ? ml('measureAreaClose')
                    : areaDraft.length === 1 ? ml('measureAreaOnePoint') : ml('measureAreaPoints').replace('{n}', String(areaDraft.length));
                measureListEl.appendChild(hint);
                if (areaDraft.length >= 3) {
                    const finishBtn = document.createElement('button');
                    finishBtn.type = 'button';
                    finishBtn.className = 'storysplat-measure-list-finish';
                    finishBtn.textContent = ml('measureAreaFinish');
                    finishBtn.addEventListener('click', (ev) => {
                        ev.stopPropagation();
                        finishAreaDraft();
                    });
                    measureListEl.appendChild(finishBtn);
                }
            }
            // Items (saved measurements of a hidden measurement set are left out)
            const listed = (item: { locked?: boolean; setId?: string }) => !item.locked || measureItemShown(item);
            if (!measureSegments.some(listed) && !measureAreas.some(listed)) {
                if (!(measureKind === 'area' && areaDraft.length > 0)) {
                    const empty = document.createElement('div');
                    empty.className = 'storysplat-measure-list-empty';
                    empty.textContent = ml(measureKind === 'area' ? 'measureAreaHint' : 'measureDistanceHint');
                    measureListEl.appendChild(empty);
                }
            } else {
                for (let i = 0; i < measureSegments.length; i++) {
                    const seg = measureSegments[i];
                    if (!listed(seg)) continue;
                    const item = document.createElement('div');
                    item.className = 'storysplat-measure-list-item';
                    const dot = document.createElement('span');
                    dot.className = 'storysplat-measure-list-dot';
                    item.appendChild(dot);
                    const text = document.createElement('span');
                    text.style.flex = '1';
                    const rawDist = seg.from.distance(seg.to);
                    text.textContent = formatDistance(rawDist);
                    item.appendChild(text);
                    if (seg.locked) {
                        // Scene-default measurements are not deletable by viewer users — show a lock indicator instead of the × button.
                        const lockEl = document.createElement('span');
                        lockEl.className = 'storysplat-measure-list-lock';
                        lockEl.textContent = '\u{1F512}'; // 🔒
                        lockEl.title = ml('measureLocked');
                        lockEl.style.fontSize = '12px';
                        lockEl.style.padding = '0 4px';
                        lockEl.style.opacity = '0.6';
                        item.appendChild(lockEl);
                    } else {
                        const delBtn = document.createElement('button');
                        delBtn.className = 'storysplat-measure-list-clear';
                        delBtn.textContent = '\u00D7'; // × symbol
                        delBtn.style.fontSize = '14px';
                        delBtn.style.padding = '0 4px';
                        delBtn.style.lineHeight = '1';
                        const idx = i;
                        delBtn.addEventListener('click', () => {
                            measureSegments.splice(idx, 1);
                            cancelPendingDraw();
                            rebuildMeasurementVisuals();
                            redraw();
                        });
                        item.appendChild(delBtn);
                    }
                    measureListEl.appendChild(item);
                }
                for (let i = 0; i < measureAreas.length; i++) {
                    const area = measureAreas[i];
                    if (!listed(area)) continue;
                    const fit = fitSurfacePolygon(area.points);
                    const item = document.createElement('div');
                    item.className = 'storysplat-measure-list-item';
                    const dot = document.createElement('span');
                    dot.className = 'storysplat-measure-list-dot storysplat-measure-list-dot--area';
                    item.appendChild(dot);
                    const text = document.createElement('span');
                    text.style.flex = '1';
                    text.textContent = `${area.label ? `${area.label}: ` : ''}${formatAreaLabel(fit?.area ?? 0)}`;
                    if (fit && (!fit.planar || fit.selfIntersecting)) {
                        // Far from flat (wraps a corner, two roof slopes) or crossing edges: the
                        // number is a projection, not the surface area. Say so instead of hiding it.
                        text.textContent += ' \u26A0';
                        item.title = ml(fit.selfIntersecting ? 'measureAreaCrossing' : 'measureAreaNotFlat');
                    }
                    item.appendChild(text);
                    if (area.locked) {
                        const lockEl = document.createElement('span');
                        lockEl.className = 'storysplat-measure-list-lock';
                        lockEl.textContent = '\u{1F512}';
                        lockEl.title = ml('measureLocked');
                        lockEl.style.fontSize = '12px';
                        lockEl.style.padding = '0 4px';
                        lockEl.style.opacity = '0.6';
                        item.appendChild(lockEl);
                    } else {
                        const delBtn = document.createElement('button');
                        delBtn.className = 'storysplat-measure-list-clear';
                        delBtn.textContent = '\u00D7';
                        delBtn.style.fontSize = '14px';
                        delBtn.style.padding = '0 4px';
                        delBtn.style.lineHeight = '1';
                        const idx = i;
                        delBtn.addEventListener('click', () => {
                            measureAreas.splice(idx, 1);
                            rebuildMeasurementVisuals();
                            redraw();
                        });
                        item.appendChild(delBtn);
                    }
                    measureListEl.appendChild(item);
                }
            }
            if (focusedSetRow !== undefined) {
                for (const row of measureListEl.querySelectorAll<HTMLElement>('[data-measure-set-id]')) {
                    if (row.dataset.measureSetId === focusedSetRow) row.focus();
                }
            }
        }
        refreshMeasurePanelLabels = () => {
            measureBtn.setAttribute('aria-label', ml('measureButton'));
            updateMeasurementList();
        };

        const formatAreaLabel = (rawArea: number): string => formatArea(rawArea, measureScale, measureUnit);

        /** Close the outline being clicked into an area measurement (needs three distinct points). */
        function finishAreaDraft(): void {
            if (areaDraft.length >= 3 && fitSurfacePolygon(areaDraft)) {
                measureAreas.push({ points: areaDraft.map(p => p.clone()), locked: false });
            }
            areaDraft.length = 0;
            areaDraftGeneration++;
            hoverScreenPos = null;
            rebuildMeasurementVisuals();
            redraw();
        }

        function setMeasureKind(kind: 'distance' | 'area'): void {
            if (kind === measureKind) return;
            measureKind = kind;
            pendingPoint = null;
            areaDraft.length = 0;
            areaDraftGeneration++;
            hoverScreenPos = null;
            if (previewLabel) { previewLabel.remove(); previewLabel = null; }
            rebuildMeasurementVisuals();
            redraw();
        }

        // Raw scene-unit distance in the chosen display unit, grouped in the visitor's language.
        function formatDistance(rawDist: number): string {
            return formatMeasureDistance(rawDist, measureScale, measureUnit);
        }

        const _measureScreenVec = new pc.Vec3();
        function worldToScreen(pt: pc.Vec3): { x: number; y: number } | null {
            camera.camera!.worldToScreen(pt, _measureScreenVec);
            const sp = _measureScreenVec;
            if (sp.z < 0) return null; // behind camera
            return { x: sp.x, y: sp.y };
        }

        function createPointMarker(screenX: number, screenY: number): HTMLElement {
            const el = document.createElement('div');
            el.className = 'storysplat-measure-point';
            el.style.left = `${screenX}px`;
            el.style.top = `${screenY}px`;
            container.appendChild(el);
            pointMarkers.push(el);
            return el;
        }

        function createLabel(text: string, screenX: number, screenY: number): HTMLElement {
            const el = document.createElement('div');
            el.className = 'storysplat-measure-label';
            el.textContent = text;
            el.style.left = `${screenX}px`;
            el.style.top = `${screenY}px`;
            container.appendChild(el);
            labelDivs.push(el);
            return el;
        }

        function clearMeasurements(): void {
            // Keep locked (scene-default) segments; only clear user-created ones.
            for (let i = measureSegments.length - 1; i >= 0; i--) {
                if (!measureSegments[i].locked) measureSegments.splice(i, 1);
            }
            for (let i = measureAreas.length - 1; i >= 0; i--) {
                if (!measureAreas[i].locked) measureAreas.splice(i, 1);
            }
            areaDraft.length = 0;
            areaDraftGeneration++;
            areaLabelDivs.forEach(el => el.remove());
            areaLabelDivs.length = 0;
            pendingPoint = null;
            hoverScreenPos = null;
            pointMarkers.forEach(el => el.remove());
            pointMarkers.length = 0;
            labelDivs.forEach(el => el.remove());
            labelDivs.length = 0;
            if (previewLabel) { previewLabel.remove(); previewLabel = null; }
            rebuildMeasurementVisuals();
            updateMeasurementList();
            redraw();
        }

        // Track container size via ResizeObserver instead of a per-frame getBoundingClientRect:
        // redraw() runs every frame while measurements render, and setting canvas width/height —
        // even to the SAME value — resets the context and reallocates the backing store, on top
        // of the forced layout from the rect read. Seed synchronously (the observer's first
        // callback is async but redraw() runs synchronously at init); compare integer-coerced
        // values (canvas dimensions truncate — a float compare would never match).
        const seedRect = container.getBoundingClientRect();
        let overlayW = Math.floor(seedRect.width);
        let overlayH = Math.floor(seedRect.height);
        const overlayResizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                const rect = container.getBoundingClientRect();
                overlayW = Math.floor(rect.width);
                overlayH = Math.floor(rect.height);
            })
            : null;
        overlayResizeObserver?.observe(container);

        // Expose DOM cleanup for destroy/portal navigation
        _measureCleanupDom = () => {
            overlayResizeObserver?.disconnect();
            pointMarkers.forEach(el => el.remove());
            pointMarkers.length = 0;
            labelDivs.forEach(el => el.remove());
            labelDivs.length = 0;
            areaLabelDivs.forEach(el => el.remove());
            areaLabelDivs.length = 0;
            if (previewLabel) { previewLabel.remove(); previewLabel = null; }
            if (measureListEl) { measureListEl.remove(); }
            measureOverlay.remove();
            measureBtn.remove();
        };

        function resizeOverlay(): void {
            if (overlayResizeObserver === null) {
                // No ResizeObserver support: fall back to the per-call rect read
                const rect = container.getBoundingClientRect();
                overlayW = Math.floor(rect.width);
                overlayH = Math.floor(rect.height);
            }
            if (measureOverlay.width !== overlayW) measureOverlay.width = overlayW;
            if (measureOverlay.height !== overlayH) measureOverlay.height = overlayH;
        }

        function redraw(): void {
            resizeOverlay();
            const ctx = measureOverlay.getContext('2d');
            if (!ctx) return;
            ctx.clearRect(0, 0, measureOverlay.width, measureOverlay.height);
            if (!shouldRenderLines()) return;

            // Draw completed segments
            ctx.strokeStyle = mColor;
            ctx.lineWidth = 2;
            ctx.setLineDash([]);
            for (const seg of measureSegments) {
                // When not in measure mode, only locked defaults render (of the sets shown)
                if (!measureItemShown(seg)) continue;
                const from = worldToScreen(seg.from);
                const to = worldToScreen(seg.to);
                if (from && to) {
                    ctx.beginPath();
                    ctx.moveTo(from.x, from.y);
                    ctx.lineTo(to.x, to.y);
                    ctx.stroke();
                }
            }

            // Draw rubber band line from pending point to hover position
            if (pendingPoint && hoverScreenPos) {
                const fromScreen = worldToScreen(pendingPoint);
                if (fromScreen) {
                    ctx.strokeStyle = mColor;
                    ctx.globalAlpha = 0.6;
                    ctx.setLineDash([6, 4]);
                    ctx.beginPath();
                    ctx.moveTo(fromScreen.x, fromScreen.y);
                    ctx.lineTo(hoverScreenPos.x, hoverScreenPos.y);
                    ctx.stroke();
                    ctx.setLineDash([]);
                    ctx.globalAlpha = 1.0;
                }
            }

            // Completed areas: translucent fill + outline. Skipped while any corner is behind
            // the camera (a partial projection would draw a misleading shape).
            ctx.strokeStyle = mColor;
            ctx.fillStyle = mColor;
            for (const area of measureAreas) {
                if (!measureItemShown(area)) continue;
                const pts = area.points.map(worldToScreen);
                if (pts.some(p => !p)) continue;
                ctx.beginPath();
                pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p!.x, p!.y) : ctx.lineTo(p!.x, p!.y)));
                ctx.closePath();
                ctx.globalAlpha = 0.22;
                ctx.fill();
                ctx.globalAlpha = 1.0;
                ctx.stroke();
                for (const p of pts) {
                    ctx.beginPath();
                    ctx.arc(p!.x, p!.y, 3, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            // The outline being clicked: solid edges, a dashed edge to the cursor, and the first
            // point drawn larger once the outline can be closed on it.
            if (isMeasureMode && measureKind === 'area' && areaDraft.length > 0) {
                const pts = areaDraft.map(worldToScreen);
                ctx.lineWidth = 2;
                ctx.beginPath();
                let started = false;
                for (const p of pts) {
                    if (!p) { started = false; continue; }
                    if (!started) { ctx.moveTo(p.x, p.y); started = true; } else ctx.lineTo(p.x, p.y);
                }
                ctx.stroke();
                const last = pts[pts.length - 1];
                if (last && hoverScreenPos) {
                    ctx.globalAlpha = 0.6;
                    ctx.setLineDash([6, 4]);
                    ctx.beginPath();
                    ctx.moveTo(last.x, last.y);
                    ctx.lineTo(hoverScreenPos.x, hoverScreenPos.y);
                    ctx.stroke();
                    ctx.setLineDash([]);
                    ctx.globalAlpha = 1.0;
                }
                pts.forEach((p, i) => {
                    if (!p) return;
                    const closable = i === 0 && areaDraft.length >= 3;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, closable ? 7 : 4, 0, Math.PI * 2);
                    ctx.fillStyle = mColor;
                    ctx.fill();
                    ctx.lineWidth = 2;
                    ctx.strokeStyle = '#ffffff';
                    ctx.stroke();
                    ctx.strokeStyle = mColor;
                });
            }
        }

        /** Screen position for an area's label: the average of its projected corners. */
        function areaLabelPosition(area: MeasureArea): { x: number; y: number } | null {
            let x = 0, y = 0;
            for (const p of area.points) {
                const sp = worldToScreen(p);
                if (!sp) return null;
                x += sp.x;
                y += sp.y;
            }
            return { x: x / area.points.length, y: y / area.points.length };
        }

        function updateMarkerPositions(): void {
            // Layout: 2 markers + 1 label per rendered segment, then 1 marker for
            // pendingPoint. Labels are created 1:1 with segments (even when
            // initially offscreen) so the label→segment mapping stays stable as
            // the camera moves and endpoints enter/leave view.
            let markerIdx = 0;
            let labelIdx = 0;
            for (let i = 0; i < measureSegments.length; i++) {
                const seg = measureSegments[i];
                if (!measureItemShown(seg)) continue;
                const from = worldToScreen(seg.from);
                if (from && pointMarkers[markerIdx]) {
                    pointMarkers[markerIdx].style.left = `${from.x}px`;
                    pointMarkers[markerIdx].style.top = `${from.y}px`;
                    pointMarkers[markerIdx].style.display = '';
                } else if (pointMarkers[markerIdx]) {
                    pointMarkers[markerIdx].style.display = 'none';
                }
                markerIdx++;
                const to = worldToScreen(seg.to);
                if (to && pointMarkers[markerIdx]) {
                    pointMarkers[markerIdx].style.left = `${to.x}px`;
                    pointMarkers[markerIdx].style.top = `${to.y}px`;
                    pointMarkers[markerIdx].style.display = '';
                } else if (pointMarkers[markerIdx]) {
                    pointMarkers[markerIdx].style.display = 'none';
                }
                markerIdx++;
                if (labelDivs[labelIdx]) {
                    if (from && to) {
                        labelDivs[labelIdx].style.left = `${(from.x + to.x) / 2}px`;
                        labelDivs[labelIdx].style.top = `${(from.y + to.y) / 2}px`;
                        labelDivs[labelIdx].style.display = '';
                    } else {
                        labelDivs[labelIdx].style.display = 'none';
                    }
                }
                labelIdx++;
            }
            // Update pending point marker
            if (pendingPoint && pointMarkers[markerIdx]) {
                const sp = worldToScreen(pendingPoint);
                if (sp) {
                    pointMarkers[markerIdx].style.left = `${sp.x}px`;
                    pointMarkers[markerIdx].style.top = `${sp.y}px`;
                    pointMarkers[markerIdx].style.display = '';
                } else {
                    pointMarkers[markerIdx].style.display = 'none';
                }
            }
            let areaIdx = 0;
            for (const area of measureAreas) {
                if (!measureItemShown(area)) continue;
                const label = areaLabelDivs[areaIdx++];
                if (!label) continue;
                const at = areaLabelPosition(area);
                if (at) {
                    label.style.left = `${at.x}px`;
                    label.style.top = `${at.y}px`;
                    label.style.display = '';
                } else {
                    label.style.display = 'none';
                }
            }
        }

        function enterMeasureMode(): void {
            // Only one top-left tool open at a time: dismiss help/staging/scene-menu.
            uiElements.exclusiveTools?.closeOthers('measure');
            isMeasureMode = true;
            isMeasureModeActive = true;
            measurePickActive = true;
            measureBtn.classList.add('active');
            canvasEl.style.cursor = 'none';
            // Set 3D reticle to orange
            reticleColorOverride = new pc.Color(1.0, 0.596, 0.0); // #FF9800
            // Show measurement list
            if (measureListEl) measureListEl.style.display = 'block';
            // Rebuild visual overlays from existing segments
            rebuildMeasurementVisuals();
            // Disable refocus on tap so clicks place measurement points instead
            preMeasureRefocusTapMode = refocusTapMode;
            refocusTapMode = 'none';
            // GPU depth pick for this measure session against the UNIFIED splat — engine
            // 2.21+ fixed the 2.20 pick-path device hang, so there is no non-unified
            // switch and no isolated layer anymore. Octree/LOD splats are included (the
            // unified world picks whatever it renders). begin() returns false where
            // unsupported (WebGL2 raster-cpu, forceCpuPick) and the session simply
            // stays on the CPU raycast.
            {
                const compareUsesCustomLayers = !!compareSplitView;
                const started = !compareUsesCustomLayers && splatDepthPicker.begin(camera);
                pickDiag.push('measure:enter', {
                    gpuPick: started,
                    compareUsesCustomLayers,
                });
            }
        }

        function exitMeasureMode(): void {
            isMeasureMode = false;
            isMeasureModeActive = false;
            measurePickActive = false;
            // Tear down the depth picker (idempotent; safe no-op if the GPU pick wasn't
            // active for this session — the splat never left unified either way).
            pickDiag.push('measure:exit', { gpuPickWasActive: splatDepthPicker.active });
            splatDepthPicker.end();
            measureBtn.classList.remove('active');
            canvasEl.style.cursor = '';
            // Restore 3D reticle to white
            reticleColorOverride = null;
            // Cancel any pending draw
            pendingPoint = null;
            areaDraft.length = 0;
            areaDraftGeneration++;
            hoverScreenPos = null;
            if (previewLabel) { previewLabel.remove(); previewLabel = null; }
            // Rebuild visuals under the current mode: if keepDefaultsVisible is on, rebuild
            // will recreate only locked-segment DOM (unlocked data is kept for re-entry).
            // Otherwise, tear down all DOM (unlocked data is still preserved for re-entry).
            if (keepDefaultsVisible && hasLockedSegments()) {
                rebuildMeasurementVisuals();
            } else {
                pointMarkers.forEach(el => el.remove());
                pointMarkers.length = 0;
                labelDivs.forEach(el => el.remove());
                labelDivs.length = 0;
                areaLabelDivs.forEach(el => el.remove());
                areaLabelDivs.length = 0;
            }
            redraw();
            // Hide measurement list
            if (measureListEl) measureListEl.style.display = 'none';
            // Restore previous refocus tap mode
            refocusTapMode = preMeasureRefocusTapMode;
        }

        /** Cancel pending point / rubber band without clearing completed measurements */
        function cancelPendingDraw(): void {
            pendingPoint = null;
            if (areaDraft.length) areaDraftGeneration++;
            areaDraft.length = 0;
            hoverScreenPos = null;
            if (previewLabel) { previewLabel.remove(); previewLabel = null; }
            // Rebuild to remove the pending point marker
            rebuildMeasurementVisuals();
        }

        /** Rebuild point markers and labels from existing measureSegments + pendingPoint */
        function rebuildMeasurementVisuals(): void {
            measureGeometryVersion++;
            // Clear old DOM elements
            pointMarkers.forEach(el => el.remove());
            pointMarkers.length = 0;
            labelDivs.forEach(el => el.remove());
            labelDivs.length = 0;
            areaLabelDivs.forEach(el => el.remove());
            areaLabelDivs.length = 0;
            if (previewLabel) { previewLabel.remove(); previewLabel = null; }
            // When not in measure mode (keepDefaultsVisible path), render only locked defaults.
            // Unlocked segments stay in data so they reappear when measure mode is re-entered.
            for (let i = 0; i < measureSegments.length; i++) {
                const seg = measureSegments[i];
                if (!measureItemShown(seg)) continue;
                const fromSp = worldToScreen(seg.from);
                const fromMarker = createPointMarker(fromSp ? fromSp.x : 0, fromSp ? fromSp.y : 0);
                if (!fromSp && fromMarker) fromMarker.style.display = 'none';
                const toSp = worldToScreen(seg.to);
                const toMarker = createPointMarker(toSp ? toSp.x : 0, toSp ? toSp.y : 0);
                if (!toSp && toMarker) toMarker.style.display = 'none';
                // Always create a label per segment so labelDivs stays index-
                // aligned with rendered segments; hide it when either endpoint
                // isn't on-screen. updateMarkerPositions will show/position it
                // once both endpoints come into view.
                const rawDist = seg.from.distance(seg.to);
                const labelEl = createLabel(
                    formatDistance(rawDist),
                    fromSp && toSp ? (fromSp.x + toSp.x) / 2 : 0,
                    fromSp && toSp ? (fromSp.y + toSp.y) / 2 : 0
                );
                if ((!fromSp || !toSp) && labelEl) labelEl.style.display = 'none';
            }
            // Add marker for pending point (first click of an in-progress segment)
            if (pendingPoint) {
                const sp = worldToScreen(pendingPoint);
                createPointMarker(sp ? sp.x : 0, sp ? sp.y : 0);
            }
            // One label per rendered area (kept out of labelDivs so segment indices stay aligned).
            for (const area of measureAreas) {
                if (!measureItemShown(area)) continue;
                const at = areaLabelPosition(area);
                const fit = fitSurfacePolygon(area.points);
                const el = document.createElement('div');
                el.className = 'storysplat-measure-label storysplat-measure-label--area';
                el.textContent = `${area.label ? `${area.label}: ` : ''}${formatAreaLabel(fit?.area ?? 0)}`;
                el.style.left = `${at ? at.x : 0}px`;
                el.style.top = `${at ? at.y : 0}px`;
                if (!at) el.style.display = 'none';
                container.appendChild(el);
                areaLabelDivs.push(el);
            }
            updateMeasurementList();
        }

        // Toggle measure mode
        measureBtn.addEventListener('click', () => {
            if (isMeasureMode) {
                exitMeasureMode();
            } else {
                enterMeasureMode();
            }
        });

        // Register with the only-one-open coordinator so opening another tool
        // (help / virtual staging / scene menu) exits measurement mode.
        uiElements.exclusiveTools?.register('measure', () => {
            if (isMeasureMode) exitMeasureMode();
        });

        // Escape cancels pending draw (does NOT exit measure mode)
        const measureKeydownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isMeasureMode) {
                if (pendingPoint || areaDraft.length) {
                    cancelPendingDraw();
                    redraw();
                }
                // If no pending point, Esc does nothing — use the button to exit
            } else if (e.key === 'Enter' && isMeasureMode && measureKind === 'area' && areaDraft.length >= 3) {
                // Not while the visitor types into a field (a form overlay, the host page).
                const target = e.target as HTMLElement | null;
                if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
                finishAreaDraft();
            }
        };
        document.addEventListener('keydown', measureKeydownHandler);
        _measureKeydownHandler = measureKeydownHandler;

        // Measurement picks. Splat surfaces come from the CPU exact pick in its haze-aware mode
        // (ExactSplatRaycaster PickOptions.seeThroughHaze): the depth where the splats reach 0.5
        // alpha, looking through large, partly transparent floaters that a viewer sees the scene
        // through. The editor and WebGL2 use the same pick, so every renderer measures the same.
        // On WebGPU the GPU depth pick (engine 2.21+, playcanvas/engine#9036) runs alongside: it
        // stops at the first splat whose alpha passes 0.3, haze included, so for splats it only
        // stands in while the CPU index builds or when the CPU pick finds nothing; a MESH it hits
        // in front (a model the CPU pick knows only by its collision, if any) wins.
        const measurePointValid = (p: pc.Vec3 | null | undefined): p is pc.Vec3 => {
            if (!p || !isFinite(p.x) || !isFinite(p.y) || !isFinite(p.z)) return false;
            const dist = camera.getPosition().distance(p);
            return dist > 0.1 && dist < 1000;
        };
        async function pickWorldPoint(screenX: number, screenY: number): Promise<pc.Vec3 | null> {
            try {
                const target = resolveSurfacePickTarget(screenX, screenY);
                const exactReady = !!(target.entity && exactSplatRaycaster.ensureSource(target.entity, target.cacheKey));
                const cpuHit = reticleRaycast(screenX, screenY, target, true);
                const cpu = measurePointValid(cpuHit) ? cpuHit : null;
                if (!compareSplitView && splatDepthPicker.active) {
                    const gpu = await splatDepthPicker.pickWorldPoint(screenX, screenY);
                    // Compare may have been enabled while the async readback was
                    // in flight. Its commit ends the World-layer picker; discard
                    // any late World-layer result instead of accepting it on a
                    // custom-layer compare side.
                    if (!compareSplitView && gpu && measurePointValid(gpu.point)) {
                        const camPos = camera.getPosition();
                        if (gpu.mesh && (!cpu || camPos.distance(gpu.point) < camPos.distance(cpu) - 0.01)) return gpu.point;
                        if (!exactReady || !cpu) return gpu.point;
                    }
                }
                return cpu;
            } catch (_e) { /* ignore */ }
            return null;
        }

        // Click handler for placing measurement points
        canvasEl.addEventListener('click', async (e: MouseEvent) => {
            if (!isMeasureMode) return;
            // Don't interfere with drag — check suppression
            if (clickSuppressed) return;
            // While measuring, a click only places points. Stop it now: after the awaited pick
            // below the event has already been delivered to the other click handlers.
            e.stopPropagation();

            const rect = canvasEl.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            if (measureKind === 'area') {
                // Clicking the first point again closes the outline.
                const first = areaDraft.length >= 3 ? worldToScreen(areaDraft[0]) : null;
                if (first && Math.hypot(first.x - x, first.y - y) <= 14) {
                    finishAreaDraft();
                    return;
                }
                const generation = areaDraftGeneration;
                const areaPt = await pickWorldPoint(x, y);
                // The outline was closed, cleared or the mode changed while the pick ran.
                if (!areaPt || !isMeasureMode || measureKind !== 'area' || generation !== areaDraftGeneration) return;
                areaDraft.push(areaPt.clone());
                rebuildMeasurementVisuals();
                redraw();
                return;
            }

            const worldPt = await pickWorldPoint(x, y);
            if (!worldPt) return;

            if (!pendingPoint) {
                // First point of a new chain
                pendingPoint = worldPt.clone();
            } else {
                // Complete a segment (user-created — never locked)
                measureSegments.push({ from: pendingPoint.clone(), to: worldPt.clone(), locked: false });
                // Chain: next segment starts from this endpoint
                pendingPoint = worldPt.clone();
            }
            // Rebuild all markers from scratch to keep indices aligned
            rebuildMeasurementVisuals();
            redraw();
        }, true); // Use capture to intercept before hotspot handler

        // Hover handler for rubber band preview
        let measureHoverThrottle = 0;
        canvasEl.addEventListener('pointermove', (e: PointerEvent) => {
            if (isMeasureMode && measureKind === 'area') {
                if (!areaDraft.length) return;
                const areaRect = canvasEl.getBoundingClientRect();
                hoverScreenPos = { x: e.clientX - areaRect.left, y: e.clientY - areaRect.top };
                redraw();
                return;
            }
            if (!isMeasureMode || !pendingPoint) return;
            const rect = canvasEl.getBoundingClientRect();
            hoverScreenPos = { x: e.clientX - rect.left, y: e.clientY - rect.top };

            // Throttle world point picks for distance label
            const now = performance.now();
            if (now - measureHoverThrottle > 80) {
                measureHoverThrottle = now;
                const sx = hoverScreenPos.x;
                const sy = hoverScreenPos.y;
                pickWorldPoint(sx, sy).then(wp => {
                    if (!isMeasureMode || !pendingPoint) return;
                    if (wp) {
                        const rawDist = pendingPoint.distance(wp);
                        const text = formatDistance(rawDist);
                        if (!previewLabel) {
                            previewLabel = document.createElement('div');
                            previewLabel.className = 'storysplat-measure-label';
                            previewLabel.style.opacity = '0.8';
                            container.appendChild(previewLabel);
                        }
                        previewLabel.textContent = text;
                        const fromSp = worldToScreen(pendingPoint);
                        if (fromSp) {
                            previewLabel.style.left = `${(fromSp.x + sx) / 2}px`;
                            previewLabel.style.top = `${(fromSp.y + sy) / 2}px`;
                        }
                    }
                });
            }

            redraw();
        });

        // Update on every frame (camera movement changes screen positions)
        app.on('update', () => {
            if (!shouldRenderLines()) return;
            if (measureSegments.length > 0 || pendingPoint || measureAreas.length > 0 || areaDraft.length > 0) {
                updateMarkerPositions();
                redraw();
            }
        });

        // A set shown or hidden (the API): redraw what shows and tell listeners.
        if (measureSets) {
            const unsubscribeSets = measureSets.onChange(() => {
                measureGeometryVersion++;
                if (shouldRenderLines()) rebuildMeasurementVisuals();
                else updateMeasurementList();
                redraw();
                renderScheduler.requestFrame('measurement-sets');
            });
            const previousCleanup = _measureCleanupDom;
            _measureCleanupDom = () => {
                unsubscribeSets();
                previousCleanup?.();
            };
        }

        // Auto-enter measurement mode on load when the editor opted into it
        // and there are default measurements to display.
        const autoShowDefaults = uiOpts.defaultMeasurementsVisibleOnLoad
            ?? config.uiOptions?.defaultMeasurementsVisibleOnLoad
            ?? false;
        if (autoShowDefaults && hasLockedSegments()) {
            enterMeasureMode();
        } else if (keepDefaultsVisible && hasLockedSegments()) {
            // Render locked defaults on the canvas without entering measure mode
            // (no measurement list UI, no rubber-band picking — just visible lines + markers).
            rebuildMeasurementVisuals();
            redraw();
        }
    }

    // ═══ AI Skins Dropdown & Overlay ═══
    if (
        config.skins && config.skins.length > 0
        && uiElements.skinsButton && uiElements.skinsDropdown
        && uiElements.skinOverlay && uiElements.skinOverlayWrapper
        && uiElements.skinOpacitySlider && uiElements.skinExitButton
    ) {
        const skinsBtn = uiElements.skinsButton;
        const skinsDropdown = uiElements.skinsDropdown;
        const skinOverlayWrapper = uiElements.skinOverlayWrapper;
        const skinOverlay = uiElements.skinOverlay;
        const opacitySlider = uiElements.skinOpacitySlider;
        const exitBtn = uiElements.skinExitButton;
        const skins = config.skins;

        let activeSkinId: string | null = null;
        // Camera mode we froze when a staging photo was opened (null = not frozen).
        let priorCameraMode: string | null = null;
        let ignoreInputUntil = 0;
        let skinLoadHandler: (() => void) | null = null;
        // Pre-click camera pose, stashed while a staging overlay is shown so
        // walk/explore can restore it on exit. Tour ignores these — it resumes
        // from its untouched scroll progress instead.
        let savedCamPos: pc.Vec3 | null = null;
        let savedCamRot: pc.Quat | null = null;
        let savedCamFov: number | null = null;

        const updateItemHighlights = () => {
            skinsDropdown.querySelectorAll('.storysplat-skin-item').forEach((el) => {
                const id = (el as HTMLElement).getAttribute('data-skin-id');
                el.classList.toggle('active', !!activeSkinId && id === activeSkinId);
            });
        };

        // Show a staging photo without changing camera modes. Clicking a skin
        // used to call setCameraMode('explore'), which on walk-enabled scenes
        // hands off to the CharacterController: it ground-snaps to the *pre-click*
        // pose, then the skin pose is applied on top and gravity drags the camera
        // down through the floor (the "falling through the floor" report). Instead
        // we freeze whatever driver is active so the camera holds the skin's exact
        // captured pose, then unfreeze on exit — no mode switch, no fall.
        const freezeCameraForStaging = () => {
            if (priorCameraMode !== null) return; // already frozen (e.g. switching skins)
            priorCameraMode = currentCameraMode;
            savedCamPos = camera.getPosition().clone();
            savedCamRot = camera.getRotation().clone();
            savedCamFov = camera.camera?.fov ?? null;
            if (currentCameraMode === 'tour') {
                if (isAutoplayEngaged()) pause(); // stop autoplay from advancing progress
                waypointControlEnabled = false;  // freeze scroll + damping in place
            } else if (currentCameraMode === 'walk' && characterController) {
                characterController.disable();    // stop gravity/collision moving the camera
            } else {
                cameraControls.disable();         // explore (orbit/fly)
            }
        };

        const unfreezeCameraFromStaging = () => {
            if (priorCameraMode === null) return;
            const mode = priorCameraMode;
            priorCameraMode = null;
            if (mode === 'tour') {
                // currentProgress never moved while frozen, so re-enabling the damping
                // loop eases the camera from the skin pose back onto the path exactly
                // where the viewer left off — then scrolling continues from there.
                waypointControlEnabled = true;
            } else if (mode === 'walk' && characterController) {
                if (savedCamPos && savedCamRot) {
                    camera.setPosition(savedCamPos);
                    camera.setRotation(savedCamRot);
                }
                characterController.enable(); // re-syncs look angles + ground-snaps from the restored pose
            } else {
                if (savedCamPos && savedCamRot) {
                    camera.setPosition(savedCamPos);
                    camera.setRotation(savedCamRot);
                    cameraControls.syncFromPose(savedCamPos, savedCamRot);
                }
                cameraControls.enable();
            }
            savedCamPos = null;
            savedCamRot = null;
            if (savedCamFov !== null && camera.camera) camera.camera.fov = savedCamFov;
            savedCamFov = null;
        };

        const clearOverlay = (restoreTour: boolean, restoreCamera = true) => {
            if (!activeSkinId) return;
            activeSkinId = null;
            if (skinLoadHandler) {
                skinOverlay.removeEventListener('load', skinLoadHandler);
                skinLoadHandler = null;
            }
            // Stop shimmer on any item whose load was still in flight.
            skinsDropdown.querySelectorAll('.storysplat-skin-item.loading').forEach((el) => {
                el.classList.remove('loading');
            });
            // Fade the wrapper out (opacity transition); img src stays for now
            // so any in-flight load doesn't thrash, but becomes invisible.
            skinOverlayWrapper.classList.remove('active');
            // Only drop the button's active state if the dropdown isn't also open.
            if (!skinsDropdown.classList.contains('open')) {
                skinsBtn.classList.remove('active');
            }
            exitBtn.style.display = 'none';
            updateItemHighlights();
            // restoreTour is retained for call-site compatibility; unfreeze always
            // fully restores the frozen driver (tour resume / walk / explore).
            void restoreTour;
            if (restoreCamera) {
                try { unfreezeCameraFromStaging(); } catch (e) { /* noop */ }
            }
        };

        // Waypoint navigation (next/back buttons, waypoint list, public API)
        // exits staging through this hook — the exact dismissal canvas
        // scroll/click input uses.
        exitVirtualStaging = () => clearOverlay(true);

        const activateSkin = (skinId: string) => {
            const skin = skins.find((s) => s.id === skinId);
            if (!skin) return;

            // Freeze the active camera driver (tour damping / walk / explore) so it
            // holds the skin's captured pose. Staying in the current mode avoids the
            // explore→walk handoff that made the camera fall through the floor, and
            // keeps tour scroll progress intact so exiting resumes where it left off.
            try { freezeCameraForStaging(); } catch (e) { /* noop */ }

            // Babylon → PlayCanvas: negate Z on position/target, (-x,-y,z,w) on quaternion
            const pcPos = new pc.Vec3(skin.cameraPosition.x, skin.cameraPosition.y, -skin.cameraPosition.z);
            const pcTarget = new pc.Vec3(skin.cameraPivot.x, skin.cameraPivot.y, -skin.cameraPivot.z);
            if (skin.cameraRotation) {
                const r = skin.cameraRotation;
                const pcRot = new pc.Quat(-r.x, -r.y, r.z, r.w);
                if (typeof (cameraControls as unknown as { syncFromPose?: unknown }).syncFromPose === 'function') {
                    (cameraControls as unknown as { syncFromPose: (p: pc.Vec3, q: pc.Quat, t?: pc.Vec3) => void })
                        .syncFromPose(pcPos, pcRot, pcTarget);
                }
                // Re-apply to preserve roll (syncFromPose zeros roll in pose angles)
                camera.setPosition(pcPos);
                camera.setRotation(pcRot);
            } else {
                camera.setPosition(pcPos);
                camera.lookAt(pcTarget);
                cameraControls.syncFromCamera(pcTarget);
            }
            // Apply the CAPTURED vertical FOV while the skin is active — the
            // height:100% overlay mapping is only exact when live FOV == capture
            // FOV (per-waypoint FOVs / changed scene FOV otherwise skew scale).
            // freeze/unfreeze saves and restores the prior FOV.
            if (skin.cameraFov && camera.camera) camera.camera.fov = skin.cameraFov;

            // Show overlay. Height-bound: lock the wrapper's aspect to the skin's
            // captured aspect so it aligns pixel-perfectly with the 3D render
            // (constant vertical FOV) regardless of viewer size.
            activeSkinId = skinId;
            const aspect = (skin.canvasWidth && skin.canvasHeight)
                ? skin.canvasWidth / skin.canvasHeight
                : undefined;
            skinOverlayWrapper.style.aspectRatio = aspect ? String(aspect) : '';
            skinOverlay.style.opacity = String(parseInt(opacitySlider.value, 10) / 100);
            skinsBtn.classList.add('active');
            exitBtn.style.display = 'block';
            updateItemHighlights();
            ignoreInputUntil = performance.now() + 400;

            // Reset wrapper to opacity 0 so the fade-in triggers when the
            // image is ready, regardless of cache state.
            skinOverlayWrapper.classList.remove('active');
            if (skinLoadHandler) {
                skinOverlay.removeEventListener('load', skinLoadHandler);
                skinLoadHandler = null;
            }
            // Shimmer the clicked list item while the full-res image loads.
            const loadingItem = skinsDropdown.querySelector(
                `.storysplat-skin-item[data-skin-id="${CSS.escape(skinId)}"]`
            ) as HTMLElement | null;
            skinsDropdown.querySelectorAll('.storysplat-skin-item.loading').forEach((el) => {
                if (el !== loadingItem) el.classList.remove('loading');
            });
            if (loadingItem) loadingItem.classList.add('loading');
            const triggerFade = () => {
                if (loadingItem) loadingItem.classList.remove('loading');
                if (activeSkinId !== skinId) return;
                // Lock the wrapper to the image's OWN aspect. The AI provider's real
                // output aspect (e.g. Gemini ~21:9) rarely matches the captured bucket
                // exactly; sizing to the capture aspect + object-fit:cover would scale
                // the image up (zoom) and break pixel alignment. The pre-load capture
                // aspect above is just an estimate to avoid a layout jump.
                if (skinOverlay.naturalWidth > 0 && skinOverlay.naturalHeight > 0) {
                    skinOverlayWrapper.style.aspectRatio = String(
                        skinOverlay.naturalWidth / skinOverlay.naturalHeight,
                    );
                }
                // rAF ensures the browser commits opacity:0 before switching to
                // opacity:1 so the transition runs (not an instant snap).
                requestAnimationFrame(() => {
                    if (activeSkinId !== skinId) return;
                    skinOverlayWrapper.classList.add('active');
                });
            };
            const sameSrc = skinOverlay.src === skin.imageUrl;
            if (sameSrc && skinOverlay.complete && skinOverlay.naturalWidth > 0) {
                triggerFade();
            } else {
                skinLoadHandler = () => {
                    skinOverlay.removeEventListener('load', skinLoadHandler!);
                    skinLoadHandler = null;
                    triggerFade();
                };
                skinOverlay.addEventListener('load', skinLoadHandler);
                if (!sameSrc) skinOverlay.src = skin.imageUrl;
            }
        };

        // Keep the button's `active` class in sync with either dropdown-open or skin-applied.
        const syncSkinsBtnActive = () => {
            const shouldBeActive = skinsDropdown.classList.contains('open') || !!activeSkinId;
            skinsBtn.classList.toggle('active', shouldBeActive);
        };

        // Only-one-open coordination: closing the staging picker shuts the
        // dropdown but keeps any already-applied skin (syncSkinsBtnActive keeps
        // the button active when a skin is live).
        uiElements.exclusiveTools?.register('skins', () => {
            skinsDropdown.classList.remove('open');
            syncSkinsBtnActive();
        });

        // Click the palette icon → toggle dropdown (and mirror open state on the button)
        skinsBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const willOpen = !skinsDropdown.classList.contains('open');
            skinsDropdown.classList.toggle('open', willOpen);
            syncSkinsBtnActive();
            if (willOpen) uiElements.exclusiveTools?.closeOthers('skins');
        });

        // Close dropdown on outside click (but keep overlay visible).
        // Handler is hoisted so cleanup paths can remove it.
        const outsideClickHandler = (e: MouseEvent) => {
            if (skinsDropdown.contains(e.target as Node)) return;
            if (skinsBtn.contains(e.target as Node)) return;
            skinsDropdown.classList.remove('open');
            syncSkinsBtnActive();
        };
        document.addEventListener('click', outsideClickHandler);

        // Skin list item clicks
        skinsDropdown.querySelectorAll('.storysplat-skin-item').forEach((el) => {
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                const id = (el as HTMLElement).getAttribute('data-skin-id');
                if (id) activateSkin(id);
            });
        });

        // Opacity slider
        opacitySlider.addEventListener('input', () => {
            skinOverlay.style.opacity = String(parseInt(opacitySlider.value, 10) / 100);
        });

        // Exit button
        exitBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            clearOverlay(true);
        });

        // Auto-exit on camera input
        const CAMERA_KEYS = new Set([
            'w', 'a', 's', 'd', 'q', 'e', 'W', 'A', 'S', 'D', 'Q', 'E',
            'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        ]);
        const handleInput = (ev: Event) => {
            if (!activeSkinId) return;
            if (performance.now() < ignoreInputUntil) return;
            if (ev.type === 'keydown') {
                const key = (ev as KeyboardEvent).key;
                if (!CAMERA_KEYS.has(key)) return;
            }
            clearOverlay(true);
        };
        canvas.addEventListener('pointerdown', handleInput);
        canvas.addEventListener('wheel', handleInput, { passive: true });
        window.addEventListener('keydown', handleInput);

        // Per-waypoint staging visibility: a skin with a waypointIndex only
        // surfaces while the tour is on that waypoint; skins without one are
        // always available. The whole staging button hides when nothing is
        // available at the current waypoint.
        const anySkinIsWaypointScoped = skins.some((s) => typeof s.waypointIndex === 'number');
        // The toolbar paints its own frosted-glass background, border and padding,
        // so an empty toolbar (every child hidden at runtime) leaves a ~10px sliver:
        // syncToolbarVisibility (viewerUI.ts) hides it while no child icon is visible.
        const refreshSkinVisibilityForWaypoint = () => {
            if (!anySkinIsWaypointScoped) return; // legacy scenes: button always shown
            const wp = currentWaypointIndex;
            // Waypoint-scoped staging only makes sense in tour mode, where currentWaypointIndex
            // tracks the tour. In explore/walk the waypoint index is frozen at whatever it was when
            // the user left tour mode, so a scoped skin's visibility would be stale. For now, hide
            // waypoint-scoped skins outside tour mode entirely; un-scoped skins (no waypointIndex)
            // always show. (True proximity-based visibility while free-roaming is a separate change.)
            const inTour = currentCameraMode === 'tour';
            let availableCount = 0;
            skinsDropdown.querySelectorAll('.storysplat-skin-item').forEach((el) => {
                const attr = (el as HTMLElement).getAttribute('data-skin-waypoint');
                const available = attr === null || (inTour && parseInt(attr, 10) === wp);
                (el as HTMLElement).style.display = available ? '' : 'none';
                if (available) availableCount++;
            });
            if (availableCount === 0) {
                // Nothing here — hide the button and any open dropdown / live overlay.
                if (activeSkinId) clearOverlay(false);
                skinsDropdown.classList.remove('open');
                skinsBtn.style.display = 'none';
            } else {
                skinsBtn.style.display = '';
            }
            // Hidden/shown the skins button — fold the toolbar container away when
            // it would otherwise render as an empty frosted-glass sliver.
            syncToolbarVisibility(uiElements.toolbar);
        };
        refreshSkinVisibilityForWaypoint();
        events.on('waypointChange', refreshSkinVisibilityForWaypoint);
        // Re-evaluate on camera-mode change too, so scoped staging hides the moment you enter
        // explore/walk (and reappears on return to tour) instead of freezing its last tour state.
        events.on('modeChange', refreshSkinVisibilityForWaypoint);

        const cleanupSkins = createSkinUiTeardown({
            button: skinsBtn,
            dropdown: skinsDropdown,
            overlayWrapper: skinOverlayWrapper,
            overlay: skinOverlay,
            canvas,
            outsideClickHandler,
            inputHandler: handleInput,
            // Teardown is immediately destroying the current camera drivers;
            // re-enabling one here can resurrect handlers on a destroyed
            // CharacterController during external destroy/recreate navigation.
            clearOverlay: () => clearOverlay(false, false),
            removeSceneListeners: () => {
                exitVirtualStaging = null;
                events.off('waypointChange', refreshSkinVisibilityForWaypoint);
                events.off('modeChange', refreshSkinVisibilityForWaypoint);
            },
        });
        const previousUiCleanup = uiElements._cleanup;
        uiElements._cleanup = () => {
            if (previousUiCleanup) previousUiCleanup();
            cleanupSkins();
        };
    }

    // ═══ Camera pose & offline capture (public: video export, virtual staging) ═══
    const getCameraPose = (): CameraPose => {
        const position = camera.getPosition();
        const rotation = camera.getRotation();
        return {
            position: { x: position.x, y: position.y, z: position.z },
            rotation: { x: rotation.x, y: rotation.y, z: rotation.z, w: rotation.w },
            fov: camera.camera?.fov ?? 60,
        };
    };
    // Deterministic tour-path pose sampling for offline rendering (video export).
    // Mirrors updateCameraFromProgress exactly but returns the pose instead of
    // feeding the damped-interpolation targets, so a renderer can step the
    // camera frame-by-frame without any time-dependent easing.
    const getPathPoseAtProgress = (progress: number): CameraPose | null => {
        if (waypointPositions.length < 2) return null;
        const wpCount = waypointPositions.length;
        const isLooping = loopMode === 'loop';
        const numSegments = isLooping ? wpCount : wpCount - 1;
        const p = timelineProgressToPathProgress(progress, loopMode);
        const segmentProgress = p * numSegments;
        const segmentIndex = Math.min(Math.floor(segmentProgress), numSegments - 1);
        const t = segmentProgress - segmentIndex;
        const pathNodes = buildWaypointPathNodes(waypointPositions, config.waypoints);
        const segmentIndices = getSegmentIndices(pathNodes, segmentIndex, loopMode);
        const sampledPosition = sampleWaypointPathSegment(pathNodes, segmentIndex, t, loopMode, pathInterpolationMode);
        if (!segmentIndices || !sampledPosition) return null;
        const rotation = new pc.Quat();
        rotation.slerp(waypointRotations[segmentIndices.startIndex], waypointRotations[segmentIndices.endIndex], t);
        return {
            position: { x: sampledPosition.x, y: sampledPosition.y, z: sampledPosition.z },
            rotation: { x: rotation.x, y: rotation.y, z: rotation.z, w: rotation.w },
            fov: lerp(waypointFOVs[segmentIndices.startIndex], waypointFOVs[segmentIndices.endIndex], t),
        };
    };
    const getPathRenderInfo = (): PathRenderInfo => ({
        waypointCount: config.waypoints?.length || 0,
        loopMode,
        defaultDurationSec: playbackSpeed > 0 ? (loopMode === 'pingpong' ? 2 : 1) / playbackSpeed : 10,
    });
    // "Autoplay feel" for offline rendering: a follower that damps toward the path pose
    // with the same pull the live tour uses (updateCameraDamping / PULL_STRENGTH, which
    // tracks the Transition Speed setting), stepped at the export's fixed frame time.
    const createPathPoseFollower = (): CameraPoseFollower => new DampedPoseFollower(PULL_STRENGTH);

    const restoreExploreControls = () => {
        if (!renderCaptureActive && !gizmoInputSuppressed && currentCameraMode === 'explore') {
            cameraControls.enable();
        }
    };

    // Pose hold: freezes the active camera driver (tour damping, walk, fly/orbit,
    // drone) through the shared render-capture flag, so the camera stays exactly
    // where the host put it — e.g. under a virtual-staging overlay.
    let cameraHold: { mode: string; position: pc.Vec3; rotation: pc.Quat; fov: number | null } | null = null;
    const holdCameraPose = (pose?: CameraPose) => {
        if (isDestroyed) return;
        assertNoFrameCapture('hold the camera');
        if (!cameraHold) {
            // The scene's own staging overlay keeps separate freeze state; dismiss it first.
            exitVirtualStaging?.();
            cameraHold = {
                mode: currentCameraMode,
                position: camera.getPosition().clone(),
                rotation: camera.getRotation().clone(),
                fov: camera.camera?.fov ?? null,
            };
            if (isAutoplayEngaged()) pause();
            isUserDragging = false;
            cameraHoldActive = true;
            syncRenderCaptureActive();
            cameraControls.disable();
        }
        if (pose) {
            camera.setPosition(pose.position.x, pose.position.y, pose.position.z);
            camera.setRotation(new pc.Quat(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w).normalize());
            if (camera.camera && Number.isFinite(pose.fov)) camera.camera.fov = pose.fov;
        }
        renderScheduler.requestFrame('camera-hold');
    };
    const releaseCameraPose = () => {
        if (!cameraHold) return;
        const hold = cameraHold;
        cameraHold = null;
        cameraHoldActive = false;
        syncRenderCaptureActive();
        if (!isDestroyed) {
            // Tour resumes from its untouched progress: damping eases the camera back
            // onto the path. Walk/explore return to where the visitor was.
            if (hold.mode !== 'tour') {
                camera.setPosition(hold.position);
                camera.setRotation(hold.rotation);
                if (currentCameraMode === 'explore') cameraControls.syncFromPose(hold.position, hold.rotation);
            }
            if (hold.fov !== null && camera.camera) camera.camera.fov = hold.fov;
            restoreExploreControls();
            renderScheduler.requestFrame('camera-release');
        }
    };

    // ═══ Splat swaps in captured video (CaptureSwapDriver, captureSwaps.ts) ═══
    /** Longest a captured frame waits for a swap load before it renders anyway. */
    const CAPTURE_SWAP_LOAD_TIMEOUT_MS = 120_000;
    /** Longest a captured frame waits for a skybox a swap applied. */
    const CAPTURE_SKYBOX_TIMEOUT_MS = 15_000;
    const waitAtMost = (promise: Promise<void>, ms: number): Promise<boolean> => new Promise((resolve) => {
        const timer = setTimeout(() => resolve(false), ms);
        void promise.then(() => {
            clearTimeout(timer);
            resolve(true);
        });
    });
    // Before every render during a capture: a splat shown this frame binds its transition shader
    // (at the transition's current time) before it draws, even with the engine tick paused.
    const syncCaptureSwapTransitions = () => forEachSwapTransition((script) => script.advance(0));
    const captureAreaPosition = new pc.Vec3();
    const captureSwapHost: CaptureSwapHost = {
        advanceTransitions: (dtSec) => forEachSwapTransition((script) => script.advance(dtSec)),
        finishTransitions: () => forEachSwapTransition((script) => script.finish()),
        suspendTransitions: () => {
            swapTransitionsCut = true;
            return () => { swapTransitionsCut = false; };
        },
        applySwaps: (pose, timelineProgress) => {
            const cs = captureSwaps;
            if (!cs || isDestroyed) return;
            cs.progress = timelineProgress === null ? null : timelineProgressToPathProgress(timelineProgress);
            cs.splatChanged = false;
            if (cs.framesApplied === 0 && cs.progress !== null) {
                // Transition direction (the wipe's sweep) counts from where the video starts.
                lastSwapProgress = cs.progress * 100;
            }
            cs.framesApplied++;
            captureAreaPosition.set(pose.position.x, pose.position.y, pose.position.z);
            cs.areaController.update({
                cameraScenePosition: toRegionScenePosition(captureAreaPosition),
                progress: swapTourProgress(),
                waypointIndex: -1,
                timeMs: cs.framesApplied,
            });
            // Re-check even without an area edge: the first frame settles where the capture began.
            reconcileAreaSwap();
            if (cs.progress !== null) updateSplats();
        },
        settle: async (pose) => {
            const deadline = performance.now() + CAPTURE_SWAP_LOAD_TIMEOUT_MS;
            while (!isDestroyed && isLoadingSplat) {
                const remaining = deadline - performance.now();
                if (remaining <= 0 || !(await waitAtMost(whenSwapLoadIdle(), remaining))) {
                    console.warn('[SplatSwap] Capture frame rendered before a swap splat finished loading');
                    break;
                }
            }
            if (!isDestroyed && !(await waitAtMost(whenSkyboxIdle(), CAPTURE_SKYBOX_TIMEOUT_MS))) {
                console.warn('[SplatSwap] Capture frame rendered before a swap skybox finished loading');
            }
            syncCaptureSwapTransitions();
            if (!isDestroyed && captureSwaps?.splatChanged) await warmUpSwappedSplats(pose);
        },
    };
    // A splat that just came on screen (a unified one re-sorts from scratch, a LOD one streams)
    // can take longer to be ready than a capture's per-frame tick cap: render it off the record
    // at the frame's pose until the engine reports it ready, so the frame shows it complete.
    const CAPTURE_SWAP_READY_TIMEOUT_MS = 10_000;
    const captureWarmPosition = new pc.Vec3();
    const captureWarmRotation = new pc.Quat();
    async function warmUpSwappedSplats(pose: CameraPose): Promise<void> {
        const camComp = camera.camera;
        if (!camComp) return;
        captureWarmPosition.set(pose.position.x, pose.position.y, pose.position.z);
        captureWarmRotation.set(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w).normalize();
        const applyWarmPose = () => {
            camera.setPosition(captureWarmPosition);
            camera.setRotation(captureWarmRotation);
            camComp.fov = pose.fov;
        };
        applyWarmPose();
        await renderUntilSplatsReady(app, camComp, applyWarmPose, CAPTURE_SWAP_READY_TIMEOUT_MS);
    }
    events.on('splatChange', () => {
        if (captureSwaps) captureSwaps.splatChanged = true;
    });
    /** Hand splat swaps over to captured frames (see CaptureSwapSession). */
    function beginCaptureSwaps(owner: CaptureSwapSession['owner']): CaptureSwapSession | null {
        if (captureSwaps) return captureSwaps.owner === owner ? captureSwaps : null;
        if (isDestroyed) return null;
        const areas = new SwapAreaState();
        // A visitor standing in an area: the frames decide afresh, starting from the splat they
        // would get back on leaving it.
        if (swapAreas.isActive) areas.resumeFrom(swapAreas.baseUrl ?? getCurrentSplatUrl());
        const areaController = new TriggerController<SwapAreaItem>({
            show: (item) => { areas.enter(item.swapUrl, { keep: item.keep }); reconcileAreaSwap(); },
            hide: (item) => { areas.leave(item.swapUrl); reconcileAreaSwap(); },
        });
        const session: CaptureSwapSession = {
            owner,
            driver: new CaptureSwapDriver(captureSwapHost),
            areas,
            areaController,
            progress: null,
            framesApplied: 0,
            splatChanged: false,
            loadedBefore: new Set(preloadedSplats.keys()),
            saved: {
                splatUrl: getCurrentSplatUrl(),
                skyboxUrl: currentSkyboxUrl(),
                skyboxRotation: config.skybox?.rotation ?? config.skyboxRotation ?? 0,
                areaSwapSkyboxChanged,
                keptAreaHold,
                lastSwapProgress,
            },
        };
        captureSwaps = session;
        // Built here even in the editor (where areas never run live): the video shows them.
        areaController.setItems(isCompareModeEnabled ? [] : buildSwapAreaItems());
        keptAreaHold = null;
        failedSwapTargetUrl = null;
        lastSplatCheckProgress = -1;
        lastSplatCheckWaypointIndex = -1;
        swapTransitionClock.manual = true;
        app.on('prerender', syncCaptureSwapTransitions);
        return session;
    }
    /** Put a splat straight back on screen (end of a capture): the primary or a swap splat. */
    function cutBackToSplat(url: string): void {
        if (url === getPrimarySplatUrl()) {
            returnToPrimarySplat(false);
            return;
        }
        const entity = preloadedSplats.get(url);
        if (!entity) {
            // Not loaded any more (it was not when the capture began): load it as usual.
            const swap = additionalSplats.find((s) => s.url === url);
            const overrides = swap && (swap.position || swap.rotation || swap.scale)
                ? { position: swap.position, rotation: swap.rotation, scale: swap.scale }
                : undefined;
            void whenSwapLoadIdle().then(() => loadSwapSplat(url, false, overrides));
            return;
        }
        if (splatEntity && splatEntity !== entity) {
            cancelSwapTransitions(splatEntity);
            splatEntity.enabled = false;
        }
        preloadedSplats.forEach((other) => {
            if (other !== entity && other.enabled) {
                cancelSwapTransitions(other);
                other.enabled = false;
            }
        });
        showSplat(url);
        cancelSwapTransitions(entity);
        events.emit('splatChange', { url, isOriginal: false });
    }
    /** End a capture's hold on splat swaps: put back what was on screen, resume live swaps. */
    function endCaptureSwaps(owner: CaptureSwapSession['owner']): void {
        const cs = captureSwaps;
        if (!cs || cs.owner !== owner) return;
        app.off('prerender', syncCaptureSwapTransitions);
        // A capture swap still loading (the capture was cancelled mid-frame) finishes hidden.
        swapShowGeneration++;
        forEachSwapTransition((script) => script.finish());
        captureSwaps = null;
        swapTransitionClock.manual = false;
        if (isDestroyed) return;
        const { saved } = cs;
        swapTransitionsCut = true;
        try {
            if (getCurrentSplatUrl() !== saved.splatUrl) cutBackToSplat(saved.splatUrl);
            const rotation = config.skybox?.rotation ?? config.skyboxRotation ?? 0;
            if (currentSkyboxUrl() !== saved.skyboxUrl || rotation !== saved.skyboxRotation) {
                if (saved.skyboxUrl) applySkybox(saved.skyboxUrl, saved.skyboxRotation);
                else setSkyboxRuntime(null);
            }
        } finally {
            swapTransitionsCut = false;
        }
        areaSwapSkyboxChanged = saved.areaSwapSkyboxChanged;
        keptAreaHold = saved.keptAreaHold;
        lastSwapProgress = saved.lastSwapProgress;
        failedSwapTargetUrl = null;
        lastSplatCheckProgress = -1;
        lastSplatCheckWaypointIndex = -1;
        // Free the swap splats only the capture loaded (keepMeshesInMemory keeps them, as live).
        if (!keepMeshesInMemory) {
            const onScreen = getCurrentSplatUrl();
            for (const url of Array.from(preloadedSplats.keys())) {
                const entity = preloadedSplats.get(url);
                if (url !== onScreen && !cs.loadedBefore.has(url) && entity && !entity.enabled) disposeSplat(url);
            }
        }
        // Live areas carry on from the splat put back (their state was left as it was).
        reconcileAreaSwap();
        renderScheduler.requestFrame('capture-swaps-ended');
    }

    let activeFrameCapture: FrameCaptureSession | null = null;
    const beginFrameCapture = (options: FrameCaptureOptions): FrameCaptureSession => {
        if (isDestroyed) throw new Error('[StorySplat Viewer] Cannot capture after viewer is destroyed.');
        if (activeFrameCapture) throw new Error('[StorySplat Viewer] A frame capture is already running.');
        validateCaptureSize(options.width, options.height);
        validateCaptureFps(options.fps);
        if (isAutoplayEngaged()) pause();
        exitVirtualStaging?.();
        const restoreHidden = options.hideMarkers === false
            ? hideForCapture([reticleLayer])
            : hideSceneMarkersForCapture();
        isUserDragging = false;
        frameCaptureActive = true;
        syncRenderCaptureActive();
        cameraControls.disable();
        // Splat swaps follow the captured frames (null: the editor's render already owns them).
        const swaps = beginCaptureSwaps('frame-capture');
        const finish = () => {
            restoreHidden();
            endCaptureSwaps('frame-capture');
            frameCaptureActive = false;
            syncRenderCaptureActive();
            restoreExploreControls();
            activeFrameCapture = null;
        };
        try {
            activeFrameCapture = createFrameCaptureSession({
                app,
                camera,
                width: options.width,
                height: options.height,
                fps: options.fps,
                prepareFrame: swaps
                    ? (pose, frame) => swaps.driver.prepareFrame({ pose, progress: frame.progress, dtSec: frame.dtSec })
                    : undefined,
                onEnd: finish,
            });
        } catch (error) {
            finish();
            throw error;
        }
        return activeFrameCapture;
    };

    // Return viewer instance
    const instance: ViewerInstance = {
        app,
        canvas,
        capturePhoto,
        captureWide,
        shareCurrentView,
        setCutaway,
        getCutaway,
        setPhotoMode,
        getPhotoMode,
        setStereoMode,
        getStereoMode,
        isStereoAvailable,
        setStereoOptions,
        takePhoto,
        // Camera pose & offline capture
        getCameraPose,
        holdCameraPose,
        releaseCameraPose,
        getPathPoseAtProgress,
        getPathRenderInfo,
        createPathPoseFollower,
        beginFrameCapture,
        // Navigation
        goToWaypoint: (index: number) => {
            if (currentCameraMode === 'explore') {
                throw new Error('[StorySplat Viewer] goToWaypoint() requires tour mode. Call setCameraMode("tour") first.');
            }
            goToWaypoint(index);
        },
        nextWaypoint: () => {
            if (currentCameraMode === 'explore') {
                throw new Error('[StorySplat Viewer] nextWaypoint() requires tour mode. Call setCameraMode("tour") first.');
            }
            nextWaypoint();
        },
        prevWaypoint: () => {
            if (currentCameraMode === 'explore') {
                throw new Error('[StorySplat Viewer] prevWaypoint() requires tour mode. Call setCameraMode("tour") first.');
            }
            prevWaypoint();
        },
        getCurrentWaypointIndex: () => currentWaypointIndex,
        getWaypointCount: () => config.waypoints?.length || 0,
        // Extra waypoints: named views off the tour path. Works from any camera mode (it travels
        // in explore mode), unlike goToWaypoint.
        goToExtraWaypoint: (id: string, options?: { instant?: boolean }) => goToExtraWaypointById(id, options?.instant === true),
        getExtraWaypoints: () => (config.extraWaypoints || []).map((wp) => ({ id: wp.id, name: wp.name || '' })),
        // Camera
        setPosition: (x, y, z) => {
            if (currentCameraMode !== 'explore') {
                throw new Error('[StorySplat Viewer] setPosition() requires explore mode. Call setCameraMode("explore") first.');
            }
            if (isPlaying) {
                throw new Error('[StorySplat Viewer] setPosition() cannot be used during autoplay. Call pause() or stop() first.');
            }
            // Disable controls to flush input, set camera, sync controller state, re-enable
            cameraControls.disable();
            camera.setPosition(x, y, z);
            cameraControls.syncFromCamera();
            cameraControls.enable();
            // Re-sync on next frame to ensure controller didn't interpolate back
            requestAnimationFrame(() => {
                camera.setPosition(x, y, z);
                cameraControls.syncFromCamera();
            });
        },
        setRotation: (x, y, z) => {
            if (currentCameraMode !== 'explore') {
                throw new Error('[StorySplat Viewer] setRotation() requires explore mode. Call setCameraMode("explore") first.');
            }
            if (isPlaying) {
                throw new Error('[StorySplat Viewer] setRotation() cannot be used during autoplay. Call pause() or stop() first.');
            }
            cameraControls.disable();
            camera.setEulerAngles(x, y, z);
            cameraControls.syncFromCamera();
            cameraControls.enable();
            requestAnimationFrame(() => {
                camera.setEulerAngles(x, y, z);
                cameraControls.syncFromCamera();
            });
        },
        getPosition: () => {
            const pos = camera.getPosition();
            return { x: pos.x, y: pos.y, z: pos.z };
        },
        getRotation: () => {
            const rot = camera.getEulerAngles();
            return { x: rot.x, y: rot.y, z: rot.z };
        },
        // Playback
        // Frame sequence playback works in any camera mode; only waypoint
        // playback requires tour mode.
        play: () => {
            if (!frameSequencePlayer && currentCameraMode === 'explore') {
                throw new Error('[StorySplat Viewer] play() requires tour mode. Call setCameraMode("tour") first.');
            }
            play();
        },
        pause: () => {
            if (!frameSequencePlayer && currentCameraMode === 'explore') {
                throw new Error('[StorySplat Viewer] pause() requires tour mode. Call setCameraMode("tour") first.');
            }
            pause();
        },
        stop: () => {
            if (!frameSequencePlayer && currentCameraMode === 'explore') {
                throw new Error('[StorySplat Viewer] stop() requires tour mode. Call setCameraMode("tour") first.');
            }
            stop();
        },
        isPlaying: () => isPlaying,
        // Entity Animation API — controls keyframe entity animations (no-ops when the scene has none)
        playEntityAnimation: (id?: string) => {
            if (!entityAnimationSystem) return;
            if (id !== undefined) entityAnimationSystem.playAnimation(id);
            else entityAnimationSystem.play();
        },
        pauseEntityAnimation: (id?: string) => {
            if (!entityAnimationSystem) return;
            if (id !== undefined) entityAnimationSystem.pauseAnimation(id);
            else entityAnimationSystem.pause();
        },
        resetEntityAnimation: (id?: string) => {
            if (!entityAnimationSystem) return;
            if (id !== undefined) entityAnimationSystem.resetAnimation(id);
            else entityAnimationSystem.reset();
        },
        getEntityAnimations: () => entityAnimationSystem?.listAnimations() ?? [],
        restartEntityAnimation: (id: string) => {
            entityAnimationSystem?.restartAnimation(id);
            renderScheduler.requestFrame('entity-animation');
        },
        seekEntityAnimation: (id: string, seconds: number) => {
            entityAnimationSystem?.seekAnimation(id, seconds);
            renderScheduler.requestFrame('entity-animation');
        },
        isEntityAnimationPlaying: (id: string) => entityAnimationSystem?.isAnimationPlaying(id) ?? false,
        // 4DGS Frame Sequence API (only functional when frameSequence is configured)
        isFrameSequencePlaying: () => frameSequencePlayer?.getIsPlaying() ?? false,
        setFrame: (index: number) => frameSequencePlayer?.setFrame(index),
        getCurrentFrame: () => frameSequencePlayer?.getCurrentFrame() ?? 0,
        getTotalFrames: () => frameSequencePlayer?.getTotalFrames() ?? 0,
        setFps: (fps: number) => frameSequencePlayer?.setFps(fps),
        getFps: () => frameSequencePlayer?.getFps() ?? 24,
        getFrameProgress: () => frameSequencePlayer?.getProgress() ?? 0,
        setFrameProgress: (progress: number) => frameSequencePlayer?.setProgress(progress),
        // Splat API
        goToOriginalSplat,
        goToSplat,
        getCurrentSplatUrl,
        isShowingOriginalSplat,
        getAdditionalSplats: () => additionalSplats.map(s => ({
            url: s.url,
            name: s.name,
            waypointIndex: s.waypointIndex,
            percentage: s.percentage
        })),
        // Compare mode API
        isCompareMode: () => isCompareModeEnabled,
        getComparePosition: () => comparePosition,
        setComparePosition: (pos: number) => {
            setComparePositionInternal(pos);
            if (compareSlider) compareSlider.setPosition(pos);
        },
        // Lifecycle
        destroy: () => {
            if (activeFrameCapture) {
                try { activeFrameCapture.end(); } catch { /* capture cleanup must not block destroy */ }
            }
            cameraHold = null;
            layersPanel?.destroy();
            layersPanel = null;
            directoryPanel?.destroy();
            directoryPanel = null;
            directoryState.destroy();
            // Before regionEffects.destroy(): removing the regions effect goes through it.
            regionsRuntime.destroy();
            windSystem.destroy();
            regionEffects.destroy();
            overlayController.destroy();
            destroyOverlayLayer(overlayLayer);
            document.removeEventListener('keydown', onOverlayEscape);
            // Set destroyed flag first to prevent async operations from completing
            isDestroyed = true;
            teardownStereo(false);
            assetLeaseHandle?.dispose();
            assetLeaseHandle = null;
            // Flush any unreported streaming bytes before teardown
            if (bandwidthReporter) {
                try { bandwidthReporter.dispose(); } catch { /* bandwidth cleanup must not block destroy */ }
                bandwidthReporter = null;
            }
            clearTourLookHintTimer();
            if (portalEnterLabelEl) { portalEnterLabelEl.remove(); portalEnterLabelEl = null; }
            pause();
            stop8thWallAR();
            xrTeardown?.();
            // Destroy compare mode overlay
            destroyCompareMode();
            // Destroy entity animation system
            if (entityAnimationSystem) {
                entityAnimationSystem.destroy();
                entityAnimationSystem = null;
            }
            // Destroy frame sequence player if exists
            if (frameSequencePlayer) {
                frameSequencePlayer.destroy();
                frameSequencePlayer = null;
            }
            // Clean up narration audio
            cleanupNarrationAudio();
            window.removeEventListener('resize', handleResize);
            if (portalNav) window.removeEventListener('popstate', onPortalPopstate);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            teardownOnDemandRendering();
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('pageshow', handlePageShow);
            audioContextsSuspendedForVisibility.clear();
            audioContextVisibilitySuspendPending.clear();
            audioContextVisibilityResumeRequested.clear();
            // Clean up editor marker and any stray error popups
            container.removeAttribute('data-storysplat-editor');
            container.querySelector('.storysplat-error-popup')?.remove();
            closePhotoSharePanel(container); // also drops its document Escape listener
            closeShareLinkPopover(container);
            container.querySelector('.storysplat-share-toast')?.remove();
            container.querySelector('.storysplat-ar-error')?.remove();
            // Remove UI elements
            if (uiElements.preloader)
                uiElements.preloader.remove();
            if (uiElements.scrollControls)
                uiElements.scrollControls.remove();
            if (uiElements.fullscreenButton)
                uiElements.fullscreenButton.remove();
            if (uiElements.helpButton)
                uiElements.helpButton.remove();
            if (uiElements.helpPanel)
                uiElements.helpPanel.remove();
            if (uiElements.waypointInfo)
                uiElements.waypointInfo.remove();
            if (uiElements.watermark)
                uiElements.watermark.remove();
            if (uiElements.wasdHint)
                uiElements.wasdHint.remove();
            if (uiElements.orbitHint)
                uiElements.orbitHint.remove();
            if (uiElements.doubleTapHint)
                uiElements.doubleTapHint.remove();
            if (uiElements.lookZone)
                uiElements.lookZone.remove();
            if (uiElements.joystick)
                uiElements.joystick.remove();
            if (uiElements.muteButton)
                uiElements.muteButton.remove();
            if (uiElements.microphoneButton)
                uiElements.microphoneButton.remove();
            if (uiElements.relightingButton)
                uiElements.relightingButton.remove();
            if (uiElements.waypointListContainer)
                uiElements.waypointListContainer.remove();
            if (uiElements.sceneMenuContainer)
                uiElements.sceneMenuContainer.remove();
            if (uiElements.shareButton)
                uiElements.shareButton.remove();
            if (uiElements.fpsCounter)
                uiElements.fpsCounter.remove();
            // Release any open hotspot lightbox first: it locks document.body scroll and adds a
            // document keydown listener that are only freed by its own cleanup. Removing the popup
            // node directly would leave the host page permanently unscrollable after viewer destroy.
            hideHotspotPopup(container);
            if (uiElements.hotspotPopup)
                uiElements.hotspotPopup.remove();
            if (uiElements.portalPopup)
                uiElements.portalPopup.remove();
            if (uiElements.vrButton)
                uiElements.vrButton.remove();
            if (uiElements.arButton)
                uiElements.arButton.remove();
            if (uiElements.modeContainer)
                uiElements.modeContainer.remove();
            if (uiElements.exploreControls)
                uiElements.exploreControls.remove();
            // Remove injected styles
            const styleEl = document.getElementById('storysplat-viewer-styles');
            if (styleEl)
                styleEl.remove();
            // Remove container class
            container.classList.remove('storysplat-viewer-container');
            // Cleanup animated GIFs
            animatedGifs.forEach(gif => gif.destroy());
            animatedGifs.length = 0;
            // Cleanup custom meshes
            cleanupCustomMeshes();
            // Clear collision references from CameraControls before destroying CharacterController
            // (CharacterController.destroy() destroys the collision entities)
            cameraControls.setCollisionEntities([]);
            // Cleanup CharacterController
            if (characterController) {
                characterController.destroy();
            }
            // Cleanup Custom Script System
            const destroyScriptSys = (app as ViewerRuntimeApp).__customScriptSystem;
            if (destroyScriptSys) {
                destroyScriptSys.dispose();
            }
            // Cleanup HTML Mesh Manager
            const destroyHtmlMgr = (app as ViewerRuntimeApp).__htmlMeshManager;
            if (destroyHtmlMgr) {
                destroyHtmlMgr.destroy();
            }
            // Cleanup reticle
            reticleMat.destroy();
            reticleMesh.destroy();
            reticleEntity.destroy();
            canvasEl.removeEventListener('mousemove', reticleMouseMoveHandler);
            if (_measurePicker) { _measurePicker.destroy(); _measurePicker = null; }
            if (_measureKeydownHandler) { document.removeEventListener('keydown', _measureKeydownHandler); _measureKeydownHandler = null; }
            if (_movementKeydownHandler) { document.removeEventListener('keydown', _movementKeydownHandler); _movementKeydownHandler = null; }
            // Cleanup hidden FPV drone mode
            if (droneController) { droneController.disable(); droneController = null; }
            if (fpvKeydownHandler) { window.removeEventListener('keydown', fpvKeydownHandler); fpvKeydownHandler = null; }
        if (panoramaRestoreTimer) {
            clearTimeout(panoramaRestoreTimer);
            panoramaRestoreTimer = null;
        }
        if (panoramaExitButton) {
            const escHandler = (panoramaExitButton as any)._escHandler;
            if (escHandler) document.removeEventListener('keydown', escHandler);
            panoramaExitButton.remove();
            panoramaExitButton = null;
        }
        if (uiElements._cleanup) { uiElements._cleanup(); uiElements._cleanup = undefined; }
            if (_measureCleanupDom) { _measureCleanupDom(); _measureCleanupDom = null; }
            refreshMeasurePanelLabels = null;
            vrMeasurementSource = null;
            vrUi?.destroy();
            vrUi = null;
            // Stop hotspot videos + close their Web Audio graphs (parity with the portal path):
            // app.destroy() stops PlayCanvas sound slots but NOT the per-video AudioContext →
            // MediaElementSource graphs — a running never-closed AudioContext isn't GC-eligible
            // and pins its detached looping video, so unmuted/spatial hotspot video audio kept
            // audibly playing after destroy (and one context + decode pipeline leaked per hotspot
            // per unmount).
            hotspotEntities.forEach((entity) => {
                const rt = entity as ViewerRuntimeEntity;
                if (rt.videoElement) {
                    rt.videoElement.pause();
                    rt.videoElement.src = '';
                }
                if (rt.alphaVideoElement) {
                    rt.alphaVideoElement.pause();
                    rt.alphaVideoElement.src = '';
                }
            });
            allAudioContexts.forEach(ctx => { ctx.close().catch(() => {}); });
            allAudioContexts.length = 0;
            // Playground teardown parity with the portal path: stop it while the app is still
            // alive (ball entities are destroyed through live systems) and drop the window
            // keydown handler — otherwise Ctrl+Shift+P after destroy ran startPlayground on the
            // destroyed app, and the handler pinned the whole dead-viewer closure. The identity
            // check keeps an older instance's late destroy from clobbering a newer viewer's stash.
            stopPlayground();
            window.removeEventListener('keydown', _playgroundKeyHandler);
            if ((window as any).__storysplat_playground_handler === _playgroundKeyHandler) {
                delete (window as any).__storysplat_playground_handler;
            }
            // Remove FXAA
            if (fxaaEffect && camera.camera) {
                try { camera.camera.postEffects.removeEffect(fxaaEffect); } catch (_e) { /* ok */ }
                fxaaEffect = null;
            }
            // Destroy CameraFrame (post-processing)
            if (cameraFrame) {
                cameraFrame.destroy();
                cameraFrame = null;
            }
            // Splat depth pass for translucent entities (restores the renderer's frame-graph build)
            splatDepth?.uninstall();
            // The splat draw's split at the water (its layer listeners and the template material's hook)
            waterSplatSplit?.destroy();
            waterSplatSplit = null;
            // Performance HUD + its hotkey
            perfHud.destroy();
            if ((window as any).__storysplat_perfhud_handler === _perfHudKeyHandler) {
                window.removeEventListener('keydown', _perfHudKeyHandler);
                (window as any).__storysplat_perfhud_handler = null;
            }
            // Full destroy is also used by externally managed/self-hosted portal navigation.
            // Match the built-in portal cleanup so the old driver and microphone cannot leak,
            // while the module-scoped analyser/context remain reusable by the next viewer.
            if (audioReactiveInstance) {
                audioReactiveInstance.destroy();
                audioReactiveInstance = null;
            }
            app.off('update', audioReactiveUpdateHandler);
            audioAnalyserBus.releaseViewer();
            // CameraControls owns its own input sources, separate from app.keyboard.
            // Release their window/canvas listeners before destroying the app.
            cameraControls.destroy();
            app.destroy();
            canvas.remove();
        },
        resize: handleResize,
        // Portal Navigation
        navigateToScene: async (sceneId: string) => {
            const portalData = { targetSceneId: sceneId, activationMode: 'click' };
            await handlePortalNavigation(portalData);
        },
        // Mode
        setCameraMode: (mode: 'tour' | 'explore' | 'walk') => setCameraMode(mode),
        getCameraMode: () => currentCameraMode,
        // Hidden FPV drone mode. Normally reached via the typed "fpv" sequence or
        // the gamepad chord (both bumpers + Start), but exposed here so an
        // integrator can wire it to their own control if they want.
        enterFpvDroneMode: () => enterFpvMode(),
        exitFpvDroneMode: () => exitFpvMode(),
        toggleFpvDroneMode: () => toggleFpvMode(),
        isFpvDroneMode: () => currentCameraMode === 'fpv',
        // Performance HUD (F3 / Ctrl+Shift+M / ?perfhud=1)
        togglePerfHud: (visible?: boolean) => togglePerfHud(visible),
        setPerformanceMode,
        setOnDemandRendering,
        getOnDemandRendering: () => onDemandRenderingActive,
        requestRender: (reason?: string) => renderScheduler.requestFrame(reason && typeof reason === 'string' ? `api:${reason}` : 'api'),
        getShadowDebugInfo: () => shadowSystem?.getDebugInfo() ?? null,
        getCloudDebugInfo: () => {
            const shell = scanSky.shell();
            return {
                ...cloudSystem.debugInfo(),
                scanSky: {
                    mode: scanSky.resolved.mode,
                    gridBuilds: scanSky.gridBuilds,
                    measured: shell ? { keepRadius: round2(shell.keepRadius), shellDistance: shell.shellDistance === null ? null : round2(shell.shellDistance), skyCoverage: round2(shell.skyCoverage), objectsTop: round2(shell.denseTop) } : null,
                },
                // splats in front of water: the splat draw's split at the water surface
                waterSplit: waterSplatSplit?.debugInfo() ?? null,
            };
        },
        getWindDebugInfo: () => windSystem.debugInfo(),
        setWindClock: (seconds: number | null) => windSystem.setClock(seconds),
        getOnDemandRenderStats: () => ({ active: onDemandRenderingActive, enabled: onDemandRenderingEnabled, continuousReasons: [...onDemandContinuousReasons], ...renderScheduler.stats, reasons: { ...renderScheduler.stats.reasons } }),
        getPerformanceMode: () => performanceMode,
        // Resume tour mode using the camera's current pose as the starting point.
        // Snaps tour progress to the nearest point on the path so re-entering tour
        // doesn't jump back to the previously paused progress.
        resumeTourAtCurrentCamera: () => resumeTourAtCurrentCamera(),
        setExploreMode: (mode: 'orbit' | 'fly' | 'walk') => {
            if (currentCameraMode !== 'explore' && currentCameraMode !== 'walk') {
                throw new Error('[StorySplat Viewer] setExploreMode() requires explore mode. Call setCameraMode("explore") first.');
            }
            if (mode === 'walk') {
                if (currentCameraMode !== 'walk') setCameraMode('walk');
                updateExploreBtnState('walk');
            } else {
                applyExploreSubMode(mode);
            }
        },
        setExploreModeSettings: (settings: ExploreModeSettingsUpdate) => updateExploreModeSettings(settings),
        // Progress (direct tour progress control)
        setProgress: (progress: number, animate?: boolean) => {
            if (currentCameraMode === 'explore') {
                throw new Error('[StorySplat Viewer] setProgress() requires tour mode. Call setCameraMode("tour") first.');
            }
            setProgress(progress, animate);
        },
        getProgress: () => currentProgress,
        setWaypointEasing: (mode: WaypointEasingMode) => {
            waypointEasing = normalizeWaypointEasing(mode);
            config.waypointEasing = waypointEasing;
        },
        setPathInterpolation: (mode: 'smooth' | 'linear') => {
            pathInterpolationMode = mode === 'linear' ? 'linear' : 'smooth';
            config.pathInterpolation = pathInterpolationMode;
        },
        // Audio
        muteAll: () => muteAllAudio(),
        unmuteAll: () => unmuteAllAudio(),
        isMuted: () => globalMuted,
        // Hotspot interaction
        getHotspots: () => {
            return hotspotEntities.map((entity) => {
                const h = entity.hotspotData;
                const pos = entity.getPosition();
                return {
                    id: h?.id || '',
                    title: h?.title || h?.information?.substring(0, 30) || '',
                    type: h?.type || 'sphere',
                    position: { x: pos.x, y: pos.y, z: pos.z }
                };
            });
        },
        triggerHotspot: (id: string) => {
            const entity = hotspotEntities.find((e) => (e as ViewerRuntimeEntity).hotspotData?.id === id);
            if (!entity) {
                throw new Error(`[StorySplat Viewer] Hotspot not found: ${id}`);
            }
            const hotspot = (entity as ViewerRuntimeEntity).hotspotData;
            // An API open is an activation: its linked actions run too (before its own popup, so
            // that popup wins, as on click).
            runHotspotActions(hotspot);
            if (hotspot && hasDisplayableHotspotPopupContent(hotspot)) {
                showHotspotPopup(container, hotspot, currentButtonLabels);
            }
            // Same event as the click path — an API-driven open is still an open, and
            // dropping it under-counted any scene driven by a custom script or host page.
            // Emitted even without popup content: a bare marker can be an overlay /
            // region switch, and the click path emits for those too.
            if (hotspot && !triggerHotspotEmitting) {
                triggerHotspotEmitting = true;
                try { events.emit('hotspotClick', { hotspot }); }
                finally { triggerHotspotEmitting = false; }
            }
        },
        runLinkedAction: (action: LinkedAction) => {
            const settled = linkedActionRunner.runAction(action);
            renderScheduler.requestFrame('linked-action');
            return settled;
        },
        // Regions: switch them like the Layers menu does (a 250 ms fade, no recompile).
        setRegionVisible: regionsApi.setRegionVisible,
        showOnlyRegion: regionsApi.showOnlyRegion,
        getVisibleRegions: regionsApi.getVisibleRegions,
        getRegions: regionsApi.getRegions,
        // Legacy aliases: focus regions are regions that dim the rest; haze keeps its own switch.
        setRegionActive: (kind: 'focus' | 'haze', id: string, active: boolean) => {
            if (kind === 'focus') return regionsApi.setFocusRegionActive(id, active);
            const controller = regionTriggerControllers.haze;
            if (kind !== 'haze' || !controller.getItems().some((r) => r.id === id)) return false;
            return active ? controller.show(id) : controller.hide(id);
        },
        getActiveRegions: () => {
            const haze = regionConfigFor('haze');
            return {
                focus: regionsApi.getActiveFocusRegions(),
                haze: haze?.enabled && Array.isArray(haze.regions) ? haze.regions.filter((r) => r && regionIsActive('haze', r)).map((r) => r.id) : [],
            };
        },
        // Legacy aliases: annotation layers are regions in the Layers menu / colour-only regions.
        setLayerGroupVisible: regionsApi.setLayerGroupVisible,
        showOnlyLayerGroup: regionsApi.showOnlyLayerGroup,
        setAllLayerGroupsVisible: regionsApi.setAllLayerGroupsVisible,
        getVisibleLayerGroups: regionsApi.getVisibleLayerGroups,
        // Measurement sets: show or hide the saved measurements of a set (empty without sets).
        ...createMeasurementSetsApi(() => measurementSetsState),
        getLayerGroups: regionsApi.getLayerGroups,
        showOverlay: (id: string) => overlayController.show(id),
        hideOverlay: (id: string) => overlayController.hide(id),
        getVisibleOverlays: () => overlayController.getOverlays().filter((o) => overlayController.isVisible(o.id)).map((o) => o.id),
        // Hotspot folders and the visitor directory
        setHotspotFolderVisible: (id: string, visible: boolean) => typeof id === 'string' && directoryState.setFolderVisible(id, visible !== false),
        showOnlyHotspotFolder: (id: string | null) => directoryState.showOnlyFolder(typeof id === 'string' ? id : null),
        getVisibleHotspotFolders: () => directoryState.getVisibleFolderIds(),
        getHotspotFolders: () => directoryState.getFolders().map((f) => ({
            id: f.id,
            name: f.name,
            parentId: f.parentId,
            visible: directoryState.isFolderVisible(f.id),
            hotspotIds: f.hotspots.map((h) => h.id),
            viewIds: f.views.map((v) => v.id),
        })),
        getDirectory: () => ({
            enabled: directoryState.enabled,
            available: !!directoryPanel,
            open: directoryPanel?.isOpen() ?? false,
            title: directoryState.title,
            hotspots: directoryState.getUnfiled().map((h) => ({ id: h.id, title: h.title })),
            folders: directoryState.getFolders(true).map((f) => ({
                id: f.id,
                name: f.name,
                parentId: f.parentId,
                visible: directoryState.isFolderVisible(f.id),
                hotspots: f.hotspots.map((h) => ({ id: h.id, title: h.title })),
                views: f.views.map(directoryViewInfo),
            })),
            views: directoryState.getViews().map(directoryViewInfo),
        }),
        openDirectory: () => {
            if (!directoryPanel) return false;
            directoryPanel.setOpen(true);
            return true;
        },
        closeDirectory: () => {
            directoryPanel?.setOpen(false);
        },
        closeHotspot: () => {
            const popupEl = container.querySelector('.storysplat-hotspot-popup') as HTMLElement;
            const overlayEl = container.querySelector('.storysplat-hotspot-overlay') as HTMLElement;
            if (popupEl)
                popupEl.classList.remove('visible');
            if (overlayEl)
                overlayEl.classList.remove('visible');
            stopAllHotspotAudio();
            stopHotspotPopupMedia(container);
        },
        // Panorama mode
        setPanoramaMode: (enabled: boolean) => setPanoramaMode(enabled),
        isPanoramaMode: () => panoramaModeEnabled,
        // UI customization
        setButtonLabels: (labels: Partial<ViewerButtonLabels>) => {
            // Merge new labels with existing
            currentButtonLabels = { ...currentButtonLabels, ...labels };
            // Helper to get resolved label
            const gl = (key: keyof ViewerButtonLabels) => getButtonLabel(currentButtonLabels, key);
            // Update DOM elements that display label text
            const modeBtn = (mode: string, key: keyof ButtonLabels) => {
                const el = container.querySelector(`.storysplat-mode-btn[data-mode="${mode}"]`) as HTMLElement;
                if (el)
                    el.textContent = gl(key);
            };
            modeBtn('tour', 'tour');
            modeBtn('explore', 'explore');
            // Explore sub-buttons (orbit/fly/walk)
            const orbitBtn = container.querySelector('.storysplat-explore-btn[data-explore-mode="orbit"]') as HTMLElement;
            if (orbitBtn)
                orbitBtn.textContent = gl('orbit');
            const flyBtn = container.querySelector('.storysplat-explore-btn[data-explore-mode="fly"]') as HTMLElement;
            if (flyBtn)
                flyBtn.textContent = gl('fly');
            const walkBtn = container.querySelector('.storysplat-explore-btn[data-explore-mode="walk"]') as HTMLElement;
            if (walkBtn)
                walkBtn.textContent = gl('walk');
            // Nav buttons
            const prevBtn = container.querySelector('.storysplat-btn-prev') as HTMLElement;
            if (prevBtn)
                prevBtn.textContent = gl('previous');
            const nextBtn = container.querySelector('.storysplat-btn-next') as HTMLElement;
            if (nextBtn)
                nextBtn.textContent = gl('next');
            // Waypoint list toggle
            const wpToggle = container.querySelector('.storysplat-waypoint-list-toggle') as HTMLElement;
            if (wpToggle) {
                const svg = wpToggle.querySelector('svg');
                wpToggle.textContent = '';
                wpToggle.append(gl('waypoints'));
                if (svg)
                    wpToggle.appendChild(svg);
                wpToggle.setAttribute('aria-label', gl('waypoints'));
            }
            // XR buttons (only update if not in active XR session)
            const vrBtn = container.querySelector('.storysplat-vr-btn') as HTMLElement;
            if (vrBtn && !vrBtn.classList.contains('active')) {
                vrBtn.textContent = gl(vrBtn.classList.contains('prompt') ? 'enterVr' : 'vr');
                vrBtn.setAttribute('aria-label', gl('vr'));
            }
            const arBtn = container.querySelector('.storysplat-ar-btn') as HTMLElement;
            if (arBtn && !arBtn.classList.contains('active')) {
                arBtn.textContent = gl('ar');
                arBtn.setAttribute('aria-label', gl('ar'));
            }
            // Fullscreen button aria-label
            const fsBtn = container.querySelector('.storysplat-fullscreen-btn') as HTMLElement;
            if (fsBtn)
                fsBtn.setAttribute('aria-label', gl('fullscreen'));
            const shareBtn = container.querySelector('.storysplat-share-btn') as HTMLElement;
            if (shareBtn) {
                shareBtn.setAttribute('aria-label', gl('share'));
                shareBtn.setAttribute('title', gl('share'));
            }
            // Hotspot popup close button
            const closeBtn = container.querySelector('.storysplat-hotspot-popup-close') as HTMLElement;
            if (closeBtn)
                closeBtn.textContent = gl('close');
            // Portal popup buttons
            const confirmBtn = container.querySelector('.storysplat-portal-popup-confirm') as HTMLElement;
            if (confirmBtn)
                confirmBtn.textContent = gl('yes');
            const cancelBtn = container.querySelector('.storysplat-portal-popup-cancel') as HTMLElement;
            if (cancelBtn)
                cancelBtn.textContent = gl('cancel');
            // Controls card
            if (uiElements.tourLookHint) {
                const cardMode = (uiElements.tourLookHint.dataset.mode as ControlsCardMode | undefined) ?? 'tour';
                setTourOnboardingMode(uiElements.tourLookHint, cardMode, currentButtonLabels);
            }
            // Measure panel (and the ruler button's name)
            refreshMeasurePanelLabels?.();
            // The VR interface's words
            vrLabelsCache = null;
            vrUi?.refresh();
            // Autoplay waiting for levels of detail
            const detailWaitText = container.querySelector('.storysplat-autoplay-detail-text') as HTMLElement | null;
            if (detailWaitText)
                detailWaitText.textContent = gl('addingMoreDetail');
            const detailWaitStart = container.querySelector('.storysplat-autoplay-detail-start') as HTMLElement | null;
            if (detailWaitStart)
                detailWaitStart.textContent = gl('startTourAnyway');
            // Help panel - rebuild using safe DOM methods
            const helpPanel = container.querySelector('.storysplat-help-panel') as HTMLElement;
            if (helpPanel) {
                helpPanel.textContent = '';
                const addEl = (tag: string, text: string, bold?: boolean) => {
                    const el = document.createElement(tag);
                    if (bold) {
                        const b = document.createElement('strong');
                        b.textContent = text;
                        el.appendChild(b);
                    }
                    else
                        el.textContent = text;
                    helpPanel.appendChild(el);
                    return el;
                };
                const addSpacer = () => helpPanel.appendChild(document.createElement('br'));
                addEl('h3', gl('helpTitle'));
                addEl('p', gl('helpCameraModes'), true);
                addEl('p', `\u2022 ${gl('tour')} - ${gl('helpTourDesc')}`);
                addEl('p', `\u2022 ${gl('explore')} - ${gl('helpExploreDesc')}`);
                addEl('p', `\u2022 ${gl('walk')} - ${gl('helpWalkDesc')}`);
                addSpacer();
                addEl('p', `${gl('tour')} Mode:`, true);
                addEl('p', '\u2022 Scroll - Move along path');
                addEl('p', '\u2022 Drag - Look around');
                addSpacer();
                addEl('p', `${gl('explore')} Mode:`, true);
                addEl('p', '\u2022 LMB Drag - Orbit camera');
                addEl('p', '\u2022 RMB Drag - Fly/look');
                addEl('p', '\u2022 WASD/QE - Move camera');
                addEl('p', '\u2022 Shift - Move fast');
                addEl('p', '\u2022 Scroll/Pinch - Zoom');
                addEl('p', '\u2022 Double-click - Focus');
                addSpacer();
                addEl('p', `${gl('walk')} Mode:`, true);
                addEl('p', '\u2022 Click to lock mouse');
                addEl('p', '\u2022 WASD/Arrows - Move');
                addEl('p', '\u2022 Mouse - Look around');
                addEl('p', '\u2022 Shift - Sprint');
                addEl('p', '\u2022 Space - Jump');
            }
            // Help button title
            const helpBtn = container.querySelector('.storysplat-help-btn') as HTMLElement;
            if (helpBtn)
                helpBtn.setAttribute('title', gl('helpTitle'));
            // Visitor directory words
            directoryPanel?.rebuild();
        },
        // Events
        on: (event: ViewerEvent, callback) => events.on(event, callback),
        off: (event: ViewerEvent, callback) => events.off(event, callback),
        onInternal: (event: ViewerEvent, callback) => events.onInternal(event, callback)
    };
    // If editor mode, extend instance with mutation API
    if (isEditorMode) {
        const editorInstance = instance as EditorViewerInstance;
        // Camera (editor keeps its own references for backward compat)
        editorInstance.setCameraMode = (mode: 'tour' | 'explore' | 'walk') => setCameraMode(mode);
        editorInstance.getCameraMode = () => currentCameraMode;
        editorInstance.getCameraControls = () => cameraControls;
        editorInstance.setOrbitCameraSettings = (settings) => updateOrbitCameraSettings(settings);
        // Gizmo input suppression — disables CameraControls AND tour mode pointer rotation
        editorInstance.suppressInput = () => {
            gizmoInputSuppressed = true;
            isUserDragging = false; // Cancel any in-progress tour drag
            cameraControls.disable();
        };
        editorInstance.resumeInput = () => {
            gizmoInputSuppressed = false;
            // Re-enable CameraControls only if we're in a mode that uses it (explore/orbit)
            if (currentCameraMode === 'explore') {
                cameraControls.enable();
            }
        };
        // Video-render capture mode: freeze the autonomous per-frame camera
        // drivers (tour damping, walk character controller) so the render's
        // pinned pose is the only thing writing the camera during capture.
        editorInstance.setRenderCaptureMode = (active: boolean) => {
            editorRenderCaptureRequested = active;
            syncRenderCaptureActive();
            if (active) {
                isUserDragging = false;
                cameraControls.disable();
            } else {
                // The splat previewed before the render comes back.
                endCaptureSwaps('editor-render');
                if (!renderCaptureActive && currentCameraMode === 'explore') cameraControls.enable();
            }
        };
        // Video render: each output frame shows the swaps the published scene would show there
        // (areas + chain), not the editor's hand-previewed splat. Swaps take over on the first
        // frame and hand back when render capture mode ends.
        editorInstance.prepareCaptureFrame = async (frame: CaptureSwapFrame) => {
            if (!editorRenderCaptureRequested) {
                throw new Error('[StorySplat Viewer] prepareCaptureFrame needs setRenderCaptureMode(true) first.');
            }
            const swaps = beginCaptureSwaps('editor-render');
            if (!swaps) return; // a frame capture already drives the swaps
            await swaps.driver.prepareFrame(frame);
        };
        // TDR-safe CPU screen-point pick for the EDITOR. The GPU pc.Picker path
        // (SelectionManager.getWorldPointAtScreen) re-renders the unified gsplat
        // with shared buffers and intermittently hangs the device on 2.20 WebGPU
        // (see the ban at reticleRaycast) — editor world-point queries must use
        // this instead. Returns the nearest real-collision hit or null.
        editorInstance.raycastScreenPoint = (sx: number, sy: number, options?: { seeThroughHaze?: boolean }): pc.Vec3 | null => {
            return reticleRaycast(sx, sy, undefined, !!options?.seeThroughHaze);
        };
        // "Splat object" selections (effects/splat-objects.ts): which object is under a canvas
        // point (CPU, no GPU pick pass), and the box around objects in editor scene coordinates.
        editorInstance.pickSplatObjectAtScreen = (sx: number, sy: number): string | null => {
            const cam = camera.camera;
            if (!cam) return null;
            // The scan surface under the point (world space), as camera-space depth.
            const hit = reticleRaycast(sx, sy);
            const surfaceDepth = hit ? -cam.viewMatrix.transformPoint(hit).z : null;
            const rect = app.graphicsDevice.clientRect;
            return pickSplatObjectAtScreen(sceneContent, cam, sx, sy, { width: rect.width, height: rect.height, surfaceDepth });
        };
        editorInstance.getSplatObjectBounds = (ids: string[]) => {
            const box = splatObjectSceneBounds(sceneContent, normalizeSplatObjectIds(ids));
            if (!box) return null;
            const round = (v: number) => Math.round(v * 1000) / 1000;
            return {
                position: { x: round((box.min.x + box.max.x) / 2), y: round((box.min.y + box.max.y) / 2), z: round(-(box.min.z + box.max.z) / 2) },
                size: { x: round(Math.max(0.01, box.max.x - box.min.x)), y: round(Math.max(0.01, box.max.y - box.min.y)), z: round(Math.max(0.01, box.max.z - box.min.z)) },
            };
        };
        editorInstance.getSplatObjectIds = () => [...new Set(splatObjectComponents(sceneContent).map((o) => o.id))];
        // Reticle control
        editorInstance.setReticleEnabled = (enabled: boolean) => {
            reticleUserEnabled = enabled;
            if (!enabled) {
                reticleEntity.enabled = false;
                reticleVisible = false;
                reticleOpacity = 0;
            }
        };
        editorInstance.setReticleColor = (color: { r: number; g: number; b: number } | null) => {
            reticleColorOverride = color ? new pc.Color(color.r, color.g, color.b) : null;
        };
        // Hotspot editing state — suppresses popup for the hotspot being edited
        editorInstance.setEditingHotspotId = (id: string | null) => {
            editorEditingHotspotId = id;
        };
        // The editor's Preview of an element's cutaway (the editor viewport runs none by itself,
        // see runClickCutaway): plays it as visitors see it; null clears the cut at once.
        editorInstance.previewCutaway = (action: CutawayAction | null) => {
            if (action) {
                runCutawayAction(action);
                return;
            }
            cutawayTransition = null;
            applyCutawayState({ enabled: false });
        };
        editorInstance.setRefocusTapMode = (mode: 'single' | 'double' | 'none') => {
            refocusTapMode = mode;
            eighthWallPlacement?.setTapMode(mode);
        };
        // Splat-derived collision system toggle. Switches both CharacterController
        // (walk mode) and CameraControls (fly/orbit) so every navigation mode
        // reflects the chosen representation. Custom collision meshes
        // (primitives + uploaded GLBs) compose additively in both systems.
        editorInstance.setCollisionSystem = (system: 'voxel' | 'mesh') => {
            currentCollisionSystem = system;
            characterController?.setCollisionSystem(currentCollisionSystem);
            cameraControls.setCollisionSystem(system);
        };
        // Master enable for splat-derived collision (voxel + mesh). The mesh
        // controller and the fly/orbit controls keep their data loaded; this
        // toggle just gates whether queries route to it.
        editorInstance.setSplatCollisionEnabled = (enabled: boolean) => {
            currentSplatCollisionEnabled = enabled;
            characterController?.setSplatCollisionEnabled(currentSplatCollisionEnabled);
            cameraControls.setSplatCollisionEnabled(currentSplatCollisionEnabled);
        };
        // Late-load of the collision GLB (e.g. after the user generates one in
        // the editor or fuses LCC2 meshes mid-session). Pushes into both
        // CharacterController and CameraControls so the live toggle has data
        // to switch to. Mirrors initVoxelCollision's lazy controller creation
        // so a mesh-only scene gets a CharacterController on first load.
        editorInstance.setCollisionMeshUrl = async (url: string | null) => {
            currentCollisionMeshUrl = url;
            // Swap collision on screen: leave its set alone (see setRuntimeVoxelCollisionUrl).
            if (swapCollisionActive) {
                if (!url) characterController?.clearMeshCollisionCache();
                return;
            }
            // Null clears the mesh collision on both systems.
            if (!url) {
                characterController?.clearMeshCollision();
                cameraControls.setMeshCollision(null, null);
                return;
            }
            characterController = createLazyCharacterController('mesh collision');
            if (splatEntity) {
                characterController.setSplatEntity(splatEntity);
            }
            await characterController.initMeshCollision(url);
            if (currentCollisionMeshUrl !== url || swapCollisionActive) return;
            const mesh = characterController.meshCollisionInstance;
            if (mesh) {
                cameraControls.setMeshCollision(mesh, splatEntity);
                console.log(`[Collision] Mesh bound to walk + fly/orbit (${mesh.triangleCount} tris, system: ${currentCollisionSystem}, splatEnabled: ${currentSplatCollisionEnabled})`);
            }
        };
        // Late-load or clear voxel collision so editor state changes cannot leave
        // stale voxel data active in the live walk/fly/orbit runtime.
        editorInstance.setVoxelCollisionUrl = async (url: string | null) => {
            await setRuntimeVoxelCollisionUrl(url);
        };
        // Scene access
        editorInstance.getApp = () => app;
        editorInstance.getSplatEntity = () => splatEntity;
        editorInstance.getPrimarySplatFrame = () => {
            // Same maths as setupSplatEntity's non-native branch (no coordinate correction).
            const scale = config.scale || { x: 1, y: 1, z: 1 };
            const invertX = config.invertXScale || false;
            const invertY = config.invertYScale || false;
            const pos = config.position || [0, 0, 0];
            const rot = config.rotation || [0, 0, 0];
            const local = new pc.Mat4().setTRS(
                new pc.Vec3(pos[0], pos[1], -pos[2]),
                new pc.Quat().setFromEulerAngles((rot[0] || 0) * 180 / Math.PI, (rot[1] || 0) * 180 / Math.PI, -(rot[2] || 0) * 180 / Math.PI),
                new pc.Vec3(
                    invertX ? -scale.x : scale.x,
                    invertY ? scale.y : -scale.y,
                    (invertX !== invertY) ? scale.z : -scale.z,
                ),
            );
            const world = new pc.Mat4().mul2(sceneContent.getWorldTransform(), local);
            const url = splatEntity ? (primarySplatFrameUrl ?? '') : '';
            return {
                nativeFrame: usesNativeSplatFrame(url, primarySplatCoordinateCorrection?.sourceFormat),
                plyWorldTransform: Float32Array.from(world.data),
                // custom meshes (incl. splat files added as models) are children of scene-content
                sceneContentWorldTransform: Float32Array.from(sceneContent.getWorldTransform().data),
            };
        };
        editorInstance.getSwapSplatEntity = (url: string) => preloadedSplats.get(url) ?? null;
        editorInstance.ensureSwapSplatLoaded = async (url: string, splatOverrides?: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: { x: number; y: number; z: number } }) => {
            if (preloadedSplats.has(url)) return preloadedSplats.get(url)!;
            await preloadSplat(url, splatOverrides);
            return preloadedSplats.get(url) ?? null;
        };
        editorInstance.getAllHotspotEntities = () => editorHotspotMap;
        editorInstance.getAllLightEntities = () => editorLightMap;
        editorInstance.getAllPortalEntities = () => editorPortalMap;
        editorInstance.getAllMirrorPlaneEntities = () => editorMirrorPlaneMap;
        editorInstance.getAllWaterPlaneEntities = () => editorWaterPlaneMap;
        editorInstance.getAllCloudLayerEntities = () => editorCloudLayerMap;
        editorInstance.getAllCustomMeshEntities = () => editorCustomMeshMap;
        editorInstance.getAllParticleEntities = () => editorParticleMap;
        editorInstance.getAllCollisionMeshEntities = () => editorCollisionMeshMap;
        editorInstance.getAllHtmlMeshEntities = () => {
            const manager = (app as ViewerRuntimeApp).__htmlMeshManager;
            const result = new Map<string, pc.Entity>();
            if (manager) {
                manager.getAllMeshes().forEach((instance: { entity: pc.Entity }, id: string) => {
                    result.set(id, instance.entity);
                });
            }
            return result;
        };
        editorInstance.getCamera = () => camera;
        // Progress
        editorInstance.setProgress = (progress: number, animate?: boolean) => setProgress(progress, animate);
        editorInstance.getProgress = () => currentProgress;
        editorInstance.setWaypointEasing = (mode: WaypointEasingMode) => {
            waypointEasing = normalizeWaypointEasing(mode);
            config.waypointEasing = waypointEasing;
        };
        editorInstance.setCameraControlSettings = (settings: CameraControlSettings) => {
            Object.assign(config, settings);
            if (settings.cameraMovementSpeed !== undefined) {
                baseMoveSpeed = (settings.cameraMovementSpeed ?? 1) * 5;
                cameraControls.moveSpeed = baseMoveSpeed;
                cameraControls.moveFastSpeed = baseMoveSpeed * 2.5;
                cameraControls.moveSlowSpeed = baseMoveSpeed * 0.5;
                droneController?.setWorldScale(baseMoveSpeed);
            }
            if (settings.cameraRotationSensitivity !== undefined) {
                cameraControls.rotateSpeed = 800 / (settings.cameraRotationSensitivity || 4000);
            }
            if (settings.cameraDamping !== undefined) {
                cameraControls.moveDamping = settings.cameraDamping;
                cameraControls.rotateDamping = settings.cameraDamping;
            }
            if (settings.invertCameraRotation !== undefined) {
                // Write back to config too: the walk CharacterController is created lazily on first
                // walk entry from config.invertCameraRotation, so a toggle made before that would
                // otherwise be lost. Tour look-around reads cameraControls.invertRotation live.
                config.invertCameraRotation = settings.invertCameraRotation;
                cameraControls.invertRotation = settings.invertCameraRotation;
                // Keep walk (mouse-look + mobile drag-look) in sync with the same invert toggle as explore.
                if (characterController) characterController.invertCameraRotation = settings.invertCameraRotation;
            }
            if (settings.doubleTapMoveSpeed !== undefined) {
                cameraControls.autoMoveSpeedFactor = settings.doubleTapMoveSpeed;
            }
            if (settings.playerWidth !== undefined) {
                // Write back so a walk controller created lazily later starts with the live width.
                config.playerWidth = settings.playerWidth;
                if (characterController) characterController.collisionRadius = playerCollisionRadius(settings.playerWidth);
            }
            if (characterController) {
                if (settings.walkSpeed !== undefined) {
                    characterController.moveSpeed = settings.walkSpeed;
                }
                if (settings.cameraRotationSensitivity !== undefined) {
                    characterController.lookSensitivity = (1 / (settings.cameraRotationSensitivity || 4000)) * 10;
                }
                if (settings.playerHeight !== undefined) {
                    characterController.playerHeight = settings.playerHeight;
                }
                if (settings.doubleTapMoveSpeed !== undefined) {
                    characterController.autoMoveSpeedFactor = settings.doubleTapMoveSpeed;
                }
                if (settings.headBobEnabled !== undefined) {
                    characterController.headBobEnabled = settings.headBobEnabled;
                }
            }
        };
        // Transition speed
        editorInstance.setTransitionSpeed = (speed: number) => {
            config.transitionSpeed = speed;
            transitionDuration = baseTransitionDuration * (speed || 1);
            PULL_STRENGTH = 0.01 + (speed || 1) * 0.1;
        };
        // Splat swap
        editorInstance.updateAdditionalSplats = (splats) => {
            const current = getCurrentSplatUrl();
            const collisionKey = () => {
                const s = additionalSplats.find((swap) => swap.url === current);
                return s ? JSON.stringify([s.collision, s.collisionMeshUrl, s.voxelCollisionUrl, s.collisionSystem,
                    s.collision === 'own' ? [s.position, s.rotation, s.scale] : null]) : '';
            };
            const before = collisionKey();
            additionalSplats = sanitizeSwapList(splats);
            failedSwapTargetUrl = null;
            syncSwapAreas();
            // Editing the collision of the splat on screen applies it right away.
            if (current && collisionKey() !== before) void applyCollisionForSplat(current);
        };
        editorInstance.setSwapTransitionEnabled = (enabled: boolean) => {
            swapTransitionEnabled = enabled;
        };
        editorInstance.setPrimarySplatUrl = (url: string) => {
            primarySplatUrlOverride = url;
        };
        // Compare mode editor methods
        editorInstance.enableCompareMode = async (afterSplatUrl: string, settings?: Partial<CompareConfig>, splatOverrides?: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: { x: number; y: number; z: number } }) => {
            destroyCompareMode();
            if (settings) {
                Object.assign(compareSettings, settings);
            }
            isCompareModeEnabled = true;
            await initCompareMode(afterSplatUrl, splatOverrides);
        };
        editorInstance.disableCompareMode = () => {
            destroyCompareMode();
            isCompareModeEnabled = false;
        };
        editorInstance.updateSwapSplatTransform = (url: string, transform: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: { x: number; y: number; z: number } }) => {
            // The compare after splat is loaded outside `preloadedSplats` (it needs
            // the unified + LOD path, not the swap preloader), so resolve it too —
            // otherwise the editor's transform sliders silently do nothing in
            // compare mode.
            const entity = preloadedSplats.get(url)
                ?? (compareAfterUrl === url ? compareAfterEntity : null);
            if (!entity) return;
            // LCC2 and SPZ sources are already in PlayCanvas-native coordinates;
            // legacy Babylon-authored splats need the Y-flip + Z-negation. Match
            // whatever convention this entity was loaded with (see setupSplatEntity,
            // preloadSplat and loadCompareAfterSplat) or the sliders fight the
            // load-time transform.
            const swapFrame = swapEntityFrames.get(entity);
            const isNativeFrameSource = entity === compareAfterEntity
                ? compareAfterNativeFrame
                : (swapFrame?.nativeFrame ?? usesNativeSplatFrame(resolveSwapUrlByTriggerUrl(url), null));
            const rotationCorrection = entity === compareAfterEntity ? compareAfterCorrection : (swapFrame?.correction ?? null);
            if (transform.scale) {
                const invertX = config.invertXScale || false;
                const invertY = config.invertYScale || false;
                const s = transform.scale;
                entity.setLocalScale(
                    invertX ? -s.x : s.x,
                    isNativeFrameSource ? (invertY ? -s.y : s.y) : (invertY ? s.y : -s.y),
                    isNativeFrameSource
                        ? ((invertX !== invertY) ? -s.z : s.z)
                        : ((invertX !== invertY) ? s.z : -s.z)
                );
            }
            if (transform.position) {
                const p = transform.position;
                entity.setPosition(p[0], p[1], isNativeFrameSource ? p[2] : -p[2]);
            }
            if (transform.rotation) {
                const r = transform.rotation;
                const rot: [number, number, number] = [r[0] || 0, r[1] || 0, r[2] || 0];
                if (isNativeFrameSource) {
                    applySplatEntityRotation(entity, rot, rotationCorrection);
                } else {
                    entity.setEulerAngles(
                        rot[0] * (180 / Math.PI),
                        rot[1] * (180 / Math.PI),
                        -rot[2] * (180 / Math.PI)
                    );
                }
            }
        };
        editorInstance.setCompareSettings = (settings: Partial<CompareConfig>) => {
            Object.assign(compareSettings, settings);
            if (compareSlider) {
                if (settings.orientation) {
                    compareSlider.setOrientation(settings.orientation);
                    compareSplitView?.setOrientation(settings.orientation);
                }
                if (settings.beforeLabel !== undefined || settings.afterLabel !== undefined) {
                    compareSlider.setLabels(
                        settings.beforeLabel ?? compareSettings.beforeLabel ?? 'Before',
                        settings.afterLabel ?? compareSettings.afterLabel ?? 'After'
                    );
                }
                if (settings.lineColor !== undefined || settings.lineOpacity !== undefined) {
                    compareSlider.updateLineStyle(settings.lineColor, settings.lineOpacity);
                }
                if (settings.handleColor !== undefined || settings.handleOpacity !== undefined) {
                    compareSlider.updateHandleColor(
                        settings.handleColor ?? compareSettings.handleColor ?? '#ffffff',
                        settings.handleOpacity ?? compareSettings.handleOpacity
                    );
                }
                if (settings.handleStyle !== undefined) {
                    compareSlider.updateHandleStyle(settings.handleStyle);
                }
                if (settings.initialPosition !== undefined) {
                    const pos = settings.initialPosition / 100;
                    compareSlider.setPosition(pos);
                    setComparePositionInternal(pos);
                }
                if (settings.showLabels !== undefined) {
                    const labelEls = compareSlider['overlay']?.querySelectorAll('.ss-compare-label');
                    if (labelEls) {
                        labelEls.forEach((el: Element) => {
                            (el as HTMLElement).style.display = settings.showLabels ? '' : 'none';
                        });
                    }
                }
                if (settings.enableKeyboard !== undefined) {
                    compareSlider.setEnableKeyboard(settings.enableKeyboard);
                }
                if (settings.enableSnap !== undefined) {
                    compareSlider.setEnableSnap(settings.enableSnap);
                }
            }
        };
        // Hotspot mutation - uses the same full rendering as createHotspots()
        editorInstance.addHotspot = (hotspot: HotspotData): string => {
            const id = hotspot.id || `hotspot-${Date.now()}`;
            const index = hotspotEntities.length;
            const entity = createSingleHotspot(hotspot, index);
            editorHotspotMap.set(id, entity);
            return id;
        };
        editorInstance.removeHotspot = (id: string) => {
            const entity = editorHotspotMap.get(id);
            if (entity) {
                const line = (entity as ViewerRuntimeEntity).leaderLineEntity;
                if (line) {
                    ((line as any)._leaderMat as pc.StandardMaterial | undefined)?.destroy();
                    line.destroy();
                }
                entity.destroy();
                editorHotspotMap.delete(id);
                const idx = hotspotEntities.indexOf(entity);
                if (idx >= 0)
                    hotspotEntities.splice(idx, 1);
            }
        };
        editorInstance.getHotspotScreenSizeFactor = (id: string) =>
            (editorHotspotMap.get(id) as ViewerRuntimeEntity | undefined)?.screenSizeState?.factor ?? 1;
        editorInstance.getPortalScreenSizeFactor = (id: string) =>
            (editorPortalMap.get(id) as ViewerRuntimeEntity | undefined)?.screenSizeState?.factor ?? 1;
        editorInstance.updateHotspot = (id: string, data: Partial<HotspotData>) => {
            const entity = editorHotspotMap.get(id);
            if (!entity)
                return;
            if (data.position) {
                const position = playCanvasPositionFromSceneVec3(data.position);
                entity.setPosition(position.x, position.y, position.z);
            }
            if (data.scale !== undefined) {
                const rawScale = data.scale;
                const scaleObj = typeof rawScale === 'number'
                    ? { x: rawScale, y: rawScale, z: rawScale } as LegacyVec3
                    : rawScale as LegacyVec3;
                const sx = scaleObj._x ?? scaleObj.x ?? 1;
                const sy = scaleObj._y ?? scaleObj.y ?? 1;
                const sz = scaleObj._z ?? scaleObj.z ?? 1;
                const existingHotspot = (entity as ViewerRuntimeEntity).hotspotData as HotspotData | undefined;
                const hotspotType = data.type ?? existingHotspot?.type ?? 'sphere';
                // Spheres (and a stack with no drawable layer, which renders the fallback sphere) use
                // the 0.2 base scale; every plane-framed marker swaps Y/Z.
                const scaleShape = hotspotLeaderLineShape({ type: hotspotType, markerStack: data.markerStack ?? existingHotspot?.markerStack });
                if (scaleShape === 'sphere') {
                    entity.setLocalScale(sx * 0.2, sy * 0.2, sz * 0.2);
                } else {
                    entity.setLocalScale(sx, sz, sy);
                }
            }
            const existing = (entity as ViewerRuntimeEntity).hotspotData || {};
            const merged = { ...existing, ...data } as HotspotData;
            (entity as ViewerRuntimeEntity).hotspotData = merged;
            // Screen-size settings are applied when the next frame is drawn; make sure there is one.
            if ('keepScreenSize' in data || 'screenSizeDistance' in data || 'screenSizeMin' in data || 'screenSizeMax' in data) {
                renderScheduler.requestFrame('hotspot-screen-size');
            }
            // Opacity edits: hotspotData is what the scroll-driven path reads, so the merge above
            // already covers animated mode; apply the value for the current progress right away.
            if ('opacity' in data || 'opacityMode' in data || 'opacityAnimation' in data) {
                const mat = (entity as ViewerRuntimeEntity).hotspotMaterial
                    || (entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined);
                if (!applyMarkerStackOpacity(entity as ViewerRuntimeEntity, resolveEntityOpacity(merged, currentProgress * 100)) && mat) {
                    const resolve = merged.type === 'sphere' ? resolveSphereEntityOpacity : resolveEntityOpacity;
                    applyEntityOpacity(mat, resolve(merged, currentProgress * 100));
                }
            }
            // Rebuild the leader line whenever its config or hotspot transform changes.
            if (
                data.position !== undefined ||
                data.scale !== undefined ||
                data.leaderLineEnabled !== undefined ||
                data.leaderLineTarget !== undefined ||
                data.leaderLineColor !== undefined ||
                data.leaderLineWidth !== undefined ||
                data.leaderLineOpacity !== undefined
            ) {
                rebuildLeaderLine(entity as ViewerRuntimeEntity, merged as HotspotConfigLike, hotspotLeaderLineShape(merged));
            }
        };
        // Light mutation
        // Light mutation - uses the same creation functions as initCustomLights()
        editorInstance.addLight = (lightCfg: LightConfig): string => {
            const id = lightCfg.id || lightCfg.name || `light-${Date.now()}`;
            let entity: pc.Entity | null = null;
            switch (lightCfg.type) {
                case 'point':
                    entity = createPointLight(lightCfg);
                    break;
                case 'directional':
                    entity = createDirectionalLightEntity(lightCfg);
                    break;
                case 'hemispheric':
                    entity = createHemisphericLight(lightCfg);
                    break;
                case 'ambient':
                    createAmbientLight(lightCfg);
                    break;
                case 'spot':
                    entity = createSpotLight(lightCfg);
                    break;
                default:
                    entity = createPointLight(lightCfg);
                    break;
            }
            if (entity) {
                // Add a small colored sphere for editor visibility/selection
                entity.addComponent('render', {
                    type: 'sphere',
                    castShadows: false,
                    receiveShadows: false,
                });
                entity.setLocalScale(0.25, 0.25, 0.25);
                const lightPickMat = new pc.StandardMaterial();
                lightPickMat.diffuse = new pc.Color(1, 0.85, 0, 1); // Yellow for lights
                lightPickMat.emissive = new pc.Color(0.5, 0.42, 0, 1);
                lightPickMat.update();
                entity.render!.meshInstances[0].material = lightPickMat;

                sceneContent.addChild(entity);
                lightEntities.push(entity);
                editorLightMap.set(id, entity);
                runtimeLightMap.set(id, entity);
                lightBaseConfigs.set(id, { ...lightCfg });
                syncMeshLightLayers();
            }
            return id;
        };
        editorInstance.removeLight = (id: string) => {
            const entity = editorLightMap.get(id);
            if (entity) {
                entity.destroy();
                editorLightMap.delete(id);
                runtimeLightMap.delete(id);
                lightBaseConfigs.delete(id);
                const idx = lightEntities.indexOf(entity);
                if (idx >= 0)
                    lightEntities.splice(idx, 1);
                syncMeshLightLayers();
            }
        };
        // Changes a light in place. The editor used to remove and re-add every light on each
        // edit: a new light starts with an empty shadow map, so each slider step showed a frame
        // without shadows. The light's type and a hemispheric light's ground colour still need
        // removeLight + addLight.
        editorInstance.updateLight = (id: string, data: Partial<LightConfig>) => {
            const entity = editorLightMap.get(id);
            if (!entity)
                return;
            const merged = { ...lightBaseConfigs.get(id), ...(data as Partial<LightConfigLike>) } as LightConfigLike;
            lightBaseConfigs.set(id, merged);
            if (data.name !== undefined)
                entity.name = data.name;
            if (data.position) {
                entity.setPosition(data.position.x ?? 0, data.position.y ?? 0, -(data.position.z ?? 0));
            }
            if (data.direction && entity.light && (entity.light.type === 'directional' || entity.light.type === 'spot')) {
                orientAuthoredLight(entity, { ...merged, rotation: undefined }, { x: 0, y: -1, z: 0 });
            }
            if (entity.light) {
                if (data.intensity !== undefined)
                    entity.light.intensity = data.intensity;
                if (data.range !== undefined)
                    entity.light.range = data.range;
                if (data.castShadows !== undefined)
                    entity.light.castShadows = data.castShadows;
                if (data.color) {
                    entity.light.color = hexToColorForLights(data.color);
                }
                if (entity.light.type === 'spot') {
                    if (data.innerConeAngle !== undefined)
                        entity.light.innerConeAngle = data.innerConeAngle;
                    if (data.outerConeAngle !== undefined)
                        entity.light.outerConeAngle = data.outerConeAngle;
                }
            }
            if (data.enabled !== undefined)
                entity.enabled = data.enabled !== false;
            renderScheduler.requestFrame('light-edit');
        };
        // Custom mesh mutation - uses loadCustomMesh() for full GLB/GLTF loading
        editorInstance.addCustomMesh = (meshCfg: CustomMeshConfig): string => {
            const id = meshCfg.id || meshCfg.name || `mesh-${Date.now()}`;
            const entity = loadCustomMesh(meshCfg, customMeshEntities.size);
            if (entity) {
                editorCustomMeshMap.set(id, entity);
            }
            return id;
        };
        editorInstance.updateCustomMesh = (id: string, data: CustomMeshUpdateData) => {
            const entity = editorCustomMeshMap.get(id);
            if (!entity) return;
            if ('wind' in data) {
                // Wind-reactive or not, and its profile: in place (a modifier part and uniforms).
                let meshKey: string | null = null;
                customMeshEntities.forEach((meshData, key) => {
                    if (meshData.entity === entity) {
                        meshKey = key;
                        meshData.config.wind = data.wind ?? undefined;
                    }
                });
                windSystem.setObject(meshKey ?? id, entity, data.wind ?? null);
            }
            if (data.unlit !== undefined) {
                // Keep the stored config in sync so a GLB that is still downloading applies the
                // final state when it lands; already-instantiated materials switch live.
                const meshData = customMeshEntities.get(id);
                if (meshData) meshData.config.unlit = data.unlit;
                const modelEntity = (entity as ViewerRuntimeEntity).modelEntity;
                if (modelEntity && setEntityUnlit(modelEntity, data.unlit) > 0) {
                    renderScheduler.requestFrame('mesh-unlit');
                }
            }
            if (data.castShadows !== undefined) {
                // The shadow system reads the config every frame (and applies it to a GLB that
                // is still loading once it lands).
                const meshData = customMeshEntities.get(id);
                if (meshData) meshData.config.castShadows = data.castShadows;
                renderScheduler.requestFrame('mesh-shadows');
            }
            if (data.position) {
                const position = playCanvasPositionFromSceneVec3(data.position);
                entity.setPosition(position.x, position.y, position.z);
            }
            if (data.rotation) {
                // Rotation stored as BabylonJS radians — convert to PC quaternion
                entity.setRotation(bjsRotationToPC(data.rotation.x ?? 0, data.rotation.y ?? 0, data.rotation.z ?? 0));
            }
            if (data.scale) {
                entity.setLocalScale(data.scale.x ?? 1, data.scale.y ?? 1, data.scale.z ?? 1);
            }
            if ('opacityMode' in data || 'opacity' in data || 'opacityAnimation' in data) {
                // Write the config that the scroll-driven path (and a still-loading GLB's ready
                // callback) reads, then apply the value for the current progress right away.
                let meshData: CustomMeshData | undefined;
                customMeshEntities.forEach((candidate) => {
                    if (candidate.entity === entity) meshData = candidate;
                });
                if (meshData) {
                    const cfg = meshData.config;
                    if ('opacityMode' in data) cfg.opacityMode = data.opacityMode;
                    if ('opacity' in data) cfg.opacity = data.opacity;
                    if ('opacityAnimation' in data) cfg.opacityAnimation = data.opacityAnimation;
                    applyOpacityToCustomMesh(entity, resolveEntityOpacity(cfg, currentProgress * 100));
                }
            }
        };
        editorInstance.removeCustomMesh = (id: string) => {
            const entity = editorCustomMeshMap.get(id);
            if (entity) {
                // Detach canvas listeners FIRST — the editor's mesh-sync does a full
                // remove+add per edit, so leaked handlers accumulate per edit cycle.
                removeCustomMeshCanvasListeners(entity as ViewerRuntimeEntity, app.graphicsDevice.canvas as HTMLCanvasElement);
                // Also remove from customMeshEntities if present, and cancel pending asset loads
                const removed: CustomMeshData[] = [];
                customMeshEntities.forEach((meshData, key) => {
                    if (meshData.entity === entity) {
                        meshData.removed = true;
                        customMeshEntities.delete(key);
                        windSystem.removeObject(key);
                        removed.push(meshData);
                    }
                });
                // Unload/remove its assets (after the entry is gone, so a container shared
                // with another live mesh of the same URL is kept for that mesh).
                removed.forEach((meshData) => releaseCustomMeshAssets(meshData));
                entity.destroy();
                editorCustomMeshMap.delete(id);
            }
        };
        // Particle mutation - uses createParticleSystemEntity() + texture loading
        editorInstance.addParticleSystem = (ps: ParticleConfig): string => {
            const id = ps.id || ps.name || `particle-${Date.now()}`;
            // Create the entity with full particle system configuration
            const entity = createParticleSystemEntity(ps);
            // Resolve and apply texture (custom async, built-in synchronous).
            const textureName = ps.particleTexture || 'flare';
            if (textureName === 'custom' && ps.customTextureUrl) {
                const cacheKey = `custom_${ps.customTextureUrl}`;
                loadCustomParticleTexture(cacheKey, ps.customTextureUrl).then((texture) => {
                    if (entity.particlesystem) {
                        entity.particlesystem.colorMap = texture;
                    }
                }).catch((err) => {
                    console.warn('[Editor] Failed to load custom particle texture:', err);
                });
            }
            else {
                const resolvedName = BUILTIN_PARTICLE_TEXTURES.has(textureName) ? textureName : 'flare';
                if (entity.particlesystem) {
                    entity.particlesystem.colorMap = createProceduralParticleTexture(resolvedName);
                }
            }
            // Add a small pickable sphere as a child so the Picker can detect this entity
            // in the viewport without affecting the particle system's scale
            const pickChild = new pc.Entity('particle-picker');
            pickChild.addComponent('render', {
                type: 'sphere',
                castShadows: false,
                receiveShadows: false,
            });
            pickChild.setLocalScale(0.15, 0.15, 0.15);
            const pickMat = new pc.StandardMaterial();
            pickMat.diffuse = new pc.Color(1, 0.5, 0, 1);
            pickMat.opacity = 0.3;
            pickMat.blendType = pc.BLEND_NORMAL;
            pickMat.depthWrite = false;
            pickMat.update();
            pickChild.render!.meshInstances[0].material = pickMat;
            entity.addChild(pickChild);

            sceneContent.addChild(entity);
            const safeName = id.replace(/[^a-zA-Z0-9]/g, '_');
            particleEntities.set(safeName, entity);
            editorParticleMap.set(id, entity);
            return id;
        };
        editorInstance.removeParticleSystem = (id: string) => {
            const safeName = id.replace(/[^a-zA-Z0-9]/g, '_');
            const entity = editorParticleMap.get(id) || particleEntities.get(safeName);
            if (entity) {
                entity.destroy();
                editorParticleMap.delete(id);
                particleEntities.delete(safeName);
            }
        };
        // Portal mutation - uses createSinglePortal() for full rendering
        editorInstance.addPortal = (portal: PortalData): string => {
            const id = portal.id || `portal-${Date.now()}`;
            const index = portalEntities.length;
            const entity = createSinglePortal(portal, index);
            editorPortalMap.set(id, entity);
            return id;
        };
        editorInstance.removePortal = (id: string) => {
            const entity = editorPortalMap.get(id);
            if (entity) {
                // The portal's video, textures and materials are released when the entity is
                // destroyed (releaseMarkerResources / releaseMarkerStack).
                const line = (entity as ViewerRuntimeEntity).leaderLineEntity;
                if (line) {
                    ((line as any)._leaderMat as pc.StandardMaterial | undefined)?.destroy();
                    line.destroy();
                }
                entity.destroy();
                editorPortalMap.delete(id);
                const idx = portalEntities.indexOf(entity);
                if (idx >= 0)
                    portalEntities.splice(idx, 1);
            }
        };
        editorInstance.updatePortal = (id: string, data: Partial<PortalData>) => {
            const entity = editorPortalMap.get(id);
            if (!entity)
                return;
            if (data.position) {
                const position = playCanvasPositionFromSceneVec3(data.position);
                entity.setPosition(position.x, position.y, position.z);
            }
            if (data.rotation) {
                // Rotation stored as BabylonJS radians — convert to PC degrees with Z negation
                const rx = (data.rotation.x ?? 0) * (180 / Math.PI);
                const ry = (data.rotation.y ?? 0) * (180 / Math.PI);
                const rz = -(data.rotation.z ?? 0) * (180 / Math.PI);
                entity.setEulerAngles(rx, ry, rz);
            }
            if (data.scale) {
                // data.scale is the portal's authored scale (as saved): apply it the way
                // createSinglePortal does — 0.2 sphere / 0.25 globe base, planes swap Y/Z —
                // so the gizmo's scale write-back is idempotent and the leader line below
                // re-anchors to the marker's new size.
                const scaleObj = typeof data.scale === 'number'
                    ? { x: data.scale, y: data.scale, z: data.scale } as LegacyVec3
                    : data.scale as LegacyVec3;
                const sx = Math.abs(scaleObj._x ?? scaleObj.x ?? 1);
                const sy = Math.abs(scaleObj._y ?? scaleObj.y ?? 1);
                const sz = scaleObj._z ?? scaleObj.z ?? 1;
                const shape = portalMarkerShape({ ...(entity as ViewerRuntimeEntity).portalData, ...data });
                if (isPlaneFramedShape(shape)) {
                    entity.setLocalScale(sx, sz, sy);
                } else {
                    const base = shape === 'globe' ? 0.25 : 0.2;
                    entity.setLocalScale(sx * base, sy * base, sz * base);
                }
            }
            if (data.opacity !== undefined && !applyMarkerStackOpacity(entity as ViewerRuntimeEntity, data.opacity)) {
                const mat = (entity as ViewerRuntimeEntity).portalMaterial
                    || (entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined);
                if (mat) {
                    const portalType = data.type ?? (entity as ViewerRuntimeEntity).portalData?.type;
                    const isLegacySphere = portalType === 'sphere' || portalType === 'panorama';
                    applyEntityOpacity(mat, isLegacySphere ? snapLegacySphereOpacity(data.opacity) : data.opacity);
                }
            }
            // Colour: recolour the colour sphere in place (the editor previews colour edits live).
            // Other markers don't draw the colour; their portalData just keeps it.
            if (data.color !== undefined) {
                const mat = (entity as ViewerRuntimeEntity).portalMaterial as pc.StandardMaterial | undefined;
                if (mat && portalMarkerUsesColor({ ...(entity as ViewerRuntimeEntity).portalData, ...data })) {
                    setPortalSphereColor(mat, data.color);
                    mat.update();
                    renderScheduler.requestFrame('portal-color');
                }
            }
            const existing = (entity as ViewerRuntimeEntity).portalData || {};
            const merged = { ...existing, ...data } as PortalData;
            (entity as ViewerRuntimeEntity).portalData = merged;
            if ('keepScreenSize' in data || 'screenSizeDistance' in data || 'screenSizeMin' in data || 'screenSizeMax' in data) {
                renderScheduler.requestFrame('portal-screen-size');
            }
            // Rebuild the leader line whenever its config or portal transform changes.
            if (
                data.position !== undefined ||
                data.scale !== undefined ||
                data.leaderLineEnabled !== undefined ||
                data.leaderLineTarget !== undefined ||
                data.leaderLineColor !== undefined ||
                data.leaderLineWidth !== undefined ||
                data.leaderLineOpacity !== undefined
            ) {
                rebuildLeaderLine(entity as ViewerRuntimeEntity, merged as PortalConfigLike, portalLeaderLineShape(merged));
            }
        };
        // Mirror plane mutation
        editorInstance.addMirrorPlane = (data: MirrorPlaneData): string => {
            const id = data.id || `mirror-${Date.now()}`;
            // Replacing a mirror under the same id: release the old one (its camera, render
            // target and prerender handler would otherwise live on, out of reach).
            if (editorMirrorPlaneMap.has(id)) destroySingleMirrorPlane(id);
            const index = mirrorPlaneEntities.length;
            // Create under the id it is registered and removed by: without one, the entity and its
            // prerender handler were keyed `mirror-<time>-<index>`, so removeMirrorPlane(id)
            // destroyed the entity but left the handler running every frame.
            const entity = createSingleMirrorPlane({ ...data, id }, index);
            editorMirrorPlaneMap.set(id, entity);
            return id;
        };
        editorInstance.removeMirrorPlane = (id: string) => {
            destroySingleMirrorPlane(id);
        };
        editorInstance.updateMirrorPlane = (id: string, data: Partial<MirrorPlaneData>) => {
            const entity = editorMirrorPlaneMap.get(id) as MirrorPlaneRuntimeEntity | undefined;
            if (!entity) return;
            if (data.position) {
                entity.setPosition(data.position.x ?? 0, data.position.y ?? 0, -(data.position.z ?? 0));
            }
            if (data.rotation) {
                const rx = (data.rotation.x ?? 0) * (180 / Math.PI);
                const ry = (data.rotation.y ?? 0) * (180 / Math.PI);
                const rz = -(data.rotation.z ?? 0) * (180 / Math.PI);
                entity.setEulerAngles(rx, ry, rz);
            }
            if (data.scale) {
                entity.setLocalScale(data.scale.x ?? 1, data.scale.y ?? 1, data.scale.z ?? 1);
            }
            // Update material uniforms if intensity or tint changed
            const mat = entity._mirrorMaterial;
            if (mat) {
                if (data.intensity !== undefined) {
                    mat.setParameter('uIntensity', data.intensity);
                }
                if (data.tint) {
                    const r = parseInt(data.tint.slice(1, 3), 16) / 255;
                    const g = parseInt(data.tint.slice(3, 5), 16) / 255;
                    const b = parseInt(data.tint.slice(5, 7), 16) / 255;
                    mat.setParameter('uTint', [r, g, b]);
                }
            }
            // Footprint change requires rebuilding the mesh + (possibly) rotating the child entity.
            if (Object.prototype.hasOwnProperty.call(data, 'footprint')) {
                const stored = entity._mirrorData;
                if (!stored) return;
                const merged: MirrorPlaneData = { ...stored, ...data };
                const index = mirrorPlaneEntities.indexOf(entity);
                destroySingleMirrorPlane(id);
                const rebuilt = createSingleMirrorPlane(merged, Math.max(index, 0));
                editorMirrorPlaneMap.set(id, rebuilt);
                return;
            }
            // Store updated data
            entity._mirrorData = { ...(entity._mirrorData ?? { id }), ...data } as MirrorPlaneData;
        };
        // Water plane mutation
        editorInstance.addWaterPlane = (data: WaterPlaneData): string => {
            const id = data.id || `water-${Date.now()}`;
            // Replacing a water plane under the same id releases the old one first.
            if (editorWaterPlaneMap.has(id)) destroySingleWaterPlane(id);
            const entity = createSingleWaterPlane({ ...data, id }, waterSystem.size);
            editorWaterPlaneMap.set(id, entity);
            return id;
        };
        editorInstance.removeWaterPlane = (id: string) => {
            destroySingleWaterPlane(id);
        };
        editorInstance.updateWaterPlane = (id: string, data: Partial<WaterPlaneData>) => {
            // Transform and look update in place; a new shape / outline rebuilds only the mesh.
            waterSystem.updatePlane(id, data);
        };
        editorInstance.getWaterQualityTier = () => waterSystem.qualityTier;
        // Cloud layer mutation (in place: look fields are uniforms; shape inputs regenerate once edits settle)
        editorInstance.addCloudLayer = (data: CloudLayerData): string => {
            const id = data.id || `clouds-${Date.now()}`;
            if (editorCloudLayerMap.has(id)) destroySingleCloudLayer(id);
            const entity = createSingleCloudLayer({ ...data, id }, cloudSystem.size);
            editorCloudLayerMap.set(id, entity);
            return id;
        };
        editorInstance.removeCloudLayer = (id: string) => {
            destroySingleCloudLayer(id);
        };
        editorInstance.updateCloudLayer = (id: string, data: Partial<CloudLayerData>) => {
            cloudSystem.updateLayer(id, data);
        };
        editorInstance.setCloudLayerBoundsVisible = (id: string | null) => cloudSystem.setBoundsVisible(id);
        // The scene's clips, whether or not the timeline is open (it pushes them to the animation
        // system only while open): a layer with a keyframed size generates for the largest size its
        // keyframes reach, exactly as the published viewer does.
        editorInstance.setCloudAnimations = (animations) => cloudSystem.setAnimations(animations);
        editorInstance.getCloudQualityTier = () => cloudSystem.qualityTier;
        editorInstance.suggestCloudLayer = (preset) => suggestCloudLayer(preset);
        editorInstance.setScanSky = (settings) => {
            config.scanSky = settings;
            scanSky.setSettings(settings);
        };
        editorInstance.setScanSkyPreview = (on: boolean) => scanSky.setPreview(on);
        // Scene wind: settings and wind-reactive areas in place (areas recompile only when their
        // set or an outline changes; everything else is uniforms).
        editorInstance.setSceneWind = (settings) => {
            config.wind = settings;
            windSystem.clearAnimated();
            windSystem.setSettings(settings);
        };
        editorInstance.setWindAreaBoundsVisible = (id: string | null) => windSystem.setAreaBoundsVisible(id);
        editorInstance.getScanSkyInfo = () => {
            scanSky.demand();
            const shell = scanSky.shell();
            if (!shell) return { available: false };
            return { available: true, keepRadius: round2(shell.keepRadius), shellDistance: shell.shellDistance === null ? null : round2(shell.shellDistance), skyCoverage: round2(shell.skyCoverage), objectsTop: round2(shell.denseTop), ground: round2(shell.ground) };
        };
        // Proximity radius visualization
        let proximityRadiusEntity: pc.Entity | null = null;
        editorInstance.showProximityRadius = (entity: pc.Entity, radius: number, audioConfig?: { maxDistance?: number; refDistance?: number; rolloffFactor?: number; distanceModel?: string }) => {
            // Remove existing visualization — unregister update handler first to avoid stale callbacks
            if (proximityRadiusEntity) {
                const oldUpdateFn = (proximityRadiusEntity as any)._proximityUpdateFn;
                if (oldUpdateFn) app.off('update', oldUpdateFn);
                proximityRadiusEntity.destroy();
                proximityRadiusEntity = null;
            }
            const root = new pc.Entity('proximity-radius-preview');
            const pos = entity.getPosition();
            root.setPosition(pos.x, pos.y, pos.z);
            // Helper to create a flat disc
            const createDisc = (name: string, r: number, color: pc.Color, opacity: number): pc.Entity => {
                const disc = new pc.Entity(name);
                disc.addComponent('render', { type: 'cylinder', castShadows: false, receiveShadows: false });
                disc.setLocalScale(r * 2, 0.05, r * 2);
                disc.setLocalPosition(0, 0, 0);
                const mat = new pc.StandardMaterial();
                mat.diffuse = color;
                mat.emissive = new pc.Color(color.r * 0.8, color.g * 0.8, color.b * 0.8);
                mat.opacity = opacity;
                mat.blendType = opacity < 1 ? pc.BLEND_NORMAL : pc.BLEND_NONE;
                mat.depthWrite = opacity >= 1;
                mat.cull = pc.CULLFACE_NONE;
                mat.useLighting = false;
                mat.update();
                disc.render!.meshInstances[0].material = mat;
                return disc;
            };
            // Helper to create a thin ring
            const createRing = (name: string, r: number, color: pc.Color, height: number): pc.Entity => {
                const ring = new pc.Entity(name);
                ring.addComponent('render', { type: 'cylinder', castShadows: false, receiveShadows: false });
                ring.setLocalScale(r * 2, height, r * 2);
                ring.setLocalPosition(0, 0, 0);
                const mat = new pc.StandardMaterial();
                mat.diffuse = color;
                mat.emissive = new pc.Color(color.r * 0.9, color.g * 0.9, color.b * 0.9);
                mat.opacity = 1.0;
                mat.blendType = pc.BLEND_NONE;
                mat.depthWrite = true;
                mat.cull = pc.CULLFACE_NONE;
                mat.useLighting = false;
                mat.update();
                ring.render!.meshInstances[0].material = mat;
                return ring;
            };
            // Trigger radius — solid blue disc + ring edge
            if (radius > 0) {
                const triggerColor = new pc.Color(0.15, 0.5, 1.0);
                const triggerDisc = createDisc('trigger-disc', radius, triggerColor, 0.5);
                const triggerRing = createRing('trigger-ring', radius, new pc.Color(0.3, 0.7, 1.0), 0.15);
                root.addChild(triggerDisc);
                root.addChild(triggerRing);
            }
            if (audioConfig) {
                const maxDist = audioConfig.maxDistance;
                const refDist = audioConfig.refDistance;
                // Max Distance — orange: the distance at which audio is fully silent
                if (maxDist && maxDist > 0) {
                    const audioDisc = createDisc('audio-max-disc', maxDist, new pc.Color(1.0, 0.6, 0.15), 0.12);
                    const audioRing = createRing('audio-max-ring', maxDist, new pc.Color(1.0, 0.7, 0.2), 0.1);
                    root.addChild(audioDisc);
                    root.addChild(audioRing);
                }
                // Ref Distance — green: the distance at which audio starts attenuating
                if (refDist && refDist > 0) {
                    const refDisc = createDisc('audio-ref-disc', refDist, new pc.Color(0.2, 0.8, 0.3), 0.3);
                    const refRing = createRing('audio-ref-ring', refDist, new pc.Color(0.3, 0.9, 0.4), 0.12);
                    root.addChild(refDisc);
                    root.addChild(refRing);
                }
                // Rolloff visualization: show intermediate attenuation rings between ref and max distance
                if (refDist && maxDist && maxDist > refDist && audioConfig.rolloffFactor) {
                    const rolloff = audioConfig.rolloffFactor;
                    const model = audioConfig.distanceModel || 'linear';
                    // Draw 3 intermediate rings showing where volume drops to 75%, 50%, 25%
                    const levels = [0.75, 0.5, 0.25];
                    for (const level of levels) {
                        let dist: number | null = null;
                        if (model === 'linear') {
                            // linear: gain = 1 - rolloff * (d - ref) / (max - ref)
                            // solve for d: d = ref + (1 - level) * (max - ref) / rolloff
                            dist = refDist + (1 - level) * (maxDist - refDist) / rolloff;
                        } else if (model === 'inverse') {
                            // inverse: gain = ref / (ref + rolloff * (d - ref))
                            // solve: d = ref + (ref / level - ref) / rolloff
                            dist = refDist + (refDist / level - refDist) / rolloff;
                        } else if (model === 'exponential') {
                            // exponential: gain = (d / ref) ^ -rolloff
                            // solve: d = ref * level ^ (-1/rolloff)
                            dist = refDist * Math.pow(level, -1 / rolloff);
                        }
                        if (dist !== null && dist > refDist && dist < maxDist) {
                            const alpha = 1 - level; // more transparent for louder (closer)
                            const ringColor = new pc.Color(0.6, 0.6, 0.6);
                            const ring = createRing(`rolloff-${Math.round(level * 100)}`, dist, ringColor, 0.04);
                            // Make it semi-transparent
                            const mat = ring.render!.meshInstances[0].material as pc.StandardMaterial;
                            mat.opacity = 0.3 + alpha * 0.4;
                            mat.blendType = pc.BLEND_NORMAL;
                            mat.depthWrite = false;
                            mat.update();
                            root.addChild(ring);
                        }
                    }
                }
            }
            const parts: string[] = [`trigger=${radius}`];
            if (audioConfig?.maxDistance) parts.push(`max=${audioConfig.maxDistance}`);
            if (audioConfig?.refDistance) parts.push(`ref=${audioConfig.refDistance}`);
            if (audioConfig?.rolloffFactor) parts.push(`rolloff=${audioConfig.rolloffFactor}`);
            console.log(`[Editor] Proximity radius: entity=${entity.name}, ${parts.join(', ')}`);
            // Track position each frame — look up entity by name so it survives
            // the destroy/recreate cycle when editor sync effects re-add entities.
            const entityName = entity.name;
            const updateFn = () => {
                let target: pc.Entity | null = entity.parent ? entity : null;
                if (!target && entityName) {
                    target = app.root.findByName(entityName) as pc.Entity | null;
                }
                if (target) {
                    const p = target.getPosition();
                    root.setPosition(p.x, p.y, p.z);
                }
            };
            app.on('update', updateFn);
            (root as any)._proximityUpdateFn = updateFn;
            app.root.addChild(root);
            proximityRadiusEntity = root;
        };
        editorInstance.hideProximityRadius = () => {
            if (proximityRadiusEntity) {
                const updateFn = (proximityRadiusEntity as any)._proximityUpdateFn;
                if (updateFn) app.off('update', updateFn);
                proximityRadiusEntity.destroy();
                proximityRadiusEntity = null;
            }
        };
        // HTML Mesh mutation - uses HtmlMeshManager
        editorInstance.addHtmlMesh = (meshConfig: HtmlMeshConfig): string => {
            const id = meshConfig.id || `html-mesh-${Date.now()}`;
            let manager = (app as ViewerRuntimeApp).__htmlMeshManager;
            if (!manager) {
                manager = setupHtmlMeshes(app, []);
                (app as ViewerRuntimeApp).__htmlMeshManager = manager;
            }
            manager.createMesh({ ...meshConfig, id, html: meshConfig.html || meshConfig.htmlContent || '' });
            // Apply the current tour position immediately so a mesh whose
            // visibilityRange excludes the current progress starts hidden, rather
            // than waiting for the next scroll tick.
            manager.updateVisibility(currentProgress * 100, getWaypointIndexForProgress(currentProgress), loopMode === 'loop');
            return id;
        };
        editorInstance.removeHtmlMesh = (id: string) => {
            const manager = (app as ViewerRuntimeApp).__htmlMeshManager;
            if (manager) {
                manager.destroyMesh(id);
            }
        };
        // Update an HTML mesh in place (no destroy/recreate): transform, pixel size, content,
        // editor visibility and visibility range, applied exactly as HtmlMeshManager.createMesh
        // would build them (HtmlMeshManager.updateMesh). Scale is the scene scale (world width =
        // scale.x times the pixel aspect), like createMesh; billboard changes need a re-create.
        editorInstance.updateHtmlMesh = (id: string, data: HtmlMeshUpdateData) => {
            const manager = (app as ViewerRuntimeApp).__htmlMeshManager;
            if (!manager?.updateMesh(id, data)) return;
            if (Object.prototype.hasOwnProperty.call(data, 'visible') || Object.prototype.hasOwnProperty.call(data, 'visibilityRange')) {
                // Same as addHtmlMesh: apply the current tour position to the new visibility.
                manager.updateVisibility(currentProgress * 100, getWaypointIndexForProgress(currentProgress), loopMode === 'loop');
            }
        };
        // Collision Mesh mutation
        // The editor's live colliders are the scene's collision: the first one it adds or removes
        // replaces the colliders built from the scene data at load, so a collider moved, deleted or
        // added in the editor collides in walk / fly preview where it is now, not where it was saved.
        let sceneCollidersReleased = false;
        const takeOverSceneColliders = () => {
            if (sceneCollidersReleased) return;
            sceneCollidersReleased = true;
            if (!characterController) return;
            characterController.releaseSceneCollisionMeshes();
            cameraControls.setCollisionEntities(characterController.collisionMeshEntities);
        };
        editorInstance.addCollisionMesh = (meshConfig: CollisionMeshConfig): string => {
            takeOverSceneColliders();
            const id = meshConfig.id || `collision-${Date.now()}`;
            const entity = new pc.Entity(`collision-mesh-${id}`);
            const meshType = meshConfig.meshType || 'cube';
            // Position: negate Z for BabylonJS → PlayCanvas (same as CharacterController)
            const pos = meshConfig.position;
            const px = Array.isArray(pos) ? pos[0] : pos.x;
            const py = Array.isArray(pos) ? pos[1] : pos.y;
            const pz = Array.isArray(pos) ? pos[2] : pos.z;
            entity.setPosition(px ?? 0, py ?? 0, -(pz ?? 0));
            // Rotation: convert BabylonJS radians YXZ left-handed → PlayCanvas quaternion
            // Matches CharacterController.configureCollisionEntity exactly
            if (meshConfig.rotation) {
                const rot = meshConfig.rotation;
                const rx = (Array.isArray(rot) ? rot[0] : rot.x) ?? 0;
                const ry = (Array.isArray(rot) ? rot[1] : rot.y) ?? 0;
                const rz = (Array.isArray(rot) ? rot[2] : rot.z) ?? 0;
                const hy = ry / 2, hx = rx / 2, hz = rz / 2;
                const cy = Math.cos(hy), sy = Math.sin(hy);
                const cx = Math.cos(hx), sx = Math.sin(hx);
                const cz = Math.cos(hz), sz = Math.sin(hz);
                // Quaternion in BJS left-handed space (YXZ order)
                const qw = cy * cx * cz + sy * sx * sz;
                const qx = cy * sx * cz + sy * cx * sz;
                const qy = sy * cx * cz - cy * sx * sz;
                const qz = cy * cx * sz - sy * sx * cz;
                // Convert LH → RH (Z-flip): negate x and y
                const userRot = new pc.Quat(-qx, -qy, qz, qw);
                if (meshType === 'plane') {
                    // PlayCanvas plane is XZ (horizontal); BabylonJS plane is XY (vertical).
                    // Compose 90° X tilt with user rotation.
                    const faceForward = new pc.Quat().setFromEulerAngles(90, 0, 0);
                    entity.setRotation(new pc.Quat().mul2(userRot, faceForward));
                } else {
                    entity.setRotation(userRot);
                }
            }
            // Scale: multiply by BabylonJS primitive base sizes (PlayCanvas primitives are 1x1x1)
            const s = meshConfig.scaling;
            const sx = (s ? (Array.isArray(s) ? s[0] : s.x) : 1) ?? 1;
            const sy = (s ? (Array.isArray(s) ? s[1] : s.y) : 1) ?? 1;
            const sz = (s ? (Array.isArray(s) ? s[2] : s.z) : 1) ?? 1;
            if (meshType === 'plane') {
                // BabylonJS plane is 3x3 in XY; after 90° X tilt: local X→width, local Z→height
                const THIN = 0.01;
                entity.setLocalScale(sx * 3, sz * THIN, sy * 3);
            } else if (meshType === 'cube' || meshType === 'sphere' || meshType === 'torus') {
                entity.setLocalScale(sx * 3, sy * 3, sz * 3);
            } else if (meshType === 'floor') {
                entity.setLocalScale(sx * 100, sy * 1, sz * 100);
            } else {
                entity.setLocalScale(sx, sy, sz);
            }
            // Add render component for visualization
            if (meshType !== 'custom') {
                const renderType = meshType === 'cube' ? 'box' : meshType === 'floor' ? 'plane' : meshType;
                entity.addComponent('render', {
                    type: renderType,
                    castShadows: false,
                    receiveShadows: false,
                });
                // Opaque green material for collision mesh visualization
                const material = new pc.StandardMaterial();
                material.diffuse = new pc.Color(0, 0.8, 0);
                material.opacity = 1;
                material.blendType = pc.BLEND_NONE;
                material.depthWrite = true;
                material.cull = pc.CULLFACE_NONE;
                material.useLighting = false;
                material.update();
                if (entity.render) {
                    entity.render.meshInstances.forEach((mi) => { mi.material = material; });
                }
            }
            entity.addComponent('collision', {
                type: meshType === 'sphere' ? 'sphere' : 'box',
            });
            // Hidden (not editing collision) still collides: collision ignores `enabled`.
            entity.enabled = meshConfig.visible !== false;
            (entity as ViewerRuntimeEntity)._collisionMeshId = id;
            // Store mesh type for collision logic
            (entity as ViewerRuntimeEntity)._collisionMeshType = meshType;
            // "Inside" (dome) colliders keep the player within them instead of blocking entry.
            (entity as ViewerRuntimeEntity)._collisionInsideMode = meshConfig.inside === true;
            sceneContent.addChild(entity);
            editorCollisionMeshMap.set(id, entity);
            // Lazily create CharacterController if it doesn't exist yet
            // (scene had no collision meshes at load time)
            characterController = createLazyCharacterController('primitive collision');
            const registerCollision = () => {
                const cc = characterController;
                if (!cc) return;
                // Register with CharacterController so walk-mode collision works immediately
                cc.addCollisionEntity(entity);
                // Update CameraControls (fly/orbit collision) with the new entity list
                cameraControls.setCollisionEntities(cc.collisionMeshEntities);
            };
            if (meshType === 'custom') {
                // A model has no shape until it loads: it collides from then on, on its triangles.
                loadEditorCustomCollider(entity, id, meshConfig, registerCollision);
            } else {
                registerCollision();
            }
            return id;
        };
        editorInstance.removeCollisionMesh = (id: string) => {
            takeOverSceneColliders();
            const entity = editorCollisionMeshMap.get(id);
            if (entity) {
                // Unregister from CharacterController before destroying
                if (characterController) {
                    characterController.removeCollisionEntity(entity);
                    cameraControls.setCollisionEntities(characterController.collisionMeshEntities);
                }
                // addCollisionMesh news a StandardMaterial per mesh; entity.destroy()
                // does not free it — destroy it here or every collision rebuild
                // (undo/redo, reposition, toggles) leaks a material + pipeline.
                const mat = (entity.render?.meshInstances?.[0]?.material as pc.StandardMaterial | undefined) ?? editorColliderMaterials.get(entity);
                editorColliderMaterials.delete(entity);
                entity.destroy();
                mat?.destroy?.();
            }
            editorCollisionMeshMap.delete(id);
        };
        // Audio Emitter mutation - uses same setup as setupAudioEmitters()
        editorInstance.addAudioEmitter = (emitterConfig: AudioEmitterConfig): string => {
            const id = emitterConfig.id || `emitter-${Date.now()}`;
            const position = playCanvasPositionFromSceneVec3(emitterConfig.position);
            const volume = finiteNonNegativeSceneNumber(emitterConfig.volume, 0.5);
            const audioEntity = new pc.Entity(`audio-emitter-${id}`);
            audioEntity.setPosition(position.x, position.y, position.z);
            audioEntity.addComponent('sound', {
                positional: emitterConfig.spatialSound !== false,
                refDistance: finitePositiveSceneNumber(emitterConfig.refDistance, 1),
                maxDistance: finitePositiveSceneNumber(emitterConfig.maxDistance, 10),
                rollOffFactor: finiteNonNegativeSceneNumber(emitterConfig.rolloffFactor, 1),
                distanceModel: getPlayCanvasDistanceModel(emitterConfig.distanceModel || 'linear'),
                volume,
            });
            audioEntity.sound?.addSlot(id, {
                volume,
                loop: emitterConfig.loop !== false,
                autoPlay: false,
                overlap: false,
            });
            // Only load and play audio if the emitter is enabled
            if (emitterConfig.url && emitterConfig.enabled !== false) {
                const audioAsset = new pc.Asset(`audio-emitter-${id}`, 'audio', { url: emitterConfig.url });
                audioAsset.on('load', () => {
                    if (isDestroyed)
                        return;
                    const slot = audioEntity.sound?.slot(id);
                    if (slot) {
                        slot.asset = audioAsset.id;
                    }
                    const emitterData = audioEmitterMap.get(id);
                    if (emitterData) {
                        emitterData.assetReady = true;
                        if (slot) {
                            trackSlotPlaybackState(slot, (playing) => {
                                emitterData.playing = playing;
                            });
                        }
                        const triggerMode = emitterConfig.triggerMode || (emitterConfig.autoplay !== false ? 'autoplay' : 'proximity');
                        if (triggerMode === 'autoplay' && slot) {
                            slot.play();
                            emitterData.playing = true;
                        }
                    }
                });
                app.assets.add(audioAsset);
                app.assets.load(audioAsset);
            }
            // Add a small pickable sphere so the entity is visible and selectable in the viewport
            audioEntity.addComponent('render', {
                type: 'sphere',
                castShadows: false,
                receiveShadows: false,
            });
            audioEntity.setLocalScale(0.3, 0.3, 0.3);
            const audioPickMat = new pc.StandardMaterial();
            audioPickMat.diffuse = new pc.Color(0.2, 0.6, 1, 1);
            audioPickMat.emissive = new pc.Color(0.1, 0.3, 0.5, 1);
            audioPickMat.update();
            audioEntity.render!.meshInstances[0].material = audioPickMat;

            sceneContent.addChild(audioEntity);
            audioEmitterMap.set(id, {
                entity: audioEntity,
                config: emitterConfig,
                slotId: id,
                playing: false,
                assetReady: false,
            });
            return id;
        };
        editorInstance.removeAudioEmitter = (id: string) => {
            const data = audioEmitterMap.get(id);
            if (data) {
                const slot = data.entity.sound?.slot(data.slotId);
                if (slot)
                    slot.stop();
                data.entity.destroy();
                audioEmitterMap.delete(id);
            }
        };
        editorInstance.updateAudioEmitter = (id: string, data: Partial<AudioEmitterConfig>) => {
            const emitterData = audioEmitterMap.get(id);
            if (!emitterData)
                return;
            if (data.position) {
                const position = playCanvasPositionFromSceneVec3(data.position);
                emitterData.entity.setPosition(position.x, position.y, position.z);
            }
            if (data.volume !== undefined && emitterData.entity.sound) {
                emitterData.entity.sound.volume = finiteNonNegativeSceneNumber(data.volume, 0.5);
            }
            emitterData.config = { ...emitterData.config, ...data };
        };
        // Splat Relighting
        editorInstance.setSplatRelighting = (relightCfg: Partial<SplatRelightingConfig>) => {
            // Update config
            config.splatRelighting = { ...config.splatRelighting, ...relightCfg };
            clearSceneAnimationOverrides('relighting');

            if (relightCfg.enabled === false) {
                // Relighting off. While shadows are on the splats still need the shader (it
                // receives them): keep the scripts on at fade 0 instead of switching them off and
                // back on (ensureRelightingForShadows), which dropped the splat shadows for a
                // frame on every editor change.
                const keepForShadows = config.splatRelighting?.shadowsEnabled === true;
                for (const s of allRelightingScripts) {
                    if (keepForShadows) s.setRelightFade(0);
                    else s.enabled = false;
                }
                relightingEnabled = false;
                renderScheduler.requestFrame('shadow-settings');
                syncMeshAmbient();
                return;
            }

            // If not yet initialized and lights exist, initialize
            if (!relightingScript && lightEntities.length > 0 && splatEntity) {
                initSplatRelighting();
            }

            if (relightingScript) {
                // Update ambient on ALL scripts
                if (relightCfg.ambientColor !== undefined || relightCfg.ambientIntensity !== undefined) {
                    const ambientColor = relightCfg.ambientColor ?? config.splatRelighting?.ambientColor ?? '#ffffff';
                    const ambientIntensity = relightCfg.ambientIntensity ?? config.splatRelighting?.ambientIntensity;
                    for (const s of allRelightingScripts) {
                        applyRelightingAmbient(s, { ambientColor, ambientIntensity });
                    }
                }
                // Editor preview should visibly turn on whenever the editor enables relighting,
                // even if a disabled script was already created at load time.
                if (relightCfg.enabled === true) {
                    for (const s of allRelightingScripts) {
                        s.enabled = true;
                        applyRelightingAmbient(s, config.splatRelighting);
                        s.setRelightFade(1.0);
                    }
                    relightingEnabled = true;
                }
            }

            // Shadow settings are read by the shadow system every frame; just render the change.
            renderScheduler.requestFrame('shadow-settings');
            syncMeshAmbient();
        };
        // Set relighting fade without changing enabled state (for editor preview toggle)
        editorInstance.setRelightFade = (fade: number) => {
            for (const s of allRelightingScripts) {
                s.setRelightFade(fade);
            }
        };
        // Narration — update track at runtime (editor uploads audio after viewer init)
        editorInstance.setNarrationTrack = (track: { audioUrl: string; volume?: number; backgroundMusicUrl?: string; backgroundMusicVolume?: number; segments: Array<{ id: string; audioStart: number; audioEnd: number; progressStart: number; progressEnd: number }>; enabled: boolean } | null) => {
            if (track) {
                initNarrationAudio(track);
            } else {
                // Disable narration
                cleanupNarrationAudio();
                narrationSegments = [];
                narrationEnabled = false;
            }
        };
        // Environment
        editorInstance.setSkybox = (url: string | null, rotation?: number, verticalOffset?: number) => {
            setSkyboxRuntime(url, rotation, undefined, verticalOffset);
        };
        editorInstance.setSkyboxVerticalOffset = (offset: number) => {
            if (config.skybox) config.skybox.verticalOffset = offset;
            config.skyboxVerticalOffset = offset;
            currentSkyboxVerticalOffset = offset;
            const hasSkyboxUrl = !!(config.skybox?.url || config.skyboxUrl);
            if (!hasSkyboxUrl) return;
            if (currentSkyboxEntity) {
                // Sphere skybox active — reproject its UVs in place, no rebuild
                // needed. When the offset returns to 0 we keep the sphere
                // (visually equivalent); the native pipeline comes back on the
                // next full skybox set / scene load.
                applySkyboxHorizonOffset(offset);
                return;
            }
            // Native (infinite) skybox active — its projection can't be warped, so
            // rebuild on the sphere path. Guard against duplicate rebuilds while one
            // is in flight (slider drags fire many changes before the image re-decodes).
            if (offset !== 0 && !skyboxOffsetRebuildPending) {
                skyboxOffsetRebuildPending = true;
                initSkybox();
            }
        };
        editorInstance.setSkyboxRotation = (rotation: number) => {
            sceneAnimationOverrides.environment.delete('skybox.rotation');
            const rotQuat = new pc.Quat();
            rotQuat.setFromEulerAngles(0, -rotation * (180 / Math.PI), 0);
            app.scene.skyboxRotation = rotQuat;
            // Also update fallback sphere entity if present
            if (currentSkyboxEntity) {
                currentSkyboxEntity.setEulerAngles(0, -rotation * (180 / Math.PI), 0);
            }
            // Keep config in sync
            if (config.skybox) config.skybox.rotation = rotation;
            config.skyboxRotation = rotation;
        };
        editorInstance.setBackgroundColor = (color: {
            r: number;
            g: number;
            b: number;
        }) => {
            sceneAnimationOverrides.environment.delete('background.color.r');
            sceneAnimationOverrides.environment.delete('background.color.g');
            sceneAnimationOverrides.environment.delete('background.color.b');
            const pcColor = new pc.Color(color.r / 255, color.g / 255, color.b / 255);
            baseBackgroundColor.copy(pcColor);
            if (camera.camera) {
                camera.camera.clearColor = pcColor;
            }
        };
        editorInstance.setFOV = (newFov: number) => {
            config.fov = newFov;
            defaultFOV = newFov;
            if (config.waypoints && waypointFOVs.length > 0) {
                config.waypoints.forEach((wp, index) => {
                    if (wp.fov == null && index < waypointFOVs.length) {
                        waypointFOVs[index] = newFov;
                    }
                });
                if (waypointFOVs.length === 1) {
                    targetFOV = waypointFOVs[0];
                }
                else if (waypointControlEnabled) {
                    updateCameraFromProgress(currentProgress);
                }
            }
            if (camera.camera) {
                camera.camera.fov = newFov;
            }
        };
        // Splat loading (editor use - load/replace primary splat after init)
        editorInstance.loadSplatUrl = (splatUrl: string, sogUrl?: string, ...fallbackUrls: Array<string | undefined>) => loadSplatByUrl(splatUrl, sogUrl, ...fallbackUrls);
        editorInstance.whenRevealReady = () => lastLoadRevealReady;
        // Waypoints (operate on config.waypoints directly)
        editorInstance.addWaypoint = (wp: WaypointData) => {
            if (!config.waypoints)
                config.waypoints = [];
            config.waypoints.push(wp);
        };
        editorInstance.removeWaypoint = (index: number) => {
            if (config.waypoints && index >= 0 && index < config.waypoints.length) {
                config.waypoints.splice(index, 1);
            }
        };
        editorInstance.updateWaypoint = (index: number, data: Partial<WaypointData>) => {
            if (config.waypoints && index >= 0 && index < config.waypoints.length) {
                Object.assign(config.waypoints[index], data);
                applyOverlays(); // name / info preview banners
            }
        };
        editorInstance.rebuildTourPath = (newWaypoints?: WaypointData[]) => {
            // Optionally replace config.waypoints before rebuilding
            if (newWaypoints) {
                config.waypoints = newWaypoints;
                applyOverlays(); // name / info preview banners
            }
            // Rebuild pre-computed waypoint arrays from config.waypoints
            waypointPositions.length = 0;
            waypointRotations.length = 0;
            waypointFOVs.length = 0;
            if (config.waypoints && config.waypoints.length > 0) {
                config.waypoints.forEach(wp => {
                    const pos = wp.position
                        ? new pc.Vec3(wp.position.x, wp.position.y, -wp.position.z)
                        : new pc.Vec3(0, config.playerHeight || 1.6, 0);
                    waypointPositions.push(pos);
                    const rot = wp.rotation
                        ? convertWaypointRotation(wp.rotation)
                        : new pc.Quat();
                    waypointRotations.push(rot);
                    let fov = wp.fov || defaultFOV;
                    if (fov < 3.5) fov = fov * (180 / Math.PI);
                    waypointFOVs.push(fov);
                });
            }
            // Always initialize targets so they're ready when tour mode activates.
            // The damping loop only runs when waypointControlEnabled is true,
            // so setting targets in explore/edit mode won't move the camera.
            if (waypointPositions.length >= 2) {
                // Temporarily enable waypoint control so updateCameraFromProgress doesn't bail
                const wasEnabled = waypointControlEnabled;
                waypointControlEnabled = true;
                updateCameraFromProgress(currentProgress);
                waypointControlEnabled = wasEnabled;
            } else if (waypointPositions.length > 0) {
                targetCameraPosition = waypointPositions[0].clone();
                targetCameraRotation = waypointRotations[0].clone();
                targetFOV = waypointFOVs[0];
            }
            // Recalculate playbackSpeed for the new waypoint count
            const numWp = config.waypoints?.length || 1;
            const pl = Math.max(1, getTourSegmentCount(loopMode, numWp) * 20);
            if (config.autoplaySpeed !== undefined) {
                playbackSpeed = (config.autoplaySpeed * 60) / pl;
            }
            // Rebuild waypoint audio entities so audio interactions on the new waypoints
            // actually play. Without this, audio interactions are silently ignored after
            // any editor-driven waypoint update.
            setupWaypointAudio();
            console.log('[Editor] Tour path rebuilt with', waypointPositions.length, 'waypoints');
        };
        // Extra waypoints live outside the tour: nothing to rebuild, hotspot clicks and
        // goToExtraWaypoint resolve against config.extraWaypoints when they run.
        editorInstance.setExtraWaypoints = (extraWaypoints: ExtraWaypointData[]) => {
            config.extraWaypoints = normalizeExtraWaypoints(extraWaypoints);
        };
        // Splat transform mutation
        editorInstance.setSplatScale = (scale: number, invertX?: boolean, invertY?: boolean) => {
            const ix = invertX ?? config.invertXScale ?? false;
            const iy = invertY ?? config.invertYScale ?? false;
            // Always keep config in sync so setupSplatEntity uses current values on reload
            config.invertXScale = ix;
            config.invertYScale = iy;
            config.scale = { x: scale, y: scale, z: scale };
            if (!splatEntity) return;
            // LCC2 uses identity scale (no Babylon Y=-1 flip) because the data is
            // already in PlayCanvas-native coords. Mirror setupSplatEntity's logic.
            const isLcc2 = primarySplatCoordinateCorrection?.sourceFormat === 'lcc2';
            const sx = ix ? -scale : scale;
            const sy = isLcc2 ? (iy ? -scale : scale) : (iy ? scale : -scale);
            const sz = isLcc2 ? ((ix !== iy) ? -scale : scale) : ((ix !== iy) ? scale : -scale);
            splatEntity.setLocalScale(sx, sy, sz);
            syncSplatCollisionBindings();
        };
        editorInstance.setSplatPosition = (x: number, y: number, z: number) => {
            config.position = [x, y, z];
            if (!splatEntity) return;
            // LCC2: skip Z negation (handled by rotation correction instead).
            const isLcc2 = primarySplatCoordinateCorrection?.sourceFormat === 'lcc2';
            splatEntity.setPosition(x, y, isLcc2 ? z : -z);
            syncSplatCollisionBindings();
        };
        editorInstance.setSplatRotation = (x: number, y: number, z: number) => {
            config.rotation = [x, y, z];
            if (!splatEntity) return;
            applySplatEntityRotation(splatEntity, [x, y, z], primarySplatCoordinateCorrection);
            syncSplatCollisionBindings();
        };
        editorInstance.getEntityBounds = (entity: pc.Entity) => computeRenderBounds(entity);
        // Editor-only hiding (the outliner's eye). Looked up through the entity maps and kept
        // off just before every render, so visibility windows, animation or triggers that
        // switch the entity back on don't show it, and a recreated entity stays hidden.
        const editorHidden = new Map<string, { source: { map: string; id: string }; entity: pc.Entity | null; ownEnabled: boolean }>();
        const resolveEditorEntity = (source: { map: string; id: string }): pc.Entity | null => {
            if (source.map === 'mainSplat') return splatEntity ?? null;
            const getter = (editorInstance as unknown as Record<string, unknown>)[source.map];
            if (typeof getter !== 'function' || !source.map.startsWith('getAll')) return null;
            return (getter as () => Map<string, pc.Entity>)()?.get(source.id) ?? null;
        };
        // the entity's own flag, not `enabled` (which is also false under a disabled parent)
        const ownEnabled = (e: pc.Entity) => (e as unknown as { _enabled?: boolean })._enabled !== false;
        const enforceEditorHidden = () => {
            for (const h of editorHidden.values()) {
                const e = resolveEditorEntity(h.source);
                if (e !== h.entity) { h.entity = e; h.ownEnabled = e ? ownEnabled(e) : true; }
                if (e && ownEnabled(e)) e.enabled = false;
            }
        };
        app.on('prerender', enforceEditorHidden);
        editorInstance.setEditorHidden = (source: { map: string; id: string }, hidden: boolean) => {
            const key = `${source.map}:${source.id}`;
            const current = editorHidden.get(key);
            if (hidden && !current) {
                editorHidden.set(key, { source, entity: null, ownEnabled: true });
                enforceEditorHidden();
            } else if (!hidden && current) {
                editorHidden.delete(key);
                const e = resolveEditorEntity(source);
                if (e && e === current.entity && current.ownEnabled) e.enabled = true;
            }
            app.renderNextFrame = true;
        };
        // Inverse of the three setters above, for the editor's main-splat gizmo.
        editorInstance.readSplatTransform = () => {
            if (!splatEntity) return null;
            const isLcc2 = primarySplatCoordinateCorrection?.sourceFormat === 'lcc2';
            const p = splatEntity.getPosition();
            const position: [number, number, number] = [p.x, p.y, isLcc2 ? p.z : -p.z];
            // applySplatEntityRotation sets base * correction with base = euler(x°, y°, -z°).
            // getRotation() reads the world matrix, which folds the splat's negative-scale axis
            // flips into the rotation (a 180° turn); parent rotation * local rotation does not.
            const parentRotation = (splatEntity.parent as pc.GraphNode | null)?.getRotation().clone() ?? new pc.Quat();
            let rotation = parentRotation.mul(splatEntity.getLocalRotation());
            const correction = primarySplatCoordinateCorrection?.eulerDegrees;
            if (correction) {
                const c = new pc.Quat().setFromEulerAngles(correction[0], correction[1], correction[2]);
                rotation = new pc.Quat().mul2(rotation, c.invert());
            }
            const e = rotation.getEulerAngles();
            const toRad = Math.PI / 180;
            const s = splatEntity.getLocalScale();
            return {
                position,
                rotation: [e.x * toRad, e.y * toRad, -e.z * toRad],
                scale: (Math.abs(s.x) + Math.abs(s.y) + Math.abs(s.z)) / 3,
            };
        };
        // Playback mutation
        editorInstance.setAutoplaySpeed = (speed: number) => {
            config.autoplaySpeed = speed;
            const numWp = config.waypoints?.length || 1;
            const pl = Math.max(1, getTourSegmentCount(loopMode, numWp) * 20);
            playbackSpeed = (speed * 60) / pl;
        };
        editorInstance.setLoopMode = (mode: 'loop' | 'pingpong' | 'none') => {
            const previousMode = loopMode;
            loopMode = mode;
            config.loopMode = mode;
            currentProgress = remapProgressForLoopMode(currentProgress, previousMode, mode);
            targetProgress = remapProgressForLoopMode(targetProgress, previousMode, mode);
            const numWp = config.waypoints?.length || 1;
            const pl = Math.max(1, getTourSegmentCount(loopMode, numWp) * 20);
            if (config.autoplaySpeed !== undefined) {
                playbackSpeed = (config.autoplaySpeed * 60) / pl;
            }
            if (waypointControlEnabled) {
                updateCameraFromProgress(currentProgress);
            }
        };
        editorInstance.setScrollSpeed = (speed: number) => {
            config.scrollSpeed = speed;
        };
        // Playground mode
        editorInstance.startPlayground = () => { startPlayground(); };
        editorInstance.stopPlayground = () => { stopPlayground(); };
        editorInstance.isPlaygroundActive = () => playgroundActive;
        // Voxel collision hot-reload (editor generates voxels after viewer init)
        editorInstance.initVoxelCollision = async (jsonUrl: string) => {
            await setRuntimeVoxelCollisionUrl(jsonUrl);
            console.log('[StorySplat Viewer] Voxel collision loaded via editor API');
        };
        // Voxel collision debug visualization
        editorInstance.setVoxelDebug = (visible: boolean) => {
            if (characterController) {
                characterController.setVoxelDebug(visible);
            }
        };
        editorInstance.getVoxelDebugVisible = () => {
            return characterController?.voxelDebugVisible ?? false;
        };
        editorInstance.clearVoxelDebug = () => {
            characterController?.clearVoxelDebug();
        };
        editorInstance.getVoxelDebugLayerId = () => voxelDebugLayer.id;

        // Entity animation system editor API
        editorInstance.getEntityAnimationSystem = () => entityAnimationSystem;
        editorInstance.setEntityAnimations = (input: import('../types').EntityAnimationConfig[]) => {
            // An editor that predates Regions still keys focus / annotation targets: retarget them.
            const animations = migrateRegionAnimations(input, config.postProcessing?.focusRegions);
            // cloud layers with a keyframed size generate for the largest size the keyframes reach
            cloudSystem.setAnimations(animations);
            if (entityAnimationSystem) {
                entityAnimationSystem.setAnimations(animations);
            } else {
                // Create system on first use
                entityAnimationSystem = createEntityAnimationSystem(animations);
            }
        };

        // Post-processing editor API
        editorInstance.setOverlays = (overlays) => setOverlays(overlays);
        // Region proxy: a bare transform node under the scene root that the editor's gizmo
        // grabs while a focus / haze / overlay region is being edited. Its wireframe is drawn
        // every frame in a distinct colour so the edited region stands out from the others.
        let regionProxy: pc.Entity | null = null;
        let regionProxyShape: RegionShape['shape'] = 'sphere';
        const regionProxyColor = new pc.Color(1, 0.6, 0.15);
        app.on('prerender', () => {
            if (regionProxy) drawRegionWireframe(app, regionProxy.getWorldTransform(), regionProxyShape, regionProxyColor);
        });
        editorInstance.setRegionProxy = (shape) => {
            if (!shape) {
                regionProxy?.destroy();
                regionProxy = null;
                return null;
            }
            if (!regionProxy) {
                regionProxy = new pc.Entity('region-proxy');
                sceneContent.addChild(regionProxy);
            }
            regionProxyShape = shape.shape;
            applyRegionToEntity(regionProxy, shape);
            return regionProxy;
        };
        editorInstance.getRegionProxyEntity = () => regionProxy;
        // One region (or haze region) kept drawn while it is edited in the viewport: a region
        // through the runtime's preview (a fade, no recompile), a haze region by rebuilding haze.
        // One target at a time, as before: previewing one releases the other.
        editorInstance.setRegionPreview = (target) => {
            const kind = target?.kind === 'focus' ? 'region' : target?.kind;
            const id = target && typeof target.id === 'string' ? target.id : null;
            regionsRuntime.setPreview(kind === 'region' ? id : null);
            const next = kind === 'haze' && id ? { kind: 'haze' as const, id } : null;
            if (next?.kind === previewRegion?.kind && next?.id === previewRegion?.id) return;
            previewRegion = next;
            if (isDestroyed || !app.graphicsDevice) return;
            applyRegionEffect('haze');
        };
        // Regions: live config from the editor's Regions panel. Recompiles only when areas change;
        // the Layers menu (viewers with UI) is rebuilt from it.
        editorInstance.setRegions = (regionsConfig) => {
            regionsAuthoritative = true;
            config.regions = regionsConfig && typeof regionsConfig === 'object' ? regionsConfig : undefined;
            if (isDestroyed || !app.graphicsDevice) return;
            // The creator edited the regions: show the panel's looks until the timeline is scrubbed.
            regionsRuntime.clearAnimatedValues(null);
            regionsRuntime.setConfig(config.regions);
            syncLayersPanel();
        };
        /**
         * Legacy edits (an editor or embedder that predates Regions): focus regions and annotation
         * layers convert to regions while the scene has no `regions` and setRegions was never called.
         */
        let legacyRegionsKey = JSON.stringify([config.postProcessing?.focusRegions ?? null, config.annotationLayers ?? null]);
        const syncLegacyRegions = () => {
            if (config.regions || regionsAuthoritative || isDestroyed || !app.graphicsDevice) return;
            const key = JSON.stringify([config.postProcessing?.focusRegions ?? null, config.annotationLayers ?? null]);
            if (key === legacyRegionsKey) return;
            legacyRegionsKey = key;
            regionsRuntime.clearAnimatedValues(null);
            regionsRuntime.setConfig(regionsInUse());
            syncLayersPanel();
        };
        editorInstance.setAnnotationLayers = (layersConfig) => {
            config.annotationLayers = layersConfig;
            syncLegacyRegions();
        };
        editorInstance.setAnnotationLayerPreview = (id) => regionsRuntime.setPreview(typeof id === 'string' ? id : null, 'layer');
        // Editor-only line overlay for region areas (outlines, volumes, splat objects), the outline
        // being clicked and area measurements. Points are in scene coordinates (what
        // raycastScreenPoint returns), drawn through the scene-content root.
        let regionEditorOverlay: import('../types').RegionEditorOverlay | null = null;
        editorInstance.setRegionEditorOverlay = (overlay) => {
            regionEditorOverlay = overlay && typeof overlay === 'object' ? overlay : null;
            // While the overlay outlines every region, the effect's own bounds would draw them twice.
            regionsRuntime.setBoundsHidden(!!regionEditorOverlay?.outlines);
            app.renderNextFrame = true;
        };
        editorInstance.setAnnotationEditorOverlay = (overlay) => {
            if (!overlay) {
                editorInstance.setRegionEditorOverlay(null);
                return;
            }
            const { selectedShapeId, selectedShapePoints, ...rest } = overlay;
            editorInstance.setRegionEditorOverlay({
                ...rest,
                ...(selectedShapeId !== undefined ? { selectedAreaId: selectedShapeId } : {}),
                ...(selectedShapePoints !== undefined ? { selectedAreaPoints: selectedShapePoints } : {}),
            });
        };
        const hexToColor = (hex: string | undefined, fallback: pc.Color): pc.Color => {
            if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return fallback;
            const [r, g, b] = regionRgb(hex);
            return new pc.Color(r, g, b);
        };
        /** The camera-facing nudges that draw a line thick (regionOverlayStyle's `thick`), for a point `at` this world position. */
        const thickOffsets = (at: pc.Vec3): pc.Vec3[] =>
            thickLineOffsets(camera.right, camera.up, at.distance(camera.getPosition())).map(([x, y, z]) => new pc.Vec3(x, y, z));
        const drawScenePolyline = (points: Array<{ x: number; y: number; z: number }>, color: pc.Color, closed: boolean, thick = false) => {
            const m = sceneContent.getWorldTransform();
            const count = closed && points.length >= 3 ? points.length : points.length - 1;
            for (let i = 0; i < count; i++) {
                const a = points[i];
                const b = points[(i + 1) % points.length];
                const wa = m.transformPoint(new pc.Vec3(a.x, a.y, a.z));
                const wb = m.transformPoint(new pc.Vec3(b.x, b.y, b.z));
                app.drawLine(wa, wb, color, false);
                if (thick) for (const o of thickOffsets(wa)) app.drawLine(new pc.Vec3().add2(wa, o), new pc.Vec3().add2(wb, o), color, false);
            }
        };
        const drawScenePoints = (points: Array<{ x: number; y: number; z: number }>, color: pc.Color) => {
            const m = sceneContent.getWorldTransform();
            const cam = camera.getPosition();
            for (const p of points) {
                const c = m.transformPoint(new pc.Vec3(p.x, p.y, p.z));
                const r = Math.max(1e-3, c.distance(cam) * 0.008);
                for (const axis of [new pc.Vec3(r, 0, 0), new pc.Vec3(0, r, 0), new pc.Vec3(0, 0, r)]) {
                    app.drawLine(new pc.Vec3().sub2(c, axis), new pc.Vec3().add2(c, axis), color, false);
                }
            }
        };
        const annotationSelectedColor = new pc.Color(1, 1, 1);
        const annotationDraftColor = new pc.Color(1, 0.6, 0.15);
        const draftRgb = [annotationDraftColor.r, annotationDraftColor.g, annotationDraftColor.b] as const;
        app.on('prerender', () => {
            const overlay = regionEditorOverlay;
            if (!overlay) return;
            const regionList = overlay.outlines ? regionsInUse()?.regions : undefined;
            for (const region of Array.isArray(regionList) ? regionList : []) {
                if (!region || region.enabled === false || !Array.isArray(region.areas)) continue;
                // Areas of drawn regions in their region's colour (other regions dimmed while one is
                // open); the selected area always, brighter and thicker (regionOverlayStyle).
                const drawn = regionsRuntime.isShown(region.id);
                const rgb = typeof region.color === 'string' && /^#[0-9a-f]{6}$/i.test(region.color) ? regionRgb(region.color) : draftRgb;
                const volume = regionAnimatableVolume(region);
                for (const area of region.areas) {
                    if (!area || area.enabled === false) continue;
                    const selected = area.id === overlay.selectedAreaId;
                    if (!selected && !drawn) continue;
                    const style = regionOverlayStyle(rgb, { regionId: region.id, areaId: area.id, selectedRegionId: overlay.selectedRegionId, selectedAreaId: overlay.selectedAreaId });
                    const lineColor = new pc.Color(style.color[0], style.color[1], style.color[2]);
                    if (area.shape === 'splats') {
                        // Splat objects: each object's own box.
                        drawSplatObjectBounds(app, sceneContent, normalizeSplatObjectIds(area.splatIds), lineColor);
                        continue;
                    }
                    if (area.shape === 'outline') {
                        const points = cleanSurfacePoints(selected && overlay.selectedAreaPoints ? overlay.selectedAreaPoints : area.points ?? []);
                        drawScenePolyline(points, lineColor, true, style.thick);
                        if (style.points) drawScenePoints(points, annotationSelectedColor);
                        continue;
                    }
                    // Volumes (editor coordinates): their wireframe, following keyframes when animated.
                    const shape = (volume?.id === area.id ? regionsRuntime.getAnimatedShape(region.id) : null)
                        ?? normalizeRegion({ ...area, shape: area.shape === 'box' ? 'box' : 'sphere' } as Partial<RegionShape>);
                    const matrix = new pc.Mat4().mul2(sceneContent.getWorldTransform(), regionMatrix(shape));
                    drawRegionWireframe(app, matrix, shape.shape, lineColor);
                    if (style.thick) {
                        for (const o of thickOffsets(matrix.getTranslation())) {
                            drawRegionWireframe(app, new pc.Mat4().mul2(new pc.Mat4().setTranslate(o.x, o.y, o.z), matrix), shape.shape, lineColor);
                        }
                    }
                }
            }
            for (const area of overlay.areas ?? []) {
                drawScenePolyline(cleanSurfacePoints(area.points), hexToColor(area.color, annotationDraftColor), true);
            }
            if (overlay.regionOutline?.points?.length) {
                const points = cleanSurfacePoints(overlay.regionOutline.points);
                const color = hexToColor(overlay.regionOutline.color, annotationSelectedColor);
                drawScenePolyline(points, color, true);
                drawScenePoints(points, color);
            }
            if (overlay.draft?.length) {
                const color = hexToColor(overlay.draftColor, annotationDraftColor);
                const points = cleanSurfacePoints(overlay.draft);
                drawScenePolyline(points, color, points.length >= 3);
                drawScenePoints(points, color);
            }
        });
        editorInstance.readRegionProxy = () => (regionProxy ? regionFromEntity(regionProxy) : null);
        editorInstance.getCameraScenePosition = () => {
            const local = new pc.Mat4().copy(sceneContent.getWorldTransform()).invert().transformPoint(camera.getPosition());
            return { x: local.x, y: local.y, z: -local.z };
        };
        editorInstance.scenePositionToWorld = (point) => {
            const world = sceneContent.getWorldTransform().transformPoint(new pc.Vec3(point.x, point.y, -point.z));
            return { x: world.x, y: world.y, z: -world.z };
        };
        editorInstance.setPostProcessing = (ppConfig: import('../types').PostProcessingConfig | undefined) => {
            config.postProcessing = ppConfig;
            // The creator edited these: show the panel's values until the timeline is scrubbed.
            clearSceneAnimationOverrides('postProcessing');
            clearSceneAnimationOverrides('haze');
            if (anyCameraFrameEffect(ppConfig) && camera.camera) {
                // WebGL's FXAA (the PostEffectQueue) points the camera at its own render target, and a
                // CameraFrame composes into whatever target the camera has when it is enabled: take FXAA
                // off first, or the frame is composed into FXAA's target, which stops being drawn once
                // FXAA is removed below, and the canvas keeps its last frame (a frozen viewport).
                if (fxaaEffect) {
                    camera.camera.postEffects.removeEffect(fxaaEffect);
                    fxaaEffect = null;
                }
                try {
                    if (!cameraFrame) {
                        cameraFrame = new pc.CameraFrame(app, camera.camera);
                    }
                    cameraFrame.enabled = true;
                    applyCameraFrameEffects(cameraFrame, ppConfig!);
                } catch (e) {
                    console.warn('[StorySplat Viewer] CameraFrame setup failed:', e);
                }
            } else if (cameraFrame) {
                // Disable all CameraFrame effects
                applyCameraFrameEffects(cameraFrame, {});
                cameraFrame.enabled = false;
            }

            syncRegionEffect('haze');
            // FXAA toggle: when CameraFrame is active, remove FXAA and use sharpness;
            // when CameraFrame is off, re-add FXAA to the PostEffectQueue.
            // WebGPU: skip entirely — our FXAA shader is GLSL-only and no transpiler
            // is loaded, so attaching it would render the canvas black.
            // Mirror the init-time mobile gate: FXAA needs an explicit opt-in on mobile, or any
            // setPostProcessing call would re-add the full-res pass the init path skipped.
            const wantAA = usesMobileRenderProfile()
                ? ppConfig?.antialiasing?.enabled === true
                : ppConfig?.antialiasing?.enabled !== false;
            const isWebGPUDevice = (app.graphicsDevice as { isWebGPU?: boolean }).isWebGPU === true;
            if (wantAA && !cameraFrame?.enabled && camera.camera && !isWebGPUDevice) {
                if (!fxaaEffect) {
                    fxaaEffect = new FxaaEffect(app.graphicsDevice);
                }
                if (!camera.camera.postEffects.effects?.some((e: any) => e.effect === fxaaEffect)) {
                    camera.camera.postEffects.addEffect(fxaaEffect);
                }
            } else if (fxaaEffect && camera.camera) {
                camera.camera.postEffects.removeEffect(fxaaEffect);
                fxaaEffect = null;
            }
            if (wantAA && cameraFrame?.enabled) {
                cameraFrame.rendering.sharpness = Math.max(cameraFrame.rendering.sharpness, 0.5);
            }

            // Focus regions (an editor or embedder that predates Regions): converted to regions.
            syncLegacyRegions();
            // Scene-level effects (independent of CameraFrame)
            applyFog(ppConfig?.fog);
            applyExposure(ppConfig?.exposure);
        };

        // Fisheye ("tiny planet") live setter — host pages and viewer button wire in here.
        editorInstance.setFisheye = (amount: number) => {
            applyFisheye(amount);
        };

        // Weather live setter — toggles or reconfigures procedural splat weather.
        editorInstance.setWeather = (weatherConfig: import('../types').WeatherConfig | undefined) => {
            config.weather = weatherConfig;
            applyWeather(weatherConfig);
        };

        // Audio-reactive live setter — toggles or reconfigures the music-driven splat effect.
        editorInstance.setAudioReactive = (arConfig: AudioReactiveConfig | undefined) => {
            config.audioReactive = arConfig;
            clearSceneAnimationOverrides('audioReactive');
            effectiveAudioReactiveConfig = arConfig;
            applyAudioReactive(effectiveAudioReactiveConfig);
        };

        // LOD settings editor API — updates scene-level + per-component LOD at runtime
        editorInstance.setLodSettings = (lodConfig: LodSettingsConfig) => {
            if (!app.scene.gsplat) return;

            sceneLodSettings = lodConfig;
            config.lodSettings = lodConfig;
            resolvedLodSettings = resolveLodSettings(lodConfig, lodMobileProfile);
            const rangeMin = resolvedLodSettings.range[0];
            const rangeMax = resolvedLodSettings.range[1];
            const baseDistance = resolvedLodSettings.lodBaseDistance;
            const multiplier = resolvedLodSettings.lodMultiplier;

            // Update scene-level gsplat settings
            app.scene.gsplat.lodRangeMin = rangeMin;
            app.scene.gsplat.lodRangeMax = rangeMax;

            // Update splat budget. Custom-mode users set `lodConfig.splatBudget`
            // directly. Preset modes (desktop/mobile/etc.) intentionally omit
            // splatBudget on the lodSettings object — the preset's own budget
            // lives on resolvedLodSettings.splatBudget, populated by
            // resolveLodSettings. Falling back to `?? 0` instead of that would
            // wipe the mobile preset's memory cap on every live update.
            const budget = lodConfig.splatBudget ?? resolvedLodSettings.splatBudget;
            applySplatBudget(budget);
            renderScheduler.requestFrame('lod-settings');

            // Update resolved variables so future splat loads use the new values
            resolvedLodBaseDistance = baseDistance;
            resolvedLodMultiplier = multiplier;
            resolvedLodRange = [rangeMin, rangeMax];

            // Update per-component lodBaseDistance/lodMultiplier on all loaded gsplat entities.
            // Set dedupes in case the main entity also lives in preloadedSplats.
            const touchedEntities = new Set<pc.Entity>();
            const updateGsplatComponent = (entity: pc.Entity | null) => {
                if (!entity || touchedEntities.has(entity)) return;
                touchedEntities.add(entity);
                const gs = entity.gsplat as unknown as GSplatComponentWithRuntime | undefined;
                if (gs) {
                    gs.lodBaseDistance = baseDistance;
                    gs.lodMultiplier = multiplier;
                    // 2.20: lodRange is per-component now (scene-level is a no-op).
                    (gs as any).lodRangeMin = rangeMin;
                    (gs as any).lodRangeMax = rangeMax;
                }
            };
            updateGsplatComponent(splatEntity);
            updateGsplatComponent(compareAfterEntity);
            preloadedSplats.forEach((entity) => updateGsplatComponent(entity));

            // Apply renderer selection on live toggle (2.18+). Same 'auto' →
            // raster-gpu mapping as init.
            const effectiveRendererLive = resolveEffectiveRenderer(resolvedLodSettings.renderer);
            (app.scene.gsplat as any).renderer = GSPLAT_RENDERER_MAP[effectiveRendererLive];
            logGsplatRenderer(app, 'setLodSettings', effectiveRendererLive);

            console.log('[StorySplat Viewer] LOD settings updated:', {
                preset: lodConfig.preset,
                rangeMin,
                rangeMax,
                baseDistance,
                multiplier,
                budget,
                renderer: effectiveRendererLive,
                entitiesUpdated: touchedEntities.size
            });
        };

        // Segment data hot-reload (editor sets segment data after generation)
        editorInstance.loadSegments = async (dataUrl: string, metaUrl?: string) => {
            // Retry until splat entity is ready (may not be loaded yet on cloud save restore)
            const tryLoad = async (retries = 20): Promise<void> => {
                if (!splatEntity?.gsplat) {
                    if (retries <= 0) {
                        console.warn('[StorySplat Viewer] Cannot load segments: splat entity never became ready');
                        return;
                    }
                    await new Promise(r => setTimeout(r, 500));
                    return tryLoad(retries - 1);
                }
                const attachment = await loadSegmentAttachment(splatEntity, dataUrl, metaUrl);
                segmentAttachment = attachment;
                config.enableSegmentHover = true;
                config.segmentDataUrl = dataUrl;
                config.segmentMetaUrl = metaUrl;
                // Segments loaded via editor API
            };
            try {
                await tryLoad();
            } catch (err) {
                console.warn('[StorySplat Viewer] Segment loading failed:', err);
            }
        };
        /** Get segment metadata (for building editor UI) */
        editorInstance.getSegmentMetadata = () => {
            return segmentAttachment?.metadata ?? null;
        };
        /** Get per-Gaussian segment IDs (for use with SplatEditSystem) */
        editorInstance.getSegmentIds = () => {
            return segmentAttachment?.segmentIds ?? null;
        };
        /** Select all Gaussians belonging to a segment.
         *  Requires a SplatEditAttachment to be passed (owned by SplatEditSystem). */
        editorInstance.selectBySegment = (segmentId: number, editAttachmentState?: Uint8Array, updateFn?: () => void) => {
            if (!segmentAttachment) return;
            const segIds = segmentAttachment.segmentIds;
            if (!editAttachmentState) {
                console.warn('[StorySplat Viewer] selectBySegment: no editAttachmentState provided');
                return;
            }
            // Clear existing selection, then select all splats in this segment
            // SplatState: 0=none, 1=selected, 2=hidden, 4=deleted
            for (let i = 0; i < Math.min(segIds.length, editAttachmentState.length); i++) {
                if (segIds[i] === segmentId) {
                    editAttachmentState[i] |= 1; // Set selected bit
                } else {
                    editAttachmentState[i] &= ~1; // Clear selected bit (keep hidden/deleted)
                }
            }
            updateFn?.();
            // Segment selected
        };
        editorInstance.clearSegments = () => {
            if (segmentAttachment) {
                segmentAttachment.destroy();
                segmentAttachment = null;
            }
            if (segmentTooltipEl) {
                segmentTooltipEl.remove();
                segmentTooltipEl = null;
            }
            _lastTooltipSeg = null; // a hot-swapped attachment may reuse ids — never keep content cache
            config.enableSegmentHover = false;
        };

        return editorInstance;
    }
    // Setup Custom Script System (after instance creation so scripts get the full Viewer API)
    if (config.customScript && config.customScript.trim() !== '') {
        console.log('[StorySplat Viewer] Initializing custom script system...');
        const customScriptSystem = setupCustomScript(
            instance, app, canvas, config.customScript,
            () => currentProgress,
            () => currentWaypointIndex,
        );
        if (customScriptSystem) {
            (app as ViewerRuntimeApp).__customScriptSystem = customScriptSystem;
            console.log('[StorySplat Viewer] Custom script system initialized');
        }
    }

    // Set up analytics if configured. `disableAnalytics` is the embedder's build
    // flag; `isAnalyticsAllowed()` is the VISITOR's opt-out (DNT, Global Privacy
    // Control, ?analytics=off, the storysplat_analytics_opt_out key), which the
    // npm/CDN path previously ignored entirely.
    const analyticsDisabled = options.disableAnalytics === true
        || !isAnalyticsAllowed()
        || (restrictAnalyticsToStorySplatHost && !isStorySplatHostedPage());
    const cleanupHandles: Array<{ destroy: () => void }> = [];

    if (options.analytics && !analyticsDisabled && !analyticsHandle) {
        const analyticsBaseUrl = options.analytics.baseUrl ?? 'https://discover.storysplat.com';
        // Analytics must never be able to take the viewer down. A throw here
        // (crypto.randomUUID outside a secure context was one) used to escape
        // createViewer and the scene never rendered.
        try {
            analyticsHandle = setupAnalyticsTracking(instance, analyticsBaseUrl, options.analytics.sceneId, options.analytics.ownerId);
            cleanupHandles.push(analyticsHandle);
        } catch (err) {
            console.warn('[StorySplat Viewer] Analytics setup failed; continuing without it:', err);
            analyticsHandle = null;
        }
    }

    // `!gaHandle` mirrors the SessionRecorder guard above: on a portal teleport
    // the previous instance is never destroyed, so re-running setup added
    // another gtag('config') + page_view + beforeunload handler on every hop.
    if (options.googleTagId && !analyticsDisabled && !gaHandle) {
        try {
            gaHandle = setupGoogleAnalytics(instance, options.googleTagId, {
                sceneId: options.analytics?.sceneId || '',
                sceneName: scene.name || '',
            });
            cleanupHandles.push(gaHandle);
        } catch (err) {
            console.warn('[StorySplat Viewer] Google Analytics setup failed; continuing without it:', err);
            gaHandle = null;
        }
    }

    if (cleanupHandles.length > 0) {
        // Wire analytics cleanup into the viewer destroy path
        const originalDestroy = instance.destroy;
        instance.destroy = () => {
            for (const handle of cleanupHandles) {
                try { handle.destroy(); } catch { /* analytics cleanup should not block destroy */ }
            }
            originalDestroy();
        };
    }

    return instance;
}
/**
 * Create viewer from scene URL
 */
export async function createViewerFromUrl(container: HTMLElement, jsonUrl: string, options?: ViewerOptions): Promise<ViewerInstance> {
    console.log('[StorySplat Viewer] Fetching scene from:', jsonUrl);
    const response = await fetch(jsonUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
    }
    const scene: SceneData = await response.json();
    console.log('[StorySplat Viewer] Scene data loaded:', scene);
    return createViewer(container, scene, options);
}
// Utility functions
function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}
function lerpAngle(a: number, b: number, t: number): number {
    let diff = b - a;
    while (diff > 180)
        diff -= 360;
    while (diff < -180)
        diff += 360;
    return a + diff * t;
}
function easeInOutCubic(t: number): number {
    return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
